import { useEffect, useState, useRef, useCallback, useLayoutEffect, isValidElement, cloneElement, Children } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import Modal from 'react-modal';
import { 
  FaPlus, FaPrint, FaSave, FaTrash, FaShareAlt, FaComment,
  FaCopy, FaCut, FaTimes, FaPencilAlt, FaTelegramPlane, FaTwitter, FaArrowsAlt,
  FaMinus, FaArrowRight, FaFont, FaSmile, FaLayerGroup, FaTint, FaDrawPolygon, FaLevelDownAlt,
  FaMagic, FaUndo, FaRedo, FaEraser,
  FaWhatsapp, FaBluetooth, FaEnvelope, FaInstagram, FaFacebookF,
  FaPalette, FaCheckSquare, FaSquare,
  FaVectorSquare, FaMousePointer, FaRegSquare, FaRegCircle, FaBroom,
  FaChevronRight, FaChevronLeft,
  FaLock, FaUnlock, FaSyncAlt, FaCheck, FaCrop, FaGem, FaFingerprint,
  FaFolder, FaCamera, FaCloud, FaCloudUploadAlt, FaFile, FaPaintBrush, FaImage,
  FaSun, FaAdjust, FaRobot, FaPaperPlane, FaMicrophone, FaHistory, FaSlidersH, FaPencilRuler, FaDownload,
  FaQuestionCircle, FaExternalLinkAlt, FaUserCircle, FaSignOutAlt, FaObjectGroup, FaComments
} from 'react-icons/fa';
import { FaEyeDropper, FaSprayCan, FaPen } from "react-icons/fa6";
import { listObjectsInFolder, listObjectsWithMetadata, uploadToStorage, readObjectContent, deleteFromStorage, renameInStorage, getEnvStorageBucket, getDownloadUrl, usingCloud, cloudOffline } from '../studio/lib/bucket';
import { formatFileSize } from '../utils/format';
import { getAuth, initCloudAuth, cloudAuthReady, onAuthChange, isCloudAuthEnabled, isCloudAdmin, signUp, signIn, signOut, setStudioRole } from '../studio/lib/cloudAuth';
import { proxiedImageUrl, isFirebaseStorageUrl } from '../services/storageFallback';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, serverTimestamp, getFirebase, storageRef as stampStorageRef, uploadBytes, getDownloadURL, deleteObject } from '../studio/lib/localDb';
import { useAlert } from '../contexts/ToastContext';
import { Capacitor, Share, isStandalone } from '../studio/lib/platform';
import { isAiEnabled, isAiReachable, aiEditImage, aiGenerateImage, aiRemoveBackground, aiFindObjects, aiReadText, aiDescribe } from '../studio/lib/backend';
import { useOnline } from '../studio/lib/useOnline';
import { SHAPES_2D, SHAPES_3D, SHAPE_2D_KEYS, SHAPE_3D_KEYS, isShape2D, isShape3D, tones3D, bodyColour3D, freeformPath, simplifyPoints, normalizeFreeform } from '../studio/lib/shapeLibrary';
import { project3D, facePath, has3DMesh, pose3D, bounds3D, sphereSeams3D } from '../studio/lib/shape3d';
import { withPhysicalSize, readPhysicalSize } from '../studio/lib/pngSize';
import { saveImageFile, canPickFolder, getStudioFolder, getStudioFolderName, changeStudioFolder, FOLDER_NAME as SAVE_FOLDER_NAME } from '../studio/lib/saveTarget';
import { openCamera, closeCamera, cameraMessage, cameraAlreadyGranted, hasCameraConsent } from '../studio/lib/camera';
import { buildProject, projectFile, projectPathFor, legacyProjectPathFor, projectBaseOf, isProject } from '../studio/lib/project';
import { saveSession, loadSession, clearSession } from '../studio/lib/session';
import { grabCutInMask } from '../studio/lib/grabcut';
import { loadWithGainMap } from '../studio/lib/ultrahdr';
const isFreeTool = (t) => t === 'freeline' || t === 'freeshape';
import { ShapeIcon, Cube3DIcon, DrawToolIcon } from '../studio/components/StudioIcons';
import { promptInstall, canOfferInstall } from '../studio/components/installState';
import ColourMixer from '../studio/components/ColourMixer';
import { normaliseHex, PICK_CURSOR } from '../studio/lib/colour';
import pkg from '../../package.json';
import './css/ImageStudio.css';

const A4_WIDTH_CM = 21;
const A4_HEIGHT_CM = 29.7;
const A4_RATIO = A4_HEIGHT_CM / A4_WIDTH_CM;

// Switching pen/pencil/spray/etc. starts the new tool at this breadth rather than carrying over
// whatever the last tool was left at -- a 25px spray width is a mistake, not an intent, once
// you've picked Pencil, and finding it still fat there is the confusing part.
const DEFAULT_BRUSH_SIZE = 5;

/** True on a device pointed at with a finger rather than a mouse. Asked once — nobody swaps
 *  their phone for a desktop mid-session, and the answer sizes every touch target below. */
const COARSE_POINTER = typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(pointer: coarse)').matches;

/**
 * How wide a thing has to be before it can be tapped: the width of the invisible copy of a
 * figure's outline, and the band around a drawn stroke that counts as touching it.
 *
 * A fingertip covers roughly 8mm of glass and lands with nothing like a cursor's precision, so
 * a phone gets a wider target than a mouse does.
 *
 * But this is a WIDTH, centred on the line, so 44 meant a band reaching 22px either side of
 * every stroke — most of a centimetre of glass, on both sides, around every line on the sheet.
 * Lines drawn near each other had overlapping bands, so a tap that was plainly on one of them
 * selected the other; and a tap nowhere near anything still found something. "It selects from
 * five centimetres away and picks the wrong line" is that number.
 *
 * Half of it. Ten pixels either side on a phone is still a forgiving target for a 2px line —
 * about 2mm of glass — while meaning, recognisably, ON the line.
 */
const TOUCH_HIT_PX = COARSE_POINTER ? 20 : 10;

// ---- Help panel: FAQ, share QR, version, developer info ---------------------------------
// What a saved picture is called when nothing was typed, and the folder it goes into.
const DEFAULT_IMAGE_NAME = 'PCake_Image';
const FOLDER_NAME = 'PCake';
const APP_SHARE_URL = 'https://priyas.mycreativeworld.app';
const APP_DEV_NAME = 'My Creative World (MCW)';
const APP_DEV_EMAIL = 'admin@mycreativeworld.app';
const APP_DEV_SITE = 'https://mycreativeworld.app';
const APP_DEV_TAGLINE = 'We turn bright ideas into delightful apps.';

// A QR code for APP_SHARE_URL, generated offline (libqrencode, level M) and verified by
// decoding the rendered bitmap back to the exact URL with zbar -- so this path is known
// scannable, not just something that looks right. It only needs regenerating if the app's
// public URL ever changes.
const APP_QR_SIZE = 37; // modules per side, including the 4-module quiet zone on each edge
const APP_QR_PATH = 'M4 4h7v1h-7zM12 4h3v1h-3zM16 4h2v1h-2zM20 4h4v1h-4zM26 4h7v1h-7zM4 5h1v1h-1zM10 5h1v1h-1zM12 5h1v1h-1zM14 5h1v1h-1zM22 5h1v1h-1zM26 5h1v1h-1zM32 5h1v1h-1zM4 6h1v1h-1zM6 6h3v1h-3zM10 6h1v1h-1zM13 6h1v1h-1zM17 6h1v1h-1zM19 6h2v1h-2zM24 6h1v1h-1zM26 6h1v1h-1zM28 6h3v1h-3zM32 6h1v1h-1zM4 7h1v1h-1zM6 7h3v1h-3zM10 7h1v1h-1zM12 7h1v1h-1zM14 7h1v1h-1zM16 7h6v1h-6zM23 7h1v1h-1zM26 7h1v1h-1zM28 7h3v1h-3zM32 7h1v1h-1zM4 8h1v1h-1zM6 8h3v1h-3zM10 8h1v1h-1zM13 8h4v1h-4zM18 8h3v1h-3zM23 8h1v1h-1zM26 8h1v1h-1zM28 8h3v1h-3zM32 8h1v1h-1zM4 9h1v1h-1zM10 9h1v1h-1zM13 9h1v1h-1zM17 9h2v1h-2zM22 9h3v1h-3zM26 9h1v1h-1zM32 9h1v1h-1zM4 10h7v1h-7zM12 10h1v1h-1zM14 10h1v1h-1zM16 10h1v1h-1zM18 10h1v1h-1zM20 10h1v1h-1zM22 10h1v1h-1zM24 10h1v1h-1zM26 10h7v1h-7zM12 11h5v1h-5zM18 11h5v1h-5zM24 11h1v1h-1zM4 12h1v1h-1zM6 12h2v1h-2zM9 12h3v1h-3zM14 12h3v1h-3zM19 12h4v1h-4zM26 12h1v1h-1zM29 12h1v1h-1zM31 12h2v1h-2zM5 13h2v1h-2zM8 13h2v1h-2zM11 13h2v1h-2zM14 13h4v1h-4zM20 13h2v1h-2zM23 13h2v1h-2zM26 13h3v1h-3zM32 13h1v1h-1zM5 14h4v1h-4zM10 14h1v1h-1zM12 14h4v1h-4zM17 14h1v1h-1zM20 14h2v1h-2zM24 14h2v1h-2zM30 14h2v1h-2zM4 15h1v1h-1zM9 15h1v1h-1zM13 15h2v1h-2zM16 15h2v1h-2zM19 15h1v1h-1zM22 15h3v1h-3zM26 15h1v1h-1zM32 15h1v1h-1zM7 16h2v1h-2zM10 16h4v1h-4zM17 16h5v1h-5zM29 16h2v1h-2zM4 17h1v1h-1zM6 17h4v1h-4zM14 17h1v1h-1zM18 17h4v1h-4zM23 17h1v1h-1zM26 17h1v1h-1zM30 17h3v1h-3zM4 18h2v1h-2zM7 18h1v1h-1zM10 18h1v1h-1zM12 18h2v1h-2zM15 18h1v1h-1zM17 18h1v1h-1zM21 18h1v1h-1zM23 18h2v1h-2zM27 18h1v1h-1zM30 18h3v1h-3zM4 19h1v1h-1zM7 19h1v1h-1zM11 19h2v1h-2zM14 19h2v1h-2zM18 19h3v1h-3zM24 19h1v1h-1zM27 19h2v1h-2zM31 19h1v1h-1zM4 20h1v1h-1zM6 20h1v1h-1zM8 20h3v1h-3zM15 20h3v1h-3zM22 20h1v1h-1zM25 20h1v1h-1zM27 20h3v1h-3zM31 20h1v1h-1zM5 21h2v1h-2zM8 21h2v1h-2zM12 21h8v1h-8zM22 21h1v1h-1zM24 21h2v1h-2zM27 21h1v1h-1zM29 21h3v1h-3zM4 22h1v1h-1zM6 22h1v1h-1zM9 22h2v1h-2zM12 22h3v1h-3zM19 22h1v1h-1zM21 22h2v1h-2zM30 22h1v1h-1zM6 23h2v1h-2zM12 23h3v1h-3zM16 23h1v1h-1zM18 23h4v1h-4zM26 23h2v1h-2zM30 23h1v1h-1zM5 24h2v1h-2zM10 24h3v1h-3zM14 24h2v1h-2zM18 24h1v1h-1zM21 24h10v1h-10zM12 25h1v1h-1zM16 25h1v1h-1zM18 25h1v1h-1zM20 25h1v1h-1zM22 25h3v1h-3zM28 25h5v1h-5zM4 26h7v1h-7zM12 26h2v1h-2zM16 26h1v1h-1zM18 26h1v1h-1zM20 26h5v1h-5zM26 26h1v1h-1zM28 26h2v1h-2zM31 26h1v1h-1zM4 27h1v1h-1zM10 27h1v1h-1zM12 27h2v1h-2zM15 27h3v1h-3zM20 27h1v1h-1zM22 27h1v1h-1zM24 27h1v1h-1zM28 27h2v1h-2zM4 28h1v1h-1zM6 28h3v1h-3zM10 28h1v1h-1zM13 28h1v1h-1zM15 28h2v1h-2zM21 28h1v1h-1zM24 28h5v1h-5zM30 28h3v1h-3zM4 29h1v1h-1zM6 29h3v1h-3zM10 29h1v1h-1zM12 29h3v1h-3zM18 29h4v1h-4zM23 29h1v1h-1zM25 29h1v1h-1zM28 29h2v1h-2zM32 29h1v1h-1zM4 30h1v1h-1zM6 30h3v1h-3zM10 30h1v1h-1zM12 30h3v1h-3zM16 30h3v1h-3zM20 30h1v1h-1zM23 30h1v1h-1zM27 30h1v1h-1zM30 30h1v1h-1zM32 30h1v1h-1zM4 31h1v1h-1zM10 31h1v1h-1zM14 31h3v1h-3zM18 31h3v1h-3zM23 31h2v1h-2zM27 31h1v1h-1zM29 31h1v1h-1zM31 31h1v1h-1zM4 32h7v1h-7zM12 32h1v1h-1zM16 32h2v1h-2zM20 32h1v1h-1zM22 32h4v1h-4zM27 32h1v1h-1zM31 32h1v1h-1z';

/**
 * FAQ, in English and German.
 *
 * These deliberately answer the questions that the toolbar cannot: the rules behind selection,
 * what the eraser really does to a figure, how a 3D object's angles work, what "exact size"
 * means when saving. Anything you can discover by tapping a button is left out — a list of
 * obvious answers only buries the ones somebody actually opened this panel to find.
 */
const HELP_FAQ = [
  {
    q: { en: 'Why does tapping inside a shape not select it?',
         de: 'Warum wird eine Form nicht ausgewählt, wenn ich hineintippe?' },
    a: { en: 'So you can keep drawing inside it. An unselected figure is only a target on the line it actually paints — everything enclosed by that line passes straight through to the sheet, which is what lets you draw or place something inside an outline you have traced. Touch the outline itself (anywhere within about a fingertip of it) to select the figure; once it is selected, its whole body drags.',
         de: 'Damit Sie darin weiterzeichnen können. Eine nicht ausgewählte Figur reagiert nur auf ihrer tatsächlich gezeichneten Linie — alles, was diese Linie umschließt, wird an das Blatt darunter durchgereicht. Genau das erlaubt es, in einen nachgezeichneten Umriss hineinzuzeichnen. Tippen Sie auf die Linie selbst (etwa fingerbreit genau genügt), um die Figur auszuwählen; danach lässt sich ihr ganzer Körper ziehen.' },
  },
  {
    q: { en: 'How do I give an object a name?',
         de: 'Wie gebe ich einem Objekt einen Namen?' },
    a: { en: 'Double tap it on a touch screen, or double-click with a mouse. A small box opens inside the object itself; type the caption and it is stored with the object, so it moves, scales and rotates with it and appears in save and print. A single touch is never anything but select-and-move, so naming can never be triggered by accident while you are dragging.',
         de: 'Doppeltippen Sie es auf dem Touchscreen oder doppelklicken Sie mit der Maus. Innerhalb des Objekts öffnet sich ein kleines Feld; der eingegebene Text gehört zum Objekt und wandert, skaliert und dreht sich mit ihm — auch beim Speichern und Drucken. Eine einfache Berührung wählt immer nur aus und verschiebt, sodass die Beschriftung beim Ziehen nie versehentlich aufgeht.' },
  },
  {
    q: { en: 'What does the eraser do to a shape or photo — does it delete it?',
         de: 'Was macht der Radierer mit einer Form oder einem Foto — löscht er es?' },
    a: { en: 'No. It rubs out only the part you drag across, and the object survives with a hole in it. On a figure the hole is stored as part of the figure, in its own coordinates, so it travels, scales and turns with it instead of staying behind on the sheet — and it comes out the same way in save and print. The rubbing appears as you drag, not when you let go.',
         de: 'Nein. Er entfernt nur die überstrichene Stelle; das Objekt bleibt mit einem Loch darin erhalten. Bei einer Figur gehört das Loch zur Figur und ist in deren eigenen Koordinaten gespeichert — es wandert, skaliert und dreht sich also mit ihr, statt auf dem Blatt zurückzubleiben, und erscheint beim Speichern und Drucken genauso. Das Radieren ist schon während des Ziehens sichtbar, nicht erst beim Loslassen.' },
  },
  {
    q: { en: 'How do I turn a 3D object to another angle?',
         de: 'Wie drehe ich ein 3D-Objekt in einen anderen Winkel?' },
    a: { en: 'Select it and drag the blue orbit handle below it: sideways spins it, up and down tilts it, and the shading follows the new angle because the object is real geometry rather than a picture of one. The X, Y and A boxes take exact degrees — X tilt, Y spin, A the flat rotation on the page. Each kind of object opens at an angle chosen to suit its own shape, which is why an arrow starts nearly face-on while a cube starts in three-quarter view.',
         de: 'Wählen Sie es aus und ziehen Sie den blauen Dreh-Griff darunter: seitwärts dreht, auf und ab kippt, und die Schattierung folgt dem neuen Winkel, weil es sich um echte Geometrie handelt und nicht um ein Bild davon. In den Feldern X, Y und A lassen sich exakte Gradwerte eingeben — X Neigung, Y Drehung, A die flache Drehung auf der Seite. Jede Objektart startet in einem zu ihrer Form passenden Winkel; deshalb beginnt ein Pfeil fast frontal, ein Würfel dagegen in der Dreiviertelansicht.' },
  },
  {
    q: { en: 'What exactly does Save produce — the sheet or the object?',
         de: 'Was genau erzeugt „Speichern“ — das Blatt oder das Objekt?' },
    a: { en: 'Whichever you have selected. With one object selected you get just that piece, cropped to its own bounds at its exact size; with nothing selected you get the whole sheet. Share and Print follow the same rule. No step ever resamples a photo, so what comes out carries the resolution that went in.',
         de: 'Das, was ausgewählt ist. Mit einem ausgewählten Objekt erhalten Sie genau dieses Stück, auf seine eigenen Grenzen beschnitten und in exakter Größe; ohne Auswahl das ganze Blatt. Teilen und Drucken folgen derselben Regel. Kein Schritt rechnet ein Foto neu — die Auflösung bleibt genau die, die hineinging.' },
  },
  {
    q: { en: 'Two objects overlap. Which one does a tap pick, and how do I reach the other?',
         de: 'Zwei Objekte überlappen sich. Welches trifft ein Tippen, und wie erreiche ich das andere?' },
    a: { en: 'The one on top — the most recently placed sits highest. To reach the one underneath, tap a part of its outline that is not covered, or move the top one aside and put it back afterwards. Hand-drawn strokes are searched the same way, from the newest backwards, so the stroke you can see is the stroke you get.',
         de: 'Das oberste — zuletzt platzierte Objekte liegen am höchsten. Um an das darunterliegende zu kommen, tippen Sie auf einen unbedeckten Teil seines Umrisses oder schieben Sie das obere kurz beiseite. Handgezeichnete Striche werden ebenso von neu nach alt durchsucht: Sie treffen den Strich, den Sie sehen.' },
  },
  {
    q: { en: 'I signed in, but the Cloud says I have no images.',
         de: 'Ich habe mich angemeldet, aber die Cloud zeigt keine Bilder.' },
    a: { en: 'Your Cloud library belongs to the account, and anything saved before you first signed in was filed under this device instead. The message names the folder it searched, so you can tell an empty account from a look in the wrong place. Nothing leaves the device on its own in either case — an upload only happens when you choose it.',
         de: 'Ihre Cloud-Bibliothek gehört zum Konto; alles, was vor der ersten Anmeldung gespeichert wurde, liegt stattdessen unter diesem Gerät. Die Meldung nennt den durchsuchten Ordner, sodass sich ein leeres Konto von einem Blick an die falsche Stelle unterscheiden lässt. In beiden Fällen verlässt nichts von selbst das Gerät — hochgeladen wird nur, was Sie selbst hochladen.' },
  },
  {
    q: { en: 'Can I use the app without a connection, and how do I install it?',
         de: 'Kann ich die App ohne Verbindung nutzen, und wie installiere ich sie?' },
    a: { en: 'Yes. Drawing, editing, saving and printing all work offline; only the AI tools and the Cloud need a connection. Install it from your browser\'s install button to get it as its own app with its own icon. That icon is copied at the moment of installing, so if you installed very early and it looks blank, uninstalling and installing again is what fixes it.',
         de: 'Ja. Zeichnen, Bearbeiten, Speichern und Drucken funktionieren offline; nur die KI-Werkzeuge und die Cloud brauchen eine Verbindung. Über die Installieren-Schaltfläche des Browsers erhalten Sie die App eigenständig mit eigenem Symbol. Dieses Symbol wird im Moment der Installation kopiert — wirkt es leer, weil sehr früh installiert wurde, hilft Deinstallieren und erneutes Installieren.' },
  },
  {
    q: { en: 'Why can I not fill my hand-drawn shape with colour?',
         de: 'Warum lässt sich meine handgezeichnete Form nicht füllen?' },
    a: { en: 'A fill needs somewhere to stop. If the outline has even a small gap the colour escapes through it and floods the sheet instead, so the stroke has to close on itself — end where it began. Zoom in on the join if you are not sure: a gap of a pixel or two is easy to miss and is usually the whole problem.',
         de: 'Eine Füllung braucht eine Grenze. Hat der Umriss auch nur eine kleine Lücke, läuft die Farbe hindurch und über das Blatt; der Strich muss sich also schließen — dort enden, wo er begann. Sehen Sie sich die Nahtstelle im Zweifel vergrößert an: Eine Lücke von ein, zwei Pixeln übersieht man leicht und ist meist die ganze Ursache.' },
  },
  {
    q: { en: 'Does a stamp stay on the picture if someone edits it later?',
         de: 'Bleibt ein Stempel im Bild, wenn es später bearbeitet wird?' },
    a: { en: 'Yes. A stamp is burned into the image\'s own pixels rather than floating above it as a separate layer, so it survives saving, printing and sending the file on, and it cannot simply be selected and deleted afterwards.',
         de: 'Ja. Ein Stempel wird in die Bildpunkte selbst eingebrannt und schwebt nicht als eigene Ebene darüber. Er übersteht daher Speichern, Drucken und Weitergeben der Datei und lässt sich nachträglich nicht einfach auswählen und löschen.' },
  },
];

const HELP_UI = {
  faq:    { en: 'FAQ',            de: 'Häufige Fragen' },
  install:{ en: 'Install',        de: 'Installieren' },
  installBtn: { en: 'Install CreativeStudio on this device',
                de: 'CreativeStudio auf diesem Gerät installieren' },
  share:  { en: 'Share this app', de: 'App teilen' },
  about:  { en: 'About',          de: 'Über' },
  title:  { en: 'Help & About',   de: 'Hilfe & Über' },
  copied: { en: 'Link copied',    de: 'Link kopiert' },
};

// Opens in German for a German browser, English otherwise, and remembers a switch after that.
const HELP_LANG_KEY = 'pcake.studio.helpLang';
const initialHelpLang = () => {
  try {
    const saved = localStorage.getItem(HELP_LANG_KEY);
    if (saved === 'en' || saved === 'de') return saved;
  } catch { /* private mode */ }
  return /^de\b/i.test(navigator.language || '') ? 'de' : 'en';
};
// Help & About comes from OneAdmin: on a deployed build it writes src/oneadmin/help.js, which takes
// over the help button below (data-oneadmin-help) and shows these questions in the chosen help
// language. Nothing is imported — that file exists only in the deployed repo — so a local build
// keeps opening this app's own help panel as before.
if (typeof window !== 'undefined') {
  window.oneadminHelpConfig = { faq: HELP_FAQ, lang: initialHelpLang };
}

// Figure picker contents. The classic five keep their own renderers; the rest come from the
// shape library (one geometry for the SVG layer, the export renderer and the icon).
const FIG_CLASSIC = [
  { tool: 'line', title: 'Line' }, { tool: 'arrow', title: 'Arrow' }, { tool: 'circle', title: 'Circle' },
  { tool: 'triangle', title: 'Triangle' }, { tool: 'rectangle', title: 'Rectangle' },
];
const FIG_2D = [...FIG_CLASSIC, ...SHAPE_2D_KEYS.map(k => ({ tool: k, title: SHAPES_2D[k].title }))];
const FIG_3D = SHAPE_3D_KEYS.map(k => ({ tool: k, title: SHAPES_3D[k].title }));
const FIG_ALL = [...FIG_2D, ...FIG_3D];

const PRINT_SIZES = {
  'A4P': { width: 21,   height: 29.7, label: 'A4',  orient: 'portrait',  display: 'A4 Portrait' },
  'A4L': { width: 29.7, height: 21,   label: 'A4',  orient: 'landscape', display: 'A4 Landscape' },
  'A5P': { width: 14.8, height: 21,   label: 'A5',  orient: 'portrait',  display: 'A5 Portrait' },
  'A5L': { width: 21,   height: 14.8, label: 'A5',  orient: 'landscape', display: 'A5 Landscape' },
  'A6P': { width: 10.5, height: 14.8, label: 'A6',  orient: 'portrait',  display: 'A6 Portrait' },
  'A6L': { width: 14.8, height: 10.5, label: 'A6',  orient: 'landscape', display: 'A6 Landscape' },
};
const PRINT_SIZE_ORDER = ['A4P', 'A4L', 'A5P', 'A5L', 'A6P', 'A6L'];

// Sheet effects — a shading or a pattern laid over the sheet's colour. Each one is written
// twice: once as CSS, for the sheet on screen, and once as canvas drawing, for Save and Print.
// Both take the sheet's width in pixels and size themselves from it — a pattern measured in
// fixed pixels would be a bold check on screen and a barely visible weave in a 2480px export.
// The six specks in one tile of the confetti pattern: across, down, colour.
const CONFETTI_SPECKS = [
  [0.12, 0.18, '#ff6b6b'], [0.44, 0.07, '#ffd93d'], [0.76, 0.24, '#4dabf7'],
  [0.22, 0.61, '#51cf66'], [0.57, 0.80, '#cc5de8'], [0.88, 0.52, '#ff922b'],
];
//
// A note on what belongs here, because the first set of these was not worth having. An effect
// earns its place by doing one of two things: making a photograph sit better on the page (the
// soft shade and the glow, which darken the edges so the picture is what the eye goes to), or
// making the sheet itself useful or cheerful (paper to write on, paper to draw on, and colour
// for its own sake). Anything that is merely a texture — a stripe, a chequerboard — is neither,
// and is not offered. `stripes` and `checks` stay defined, unlisted, so sheets already saved
// with them still open the way they were left.
// Ink for the shading and the patterns: dark on pale paper, pale on dark paper.
const FX_INK = (dark, a) => (dark ? `rgba(255,255,255,${+(a * 1.3).toFixed(3)})` : `rgba(20,30,60,${a})`);
const SHEET_FX = {
  fade: {
    label: 'Fade',
    // Ink, not white: a wash of white is invisible on a white sheet, which is what the first
    // version of this did. Shade deepening downwards reads on any colour.
    css: (cw, dark) => `linear-gradient(180deg, ${FX_INK(dark, 0)} 0%, ${FX_INK(dark, 0.16)} 100%)`,
    paint: (ctx, w, h, dark) => {
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0, FX_INK(dark, 0)); g.addColorStop(1, FX_INK(dark, 0.16));
      ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
    },
  },
  spotlight: {
    label: 'Glow',
    css: (cw, dark) => `radial-gradient(circle at 50% 38%, ${FX_INK(dark, 0)} 26%, ${FX_INK(dark, 0.24)} 100%)`,
    paint: (ctx, w, h, dark) => {
      const g = ctx.createRadialGradient(w / 2, h * 0.38, Math.min(w, h) * 0.26,
                                         w / 2, h * 0.38, Math.max(w, h) * 0.80);
      g.addColorStop(0, FX_INK(dark, 0)); g.addColorStop(1, FX_INK(dark, 0.24));
      ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
    },
  },
  sunrise: {
    label: 'Sunrise',
    css: () => 'linear-gradient(180deg, #ffd9a8 0%, #ffb3c1 52%, #cdb4ff 100%)',
    paint: (ctx, w, h) => {
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0, '#ffd9a8'); g.addColorStop(0.52, '#ffb3c1'); g.addColorStop(1, '#cdb4ff');
      ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
    },
  },
  sky: {
    label: 'Sky',
    css: () => 'linear-gradient(180deg, #7ec8ff 0%, #d9f1ff 70%, #ffffff 100%)',
    paint: (ctx, w, h) => {
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0, '#7ec8ff'); g.addColorStop(0.7, '#d9f1ff'); g.addColorStop(1, '#ffffff');
      ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
    },
  },
  // Paper to write on: ruled lines about 8mm apart on A4, the spacing of a school exercise book.
  lines: {
    label: 'Lines',
    css: (cw, dark) => `repeating-linear-gradient(180deg, ${FX_INK(dark, 0.20)} 0 ${Math.max(1, cw / 620)}px,`
               + ` rgba(0,0,0,0) ${Math.max(1, cw / 620)}px ${cw / 26}px)`,
    paint: (ctx, w, h, dark) => {
      const gap = w / 26;
      ctx.fillStyle = FX_INK(dark, 0.20);
      const t = Math.max(1, w / 620);
      for (let y = gap; y < h; y += gap) ctx.fillRect(0, y - t, w, t);
    },
  },
  // Paper to draw on: one-centimetre squares on A4, which is what the grid guide shows — except
  // this one is part of the sheet, so it is saved and it prints.
  graph: {
    label: 'Graph',
    css: (cw, dark) => `linear-gradient(180deg, ${FX_INK(dark, 0.16)} ${Math.max(1, cw / 700)}px, rgba(0,0,0,0) ${Math.max(1, cw / 700)}px),`
               + `linear-gradient(90deg, ${FX_INK(dark, 0.16)} ${Math.max(1, cw / 700)}px, rgba(0,0,0,0) ${Math.max(1, cw / 700)}px)`,
    size: (cw) => `${cw / 21}px ${cw / 21}px, ${cw / 21}px ${cw / 21}px`,
    paint: (ctx, w, h, dark) => {
      const s = w / 21, t = Math.max(1, w / 700);
      ctx.fillStyle = FX_INK(dark, 0.16);
      for (let x = 0; x <= w; x += s) ctx.fillRect(x, 0, t, h);
      for (let y = 0; y <= h; y += s) ctx.fillRect(0, y, w, t);
    },
  },
  dots: {
    label: 'Dots',
    css: (cw, dark) => `radial-gradient(${FX_INK(dark, 0.16)} 22%, rgba(0,0,0,0) 23%)`,
    size: (cw) => `${cw / 16}px ${cw / 16}px`,
    paint: (ctx, w, h, dark) => {
      const s = w / 16;
      ctx.fillStyle = FX_INK(dark, 0.16);
      for (let y = s / 2; y < h + s; y += s) {
        for (let x = s / 2; x < w + s; x += s) { ctx.beginPath(); ctx.arc(x, y, s * 0.22, 0, Math.PI * 2); ctx.fill(); }
      }
    },
  },
  // Specks of colour thrown across the sheet. The positions are fixed rather than random for a
  // plain reason: the sheet on screen is CSS and the sheet that prints is drawn on a canvas, and
  // the two have to be the same picture. A tile with six known specks in it is the same picture
  // in both, at any size.
  confetti: {
    label: 'Confetti',
    css: (cw) => CONFETTI_SPECKS
      .map(([x, y, c]) => `radial-gradient(circle at ${x * 100}% ${y * 100}%, ${c} 0 ${(cw / 8) * 0.075}px, rgba(0,0,0,0) ${(cw / 8) * 0.075}px)`)
      .join(','),
    size: (cw) => CONFETTI_SPECKS.map(() => `${cw / 8}px ${cw / 8}px`).join(','),
    paint: (ctx, w, h) => {
      const t = w / 8, r = t * 0.075;
      for (let y = 0; y < h + t; y += t) {
        for (let x = 0; x < w + t; x += t) {
          CONFETTI_SPECKS.forEach(([fx, fy, c]) => {
            ctx.fillStyle = c;
            ctx.beginPath(); ctx.arc(x + fx * t, y + fy * t, r, 0, Math.PI * 2); ctx.fill();
          });
        }
      }
    },
  },
  stripes: {
    label: 'Stripes',
    css: (cw) => `repeating-linear-gradient(45deg, rgba(0,0,0,0.10) 0 ${cw / 32}px, rgba(0,0,0,0) ${cw / 32}px ${cw / 16}px)`,
    paint: (ctx, w, h) => {
      const band = w / 32;
      ctx.save();
      ctx.strokeStyle = 'rgba(0,0,0,0.10)'; ctx.lineWidth = band;
      for (let i = -h; i < w + h; i += band * 2) { ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i + h, h); ctx.stroke(); }
      ctx.restore();
    },
  },
  checks: {
    label: 'Checks',
    css: () => 'linear-gradient(45deg, rgba(0,0,0,0.10) 25%, transparent 25% 75%, rgba(0,0,0,0.10) 75%),'
             + 'linear-gradient(45deg, rgba(0,0,0,0.10) 25%, transparent 25% 75%, rgba(0,0,0,0.10) 75%)',
    size: (cw) => `${cw / 12}px ${cw / 12}px, ${cw / 12}px ${cw / 12}px`,
    position: (cw) => `0 0, ${cw / 24}px ${cw / 24}px`,
    paint: (ctx, w, h) => {
      const s = w / 24;
      ctx.fillStyle = 'rgba(0,0,0,0.10)';
      for (let r = 0, y = 0; y < h; r++, y += s) {
        for (let c = 0, x = 0; x < w; c++, x += s) if ((r + c) % 2 === 0) ctx.fillRect(x, y, s, s);
      }
    },
  },
  rainbow: {
    label: 'Rainbow',
    css: () => 'linear-gradient(180deg, #ff9aa2 0 16.6%, #ffdac1 16.6% 33.3%, #fff5ba 33.3% 50%,'
             + ' #b5ead7 50% 66.6%, #a0d8ef 66.6% 83.3%, #cdb4ff 83.3% 100%)',
    paint: (ctx, w, h) => {
      const bands = ['#ff9aa2', '#ffdac1', '#fff5ba', '#b5ead7', '#a0d8ef', '#cdb4ff'];
      bands.forEach((c, i) => { ctx.fillStyle = c; ctx.fillRect(0, Math.floor(h * i / 6), w, Math.ceil(h / 6) + 1); });
    },
  },
};
// What the picker offers, in the order it offers it: the two that help a photograph, the two
// that colour the whole sheet, the two that make paper worth writing and drawing on, then the
// three that are simply nice to look at. Nine, on one grid, with no headings to read.
const SHEET_FX_PICK = ['fade', 'spotlight', 'sunrise', 'sky', 'lines', 'graph', 'dots', 'confetti', 'rainbow'];
// The swatches are 44px wide but they stand for a 21cm sheet, so a pattern measured against the
// sheet's width would be too fine to see in one. They are drawn as a crop of the sheet instead:
// the same pattern at the same relative size, shown about five times closer.
const FX_PREVIEW_CW = 210;
// Is the sheet a dark one? Ink drawn on paper has to be dark; the same ink on a dark sheet is
// invisible, and every swatch of it comes out as a black rectangle — which is exactly what the
// shading and the patterns looked like once someone chose the dark paper. So the ink turns pale
// when the paper is dark, and the effects go on working instead of disappearing.
const isDarkSheet = (bg) => {
  const c = hexToRgb(String(bg || ''));
  if (!c) return false;
  return (0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b) < 118;
};
// The sheet's background as CSS: colour underneath, effect on top. One place, so the sheet, the
// little previews in the menu and anything else that shows a sheet all agree.
const sheetFxStyle = (fxKey, cw, dark = false) => {
  const fx = SHEET_FX[fxKey];
  if (!fx) return null;
  return {
    backgroundImage: fx.css(cw, dark),
    ...(fx.size ? { backgroundSize: fx.size(cw) } : {}),
    ...(fx.position ? { backgroundPosition: fx.position(cw) } : {}),
  };
};

// Sheet background presets. "None" is the paper itself — nothing is painted, and a saved PNG
// keeps its transparency; the rest are painted behind everything on the sheet and come out in
// Save and Print. Any other colour comes from the palette underneath these.
// Paper was #fdf4e3 — two per cent off white, which is to say indistinguishable from it in a
// 26px swatch. If a choice is offered it has to look like a choice, so it is a real cream now.
const BG_THEMES = [
  { value: 'transparent', label: 'None',  title: 'No background — transparent where nothing is drawn' },
  { value: '#ffffff',     label: 'White', title: 'White sheet' },
  { value: '#f3e5c8',     label: 'Paper', title: 'Warm cream paper' },
  { value: '#22242a',     label: 'Dark',  title: 'Dark sheet' },
];

// ── Mixing a colour ─────────────────────────────────────────────────────────────────────────
// One rail, holding black, white, every grey between them and the whole spectrum after. The
// same list of stops both draws the gradient and answers "what colour is under this point", so
// the colour you see under your finger is exactly the colour you get — which is not true of a
// picker where the gradient is CSS and the answer comes from somewhere else.
const MIX_STOPS = [
  [0.00, [0, 0, 0]],
  [0.10, [255, 255, 255]],
  [0.18, [255, 0, 0]],
  [0.31, [255, 255, 0]],
  [0.45, [0, 255, 0]],
  [0.59, [0, 255, 255]],
  [0.73, [0, 0, 255]],
  [0.87, [255, 0, 255]],
  [1.00, [255, 0, 0]],
];
const MIX_RAIL_CSS = `linear-gradient(90deg, ${
  MIX_STOPS.map(([p, c]) => `rgb(${c[0]},${c[1]},${c[2]}) ${(p * 100).toFixed(1)}%`).join(', ')})`;
const chan = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
const rgbHex = (r, g, b) => `#${chan(r)}${chan(g)}${chan(b)}`;
const mixColorAt = (t) => {
  const x = Math.max(0, Math.min(1, t));
  for (let i = 1; i < MIX_STOPS.length; i++) {
    const [p1, c1] = MIX_STOPS[i - 1];
    const [p2, c2] = MIX_STOPS[i];
    if (x <= p2) {
      const f = p2 === p1 ? 0 : (x - p1) / (p2 - p1);
      return rgbHex(c1[0] + (c2[0] - c1[0]) * f, c1[1] + (c2[1] - c1[1]) * f, c1[2] + (c2[2] - c1[2]) * f);
    }
  }
  return '#ffffff';
};
// Lighter and darker versions of whatever was picked, because a sheet is rarely a pure hue.
// The two circles a person makes for themselves: a colour mixed in the mixer, and one picked from
// the sheet. They are palette keys like the built-in colours, so they open the same shades.
const CUSTOM_KEY = '__custom';
const PICK_KEY = '__pick';
const DEFAULT_CUSTOM = '#3A86FF';

const shadesOfColor = (hex) => {
  const c = hexToRgb(hex) || { r: 255, g: 255, b: 255 };
  const towards = (to, f) => rgbHex(c.r + (to - c.r) * f, c.g + (to - c.g) * f, c.b + (to - c.b) * f);
  return [towards(255, 0.78), towards(255, 0.54), towards(255, 0.28), hex,
    towards(0, 0.24), towards(0, 0.48), towards(0, 0.72)];
};

// ── Selfie Mode ─────────────────────────────────────────────────────────────────────────────
// The first version of this offered a heart, a star and a speech bubble, which is not what
// anyone means by a selfie mode. What people mean is two things: something done to the face —
// smoothed, brightened, drained of colour, flipped the right way round — and something put ON
// the face: shades, a cap, a moustache, a pair of dog ears.
//
// Both are here. The effects are real work on the picture's own pixels rather than a CSS filter
// on the screen, so what is saved and printed is what was seen. The props are ordinary objects
// on the sheet — drawn as vectors, so they stay sharp at any size — dropped where a face
// usually is and then dragged, turned and resized like anything else.
const SELFIE_FX = {
  beauty:   { label: 'Beauty',   filter: 'brightness(1.07) saturate(1.06) contrast(1.02)', smooth: 0.45 },
  bright:   { label: 'Bright',   filter: 'brightness(1.18) contrast(1.04)' },
  mono:     { label: 'B & W',    filter: 'grayscale(1) contrast(1.14)' },
  warm:     { label: 'Warm',     filter: 'sepia(0.34) saturate(1.30) brightness(1.04)' },
  cool:     { label: 'Cool',     filter: 'saturate(1.12) hue-rotate(-12deg) brightness(1.03)', wash: 'rgba(60,120,255,0.10)' },
  pop:      { label: 'Pop',      filter: 'saturate(1.60) contrast(1.20)' },
  faded:    { label: 'Faded',    filter: 'saturate(0.70) contrast(0.92) brightness(1.08)', wash: 'rgba(255,226,190,0.14)' },
  vignette: { label: 'Vignette', filter: '', vignette: 0.55 },
  negative: { label: 'Negative', filter: 'invert(1)' },
  xray:     { label: 'X-ray',    filter: 'invert(1) grayscale(1) contrast(1.25)' },
  noir:     { label: 'Noir',     filter: 'grayscale(1) contrast(1.55) brightness(0.95)', vignette: 0.45 },
  sepia:    { label: 'Sepia',    filter: 'sepia(1) contrast(1.05) brightness(1.03)' },
  dream:    { label: 'Dream',    filter: 'saturate(1.15) brightness(1.06)', smooth: 0.62, wash: 'rgba(255,190,230,0.10)' },
  neon:     { label: 'Neon',     filter: 'saturate(2.2) contrast(1.35) hue-rotate(-18deg)', vignette: 0.4 },
  frost:    { label: 'Frost',    filter: 'saturate(0.85) brightness(1.08) hue-rotate(-18deg)', wash: 'rgba(140,200,255,0.16)' },
  sun:      { label: 'Sunburst', filter: 'saturate(1.25) brightness(1.05)', wash: 'rgba(255,160,40,0.16)' },
};
const SELFIE_FX_ORDER = [
  'beauty', 'bright', 'mono', 'warm', 'cool',
  'pop', 'faded', 'vignette', 'noir', 'sepia',
  'negative', 'xray', 'dream', 'neon', 'frost', 'sun',
];

// Faces and hearts for the smiley button. The Stickers button used to open the stamp picker,
// which is a library of pictures somebody has to have uploaded first — so on a fresh install it
// opened an empty box, which is what "the smiley doesn't work" was. These need no library: they
// are letters, placed as text, so they move, resize, turn, save and print like any other words
// on the sheet. The hearts are in here too, which is why there is no longer a heart button of
// its own beside it.
const SELFIE_EMOJI = [
  '😀', '😄', '😁', '😆', '😅', '🤣', '😊', '🙂',
  '😉', '😍', '🥰', '😘', '😎', '🤩', '🥳', '😛',
  '😜', '🤪', '🤗', '🤔', '🤨', '😐', '🙄', '😏',
  '😴', '😢', '😭', '😤', '😡', '🤯', '😱', '🥶',
  '🥵', '😇', '🤠', '🤡', '👻', '💀', '👽', '🤖',
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍',
  '💖', '💕', '💞', '💘', '💔', '💯', '🔥', '✨',
  '⭐', '🌟', '🎉', '🎊', '👑', '👍', '👌', '🙏',
];

/**
 * ── An emoji is a picture, not writing ──────────────────────────────────────────────────────
 *
 * These are placed as text shapes, which is what lets them move, turn, save and print like
 * anything else on the sheet. But a text shape deliberately does NOT scale with its box: drag a
 * paragraph wider and it takes more words per line, it does not grow bigger letters. Applied to
 * a smiley that rule is nonsense — the box stretched and the face stayed exactly where it was,
 * the size it was, pinned to the top-left corner of a box several times its size.
 *
 * So a text shape that is nothing but emoji is treated as a picture and grows with its box. The
 * flag is written on everything placed from the smiley list; the test is there for the ones
 * already sitting on people's sheets, which have no flag: pictographs and not one letter or
 * digit among them is not something anybody typed as writing.
 */
// How the box is cut around the glyph, as multiples of the type size. Measured rather than
// guessed: an emoji drawn at 41px paints about 56 across and 46 down, because the character
// carries its own side bearing. The old 1.15 was narrower than the face itself, which is why a
// selection box could cut through the cheeks.
const EMOJI_BOX_W = 1.4, EMOJI_BOX_H = 1.2;
const isEmojiGlyph = (s) => !!s && s.type === 'text' && (
  s.emojiGlyph === true ||
  (typeof s.text === 'string' && /\p{Extended_Pictographic}/u.test(s.text) && !/[\p{L}\p{N}]/u.test(s.text))
);
/** The type size that fills a box this big — the exact inverse of how the box was cut. */
const emojiFsForBox = (wPx, hPx) =>
  Math.max(8, Math.round(Math.min(hPx / EMOJI_BOX_H, wPx / EMOJI_BOX_W)));

/**
 * An SVG as a data: URL that is also safe inside CSS url(...).
 *
 * encodeURIComponent leaves brackets alone — they are legal in a URL — but a bracket is not
 * legal inside an unquoted CSS url(), where the first ")" ends the value. The two props whose
 * artwork contains a rotate(...) were therefore producing a declaration the browser could not
 * parse, and dropping it: background-image resolved to "none" and the tile came out blank while
 * the other five, whose artwork happens to contain no brackets, were fine. Encoding them costs
 * nothing anywhere else — an <img src> reads %28 and %29 back as brackets.
 */
const svgProp = (s) => 'data:image/svg+xml;utf8,'
  + encodeURIComponent(s).replace(/\(/g, '%28').replace(/\)/g, '%29');
// vw/vh are the artwork's own proportions; w is how wide it lands as a fraction of the photo,
// and cx/cy where its centre goes in that photo — a cap above the hair, shades across the eyes,
// a moustache under the nose. A face is not always where a face usually is, which is why every
// one of these is a normal object the moment it lands: drag it onto the face and let go.
const SELFIE_PROPS = [
  {
    key: 'shades', label: 'Shades', vw: 320, vh: 120, w: 0.66, cx: 0.50, cy: 0.43,
    url: svgProp(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 120"><g fill="#16161c"><rect x="8" y="24" width="130" height="74" rx="30"/><rect x="182" y="24" width="130" height="74" rx="30"/><rect x="134" y="44" width="52" height="14" rx="7"/><rect x="0" y="28" width="16" height="13" rx="6"/><rect x="304" y="28" width="16" height="13" rx="6"/></g><g fill="#fff" opacity=".24"><rect x="24" y="36" width="46" height="20" rx="10" transform="rotate(-14 47 46)"/><rect x="198" y="36" width="46" height="20" rx="10" transform="rotate(-14 221 46)"/></g></svg>`),
  },
  {
    key: 'cap', label: 'Cap', vw: 320, vh: 170, w: 0.86, cx: 0.50, cy: 0.15,
    url: svgProp(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 170"><path d="M46 118a114 114 0 0 1 228 0z" fill="#2a9d8f"/><path d="M160 10v108" stroke="#1f7a70" stroke-width="5" opacity=".6"/><ellipse cx="160" cy="142" rx="152" ry="26" fill="#1f7a70"/><rect x="40" y="112" width="240" height="22" rx="11" fill="#248b80"/><circle cx="160" cy="16" r="12" fill="#1f7a70"/></svg>`),
  },
  {
    key: 'crown', label: 'Crown', vw: 320, vh: 150, w: 0.58, cx: 0.50, cy: 0.07,
    url: svgProp(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 150"><path d="M20 132V40l60 45 80-67 80 67 60-45v92z" fill="#ffc233"/><rect x="20" y="112" width="280" height="24" rx="6" fill="#e0a11a"/><circle cx="20" cy="34" r="13" fill="#ff5d8f"/><circle cx="160" cy="12" r="14" fill="#ff5d8f"/><circle cx="300" cy="34" r="13" fill="#ff5d8f"/><circle cx="80" cy="124" r="9" fill="#4dabf7"/><circle cx="160" cy="124" r="9" fill="#51cf66"/><circle cx="240" cy="124" r="9" fill="#4dabf7"/></svg>`),
  },
  {
    key: 'tache', label: 'Moustache', vw: 320, vh: 120, w: 0.36, cx: 0.50, cy: 0.63,
    url: svgProp(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 120"><path d="M160 34c18-22 56-34 90-24 38 11 58 46 66 74-30-18-62-24-88-14-24 9-46 26-68 26s-44-17-68-26c-26-10-58-4-88 14 8-28 28-63 66-74 34-10 72 2 90 24z" fill="#3b2b20"/></svg>`),
  },
  {
    key: 'ears', label: 'Dog ears', vw: 320, vh: 170, w: 0.98, cx: 0.50, cy: 0.16,
    url: svgProp(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 170"><g fill="#8b5a2b"><path d="M62 10c-38 0-58 36-56 78 2 42 22 74 46 74 18 0 28-18 26-44-2-30-6-54 2-76 6-18 2-32-18-32z"/><path d="M258 10c38 0 58 36 56 78-2 42-22 74-46 74-18 0-28-18-26-44 2-30 6-54-2-76-6-18-2-32 18-32z"/></g><g fill="#cd8f55"><path d="M66 44c-19 4-27 29-25 57 2 27 11 44 21 44 7 0 11-11 10-28-2-23-4-42 1-55 3-12 2-20-7-18z"/><path d="M254 44c19 4 27 29 25 57-2 27-11 44-21 44-7 0-11-11-10-28 2-23 4-42-1-55-3-12-2-20 7-18z"/></g></svg>`),
  },
  {
    key: 'snout', label: 'Dog nose', vw: 220, vh: 150, w: 0.32, cx: 0.50, cy: 0.60,
    url: svgProp(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 220 150"><ellipse cx="110" cy="96" rx="86" ry="48" fill="#fffaf6" opacity=".95"/><path d="M110 40c26 0 44 16 44 30s-20 24-44 24-44-10-44-24 18-30 44-30z" fill="#1b1b1f"/><path d="M110 94v18M110 112c0 10-10 16-20 14M110 112c0 10 10 16 20 14" stroke="#1b1b1f" stroke-width="7" fill="none" stroke-linecap="round"/><g stroke="#1b1b1f" stroke-width="4" stroke-linecap="round" opacity=".65"><path d="M28 84H2M30 100H4M34 116H10M192 84h26M190 100h26M186 116h24"/></g></svg>`),
  },
  {
    key: 'bunny', label: 'Bunny ears', vw: 240, vh: 210, w: 0.60, cx: 0.50, cy: 0.02,
    url: svgProp(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 210"><g fill="#fbf7f5" stroke="#e3d8d3" stroke-width="4"><ellipse cx="84" cy="96" rx="31" ry="90" transform="rotate(-12 84 96)"/><ellipse cx="156" cy="96" rx="31" ry="90" transform="rotate(12 156 96)"/></g><g fill="#ffb3c6"><ellipse cx="84" cy="98" rx="15" ry="63" transform="rotate(-12 84 98)"/><ellipse cx="156" cy="98" rx="15" ry="63" transform="rotate(12 156 98)"/></g></svg>`),
  },
];

// Hex colour -> {r, g, b}
const hexToRgb = (hex) => {
  const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return r ? { r: parseInt(r[1], 16), g: parseInt(r[2], 16), b: parseInt(r[3], 16) } : null;
};

// The side wall of a raised letter: the letter's own colour taken towards black, so the face
// stands off its own shadow. A colour that is already near black has no darker version to go to
// — black letters would simply thicken — so those are lifted towards grey instead, which is the
// same effect the other way up. Anything that is not a plain #rrggbb (a named colour, a
// gradient) falls back to a neutral shadow that works against every hue.
const text3DWall = (hex) => {
  const c = hexToRgb(hex);
  if (!c) return 'rgba(0,0,0,0.45)';
  const lum = (0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255;
  const mix = lum < 0.28
    ? (v) => Math.round(v + (255 - v) * 0.45)   // too dark to darken — raise it
    : (v) => Math.round(v * 0.55);
  return `rgb(${mix(c.r)}, ${mix(c.g)}, ${mix(c.b)})`;
};
// How far a raised letter stands off the page, in pixels, for a given font size.
const text3DDepth = (fs) => Math.max(2, Math.round(fs * 0.14));

// Stack-based flood fill on a flat Uint8ClampedArray (RGBA, widthxheight).
// Returns the number of pixels filled, or 0 if nothing changed.
const floodFill = (data, width, height, sx, sy, fill) => {
  const startI = (sy * width + sx) * 4;
  const tR = data[startI], tG = data[startI + 1], tB = data[startI + 2], tA = data[startI + 3];
  if (tR === fill.r && tG === fill.g && tB === fill.b && tA === 255) return 0;
  const tol = 15;
  // For transparent-source fills: require exact alpha=0 match so the fill never
  // touches anti-aliased stroke edge pixels (alpha 1-20), giving a tight bbox.
  const tolA = tA < 10 ? 0 : tol;
  const matches = (i) => (
    Math.abs(data[i+3] - tA) <= tolA &&
    Math.abs(data[i]   - tR) <= tol &&
    Math.abs(data[i+1] - tG) <= tol &&
    Math.abs(data[i+2] - tB) <= tol
  );
  const visited = new Uint8Array(width * height);
  const stack = [sx + sy * width];
  let count = 0;
  while (stack.length > 0) {
    const pos = stack.pop();
    if (visited[pos]) continue;
    visited[pos] = 1;
    const i = pos * 4;
    if (!matches(i)) continue;
    data[i] = fill.r; data[i + 1] = fill.g; data[i + 2] = fill.b; data[i + 3] = 255;
    count++;
    const x = pos % width, y = Math.floor(pos / width);
    if (x > 0)          stack.push(pos - 1);
    if (x < width - 1)  stack.push(pos + 1);
    if (y > 0)          stack.push(pos - width);
    if (y < height - 1) stack.push(pos + width);
  }
  return count;
};

// Shared core of the paint-bucket fill: figures out the region enclosed around (px, py) —
// device pixels of ctx's canvas — and returns a small canvas holding just that region painted
// with `color` (fully transparent elsewhere), or null when there's nothing closed to fill.
// `wallsCanvas`, when given, is composited in as EXTRA opaque boundary (same size as ctx's
// canvas) that counts as a wall but is never itself painted or persisted -- this is how a
// vector shape (drawn separately from the ink layer, so it has no pixels of its own here)
// still blocks the fill from leaking past it. Anti-aliased stroke edges count as part of the
// region so the fill paints under them and the outline keeps its look. Works at the canvas's
// own resolution up to ~4 MP, on a downscaled copy above that (a 600-dpi export).
const computeFillMask = (ctx, px, py, color, wallsCanvas) => {
  const W = ctx.canvas.width, H = ctx.canvas.height;
  if (!W || !H) return null;
  const s = Math.min(1, Math.sqrt(4_000_000 / (W * H)));
  const wW = Math.max(1, Math.round(W * s)), wH = Math.max(1, Math.round(H * s));
  let src = ctx;
  if (s < 1 || wallsCanvas) {
    // A working copy is required whenever we need to blend in extra "wall" pixels (vector
    // shapes) -- never read/write ctx itself in that case, so the real canvas ends up with
    // only the flood-filled colour, never the shape pixels used just to find the boundary.
    const work = document.createElement('canvas'); work.width = wW; work.height = wH;
    const wc = work.getContext('2d', { willReadFrequently: true });
    wc.drawImage(ctx.canvas, 0, 0, wW, wH);
    if (wallsCanvas) wc.drawImage(wallsCanvas, 0, 0, wW, wH);
    src = wc;
  }
  const sx = Math.round(px * s), sy = Math.round(py * s);
  if (sx < 0 || sy < 0 || sx >= wW || sy >= wH) return null;
  const d = src.getImageData(0, 0, wW, wH).data;
  const i0 = (sy * wW + sx) * 4;
  const tr = d[i0], tg = d[i0 + 1], tb = d[i0 + 2], ta = d[i0 + 3];
  const clearSeed = ta < 40;

  // Two separately drawn boundaries that visually meet (a hand-drawn loop touching another
  // shape's edge, say) are rarely pixel-perfect -- there's often a 1-2 px gap between them
  // that would otherwise leak the fill straight out to the rest of the sheet. Close small
  // gaps in the "wall" (opaque, alpha>=200) mask first, dilate then erode, so a drawing that
  // LOOKS closed behaves as closed. The radius is tiny relative to the canvas, so genuinely
  // separate strokes (an intentionally open shape, unrelated drawings) stay separate.
  let wallClosed = null;
  if (clearSeed) {
    const R = Math.min(6, Math.max(2, Math.round(Math.min(wW, wH) * 0.003)));
    const wall = new Uint8Array(wW * wH);
    for (let p = 0; p < wW * wH; p++) if (d[p * 4 + 3] >= 200) wall[p] = 1;
    const dilH = new Uint8Array(wW * wH);
    for (let y = 0; y < wH; y++) {
      const rowBase = y * wW;
      for (let x = 0; x < wW; x++) {
        const xMin = Math.max(0, x - R), xMax = Math.min(wW - 1, x + R);
        for (let nx = xMin; nx <= xMax; nx++) { if (wall[rowBase + nx]) { dilH[rowBase + x] = 1; break; } }
      }
    }
    const dil = new Uint8Array(wW * wH);
    for (let x = 0; x < wW; x++) {
      for (let y = 0; y < wH; y++) {
        const yMin = Math.max(0, y - R), yMax = Math.min(wH - 1, y + R);
        for (let ny = yMin; ny <= yMax; ny++) { if (dilH[ny * wW + x]) { dil[y * wW + x] = 1; break; } }
      }
    }
    const erH = new Uint8Array(wW * wH);
    for (let y = 0; y < wH; y++) {
      const rowBase = y * wW;
      for (let x = 0; x < wW; x++) {
        if (!dil[rowBase + x]) continue;
        const xMin = Math.max(0, x - R), xMax = Math.min(wW - 1, x + R);
        let all = 1;
        for (let nx = xMin; nx <= xMax; nx++) { if (!dil[rowBase + nx]) { all = 0; break; } }
        erH[rowBase + x] = all;
      }
    }
    wallClosed = new Uint8Array(wW * wH);
    for (let x = 0; x < wW; x++) {
      for (let y = 0; y < wH; y++) {
        if (!erH[y * wW + x]) continue;
        const yMin = Math.max(0, y - R), yMax = Math.min(wH - 1, y + R);
        let all = 1;
        for (let ny = yMin; ny <= yMax; ny++) { if (!erH[ny * wW + x]) { all = 0; break; } }
        wallClosed[y * wW + x] = all;
      }
    }
  }

  const ok = clearSeed
    ? (i) => !wallClosed[i >> 2]
    : (i) => Math.abs(d[i + 3] - ta) <= 60 && (Math.abs(d[i] - tr) + Math.abs(d[i + 1] - tg) + Math.abs(d[i + 2] - tb)) <= 90;
  const mask = new Uint8Array(wW * wH);
  const stack = [sy * wW + sx];
  let count = 0;
  while (stack.length) {
    const p = stack.pop();
    if (mask[p]) continue;
    if (!ok(p * 4)) continue;
    mask[p] = 1; count++;
    const x = p % wW, y = (p - x) / wW;
    if (x > 0) stack.push(p - 1);
    if (x < wW - 1) stack.push(p + 1);
    if (y > 0) stack.push(p - wW);
    if (y < wH - 1) stack.push(p + wW);
  }
  if (!count || count > wW * wH * 0.97) return null;
  // A region that runs off the edge of the sheet AND covers most of it is the outside of the
  // drawing, not the inside of anything — the tap missed the closed object.
  let touchesEdge = false;
  for (let x = 0; x < wW && !touchesEdge; x++) if (mask[x] || mask[(wH - 1) * wW + x]) touchesEdge = true;
  for (let y = 0; y < wH && !touchesEdge; y++) if (mask[y * wW] || mask[y * wW + wW - 1]) touchesEdge = true;
  if (touchesEdge && count > wW * wH * 0.5) return null;
  const rgb = hexToRgb(color) || { r: 0, g: 0, b: 0 };
  const mc = document.createElement('canvas'); mc.width = wW; mc.height = wH;
  const mctx = mc.getContext('2d');
  const md = mctx.createImageData(wW, wH);
  for (let p = 0; p < wW * wH; p++) if (mask[p]) { const q = p * 4; md.data[q] = rgb.r; md.data[q + 1] = rgb.g; md.data[q + 2] = rgb.b; md.data[q + 3] = 255; }
  mctx.putImageData(md, 0, 0);
  return { mc, wW, wH, W, H, s, clearSeed };
};

// Fill the region around (px, py) live onto ctx itself. Kept for replaying OLD 'fill' paths
// (saved before fills became precomputed 'fillmask' bitmaps -- see handleFillClick) so
// existing drawings still replay the same way. Returns false when there's nothing to fill.
const floodFillLayer = (ctx, px, py, color) => {
  const r = computeFillMask(ctx, px, py, color);
  if (!r) return false;
  const { mc, W, H, s, clearSeed } = r;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalCompositeOperation = clearSeed ? 'destination-over' : 'source-over';
  ctx.imageSmoothingEnabled = s < 1;
  ctx.drawImage(mc, 0, 0, W, H);
  ctx.restore();
  return true;
};

// Convert RGB to hex colour string
const rgbToHex = (r, g, b) => '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');

// Replace every non-transparent pixel in a fill-shape PNG with a new colour.
const recolourFill = (dataUrl, newRgb) => new Promise(resolve => {
  const img = new Image();
  img.onload = () => {
    const c = document.createElement('canvas');
    c.width = img.width; c.height = img.height;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0);
    const id = ctx.getImageData(0, 0, c.width, c.height);
    const d = id.data;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] > 0) { d[i] = newRgb.r; d[i + 1] = newRgb.g; d[i + 2] = newRgb.b; }
    }
    ctx.putImageData(id, 0, 0);
    resolve(c.toDataURL('image/png'));
  };
  img.src = dataUrl;
});

Modal.setAppElement('#root');

// Render a single drawing path (pen, eraser, or spray) onto a canvas context.
// scaleX/scaleY map from the path's original canvas size to the destination canvas size.
// Mask paths (an auto-cropped drawing lifted off the sheet) erase through a PNG. Decoded once.
const _maskImgCache = new Map();
// Hardened eraser stamps, keyed by path id + render scale (see the eraser branch below).
const _eraserStampCache = new Map();
const renderDrawingPath = (ctx, path, scaleX, scaleY) => {
  if (path.type === 'mask') {
    let img = _maskImgCache.get(path.mask);
    if (!img) {
      img = new Image();
      _maskImgCache.set(path.mask, img);
      img.onload = () => window.dispatchEvent(new Event('is-mask-ready'));
      img.src = path.mask;
    }
    if (!img.complete || !img.naturalWidth) return;
    ctx.globalCompositeOperation = 'destination-out';
    ctx.drawImage(img, path.x * scaleX, path.y * scaleY, path.w * scaleX, path.h * scaleY);
    ctx.globalCompositeOperation = 'source-over';
    return;
  }
  if (path.type === 'fill') {
    // A fill is replayed in order on whatever is drawn below it, at whatever resolution.
    // Kept only for OLD fills made before fills became precomputed bitmaps (see 'fillmask'
    // below) -- a live re-flood-fill here has no idea where any vector shape's edges are,
    // since shapes render on a separate SVG layer, never onto this canvas.
    const m = ctx.getTransform();
    const p = m.transformPoint(new DOMPoint(path.x * scaleX, path.y * scaleY));
    floodFillLayer(ctx, p.x, p.y, path.color);
    return;
  }
  if (path.type === 'fillmask') {
    // A paint-bucket fill, baked to a fixed bitmap at the moment it was applied -- computed
    // then with full knowledge of both the ink layer AND any vector shapes forming its
    // boundary (handleFillClick), which a live replay on this canvas alone can't see. Same
    // decode-once caching as 'mask' paths, but painted normally (source-over) instead of
    // erasing.
    let img = _maskImgCache.get(path.mask);
    if (!img) {
      img = new Image();
      _maskImgCache.set(path.mask, img);
      img.onload = () => window.dispatchEvent(new Event('is-mask-ready'));
      img.src = path.mask;
    }
    if (!img.complete || !img.naturalWidth) return;
    ctx.globalCompositeOperation = 'source-over';
    ctx.drawImage(img, path.x * scaleX, path.y * scaleY, path.w * scaleX, path.h * scaleY);
    return;
  }
  if (path.type === 'spray') {
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = path.color;
    const dotR = Math.max(0.3, (path.size || 5) * 0.10 * scaleX);
    for (const pt of path.path) {
      ctx.beginPath();
      ctx.arc(pt.x * scaleX, pt.y * scaleY, dotR, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalCompositeOperation = 'source-over';
    return;
  }
  if (path.type === 'pencil') {
    if (path.path.length === 1) {          // a tap is a dot, same as the pen
      const p0 = path.path[0];
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = path.color;
      ctx.beginPath();
      ctx.arc(p0.x * scaleX, p0.y * scaleY, Math.max(0.5, (path.size * scaleX) / 2), 0, Math.PI * 2);
      ctx.fill();
      return;
    }
    if (path.path.length < 2) return;
    ctx.globalCompositeOperation = 'source-over';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = path.color;
    const size = path.size * scaleX;
    // More strands for richer pencil effect
    const strands = Math.max(3, Math.min(11, Math.ceil(size * 1.2)));
    const strandW = Math.max(0.3, size / strands) * 0.85;
    // Add slight randomness to strand positions for natural look
    for (let s = 0; s < strands; s++) {
      const offsetFactor = (s - (strands - 1) / 2);
      const dx = offsetFactor * size * 0.18 + (Math.random() - 0.5) * size * 0.08;
      ctx.beginPath();
      ctx.moveTo(path.path[0].x * scaleX + dx, path.path[0].y * scaleY);
      for (let i = 1; i < path.path.length; i++) {
        ctx.lineTo(path.path[i].x * scaleX + dx, path.path[i].y * scaleY);
      }
      // Gradient opacity: centre is darkest, edges fade
      const mid = (strands - 1) / 2;
      const dist = Math.abs(s - mid) / (mid || 1);
      ctx.globalAlpha = 0.85 - dist * 0.55;
      ctx.lineWidth = strandW;
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    return;
  }
  if (path.color === 'eraser') {
    // destination-out removes dst x (1 - a), so stroking straight onto the layer erased an
    // antialiased edge pixel only by its own partial alpha and left a x (1 - a) of it behind
    // -- up to 25%. That faint rim is why rubbing at a mark never quite cleared it, however
    // many passes. Stamp the stroke into its own buffer, force every touched pixel to full
    // alpha, and cut with THAT: what the eraser covers is gone, completely.
    if (!path.path || !path.path.length) return;
    const lw = Math.max(1, (path.size || 5) * scaleX);
    let exMin = Infinity, eyMin = Infinity, exMax = -Infinity, eyMax = -Infinity;
    for (const pt of path.path) {
      const px = pt.x * scaleX, py = pt.y * scaleY;
      if (px < exMin) exMin = px; if (px > exMax) exMax = px;
      if (py < eyMin) eyMin = py; if (py > eyMax) eyMax = py;
    }
    if (!isFinite(exMin) || !isFinite(eyMin)) return;
    const pad = Math.ceil(lw / 2) + 2;
    const ox = Math.floor(exMin - pad), oy = Math.floor(eyMin - pad);
    const ow = Math.ceil(exMax + pad) - ox, oh = Math.ceil(eyMax + pad) - oy;
    if (ow <= 0 || oh <= 0) return;
    // The stroke in progress has no id yet and changes every frame, so it is never cached.
    const key = path.id ? `${path.id}|${Math.round(scaleX * 1000)}|${ow}x${oh}` : null;
    let stamp = key ? _eraserStampCache.get(key) : null;
    if (!stamp) {
      stamp = document.createElement('canvas');
      stamp.width = ow; stamp.height = oh;
      const sctx = stamp.getContext('2d', { willReadFrequently: true });
      sctx.strokeStyle = '#000';
      sctx.lineWidth = lw;
      sctx.lineCap = 'round';
      sctx.lineJoin = 'round';
      sctx.beginPath();
      sctx.moveTo(path.path[0].x * scaleX - ox, path.path[0].y * scaleY - oy);
      if (path.path.length === 1) sctx.lineTo(path.path[0].x * scaleX - ox + 0.01, path.path[0].y * scaleY - oy);
      else for (let i = 1; i < path.path.length; i++) sctx.lineTo(path.path[i].x * scaleX - ox, path.path[i].y * scaleY - oy);
      sctx.stroke();
      const sd = sctx.getImageData(0, 0, ow, oh);
      for (let i = 3; i < sd.data.length; i += 4) if (sd.data[i] > 0) sd.data[i] = 255;
      sctx.putImageData(sd, 0, 0);
      if (key) {
        if (_eraserStampCache.size > 120) _eraserStampCache.clear();
        _eraserStampCache.set(key, stamp);
      }
    }
    ctx.globalCompositeOperation = 'destination-out';
    ctx.drawImage(stamp, ox, oy);
    ctx.globalCompositeOperation = 'source-over';
    return;
  }
  // A single touch leaves one point, and a line needs two — so a tap used to draw nothing at
  // all and read as the canvas simply not noticing the pen. One point is a dot: the same
  // colour, the width of the brush, which is what a pen does when you touch it down and lift.
  if (path.path.length === 1) {
    const p0 = path.path[0];
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = path.color;
    ctx.beginPath();
    ctx.arc(p0.x * scaleX, p0.y * scaleY, Math.max(0.5, (path.size * scaleX) / 2), 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  if (path.path.length < 2) return;
  ctx.beginPath();
  ctx.moveTo(path.path[0].x * scaleX, path.path[0].y * scaleY);
  for (let i = 1; i < path.path.length; i++) {
    ctx.lineTo(path.path[i].x * scaleX, path.path[i].y * scaleY);
  }
  ctx.globalCompositeOperation = 'source-over';
  ctx.strokeStyle = path.color;
  ctx.lineWidth = path.size * scaleX;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke();
  ctx.globalCompositeOperation = 'source-over';
};

// Draw every drawing path onto ctx through a separate transparent layer, so flood fills see
// only the drawing (not the images under it) — exactly like the live sheet. The sheet is
// renderW×renderH output px for the content box (offXpct/offYpct, contentWpct/contentHpct in
// sheet percent); a whole-sheet export passes the defaults.
const drawDrawingLayer = (ctx, paths, renderW, renderH, offXpct = 0, offYpct = 0, contentWpct = 100, contentHpct = 100) => {
  if (!paths || !paths.length) return;
  const layer = document.createElement('canvas');
  layer.width = ctx.canvas.width; layer.height = ctx.canvas.height;
  const lctx = layer.getContext('2d', { willReadFrequently: true });
  const sheetW = renderW * 100 / contentWpct, sheetH = renderH * 100 / contentHpct;
  const offsetX = (offXpct / 100) * sheetW, offsetY = (offYpct / 100) * sheetH;
  paths.forEach(path => {
    const pCanvasW = path.canvasW || 800, pCanvasH = path.canvasH || (800 * A4_RATIO);
    lctx.save();
    lctx.translate(-offsetX, -offsetY);
    renderDrawingPath(lctx, path, sheetW / pCanvasW, sheetH / pCanvasH);
    lctx.restore();
  });
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalCompositeOperation = 'source-over';
  ctx.drawImage(layer, 0, 0);
  ctx.restore();
};

// Module-level cache: survives modal open/close, cleared on upload/delete/rename
let _bucketImagesCache = null;
let _bucketImagesCacheKey = '';

/**
 * A picture turned in space, the way the 3D figures turn — with a little thickness, so it turns
 * like a solid piece (a printed board, a cake topper) and not like a sheet of paper.
 *
 * Same projection as the figures (lib/shape3d.js): straight down the Z axis, no perspective,
 * spin (ry) first and then tilt (rx). With no perspective every flat layer of the picture lands
 * on the sheet as one affine transform, so the whole piece is a stack of ordinary drawImage
 * calls: its edge first, as copies of the picture stepped back through the thickness and
 * darkened, then the face on top. Because each copy is the picture itself, a cut-out's edge
 * follows the cut-out's own shape — the side of a cake, not the side of a rectangle.
 *
 * Turned past side-on, the face is at the back: what shows is the reverse of the piece.
 *
 * The answer is the picture's own pixel size with the turned piece centred in it, shrunk only
 * when the turn would not otherwise fit. So the object keeps its place and its box on the sheet,
 * and save, print and share — which all simply draw the picture — need nothing new.
 */
const TURN_DEPTH = 0.045;   // thickness, as a fraction of the picture's shorter side
const renderTurnedPicture = (el, rxDeg, ryDeg, maxSide = 2048) => {
  const nw = el.naturalWidth || el.width, nh = el.naturalHeight || el.height;
  const k = Math.min(1, maxSide / Math.max(nw, nh));
  const W = Math.max(1, Math.round(nw * k)), H = Math.max(1, Math.round(nh * k));
  const rx = ((rxDeg || 0) * Math.PI) / 180, ry = ((ryDeg || 0) * Math.PI) / 180;
  const cx = Math.cos(rx), sx = Math.sin(rx), cy = Math.cos(ry), sy = Math.sin(ry);
  // A point (u, v) of the picture, measured from its centre with v downwards, at depth t behind
  // the face, lands at  X = cy·u − t·sy,  Y = −sy·sx·u + cx·v − t·cy·sx.
  const a = cy, b = -sy * sx, d = cx;
  const depth = Math.max(2, Math.round(Math.min(W, H) * TURN_DEPTH));
  const offset = (t) => [-t * sy, -t * cy * sx];
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const t of [0, depth]) {
    const [ox, oy] = offset(t);
    for (const [u, v] of [[-W / 2, -H / 2], [W / 2, -H / 2], [W / 2, H / 2], [-W / 2, H / 2]]) {
      const X = a * u + ox, Y = b * u + d * v + oy;
      if (X < x0) x0 = X; if (X > x1) x1 = X; if (Y < y0) y0 = Y; if (Y > y1) y1 = Y;
    }
  }
  const fit = Math.min(1, W / Math.max(1e-6, x1 - x0), H / Math.max(1e-6, y1 - y0));
  const midX = (x0 + x1) / 2, midY = (y0 + y1) / 2;

  const out = document.createElement('canvas');
  out.width = W; out.height = H;
  const ctx = out.getContext('2d');
  ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
  // The edge: the picture's own colours, darkened — and only where the picture has paint.
  const edge = document.createElement('canvas');
  edge.width = W; edge.height = H;
  const ectx = edge.getContext('2d');
  ectx.drawImage(el, 0, 0, W, H);
  ectx.globalCompositeOperation = 'source-atop';
  ectx.fillStyle = 'rgba(0,0,0,0.42)';
  ectx.fillRect(0, 0, W, H);
  const layer = (src, t) => {
    const [ox, oy] = offset(t);
    ctx.setTransform(fit * a, fit * b, 0, fit * d, W / 2 + fit * (ox - midX), H / 2 + fit * (oy - midY));
    ctx.drawImage(src, -W / 2, -H / 2, W, H);
  };
  // Steps of at most a pixel and a quarter, so the edge reads as one solid side, never a
  // stack of slices.
  const step = Math.max(0.5, depth / Math.ceil(depth / 1.25));
  if (cx * cy >= 0) {
    for (let t = depth; t > 0.01; t -= step) layer(edge, t);
    layer(el, 0);
  } else {
    for (let t = 0; t < depth - 0.01; t += step) layer(edge, t);
    layer(edge, depth);
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  return out;
};

const ImageStudio = ({ userRole }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // PCake: the role decides the library (Admin/Super share istudio/), who may manage stamps,
  // and who gets the AI tools (Movers do not — same as the old studio).
  setStudioRole(userRole);
  const isStaff = ['Admin', 'Super'].includes(userRole);
  const canUseAi = ['Admin', 'Super', 'Buyer'].includes(userRole);
  const canEditTiers = ['Super', 'Buyer'].includes(userRole);
  const fromCustom = location.state?.fromCustom === true;
  const fromCustomRef = useRef(fromCustom);
  const showAlert = useAlert();
  const fileInputRef = useRef(null);
  const connectMenuRef = useRef(null);
  const cameraInputRef = useRef(null);
  const cloudUploadInputRef = useRef(null);
  const canvasRef = useRef(null);
  const drawingCanvasRef = useRef(null);
  
  // State
  const [canvasImages, _setCanvasImages] = useState([]);
  /**
   * Anything put ON a picture travels WITH it.
   *
   * Shades and a cap dropped on a selfie are part of that selfie from then on: move the photo
   * and they move, make it bigger and they grow with it, turn it and they swing round with it.
   * Before this they sat where they were first dropped and the face slid out from under them.
   *
   * Rather than teach the drag path, the resize path, the rotate path, the position boxes and
   * the arrow keys each to look after children, this sits underneath all of them: it compares
   * the pictures before and after, and whatever happened to a parent it does to that parent's
   * props. It costs nothing when there are none, which is almost always.
   *
   * The arithmetic is done in centimetres because x is a percentage of the page's WIDTH and y a
   * percentage of its HEIGHT — two different units, which cannot be rotated against each other.
   */
  const carryProps = (prev, next) => {
    if (prev === next || !next || !prev) return next;
    let moved = null;
    for (const b of prev) {
      if (b.source === 'prop') continue;
      const a = next.find(n => n.id === b.id);
      if (!a) continue;
      if (a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height
        && (a.rotation || 0) === (b.rotation || 0)) continue;
      (moved || (moved = new Map())).set(b.id, { b, a });
    }
    if (!moved || !next.some(n => n.parentId && moved.has(n.parentId))) return next;
    const cmX = (p) => (p / 100) * 21, cmY = (p) => (p / 100) * 29.7;
    return next.map(n => {
      const pair = n.parentId && moved.get(n.parentId);
      if (!pair) return n;
      const { b, a } = pair;
      const sx = b.width ? a.width / b.width : 1;
      const sy = b.height ? a.height / b.height : 1;
      const dr = ((a.rotation || 0) - (b.rotation || 0)) * Math.PI / 180;
      const cos = Math.cos(dr), sin = Math.sin(dr);
      // Centres, not corners: a turn is about the middle of the picture.
      const pbx = cmX(b.x) + b.width / 2, pby = cmY(b.y) + b.height / 2;
      const pax = cmX(a.x) + a.width / 2, pay = cmY(a.y) + a.height / 2;
      const nbx = cmX(n.x) + n.width / 2, nby = cmY(n.y) + n.height / 2;
      const vx = (nbx - pbx) * sx, vy = (nby - pby) * sy;
      const cx = pax + vx * cos - vy * sin;
      const cy = pay + vx * sin + vy * cos;
      const w = n.width * sx, h = n.height * sy;
      return {
        ...n,
        width: w,
        height: h,
        x: ((cx - w / 2) / 21) * 100,
        y: ((cy - h / 2) / 29.7) * 100,
        rotation: (n.rotation || 0) + ((a.rotation || 0) - (b.rotation || 0)),
      };
    });
  };
  const setCanvasImages = useCallback((updater) => _setCanvasImages(prev => {
    const next = typeof updater === 'function' ? updater(prev) : updater;
    // carryProps is defined above and closes over nothing that changes between renders.
    return carryProps(prev, next);
  }), []);
  const [selectedImage, setSelectedImage] = useState(null);
  // In fromCustom mode auto-fall back to the only image so buttons work without tapping
  // Which image the AI tools act on. In fromCustom mode there's only ever one image, so it
  // auto-selects. Standalone this used to require an explicit tap: with nothing selected every
  // AI button sits disabled and clicking does nothing — indistinguishable from "the feature is
  // broken". When the canvas holds exactly ONE image there's no ambiguity, so target it too.
  // With several images the user still has to pick, since guessing could edit the wrong one.
  const effectiveImageId = fromCustom
    ? (selectedImage || canvasImages[0]?.id || null)
    : (selectedImage || (canvasImages.length === 1 ? canvasImages[0].id : null));
  const [resizeWidth, setResizeWidth] = useState(10);
  const lastResizeImgRef = useRef(null);   // which image the W/H boxes currently describe
  const [resizeHeight, setResizeHeight] = useState(10);
  const [isConnectMenuOpen, setIsConnectMenuOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [bucketDeleteMsg, setBucketDeleteMsg] = useState(null); // { text, ok }
  const [confirmDeleteBucket, setConfirmDeleteBucket] = useState(false);
  const [bucketSearch, setBucketSearch]           = useState('');
  const [bucketSortBy, setBucketSortBy]           = useState('date');  // 'date' | 'size' | 'name'
  const [bucketSortDir, setBucketSortDir]         = useState('desc');  // 'asc' | 'desc'
  const [renamingBucketFile, setRenamingBucketFile] = useState(null); // { fileName, displayName, newName }
  // True for any phone or tablet web browser. Used to swap Print→Share and Share→Chat
  // in the bottom toolbar (mobile/tablet browsers can't iframe-print reliably).
  // Detection covers:
  //   • Android phones & tablets (UA contains "Android")
  //   • iPhones and older iPads (UA contains "iPhone"/"iPad")
  //   • iPadOS 13+ which reports as Macintosh but has maxTouchPoints > 1
  //   • Any other touch-enabled device with a screen ≤ 1366 px wide (generic tablets)
  const isMobileWeb = !Capacitor.isNativePlatform() && (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 1 && /Macintosh/i.test(navigator.userAgent)) ||
    (navigator.maxTouchPoints > 0 && window.innerWidth <= 1366)
  );

  const [draggingImage, setDraggingImage] = useState(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 }); // ref so reads are always current, no extra re-render
  const [resizingImage, setResizingImage] = useState(null);
  const [rotatingImage, setRotatingImage] = useState(null);
  const [showHelpPanel, setShowHelpPanel] = useState(false);
  const [helpFaqSectionOpen, setHelpFaqSectionOpen] = useState(false);   // the FAQ list starts folded away
  const [helpFaqOpen, setHelpFaqOpen] = useState(null); // index of the expanded FAQ item, if any
  const [helpLang, setHelpLang] = useState(initialHelpLang);   // 'en' | 'de'
  const pickHelpLang = (lng) => {
    setHelpLang(lng);
    try { localStorage.setItem(HELP_LANG_KEY, lng); } catch { /* private mode */ }
  };
  const [helpLinkCopied, setHelpLinkCopied] = useState(false);
  // Real (Supabase) sign-in state -- null while signed out, whatever action triggered the
  // sign-in modal is retried automatically once signIn/signUp resolves (see pendingAuthActionRef).
  const [authUser, setAuthUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [authMode, setAuthMode] = useState('signin'); // 'signin' | 'signup'
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [authNotice, setAuthNotice] = useState(null); // e.g. "check your inbox to confirm"
  const pendingAuthActionRef = useRef(null); // the connect-option the user was trying when asked to sign in
  // Pick up any existing Supabase session on load, then keep authUser in sync with sign-in/out.
  // With cloud auth unset (no VITE_SUPABASE_* vars) this is a no-op and the app stays anonymous.
  useEffect(() => {
    initCloudAuth();
    const unsub = onAuthChange((session) => {
      setAuthUser(session?.user || null);
      // Whose library is on screen just changed, so any list held in memory belongs to the
      // previous identity. Keyed by folder, it could not be served to the wrong account — but
      // it would keep a signed-out listing alive in memory for no reason.
      _bucketImagesCache = null;
      _bucketImagesCacheKey = '';
    });
    return unsub;
  }, []);
  const [showBucketModal, setShowBucketModal] = useState(false);
  const [bucketImages, setBucketImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showImageControls, setShowImageControls] = useState(null);
  const [activeTool, setActiveTool] = useState(null);
  const [saveType, setSaveType] = useState('file');
  const [fileName, setFileName] = useState(DEFAULT_IMAGE_NAME);
  // The folder saves actually go to. Read from the chosen folder itself, never from a constant:
  // once it has been changed, a label built from the constant would name the wrong place.
  const [saveFolderName, setSaveFolderName] = useState(null);
  // Set while the save is waiting on the folder question. Holds the resolver, so the save can
  // simply `await` an answer instead of being restarted afterwards.
  const [folderPrompt, setFolderPrompt] = useState(null);
  // The camera's own question, and the answer looked up BEFORE it is needed — see
  // openSelfieCamera for why a single await in the wrong place made the button dead on iOS.
  const [cameraPrompt, setCameraPrompt] = useState(null);
  const cameraGrantedRef = useRef(false);
  useEffect(() => {
    let alive = true;
    cameraAlreadyGranted().then(g => { if (alive) cameraGrantedRef.current = g; }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const askForFolder = () => new Promise(resolve => {
    // Never let an unanswered question strand the save. If the dialog is somehow dismissed
    // without either button — a stray navigation, a reload of the panel — the save carries on
    // to the downloads rather than sitting at 85% with the UI locked in "saving".
    let done = false;
    const finish = (v) => { if (done) return; done = true; resolve(v); };
    const timer = setTimeout(() => { setFolderPrompt(null); finish(false); }, 60_000);
    setFolderPrompt({ resolve: (v) => { clearTimeout(timer); finish(v); } });
  });
  useEffect(() => { let alive = true; getStudioFolderName().then(n => { if (alive) setSaveFolderName(n); }); return () => { alive = false; }; }, []);
  const [drawingColor, setDrawingColor] = useState(() => {
    // Ink follows the SHEET, not the app chrome. The chrome is dark but the sheet is
    // always light, so this used to return white in dark mode and drew invisible strokes.
    return '#000000';
  });
  const [brushSize, setBrushSize] = useState(5);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingPaths, setDrawingPaths] = useState([]);
  // Always-current mirror of drawingPaths, same pattern as canvasShapesRef/canvasImagesRef:
  // the ink select/move/resize/rotate handlers and the keyboard Delete run from event
  // listeners, where the state closure can be a render behind.
  const drawingPathsRef = useRef([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [eyedropperActive, setEyedropperActive] = useState(false); // colour-picker mode
  const [pickedColor, setPickedColor] = useState(null); // last eyedropper-sampled colour
  // The two palette circles a person makes for themselves (see startEyedropper and pickColourAt).
  const [customSwatch, setCustomSwatch]       = useState(null);   // the colour mixed in the mixer
  const [pickSwatch, setPickSwatch]           = useState(null);   // the colour last taken from the sheet
  const [eyedropperRoute, setEyedropperRoute] = useState(null);   // where a pick lands: 'draw' | 'shape' | 'ai' | 'remove' | 'bg'
  const prevCursorRef = useRef(null);
  // While the eyedropper is out, the press on the sheet is taken here, in the capture phase, before
  // the drawing tool, a picture, a figure or the touch handlers can claim it. Without this a touch
  // started a stroke (and never produced the click the pick waited for), and a press on a picture
  // or figure selected it instead of taking its colour. finishPickRef is filled in further down.
  const finishPickRef = useRef(null);
  const cancelPickRef = useRef(null);
  useEffect(() => {
    if (!eyedropperActive) return undefined;
    const inSheet = (t) => !!(canvasRef.current && t instanceof window.Node && canvasRef.current.contains(t));
    const swallow = (e) => {
      if (!inSheet(e.target)) return;
      if (e.cancelable) e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    };
    let taken = false;
    const onDown = (e) => {
      if (!inSheet(e.target)) return;
      swallow(e);
      if (taken) return;
      taken = true;
      const x = e.clientX, y = e.clientY;
      finishPickRef.current?.(x, y);
    };
    const onKey = (e) => { if (e.key === 'Escape') cancelPickRef.current?.(); };
    const trailing = ['mousedown', 'touchstart', 'mouseup', 'touchend', 'pointerup', 'click'];
    window.addEventListener('pointerdown', onDown, { capture: true, passive: false });
    trailing.forEach(t => window.addEventListener(t, swallow, { capture: true, passive: false }));
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onDown, { capture: true });
      window.removeEventListener('keydown', onKey);
      // The release and click of the press that made the pick arrive after the pick is over;
      // they are still swallowed for a moment so they do not draw, select or deselect.
      setTimeout(() => trailing.forEach(t => window.removeEventListener(t, swallow, { capture: true })), 450);
    };
  }, [eyedropperActive]);
  const [saveProgress, setSaveProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [printSize, setPrintSize] = useState('A4P');
  // Whether the sheet is ruled in centimetres. Remembered, because it is a way of working rather
  // than a property of the picture — somebody who lines things up wants the ruling every time.
  const [showGrid, setShowGrid] = useState(() => {
    try { return localStorage.getItem('pcake.studio.grid') === '1'; } catch { return false; }
  });
  const toggleGrid = () => setShowGrid(v => {
    const next = !v;
    try { localStorage.setItem('pcake.studio.grid', next ? '1' : '0'); } catch { /* private mode */ }
    return next;
  });
  const [vpSize, setVpSize] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  const [drawingHistory, setDrawingHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [drawingMode, setDrawingMode] = useState('pen');
  const [fillModeActive, setFillModeActive] = useState(false);
  const [hasClosedArea, setHasClosedArea] = useState(false);
  const [canvasCursor, setCanvasCursor] = useState('default');
  const [eraserPos, setEraserPos] = useState(null); // {x, y} in canvas px for eraser circle overlay
  // Magic Wand AI tool
  const [magicSubTool, setMagicSubTool] = useState(null); // 'removeImageBg'|'removePlate'|'contrast'|'brightness'|'chat'|'color'
  const [magicProcessing, setMagicProcessing] = useState(false);
  const [magicChatImages, setMagicChatImages] = useState([]); // image IDs selected for multi-image chat
  const [magicCakeColorBase, setMagicCakeColorBase] = useState(null); // selected base colour name in colour picker
  const [magicBrightness, setMagicBrightness] = useState(0); // -50 to +50
  const [magicContrast, setMagicContrast] = useState(0);    // -50 to +50
  // Live preview values while dragging (not yet committed); reset to 0 when slider released
  // ── The live preview while a slider is held ───────────────────────────────────────────
  // `on` is separate from the two numbers on purpose. Dragging brightness back to 0 on a
  // picture already baked at +40 leaves both numbers at zero, and "both zero" used to mean
  // "nothing to preview" — so the picture sat at +40 under the finger and dropped to 0 the
  // instant it lifted. The flag says a slider is being held; the numbers say what to show.
  const [magicFilterPreview, setMagicFilterPreview] = useState({ on: false, brightness: 0, contrast: 0 });
  const [showLayoutMenu, setShowLayoutMenu] = useState(false);   // page-layout picker from the bottom bar
  const [canvasBgFx, setCanvasBgFx] = useState(null);             // shading or pattern over the sheet colour (SHEET_FX key)
  const [selfieMode, setSelfieMode] = useState(false);            // photo taken with Selfie Mode: the toolbar turns into the fun set
  const [selfieStream, setSelfieStream] = useState(null);         // the live camera, while Selfie Mode is taking a picture
  // The same stream, reachable without waiting for a render. Flipping cameras has to let go
  // of the old one BEFORE asking for the new one (see flipSelfieCamera), and a state value
  // read inside a handler is the value from the last render, not necessarily the live one.
  const selfieStreamRef = useRef(null);
  const [selfieFacing, setSelfieFacing] = useState('user');
  const selfieVideoRef = useRef(null);
  const [showShareRow, setShowShareRow] = useState(false);       // share targets replace the bottom bar while open
  const [showFiguresMenu, setShowFiguresMenu] = useState(false); // line/arrow/circle/triangle/rectangle picker
  const [figuresMenuPos, setFiguresMenuPos] = useState({ top: 0, left: 0 });
  const figuresBtnRef = useRef(null);
  const [magicChatMessages, setMagicChatMessages] = useState([]); // [{role:'user'|'assistant', content, imageUrl?}]
  const [magicChatInput, setMagicChatInput] = useState('');
  const [magicChatLoading, setMagicChatLoading] = useState(false);
  /**
   * Dictation for the prompt box.
   *
   * The language is the DEVICE's own, not the app's. Somebody whose phone is set to German expects
   * to speak German into it, and the speech API has no auto-detect to fall back on — so the one
   * signal that is actually reliable, the language they chose for their phone, is the one used.
   * The same value goes on the text box itself, so the keyboard that opens offers that language
   * rather than inheriting the page's English and offering nothing.
   *
   * One utterance at a time: a prompt is a sentence, not a monologue, so it stops when you do.
   */
  const SPEECH_API = typeof window !== 'undefined'
    ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
  const deviceLang = (typeof navigator !== 'undefined' && (navigator.language || (navigator.languages || [])[0])) || 'en';
  // Speech recognition hears ONE language at a time — there is no auto-detect to lean on — so the
  // language is a choice, kept between sessions. "Auto" is the device's own, which is right for
  // most people most of the time; the rest are here because they are the ones actually spoken to
  // this app. A long press on the microphone opens the list; a short press just listens.
  const SPEECH_LANGS = [
    { code: '',      label: 'Auto', name: 'Device language' },
    { code: 'en-US', label: 'EN',   name: 'English' },
    { code: 'hi-IN', label: 'हि',   name: 'हिन्दी — Hindi' },
    { code: 'bn-IN', label: 'বাং',  name: 'বাংলা — Bengali' },
    { code: 'de-DE', label: 'DE',   name: 'Deutsch — German' },
  ];
  const SPEECH_LANG_KEY = 'pcake.studio.speechLang';
  const [speechLang, setSpeechLang] = useState(() => {
    try { return localStorage.getItem(SPEECH_LANG_KEY) || ''; } catch { return ''; }
  });
  const pickSpeechLang = (code) => {
    setSpeechLang(code);
    try { localStorage.setItem(SPEECH_LANG_KEY, code); } catch { /* private mode */ }
    setShowLangMenu(false);
  };
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [langMenuPos, setLangMenuPos] = useState({ top: 0, left: 0 });
  const micPressRef = useRef({ timer: null, fired: false });
  // What the recogniser is told, and what the keyboard is hinted with.
  const listenLang = speechLang || deviceLang;
  const [listening, setListening] = useState(false);
  // Two refs the blur handlers read. A field that is dictating must NOT commit when it loses
  // focus — losing focus is the point, it is how the on-screen keyboard gets out of the way —
  // and state read inside an event handler is a render behind, so these are refs.
  const listeningRef = useRef(false);
  const dictateTargetRef = useRef('chat');   // 'chat' | 'text' | 'label'
  const recogRef = useRef(null);
  const sendPressRef = useRef({ timer: null, fired: false });
  const stopDictation = () => { try { recogRef.current?.stop(); } catch { /* already stopped */ } };
  /**
   * Get the microphone the ordinary way, so the browser ASKS.
   *
   * Speech recognition does not reliably raise the permission prompt by itself — in an installed
   * app, and in Chrome where the site has never been granted the microphone, it can go straight
   * to "not-allowed" having asked nobody anything. What the person then saw was this app telling
   * them to go and change a setting they had never been offered, which is no way to be asked for
   * something. getUserMedia is the call that raises the browser's own prompt and, once allowed,
   * puts the site on its allowed list for good — the same call Talk2Note makes before it records.
   *
   * The stream is released immediately: recognition opens the microphone itself, and holding a
   * second one would leave the recording indicator on for no reason.
   */
  const askForMicrophone = async () => {
    // A page served over plain http (an app opened at http://192.168.x.x, say) is not allowed a
    // microphone at all, by the browser, whatever anyone consents to — and that is NOT the
    // person having blocked it. It was being reported as "blocked, change your settings", which
    // is advice that cannot work: there is no setting for it.
    if (typeof window !== 'undefined' && window.isSecureContext === false) return 'insecure';
    if (!navigator.mediaDevices?.getUserMedia) return 'unavailable';
    // Ask. The prompt is the only honest source of truth here: navigator.permissions.query
    // reports "denied" for a site that has merely never asked in some browsers, and for an
    // insecure page in others, so consulting it first was what produced a refusal before the
    // person had been given anything to refuse.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => { try { t.stop(); } catch { /* already stopped */ } });
      return 'granted';
    } catch (err) {
      if (err?.name === 'NotAllowedError') return 'denied';        // the person, or a policy, said no
      if (err?.name === 'SecurityError') return 'insecure';
      if (err?.name === 'NotFoundError' || err?.name === 'OverconstrainedError') return 'missing';
      return 'unavailable';
    }
  };

  const startDictation = async (target = 'chat') => {
    if (!SPEECH_API || listening) return;
    dictateTargetRef.current = target;
    const mic = await askForMicrophone();
    if (mic === 'insecure') {
      showAlert('Speech needs a secure (https) address. This page is on plain http, where no browser allows a microphone.');
      return;
    }
    if (mic === 'missing') { showAlert('No microphone was found on this device.'); return; }
    if (mic === 'denied') {
      showAlert('The microphone is blocked for this app. Allow it from the padlock in the address bar — or, in the installed app, from its site settings — and try again.');
      return;
    }
    let rec;
    try { rec = new SPEECH_API(); } catch { showAlert('This browser cannot listen for speech.'); return; }
    recogRef.current = rec;
    rec.lang = listenLang;
    rec.interimResults = true;
    rec.continuous = false;
    rec.maxAlternatives = 1;
    // What was already typed stays; speech is added to it rather than replacing it, and the
    // interim guesses overwrite each other instead of piling up. Which box it goes into is
    // whichever one is being edited: the prompt, a text object, or a figure's label.
    const write = target === 'text' ? setPendingTextValue
      : target === 'label' ? setPendingLabelValue
        : setMagicChatInput;
    const base = (target === 'text' ? pendingTextValue
      : target === 'label' ? pendingLabelValue
        : magicChatInput).trim();
    rec.onresult = (e) => {
      let said = '';
      for (let i = 0; i < e.results.length; i++) said += e.results[i][0].transcript;
      said = said.trim();
      write(base && said ? `${base} ${said}` : (said || base));
    };
    rec.onerror = (e) => {
      setListening(false);
      listeningRef.current = false;
      // Permission was asked for and settled before this started, so a refusal here is not the
      // person saying no — it is something else holding the microphone, or the browser's speech
      // service being unavailable. Saying "change your settings" would be a wrong guess.
      if (e?.error === 'not-allowed' || e?.error === 'service-not-allowed') {
        showAlert('The microphone is busy — close anything else using it and try again.');
      } else if (e?.error && e.error !== 'aborted' && e.error !== 'no-speech') {
        showAlert('Could not hear anything — try again.');
      }
    };
    rec.onend = () => { setListening(false); listeningRef.current = false; recogRef.current = null; };
    try {
      rec.start();
      setListening(true);
      listeningRef.current = true;
      // Speaking and typing are alternatives, so the keyboard gets out of the way — on a phone
      // it covers half the sheet, and there is nothing to type while the microphone is open.
      // The box keeps its text and stays open: the blur handlers check listeningRef and leave
      // it alone (see the textarea below).
      if (target !== 'chat') { try { document.activeElement?.blur?.(); } catch { /* nothing focused */ } }
    } catch { setListening(false); listeningRef.current = false; }
  };
  /** The microphone, for whichever box is open. Kept here so the top bar and the chat agree. */
  const dictateInto = (target) => {
    if (listening) { stopDictation(); return; }
    void startDictation(target);
  };
  // A tab left mid-sentence should not keep the microphone open.
  useEffect(() => () => { try { recogRef.current?.abort(); } catch { /* gone already */ } }, []);
  const [magicChatStep, setMagicChatStep] = useState(''); // 'preparing'|'sending'|'waiting'|''
  const [magicChatError, setMagicChatError] = useState(''); // error message for status strip
  const [aiElapsed, setAiElapsed] = useState(0);            // seconds since the AI request started
  const [magicChatDone, setMagicChatDone] = useState(false); // brief 'done' confirmation after a run
  const aiBusy = magicProcessing || magicChatLoading;      // any AI request in flight (buttons or AI Edit)
  const prevMagicProcessingRef = useRef(false);
  useEffect(() => {
    if (!aiBusy) { setAiElapsed(0); return; }
    const t0 = Date.now();
    const id = setInterval(() => setAiElapsed(Math.round((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(id);
  }, [aiBusy]);
  useEffect(() => {
    const was = prevMagicProcessingRef.current;
    prevMagicProcessingRef.current = aiBusy;
    if (aiBusy) { setMagicChatDone(false); return; }
    if (was && !magicChatError) {
      setMagicChatDone(true);
      const id = setTimeout(() => setMagicChatDone(false), 3000);
      return () => clearTimeout(id);
    }
  }, [aiBusy]);
  // An error stays until dismissed, the next run, or 15 s — it must never sit there for good.
  useEffect(() => {
    if (!magicChatError) return;
    const id = setTimeout(() => setMagicChatError(''), 15000);
    return () => clearTimeout(id);
  }, [magicChatError]);
  // One line describing where an AI run is, for the status strip and the AI Edit card.
  const aiStatusText = () => {
    if (magicChatError) return magicChatError;
    if (magicChatStep === 'preparing') return '1/4 Preparing…';
    if (magicChatStep === 'sending')   return `2/4 Sending… ${aiElapsed}s`;
    if (magicChatStep === 'waiting')   return `3/4 Working… ${aiElapsed}s`;
    if (magicChatStep === 'applying')  return '4/4 Applying…';
    if (aiBusy) return `Working… ${aiElapsed}s`;
    if (magicChatDone) return 'Done';
    return '';
  };
  // Whether the AI's progress/result should take over the top toolbar's middle section right
  // now -- covers a request in flight, its error, and its brief "Done" confirmation, whether
  // it was started from a toolbar button or from the AI Edit chat panel. Read wherever that
  // section decides between showing this and its normal content (coordinates, draw controls…).
  const aiStatusInTopBar = !!(magicChatStep || aiBusy || magicChatError || magicChatDone);
  const [magicPromptHistory, setMagicPromptHistory] = useState([]); // last 10 unique prompts
  const [showPromptHistory, setShowPromptHistory] = useState(false);
  // Buyer-only Cake Tier / Name tools (replace the Hologram slot in the AI toolbar)
  const [magicTierCounts, setMagicTierCounts] = useState({}); // {[imageId]: number of tiers added}
  const [showNamePopup, setShowNamePopup] = useState(false);
  const [pendingNameValue, setPendingNameValue] = useState('');
  const [magicCakeNames, setMagicCakeNames] = useState({}); // {[imageId]: the name last written on the cake}
  const [namePlacementPending, setNamePlacementPending] = useState(null); // the name text awaiting a tap-to-place, or null
  const nameBtnRef = useRef(null);
  const magicChatScrollRef = useRef(null);
  const magicFilterOrigUrlRef = useRef(null); // original image URL captured on entering brightness/contrast mode
  const magicTouchStartRef = useRef(null); // { id, x, y } for tap-vs-drag detection on touch devices
  const [bucketModalMode, setBucketModalMode] = useState('select');
  const [selectedBucketImages, setSelectedBucketImages] = useState([]);
  const [selectionMode, setSelectionMode] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState({ x: 0, y: 0 });
  const [selectionEnd, setSelectionEnd] = useState({ x: 0, y: 0 });
  const [selectionPath, setSelectionPath] = useState([]);
  const [selectionTouchPos, setSelectionTouchPos] = useState(null); // live pen-cursor position for freeform/smart (mouse and touch)
  // Crop path tools (freeform, smart) keep their path in a ref and mirror it to state for
  // rendering. The ref is the source of truth: on mobile a touchmove can arrive before React
  // has flushed the touchstart's setState, and a fast mouse drag has the same stale-closure
  // hazard — so both pointer types build the path here and read it back from here on release.
  const cropPathRef = useRef([]);
  const cropActiveRef = useRef(false);   // true from crop pointer-down to pointer-up, on any pointer type
  // Near-edge zone of the rectangle crop treated as a resize grab. One value for mouse and
  // touch: the two used to differ (16 vs 24px), so the same press moved the box on desktop
  // and resized it on a phone.
  const CROP_EDGE_PX = 20;
  // For circle crop: track drag interaction (move or resize) on the pre-placed overlay
  const circleCropInteractRef = useRef(null); // null | { type:'move'|'resize', startX, startY, origStart, origEnd }
  /**
   * Did the gesture that is ending actually MOVE the crop box?
   *
   * A drag is a mousedown, some mousemoves, a mouseup — and then a `click`, fired at the point
   * the button came up. The crop's click handler reads a click as "clicked the sheet": inside
   * the box it is ignored, outside it CANCELS the crop. So the click that merely ends a resize
   * is indistinguishable from one that means "I'm done with this", and when the drag finishes
   * with the pointer off the shape — which is the normal end of a rim drag, because a side
   * handle moves one radius and the pointer carries on past it — the release cancelled the
   * crop and threw the selection away.
   *
   * The same absorb the figures have had for a long time (shapeClickedRef), which the crop
   * never got because that one is behind `if (!activeTool)` and crop always has a tool set.
   */
  const cropDragMovedRef = useRef(false);
  // Smart crop (object detection) state
  const [smartCropRunning, setSmartCropRunning] = useState(false);
  const smartDetectedRef = useRef(false); // true = polygon shown, waiting for confirm ✓
  const [smartDetected, setSmartDetected] = useState(false); // mirrors ref, triggers SVG re-render
  const smartCropMaskRef = useRef(null); // pixel-level FG mask canvas for precise crop cutout
  // A latch, not a piece of state. React 17 attaches touchstart/touchmove as PASSIVE listeners
  // on the root, so the preventDefault() in the touch handlers is ignored and the browser sends
  // its synthetic mouse events afterwards anyway: every lasso released on a touch screen (and on
  // a touch-capable laptop) ran the whole pointer-up path twice. The second pass found the path
  // already consumed and took the "nothing was drawn" branch, which resets isSelecting and the
  // selection box — wiping, milliseconds later, the very outline the first pass was busy
  // detecting. Hence "the outline appears and then disappears, and no confirm ever comes".
  // setSmartCropRunning could not guard it: a state setter does not take effect until the next
  // render, and both passes happen inside the same one. A ref changes on the spot.
  const smartCropBusyRef = useRef(false);
  // Auto Crop reuses the "smart" selection-mode plumbing internally (object detection,
  // pixel mask, auto-confirm), but it is a distinct button from Smart Crop -- this flag
  // lets the toolbar tell the two apart so pressing Auto Crop doesn't visually light up
  // the Smart Crop button or claim the tool "switched" to Smart Crop.
  const [autoCropActive, setAutoCropActive] = useState(false);
  const [showLeftPanel, setShowLeftPanel] = useState(true);
  const [showRightPanel, setShowRightPanel] = useState(false);
  const [showToolsPanel, setShowToolsPanel] = useState(false);
  const [toolsTouchStartY, setToolsTouchStartY] = useState(0);
  const [toolsWrapperHeight, setToolsWrapperHeight] = useState(0);
  const toolsWrapperRef = useRef(null);
  /**
   * How tall the tools strip actually is.
   *
   * The sheet's height is whatever is left over once the bars have had their share, and that sum
   * used to be written out by hand: 62px for this strip. It is not 62 — it is 41 at ordinary
   * sizes and more when the buttons grow on a large screen — so about twenty pixels of paper were
   * being set aside for a strip that did not want them, and the sheet floated in the gap. Measure
   * it. Nothing here depends on the sheet's size, so measuring cannot chase its own tail.
   */
  const [toolsStripH, setToolsStripH] = useState(62);
  useLayoutEffect(() => {
    const el = toolsWrapperRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const read = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h <= 0) return;
      setToolsStripH(prev => (Math.abs(prev - h) > 1 ? h : prev));
      // The strip floats over the canvas area's top padding, which was a flat 56px — fifteen
      // more than the strip needs, and too few once the buttons grow on a large screen. Tell
      // the stylesheet the real number so the padding is exactly the strip and nothing else.
      document.documentElement.style.setProperty('--is-tools-h', `${h}px`);
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /**
   * The height the sheet actually has to play with: the canvas area's content box.
   *
   * Worked out by hand, this sum was wrong twice over — it counted a tools strip that is not 62px
   * and knew nothing of the area's own padding — and every pixel it got wrong showed up as a gap
   * above or below the paper. Asking the element is not a better guess; it is the answer, and it
   * keeps being the answer when the padding changes underneath it (the AI strip does exactly
   * that). Nothing here reads the sheet's size, so it cannot feed back into itself.
   */
  const [areaH, setAreaH] = useState(0);
  useLayoutEffect(() => {
    const el = canvasAreaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const read = () => {
      const cs = window.getComputedStyle(el);
      const inner = Math.round(el.clientHeight - parseFloat(cs.paddingTop || 0) - parseFloat(cs.paddingBottom || 0));
      if (inner > 0) setAreaH(prev => (Math.abs(prev - inner) > 1 ? inner : prev));
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const toolsContentRef = useRef(null);
  const [showTopControls, setShowTopControls] = useState(false);
  const [currentControlSection, setCurrentControlSection] = useState(null);
  const [lockAspectRatio, setLockAspectRatio] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(1);
  const [showColorShades, setShowColorShades] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedBaseColor, setSelectedBaseColor] = useState('#000000');
  // Selection is the canvas default, not a mode. Kept as a constant (rather than deleting
  // ~29 call sites) so every `if (imageSelectionMode)` branch stays live and the setter
  // calls that used to toggle the old tool off are harmless no-ops.
  const imageSelectionMode = true;
  const setImageSelectionMode = () => {};
  const topControlsRef = useRef(null);
  const canvasImagesRef = useRef([]);
  const drawingHistoryRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const dragStartImagesRef = useRef(null);
  const dragStartPathsRef = useRef(null); // drawingPaths snapshot at the start of a multi-drag that includes ink strokes
  const resizeStartImagesRef = useRef(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [pendingNavPath, setPendingNavPath] = useState(null);
  const [pendingClear, setPendingClear] = useState(false);   // broom tapped once: warn, tap again to clear
  const [figures3D, setFigures3D] = useState(false);          // 3D button: the Figures picker lists 3D objects
  // Phone / tablet status bar and home indicator: the top bar and bottom bar grow by the safe
  // areas (CSS env()), and the sheet's size maths reads the same numbers from a probe element.
  const [safeInset, setSafeInset] = useState({ top: 0, bottom: 0 });
  useEffect(() => {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px);pointer-events:none;visibility:hidden';
    document.body.appendChild(probe);
    const read = () => {
      const cs = getComputedStyle(probe);
      const top = parseFloat(cs.paddingTop) || 0, bottom = parseFloat(cs.paddingBottom) || 0;
      setSafeInset(prev => (prev.top === top && prev.bottom === bottom) ? prev : { top, bottom });
    };
    read();
    window.addEventListener('resize', read);
    window.addEventListener('orientationchange', read);
    return () => { window.removeEventListener('resize', read); window.removeEventListener('orientationchange', read); probe.remove(); };
  }, []);
  const [openGroup, setOpenGroup] = useState(null);            // 'ai' | 'draw' — which tool panel is open
  const [groupPanelPos, setGroupPanelPos] = useState({ top: 0, left: 0 });
  const openGroupPanel = (e, group, cols) => {
    const r = e.currentTarget.getBoundingClientRect();
    const W = cols * 44 + 24;
    setGroupPanelPos({ top: r.bottom + 6, left: Math.max(6, Math.min(r.left, window.innerWidth - W - 6)) });
    setOpenGroup(g => g === group ? null : group);
  };
  /**
   * Then put it where it actually fits.
   *
   * The line above places the panel from a GUESS at its width — a column count times a button —
   * made before the panel exists. Guess low and the panel hangs off the right of the screen with
   * its last column out of reach, which is what happened to the effects on a phone. This runs
   * once it is on screen, measures the real thing, and pulls it back in if it is over the edge.
   */
  useLayoutEffect(() => {
    if (!openGroup) return;
    const el = document.querySelector('.is-group-panel');
    if (!el) return;
    const r = el.getBoundingClientRect();
    const maxLeft = window.innerWidth - r.width - 6;
    if (r.left > maxLeft || r.left < 6) {
      setGroupPanelPos(p => ({ ...p, left: Math.max(6, maxLeft) }));
    }
  }, [openGroup]);
  const pendingAutoCutRef = useRef(false);                     // auto crop: lift the object once the selection state has settled
  useEffect(() => { setFillModeActive(drawingMode === 'fill'); }, [drawingMode]);   // the Fill drawing tool
  const [maskTick, setMaskTick] = useState(0);                 // bumps when an auto-crop mask PNG decodes
  const committedLayerRef = useRef(null);                      // drawing paths rendered once; the live stroke goes on top
  const pendingClearTimerRef = useRef(null);
  const shapeClipboardRef = useRef(null);                    // Ctrl+C of a drawn object
  const keyActionsRef = useRef({});                          // latest shortcut handlers (assigned every render)
  const [flashMsg, setFlashMsg] = useState(null);            // short confirmation in the top bar (copy, paste…)
  const flashTimerRef = useRef(null);
  const flash = (text) => {
    setFlashMsg(text);
    setShowTopControls(true);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => { flashTimerRef.current = null; setFlashMsg(null); }, 2500);
  };
  const isSavedRef = useRef(false);
  // The app is on its way out through its own close button, rather than merely being put down.
  // Declared up here because closeApp is further up the file than the autosave that reads it.
  const sessionClosingRef = useRef(false);
  const canvasAreaRef = useRef(null);
  const panRef = useRef({ active: false, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 });
  const resizeDirectionRef = useRef(null);

  const [canvasShapes, setCanvasShapes]               = useState([]);
  const [selectedShapeId, setSelectedShapeId]         = useState(null);
  const [selectedShapeIds, setSelectedShapeIds]       = useState(new Set());   // multi-select
  const [marqueeRect, setMarqueeRect]                 = useState(null); // {x1,y1,x2,y2} pct
  const [activeShapeTool, setActiveShapeTool]         = useState(null);
  const [lastUsedShapeTool, setLastUsedShapeTool]     = useState('triangle');
  const [showShapesPanel, setShowShapesPanel]         = useState(false);
  const [isDrawingShape, setIsDrawingShape]           = useState(false);
  const [shapeDrawStart, setShapeDrawStart]           = useState(null);
  const [shapeDrawEnd, setShapeDrawEnd]               = useState(null);
  const [shapeStrokeColor, setShapeStrokeColor]       = useState(() =>
    '#000000'
  );
  const [shapeFillColor, setShapeFillColor]           = useState('none');
  const [shapeStrokeWidth, setShapeStrokeWidth]       = useState(2);
  const [shapeColorTarget, setShapeColorTarget]       = useState('stroke');
  const [showShapeColorPicker, setShowShapeColorPicker] = useState(false);
  const [showShapeColorShades, setShowShapeColorShades] = useState(false);
  const [shapeSelectedBase, setShapeSelectedBase]     = useState('#000000');
  const [shapeFontSize, setShapeFontSize]             = useState(() => Math.round(Math.max(12, Math.min(24, window.innerWidth * (29.7 / 21) * 0.028))));
  const [shapeFontFamily, setShapeFontFamily]         = useState('Arial');
  const [shapeTextBold, setShapeTextBold]             = useState(false);
  const [shapeTextItalic, setShapeTextItalic]         = useState(false);
  const [shapeTextUnderline, setShapeTextUnderline]   = useState(false);
  // Raised letters: the same words drawn a few times behind themselves, each copy a step down
  // and to the right in a darker shade of their own colour, so the face of the letter stands on
  // top of its own side wall. It is an effect any typeface can take, unlike a 3D figure, which
  // is real geometry — so it lives with bold and italic rather than with the objects.
  const [shapeText3D, setShapeText3D]                 = useState(false);
  const [pendingTextPos, setPendingTextPos]           = useState(null);
  const [pendingTextValue, setPendingTextValue]       = useState('');
  const [pendingTextShapeId, setPendingTextShapeId]   = useState(null);
  // Caption editor for double-click (desktop) / long-press (touch) on any 2D or 3D SHAPE
  // (rectangle, circle, triangle, freeform, library 2D/3D figures -- everything except the
  // dedicated 'text' tool, which already has its own edit flow above). Deliberately separate
  // state/commit path from pendingText* -- that mechanism resizes the shape to fit the text,
  // which would corrupt a shape's geometry; a caption only ever sets shape.text.
  const [pendingLabelShapeId, setPendingLabelShapeId] = useState(null);
  const [pendingLabelValue, setPendingLabelValue]     = useState('');
  const labelInputRef = useRef(null);     // the caption box itself — see setCaptionStyle
  const shapeTapRef = useRef(null);       // { id, t } — previous tap, for double-tap detection
  const [selectedEmoji, setSelectedEmoji]             = useState(null);
  const [studioStamps, setStudioStamps]               = useState([]);
  const [stampUploading, setStampUploading]           = useState(false);
  const [stampRemoving, setStampRemoving]             = useState(false);
  const [selectedStampIds, setSelectedStampIds]       = useState(new Set());
  const [stampSelectMode, setStampSelectMode]         = useState(false);
  const stampImageCacheRef = useRef(new Map());
  const stampGridRef = useRef(null);
  const draggingShapeRef         = useRef(null);
  const multiDragRef             = useRef(null);  // [{id, offsetX, offsetY}] for multi-shape drag
  const isMarqueeSelectingRef    = useRef(false);
  const marqueeJustFinishedRef   = useRef(false); // prevents handleCanvasClick from clearing marquee selection
  const marqueeRectRef           = useRef(null);  // mirrors marqueeRect for sync access in mouseUp
  const resizingShapeRef = useRef(null);
  const rotatingShapeRef = useRef(null);
  // 3D figures only: dragging the orbit handle turns the object in space -- sideways spins it
  // (rotY), up/down tilts it (rotX). The in-plane turn stays on the ordinary rotate handle.
  const orbitShapeRef = useRef(null);   // { id, sx, sy, rx, ry }
  const rotateImageCenterRef = useRef(null); // { id, cx, cy (rect-relative px), before }
  const shapeBeforeRef = useRef(null);
  const shapeDraggedRef = useRef(false);
  // A press on the already-selected shape MIGHT mean "give me the one underneath" — but only if
  // it turns out to be a tap. Held here until the release decides. See the two sites below.
  const cycleUnderRef = useRef(null);
  const shapeClickedRef = useRef(false);
  const shapeJustDrawnRef = useRef(false);
  const penStrokeDrawnRef = useRef(false);     // a pen gesture drew something (so the click after it is not a tap)
  const lastPenPointRef = useRef(null);         // last point of the stroke in progress (state lags fast pointer moves)
  const freeDraftRef = useRef([]);              // points (sheet %) of the freeform figure being drawn
  const [freeDraft, setFreeDraft] = useState([]);
  const nodeDragRef = useRef(null);              // { shapeId, index } while a freeform node is dragged   // a figure was just drawn: the click that follows the release must not deselect it
  const textEditCandidateRef = useRef(null); // id of selected text shape tapped, for edit-on-double-tap
  const textLastTapRef       = useRef({ id: null, time: 0 }); // tracks last tap for double-tap detection
  const canvasShapesRef = useRef([]);
  const [isResizingShape, setIsResizingShape]         = useState(false);
  const [isRotatingShape, setIsRotatingShape]         = useState(false);
  // Reactive twin of draggingShapeRef -- that ref drives the actual per-pixel drag math (refs
  // don't trigger a re-render), this state exists purely so the top bar can react to "a plain
  // move is actively in progress" the same way it already can for isResizingShape/isRotatingShape
  // and images' own draggingImage/resizingImage/rotatingImage state.
  const [isMovingShape, setIsMovingShape]             = useState(false);
  const [selectMoveControlMode, setSelectMoveControlMode] = useState('position'); // 'position' | 'rotate' | 'resize'
  // Ink (hand-drawn stroke) gestures. A selected stroke is held in selectedShapeId, the same
  // slot a selected figure uses -- the id is looked up in canvasShapes first and in
  // drawingPaths only if that misses, so nothing that already reads selectedShapeId changes
  // meaning. These mirror draggingShapeRef / resizingShapeRef / rotatingShapeRef / shapeBeforeRef
  // / shapeDraggedRef one for one, and the reactive isMovingShape / isResizingShape /
  // isRotatingShape flags are shared with the shape gestures rather than duplicated.
  const draggingInkRef = useRef(null);  // { id, offsetX, offsetY }
  const resizingInkRef = useRef(null);  // { id, corner, anchor, origW, origH, origPath }
  const rotatingInkRef = useRef(null);  // { id, cx, cy, origPath }
  const inkBeforeRef   = useRef(null);  // drawingPaths snapshot at gesture start, for undo
  const inkDraggedRef  = useRef(false); // this press actually moved the stroke (vs just selecting it)
  // A stroke has no rotation field to read back -- a turn is baked straight into its points --
  // so the live angle is tracked here purely for the A° readout during the gesture.
  const [inkGestureAngle, setInkGestureAngle] = useState(0);
  /**
   * Sticky "I am positioning things" mode, set by the Move button.
   *
   * Selecting a drawn object always opened its colour & style controls — which is right when
   * you have just drawn it, and wrong the moment you press Move: the coordinates appeared,
   * then the very next tap on an object threw them away and put the palette back, so Move
   * could never be used on more than one object without pressing it again each time. This
   * remembers the choice until the user leaves it: while it is on, touching an object shows
   * WHERE it is, never the palette.
   */
  const [positionMode, setPositionMode] = useState(false);
  const positionModeRef = useRef(false);
  useEffect(() => { positionModeRef.current = positionMode; }, [positionMode]);
  // ── …and WHICH object asked for it ──────────────────────────────────────────────────────
  // Move used to be sticky across everything: turn it on for one figure and every figure
  // touched afterwards opened on its coordinates too. With one object on the sheet that is
  // invisible; with several it reads as "the palette only works on the first one", because
  // the way back is a button most people have not noticed is lit. Nudging one figure should
  // not quietly take the colours away from all the others.
  //
  // So Move is sticky for the object it was switched on for — tap that one again and its
  // numbers are still there, which is the reason it is sticky at all — and any OTHER object
  // opens the way it always did, on its own colour and style.
  const positionModeForRef = useRef(null);
  const [formatPainterActive, setFormatPainterActive] = useState(false);
  const formatPainterDataRef = useRef(null);
  const [showStrokeWidthMenu, setShowStrokeWidthMenu] = useState(false);
  // The width menu serves two masters now: a figure's border, and the outline drawn round the
  // letters of a text object. Same menu, same gesture — different thing being set, so the target
  // has to be remembered while it is open.
  const [strokeMenuFor, setStrokeMenuFor] = useState('shape');   // 'shape' | 'text'
  // Nothing has an outline until it is asked for: text has always been drawn as a plain fill,
  // and starting everyone's existing captions with a 2mm border would be a change nobody made.
  const [textOutline, setTextOutline] = useState(0);
  const [showFontSizeMenu, setShowFontSizeMenu] = useState(false);
  const [fontMenuPos, setFontMenuPos] = useState({ top: 0, left: 0 });
  const fontSizeBtnRef = useRef(null);
  const [strokeMenuPos, setStrokeMenuPos] = useState({ top: 0, left: 0 });
  const strokeWidthBtnRef = useRef(null);
  const strokeMenuRef = useRef(null);
  const [shapeDashed, setShapeDashed] = useState(false);
  const [showBrushSizeMenu, setShowBrushSizeMenu] = useState(false);
  const [brushMenuPos, setBrushMenuPos] = useState({ top: 0, left: 0 });
  const brushSizeBtnRef = useRef(null);
  const brushMenuRef = useRef(null);
  const [showDrawPalette, setShowDrawPalette] = useState(false);
  const [drawPalettePos, setDrawPalettePos] = useState({ top: 0, left: 0 });
  const drawPaletteRef = useRef(null);
  const drawPaletteBtnRef = useRef(null);
  // Colour picker (draw tool's swatch button and the shape tool's border/fill/text swatch
  // buttons) now opens as a floating pop-down instead of taking over the whole toolbar row --
  // one shared position since only one of those buttons' pickers is ever open at a time.
  const [colorMenuPos, setColorMenuPos] = useState({ top: 0, left: 0 });
  const accountBtnRef = useRef(null);
  const accountMenuRef = useRef(null);
  // Anchored by its RIGHT edge, not its left: the account button sits at the end of the bottom
  // bar, so a menu that starts there and grows rightwards — as wide as the address it shows —
  // runs straight off the screen, and the address was cut off halfway through.
  const [accountMenuPos, setAccountMenuPos] = useState({ bottom: 0, right: 0 });
  const lastDragDirRef = useRef({ x: 1, y: 1 }); // tracks last drag direction for leading-edge display
  const [canvasBackground, setCanvasBackground]       = useState('transparent');
  // A sheet colour that is none of the four papers is one the person mixed themselves, and the
  // Colour swatch then shows that colour rather than the wheel.
  const bgIsCustom = canvasBackground !== 'transparent'
    && !BG_THEMES.some(t => t.value === canvasBackground);
  // Where the thumb sits on the mixing rail. Only the rail moves it, so it never has to be
  // reverse-engineered from an arbitrary colour — the thumb shows the colour itself instead.
  const [mixPos, setMixPos] = useState(0.5);
  const setMixFromPos = (t) => {
    const x = Math.max(0, Math.min(1, t));
    setMixPos(x);
    setCanvasBackground(mixColorAt(x));
    markDirty();
  };
  const pickFromRail = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    if (!r.width) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setMixFromPos((e.clientX - r.left) / r.width);
  };
  const mixShades = shadesOfColor(canvasBackground === 'transparent' ? '#ffffff' : canvasBackground);
  const [showEmojiPopdown, setShowEmojiPopdown]       = useState(false);
  const [emojiPopdownPos, setEmojiPopdownPos]         = useState({ top: 0, left: 0 });
  const emojiPopdownRef = useRef(null);
  const emojiBtnRef    = useRef(null);
  const [showColorPopdown, setShowColorPopdown]     = useState(false);
  const [colorPopdownPos, setColorPopdownPos]       = useState({ top: 0, left: 0 });
  const colorPopdownRef = useRef(null);
  const colorBtnRef    = useRef(null);
  const [showBgColorPopdown, setShowBgColorPopdown] = useState(false);
  const [bgColorPopdownPos, setBgColorPopdownPos]   = useState({ top: 0, left: 0 });
  const bgColorPopdownRef = useRef(null);
  // The background popdown's own colour, mixed or picked. Only its "Use this colour" button sends it
  // to the AI replacement. label is the word the prompt uses: 'custom' or 'picked'.
  const [bgRemoveMix, setBgRemoveMix]         = useState(false);
  const [bgRemovePending, setBgRemovePending] = useState(null);
  const bgColorBtnRef     = useRef(null);
  const [namePopdownPos, setNamePopdownPos]         = useState({ top: 0, left: 0 });
  const namePopdownRef = useRef(null);
  const layerCounterRef = useRef(1000);

  // ── The third try goes to a different editor ────────────────────────────────────
  // Asking a model the same thing again gets the same answer again, and people do ask again:
  // three identical attempts is the ordinary shape of an edit a model simply will not do. So
  // once the same instruction has been run three times on a layer, the next one goes to Kling
  // instead of to the editors that have already declined it three times over.
  //
  // Keyed on the LAYER rather than on the picture, because an AI edit REPLACES the layer's
  // pixels: the bytes differ on every attempt while the thing the person is editing is plainly
  // the same thing, so hashing the image would never see a repeat. The count is per session —
  // a reload starts over, which is the forgiving way round for an editor that costs money.
  const aiTriesRef = useRef(new Map());
  // Three goes at the ordinary chain; the FOURTH run of the same instruction is Kling's.
  const AI_TRIES_ON_USUAL_CHAIN = 3;

  /**
   * Record one attempt and say which editor it should go to: undefined for the ordinary chain,
   * 'kling' once the same instruction has already been run AI_TRIES_ON_USUAL_CHAIN times on
   * this layer.
   */
  const aiProviderFor = (layerId, prompt) => {
    const text = String(prompt || '').trim().toLowerCase();
    if (!text) return undefined;
    const key = `${layerId ?? 'sheet'}\u0000${text}`;
    const n = (aiTriesRef.current.get(key) || 0) + 1;
    // Bounded, but by dropping the OLDEST entry rather than all of them: a Map iterates in
    // insertion order, so this is one eviction. Clearing the lot looked equivalent and was
    // not — every magic button carries its own long prompt and the tier and name edits put
    // the count and the name in the text, so two hundred distinct pairs is an ordinary
    // afternoon, and a wipe at that point reset a prompt already at three strikes back to
    // one and put the escalation permanently out of reach.
    if (aiTriesRef.current.size >= 400 && !aiTriesRef.current.has(key)) {
      aiTriesRef.current.delete(aiTriesRef.current.keys().next().value);
    }
    aiTriesRef.current.set(key, n);
    if (n <= AI_TRIES_ON_USUAL_CHAIN) return undefined;
    // Kling is for signed-in members. Signed out there is nothing to escalate to, so the
    // ordinary chain runs again and says honestly why it could not do the edit — rather than
    // asking for an editor the server will refuse and turning that into a different error.
    const member = !!authUser || getAuth().currentUser?.isAnonymous === false;
    return member ? 'kling' : undefined;
  };

  const markDirty = () => { isSavedRef.current = false; };

  const _rcDirs = ['n','ne','e','se','s','sw','w','nw'];
  const getRotatedCursor = (dir, rotation) => {
    const idx = _rcDirs.indexOf(dir);
    if (idx === -1) return `${dir}-resize`;
    const steps = (Math.round((rotation || 0) / 45) % 8 + 8) % 8;
    return `${_rcDirs[(idx + steps) % 8]}-resize`;
  };

  // A hand-drawn ink stroke's points are stored in pixels relative to its OWN recorded
  // canvasW/canvasH (so it can be rescaled if the on-screen canvas size ever differs), not in
  // the 0-100 percent coordinates shapes/images use. This gives its bounding-box origin as a
  // percentage of the sheet -- the same terms shapes/images already use for x1/x -- so a multi-
  // drag that mixes ink with shapes/images can treat every member the same way (see the
  // multi-drag arm/move code below). Returns null for paths with no points or no recorded size
  // (e.g. a mask patch), which callers skip.
  const inkPathOriginPct = (p) => {
    if (!p || !p.path || !p.path.length || !p.canvasW || !p.canvasH) return null;
    let minX = Infinity, minY = Infinity;
    for (const pt of p.path) { if (pt.x < minX) minX = pt.x; if (pt.y < minY) minY = pt.y; }
    if (!isFinite(minX) || !isFinite(minY)) return null;
    return { xPct: (minX / p.canvasW) * 100, yPct: (minY / p.canvasH) * 100 };
  };
  // Same idea as inkPathOriginPct but the full box, in percent -- used to test whether a
  // marquee-select rectangle overlaps an ink stroke.
  const inkPathBBoxPct = (p) => {
    if (!p || !p.path || !p.path.length || !p.canvasW || !p.canvasH) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const pt of p.path) {
      if (pt.x < minX) minX = pt.x; if (pt.x > maxX) maxX = pt.x;
      if (pt.y < minY) minY = pt.y; if (pt.y > maxY) maxY = pt.y;
    }
    if (!isFinite(minX) || !isFinite(minY)) return null;
    return { x1: (minX / p.canvasW) * 100, y1: (minY / p.canvasH) * 100, x2: (maxX / p.canvasW) * 100, y2: (maxY / p.canvasH) * 100 };
  };
  // Same box again but left in the path's OWN pixel space -- move/resize/rotate rewrite the
  // stored point coordinates directly (a stroke is pixels on one flat canvas, it has no
  // element of its own to transform), so that maths has to stay in the points' own units.
  const inkPathLocalBBox = (p) => {
    if (!p || !p.path || !p.path.length) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const pt of p.path) {
      if (pt.x < minX) minX = pt.x; if (pt.x > maxX) maxX = pt.x;
      if (pt.y < minY) minY = pt.y; if (pt.y > maxY) maxY = pt.y;
    }
    if (!isFinite(minX) || !isFinite(minY)) return null;
    return { minX, minY, maxX, maxY };
  };
  // Distance from a point to a line segment -- the primitive the ink hit-test is built on.
  const inkSegDist = (px, py, ax, ay, bx, by) => {
    const dx = bx - ax, dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    let t = lenSq > 0 ? ((px - ax) * dx + (py - ay) * dy) / lenSq : 0;
    if (t < 0) t = 0; else if (t > 1) t = 1;
    const qx = ax + t * dx, qy = ay + t * dy;
    return Math.hypot(px - qx, py - qy);
  };
  /**
   * Which hand-drawn stroke, if any, is under a press at (xPx, yPx) -- canvas-relative screen
   * pixels, rect being the canvas's own getBoundingClientRect.
   *
   * Shapes are SVG elements, so the browser hit-tests them for free; ink is pixels on one
   * shared canvas with pointerEvents:none, so there is nothing to hit-test against and a tap
   * on a drawn line used to do nothing at all (only a marquee drag could ever catch one).
   * This walks the stored polylines instead. The margin is 14 screen px -- a finger-sized
   * target, matching the invisible hit-stroke line/arrow shapes already carry -- plus half
   * the stroke's own width, so a fat brush stroke is grabbable across its whole body.
   *
   * Excluded: mask (eraser cut-outs) and fillmask (paint-bucket fills), which are baked
   * bitmaps with no polyline to test and nothing sensible to move; and eraser strokes, which
   * are invisible. Same set the marquee already skips.
   *
   * Later strokes paint over earlier ones, so the search runs back to front: the one you can
   * see on top is the one you get.
   */
  const inkPathAtPoint = (xPx, yPx, rect) => {
    if (!rect || !rect.width || !rect.height) return null;
    const paths = drawingPathsRef.current || [];
    for (let i = paths.length - 1; i >= 0; i--) {
      const p = paths[i];
      if (!p || !p.path || !p.path.length) continue;
      if (p.type === 'mask' || p.type === 'fillmask' || p.type === 'fill') continue;
      if (p.color === 'eraser') continue;
      // Screen px -> this path's own pixel units (the inverse of the scaleX/scaleY the
      // renderer uses).
      const scX = (p.canvasW || rect.width) / rect.width;
      const scY = (p.canvasH || rect.height) / rect.height;
      const lx = xPx * scX, ly = yPx * scY;
      // Half a fingertip either side of the line, plus half the stroke's own width (the 14
      // this started at was thinner than a fingertip lands, so a soft touch on a stroke often
      // selected nothing at all). The band matches the figures' invisible hit-stroke, and
      // widens on a phone for the same reason. Only ever consulted with no tool armed — see
      // the two call sites — so a wider band cannot steal a press meant for drawing.
      const margin = (TOUCH_HIT_PX / 2) * Math.max(scX, scY) + (p.size || 4) / 2;
      const b = inkPathLocalBBox(p);
      if (!b) continue;
      if (lx < b.minX - margin || lx > b.maxX + margin || ly < b.minY - margin || ly > b.maxY + margin) continue;
      if (p.path.length === 1) {
        if (Math.hypot(lx - p.path[0].x, ly - p.path[0].y) <= margin) return p.id;
        continue;
      }
      for (let j = 0; j < p.path.length - 1; j++) {
        if (inkSegDist(lx, ly, p.path[j].x, p.path[j].y, p.path[j + 1].x, p.path[j + 1].y) <= margin) return p.id;
      }
    }
    return null;
  };

  // ---- Shape constants & renderer ---------------------------------------
  /**
   * The fonts offered for text on the sheet.
   *
   * These were a dozen cake-shop display faces, inherited from when this was part of a cake app.
   * A studio for pictures of anything needs the ordinary ones first — you cannot set a caption,
   * a label or a price in Strawberry Script. Every family here is present on Windows, macOS,
   * Android and iOS, or falls back to something of the same kind, so a picture looks the same
   * wherever it is opened. The decorative faces are still installed (see the @font-face block in
   * the CSS) and anything already set in one keeps it.
   */
  // The last two carry Devanagari and Bengali (see index.html). They are ordinary choices in the
  // list rather than something that switches on with a language: a heading in Hindi inside an
  // otherwise English sheet is a normal thing to want.
  const SHAPE_FONTS = [
    'Arial', 'Helvetica', 'Verdana', 'Tahoma', 'Trebuchet MS',
    'Times New Roman', 'Georgia', 'Palatino Linotype', 'Courier New',
    'Impact', 'Comic Sans MS', 'Brush Script MT',
    'Noto Sans Devanagari', 'Noto Sans Bengali',
  ];

  const handleShapeMouseDown = (e, shapeId) => {
    e.stopPropagation();
    // While the eyedropper is out, a touch on a figure is a colour to take, not a figure to select.
    if (eyedropperActive) return;
    // Fill tool: a tap on a closed figure colours it instead of selecting it.
    if (activeTool === 'draw' && drawingMode === 'fill') {
      const t = e.touches ? e.touches[0] : e;
      if (t) handleFillClick({ clientX: t.clientX, clientY: t.clientY });
      return;
    }
    shapeClickedRef.current = true;

    // Format painter: apply stored format to the touched shape then deactivate
    if (formatPainterActive && formatPainterDataRef.current) {
      const fmt = formatPainterDataRef.current;
      setCanvasShapes(prev => prev.map(s => {
        if (s.id !== shapeId) return s;
        if (s.type === 'text') {
          return {
            ...s,
            strokeColor: fmt.strokeColor,
            ...(fmt.fontSize != null   && { fontSize: fmt.fontSize }),
            ...(fmt.fontFamily != null && { fontFamily: fmt.fontFamily }),
            ...(fmt.bold != null       && { bold: fmt.bold }),
            ...(fmt.italic != null     && { italic: fmt.italic }),
          };
        }
        if (s.type === 'emoji') return s;
        return { ...s, strokeColor: fmt.strokeColor, fillColor: fmt.fillColor, strokeWidth: fmt.strokeWidth, dashed: fmt.dashed };
      }));
      markDirty();
      setFormatPainterActive(false);
      formatPainterDataRef.current = null;
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || (e.touches && e.touches[0].clientX)) - rect.left;
    const y = (e.clientY || (e.touches && e.touches[0].clientY)) - rect.top;
    const xPct = (x / rect.width) * 100;
    const yPct = (y / rect.height) * 100;

    // Multi-drag: user presses down on one of the already-multi-selected shapes/images
    if (imageSelectionMode && selectedShapeIds.size > 1 && selectedShapeIds.has(shapeId)) {
      shapeBeforeRef.current = canvasShapesRef.current;
      dragStartImagesRef.current = canvasImagesRef.current;
      dragStartPathsRef.current = drawingPaths;
      const _msShapes = canvasShapesRef.current.filter(s => selectedShapeIds.has(s.id));
      const _msImages = canvasImagesRef.current.filter(i => selectedShapeIds.has(i.id));
      const _msInk    = drawingPaths.filter(p => selectedShapeIds.has(p.id));
      multiDragRef.current = [
        ..._msShapes.map(s => ({ id: s.id, offsetX: x - (s.x1 / 100) * rect.width, offsetY: y - (s.y1 / 100) * rect.height })),
        ..._msImages.map(i => ({ id: i.id, offsetX: x - (i.x / 100) * rect.width, offsetY: y - (i.y / 100) * rect.height, isImage: true })),
        ..._msInk.map(p => {
          const o = inkPathOriginPct(p);
          return o && { id: p.id, offsetX: x - (o.xPct / 100) * rect.width, offsetY: y - (o.yPct / 100) * rect.height, isInk: true };
        }).filter(Boolean),
      ];
      return;
    }

    // Clicking outside the multi-selected set -> clear multi-select
    if (selectedShapeIds.size > 0 && !selectedShapeIds.has(shapeId)) {
      setSelectedShapeIds(new Set());
    }

    // If clicking an already-selected TEXT shape, set up drag normally but mark for
    // edit-mode activation on mouseup (only if no drag movement happened)
    if (shapeId === selectedShapeId) {
      const shape = canvasShapesRef.current.find(s => s.id === shapeId);
      if (shape && shape.type === 'text') {
        textEditCandidateRef.current = shape.id;
        shapeBeforeRef.current = canvasShapesRef.current;
        shapeClickedRef.current = true;
        draggingShapeRef.current = {
          id: shape.id,
          offsetX: x - (shape.x1 / 100) * rect.width,
          offsetY: y - (shape.y1 / 100) * rect.height,
        };
        return;
      }
    }

    // Pressing the already-selected shape can mean "give me the one underneath" — a real and
    // useful gesture when things overlap. It used to happen at once, on the press itself, which
    // is also the press that BEGINS A DRAG: so with two shapes on top of each other you took
    // hold of the top one and the one beneath moved instead. Copying made that a certainty,
    // because a copy lands 2% from its source — all but on top of it — so every attempt to move
    // the copy moved the original, and the selection seemed to jump about at random.
    //
    // The press now always keeps hold of what was pressed. The cycle is deferred to the release
    // and only happens if the press never moved, which is exactly what separates a second tap
    // from the start of a drag.
    const targetId = shapeId;
    cycleUnderRef.current = null;
    if (shapeId === selectedShapeId) {
      const shapesAtPoint = canvasShapesRef.current
        .filter(s => {
          if (!s.id) return false;
          const minX = Math.min(s.x1, s.x2), maxX = Math.max(s.x1, s.x2);
          const minY = Math.min(s.y1, s.y2), maxY = Math.max(s.y1, s.y2);
          if (s.type === 'circle') {
            const cx = (s.x1 + s.x2) / 2, cy = (s.y1 + s.y2) / 2;
            const rx = (maxX - minX) / 2, ry = (maxY - minY) / 2;
            if (rx === 0 || ry === 0) return false;
            return ((xPct - cx) / rx) ** 2 + ((yPct - cy) / ry) ** 2 <= 1;
          }
          return xPct >= minX && xPct <= maxX && yPct >= minY && yPct <= maxY;
        })
        .sort((a, b) => (b.layerIndex ?? 4) - (a.layerIndex ?? 4));
      const curIdx = shapesAtPoint.findIndex(s => s.id === shapeId);
      if (curIdx !== -1 && shapesAtPoint.length > 1) {
        cycleUnderRef.current = { from: shapeId, to: shapesAtPoint[(curIdx + 1) % shapesAtPoint.length].id };
      }
    }

    setSelectedShapeId(targetId);
    setSelectedImage(null);
    setShowImageControls(null);
    const shape = canvasShapesRef.current.find(s => s.id === targetId);
    if (shape) {
      // Reset resize/rotate mode so colour controls show for the freshly selected shape
      setIsResizingShape(false);
      setIsRotatingShape(false);
      shapeBeforeRef.current = canvasShapesRef.current;
      draggingShapeRef.current = {
        id: targetId,
        offsetX: x - (shape.x1 / 100) * rect.width,
        offsetY: y - (shape.y1 / 100) * rect.height,
      };
      if (positionModeRef.current && positionModeForRef.current !== targetId) {
        // Move belongs to a different object: this one opens on its own colour and style.
        positionModeForRef.current = null;
        positionModeRef.current = false;
        setPositionMode(false);
      }
      if (positionModeRef.current) {
        // Move is on: this object's POSITION is what the user asked to see, so show the
        // coordinates and leave the palette out of it — including not re-arming the figure
        // tool, which would otherwise start drawing a new shape on the next tap of the sheet.
        setCurrentControlSection('selectmove');
        setSelectMoveControlMode(prev => (prev === 'resize' || prev === 'rotate') ? prev : 'position');
        setShowTopControls(true);
      } else {
        // A drawn object opens with its colour & style controls; the Position button switches
        // to coordinates (images keep their position-first behaviour).
        const section = shape.type === 'emoji' ? 'shapes-emoji' : shape.type === 'text' ? 'shapes-text' : 'shapes-draw';
        setActiveShapeTool(shape.type);
        setActiveTool('shapes');
        setCurrentControlSection(section);
        // Sync stored shape properties to toolbar controls
        if (shape.strokeColor) setShapeStrokeColor(shape.strokeColor);
        if (shape.fillColor)   setShapeFillColor(shape.fillColor);
        if (shape.strokeWidth) setShapeStrokeWidth(shape.strokeWidth);
        setShapeDashed(shape.dashed || false);
        if (shape.type === 'text' || shape.type === 'emoji') {
          if (shape.fontSize)    setShapeFontSize(shape.fontSize || shape.emojiSize || 18);
          if (shape.fontFamily)  setShapeFontFamily(shape.fontFamily);
          if (shape.bold   != null) setShapeTextBold(shape.bold);
          if (shape.italic != null) setShapeTextItalic(shape.italic);
          setShapeTextUnderline(!!shape.underline);
          setShapeText3D(!!shape.threeD);
        }
        setShowTopControls(true);
      }
    }
  };

  // A freeform shape can be filled once its outline is effectively a closed loop, whether or
  // not it was drawn with the dedicated "closed shape" tool: a loop drawn with the open
  // freeline tool that happens to return to (near) its own starting point is, visually, just
  // as closed. `pts` are stored as 0..1 fractions of the shape's own bounding box, so a small
  // fixed fraction works as the threshold regardless of the shape's actual size.
  const FREEFORM_CLOSE_GAP = 0.08;
  const isFreeformClosed = (shape) => {
    if (!shape || shape.type !== 'freeform') return false;
    if (shape.closed) return true;
    const pts = shape.pts;
    if (!pts || pts.length < 3) return false;
    const a = pts[0], b = pts[pts.length - 1];
    return Math.hypot(a.x - b.x, a.y - b.y) <= FREEFORM_CLOSE_GAP;
  };

  /**
   * Turn one eraser stroke into the form a figure stores it in: points as fractions of the
   * figure's OWN box, with the figure's own turn undone, so the hole travels and scales with
   * it. `pts` are canvas-relative pixels; cW/cH are the canvas's size in the same units.
   * Returns null when the stroke misses this figure entirely.
   *
   * Shared by the live preview while the finger is still down (applyErase, below) and by the
   * commit on release (applyEraseToShapes), so the two cannot drift apart.
   */
  const eraseStrokeForShape = (pts, widthPx, s, cW, cH) => {
    if (!pts || !pts.length) return null;
    const bx = (Math.min(s.x1, s.x2) / 100) * cW;
    const by = (Math.min(s.y1, s.y2) / 100) * cH;
    const bw = (Math.abs(s.x2 - s.x1) / 100) * cW;
    const bh = (Math.abs(s.y2 - s.y1) / 100) * cH;
    if (bw <= 0 || bh <= 0) return null;
    const scx = bx + bw / 2, scy = by + bh / 2;
    const a = -((s.rotation || 0) * Math.PI) / 180;
    const ca = Math.cos(a), sa = Math.sin(a);
    const local = pts.map(p => {
      const dx = p.x - scx, dy = p.y - scy;
      return { x: ((scx + dx * ca - dy * sa) - bx) / bw, y: ((scy + dx * sa + dy * ca) - by) / bh };
    });
    const half = widthPx / 2 + 1;
    const mx = half / bw, my = half / bh;
    if (!local.some(p => p.x >= -mx && p.x <= 1 + mx && p.y >= -my && p.y <= 1 + my)) return null;
    if (local.length === 1) local.push({ x: local[0].x + 0.001, y: local[0].y });
    return { pts: local, w: widthPx / Math.max(1, Math.min(bw, bh)) };
  };

  /**
   * Break a piece of text into the lines it needs at a given width.
   *
   * Text used to be one long line whatever happened: it ran off the side of the sheet while it
   * was being typed and stayed off it afterwards, and the drawn version was squeezed to fit its
   * box by SVG's textLength — so the only thing that ever changed was the letter spacing. This
   * measures real glyphs in the real font and breaks between words, falling back to breaking
   * inside a word only when a single word is wider than the whole box.
   *
   * Wrapping happens at DRAW time, not when the text is typed, so widening a text box reflows it.
   */
  const _measureCtx = useRef(null);
  const wrapTextLines = (text, font, maxPx) => {
    const raw = String(text ?? '');
    if (!raw) return [''];
    if (!(maxPx > 0)) return raw.split('\n');
    if (!_measureCtx.current) _measureCtx.current = document.createElement('canvas').getContext('2d');
    const m = _measureCtx.current;
    m.font = font;
    const width = (t) => m.measureText(t).width;
    const out = [];
    for (const para of raw.split('\n')) {
      if (!para) { out.push(''); continue; }
      let line = '';
      for (const word of para.split(/(\s+)/)) {
        if (!word) continue;
        const next = line + word;
        if (width(next) <= maxPx || !line.trim()) {
          // A single word too wide for the box has to be broken somewhere; one character at a
          // time is the only answer that always terminates.
          if (width(next) > maxPx && !line.trim() && word.trim()) {
            let chunk = '';
            for (const ch of word) {
              if (chunk && width(chunk + ch) > maxPx) { out.push(chunk); chunk = ch; }
              else chunk += ch;
            }
            line = chunk;
            continue;
          }
          line = next;
        } else {
          out.push(line.replace(/\s+$/, ''));
          line = word.replace(/^\s+/, '');
        }
      }
      out.push(line.replace(/\s+$/, ''));
    }
    return out.length ? out : [''];
  };
  const textFontOf = (shape, fs) =>
    `${shape.italic ? 'italic ' : ''}${shape.bold ? 'bold ' : ''}${fs}px ${shape.fontFamily || 'Arial'}`;

  // Changing the letters changes how many lines there are, so the bottom edge of the box follows
  // them. The corner it sits on and the width it wraps at are the user's and are never touched —
  // that is the whole point of the box — but leaving the height behind put the handles around
  // where the text used to be and let the letters run out of the bottom of their own box.
  // ── Resizing a text ──────────────────────────────────────────────────────────────────────
  // A corner handle scales the text: letters and box grow and shrink together, measured from the
  // box as it was when the drag began (so nothing accumulates rounding as the pointer moves), and
  // the opposite corner stays put. A side handle changes the width the words wrap at and leaves
  // the letters alone. Either way the box's height is the text's height: it used to follow the
  // pointer freely while the letters stayed small, leaving a tall empty frame that jumped about.
  const resizeTextShape = (s0, corner, xPct, yPct, rect) => {
    const W = rect.width, H = rect.height;
    const ax0 = Math.min(s0.x1, s0.x2), ax1 = Math.max(s0.x1, s0.x2);
    const ay0 = Math.min(s0.y1, s0.y2), ay1 = Math.max(s0.y1, s0.y2);
    const w0 = Math.max(0.5, ax1 - ax0), h0 = Math.max(0.5, ay1 - ay0);
    const fs0 = s0.fontSize || 12;
    const left = corner === 'w' || corner === 'nw' || corner === 'sw' || corner === 'p1';
    const top = corner === 'n' || corner === 'nw' || corner === 'ne' || corner === 'p1';
    const isCorner = ['nw', 'ne', 'sw', 'se', 'p1', 'p2'].includes(corner);
    let wPct = w0, fs = fs0;
    if (isCorner) {
      // How far along the box's own diagonal the pointer has gone, from the fixed corner.
      const axp = left ? ax1 : ax0, ayp = top ? ay1 : ay0;
      const dx = (left ? -w0 : w0) / 100 * W, dy = (top ? -h0 : h0) / 100 * H;
      const px = (xPct - axp) / 100 * W, py = (yPct - ayp) / 100 * H;
      let f = (px * dx + py * dy) / Math.max(1, dx * dx + dy * dy);
      f = Math.max(6 / fs0, Math.min(400 / fs0, f));
      fs = Math.round(fs0 * f * 10) / 10;
      wPct = w0 * (fs / fs0);
    } else if (corner === 'e' || corner === 'w') {
      wPct = Math.max((fs0 * 1.2 / W) * 100, left ? ax1 - xPct : xPct - ax0);
    } else {
      return s0;                                   // top and bottom: the text decides its height
    }
    const lines = wrapTextLines(s0.text, textFontOf(s0, fs), Math.max(1, (wPct / 100) * W));
    const hPct = ((Math.max(1, lines.length) * fs * 1.2) / H) * 100;
    const x1 = left ? ax1 - wPct : ax0;
    const y1 = top ? ay1 - hPct : ay0;
    return { ...s0, fontSize: fs, x1, y1, x2: x1 + wPct, y2: y1 + hPct };
  };

  const refitTextBox = (s) => {
    if (!s || s.type !== 'text') return s;
    const cW = canvasRef.current?.offsetWidth  || 595;
    const cH = canvasRef.current?.offsetHeight || 841;
    const x0 = Math.min(s.x1, s.x2), y0 = Math.min(s.y1, s.y2);
    const wPct = Math.abs(s.x2 - s.x1);
    const fs = s.fontSize || 12;
    // An emoji's box is cut around the glyph on BOTH sides — words reflow inside a box whose
    // width you chose, a picture does not. Keeping the old width here would leave the face
    // hanging out of its own selection box the moment a bigger size was picked from the menu.
    if (isEmojiGlyph(s)) {
      return { ...s, x1: x0, y1: y0,
        x2: x0 + (fs * EMOJI_BOX_W / cW) * 100,
        y2: Math.min(100, y0 + (fs * EMOJI_BOX_H / cH) * 100) };
    }
    const lines = wrapTextLines(s.text, textFontOf(s, fs), Math.max(1, (wPct / 100) * cW));
    const hPct = ((Math.max(1, lines.length) * fs * 1.2) / cH) * 100;
    return { ...s, x1: x0, y1: y0, x2: x0 + wPct, y2: Math.min(100, y0 + hPct) };
  };

  // ── Finger-width hit target for an unselected figure ──────────────────────────────────
  // A figure's outline is hit-tested on the pixels it actually paints, which at the default
  // 2px stroke is a target no fingertip can land on: on a phone a soft tap on the edge simply
  // missed, and only a lucky repeated press caught it — by which time the long-press timer had
  // fired and opened the caption box instead. So each unselected figure also renders an
  // invisible, finger-width copy of its OWN outline: same geometry and the same handlers,
  // transparent, hit-tested on the stroke only. The interior stays click-through (fill none),
  // so drawing inside a traced outline still works exactly as before.
  const HIT_TAGS = new Set(['path', 'line', 'polyline', 'polygon', 'rect', 'circle', 'ellipse']);
  const toHitLayer = (node, key) => {
    if (!isValidElement(node)) return null;
    if (node.type === 'g') {
      const kids = Children.map(node.props.children, (k, i) => toHitLayer(k, `${key}-${i}`));
      const any = (kids || []).some(Boolean);
      return any ? cloneElement(node, { key, style: undefined, filter: undefined, mask: undefined }, kids) : null;
    }
    if (typeof node.type !== 'string' || !HIT_TAGS.has(node.type)) return null;
    return cloneElement(node, {
      key,
      stroke: 'transparent',
      // Keep whatever the figure already had if it is thicker than a fingertip needs.
      strokeWidth: Math.max(Number(node.props.strokeWidth) || 0, TOUCH_HIT_PX),
      strokeDasharray: undefined,   // a dashed edge must not have gaps you cannot tap
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      fill: 'none',
      style: { ...(node.props.style || {}), pointerEvents: 'stroke', filter: 'none', cursor: 'move' },
    });
  };

  const renderShapeElement = (shape, cW, cH) => {
    const x1 = (shape.x1 / 100) * cW;
    const y1 = (shape.y1 / 100) * cH;
    const x2 = (shape.x2 / 100) * cW;
    const y2 = (shape.y2 / 100) * cH;
    const cx = (x1 + x2) / 2;
    const cy = (y1 + y2) / 2;
    const rx = Math.abs(x2 - x1) / 2;
    const ry = Math.abs(y2 - y1) / 2;
    const stroke = shape.strokeColor || '#000000';
    const fill   = shape.fillColor   || 'none';
    const sw     = shape.strokeWidth || 2;
    const isPreview = shape.id === 'preview';
    const isSel  = !isPreview && (selectedShapeId === shape.id || selectedShapeIds.has(shape.id));
    const rot    = shape.rotation || 0;
    const evProps = isPreview ? {} : {
      style: {
        cursor: 'move',
        // UNSELECTED: only the shape's own drawn line is a hit target ('visiblePainted' --
        // paints-only, so a filled interior with nothing actually painted there is click-
        // through), whether or not it has a fill. That is what lets a pen stroke land inside a
        // hand-traced outline (or a filled figure) instead of the shape swallowing every touch
        // meant for the canvas underneath -- touch the outline itself to select it.
        // SELECTED: the whole body becomes live ('all'), so touching anywhere inside it -- not
        // just its outline -- drags it; the corner/rotate handles (rendered above the shape,
        // higher z-index) still take an edge touch first for resize/rotate. 3D shapes are
        // always a solid drawn body with no meaningful "interior" distinct from their outline,
        // so they keep the old always-clickable behaviour.
        pointerEvents: isShape3D(shape.type) ? 'all' : (isSel ? 'all' : 'visiblePainted'),
        filter: isSel ? 'drop-shadow(0 0 3px #fa4848) drop-shadow(0 0 6px #fa4848) drop-shadow(0 0 10px rgba(250,72,72,0.7))' : undefined,
      },
      onMouseDown: (e) => startShapeSelectPress(e, shape),
      onTouchStart: (e) => startShapeSelectPress(e, shape),
      onDoubleClick: (e) => { e.stopPropagation(); openShapeLabelEditor(shape); },
    };
    const common = { stroke, strokeWidth: sw, vectorEffect: 'non-scaling-stroke', ...(shape.dashed ? { strokeDasharray: '6 3' } : {}) };
    // Helper: wrap element in rotation group if needed
    const withRotation = (el, pivotX, pivotY) => {
      if (!rot || isPreview) return el;
      return <g key={shape.id} transform={`rotate(${rot} ${pivotX} ${pivotY})`}>{el}</g>;
    };

    const shapeBody = (() => {
    switch (shape.type) {
      case 'line':
        return withRotation(
          <g key={shape.id}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="transparent" strokeWidth={Math.max(14, sw + 10)}
              style={{ pointerEvents: 'stroke', cursor: 'move' }}
              onMouseDown={isPreview ? undefined : (e) => startShapeSelectPress(e, shape)}
              onTouchStart={isPreview ? undefined : (e) => startShapeSelectPress(e, shape)}
              onDoubleClick={isPreview ? undefined : (e) => { e.stopPropagation(); openShapeLabelEditor(shape); }}
            />
            <line x1={x1} y1={y1} x2={x2} y2={y2} {...common} fill="none"
              style={{ pointerEvents: 'none', filter: isSel ? 'drop-shadow(0 0 3px #fa4848) drop-shadow(0 0 6px #fa4848) drop-shadow(0 0 10px rgba(250,72,72,0.7))' : undefined }} />
          </g>, cx, cy
        );

      case 'arrow': {
        const angle = Math.atan2(y2 - y1, x2 - x1);
        const aLen  = Math.max(10, sw * 5);
        const p1x = x2 - aLen * Math.cos(angle - 0.45);
        const p1y = y2 - aLen * Math.sin(angle - 0.45);
        const p2x = x2 - aLen * Math.cos(angle + 0.45);
        const p2y = y2 - aLen * Math.sin(angle + 0.45);
        return withRotation(
          <g key={shape.id} {...evProps}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="transparent" strokeWidth={Math.max(14, sw + 10)}
              style={{ pointerEvents: 'stroke', cursor: 'move' }}
              onMouseDown={isPreview ? undefined : (e) => startShapeSelectPress(e, shape)}
              onTouchStart={isPreview ? undefined : (e) => startShapeSelectPress(e, shape)}
            />
            <line x1={x1} y1={y1} x2={x2} y2={y2} {...common} fill="none" style={{ pointerEvents: 'none' }} />
            <polygon points={`${x2},${y2} ${p1x},${p1y} ${p2x},${p2y}`} fill={stroke} stroke="none" style={{ pointerEvents: 'none' }} />
          </g>, cx, cy
        );
      }

      case 'circle':
        return withRotation(<ellipse key={shape.id} cx={cx} cy={cy} rx={Math.max(1,rx)} ry={Math.max(1,ry)} {...common} fill={fill} {...evProps} />, cx, cy);

      case 'triangle': {
        const tx = cx, ty1v = Math.min(y1, y2);
        const tx2v = Math.min(x1, x2), ty2v = Math.max(y1, y2);
        const tx3v = Math.max(x1, x2), ty3v = Math.max(y1, y2);
        return withRotation(<polygon key={shape.id} points={`${tx},${ty1v} ${tx2v},${ty2v} ${tx3v},${ty3v}`} {...common} fill={fill} {...evProps} />, cx, cy);
      }

      case 'rectangle':
        return withRotation(<rect key={shape.id} x={Math.min(x1,x2)} y={Math.min(y1,y2)} width={Math.abs(x2-x1)} height={Math.abs(y2-y1)} {...common} fill={fill} {...evProps} />, cx, cy);

      case 'text': {
        const fs  = shape.fontSize   || 12;
        const ff  = shape.fontFamily || 'Arial';
        const fw  = shape.bold   ? 'bold'   : 'normal';
        const fi  = shape.italic ? 'italic' : 'normal';
        const tX  = Math.min(x1, x2);
        const tY  = Math.min(y1, y2);
        const tW  = Math.abs(x2 - x1);
        const tH  = Math.abs(y2 - y1);
        // Wrapped here rather than stored wrapped, so dragging the box wider reflows it. The
        // old textLength/lengthAdjust pair is gone with it: it did not wrap, it STRETCHED — a
        // long line was squeezed into the box by spacing the letters differently, which is why
        // text never went onto a second line however narrow the box got.
        const tLines = wrapTextLines(shape.text, textFontOf(shape, fs), tW);
        const tLH = fs * 1.2;
        // The outline sits BEHIND the fill (paint-order), so thickening it grows outwards and
        // never eats into the letterforms. Its colour is the object's fill colour — what the
        // second colour button in the bar sets — and white until somebody chooses one.
        const tOutline = Number(shape.outlineWidth) || 0;
        const tOutlineColor = (shape.fillColor && shape.fillColor !== 'none') ? shape.fillColor : '#ffffff';
        const tUnder  = !!shape.underline;
        // Raised letters: copies of the same words stepped down-and-right behind the face, in a
        // darker shade of the letter's own colour. Drawn first, so the face lands on top of them.
        const tDepth  = shape.threeD ? text3DDepth(fs) : 0;
        const tWall   = tDepth ? text3DWall(stroke) : null;
        const tGlow   = isSel ? 'drop-shadow(0 0 3px #fa4848) drop-shadow(0 0 6px #fa4848) drop-shadow(0 0 10px rgba(250,72,72,0.7))' : undefined;
        const tSpans  = tLines.map((ln, i) => (
          <tspan key={i} x={tX} dy={i === 0 ? 0 : tLH}>{ln || ' '}</tspan>
        ));
        const tFace = (
          <text key={shape.id}
            x={tX} y={tY}
            dominantBaseline="hanging"
            fill={stroke} fontSize={fs} fontFamily={ff} fontWeight={fw} fontStyle={fi}
            {...(tUnder ? { textDecoration: 'underline' } : {})}
            {...(tOutline > 0 ? { stroke: tOutlineColor, strokeWidth: tOutline * 2, strokeLinejoin: 'round', paintOrder: 'stroke' } : {})}
            style={{ cursor: 'move', pointerEvents: isSel ? 'all' : 'visiblePainted', userSelect: 'none', filter: tDepth ? undefined : tGlow }}
            onMouseDown={isPreview ? undefined : (e) => startShapeSelectPress(e, shape)}
            onTouchStart={isPreview ? undefined : (e) => startShapeSelectPress(e, shape)}
          >
            {tSpans}
          </text>
        );
        if (!tDepth) return withRotation(tFace, cx, cy);
        return withRotation(
          <g key={shape.id} style={{ filter: tGlow }}>
            {Array.from({ length: tDepth }, (_, k) => tDepth - k).map(k => (
              <text key={`w${k}`}
                x={tX + k} y={tY + k}
                dominantBaseline="hanging"
                fill={tWall} fontSize={fs} fontFamily={ff} fontWeight={fw} fontStyle={fi}
                {...(tUnder ? { textDecoration: 'underline' } : {})}
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                {tLines.map((ln, i) => (
                  <tspan key={i} x={tX + k} dy={i === 0 ? 0 : tLH}>{ln || ' '}</tspan>
                ))}
              </text>
            ))}
            {tFace}
          </g>, cx, cy
        );
      }

      case 'emoji': {
        const sW = Math.abs(x2 - x1) || 80;
        const sH = Math.abs(y2 - y1) || 80;
        const sX = Math.min(x1, x2);
        const sY = Math.min(y1, y2);
        // Actual image rendered as HTML <img> in a separate absolutely-positioned layer
        // (SVG <image> and <foreignObject> both fail cross-origin without CORS headers)
        // This rect is only for pointer events + selection indicator
        return withRotation(
          <rect key={shape.id} x={sX} y={sY} width={sW} height={sH}
            fill="transparent"
            stroke={isSel ? '#fa4848' : 'none'}
            strokeWidth={isSel ? 1.5 : 0}
            strokeDasharray={isSel ? '5 3' : undefined}
            style={{ cursor: 'move', pointerEvents: 'all' }}
            onMouseDown={isPreview ? undefined : (e) => startShapeSelectPress(e, shape)}
            onTouchStart={isPreview ? undefined : (e) => startShapeSelectPress(e, shape)}
            onDoubleClick={isPreview ? undefined : (e) => { e.stopPropagation(); openShapeLabelEditor(shape); }}
          />, cx, cy
        );
      }

      case 'freeform': {
        const bx = Math.min(x1, x2), by = Math.min(y1, y2);
        const bw = Math.max(1, Math.abs(x2 - x1)), bh = Math.max(1, Math.abs(y2 - y1));
        const shapeClosed = isFreeformClosed(shape);
        const d = freeformPath(shape.pts || [], bw, bh, shapeClosed, shape.smooth !== false);
        return withRotation(
          <path key={shape.id} d={d} transform={`translate(${bx} ${by})`} {...common}
            fill={shapeClosed ? fill : 'none'} strokeLinejoin="round" strokeLinecap="round" {...evProps} />, cx, cy);
      }
      default: {
        const bx = Math.min(x1, x2), by = Math.min(y1, y2);
        const bw = Math.max(1, Math.abs(x2 - x1)), bh = Math.max(1, Math.abs(y2 - y1));
        if (isShape2D(shape.type)) {
          const def = SHAPES_2D[shape.type];
          return withRotation(
            <path key={shape.id} d={def.path(bw, bh)} transform={`translate(${bx} ${by})`} {...common}
              fill={def.open ? 'none' : fill} strokeLinejoin="round" strokeLinecap="round" {...evProps} />, cx, cy);
        }
        if (isShape3D(shape.type)) {
          // Real geometry: the object is turned by its three angles, projected, lit and drawn
          // back to front (see lib/shape3d.js). Tilt and spin therefore show genuinely
          // different faces, and the shading follows the light instead of being painted on.
          // rz is the same in-plane turn the rotate handle has always set, so it is applied
          // here rather than through withRotation -- turning the mesh keeps the lighting right,
          // where an outer SVG rotate would have carried the highlights around with it.
          const body = bodyColour3D(shape);
          const faces = has3DMesh(shape.type)
            ? project3D(shape.type, bw, bh, { ...pose3D(shape), rz: rot }, body)
            : [];
          if (!faces.length) {
            // No mesh for this type (a figure added to the library without one): fall back to
            // the original painted-on drawing rather than showing nothing at all.
            const def = SHAPES_3D[shape.type];
            if (!def) return null;
            const t = tones3D(body);
            const gid = `g3-${shape.id}`;
            const fillFor = (kind) => kind === 'radial' ? `url(#${gid}-r)` : kind === 'hgrad' ? `url(#${gid}-h)` : kind === 'vgrad' ? `url(#${gid}-v)` : (t[kind] || t.base);
            return withRotation(
              <g key={shape.id} transform={`translate(${bx} ${by})`} {...evProps}>
                <defs>
                  <radialGradient id={`${gid}-r`} cx="35%" cy="30%" r="75%"><stop offset="0" stopColor={t.light} /><stop offset="0.55" stopColor={t.base} /><stop offset="1" stopColor={t.darker} /></radialGradient>
                  <linearGradient id={`${gid}-h`} x1="0" x2="1" y1="0" y2="0"><stop offset="0" stopColor={t.light} /><stop offset="0.45" stopColor={t.base} /><stop offset="1" stopColor={t.darker} /></linearGradient>
                  <linearGradient id={`${gid}-v`} x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor={t.light} /><stop offset="0.45" stopColor={t.base} /><stop offset="1" stopColor={t.darker} /></linearGradient>
                </defs>
                {def.parts(bw, bh).map((p, i) => (
                  <path key={i} d={p.d} fillRule={p.evenOdd ? 'evenodd' : undefined} fill={fillFor(p.fill)}
                    stroke={t.darker} strokeWidth={Math.max(0.4, (shape.strokeWidth || 2) * 0.35)} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                ))}
                {/* The ball's seams: the part of it that turns when it is turned (sphereSeams3D). */}
                {shape.type === 'sphere' && (
                  <path
                    d={sphereSeams3D(bw, bh, pose3D(shape)).map(run => 'M' + run.map(pt => pt.join(' ')).join('L')).join('')}
                    fill="none" stroke={t.darker} strokeOpacity={0.55} strokeLinecap="round" strokeLinejoin="round"
                    strokeWidth={Math.max(0.8, Math.min(bw, bh) * 0.014)}
                    style={{ pointerEvents: 'none' }}
                  />
                )}
              </g>, cx, cy);
          }
          return (
            <g key={shape.id} transform={`translate(${bx} ${by})`} {...evProps}>
              {faces.map((f, i) => (
                <path
                  key={i}
                  d={facePath(f.pts)}
                  fill={f.fill}
                  // Hairline in the face's own colour: without it the antialiased edges of
                  // neighbouring faces leave pale seams across a curved surface.
                  stroke={f.fill}
                  strokeWidth={0.7}
                  strokeLinejoin="round"
                />
              ))}
            </g>
          );
        }
        return null;
      }
    }
    })();

    // Caption: any 2D or 3D shape (everything above except the dedicated 'text' tool, which
    // already renders its own content) can carry a short label set via double-click / long-
    // press. Rendered upright and centred over the shape rather than baked into its rotation,
    // so it stays readable no matter how the shape itself is turned, with a soft white halo
    // (via paintOrder) so it reads over any fill colour.
    /**
     * Eraser on a figure. Whatever the eraser has been rubbed over is hidden behind an SVG
     * mask rather than deleted, so the figure stays a real figure -- still selectable,
     * movable, resizable, recolourable -- with a hole in it.
     *
     * shape.erase holds each rub as points in fractions of the figure's OWN box, so the hole
     * travels and scales with the figure instead of staying behind on the sheet. The mask
     * re-applies the figure's rotation, so the hole turns with it too; the strokes themselves
     * are therefore stored unrotated (see applyEraseToShapes).
     */
    const applyErase = (el) => {
      if (isPreview) return el;
      const stored = Array.isArray(shape.erase) ? shape.erase : [];
      // The stroke still under the finger. Including it here is what makes a figure rub away
      // AS you move, the way ink already does, instead of all at once when you let go -- the
      // commit on release (applyEraseToShapes) then stores this very same stroke.
      const live = (isDrawing && activeTool === 'draw' && drawingMode === 'eraser' && currentPath.length)
        ? eraseStrokeForShape(currentPath, brushSize, shape, cW, cH)
        : null;
      const strokes = live ? [...stored, live] : stored;
      if (!strokes.length) return el;
      const bx = Math.min(x1, x2), by = Math.min(y1, y2);
      const bw = Math.abs(x2 - x1) || 1, bh = Math.abs(y2 - y1) || 1;
      // The drawn line and the selection glow both spill past the box, so the mask region is
      // padded -- anything outside a mask's region is treated as black, i.e. hidden.
      const pad = Math.max(40, sw * 4);
      const unit = Math.min(bw, bh) || 1;
      const mId = `is-erase-${shape.id}`;
      return (
        <g key={shape.id}>
          <defs>
            <mask id={mId} maskUnits="userSpaceOnUse" x={bx - pad} y={by - pad} width={bw + pad * 2} height={bh + pad * 2}>
              <rect x={bx - pad} y={by - pad} width={bw + pad * 2} height={bh + pad * 2} fill="#fff" />
              <g transform={rot ? `rotate(${rot} ${cx} ${cy})` : undefined}>
                {strokes.map((st, i) => (
                  <polyline
                    key={i}
                    points={(st.pts || []).map(p => `${bx + p.x * bw},${by + p.y * bh}`).join(' ')}
                    fill="none" stroke="#000"
                    strokeWidth={Math.max(1, (st.w || 0.05) * unit)}
                    strokeLinecap="round" strokeLinejoin="round"
                  />
                ))}
              </g>
            </mask>
          </defs>
          <g mask={`url(#${mId})`}>{el}</g>
        </g>
      );
    };

    // An already-selected figure is draggable over its whole body, and 3D solids are too, so
    // neither needs the extra target; a preview is not interactive at all.
    const bodyWithHit = (() => {
      if (isPreview || isSel || isShape3D(shape.type)) return shapeBody;
      const hit = toHitLayer(shapeBody, `${shape.id}-hit`);
      return hit ? <g key={`${shape.id}-hitwrap`}>{hit}{shapeBody}</g> : shapeBody;
    })();

    if (isPreview || shape.type === 'text' || !shape.text) return applyErase(bodyWithHit);
    const capLines = String(shape.text).split('\n').slice(0, 6);
    const capFs = Math.max(9, Math.min(28, Math.min(Math.abs(x2 - x1), Math.abs(y2 - y1)) * 0.18)) || 12;
    const capLH = capFs * 1.15;
    const capY0 = cy - ((capLines.length - 1) * capLH) / 2;
    const capRot = shape.textLevel ? 0 : (shape.rotation || 0);
    // A caption is text, and takes a typeface like any other text — the same Font button sets it.
    // It keeps its OWN keys rather than borrowing the text tool's: fontFamily/bold/italic/
    // underline/threeD belong to a 'text' shape, and fontSize on an emoji is the size of the
    // picture, not of its label. Nothing set is exactly what captions have always looked like
    // — Arial at weight 600 — so no sheet drawn before this changes. The size stays fitted to
    // the figure: a label lives inside the thing it names.
    const capFam = shape.captionFont || 'Arial, sans-serif';
    const capFw  = shape.captionBold ? 'bold' : 600;
    const capFi  = shape.captionItalic ? 'italic' : 'normal';
    const capDec = shape.captionUnderline ? { textDecoration: 'underline' } : {};
    const capInk = shape.captionColor || '#1a1a1a';
    const capDep = shape.caption3D ? text3DDepth(capFs) : 0;
    const capSpans = (xx) => capLines.map((line, i) => <tspan key={i} x={xx} dy={i === 0 ? 0 : capLH}>{line}</tspan>);
    return applyErase(
      <g key={shape.id}>
        {bodyWithHit}
        {/* The caption turns with the thing it names — but only through the turning that happened
            AFTER it was written (see commitShapeLabel). Written first, it rides round with the
            figure; written on a figure already lying on its side, it stays level and readable. */}
        <g transform={capRot ? `rotate(${capRot} ${cx} ${cy})` : undefined}>
          {/* Raised letters: stepped copies of the same words behind the face, in a darker shade
              of the caption's own colour — the same construction the text tool uses. */}
          {capDep > 0 && Array.from({ length: capDep }, (_, k) => capDep - k).map(k => (
            <text key={`cw${k}`}
              x={cx + k} y={capY0 + k} textAnchor="middle" dominantBaseline="central"
              fill={text3DWall(capInk)} fontSize={capFs} fontFamily={capFam} fontWeight={capFw} fontStyle={capFi}
              {...capDec}
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
              {capSpans(cx + k)}
            </text>
          ))}
          <text
            x={cx} y={capY0} textAnchor="middle" dominantBaseline="central"
            fill={capInk} fontSize={capFs} fontFamily={capFam} fontWeight={capFw} fontStyle={capFi}
            {...capDec}
            style={{
              pointerEvents: 'none', userSelect: 'none',
              paintOrder: 'stroke', stroke: 'rgba(255,255,255,0.8)', strokeWidth: Math.max(1.5, capFs * 0.14), strokeLinejoin: 'round',
            }}
          >
            {capSpans(cx)}
          </text>
        </g>
      </g>
    );
  };
  // -----------------------------------------------------------------------

  // Shape caption editor -- double-click (desktop) or long-press (touch) on any shape other
  // than the dedicated text tool opens a small inline editor that sets shape.text WITHOUT
  // touching its x1/y1/x2/y2 geometry (unlike finalizePendingText, which resizes the shape
  // to fit -- fine for the text tool itself, but would corrupt every other shape's size).
  const openShapeLabelEditor = (shape) => {
    if (!shape || shape.id === 'preview' || shape.type === 'text') return;
    setSelectedShapeId(shape.id);
    setPendingLabelShapeId(shape.id);
    setPendingLabelValue(shape.text || '');
  };
  const commitShapeLabel = () => {
    if (pendingLabelShapeId) {
      const _before = canvasShapesRef.current;
      const _val = pendingLabelValue.trim();
      // Was the figure already turned when these words were typed?
      //
      // A label put on an upright figure belongs to the drawing: turn the figure afterwards and
      // the label goes round with it, the way a word painted on a box does. A label typed onto a
      // figure that was ALREADY lying at an angle was written to be READ, so it stays level and
      // keeps staying level however the figure is turned after that. Re-opening the label and
      // pressing enter re-decides it, which is how you switch a label from one to the other.
      const _after = _before.map(s => s.id === pendingLabelShapeId
        ? { ...s, text: _val, textLevel: !!(s.rotation || 0) }
        : s);
      setCanvasShapes(_after);
      pushShapeHistory(_before, _after);
      markDirty();
    }
    setPendingLabelShapeId(null);
    setPendingLabelValue('');
  };
  const cancelShapeLabelEditor = () => { setPendingLabelShapeId(null); setPendingLabelValue(''); };
  // Long press used to open the name box. It is gone, because it cannot share a finger with
  // dragging: the timer starts the moment you touch down, so any press that pauses before it
  // moves — which is most of them on a phone — opened the name box instead of picking the
  // object up. Double tap does the same job and can never be mistaken for the start of a drag,
  // so that (and double-click on a mouse) is the only way in now.

  // A quick touch/click on a shape used to select it immediately, which made it impossible to
  // draw over or inside an existing shape (or start a new one on top of it) -- the very first
  // touch of the gesture was captured as a select instead of reaching the canvas underneath.
  //
  // The fix for that was once a long press (touch) / double-click (mouse) on EVERY press. That
  // turned out far worse: selection felt dead on phone and iPad, and a long press that drifted
  // a few pixels left a stray half-drawn figure behind. The conflict is much narrower than that
  // -- it only exists while a pen or figure tool is actually armed, so that is the only case
  // gated now. With a tool armed the press belongs to the tool and passes straight through;
  // with no tool armed (the pointer -- how selecting is normally done) a single tap or click
  // selects immediately, exactly as it always did. Nothing is timed or cancelled either way,
  // so a press can never leave a stray figure behind.
  const startShapeSelectPress = (e, shape) => {
    // Fill tool / format painter: one-shot actions on the shape itself, always immediate.
    if ((activeTool === 'draw' && drawingMode === 'fill') || (formatPainterActive && formatPainterDataRef.current)) {
      e.stopPropagation();
      handleShapeMouseDown(e, shape.id);
      return;
    }
    // Touching a figure's own drawn line ALWAYS selects it, whatever tool is in hand.
    //
    // This used to pass the press through to the tool whenever a pen or figure tool was armed,
    // so that a stroke or a new figure could begin on top of an existing one. In practice that
    // read as broken: the figure tool stays armed after you draw, so the next tap on a figure
    // appeared to do nothing at all. Nothing is really lost -- an unselected figure is only a
    // target on the line it actually paints (pointerEvents below), so the empty space inside it
    // still passes straight through to the sheet and you can keep drawing in there.
    e.stopPropagation();
    // Double tap — the touch equivalent of the desktop double-click — opens the name box.
    // Deliberately a separate gesture from a plain touch, so a single touch is only ever
    // select-and-move.
    if (e.touches) {
      const now = Date.now();
      const prev = shapeTapRef.current;
      shapeTapRef.current = { id: shape.id, t: now };
      if (prev && prev.id === shape.id && now - prev.t < 320) {
        shapeTapRef.current = null;
        openShapeLabelEditor(shape);
        return;
      }
    }
    handleShapeMouseDown(e, shape.id);
  };

  // Background drag-to-pan -- disabled (canvas is static)
  const handleAreaMouseDown = () => {};
  const handleAreaMouseMove = () => {};
  const handleAreaMouseUp   = () => {};

  // Deselect when clicking/touching the free area around the canvas
  const handleAreaDeselect = (e) => {
    // Only act when the click target is the area itself (not the canvas or its children)
    if (canvasRef.current && canvasRef.current.contains(e.target)) return;
    // Don't fire when touching/clicking a canvas image overlay (prevents onTouchEnd bubbling from images)
    if (e.target && e.target.closest && e.target.closest('[data-image-id]')) return;
    if (shapeClickedRef.current) { shapeClickedRef.current = false; shapeDraggedRef.current = false; return; }
    // Dismiss AI status on any canvas-area click
    {
      setMagicChatStep('');
      setMagicChatError('');
      setShowPromptHistory(false);
      setNamePlacementPending(null);
      // Reset active sub-tool (colour, brightness, contrast) -- chat persists for multi-image selection
      if (magicSubTool && magicSubTool !== 'chat') {
        setMagicSubTool(null);
        setCurrentControlSection(null);
        setShowTopControls(false);
      }
    }
    if (selectedImage || selectedShapeId || selectedShapeIds.size > 0 || imageSelectionMode) {
      setSelectedImage(null);
      setShowImageControls(null);
      setSelectedShapeId(null);
      setSelectedShapeIds(new Set());
      setActiveShapeTool(null);
      setImageSelectionMode(false);
      // Keep magic-wand active -- only the magic wand button itself should deactivate it
      setActiveTool(null);
      setCurrentControlSection(null);
      setCanvasCursor('default');
      setShowTopControls(false);
    }
  };

  // Clear chat input when leaving chat mode; reset filter preview when leaving brightness/contrast
  useEffect(() => {
    if (magicSubTool !== 'chat') {
      setMagicChatInput('');
      setShowPromptHistory(false);
      setMagicChatImages([]);
    }
    if (magicSubTool !== 'brightness' && magicSubTool !== 'contrast') {
      setMagicFilterPreview({ on: false, brightness: 0, contrast: 0 });
      setMagicBrightness(0);
      setMagicContrast(0);
    }
    if (magicSubTool !== 'color') {
      setMagicCakeColorBase(null);
    }
  }, [magicSubTool]);

  // ── What AI Edit is pointed at, said out loud ────────────────────────────────────────────
  // The chat has a picker of its own — tap a picture while it is open and it gets a blue
  // border — and it used to start empty whatever was already selected. So the panel looked
  // as though it had no opinion about which picture the prompt was for, while the send path
  // quietly formed one (see handleMagicChat). Carrying the ordinary selection in means the
  // border shows the same answer the send will use. It only fills an EMPTY picker, so
  // tapping pictures in the chat still overrules it, and it is keyed on the selection as
  // well as on the tool so it works whether the picture or the panel came first.
  useEffect(() => {
    if (magicSubTool !== 'chat' || !selectedImage) return;
    if (!canvasImagesRef.current.some(i => i.id === selectedImage)) return;
    setMagicChatImages(prev => (prev.length ? prev : [selectedImage]));
  }, [magicSubTool, selectedImage]);

  // Capture original image URL when entering brightness/contrast mode (so every bake is from original)
  // Also initialise sliders from the last-applied values stored on the image.
  useEffect(() => {
    if ((magicSubTool === 'brightness' || magicSubTool === 'contrast') && selectedImage) {
      const img = canvasImagesRef.current.find(i => i.id === selectedImage);
      if (img) {
        // Use the stored pre-filter original URL so re-adjusting always starts from the true original
        magicFilterOrigUrlRef.current = img._filterOrigUrl || img.url;
        // Restore last applied values so sliders show the current state
        setMagicBrightness(img._brightness ?? 0);
        setMagicContrast(img._contrast ?? 0);
        setMagicFilterPreview({ on: false, brightness: 0, contrast: 0 });
      }
    } else {
      magicFilterOrigUrlRef.current = null;
    }
  }, [magicSubTool, selectedImage]);

  // Touch-friendly states
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // CreativeStudio is a single-user local app: every tool is available.
  // The AI tools need a server. The panel is always shown (it is part of the editor);
  // when no backend is configured a banner says so, instead of the buttons silently failing.
  const aiEnabled = isAiEnabled();

  // ── What a lost connection is allowed to take away ──────────────────────────────────────
  // Almost nothing. Drawing, shapes, text, the camera, cropping — including the on-device
  // matting Smart Crop now falls back to — the device library, saving to a folder, printing
  // and PDF are all local, and every one of those buttons stays exactly as it was. What the
  // network is actually needed for is the AI tools, signing in, and the account's library.
  // Those are disabled rather than left to fail: a button that is going to spend thirty
  // seconds and then apologise is worse than one that says up front what it needs.
  const online = useOnline();
  // Every AI control was gated on `magicProcessing` and nothing else, so one derived flag
  // reaches all of them without hunting through a toolbar. Deliberately NOT the existing
  // `aiBusy` above, which also covers a chat request in flight: that flag drives the status
  // line and the elapsed timer, and folding these buttons into it would disable them during
  // a chat as a side effect of an unrelated change. Same meaning as before, plus offline.
  const aiHold = magicProcessing || !online;

  // Measure tools wrapper height via ResizeObserver so canvas top tracks it exactly
  useLayoutEffect(() => {
    const el = toolsWrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setToolsWrapperHeight(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Sync refs with latest state
  useEffect(() => { canvasImagesRef.current = canvasImages; }, [canvasImages]);
  useEffect(() => { drawingHistoryRef.current = drawingHistory; }, [drawingHistory]);
  useEffect(() => { historyIndexRef.current = historyIndex; }, [historyIndex]);
  useEffect(() => { canvasShapesRef.current = canvasShapes; }, [canvasShapes]);
  useEffect(() => { drawingPathsRef.current = drawingPaths; }, [drawingPaths]);

  useLayoutEffect(() => {
    const el = document.documentElement;
    const prev = el.getAttribute('data-theme');
    el.setAttribute('data-theme', 'dark');
    return () => { if (prev === null) el.removeAttribute('data-theme'); else el.setAttribute('data-theme', prev); };
  }, []);

  // PCake: only these roles may use the studio (same list as before).
  useEffect(() => {
    if (!userRole) return;   // still loading
    if (!['Admin', 'Super', 'Mover', 'Buyer'].includes(userRole)) navigate('/login', { replace: true });
  }, [userRole, navigate]);

  // Auto-load image when launched from CustomLargeCardSuper
  useEffect(() => {
    if (!fromCustom) return;
    const imageBase64 = location.state?.imageBase64;
    if (!imageBase64) return;
    const img = new Image();
    img.onload = () => {
      const naturalAspectRatio = img.naturalWidth / img.naturalHeight;
      const psW = PRINT_SIZES[printSize].width;
      const psH = PRINT_SIZES[printSize].height;
      const imageCanvasAR = naturalAspectRatio * psH / psW;
      const _fillFrac = 0.88; // fill most of the canvas in fromCustom mode
      let width, height;
      if (imageCanvasAR >= 1) {
        width  = _fillFrac * 21;
        height = _fillFrac * psW * 29.7 / (naturalAspectRatio * psH);
      } else {
        height = _fillFrac * 29.7;
        width  = naturalAspectRatio * _fillFrac * psH * 21 / psW;
      }
      width  = Math.max(0.5, Math.min(21, width));
      height = Math.max(0.5, Math.min(29.7, height));
      const newImage = {
        id: Date.now(),
        layerIndex: ++layerCounterRef.current,
        url: imageBase64,
        name: 'cake-image',
        size: '',
        x: (50 - (width / 21 * 50)),
        y: (50 - (height / 29.7 * 50)),
        width,
        height,
        source: 'local',
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
      };
      setCanvasImages([newImage]);
      setSelectedImage(newImage.id);
      setResizeWidth(width);
      setResizeHeight(height);
      setAspectRatio(naturalAspectRatio);
      setActiveTool('magic-wand');
      setImageSelectionMode(false);
      setCurrentControlSection(null);
      setShowTopControls(false);
      setCanvasCursor('pointer');
    };
    img.src = imageBase64;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close brush size menu when clicking outside
  useEffect(() => {
    if (!showBrushSizeMenu) return;
    const handler = (e) => {
      if (
        brushMenuRef.current && !brushMenuRef.current.contains(e.target) &&
        brushSizeBtnRef.current && !brushSizeBtnRef.current.contains(e.target)
      ) {
        setShowBrushSizeMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showBrushSizeMenu]);

  // Close account menu when clicking outside
  useEffect(() => {
    if (!showAccountMenu) return;
    const handler = (e) => {
      if (
        accountMenuRef.current && !accountMenuRef.current.contains(e.target) &&
        accountBtnRef.current && !accountBtnRef.current.contains(e.target)
      ) {
        setShowAccountMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showAccountMenu]);

  // Close draw palette dropdown when clicking outside
  useEffect(() => {
    if (!showDrawPalette) return;
    const handler = (e) => {
      if (
        drawPaletteRef.current && !drawPaletteRef.current.contains(e.target) &&
        drawPaletteBtnRef.current && !drawPaletteBtnRef.current.contains(e.target)
      ) {
        setShowDrawPalette(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showDrawPalette]);

  // Close stroke width menu when clicking outside
  useEffect(() => {
    if (!showStrokeWidthMenu) return;
    const handler = (e) => {
      if (
        strokeMenuRef.current && !strokeMenuRef.current.contains(e.target) &&
        strokeWidthBtnRef.current && !strokeWidthBtnRef.current.contains(e.target)
      ) {
        setShowStrokeWidthMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showStrokeWidthMenu]);

  // Close emoji popdown when clicking outside
  useEffect(() => {
    if (!showEmojiPopdown) return;
    const handler = (e) => {
      if (
        emojiPopdownRef.current && !emojiPopdownRef.current.contains(e.target) &&
        (!emojiBtnRef.current || !emojiBtnRef.current.contains(e.target))
      ) {
        setShowEmojiPopdown(false);
        setSelectedStampIds(new Set());
        setStampSelectMode(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showEmojiPopdown]);


  // Close color popdown when clicking outside
  useEffect(() => {
    if (!showColorPopdown) return;
    const handler = (e) => {
      if (
        colorPopdownRef.current && !colorPopdownRef.current.contains(e.target) &&
        colorBtnRef.current && !colorBtnRef.current.contains(e.target)
      ) {
        setShowColorPopdown(false);
        setMagicCakeColorBase(null);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showColorPopdown]);

  // Close bg-color popdown when clicking outside
  useEffect(() => {
    if (!showBgColorPopdown) return;
    const handler = (e) => {
      if (
        bgColorPopdownRef.current && !bgColorPopdownRef.current.contains(e.target) &&
        (!bgColorBtnRef.current || !bgColorBtnRef.current.contains(e.target))
      ) {
        setShowBgColorPopdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showBgColorPopdown]);

  // Close name popdown when clicking outside
  useEffect(() => {
    if (!showNamePopup) return;
    const handler = (e) => {
      if (
        namePopdownRef.current && !namePopdownRef.current.contains(e.target) &&
        nameBtnRef.current && !nameBtnRef.current.contains(e.target)
      ) {
        setShowNamePopup(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showNamePopup]);

  // Track viewport size for canvas centering
  useEffect(() => {
    const onResize = () => setVpSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Live drawing layer. Committed paths are rendered once per change into an offscreen layer
  // (fills are flood fills — replaying them on every pointer move would lag); the stroke in
  // progress is drawn on top of a copy of that layer. destination-out erasing works on both.
  const composeLiveDrawing = () => {
    const canvas = drawingCanvasRef.current, layer = committedLayerRef.current;
    if (!canvas || !layer) return;
    const ctx = canvas.getContext('2d');
    // Other code draws on this context too (crop marquee dashes, etc.); start from a clean state,
    // otherwise a leftover dash pattern turns the pen into a dotted line — and a dotted outline
    // is one a fill leaks straight through.
    if (typeof ctx.reset === 'function') ctx.reset();
    else { ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.setLineDash([]); ctx.globalAlpha = 1; ctx.filter = 'none'; }
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(layer, 0, 0);
    if (currentPath.length > 0) {
      renderDrawingPath(ctx, {
        path: currentPath,
        color: drawingMode === 'eraser' ? 'eraser' : drawingColor,
        size: brushSize,
        type: drawingMode,
        canvasW: canvas.width,
        canvasH: canvas.height,
      }, 1, 1);
    }
    ctx.globalCompositeOperation = 'source-over';
  };
  useEffect(() => {
    const canvas = drawingCanvasRef.current;
    if (!canvas) return;
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (w > 0 && h > 0 && (canvas.width !== w || canvas.height !== h)) { canvas.width = w; canvas.height = h; }
    let layer = committedLayerRef.current;
    if (!layer) { layer = document.createElement('canvas'); committedLayerRef.current = layer; }
    layer.width = canvas.width; layer.height = canvas.height;
    const lctx = layer.getContext('2d', { willReadFrequently: true });
    drawingPaths.forEach(path => {
      const scaleX = layer.width / (path.canvasW || layer.width);
      const scaleY = layer.height / (path.canvasH || layer.height);
      renderDrawingPath(lctx, path, scaleX, scaleY);
    });
    lctx.globalCompositeOperation = 'source-over';
    composeLiveDrawing();
  }, [drawingPaths, vpSize, maskTick]);
  useEffect(() => {
    const bump = () => setMaskTick(t => t + 1);
    window.addEventListener('is-mask-ready', bump);
    return () => window.removeEventListener('is-mask-ready', bump);
  }, []);
  useEffect(() => { composeLiveDrawing(); }, [currentPath, drawingColor, drawingMode, brushSize]);

  // Detect whether the drawing canvas contains a closed (enclosed) area.
  // BFS from (0,0) through transparent pixels: if some transparent pixels are
  // not reachable from the edge, they are enclosed by strokes.
  useEffect(() => {
    const timer = setTimeout(() => {
      const canvas = drawingCanvasRef.current;
      if (!canvas || !canvas.width || !canvas.height || drawingPaths.length === 0) {
        setHasClosedArea(false);
        return;
      }
      const cW = canvas.width, cH = canvas.height;
      const total = cW * cH;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      const data = ctx.getImageData(0, 0, cW, cH).data;
      // BFS flood from (0,0) through pixels with alpha <= 10 (transparent / near-transparent)
      const visited = new Uint8Array(total);
      const stack = [0];
      let reached = 0;
      while (stack.length > 0) {
        const pos = stack.pop();
        if (visited[pos]) continue;
        visited[pos] = 1;
        if (data[pos * 4 + 3] > 10) continue; // stroke pixel -- barrier
        reached++;
        const x = pos % cW, y = Math.floor(pos / cW);
        if (x > 0)          stack.push(pos - 1);
        if (x < cW - 1)     stack.push(pos + 1);
        if (y > 0)           stack.push(pos - cW);
        if (y < cH - 1)     stack.push(pos + cW);
      }
      // Count total transparent pixels; if some were not reached, a closed area exists
      let transparent = 0;
      for (let i = 3; i < data.length; i += 4) { if (data[i] <= 10) transparent++; }
      setHasClosedArea(reached < transparent);
    }, 80);
    return () => clearTimeout(timer);
  }, [drawingPaths]);

  // Auto-clear save message after 4 s
  useEffect(() => {
    if (saveMessage) {
      const t = setTimeout(() => setSaveMessage(null), 4000);
      return () => clearTimeout(t);
    }
  }, [saveMessage]);

  const isDirty = (canvasImages.length > 0 || drawingPaths.length > 0 || canvasShapes.length > 0) && !isSavedRef.current;

  // The Close (X) button, for a PWA launched standalone from the home screen. There is no
  // in-app "previous page" to go back to there -- navigate(-1) walked the browser history the
  // app was never given, so it silently did nothing. window.close() is normally refused by the
  // browser for a page it didn't open with a script, EXCEPT installed apps get an exemption for
  // exactly this "app window" case; Capacitor.isNativePlatform() covers a future real native
  // build, should this ever ship as one instead of a PWA. Neither is available (a plain browser
  // tab), fall back to app history, which is at least sometimes right.
  const closeApp = async () => {
    // Closing the app is the one exit that means "I am finished with this sheet", so it is the
    // one that forgets it. Every other way out — the phone's back gesture, the home gesture,
    // the browser deciding it needs the memory — leaves the sheet where the autosave put it.
    //
    // The latch has to go on BEFORE the delete, and it has to stay on. Closing an app is itself
    // a going-away, so the browser fires the very events the autosave listens for — and the
    // sheet that was just deleted was written straight back out on the way through the door,
    // which is why a closed app still opened on yesterday's canvas. While this is set nothing
    // is written down at all. It is taken off again only if the close does not happen (see the
    // two places below), because then the work is still on screen and still theirs.
    sessionClosingRef.current = true;
    await clearSession();      // awaited, or the delete would be racing the page change
    // PCake: the studio is one page of the app, so closing it goes back to where it was opened
    // from (Profile, usually) — it must never exit the native app or close the PWA window.
    if (window.history.length > 1) navigate(-1);
    else navigate('/profile');
  };

  const handleGuardedNavigate = (path) => {
    if (isDirty) {
      setPendingNavPath(path ?? '__back__');
      setCurrentControlSection(null);
      setShowTopControls(true);
    } else {
      if (path === null || path === '__back__') closeApp();
      else navigate(path);
    }
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if we're clicking outside the connect menu
      if (connectMenuRef.current && 
          !connectMenuRef.current.contains(event.target) &&
          !event.target.closest('.connect-dropdown-vertical')) {
        setIsConnectMenuOpen(false);
      }
      
      // Close color picker when clicking outside
      if ((showColorPicker || showColorShades || showShapeColorPicker || showShapeColorShades) && 
          !event.target.closest('.color-picker-container') &&
          !event.target.closest('.base-colors-controls') &&
          !event.target.closest('.color-shades-controls') &&
          !event.target.closest('.color-palette-btn')) {
        setShowColorPicker(false);
        setShowColorShades(false);
        setShowShapeColorPicker(false);
        setShowShapeColorShades(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showColorPicker, showColorShades, showShapeColorPicker, showShapeColorShades, isConnectMenuOpen]);

  // Keyboard shortcuts. Ctrl/Cmd+S save, +Shift+S share, +P print, +Z/+Y undo/redo, +C copy the
  // selected object, +V paste an image (or a copied drawn object), +D duplicate; Esc closes or
  // deselects; Delete removes. Handlers are read through keyActionsRef so the listener always
  // sees the latest state without re-subscribing. Text fields keep their own copy/paste/undo.
  useEffect(() => {
    const isEditable = (t) => !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
    const handleKeyDown = (e) => {
      const A = keyActionsRef.current;
      const mod = e.ctrlKey || e.metaKey;
      const k = (e.key || '').toLowerCase();
      if (mod && !e.altKey) {
        if (k === 's') { e.preventDefault(); if (e.shiftKey) A.share?.(); else A.save?.(); return; }
        if (k === 'p') { e.preventDefault(); A.print?.(); return; }
        if (isEditable(e.target)) return;
        if (k === 'z') { e.preventDefault(); if (e.shiftKey) A.redo?.(); else A.undo?.(); return; }
        if (k === 'y') { e.preventDefault(); A.redo?.(); return; }
        if (k === 'c') { if (A.copy?.()) e.preventDefault(); return; }
        if (k === 'd') { e.preventDefault(); A.duplicate?.(); return; }
        return;
      }
      if (e.key === 'Escape') { A.escape?.(); return; }
      if (isEditable(e.target)) return;
      if (e.key === 'Delete' && selectedImage) {
        const image = canvasImages.find(img => img.id === selectedImage);
        if (image) {
          handleDeleteImage(image.id);
        }
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedShapeId) {
        // Same figure-or-stroke split as the toolbar's Delete button.
        const _sBefore = canvasShapesRef.current;
        if (_sBefore.some(s => s.id === selectedShapeId)) {
          const _sAfter = _sBefore.filter(s => s.id !== selectedShapeId);
          setCanvasShapes(_sAfter);
          pushShapeHistory(_sBefore, _sAfter);
        } else {
          const _pBefore = drawingPathsRef.current;
          const _pAfter = _pBefore.filter(p => p.id !== selectedShapeId);
          if (_pAfter.length !== _pBefore.length) {
            setDrawingPaths(_pAfter);
            pushInkHistory(_pBefore, _pAfter);
          }
        }
        setSelectedShapeId(null);
        setActiveTool(null);
        setActiveShapeTool(null);
        setSelectedEmoji(null);
        setCanvasCursor('default');
        setCurrentControlSection(null);
        setShowTopControls(false);
      }
    };
    // Ctrl+V: image data on the clipboard lands on the sheet like an added image.
    const handlePaste = (e) => {
      if (isEditable(e.target)) return;
      const A = keyActionsRef.current;
      const items = Array.from(e.clipboardData?.items || []);
      const files = items.filter(it => it.kind === 'file' && it.type.startsWith('image/')).map(it => it.getAsFile()).filter(Boolean);
      if (files.length) { e.preventDefault(); A.pasteImages?.(files); return; }
      if (A.pasteShape?.()) e.preventDefault();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('paste', handlePaste);
    };
  }, [selectedImage, canvasImages, selectedShapeId]);

  useEffect(() => {
    const updateTopControlHeight = () => {
      if (showTopControls && topControlsRef.current) {
        const height = topControlsRef.current.offsetHeight;
        document.documentElement.style.setProperty('--top-control-height', `${height}px`);
      } else {
        document.documentElement.style.setProperty('--top-control-height', '0px');
      }
    };

    updateTopControlHeight();

    // Observe height changes (e.g., window resize, content change)
    const observer = new ResizeObserver(updateTopControlHeight);
    if (topControlsRef.current) {
      observer.observe(topControlsRef.current);
    }

    return () => observer.disconnect();
  }, [showTopControls]);

  // ── The end of the crop row comes to you ──────────────────────────────────────────────────
  //
  // Six ways to crop fill the row on a phone, so Copy and Cut — which only appear once there
  // is something to copy — sit past the right-hand edge, reachable only by dragging the row
  // sideways, which nobody thinks to do. While they are there the row parks itself at that
  // end; when they go, it comes back to the start so the crop tools are under the thumb again.
  // (A running detection is handled differently and better: it takes the row over completely,
  // see the crop controls below.)
  const cropRowTail = currentControlSection === 'selection' && !smartCropRunning && (
    ((selectionMode === 'rectangle' || selectionMode === 'circle' || selectionMode === 'smart') && isSelecting)
    || (selectionMode === 'freeform' && selectionPath.length > 2)
  );
  useEffect(() => {
    if (currentControlSection !== 'selection') return undefined;
    // A frame, so the message or the two buttons are laid out and the row knows its own width.
    const id = requestAnimationFrame(() => {
      const el = topControlsRef.current;
      if (!el) return;
      const over = el.scrollWidth - el.clientWidth;
      if (cropRowTail) { if (over > 2) el.scrollTo({ left: over, behavior: 'smooth' }); }
      else if (el.scrollLeft > 2) el.scrollTo({ left: 0, behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(id);
  }, [cropRowTail, currentControlSection]);

  useEffect(() => {
    document.body.classList.add('no-scroll');
    return () => document.body.classList.remove('no-scroll');
  }, []);

  // ---- Load shared stamps from Firestore --------------------------------
  const loadStampsFromFirestore = async () => {
    try {
      const { db } = getFirebase();
      const snap = await getDocs(collection(db, 'studioStamps'));
      let stamps = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
      // Repair stamps saved before the file path was recorded: their url was a session-only
      // object URL, so after a reload they showed as broken images. The files are still in the
      // local library — pair the path-less records with unreferenced files, oldest to oldest.
      const orphans = stamps.filter(st => !st.path && !/^https?:/i.test(st.url || ''));   // PCake stamps keep a real url
      if (orphans.length) {
        try {
          const used = new Set(stamps.map(st => st.path).filter(Boolean));
          const { items } = await listObjectsInFolder('studioStamps');
          const spare = items.map(i => i.name).filter(n => !used.has(n))
            .sort((a, b) => (parseInt(a.match(/_(\d+)\.png$/)?.[1] || '0', 10)) - (parseInt(b.match(/_(\d+)\.png$/)?.[1] || '0', 10)));
          for (const st of orphans) {
            const path = spare.shift();
            if (!path) break;
            st.path = path;
            try { st.url = await getDownloadUrl(path); } catch { continue; }
            updateDoc(doc(db, 'studioStamps', st.id), { path }).catch(() => {});
          }
          stamps = stamps.filter(st => st.url && (st.path || /^https?:/i.test(st.url)));
        } catch (e) { console.warn('Stamp repair skipped:', e); }
      }
      setStudioStamps(stamps);
      stamps.forEach(s => {
        if (s.url && !stampImageCacheRef.current.has(s.url)) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => stampImageCacheRef.current.set(s.url, img);
          img.src = s.url;
        }
      });
    } catch (e) {
      console.error('Failed to load stamps:', e);
    }
  };

  useEffect(() => { loadStampsFromFirestore(); }, []);

  // High-quality image draw with progressive halving for large downscales.
  // Browsers perform a single-step bilinear/Lanczos pass even with
  // imageSmoothingQuality='high', which can produce aliasing/moiré when the
  // source is many times larger than the target. Halving the source repeatedly
  // until it is within 2× of the target dimensions gives mipmap-style filtering
  // with no visible aliasing — the technique used by professional photo apps.
  // dw/dh are positive pixel sizes; the existing context transforms (translate,
  // rotate, clip) still apply.
  const drawImageHQ = (ctx, srcImg, dx, dy, dw, dh) => {
    const sw = srcImg.naturalWidth  || srcImg.width;
    const sh = srcImg.naturalHeight || srcImg.height;
    if (!sw || !sh || dw <= 0 || dh <= 0) {
      ctx.drawImage(srcImg, dx, dy, dw, dh);
      return;
    }
    // No meaningful downscale needed — single draw is best (no extra blur)
    if (sw <= dw * 2 && sh <= dh * 2) {
      ctx.drawImage(srcImg, dx, dy, dw, dh);
      return;
    }
    let curW = sw, curH = sh;
    let cur = srcImg;
    // Halve in both axes together until EITHER dimension is within 2× of target.
    // Using || (not &&) prevents a single large-ratio axis from skipping the loop
    // entirely, which would cause a one-shot 3-5× downscale and visible blurring.
    while (curW > dw * 2 || curH > dh * 2) {
      const nextW = Math.max(Math.ceil(dw), Math.floor(curW / 2));
      const nextH = Math.max(Math.ceil(dh), Math.floor(curH / 2));
      if (nextW === curW && nextH === curH) break;
      const tmp = document.createElement('canvas');
      tmp.width  = nextW;
      tmp.height = nextH;
      const tctx = tmp.getContext('2d');
      tctx.imageSmoothingEnabled = true;
      tctx.imageSmoothingQuality = 'high';
      tctx.drawImage(cur, 0, 0, nextW, nextH);
      cur  = tmp;
      curW = nextW;
      curH = nextH;
    }
    ctx.drawImage(cur, dx, dy, dw, dh);
  };

  // Load an HTTP image via fetch -> blob URL to avoid canvas taint.
  // Falls back to crossOrigin=anonymous if fetch fails.
  const loadImgSafe = (url) => new Promise(async (resolve) => {
    if (!url || !url.startsWith('http')) {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = url;
      return;
    }
    const viaBlob = async (u) => {
      const res = await fetch(u, { mode: 'cors' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(blobUrl); resolve(img); };
      img.onerror = () => { URL.revokeObjectURL(blobUrl); resolve(null); };
      img.src = blobUrl;
    };
    try {
      await viaBlob(url);
    } catch {
      // PCake: some desktop browsers block firebasestorage.googleapis.com outright; the same
      // picture comes through the app's own storageImage function (see storageFallback).
      if (isFirebaseStorageUrl(url)) {
        try { await viaBlob(proxiedImageUrl(url)); return; } catch { /* fall through */ }
      }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = url;
    }
  });

  /**
   * The same picture, but carrying the brightness the screen was showing.
   *
   * ── Why there are two loaders ────────────────────────────────────────────────────────
   * A phone photo is a Google Ultra HDR file: an ordinary picture plus a gain map recording
   * how much brighter each part should be. The phone applies that map when it draws the
   * picture on the sheet. A canvas cannot — so everything made through a canvas came out
   * darker than the thing it was made from, on a phone and only on a phone. See
   * lib/ultrahdr.js for the measurement and the arithmetic.
   *
   * This is used ONLY where pixels are being composed into something the user keeps — the
   * crop, Save, Print, Share and the sheet the AI is shown. Deliberately NOT by smart crop
   * or auto crop, which read pixels to DECIDE something: those two have been tuned against
   * the pictures as they are, and quietly handing them brighter ones would change where
   * they think an edge is. Detection keeps loadImgSafe; output gets this.
   *
   * Falls back to loadImgSafe for anything that is not an Ultra HDR file, which is every
   * picture from a camera, a scanner, a download or this studio's own output.
   */
  const loadImgForOutput = async (url) => {
    const baked = await loadWithGainMap(url);
    return baked || loadImgSafe(url);
  };

  // Prepares an image for Kling AI submission.
  // Firebase Storage download URLs are returned as-is (Kling's servers can fetch them
  // directly, so we avoid the CORS-blocked XHR that occurs when the browser tries to
  // download them). Local data URLs are resized and re-uploaded to temp Firebase Storage.
  // Returns { imageBase64, w, h, origW, origH, tempRef:null }. Nothing is uploaded, so
  // there is nothing for the caller to clean up — tempRef stays null for compatibility.
  // ── The AI's own frame ─────────────────────────────────────────────────────────────────
  // An image model does not answer in the size it was sent. It picks the nearest of its own
  // frame sizes and FILLS that frame: the picture is scaled up uniformly until it covers the
  // frame, and whatever overhangs is cut off the two sides that overhang. Measured on the live
  // function, a 300 x 600 picture came back 736 x 1408: a disc in it was still a perfect circle,
  // 2.44 times larger, and about 30 px had gone off its top and bottom. A 320 x 240 picture came
  // back 1184 x 864.
  //
  // Stretching that answer back to the original size — which is what used to happen — did two
  // wrong things at once: it squashed the object out of its proportions, and because the
  // overhang had already been cut away it also enlarged the object and moved it in its frame.
  // So a picture now goes to the AI inside a margin (AI_FRAME_PAD of its size on every side)
  // that the model can cut into without reaching the picture, and comes back through the exact
  // inverse: one uniform scale, then the original picture's own rectangle taken out of the
  // answer. Nothing is stretched. The object keeps its size, its proportions and its place.
  //
  // 6 % a side absorbs a frame up to about 12 % away from the picture's shape; both measured
  // cases were under 5 %. A model that cuts deeper still is not stretched over the gap — the
  // strip it took is filled from the picture as it was.
  const AI_FRAME_PAD = 0.06;

  const frameForAi = (src, w, h, { fill = '#cccccc' } = {}) => {
    const px = Math.max(1, Math.round(w * AI_FRAME_PAD)), py = Math.max(1, Math.round(h * AI_FRAME_PAD));
    const pw = w + 2 * px, ph = h + 2 * py;
    const c = document.createElement('canvas');
    c.width = pw; c.height = ph;
    const ctx = c.getContext('2d');
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    ctx.fillStyle = fill;
    ctx.fillRect(0, 0, pw, ph);
    const sw = src.naturalWidth || src.width, sh = src.naturalHeight || src.height;
    ctx.drawImage(src, 0, 0, sw, sh, px, py, w, h);
    // The margin continues the picture's own edges, so the model sees more of the same rather
    // than a frame it might decide to keep, remove or decorate. Transparent edges stay the fill.
    ctx.drawImage(c, px, py, 1, h, 0, py, px, h);
    ctx.drawImage(c, px + w - 1, py, 1, h, px + w, py, px, h);
    ctx.drawImage(c, 0, py, pw, 1, 0, 0, pw, py);
    ctx.drawImage(c, 0, py + h - 1, pw, 1, 0, py + h, pw, py);
    return { dataUrl: c.toDataURL('image/png'), frame: { pw, ph, ox: px, oy: py, iw: w, ih: h } };
  };

  // Where the sent picture's rectangle sits in an answer that is aw x ah: the inverse of
  // "scale uniformly to cover, cut the overhang equally off both sides".
  const frameRectIn = (frame, aw, ah) => {
    const k = Math.max(aw / frame.pw, ah / frame.ph);
    const cutX = (frame.pw * k - aw) / 2, cutY = (frame.ph * k - ah) / 2;
    return { x: frame.ox * k - cutX, y: frame.oy * k - cutY, w: frame.iw * k, h: frame.ih * k };
  };

  // Draw rect r of an answer into a W x H destination, clipping by hand: a source rectangle
  // that runs off the image draws nothing at all in some browsers rather than drawing the part
  // that is there.
  const drawFramed = (ctx, img, aw, ah, r, W, H) => {
    const x0 = Math.max(0, r.x), y0 = Math.max(0, r.y);
    const x1 = Math.min(aw, r.x + r.w), y1 = Math.min(ah, r.y + r.h);
    if (x1 <= x0 || y1 <= y0) return false;
    const kx = W / r.w, ky = H / r.h;
    ctx.drawImage(img, x0, y0, x1 - x0, y1 - y0, (x0 - r.x) * kx, (y0 - r.y) * ky, (x1 - x0) * kx, (y1 - y0) * ky);
    return x0 > r.x + 0.5 || y0 > r.y + 0.5 || x1 < r.x + r.w - 0.5 || y1 < r.y + r.h - 0.5;   // true = a strip is missing
  };

  // Does this picture have real transparency — is it a cut-out rather than a photo?
  const imageHasTransparency = (el) => {
    try {
      const sw = el.naturalWidth || el.width, sh = el.naturalHeight || el.height;
      const k = Math.min(1, 160 / Math.max(sw, sh));
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(sw * k)); c.height = Math.max(1, Math.round(sh * k));
      const ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(el, 0, 0, c.width, c.height);
      const d = ctx.getImageData(0, 0, c.width, c.height).data;
      let clear = 0;
      for (let i = 3; i < d.length; i += 4) if (d[i] < 200) clear++;
      return clear / (d.length / 4) >= 0.01;
    } catch { return false; }
  };

  // The colour the AI painted round the object, cut away again from the edges inwards. Returns
  // { alpha (0..1 per pixel), bg: [r, g, b] }, or null when the border is not one plain colour —
  // then there is no telling background from object this way, and the caller falls back to the
  // old outline.
  const keyBorderAlpha = (data, W, H) => {
    const border = [];
    for (let x = 0; x < W; x++) border.push(x, (H - 1) * W + x);
    for (let y = 0; y < H; y++) border.push(y * W, y * W + W - 1);
    const ch = (k) => { const v = border.map(i => data[i * 4 + k]).sort((p, q) => p - q); return v[v.length >> 1]; };
    const br = ch(0), bg = ch(1), bb = ch(2);
    const dist = (i) => Math.abs(data[i * 4] - br) + Math.abs(data[i * 4 + 1] - bg) + Math.abs(data[i * 4 + 2] - bb);
    if (border.filter(i => dist(i) <= 60).length / border.length < 0.6) return null;
    const TOL_IN = 45, TOL_OUT = 110;
    const n = W * H;
    const alpha = new Float32Array(n).fill(1);
    const seen = new Uint8Array(n);
    const stack = border.slice();
    while (stack.length) {
      const i = stack.pop();
      if (seen[i]) continue;
      const d = dist(i);
      if (d > TOL_OUT) continue;
      seen[i] = 1;
      alpha[i] = Math.min(1, Math.max(0, (d - TOL_IN) / (TOL_OUT - TOL_IN)));
      const x = i % W, y = (i - x) / W;
      if (x > 0) stack.push(i - 1);
      if (x < W - 1) stack.push(i + 1);
      if (y > 0) stack.push(i - W);
      if (y < H - 1) stack.push(i + W);
    }
    return { alpha, bg: [br, bg, bb] };
  };

  // A cut-out goes to the AI on a plain ground — it has no transparency to give back — and the
  // answer comes back with that ground filled in: the box round a recoloured cake.
  //   'keep'  the change cannot move the outline (a colour, a sharpening, a name written on
  //           it): the original outline is put back exactly, so the object is the same shape
  //           and size to the pixel.
  //   'key'   the change may move the outline (a tier added or taken off, a free edit): the
  //           plain ground the AI painted is cut away from the edges inwards. Inside the old
  //           outline a pixel stays the object unless the AI turned it INTO ground — so white
  //           icing on the rim of a cake, which looks exactly like a white ground, is kept,
  //           while a tier the AI took away really goes. If the ground cannot be told apart at
  //           all, the original outline stands in.
  const restoreCutout = (ctx, W, H, srcEl, mode) => {
    const out = ctx.getImageData(0, 0, W, H);
    const o = out.data;
    const sc = document.createElement('canvas'); sc.width = W; sc.height = H;
    const sctx = sc.getContext('2d', { willReadFrequently: true });
    sctx.drawImage(srcEl, 0, 0, W, H);
    const src = sctx.getImageData(0, 0, W, H).data;
    if (mode === 'key') {
      // Keyed at a working size and scaled back up: the fill walks every background pixel,
      // and a phone photo has twelve million of them.
      const k = Math.min(1, 1024 / Math.max(W, H));
      const kw = Math.max(1, Math.round(W * k)), kh = Math.max(1, Math.round(H * k));
      const kc = document.createElement('canvas'); kc.width = kw; kc.height = kh;
      const kctx = kc.getContext('2d', { willReadFrequently: true });
      kctx.drawImage(ctx.canvas, 0, 0, kw, kh);
      const keyed = keyBorderAlpha(kctx.getImageData(0, 0, kw, kh).data, kw, kh);
      if (keyed) {
        const kd = kctx.createImageData(kw, kh);
        for (let i = 0; i < kw * kh; i++) kd.data[i * 4 + 3] = Math.round(keyed.alpha[i] * 255);
        kctx.clearRect(0, 0, kw, kh); kctx.putImageData(kd, 0, 0);
        const up = document.createElement('canvas'); up.width = W; up.height = H;
        const uctx = up.getContext('2d', { willReadFrequently: true });
        uctx.imageSmoothingEnabled = true; uctx.imageSmoothingQuality = 'high';
        uctx.drawImage(kc, 0, 0, W, H);
        const a = uctx.getImageData(0, 0, W, H).data;
        const [br, bgc, bb] = keyed.bg;
        const groundLike = (d, i) => Math.abs(d[i] - br) + Math.abs(d[i + 1] - bgc) + Math.abs(d[i + 2] - bb) <= 60;
        for (let i = 0; i < o.length; i += 4) {
          const was = src[i + 3];
          const takenAway = was > 0 && groundLike(o, i) && !groundLike(src, i);
          o[i + 3] = (was > 0 && !takenAway) ? Math.max(a[i + 3], was) : a[i + 3];
        }
        ctx.putImageData(out, 0, 0);
        return;
      }
    }
    for (let i = 3; i < o.length; i += 4) o[i] = Math.min(o[i], src[i]);
    ctx.putImageData(out, 0, 0);
  };

  const prepareImageForKling = async (imgObj, { min = 300, max = 1024, suffix = '' } = {}) => {
    const url = imgObj?.url || '';

    // Resolve the effective source URL.
    // For Firebase Storage images we route through the backend proxy so the browser
    // can read the raw bytes (no CORS block) and then draw to canvas, which:
    //   1. Strips EXIF orientation data — phones often store images with orientation 2
    //      (horizontal flip) so Kling was receiving a mirrored raw file and returning
    //      a mirrored result.
    //   2. Resizes to a Kling-friendly resolution before upload to temp storage.
    // No proxy step: every image here is a local blob or data URL, so the bytes are
    // already readable and same-origin. (PCAKE routed Firebase Storage URLs through a
    // /proxy-image endpoint to dodge CORS; there is no remote origin to dodge now.)
    const el = await loadImgSafe(url);
    if (!el) throw new Error('Could not load image');
    // The TRUE size of the image we're editing. Every AI result is normalized back to
    // exactly this (see the normalizeAiResult calls), so an edit can never change the
    // photo's dimensions or aspect ratio. The w/h below are only what we SEND to the
    // model — they're clamped to a model-friendly resolution and rounded per-axis, which
    // shifts the aspect slightly; normalizing to origW/origH undoes that. Without it the
    // picture shrank a little on every edit (and the drift compounded edit after edit).
    const origW = el.naturalWidth || el.width || 512;
    const origH = el.naturalHeight || el.height || 512;
    let w = origW;
    let h = origH;
    if (w < min || h < min) { const s = Math.max(min / w, min / h); w = Math.round(w * s); h = Math.round(h * s); }
    if (w > max || h > max) { const s = Math.min(max / w, max / h); w = Math.round(w * s); h = Math.round(h * s); }
    // Sent inside its frame (see frameForAi). w/h returned are the FRAME's — the shape the
    // model is actually looking at, which is what an editor that is told an aspect ratio
    // (Kling) has to be told. `frame` is what normalizeAiResult needs to undo it, and carries
    // the source picture along so a cut-out can get its outline back.
    const fr = frameForAi(el, w, h);
    fr.frame.srcEl = el;
    fr.frame.hasAlpha = imageHasTransparency(el);
    // The AI function takes the bytes directly, so there is no upload and nothing to clean
    // up afterwards. PCAKE uploaded here only to obtain a public URL for a server that then
    // downloaded the same bytes straight back; tempRef stays null so the existing cleanup
    // paths keep working unchanged.
    return { imageBase64: fr.dataUrl, w: fr.frame.pw, h: fr.frame.ph, origW, origH, tempRef: null, frame: fr.frame };
  };

  // Flattens EVERY layer on the sheet — every inserted image, every drawn shape, every ink
  // stroke — into one picture, the same way Save does. AI Edit used to send the model only
  // the raw bytes of the one photo being edited (prepareImageForKling above), so a prompt
  // like "make this a cosmos" when the sheet also had a hand-drawn solar system around that
  // photo left the model completely blind to the drawing — it only ever saw the isolated
  // photo and returned a result as if nothing else was on the sheet. This gives the model
  // the whole composition instead.
  const prepareFullSheetForAi = async ({ max = 1536 } = {}) => {
    const images = canvasImagesRef.current;
    const paths  = drawingPaths;
    const shapes = canvasShapesRef.current;
    const loadedImages = await Promise.all(images.map(i => loadImgForOutput(i.url)));

    // Same aspect ratio as the print sheet so nothing gets stretched or letterboxed oddly.
    const PS = PRINT_SIZES[printSize] || { width: 21, height: 29.7 };
    const aspect = PS.width / PS.height;
    let widthInPx, heightInPx;
    if (aspect >= 1) { widthInPx = max; heightInPx = Math.round(max / aspect); }
    else { heightInPx = max; widthInPx = Math.round(max * aspect); }

    const canvas = document.createElement('canvas');
    canvas.width = widthInPx; canvas.height = heightInPx;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    // AI image models expect an opaque frame -- fall back to white instead of leaving a
    // transparent sheet background undefined to the model.
    ctx.fillStyle = (canvasBackground && canvasBackground !== 'transparent') ? canvasBackground : '#ffffff';
    ctx.fillRect(0, 0, widthInPx, heightInPx);
    SHEET_FX[canvasBgFx]?.paint(ctx, widthInPx, heightInPx, isDarkSheet(canvasBackground));

    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      const image = loadedImages[i];
      if (!image) continue;
      const imgXPx = (img.x / 100) * widthInPx;
      const imgYPx = (img.y / 100) * heightInPx;
      const imgWidthPx  = (img.width  / 21)   * widthInPx;
      const imgHeightPx = (img.height / 29.7) * heightInPx;
      const rot = ((img.rotation || 0) * Math.PI) / 180;
      if (rot) {
        const cx = imgXPx + imgWidthPx / 2, cy = imgYPx + imgHeightPx / 2;
        ctx.save(); ctx.translate(cx, cy); ctx.rotate(rot);
        if (img.cropShape === 'circle') {
          ctx.beginPath(); ctx.ellipse(0, 0, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2); ctx.clip();
        }
        drawImageHQ(ctx, image, -imgWidthPx / 2, -imgHeightPx / 2, imgWidthPx, imgHeightPx);
        ctx.restore();
      } else if (img.cropShape === 'circle') {
        ctx.save();
        ctx.beginPath(); ctx.ellipse(imgXPx + imgWidthPx / 2, imgYPx + imgHeightPx / 2, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2); ctx.clip();
        drawImageHQ(ctx, image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
        ctx.restore();
      } else {
        drawImageHQ(ctx, image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
      }
    }

    // Same helpers Save uses for the drawn/ink layers, over the whole sheet (0,0 -> 100,100).
    drawDrawingLayer(ctx, paths, widthInPx, heightInPx, 0, 0, 100, 100);
    if (shapes.length > 0) drawShapesOnCanvas(ctx, shapes, widthInPx, heightInPx, 0, 0, 100, 100);

    // w/h stay the SHEET's size (the new layer's natural size); the frame travels separately.
    const fr = frameForAi(canvas, widthInPx, heightInPx, { fill: '#ffffff' });
    return { imageBase64: fr.dataUrl, w: widthInPx, h: heightInPx, frame: fr.frame };
  };

  // (The Kling composite helper lived here: it pasted the reference image into a card
  //  beside the base image because Kling took only ONE image. Gemini accepts a second
  //  inline image directly, so the composite — and its temp upload — are gone.)


  // ─── Smart Crop helpers ────────────────────────────────────────────────────
  // Flood-fill from (startX, startY) in imageData; returns the bounding box
  // of all connected pixels that are either non-transparent (alpha-first) or
  // within a colour tolerance (fallback for fully-opaque images).
  const floodFillBounds = (imageData, startX, startY) => {
    const { data, width, height } = imageData;
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
    startX = clamp(startX, 0, width  - 1);
    startY = clamp(startY, 0, height - 1);
    const ALPHA_THRESHOLD = 30;

    // ── Helper: bounding box of all non-transparent pixels ──────────────────
    const allVisibleBbox = () => {
      let ax = width, bx = 0, ay = height, by = 0;
      for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
        if (data[(y * width + x) * 4 + 3] > ALPHA_THRESHOLD) {
          if (x < ax) ax = x; if (x > bx) bx = x;
          if (y < ay) ay = y; if (y > by) by = y;
        }
      }
      return { minX: ax, maxX: bx, minY: ay, maxY: by };
    };

    // ── Check for meaningful transparency ────────────────────────────────────
    let hasTransparency = false;
    for (let i = 3; i < data.length; i += 4) { if (data[i] < ALPHA_THRESHOLD) { hasTransparency = true; break; } }

    if (hasTransparency) {
      // Alpha path: flood-fill connected visible pixels from the click point
      const startA = data[(startY * width + startX) * 4 + 3];
      if (startA < ALPHA_THRESHOLD) return allVisibleBbox(); // clicked on transparent → full bbox
      const visited = new Uint8Array(width * height);
      const stack = [];
      const pushA = (x, y) => {
        if (x < 0 || x >= width || y < 0 || y >= height) return;
        const i = y * width + x; if (visited[i]) return; visited[i] = 1;
        if (data[i * 4 + 3] < ALPHA_THRESHOLD) return;
        stack.push(i);
      };
      pushA(startX, startY);
      let minX = startX, maxX = startX, minY = startY, maxY = startY;
      while (stack.length) {
        const i = stack.pop();
        const x = i % width, y = (i / width) | 0;
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
        pushA(x+1,y); pushA(x-1,y); pushA(x,y+1); pushA(x,y-1);
      }
      return { minX, maxX, minY, maxY };
    }

    // ── Opaque image: gutter-scan approach ───────────────────────────────────
    // Works for grids of cards AND isolated objects.
    // Strategy: determine background colour from image edges (majority cluster),
    // then scan outward from the click point in each direction until we hit a
    // "gutter" — a row/col where ≥50% of pixels are background colour.
    // The gutter boundaries define the cell containing the clicked object.
    const rgb = (x, y) => { const p = (y * width + x) * 4; return [data[p], data[p+1], data[p+2]]; };
    const dist = (a, b) => Math.abs(a[0]-b[0]) + Math.abs(a[1]-b[1]) + Math.abs(a[2]-b[2]);

    // Sample 16 border pixels; use majority cluster to find bgColor.
    const edgeSamples = [
      rgb(0,0), rgb(width-1,0), rgb(0,height-1), rgb(width-1,height-1),
      rgb(width>>1,0), rgb(width>>1,height-1), rgb(0,height>>1), rgb(width-1,height>>1),
      rgb(width>>2,0), rgb(3*(width>>2),0), rgb(width>>2,height-1), rgb(3*(width>>2),height-1),
      rgb(0,height>>2), rgb(0,3*(height>>2)), rgb(width-1,height>>2), rgb(width-1,3*(height>>2)),
    ];
    const CLUSTER_TOL = 50 * 3;
    let bgColor = edgeSamples[0], bestClusterCount = 0;
    for (const cand of edgeSamples) {
      let cnt = 0, sr = 0, sg = 0, sb = 0;
      for (const s of edgeSamples) {
        if (dist(s, cand) < CLUSTER_TOL) { cnt++; sr += s[0]; sg += s[1]; sb += s[2]; }
      }
      if (cnt > bestClusterCount) { bestClusterCount = cnt; bgColor = [sr/cnt, sg/cnt, sb/cnt]; }
    }

    const BG_TOL = 60 * 3;
    const isBg = (x, y) => dist(rgb(x, y), bgColor) < BG_TOL;

    // A row is a gutter if ≥50% of its pixels are background
    const rowIsGutter = (y) => {
      let n = 0;
      for (let x = 0; x < width; x++) if (isBg(x, y)) n++;
      return n / width >= 0.5;
    };
    // A col is a gutter if ≥50% of its pixels are background
    const colIsGutter = (x) => {
      let n = 0;
      for (let y = 0; y < height; y++) if (isBg(x, y)) n++;
      return n / height >= 0.5;
    };

    // Scan outward from click to find the nearest gutter in each direction
    let minY = startY, maxY = startY, minX = startX, maxX = startX;
    while (minY > 0        && !rowIsGutter(minY)) minY--;
    while (maxY < height-1 && !rowIsGutter(maxY)) maxY++;
    while (minX > 0        && !colIsGutter(minX)) minX--;
    while (maxX < width-1  && !colIsGutter(maxX)) maxX++;

    // If click itself is on background (gutter), return bbox of all non-background pixels
    if (isBg(startX, startY)) {
      let ax = width, bx = 0, ay = height, by = 0;
      for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
        if (!isBg(x, y)) {
          if (x < ax) ax = x; if (x > bx) bx = x;
          if (y < ay) ay = y; if (y > by) by = y;
        }
      }
      return { minX: ax, maxX: bx, minY: ay, maxY: by };
    }

    return { minX, maxX, minY, maxY };
  };

  // Frame peeler: samples the current bbox corners to determine this layer's color,
  // then scans rows/cols inward from each edge — peeling any that are ≥70% that color.
  // One call = one frame layer stripped. Loop in runSmartCrop handles multiple layers
  // (e.g. dark shadow → white card frame → stop at princess content).
  const shrinkFrameBbox = (imageData, bbox) => {
    const { data, width } = imageData;
    let { minX, maxX, minY, maxY } = bbox;
    const origBw = maxX - minX, origBh = maxY - minY;
    if (origBw < 20 || origBh < 20) return bbox;

    const px = (x, y) => { const p = (y * width + x) * 4; return [data[p], data[p+1], data[p+2]]; };
    const dist = (a, b) => Math.abs(a[0]-b[0]) + Math.abs(a[1]-b[1]) + Math.abs(a[2]-b[2]);

    // Sample corners + edge midpoints to determine the current outer frame color
    const mx = (minX + maxX) >> 1, my = (minY + maxY) >> 1;
    const samples = [
      px(minX,minY), px(maxX,minY), px(minX,maxY), px(maxX,maxY),
      px(mx, minY), px(mx, maxY), px(minX, my), px(maxX, my),
    ];
    const frameMean = [0,0,0];
    for (const s of samples) { frameMean[0]+=s[0]; frameMean[1]+=s[1]; frameMean[2]+=s[2]; }
    frameMean[0]/=samples.length; frameMean[1]/=samples.length; frameMean[2]/=samples.length;

    // If the sampled edges are too varied, they're content — no frame to peel
    let edgeVariance = 0;
    for (const s of samples) edgeVariance += dist(s, frameMean);
    edgeVariance /= samples.length;
    if (edgeVariance > 75) return bbox;

    // A row/col is "frame" if ≥70% of its pixels match frameMean within tolerance
    const FRAME_TOL = 55; // L1 per-pixel tolerance
    const FRAME_FRAC = 0.85; // 85% of row/col must match frame color
    const rowIsFrame = (y, x0, x1) => {
      const n = x1 - x0 + 1; let m = 0;
      for (let x = x0; x <= x1; x++) if (dist(px(x, y), frameMean) < FRAME_TOL) m++;
      return m / n >= FRAME_FRAC;
    };
    const colIsFrame = (x, y0, y1) => {
      const n = y1 - y0 + 1; let m = 0;
      for (let y = y0; y <= y1; y++) if (dist(px(x, y), frameMean) < FRAME_TOL) m++;
      return m / n >= FRAME_FRAC;
    };

    while (minY < maxY - 10 && rowIsFrame(minY, minX, maxX)) minY++;
    while (maxY > minY + 10 && rowIsFrame(maxY, minX, maxX)) maxY--;
    while (minX < maxX - 10 && colIsFrame(minX, minY, maxY)) minX++;
    while (maxX > minX + 10 && colIsFrame(maxX, minY, maxY)) maxX--;

    if ((maxX - minX) < origBw * 0.15 || (maxY - minY) < origBh * 0.15) return bbox;

    // A PALE band of even width all the way round is a border the picture was printed or
    // scanned with — a photo's white margin, a mount, a passe-partout. It belongs to the
    // picture, so cropping it away bit into the thing being cropped, and on a white sheet the
    // result looked like the crop had simply eaten the photo's edge. White that genuinely sits
    // BEHIND a subject is almost never this even: a subject sits off-centre, and the gap above
    // a head is nothing like the gap beside its shoulders. So an even pale frame stays, while
    // an uneven or wide one — and any dark frame, such as a drop shadow or a mat — still peels.
    const peels = [minX - bbox.minX, bbox.maxX - maxX, minY - bbox.minY, bbox.maxY - maxY];
    const thinnest = Math.min(...peels), thickest = Math.max(...peels);
    const pale = frameMean[0] > 218 && frameMean[1] > 218 && frameMean[2] > 218;
    if (pale && thinnest > 0
        && thickest <= thinnest * 1.4
        && thickest <= Math.min(origBw, origBh) * 0.25) return bbox;

    return { minX, maxX, minY, maxY };
  };

  // Detect the object under (canvasX, canvasY) and set the silhouette selection.
  // Optional drawnBox {x,y,w,h} constrains detection to the user-drawn area.
  // How much room a crop leaves around what it found, and how sure it has to be. Smart Crop is
  // aimed by hand, so it trusts the finger and hugs the edge. Auto Crop is aimed by nobody, so
  // it leaves a margin and refuses a result that is plainly not an object — see runAutoCrop.
  const cropOptsRef = useRef({ padFrac: 0.01 });
  // ── The objects inside a cut-out ───────────────────────────────────────────────────────
  // A picture that is already a cut-out has answered "what is the background?" — its alpha says
  // so exactly. Cropping it again asked that question a second time and got the same picture
  // back: figures standing on a cake are ONE island of opaque pixels, so neither the island pass
  // nor the split into pieces had anything to part.
  //
  // For the matting below, the cut-out is laid on a loud colour it does not use. Transparent
  // pixels are black underneath, and a black figure in a black "background" cannot be told
  // apart from it.
  const cutoutOnKeyColour = (imgData) => {
    const pd = imgData.data, n = pd.length / 4;
    const KEYS = [[255, 0, 255], [0, 255, 0], [0, 0, 255], [255, 255, 0]];
    const near = KEYS.map(() => 0);
    for (let i = 0; i < n; i += 7) {
      const p = i * 4;
      if (pd[p + 3] <= 30) continue;
      KEYS.forEach((k, j) => {
        if (Math.abs(pd[p] - k[0]) + Math.abs(pd[p + 1] - k[1]) + Math.abs(pd[p + 2] - k[2]) < 230) near[j]++;
      });
    }
    const key = KEYS[near.indexOf(Math.min(...near))];
    const out = new Uint8ClampedArray(pd.length);
    for (let i = 0; i < n; i++) {
      const p = i * 4;
      if (pd[p + 3] > 30) { out[p] = pd[p]; out[p + 1] = pd[p + 1]; out[p + 2] = pd[p + 2]; }
      else { out[p] = key[0]; out[p + 1] = key[1]; out[p + 2] = key[2]; }
      out[p + 3] = 255;
    }
    return { data: out, width: imgData.width, height: imgData.height };
  };

  // Auto crop on a cut-out: the AI names the objects as boxes (aiFindObjects — a few seconds;
  // asking it for a mask per object as well ran past ninety on a busy cake), and each object's
  // real edge is found inside its box on the device (grabCutInMask). A label per pixel of the
  // sample comes out: 0 is transparent, 1…n the object it belongs to.
  //   • Where two objects claim a pixel, the smaller has it — a figure, not the box of "cake"
  //     that happens to take the figure in too.
  //   • What no object claimed is not handed to its neighbours: it is the rest of the picture —
  //     the cake the figures stand on — and each part of it big enough to matter is an object of
  //     its own. Only specks are folded into whatever they touch.
  // Returns { labels, count } (count = objects holding a real share of the picture), or null.
  const labelCutoutObjects = (objects, imgData, sw, sh) => {
    const pd = imgData.data;
    const n = sw * sh;
    const opaque = new Uint8Array(n);
    let opaqueN = 0;
    for (let i = 0; i < n; i++) if (pd[i * 4 + 3] > 30) { opaque[i] = 1; opaqueN++; }
    if (!opaqueN) return null;
    const minPart = Math.max(24, Math.round(opaqueN * 0.02));
    const keyed = cutoutOnKeyColour(imgData);
    const members = [];
    for (const o of (objects || []).slice(0, 8)) {
      const [y0, x0, y1, x1] = (o?.box_2d || []).map(Number);
      if (![y0, x0, y1, x1].every(Number.isFinite)) continue;
      const bx0 = Math.max(0, Math.floor((x0 / 1000) * sw)), by0 = Math.max(0, Math.floor((y0 / 1000) * sh));
      const bx1 = Math.min(sw - 1, Math.ceil((x1 / 1000) * sw)), by1 = Math.min(sh - 1, Math.ceil((y1 / 1000) * sh));
      if (bx1 - bx0 < 4 || by1 - by0 < 4) continue;
      const region = new Uint8Array(n);
      let rn = 0;
      for (let y = by0; y <= by1; y++) for (let x = bx0; x <= bx1; x++) { const i = y * sw + x; if (opaque[i]) { region[i] = 1; rn++; } }
      if (rn < minPart) continue;
      let m = null;
      try { m = grabCutInMask(keyed, region); } catch { m = null; }
      if (!m) continue;
      let area = 0;
      for (let i = 0; i < n; i++) { if (m[i] && opaque[i]) area++; else m[i] = 0; }
      if (area >= minPart) members.push({ m, area, box: [bx0, by0, bx1, by1] });
    }
    if (!members.length) return null;
    members.sort((p, q) => p.area - q.area);
    const labels = new Int32Array(n);
    members.forEach((o, k) => { for (let i = 0; i < n; i++) if (o.m[i] && !labels[i]) labels[i] = k + 1; });
    const queue = new Int32Array(n);

    // ── A figure keeps all of itself ────────────────────────────────────────────────────────
    // The edge-finder inside each box learns "background" from what lies just outside the box —
    // and under a figure's feet that is the cake, the sword, whatever it stands on. So a figure
    // whose trousers were the colour of the sword below lost its legs, and they went off with
    // the cake top as a stray piece of their own. The box itself says what belongs: each small
    // object, smallest first, now takes the unclaimed pixels it touches inside its own box. A
    // big box (the cake, which takes in nearly everything) is left out — it would swallow all —
    // and so is a thin thing whose box is mostly something else.
    members.forEach((o, k) => {
      const [x0, y0, x1, y1] = o.box;
      const boxArea = (x1 - x0 + 1) * (y1 - y0 + 1);
      if (boxArea > n * 0.4) return;
      // A thin thing lying across its box (a sword laid diagonally over the cake) fills little of
      // it, and its box is mostly whatever it lies on: the box says nothing about its shape.
      if (o.area < boxArea * 0.35) return;
      const L = k + 1;
      let head = 0, tail = 0;
      for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) { const i = y * sw + x; if (labels[i] === L) queue[tail++] = i; }
      const free = (j) => opaque[j] && !labels[j];
      while (head < tail) {
        const q = queue[head++], qx = q % sw, qy = (q - qx) / sw;
        if (qx > x0 && free(q - 1))  { labels[q - 1]  = L; queue[tail++] = q - 1; }
        if (qx < x1 && free(q + 1))  { labels[q + 1]  = L; queue[tail++] = q + 1; }
        if (qy > y0 && free(q - sw)) { labels[q - sw] = L; queue[tail++] = q - sw; }
        if (qy < y1 && free(q + sw)) { labels[q + sw] = L; queue[tail++] = q + sw; }
      }
    });

    // What nobody claimed. A part that rests along an object — the top tier of the cake, left
    // over when the cake's own edge-finder stopped short of it — belongs to that object: it is
    // merged when its border with the object is a real share of its outline. A part that only
    // touches at a point (a figure standing on the cake that nobody boxed) is its own object.
    // Specks are folded into whatever they touch.
    const specks = [];
    let next = members.length + 1;
    for (let s0 = 0; s0 < n; s0++) {
      if (!opaque[s0] || labels[s0]) continue;
      const L = next++;
      let head = 0, tail = 0;
      queue[tail++] = s0; labels[s0] = L;
      while (head < tail) {
        const q = queue[head++], qx = q % sw, qy = (q - qx) / sw;
        if (qx > 0      && opaque[q - 1]  && !labels[q - 1])  { labels[q - 1]  = L; queue[tail++] = q - 1; }
        if (qx < sw - 1 && opaque[q + 1]  && !labels[q + 1])  { labels[q + 1]  = L; queue[tail++] = q + 1; }
        if (qy > 0      && opaque[q - sw] && !labels[q - sw]) { labels[q - sw] = L; queue[tail++] = q - sw; }
        if (qy < sh - 1 && opaque[q + sw] && !labels[q + sw]) { labels[q + sw] = L; queue[tail++] = q + sw; }
      }
      const part = queue.slice(0, tail);
      if (tail < minPart) { specks.push(part); continue; }
      let edge = 0;
      const touch = new Map();
      for (const q of part) {
        const qx = q % sw, qy = (q - qx) / sw;
        let onEdge = false;
        for (const j of [qx > 0 ? q - 1 : -1, qx < sw - 1 ? q + 1 : -1, qy > 0 ? q - sw : -1, qy < sh - 1 ? q + sw : -1]) {
          if (j < 0) { onEdge = true; continue; }
          const lj = labels[j];
          if (lj === L) continue;
          onEdge = true;
          if (lj && lj <= members.length) touch.set(lj, (touch.get(lj) || 0) + 1);
        }
        if (onEdge) edge++;
      }
      let best = 0, bestN = 0;
      for (const [lj, c] of touch) if (c > bestN) { best = lj; bestN = c; }
      if (best && bestN >= edge * 0.15) for (const q of part) labels[q] = best;
    }
    // Fold the specks into whatever they touch, growing in from their edges.
    for (const sp of specks) for (const i of sp) labels[i] = 0;
    let head = 0, tail = 0;
    for (let i = 0; i < n; i++) if (labels[i]) queue[tail++] = i;
    while (head < tail) {
      const q = queue[head++], qx = q % sw, qy = (q - qx) / sw, L = labels[q];
      if (qx > 0      && opaque[q - 1]  && !labels[q - 1])  { labels[q - 1]  = L; queue[tail++] = q - 1; }
      if (qx < sw - 1 && opaque[q + 1]  && !labels[q + 1])  { labels[q + 1]  = L; queue[tail++] = q + 1; }
      if (qy > 0      && opaque[q - sw] && !labels[q - sw]) { labels[q - sw] = L; queue[tail++] = q - sw; }
      if (qy < sh - 1 && opaque[q + sw] && !labels[q + sw]) { labels[q + sw] = L; queue[tail++] = q + sw; }
    }
    const size = new Map();
    for (let i = 0; i < n; i++) if (labels[i]) size.set(labels[i], (size.get(labels[i]) || 0) + 1);
    const count = [...size.values()].filter(v => v >= minPart).length;
    return { labels, count };
  };

  const runSmartCrop = async (canvasX, canvasY, drawnBox = null, lassoPath = null) => {
    const canvas = canvasRef.current;
    if (!canvas || smartCropRunning || smartCropBusyRef.current) return;
    smartCropBusyRef.current = true;
    try {
      return await runSmartCropInner(canvasX, canvasY, drawnBox, lassoPath, canvas);
    } finally {
      smartCropBusyRef.current = false;
    }
  };
  const runSmartCropInner = async (canvasX, canvasY, drawnBox, lassoPath, canvas) => {
    const rect = canvas.getBoundingClientRect();
    // ── Which picture is this crop about? ───────────────────────────────────────────────
    // Topmost box containing the point used to win outright, and that is wrong twice over.
    //
    // A cut-out layer is a full rectangle of which most is transparent — a head on a sheet of
    // nothing. Its BOX covers everything near it, so once one exists, every crop anywhere
    // under that box was handed the cut-out instead of the photograph the person was pointing
    // at, and the detection ran on empty pixels. Hence "it only ever crops the big one".
    //
    // So: a layer only claims the point if it is actually OPAQUE there, and the layer the
    // person has selected is asked first — selecting a picture and then cropping is about as
    // clear a statement of intent as the app ever gets. The old box test stays as the last
    // resort, so a picture that fails to load is still croppable.
    const sorted = [...canvasImages].sort((a, b) => (b.layerIndex ?? 1) - (a.layerIndex ?? 1));
    const boxOf = (img) => ({
      left: (img.x / 100) * rect.width,
      top:  (img.y / 100) * rect.height,
      w:    (img.width  / 21)   * rect.width,
      h:    (img.height / 29.7) * rect.height,
    });
    const inBox = (img) => {
      const { left, top, w, h } = boxOf(img);
      return canvasX >= left && canvasX <= left + w && canvasY >= top && canvasY <= top + h;
    };
    /** Is this layer actually painted at the point, or is it transparent there? */
    const opaqueAt = async (img) => {
      const { left, top, w, h } = boxOf(img);
      if (w <= 0 || h <= 0) return false;
      const el = await loadImgSafe(img.url);
      if (!el?.naturalWidth) return true;         // unreadable: fall back to the box
      const px = Math.min(el.naturalWidth - 1,  Math.max(0, Math.round(((canvasX - left) / w) * el.naturalWidth)));
      const py = Math.min(el.naturalHeight - 1, Math.max(0, Math.round(((canvasY - top)  / h) * el.naturalHeight)));
      try {
        const c = document.createElement('canvas'); c.width = 1; c.height = 1;
        const g = c.getContext('2d', { willReadFrequently: true });
        g.drawImage(el, px, py, 1, 1, 0, 0, 1, 1);
        return g.getImageData(0, 0, 1, 1).data[3] > 24;
      } catch { return true; }                    // tainted canvas: the box is all we have
    };
    let targetImg = null, relX = 0, relY = 0, rendW = 0, rendH = 0;
    const take = (img) => {
      const { left, top, w, h } = boxOf(img);
      targetImg = img; relX = canvasX - left; relY = canvasY - top; rendW = w; rendH = h;
    };
    // The selected layer is asked first, but it still has to be PAINTED where the person
    // pointed. Selecting a cut-out and then pointing at the photograph behind it, through a
    // transparent part of that cut-out, means the photograph — the selection is a tie-break
    // between layers that are both really there, not a way to overrule what is on the screen.
    const chosen = selectedImage ? canvasImages.find(i => i.id === selectedImage) : null;
    if (chosen && inBox(chosen) && await opaqueAt(chosen)) take(chosen);
    if (!targetImg) { for (const img of sorted) if (inBox(img) && await opaqueAt(img)) { take(img); break; } }
    if (!targetImg) { for (const img of sorted) if (inBox(img)) { take(img); break; } }
    if (!targetImg) {
      // No image under the lasso. If the sheet holds drawings or shapes, the lasso itself is
      // the right selection: hand it to the freeform crop, which cuts the composite (images,
      // strokes and shapes together). It used to reset here, so smart crop on a diagram
      // silently did nothing.
      const hasDiagram = drawingPaths.length > 0 || canvasShapesRef.current.length > 0;
      if (hasDiagram && lassoPath && lassoPath.length > 3) {
        setSelectionMode('freeform');
        setSelectionPath(lassoPath);
        setIsSelecting(false);            // freeform keeps its path visible with isSelecting off
        smartDetectedRef.current = false;
        setSmartDetected(false);
        return;
      }
      setIsSelecting(false);
      setSelectionPath([]);
      setSelectionStart({ x: 0, y: 0 }); setSelectionEnd({ x: 0, y: 0 });
      return;
    }
    setSmartCropRunning(true);
    setSelectionPath([]); // clear lasso; will be replaced by detected polygon
    try {
      const imgEl = await loadImgSafe(targetImg.url);
      if (!imgEl) return;
      // Sample at ≤800px wide for fast flood fill
      const MAX_W = 800;
      const scale = Math.min(1, MAX_W / (imgEl.naturalWidth || rendW));
      const sw = Math.max(1, Math.round((imgEl.naturalWidth || rendW) * scale));
      const sh = Math.max(1, Math.round((imgEl.naturalHeight || rendH) * scale));
      const tmp = document.createElement('canvas');
      tmp.width = sw; tmp.height = sh;
      tmp.getContext('2d').drawImage(imgEl, 0, 0, sw, sh);
      const imgData = tmp.getContext('2d').getImageData(0, 0, sw, sh);
      const sampleX = Math.floor((relX / rendW) * sw);
      const sampleY = Math.floor((relY / rendH) * sh);
      const imgLeft = (targetImg.x / 100) * rect.width;
      const imgTop  = (targetImg.y / 100) * rect.height;

      // If user drew a box, clamp detection to within that box on the image
      let clampMinX = 0, clampMinY = 0, clampMaxX = sw - 1, clampMaxY = sh - 1;
      if (drawnBox) {
        const boxLeft   = Math.max(0, drawnBox.x - imgLeft);
        const boxTop    = Math.max(0, drawnBox.y - imgTop);
        const boxRight  = Math.min(rendW, drawnBox.x + drawnBox.w - imgLeft);
        const boxBottom = Math.min(rendH, drawnBox.y + drawnBox.h - imgTop);
        clampMinX = Math.max(0, Math.floor((boxLeft   / rendW) * sw));
        clampMinY = Math.max(0, Math.floor((boxTop    / rendH) * sh));
        clampMaxX = Math.min(sw - 1, Math.ceil((boxRight  / rendW) * sw));
        clampMaxY = Math.min(sh - 1, Math.ceil((boxBottom / rendH) * sh));
      }

      // ── The user's lasso, rasterised in sample space ──────────────────────
      // The heuristic below only ever looked at the lasso's bounding BOX, so anything
      // inside the box but outside the drawn shape could survive. The polygon is the
      // user's actual intent; every mask produced here is intersected with it.
      const toSample = (pt) => [
        Math.max(0, Math.min(sw - 1, ((pt.x - imgLeft) / rendW) * sw)),
        Math.max(0, Math.min(sh - 1, ((pt.y - imgTop)  / rendH) * sh)),
      ];
      let lassoMask = null, lassoArea = 0;
      if (lassoPath && lassoPath.length > 3) {
        const lc = document.createElement('canvas'); lc.width = sw; lc.height = sh;
        const lctx = lc.getContext('2d');
        lctx.beginPath();
        lassoPath.forEach((pt, i) => { const [x, y] = toSample(pt); if (i === 0) lctx.moveTo(x, y); else lctx.lineTo(x, y); });
        lctx.closePath(); lctx.fillStyle = '#fff'; lctx.fill();
        const ld = lctx.getImageData(0, 0, sw, sh).data;
        lassoMask = new Uint8Array(sw * sh);
        for (let i = 0; i < sw * sh; i++) if (ld[i * 4 + 3] > 127) { lassoMask[i] = 1; lassoArea++; }
        if (lassoArea < 50) { lassoMask = null; lassoArea = 0; }
      }

      // ── AI cut-out (Gemini background removal), when a backend is configured ──
      // Colour heuristics fail whenever the subject shares a colour with what the lasso
      // edge touches — a yellow cake on a gold board came back as stripes of white cream
      // and a few sprinkles. A real matting pass handles that. The region is never stored,
      // and any failure (offline, no key, quota) falls through to the local heuristic below.
      // Transparent sources skip this: their alpha is exact.
      //
      // ── Why the model is shown more than you drew round ──────────────────────────────
      // It used to be sent the lasso's box and nothing else. Draw snugly round a head — which
      // is what drawing round a head means — and the picture handed to a BACKGROUND remover is
      // then almost entirely head, with no background in it to remove. So it answered, quite
      // correctly, that nearly all of it was foreground; intersecting that with the lasso gave
      // back the lasso itself, jagged hand-drawn edge and all. That is the whole of "smart crop
      // is exactly the same as freeform crop": the clever step ran, was starved of the one
      // thing it needed, and its answer was indistinguishable from doing nothing.
      // It is now given room around the lasso — and the whole picture when that is barely
      // bigger — so there is background in the frame for it to tell the subject FROM.
      const hasAlphaPathEarly = (d) => { const a = d.data; for (let i = 3; i < a.length; i += 4) if (a[i] < 30) return true; return false; };
      let aiFg = null;
      let _aiCutFailed = null;
      let _aiFilledLasso = false;
      let _autoLeadIsland = null;   // the biggest single object, for auto crop's own sanity checks
      // isAiReachable, not isAiEnabled: offline this whole block is a guaranteed failure that
      // costs the browser's connect timeout to discover, with "Detecting object…" on screen the
      // whole time. Skipping straight to the on-device matting below is both faster and better.
      if (!hasAlphaPathEarly(imgData) && isAiReachable() && clampMaxX > clampMinX + 8 && clampMaxY > clampMinY + 8) {
        try {
          const lw = clampMaxX - clampMinX + 1, lh = clampMaxY - clampMinY + 1;
          // Half the lasso's own size again on every side, and at least 24 sample pixels —
          // enough that a head drawn round tightly still arrives with wall on both sides of it.
          const pad = Math.max(24, Math.round(Math.max(lw, lh) * 0.5));
          const gx0 = Math.max(0, clampMinX - pad), gy0 = Math.max(0, clampMinY - pad);
          const gx1 = Math.min(sw - 1, clampMaxX + pad), gy1 = Math.min(sh - 1, clampMaxY + pad);
          const cw = gx1 - gx0 + 1, ch = gy1 - gy0 + 1;
          const cc = document.createElement('canvas'); cc.width = cw; cc.height = ch;
          cc.getContext('2d').drawImage(tmp, gx0, gy0, cw, ch, 0, 0, cw, ch);
          // ── Ask for one subject when one subject was drawn round ────────────────────────
          // The server has three ways to answer, and the middle one — "list every object, each
          // with its own mask" — exists for AUTO crop, which must part a table of things into
          // layers. A lasso has already said which thing; asking for the list costs a call from
          // a small daily allowance and can only return the same head. It also displaced the
          // route that produced the results confirmed good here. So a lasso asks for the mask
          // directly, exactly as it used to.
          // Auto crop goes straight to the backdrop route: the object list in front of it took
          // up to 45 s on a photograph and then declined, so it cost the wait and gave nothing.
          const cutUrl = await removeImageBg(cc, lassoMask ? { prefer: 'mask' }
            : (cropOptsRef.current?.fromAuto ? { prefer: 'backdrop' } : undefined));
          const cutEl = await loadImgSafe(cutUrl);
          if (cutEl) {
            const rc = document.createElement('canvas'); rc.width = sw; rc.height = sh;
            const rctx = rc.getContext('2d');
            rctx.drawImage(cutEl, gx0, gy0, cw, ch);               // back into sample space
            const rd = rctx.getImageData(0, 0, sw, sh).data;
            // ── Where the object stops, exactly ───────────────────────────────────────────
            // The model does not answer in yes and no; it answers in HOW MUCH, and at the edge
            // of a head that ramps from nothing to everything across two or three pixels. Those
            // in-between pixels are, in the original photograph, a mixture of hair and whatever
            // was behind it — and only the model's alpha is used here, the colours come from
            // the photo — so every one of them that is kept brings a little of the wall with it.
            // Keeping anything above 12% (the old > 30) kept nearly the whole ramp, and that is
            // the pale rim of doorframe still stuck to the hair and the grey halo round the
            // cake plaque: the shape was right, the edge was carrying its surroundings.
            //
            // So: anything the model is more than half sure of is the object, and the soft rim
            // below that is kept only where it is surrounded by more of the same. Solid
            // interiors are untouched, which matters for line art like the plaque — thin strokes
            // are opaque in their middles and survive; it is only the fringe that is pared back.
            // ── Alpha, and nothing else ──────────────────────────────────────────────────
            // `cutUrl` is the output of removeImageBg, and removeImageBg does not hand back what
            // the server said. It hands back applyAiCutout's work: whatever arrived — a cut-out
            // with real transparency, a black-and-white mask, a rendered subject on a flat
            // ground — has already been read, turned into an alpha channel and applied to THESE
            // pixels. By the time it reaches here there is exactly one form, and its answer is
            // in the alpha channel.
            //
            // A later version of this block read brightness instead whenever the picture looked
            // opaque, on the strength of a measurement taken from the SERVER's reply — which is
            // indeed a mask. But that reply is not what arrives here; it is converted one step
            // upstream. So the mask reading was solving a problem that had already been solved,
            // in a place where it could only misfire, and it cost Smart Crop the quality that
            // had been confirmed good. It is gone. If the server's answer ever needs
            // interpreting differently, applyAiCutout is where that belongs.
            const SURE = 200, MAYBE = 140;
            aiFg = new Uint8Array(sw * sh);
            let n = 0;
            for (let i = 0; i < sw * sh; i++) {
              const a = rd[i * 4 + 3];
              if (a >= SURE) { aiFg[i] = 1; n++; continue; }
              if (a < MAYBE) continue;
              const x = i % sw, y = (i - x) / sw;
              const ok = x > 0 && x < sw - 1 && y > 0 && y < sh - 1
                && rd[(i - 1) * 4 + 3] >= MAYBE && rd[(i + 1) * 4 + 3] >= MAYBE
                && rd[(i - sw) * 4 + 3] >= MAYBE && rd[(i + sw) * 4 + 3] >= MAYBE;
              if (ok) { aiFg[i] = 1; n++; }
            }
            if (n < 50) { aiFg = null; _aiCutFailed = 'the model returned nothing'; }
            // Did it actually find an edge INSIDE what was drawn, or is it handing back the
            // drawn shape? Measured, not assumed, and used only to say so — the mask itself is
            // left exactly as it came, because the lasso is still the best answer available.
            if (aiFg && lassoMask) {
              let inLasso = 0;
              for (let i = 0; i < sw * sh; i++) if (aiFg[i] && lassoMask[i]) inLasso++;
              _aiFilledLasso = lassoArea > 0 && inLasso / lassoArea > 0.97;
            }
          } else {
            _aiCutFailed = 'the cut-out could not be read back';
          }
        } catch (err) {
          // Worth saying out loud. On a photograph this IS the method that works — the colour
          // heuristic below is a fallback for simple pictures, and on a face against a wall of
          // similar tone it returns a scatter of patches. Falling back to it in silence is how
          // a broken cut-out gets handed over looking like the intended answer.
          console.warn('Smart crop: AI cut-out unavailable, using local detection.', err?.message || err);
          _aiCutFailed = err?.message || 'unavailable';
          aiFg = null;
        }
        // ── Say it, on the device, where the person is ───────────────────────────────────
        // On a photograph this is THE method. What follows it is a flood-fill on colour, which
        // works on a drawing or a product on a white background and cannot separate a face from
        // a wall of the same tone — tap a face with it and you get an outline in blocks. Losing
        // the model and carrying on in silence means that blocky result is presented as the
        // answer to "find the face", with nothing to say the good method never ran.
        // ── Only worth saying when it changes what you get ────────────────────────────────
        // With a lasso there is now a real method on the device, so the service being out is
        // an implementation detail, not news: announcing it over a good result reads as an
        // error and teaches people to distrust a cut-out that is fine. Where nothing was drawn
        // there is no second method, so the warning still matters and still fires here.
        if (_aiCutFailed && !lassoMask) flash(`Cut-out service: ${_aiCutFailed}`);
      }

      // ── A cut-out: what is it made of? ──────────────────────────────────────────────────
      // Drawn round part of a cut-out: that part's own edge, found on the device — the matting a
      // photograph gets when the service is out, needing no service here at all, and quick.
      // Auto crop: the AI names the objects (labelCutoutObjects), but only for a few seconds. If
      // it has not answered by then, the crop carries on exactly as it always did — the island
      // split — rather than leaving the person watching "Detecting…" for a minute and a half.
      let cutoutLabels = null, cutoutObjects = 0, cutoutLassoFg = null;
      if (hasAlphaPathEarly(imgData)) {
        if (lassoMask) {
          try {
            const m = grabCutInMask(cutoutOnKeyColour(imgData), lassoMask);
            if (m) {
              for (let i = 0; i < sw * sh; i++) if (imgData.data[i * 4 + 3] <= 30) m[i] = 0;
              cutoutLassoFg = m;
            }
          } catch (err) { console.warn('Smart crop: could not find the edge inside the cut-out.', err?.message || err); }
        } else if (cropOptsRef.current?.fromAuto && isAiReachable()) {
          try {
            const ground = document.createElement('canvas'); ground.width = sw; ground.height = sh;
            const gctx = ground.getContext('2d');
            gctx.fillStyle = '#c8c8c8'; gctx.fillRect(0, 0, sw, sh);
            gctx.drawImage(tmp, 0, 0);
            const objects = await Promise.race([
              aiFindObjects(ground.toDataURL('image/png')),
              new Promise((_, reject) => setTimeout(() => reject(new Error('no answer within 12 s')), 12000)),
            ]);
            const found = labelCutoutObjects(objects, imgData, sw, sh);
            if (found && found.count >= 2) { cutoutLabels = found.labels; cutoutObjects = found.count; }
          } catch (err) {
            console.warn('Auto crop: could not name the objects inside the cut-out — splitting it the usual way.', err?.message || err);
          }
        }
      } else if (aiFg && !lassoMask && cropOptsRef.current?.fromAuto && isAiReachable()) {
        // ── Auto crop on a photograph: the objects inside the cut-out ──────────────────────
        // The cut-out service used to answer auto crop with a list of objects, each with its own
        // mask — which is what parted the figures from the cake — but that answer took up to
        // 45 s and often did not come, so auto crop now gets the plain backdrop cut-out in a few
        // seconds instead. That cut-out is one piece. So the same question a cut-out picture is
        // asked is asked of it: which separate things is it made of (boxes only, a few seconds),
        // and each one's edge is found on the device. If no answer comes, it stays one piece.
        try {
          const N = sw * sh;
          const cc2 = document.createElement('canvas'); cc2.width = sw; cc2.height = sh;
          const c2x = cc2.getContext('2d');
          const cut = c2x.createImageData(sw, sh);
          cut.data.set(imgData.data);
          for (let i = 0; i < N; i++) if (!aiFg[i]) cut.data[i * 4 + 3] = 0;
          c2x.putImageData(cut, 0, 0);
          const ground = document.createElement('canvas'); ground.width = sw; ground.height = sh;
          const gctx = ground.getContext('2d');
          gctx.fillStyle = '#c8c8c8'; gctx.fillRect(0, 0, sw, sh);
          gctx.drawImage(cc2, 0, 0);
          const objects = await Promise.race([
            aiFindObjects(ground.toDataURL('image/png')),
            new Promise((_, reject) => setTimeout(() => reject(new Error('no answer within 12 s')), 12000)),
          ]);
          const found = labelCutoutObjects(objects, cut, sw, sh);
          if (found && found.count >= 2) { cutoutLabels = found.labels; cutoutObjects = found.count; }
        } catch (err) {
          console.warn('Auto crop: could not name the objects in the cut-out — keeping it whole.', err?.message || err);
        }
      }

      let bounds = floodFillBounds(imgData, sampleX, sampleY);
      // Clamp to drawn box
      bounds = {
        minX: Math.max(bounds.minX, clampMinX), maxX: Math.min(bounds.maxX, clampMaxX),
        minY: Math.max(bounds.minY, clampMinY), maxY: Math.min(bounds.maxY, clampMaxY),
      };

      // Check for transparency BEFORE shrinking — transparent images are already
      // alpha-masked (polygon-clipped), so shrinkFrameBbox must be skipped.
      // If we don't skip it, transparent border pixels (alpha=0, rgb=0,0,0) look like
      // a solid black "frame" and the bbox collapses to a thin sliver.
      const { data: pd, width: pw } = imgData;
      let hasAlphaPath = false;
      for (let ai = 3; ai < pd.length; ai += 4) if (pd[ai] < 30) { hasAlphaPath = true; break; }

      // Iteratively peel nested frame layers (dark shadow → white card → content).
      // Only for opaque images — transparent images are already correctly bounded.
      if (!hasAlphaPath) {
        for (let pass = 0; pass < 10; pass++) {
          const refined = shrinkFrameBbox(imgData, bounds);
          if (
            refined.minX === bounds.minX && refined.maxX === bounds.maxX &&
            refined.minY === bounds.minY && refined.maxY === bounds.maxY
          ) break;
          bounds = refined;
        }
      }
      let { minX, maxX, minY, maxY } = bounds;

      // ── Detect inner background color from the LASSO BOUNDARY pixels ─────
      // The lasso boundary (clamp edges) is drawn by the user OUTSIDE the object
      // but INSIDE the actual background (e.g. white card, not the dark outer frame).
      // Sampling here avoids picking up the dark outer frame color that the full
      // image border samples would return — which would cause dark dress/hair pixels
      // to be misclassified as "similar to background" and punched out of the mask.
      const ppx = (x, y) => { const p = (y*pw+x)*4; return [pd[p],pd[p+1],pd[p+2]]; };
      const pdt = (a, b) => Math.abs(a[0]-b[0])+Math.abs(a[1]-b[1])+Math.abs(a[2]-b[2]);
      let polyBgColor = [255, 255, 255];
      // How uniform that backdrop actually is: the share of the sampled border that agreed with
      // the winning colour. A sheet of one colour scores near 1; a photographed scene does not.
      // Only a flat backdrop can be painted back over a hole convincingly, so this decides it.
      let bgFlatness = 0;
      if (!hasAlphaPath) {
        const bsamp = [];
        const bstep = Math.max(1, Math.round(Math.min(clampMaxX - clampMinX, clampMaxY - clampMinY) / 8));
        for (let x = clampMinX; x <= clampMaxX; x += bstep) {
          bsamp.push(ppx(x, clampMinY)); bsamp.push(ppx(x, clampMaxY));
        }
        for (let y = clampMinY + bstep; y < clampMaxY; y += bstep) {
          bsamp.push(ppx(clampMinX, y)); bsamp.push(ppx(clampMaxX, y));
        }
        const CLUSTER_TOL = 50 * 3;
        let best = 0;
        for (const c of bsamp) {
          let cnt=0,r=0,g=0,b=0;
          for (const s of bsamp) if (pdt(s,c) < CLUSTER_TOL) { cnt++; r+=s[0]; g+=s[1]; b+=s[2]; }
          if (cnt > best) { best=cnt; polyBgColor=[r/cnt, g/cnt, b/cnt]; }
        }
        if (bsamp.length) bgFlatness = best / bsamp.length;
      }
      const POLY_BG_TOL = 60 * 3;
      const isFgPx = (x, y) => {
        if (hasAlphaPath) return pd[(y*pw+x)*4+3] > 30;
        const p = (y*pw+x)*4;
        return Math.abs(pd[p]-polyBgColor[0])+Math.abs(pd[p+1]-polyBgColor[1])+Math.abs(pd[p+2]-polyBgColor[2]) >= POLY_BG_TOL;
      };

      // ── Build pixel-level FG mask via connected-BG flood fill ─────────────
      // Seed BFS from lasso boundary pixels that match the background color.
      // The BFS spreads through all connected background-colored pixels within
      // the lasso area. Pixels NOT reachable = foreground.
      //
      // Why this beats per-pixel threshold:
      //   Armpit gap (white, connected to outer bg via path around the arm) → BFS
      //     reaches it → correctly marked background.
      //   Face skin (light pink, NOT connected to outer bg) → BFS blocked by
      //     surrounding body pixels → correctly stays foreground, no holes.
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = sw; maskCanvas.height = sh;
      const mkCtx = maskCanvas.getContext('2d');
      const mkData = mkCtx.createImageData(sw, sh);
      let fgMinX = sw, fgMaxX = 0, fgMinY = sh, fgMaxY = 0;

      if (hasAlphaPath) {
        // Transparent images: alpha channel is exact — direct copy, no flood fill needed
        for (let fy = clampMinY; fy <= clampMaxY; fy++) {
          for (let fx = clampMinX; fx <= clampMaxX; fx++) {
            if (pd[(fy*pw+fx)*4+3] > 30) {
              const mi = (fy*sw+fx)*4;
              mkData.data[mi] = mkData.data[mi+1] = mkData.data[mi+2] = 255; mkData.data[mi+3] = 255;
              if (fx < fgMinX) fgMinX = fx; if (fx > fgMaxX) fgMaxX = fx;
              if (fy < fgMinY) fgMinY = fy; if (fy > fgMaxY) fgMaxY = fy;
            }
          }
        }
      } else {
        const visited = new Uint8Array(sw * sh);
        const bfsQ = new Int32Array(sw * sh);
        let bfsHead = 0, bfsTail = 0;
        const seed = (x, y) => {
          const idx = y * sw + x;
          if (!visited[idx] && !isFgPx(x, y)) { visited[idx] = 1; bfsQ[bfsTail++] = idx; }
        };
        for (let x = clampMinX; x <= clampMaxX; x++) { seed(x, clampMinY); seed(x, clampMaxY); }
        for (let y = clampMinY + 1; y < clampMaxY; y++) { seed(clampMinX, y); seed(clampMaxX, y); }
        const DX = [-1, 1, 0, 0], DY = [0, 0, -1, 1];
        while (bfsHead < bfsTail) {
          const idx = bfsQ[bfsHead++];
          const px = idx % sw, py = (idx / sw) | 0;
          for (let d = 0; d < 4; d++) {
            const nx = px + DX[d], ny = py + DY[d];
            if (nx < clampMinX || nx > clampMaxX || ny < clampMinY || ny > clampMaxY) continue;
            const nidx = ny * sw + nx;
            if (!visited[nidx] && !isFgPx(nx, ny)) { visited[nidx] = 1; bfsQ[bfsTail++] = nidx; }
          }
        }
        for (let fy = clampMinY; fy <= clampMaxY; fy++) {
          for (let fx = clampMinX; fx <= clampMaxX; fx++) {
            if (!visited[fy * sw + fx]) {
              const mi = (fy * sw + fx) * 4;
              mkData.data[mi] = mkData.data[mi+1] = mkData.data[mi+2] = 255; mkData.data[mi+3] = 255;
              if (fx < fgMinX) fgMinX = fx; if (fx > fgMaxX) fgMaxX = fx;
              if (fy < fgMinY) fgMinY = fy; if (fy > fgMaxY) fgMaxY = fy;
            }
          }
        }
      }
      // ── Hole-fill pass via morphological closing (dilate → erode) ────────
      // Closes background "leaks" where BFS bled through highlights/shading and
      // punched an interior region transparent. Preserves the silhouette outline
      // because we only ADD newly-closed pixels — never remove originals.
      // Skipped for transparent-source images (mask is already exact).
      //
      // Uses separable (1-D horizontal then 1-D vertical) kernels for speed,
      // and treats out-of-bounds neighbors as foreground in the erode step so
      // holes that touch the silhouette near a canvas/clamp edge get filled too.
      if (!hasAlphaPath && fgMaxX > fgMinX && fgMaxY > fgMinY) {
        // Closing kernel scaled to image size (≈1.5% of min dimension, min 8 px)
        // → bridges gaps up to ~2·R wide (≈24 px on an 800-px sample).
        const CLOSE_R = Math.max(8, Math.round(Math.min(sw, sh) * 0.015));

        const fgArr = new Uint8Array(sw * sh);
        for (let i = 0; i < sw * sh; i++) {
          if (mkData.data[i * 4 + 3] === 255) fgArr[i] = 1;
        }

        const x0 = Math.max(0, fgMinX - CLOSE_R), x1 = Math.min(sw - 1, fgMaxX + CLOSE_R);
        const y0 = Math.max(0, fgMinY - CLOSE_R), y1 = Math.min(sh - 1, fgMaxY + CLOSE_R);

        // === DILATE — separable: horizontal pass, then vertical pass ===
        const dilH = new Uint8Array(sw * sh);
        for (let y = y0; y <= y1; y++) {
          const rowBase = y * sw;
          for (let x = x0; x <= x1; x++) {
            const xMin = x - CLOSE_R < 0 ? 0 : x - CLOSE_R;
            const xMax = x + CLOSE_R > sw - 1 ? sw - 1 : x + CLOSE_R;
            for (let nx = xMin; nx <= xMax; nx++) {
              if (fgArr[rowBase + nx]) { dilH[rowBase + x] = 1; break; }
            }
          }
        }
        const dil = new Uint8Array(sw * sh);
        for (let x = x0; x <= x1; x++) {
          for (let y = y0; y <= y1; y++) {
            const yMin = y - CLOSE_R < 0 ? 0 : y - CLOSE_R;
            const yMax = y + CLOSE_R > sh - 1 ? sh - 1 : y + CLOSE_R;
            for (let ny = yMin; ny <= yMax; ny++) {
              if (dilH[ny * sw + x]) { dil[y * sw + x] = 1; break; }
            }
          }
        }

        // === ERODE — separable, with OOB treated as FG (preserves edges) ===
        const erH = new Uint8Array(sw * sh);
        for (let y = y0; y <= y1; y++) {
          const rowBase = y * sw;
          for (let x = x0; x <= x1; x++) {
            if (!dil[rowBase + x]) continue;
            const xMin = x - CLOSE_R < 0 ? 0 : x - CLOSE_R;
            const xMax = x + CLOSE_R > sw - 1 ? sw - 1 : x + CLOSE_R;
            let all = 1;
            for (let nx = xMin; nx <= xMax; nx++) {
              if (!dil[rowBase + nx]) { all = 0; break; }
            }
            if (all) erH[rowBase + x] = 1;
          }
        }
        // Strict source-colour gate — distinguishes:
        //   - LEAK-INDUCED HOLES (e.g. dress highlight: fuzzy-near-bg colour
        //       within BFS POLY_BG_TOL=180 but well above STRICT_BG_TOL) → FILL
        //   - TRUE BG POCKETS (e.g. white between hair strands: essentially the
        //       exact polyBgColor) → SKIP (otherwise they'd render as visible
        //       white patches in the cropped output)
        const STRICT_BG_TOL = 60; // ≈ ±20 per channel from polyBgColor
        const bgR = polyBgColor[0], bgG = polyBgColor[1], bgB = polyBgColor[2];

        for (let x = x0; x <= x1; x++) {
          for (let y = y0; y <= y1; y++) {
            if (!erH[y * sw + x]) continue;
            const yMin = y - CLOSE_R < 0 ? 0 : y - CLOSE_R;
            const yMax = y + CLOSE_R > sh - 1 ? sh - 1 : y + CLOSE_R;
            let all = 1;
            for (let ny = yMin; ny <= yMax; ny++) {
              if (!erH[ny * sw + x]) { all = 0; break; }
            }
            if (all && !fgArr[y * sw + x]) {
              const p = (y * sw + x) * 4;
              const dist = Math.abs(pd[p] - bgR) + Math.abs(pd[p+1] - bgG) + Math.abs(pd[p+2] - bgB);
              if (dist > STRICT_BG_TOL) {
                mkData.data[p]   = mkData.data[p+1] = mkData.data[p+2] = 255;
                mkData.data[p+3] = 255;
              }
            }
          }
        }
      }

      // ── Final mask: AI result if we have one, then lasso intersection, then a sanity check ──
      const finalFg = new Uint8Array(sw * sh);
      if (aiFg) {
        finalFg.set(aiFg);
      } else {
        for (let i = 0; i < sw * sh; i++) if (mkData.data[i * 4 + 3] === 255) finalFg[i] = 1;
      }
      if (lassoMask) for (let i = 0; i < sw * sh; i++) if (!lassoMask[i]) finalFg[i] = 0;

      // ── A picture that has already been cut out ──────────────────────────────────────
      // Its alpha IS the object: somebody, or something, has already answered the question
      // every heuristic below is guessing at, and exactly. Detecting it again was making a
      // worse job of an answered question — the passes that look at COLOUR read the cleared
      // surroundings as pixels like any other (transparent black is a colour to them) and
      // handed back most of the rectangle. So the alpha is taken as the mask, plainly, and
      // the rest of the pipeline is skipped. The one thing still worth doing is separating
      // touching pieces, which the island pass below does on any mask.
      if (hasAlphaPath) {
        finalFg.fill(0);
        for (let y = clampMinY; y <= clampMaxY; y++) {
          for (let x = clampMinX; x <= clampMaxX; x++) {
            if (pd[(y * pw + x) * 4 + 3] > 30) finalFg[y * sw + x] = 1;
          }
        }
        if (lassoMask) for (let i = 0; i < sw * sh; i++) if (!lassoMask[i]) finalFg[i] = 0;
      }

      // ── …and which of its objects was meant ─────────────────────────────────────────────
      // A lasso: the part drawn round, with its own edge instead of the line that was drawn.
      // Auto crop: every object, each parted from its neighbours by a two-pixel seam where they
      // touch, so the split into pieces that follows the copy (splitCutoutIntoPieces) sees
      // separate islands and makes separate layers. An object wholly inside another (a plaque on
      // a cake) is closed back in by the hole fill below and stays with what it sits in.
      if (hasAlphaPath && cutoutLassoFg) {
        finalFg.fill(0);
        for (let i = 0; i < sw * sh; i++) if (cutoutLassoFg[i]) finalFg[i] = 1;
      }
      if (cutoutLabels) {
        const L = cutoutLabels;
        const N = sw * sh;
        if (cropOptsRef.current?.fromAuto) {
          finalFg.fill(0);
          for (let i = 0; i < N; i++) {
            const k = L[i];
            if (!k) continue;
            const x = i % sw, y = (i - x) / sw;
            if (x < clampMinX || x > clampMaxX || y < clampMinY || y > clampMaxY) continue;
            const seam = (x > 0 && L[i - 1] && L[i - 1] !== k) || (x < sw - 1 && L[i + 1] && L[i + 1] !== k)
              || (y > 0 && L[i - sw] && L[i - sw] !== k) || (y < sh - 1 && L[i + sw] && L[i + sw] !== k);
            if (!seam) finalFg[i] = 1;
          }
        }
      }

      // ── When there is no method, use what was drawn ───────────────────────────────────
      // Two things can separate an object here. The cut-out service, which works on anything,
      // and the colour flood fill, which needs a backdrop that is actually one colour: a
      // drawing on white, a product on a plain sheet. On a PHOTOGRAPHED SCENE the colour test
      // has nothing to go on, because a face and the wall behind it are a continuum of tone —
      // and what it returns is not a near-miss, it is a face with black wedges bitten out of
      // it and a ragged pink rim. Handed over as the answer to "cut round this head", that is
      // worse than useless: it looks like the tool is broken rather than unavailable.
      //
      // So the test is not "how much did it keep" — the mangling keeps two thirds of the
      // lasso and sails through that. It is whether the method APPLIES at all. A flat backdrop
      // (bgFlatness ≥ 0.8) is the colour test's home ground and it is left alone there. A
      // photograph is not, and on a photograph the shape the person drew is both honest and
      // better: it is exactly what they asked for, with a clean edge.
      //
      // None of this touches a cut-out that answered. aiFg and an exact alpha mask are both
      // excluded, so the path that was confirmed good is not on this branch at all.
      if (!aiFg && !hasAlphaPath && lassoMask) {
        // ── The smartness, on the device ──────────────────────────────────────────────────
        // A minimum cut seeded by the drawn shape: it learns the colours inside against the
        // colours outside and puts the boundary where the picture's own edges are. It needs
        // no service, no allowance and no download, and unlike the flood fill it answers for
        // every pixel at once, which is why the edge comes out connected instead of in slabs.
        // Measured against known shapes: 0.99 overlap where the drawn shape alone scores 0.44.
        const _t0 = Date.now();
        let localMatte = null;
        try { localMatte = grabCutInMask(imgData, lassoMask); }
        catch (err) { console.warn('Smart crop: on-device matting failed.', err?.message || err); }
        if (localMatte) {
          finalFg.set(localMatte);
          console.debug(`Smart crop: no cut-out service — found the edge on the device in ${Date.now() - _t0}ms.`);
        } else {
          // It could not tell the two sides apart either — which happens when what was drawn
          // round is the same colour as what surrounds it. Then the honest answers are the
          // colour flood fill, where its one requirement is met, and otherwise the drawn shape.
          let fgIn = 0;
          for (let i = 0; i < sw * sh; i++) if (finalFg[i]) fgIn++;
          // The cake-on-gold case: the colour model ate the subject and left confetti.
          const tooLittle = fgIn < lassoArea * 0.3;
          // The photographed-scene case: the colour model cannot separate this at all.
          const noMethod = bgFlatness < 0.8;
          if (tooLittle || noMethod) {
            console.warn(`Smart crop: ${noMethod
              ? `the backdrop is not one colour (${Math.round(100 * bgFlatness)}% agreement) and the cut-out service did not answer`
              : `detection kept only ${Math.round(100 * fgIn / lassoArea)}% of the lasso`} — using the drawn shape instead.`);
            finalFg.set(lassoMask);
            if (_aiCutFailed) flash('Could not find the edge — used your drawn shape');
          }
        }
      }

      // ── A photo's own white border is not the background behind it ────────────────────
      // A picture printed or scanned with a white margin arrives as a solid rectangle of
      // content sitting inside an even pale frame. Every detector above — the colour flood
      // fill and the AI matting alike — reads that margin as background and cuts it away,
      // and on a white sheet that looks exactly like the crop biting into the photo: the
      // border the picture came with is gone, and only the surrounding white it was supposed
      // to be told apart from is left.
      //
      // The giveaway is the shape of the two parts. What was kept is a full rectangle, and
      // what was dropped is an even pale frame all the way round. Real background fails both
      // tests: a subject is not rectangular, and the room around it is never that even. Only
      // applied where nothing was drawn to say otherwise — a box or a lasso IS an instruction
      // to cut inside the picture, and is left alone.
      if (!lassoMask && !drawnBox && !hasAlphaPath) {
        let bx0 = sw, bx1 = -1, by0 = sh, by1 = -1, kept = 0;
        for (let i = 0; i < sw * sh; i++) {
          if (!finalFg[i]) continue;
          kept++;
          const fx = i % sw, fy = (i / sw) | 0;
          if (fx < bx0) bx0 = fx; if (fx > bx1) bx1 = fx;
          if (fy < by0) by0 = fy; if (fy > by1) by1 = fy;
        }
        const gaps = [bx0 - clampMinX, clampMaxX - bx1, by0 - clampMinY, clampMaxY - by1];
        const thinnest = Math.min(...gaps), thickest = Math.max(...gaps);
        const clampW = clampMaxX - clampMinX + 1, clampH = clampMaxY - clampMinY + 1;
        const boxArea = (bx1 - bx0 + 1) * (by1 - by0 + 1);
        if (bx1 > bx0 && by1 > by0
            && thinnest > 0
            && thickest <= thinnest * 1.4
            && thickest <= Math.min(clampW, clampH) * 0.25
            && kept >= boxArea * 0.9) {
          // Is the dropped frame actually pale, and all one colour? Sample it thinly.
          let n = 0, sum = 0, dark = 0;
          const step = Math.max(1, Math.round(Math.min(clampW, clampH) / 60));
          for (let y = clampMinY; y <= clampMaxY; y += step) {
            for (let x = clampMinX; x <= clampMaxX; x += step) {
              if (x >= bx0 && x <= bx1 && y >= by0 && y <= by1) continue;   // inside the content
              const p = (y * pw + x) * 4;
              const lum = (pd[p] + pd[p + 1] + pd[p + 2]) / 3;
              sum += lum; n++;
              if (lum < 218) dark++;
            }
          }
          if (n > 0 && sum / n > 232 && dark / n < 0.08) {
            for (let y = clampMinY; y <= clampMaxY; y++) {
              for (let x = clampMinX; x <= clampMaxX; x++) finalFg[y * sw + x] = 1;
            }
          }
        }
      }

      // ── One object, not every object ──────────────────────────────────────────────────
      //
      // Everything above answers "what is not the background?", which on a flat backdrop with
      // three things standing on it answers "all three". That is why picking one object out of a
      // group did nothing useful: the cut-out was the whole group, edges and gaps included.
      //
      // What is left is separating them, and connectedness does it — two things of different
      // colours that do not touch are two runs of foreground with background in between. So the
      // foreground is labelled into its own islands and all but one are dropped: the island under
      // the finger, or, when the press landed on the backdrop itself (auto crop aims at the middle
      // of the picture and cannot know what is there), the biggest one.
      // ── Which side of the mask is the object? ────────────────────────────────────────────
      // A matte says "these pixels belong together". It does not say which of the two sides you
      // wanted, and when it comes back the other way round — the wall held, the person dropped —
      // everything after it is faithful to that: the outline traces the room, and the cut-out is
      // a doorway with a person-shaped hole in it. A good matte, used inside out.
      //
      // A TAP settles it, because a tap is a statement: this, the thing under my finger. So if
      // the mask does not contain the tapped pixel, and it lines the frame's border — which a
      // subject, by the nature of pointing a camera at something, seldom does — it is the
      // background, and it is turned inside out. Both conditions must hold, so a subject that
      // genuinely runs to the edges does not trip it.
      //
      // Auto Crop is excluded, and this is the second time that has had to be written down.
      // It was tried: the same test, on a stricter setting, using the middle of the picture in
      // place of the tap. It misfires — a portrait's shoulders and hair reach the frame's edge,
      // and the exact centre pixel can easily fall on background between features, so a mask
      // that was already CORRECT gets called the background and turned inside out. What comes
      // back then is the room, and the room's bounding box is the whole photograph.
      //
      // The difference is not the threshold, it is the evidence. A tap is someone saying "this
      // one". The centre of the picture is an assumption about where subjects usually are, and
      // an assumption is not enough to overrule a matte.
      {
        const isAuto = !!cropOptsRef.current?.fromAuto;
        // Objects taken from a cut-out's own alpha have no wrong side: the clear part is never
        // the object. Kept objects may also run past the lasso's box, which this test reads.
        const needBorder = (isAuto || (hasAlphaPath && (cutoutLabels || cutoutLassoFg))) ? Infinity : 0.7;
        const sx = Math.max(clampMinX, Math.min(clampMaxX, sampleX));
        const sy = Math.max(clampMinY, Math.min(clampMaxY, sampleY));
        let ring = 0, ringFg = 0;
        for (let x = clampMinX; x <= clampMaxX; x++) {
          ring += 2;
          if (finalFg[clampMinY * sw + x]) ringFg++;
          if (finalFg[clampMaxY * sw + x]) ringFg++;
        }
        for (let y = clampMinY + 1; y < clampMaxY; y++) {
          ring += 2;
          if (finalFg[y * sw + clampMinX]) ringFg++;
          if (finalFg[y * sw + clampMaxX]) ringFg++;
        }
        const atTap = finalFg[sy * sw + sx] === 1;
        if (!atTap && ring > 0 && ringFg / ring > needBorder) {
          console.warn(`${isAuto ? 'Auto' : 'Smart'} crop: the mask covers ${Math.round(100 * ringFg / ring)}% of the border and misses the ${isAuto ? 'centre' : 'tap'} — it is the background. Inverting.`);
          for (let i = 0; i < sw * sh; i++) finalFg[i] = finalFg[i] ? 0 : 1;
          if (lassoMask) for (let i = 0; i < sw * sh; i++) if (!lassoMask[i]) finalFg[i] = 0;
          for (let y = 0; y < sh; y++) {
            for (let x = 0; x < sw; x++) {
              if (x < clampMinX || x > clampMaxX || y < clampMinY || y > clampMaxY) finalFg[y * sw + x] = 0;
            }
          }
        }
      }
      {
        const label = new Int32Array(sw * sh);
        const queue = new Int32Array(sw * sh);
        let next = 1, biggest = 0, biggestN = 0, atPoint = 0;
        const seedX = Math.max(clampMinX, Math.min(clampMaxX, sampleX));
        const seedY = Math.max(clampMinY, Math.min(clampMaxY, sampleY));
        for (let i = 0; i < sw * sh; i++) {
          if (!finalFg[i] || label[i]) continue;
          const id = next++;
          let head = 0, tail = 0, n = 0;
          queue[tail++] = i; label[i] = id;
          while (head < tail) {
            const q = queue[head++]; n++;
            const qx = q % sw, qy = (q - qx) / sw;
            if (qx > 0      && finalFg[q - 1]  && !label[q - 1])  { label[q - 1]  = id; queue[tail++] = q - 1; }
            if (qx < sw - 1 && finalFg[q + 1]  && !label[q + 1])  { label[q + 1]  = id; queue[tail++] = q + 1; }
            if (qy > 0      && finalFg[q - sw] && !label[q - sw]) { label[q - sw] = id; queue[tail++] = q - sw; }
            if (qy < sh - 1 && finalFg[q + sw] && !label[q + sw]) { label[q + sw] = id; queue[tail++] = q + sw; }
          }
          if (n > biggestN) { biggestN = n; biggest = id; }
        }
        atPoint = label[seedY * sw + seedX] || 0;
        // A lasso says "these things, together" — honour that and leave the group alone. A tap
        // says "this one". An AUTO crop says neither: nobody aimed it, and a picture with three
        // things on a table has three answers, not one. It used to find all three and then throw
        // two away, because the seed at the middle of the picture could only ever name one — the
        // islands were separated correctly and the separation was discarded, which is why it
        // looked as though nothing had been identified at all. Auto crop now keeps every island
        // and they are parted into their own layers once the cut-out exists (see splitCutout).
        const keep = (lassoMask || cropOptsRef.current?.fromAuto) ? 0 : (atPoint || biggest);
        // Specks are not objects. Below a thousandth of the picture, keeping only the island
        // under the finger would hand back a dust mote, so the whole foreground stays instead.
        const floor = Math.max(24, Math.round(sw * sh * 0.001));
        if (keep && biggestN >= floor) {
          for (let i = 0; i < sw * sh; i++) if (label[i] !== keep) finalFg[i] = 0;
        }
        // The checks that decide whether auto crop found anything worth having were written for
        // ONE object and tuned on one. Measured across a group they say the wrong thing — three
        // objects spread over a table fill their shared bounding box poorly, and a detection that
        // is perfectly good gets refused for being "a scatter". So they keep being answered about
        // the biggest single island, exactly as before, while the mask carries the rest.
        if (cropOptsRef.current?.fromAuto && biggest) {
          let bn = 0, bx0 = sw, by0 = sh, bx1 = -1, by1 = -1;
          for (let i = 0; i < sw * sh; i++) {
            if (label[i] !== biggest) continue;
            const x = i % sw, y = (i - x) / sw;
            bn++;
            if (x < bx0) bx0 = x; if (x > bx1) bx1 = x;
            if (y < by0) by0 = y; if (y > by1) by1 = y;
          }
          _autoLeadIsland = bx1 >= bx0
            ? { fgFrac: bn / (sw * sh), boxFill: bn / ((bx1 - bx0 + 1) * (by1 - by0 + 1)) }
            : null;
        }
      }

      // ── No smoothing pass here, deliberately ────────────────────────────────────────────
      // There was one, briefly: grow-then-shrink to fill thin notches, shrink-then-grow to trim
      // thin fingers. On paper it is the right treatment for a ragged edge. On a real face it
      // squared the outline off into blocks — because the notches and fingers in a bad mask are
      // not thin, they are large patches of wall the colour test genuinely believes in, and an
      // operator big enough to remove them is big enough to flatten a chin.
      //
      // The edge cannot be repaired after the fact. Either the mask is right, which on a
      // photograph means the matting model produced it, or it is wrong and should be refused —
      // which is what the check further down does. Please do not add a morphology pass here
      // without a photograph to prove it on.

      // ── Fill what the object encloses ───────────────────────────────────────────────────
      // A silhouette is the whole of a thing, not the parts of it that happened to contrast
      // with what is behind it. On a pale object with darker markings — a white-chocolate cake
      // topper with piped line art on it, say — the colour test keeps the lines and drops the
      // pale body between them, because the body really is close to the backdrop's colour. The
      // cut-out that comes back is a piece of lace: the drawing survives, the object it was
      // drawn on does not. That is the "the colours are gone" case.
      //
      // Enclosed means enclosed. Anything the outside cannot reach without crossing the object
      // is inside the object, whatever colour it is. So the outside is flooded from the border
      // and whatever it fails to reach is filled in. This can only ADD pixels, and only ones
      // the object already surrounds — it cannot move an outer edge, round a corner, or take
      // anything away, which is what makes it safe in a way the smoothing pass was not.
      // The flood runs on a grid one pixel LARGER than the region on every side. That ring is
      // always background, so the outside always has somewhere to start from.
      //
      // Seeding on the region's own border instead — which is what this did at first — fails
      // the moment the object touches that border, because then there is no background pixel on
      // the edge to start from, the flood reaches nothing, and "everything the flood did not
      // reach" means everything. The mask became the whole box and Smart Crop turned into a
      // rectangle crop with the object sitting in a field of backdrop. One pixel of guaranteed
      // margin is the whole fix.
      {
        const bx = clampMinX - 1, by = clampMinY - 1;
        const W = (clampMaxX - clampMinX + 1) + 2;
        const H = (clampMaxY - clampMinY + 1) + 2;
        const seen = new Uint8Array(W * H);
        const stack = new Int32Array(W * H);
        let top = 0;
        const solid = (lx, ly) => {
          const x = bx + lx, y = by + ly;
          if (x < clampMinX || x > clampMaxX || y < clampMinY || y > clampMaxY) return false;  // the ring
          return finalFg[y * sw + x] === 1;
        };
        const push = (lx, ly) => {
          if (lx < 0 || lx >= W || ly < 0 || ly >= H) return;
          const k = ly * W + lx;
          if (seen[k] || solid(lx, ly)) return;
          seen[k] = 1;
          stack[top++] = k;
        };
        push(0, 0);
        while (top > 0) {
          const k = stack[--top];
          const lx = k % W, ly = (k - lx) / W;
          push(lx - 1, ly); push(lx + 1, ly); push(lx, ly - 1); push(lx, ly + 1);
        }
        const add = [];
        for (let ly = 1; ly < H - 1; ly++) {
          for (let lx = 1; lx < W - 1; lx++) {
            if (seen[ly * W + lx]) continue;                       // the outside reached it
            const x = bx + lx, y = by + ly, i = y * sw + x;
            if (finalFg[i]) continue;                              // already the object
            if (lassoMask && !lassoMask[i]) continue;
            add.push(i);
          }
        }
        // No cap on how much this may fill, deliberately. There was one — skip the fill if the
        // result would cover more than 85% of the region — and it did the opposite of its job:
        // an object that fills its own crop box, which is what a tapped object USUALLY is, went
        // over that line and had its interior left hollow. It was suppressing the fill in
        // precisely the case the fill exists for.
        //
        // The runaway it was meant to catch had a cause, and the cause is fixed above: the
        // flood had nowhere to start when the object touched the region's edge. A guess laid on
        // top of a fixed bug is just a second bug with better intentions.
        if (add.length) {
          for (const i of add) finalFg[i] = 1;
          console.warn(`Smart crop: filled ${add.length} pixels the object encloses.`);
        }
      }

      // Rewrite the mask pixels and the FG extent from the final mask.
      fgMinX = sw; fgMaxX = 0; fgMinY = sh; fgMaxY = 0;
      let fgCount = 0;
      for (let i = 0; i < sw * sh; i++) {
        const on = finalFg[i] ? 255 : 0;
        if (on) fgCount++;
        mkData.data[i * 4] = mkData.data[i * 4 + 1] = mkData.data[i * 4 + 2] = on;
        mkData.data[i * 4 + 3] = on;
        if (on) {
          const fx = i % sw, fy = (i / sw) | 0;
          if (fx < fgMinX) fgMinX = fx; if (fx > fgMaxX) fgMaxX = fx;
          if (fy < fgMinY) fgMinY = fy; if (fy > fgMaxY) fgMaxY = fy;
        }
      }
      const fgAt = (x, y) => finalFg[y * sw + x] === 1;

      mkCtx.putImageData(mkData, 0, 0);
      /**
       * The colour to put back where the object was, so lifting it out MENDS the picture rather
       * than punching a hole in it.
       *
       * Two ways of arriving at it, and the second is why this is not conditional any more. If
       * the backdrop is genuinely one colour, that colour is the answer and it is exact. If it
       * is not — a photographed scene, a gradient — the honest local answer is the colour of the
       * ring of pixels immediately AROUND the object, which is what would have been visible had
       * the object never been there. It will not reconstruct detail, but it is the surrounding
       * colour, which is what was asked for and is always better than a hole in the shape of the
       * thing you removed.
       */
      const surroundingColour = () => {
        if (hasAlphaPath) return null;                 // already transparent: nothing behind it
        if (bgFlatness >= 0.8) return polyBgColor;     // one flat colour, and we know it
        // Walk only the band just outside the object, inside its own bounding box grown a little.
        let bx0 = sw, by0 = sh, bx1 = -1, by1 = -1;
        for (let i = 0; i < sw * sh; i++) {
          if (!finalFg[i]) continue;
          const fx = i % sw, fy = (i - fx) / sw;
          if (fx < bx0) bx0 = fx; if (fx > bx1) bx1 = fx;
          if (fy < by0) by0 = fy; if (fy > by1) by1 = fy;
        }
        if (bx1 < bx0) return null;
        const RING = 4;
        const x0 = Math.max(0, bx0 - RING), x1 = Math.min(sw - 1, bx1 + RING);
        const y0 = Math.max(0, by0 - RING), y1 = Math.min(sh - 1, by1 + RING);
        let n = 0, r = 0, g = 0, b = 0;
        for (let y = y0; y <= y1; y++) {
          for (let x = x0; x <= x1; x++) {
            const i = y * sw + x;
            if (finalFg[i]) continue;                  // that is the object, not its surroundings
            let touching = false;
            for (let dy = -RING; dy <= RING && !touching; dy++) {
              const yy = y + dy;
              if (yy < 0 || yy >= sh) continue;
              for (let dx = -RING; dx <= RING; dx++) {
                const xx = x + dx;
                if (xx < 0 || xx >= sw) continue;
                if (finalFg[yy * sw + xx]) { touching = true; break; }
              }
            }
            if (!touching) continue;
            const p2 = i * 4;
            r += pd[p2]; g += pd[p2 + 1]; b += pd[p2 + 2]; n++;
          }
        }
        return n ? [r / n, g / n, b / n] : null;
      };
      const _bg = surroundingColour();
      const bgColor = _bg
        ? `rgb(${Math.round(_bg[0])}, ${Math.round(_bg[1])}, ${Math.round(_bg[2])})`
        : null;
      smartCropMaskRef.current = {
        maskCanvas, imgLeft, imgTop, rendW, rendH, targetImgId: targetImg.id, bgColor,
        // What fraction of the picture the thing we found actually covers, and how much of its
        // own bounding box it fills. Auto Crop, which nobody aimed, uses these to tell an object
        // from a patch of wall that happened to be a slightly different colour.
        // Two questions, and they are not asked of the same thing when auto crop has kept a
        // group. "Did it find enough to be a subject at all?" is about everything it found —
        // three objects are collectively the subject even though each alone is a small part of
        // the picture. "Is what it found shaped like an object, or is it a scatter?" is about
        // one object, because a group is SUPPOSED to fill its shared box poorly.
        fgFrac: fgCount / (sw * sh),
        // The biggest single object as a share of the picture. One connected blob covering
        // almost all of it is not a subject, it is a detector that failed to find an edge —
        // and the group figure above cannot see that, because a speck alongside it drags the
        // total down just enough to look reasonable.
        leadFrac: _autoLeadIsland ? _autoLeadIsland.fgFrac : null,
        // Which method actually answered, and whether the picture is one the local one can do.
        aiUsed: !!aiFg,
        // …and, when it did not, why. Auto crop's "the cut-out service did not answer" used to
        // stop there, so a spent quota, an unreachable server and a key the deployment cannot
        // see all read as the same shrug. The reason is already known here; carry it.
        aiWhy: _aiCutFailed || null,
        bgFlat: bgFlatness,
        boxFill: _autoLeadIsland ? _autoLeadIsland.boxFill
          : (fgMaxX > fgMinX && fgMaxY > fgMinY)
            ? fgCount / ((fgMaxX - fgMinX + 1) * (fgMaxY - fgMinY + 1)) : 0,
        boxFrac: (fgMaxX > fgMinX && fgMaxY > fgMinY)
          ? ((fgMaxX - fgMinX + 1) * (fgMaxY - fgMinY + 1)) / (sw * sh) : 0,
        // Was this a cut-out to begin with? It changes what "the object fills the frame" MEANS:
        // on a cut-out it means there is nothing left to trim, and on a photograph it means the
        // detector failed to find anything. Same number, opposite conclusions.
        hadAlpha: hasAlphaPath,
        // A cut-out: how many separate objects the AI found in it (0 = not asked, or only one).
        objectsFound: cutoutObjects,
      };
      // Expand bounds to true FG pixel extent (includes braid/elbow missed by gutter scan)
      if (fgMaxX > fgMinX && fgMaxY > fgMinY) {
        minX = fgMinX; maxX = fgMaxX; minY = fgMinY; maxY = fgMaxY;
      }

      // Scanline polygon for the neon preview outline (approximate — exact crop uses the mask
      // above). Traced from the FINAL mask so the outline shows what will actually be cut;
      // it used to trace the raw colour test, which is why the preview could look nothing
      // like the result.
      const PSTEP = Math.max(1, Math.round((maxY - minY) / 120));
      const leftEdgePts = [], rightEdgePts = [];
      for (let y = minY; y <= maxY; y += PSTEP) {
        let lx = -1, rx = -1;
        for (let x = minX; x <= maxX; x++) if (fgAt(x, y)) { lx = x; break; }
        for (let x = maxX; x >= minX; x--) if (fgAt(x, y)) { rx = x; break; }
        if (lx >= 0 && rx >= 0 && rx >= lx) { leftEdgePts.push({ x: lx, y }); rightEdgePts.push({ x: rx, y }); }
      }
      const polyImgPts = [...rightEdgePts, ...leftEdgePts.slice().reverse()];
      const canvasPoly = polyImgPts.map(pt => ({
        x: imgLeft + Math.max(0, Math.min(sw, pt.x)) / sw * rendW,
        y: imgTop  + Math.max(0, Math.min(sh, pt.y)) / sh * rendH,
      }));
      // ─────────────────────────────────────────────────────────────────────

      // Bounding box for crop coords. A cut-out gets none: its edge is already the object's
      // edge, and padding it only adds a rim of nothing. Everything else gets the margin the
      // caller asked for — a hand-aimed Smart Crop hugs the edge at 1%, Auto Crop leaves a
      // real border, because a subject cut flush to its own outline looks like a mistake.
      const _padFrac = cropOptsRef.current?.padFrac ?? 0.01;
      const padX = hasAlphaPath ? 0 : Math.max(2, (maxX - minX) * _padFrac);
      const padY = hasAlphaPath ? 0 : Math.max(2, (maxY - minY) * _padFrac);
      const startX = Math.max(0, imgLeft + ((minX - padX) / sw) * rendW);
      const startY = Math.max(0, imgTop  + ((minY - padY) / sh) * rendH);
      const endX   = Math.min(rect.width,  imgLeft + ((maxX + padX) / sw) * rendW);
      const endY   = Math.min(rect.height, imgTop  + ((maxY + padY) / sh) * rendH);
      // ── Refuse a result that is plainly rubbish ─────────────────────────────────────────
      // A tap says "the thing under my finger", and on a photograph where the subject and the
      // backdrop are close in tone the colour test cannot honour that: what comes back is a
      // scatter of same-coloured patches across the picture — some forehead, some cheek, some
      // wall — and cutting it out produces a mangled thing with holes and wedges of doorframe
      // in it. That is worse than nothing, because it is offered as if it worked.
      //
      // A real object fills a good share of its own bounding box. A scatter does not. Below a
      // third, this says so and points at Freeform, where a finger draws the edge and no
      // guessing is involved. A lasso or a drawn box is the person telling us where to look, so
      // those are trusted and never refused here.
      // Measured on the biggest single object when auto crop kept a whole group — three things
      // spread across a table fill their shared box poorly however good each one is, and asking
      // this question of the group turned away detections that were entirely correct.
      const _boxFill = _autoLeadIsland ? _autoLeadIsland.boxFill
        : (fgMaxX > fgMinX && fgMaxY > fgMinY)
          ? fgCount / ((fgMaxX - fgMinX + 1) * (fgMaxY - fgMinY + 1)) : 0;
      const _ragged = !lassoPath && !drawnBox && _boxFill < 0.34;
      if (_ragged) {
        console.warn(`Smart crop: the detected area fills only ${Math.round(_boxFill * 100)}% of its own box — refusing it.`);
        flash(_aiCutFailed
          ? 'The cut-out service did not answer — try Freeform crop'
          : 'Could not separate that cleanly — try Freeform crop');
      }
      // ── When it found nothing to tighten, say so ─────────────────────────────────────
      // Handing back the drawn shape is not wrong — it is the best answer available, and the
      // one a finger meant. Handing it back SILENTLY, under the name "smart", is: the result
      // is then identical to Freeform and there is nothing to tell you the clever part found
      // no edge to improve on. Said once, briefly, and only when the shape really did come
      // back untouched.
      if (!_ragged && _aiFilledLasso) {
        console.warn('Smart crop: the cut-out kept the whole lasso — no edge found inside it.');
        flash('No clear edge inside that — kept the shape you drew');
      }
      const _ok = canvasPoly.length > 4 && !_ragged;
      smartDetectedRef.current = _ok;
      setSmartDetected(_ok);
      setSelectionPath(_ok ? canvasPoly : []);
      setSelectionStart({ x: startX, y: startY });
      setSelectionEnd  ({ x: endX,   y: endY   });
      setIsSelecting(true);
    } catch (err) {
      console.error('Smart crop detection failed:', err);
      smartDetectedRef.current = false;
      setSmartDetected(false);
    } finally {
      setSmartCropRunning(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  // Pre-load stamp images referenced in shapes (for canvas export)
  const preloadStampShapes = async (shapes) => {
    const urls = [...new Set(shapes.filter(s => s.type === 'emoji' && s.emoji && s.emoji.startsWith('http')).map(s => s.emoji))];
    await Promise.all(urls.map(url => new Promise(resolve => {
      if (stampImageCacheRef.current.has(url)) return resolve();
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { stampImageCacheRef.current.set(url, img); resolve(); };
      img.onerror = resolve;
      img.src = url;
    })));
  };

  // Call backend -> Kling AI to remove background from a canvas element
  // Returns a data URL (PNG with transparent bg) or null on failure
  // ── Background removal ────────────────────────────────────────────────────────
  // Gemini's image model cannot emit alpha — asked for "a transparent background" it hands
  // back an opaque picture (black, white or checkerboard where the transparency should be),
  // which is how a stamp came out as a black sheet. So the AI is asked for a MASK (subject
  // white, rest black) and that mask becomes the alpha of the caller's OWN pixels: nothing in
  // the image is re-rendered, and the result is exactly the input's size. If an older edge
  // function still answers with a rendered cut-out on a flat colour, that colour is keyed out.
  const canvasHasTransparency = (canvas, minFrac = 0.02) => {
    try {
      const d = canvas.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, canvas.width, canvas.height).data;
      let clear = 0;
      for (let i = 3; i < d.length; i += 4) if (d[i] < 16) clear++;
      return clear / (d.length / 4) >= minFrac;
    } catch { return false; }
  };
  const trimCanvasToContent = (canvas, pad = 0) => {
    try {
      const W = canvas.width, H = canvas.height;
      const d = canvas.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, W, H).data;
      let x0 = W, y0 = H, x1 = -1, y1 = -1;
      for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
        if (d[(y * W + x) * 4 + 3] > 8) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
      }
      if (x1 < x0 || y1 < y0) return null;
      x0 = Math.max(0, x0 - pad); y0 = Math.max(0, y0 - pad); x1 = Math.min(W - 1, x1 + pad); y1 = Math.min(H - 1, y1 + pad);
      const out = document.createElement('canvas');
      out.width = x1 - x0 + 1; out.height = y1 - y0 + 1;
      out.getContext('2d').drawImage(canvas, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
      return out;
    } catch { return null; }
  };

  const removeImageBg = async (canvas, opts) => {
    const W = canvas.width, H = canvas.height;
    if (!W || !H) throw new Error('Empty image');
    // Send a working-size copy on a neutral ground (transparent areas must read as "background").
    const MAX = 1024;
    const scale = Math.min(1, MAX / Math.max(W, H));
    const sendW = Math.max(1, Math.round(W * scale)), sendH = Math.max(1, Math.round(H * scale));
    // Inside a frame, like every other AI request (see frameForAi): the painted-mask route is an
    // image model and answers in its own frame, and a mask stretched back over the picture cut
    // a little off one side of the object and left a sliver of background on the other.
    const fr = frameForAi(canvas, sendW, sendH, { fill: '#c8c8c8' });
    const aiUrl = await aiRemoveBackground(fr.dataUrl, opts);
    return applyAiCutout(canvas, aiUrl, fr.frame);
  };

  // Turn the AI's answer into an alpha channel and apply it to `canvas`'s pixels.
  const applyAiCutout = async (canvas, aiUrl, frame = null) => {
    const W = canvas.width, H = canvas.height;
    const aiImg = await new Promise((res, rej) => {
      const i = new Image(); i.onload = () => res(i); i.onerror = () => rej(new Error('AI result could not be read')); i.src = aiUrl;
    });
    const aW = aiImg.naturalWidth, aH = aiImg.naturalHeight;
    const ac = document.createElement('canvas'); ac.width = aW; ac.height = aH;
    const actx = ac.getContext('2d', { willReadFrequently: true });
    actx.drawImage(aiImg, 0, 0);
    const ad = actx.getImageData(0, 0, aW, aH).data;
    const n = aW * aH;
    const alpha = new Float32Array(n);

    let hasAlpha = false;
    for (let i = 3; i < ad.length; i += 4) if (ad[i] < 128) { hasAlpha = true; break; }
    // Does the answer look like a mask? A real segmentation mask (the shape studio-ai's
    // 'remove-bg' action asks for) is grayscale essentially everywhere -- black background,
    // white subject, and a thin anti-aliased grey edge in between where the prompt explicitly
    // allows it. So the right test is LOW SATURATION across the image, not "near-pure black
    // or white": requiring near-pure b/w used to mistake a mask with a richly detailed edge
    // (hair, dust, motion blur -- all still grayscale, just not binary) for a colourful flat-
    // background cut-out, routing it into the 'key' branch below, which then failed outright
    // whenever the subject touched the image border. A genuine cut-out or natural photo, by
    // contrast, carries real colour across most of its area, so this still tells them apart.
    let lowSat = 0;
    for (let i = 0; i < n; i++) {
      const r = ad[i * 4], g = ad[i * 4 + 1], b = ad[i * 4 + 2];
      const sat = Math.max(r, g, b) - Math.min(r, g, b);
      if (sat <= 48) lowSat++;
    }
    let mode;
    if (hasAlpha) {
      // 1) The answer already carries real transparency — use it as it is.
      mode = 'alpha';
      for (let i = 0; i < n; i++) alpha[i] = ad[i * 4 + 3] / 255;
    } else if (lowSat / n >= 0.9) {
      // 2) A mask: white = subject, black = background (soft grey edge in between).
      mode = 'mask';
      for (let i = 0; i < n; i++) {
        const lum = (ad[i * 4] * 299 + ad[i * 4 + 1] * 587 + ad[i * 4 + 2] * 114) / 1000;
        alpha[i] = Math.min(1, Math.max(0, (lum - 64) / 128));
      }
    } else {
      // 3) A rendered cut-out on a flat colour: key out that colour, growing in from the border.
      mode = 'key';
      const border = [];
      for (let x = 0; x < aW; x++) { border.push(x, (aH - 1) * aW + x); }
      for (let y = 0; y < aH; y++) { border.push(y * aW, y * aW + aW - 1); }
      const ch = (k) => { const v = border.map(i => ad[i * 4 + k]).sort((p, q) => p - q); return v[v.length >> 1]; };
      const br = ch(0), bg = ch(1), bb = ch(2);
      const dist = (i) => Math.abs(ad[i * 4] - br) + Math.abs(ad[i * 4 + 1] - bg) + Math.abs(ad[i * 4 + 2] - bb);
      const uniform = border.filter(i => dist(i) <= 60).length / border.length;
      if (uniform < 0.6) throw new Error('The AI did not return a usable cut-out — please try again.');
      const TOL_IN = 45, TOL_OUT = 110;             // fully background … fully subject (sum of |dr|+|dg|+|db|)
      const seen = new Uint8Array(n);
      const stack = border.slice();
      for (let i = 0; i < n; i++) alpha[i] = 1;
      while (stack.length) {
        const i = stack.pop();
        if (seen[i]) continue;
        const d = dist(i);
        if (d > TOL_OUT) continue;
        seen[i] = 1;
        alpha[i] = Math.min(1, Math.max(0, (d - TOL_IN) / (TOL_OUT - TOL_IN)));
        const x = i % aW, y = (i - x) / aW;
        if (x > 0) stack.push(i - 1);
        if (x < aW - 1) stack.push(i + 1);
        if (y > 0) stack.push(i - aW);
        if (y < aH - 1) stack.push(i + aW);
      }
    }
    // Sanity: a subject that covers almost nothing or almost everything is a failed answer.
    let cover = 0; for (let i = 0; i < n; i++) cover += alpha[i];
    cover /= n;
    if (cover < 0.005 || cover > 0.995) throw new Error('The AI could not separate the subject — please try again.');

    // Scale the alpha map to the caller's size and apply it to the caller's pixels.
    const mc = document.createElement('canvas'); mc.width = aW; mc.height = aH;
    const mImg = actx.createImageData(aW, aH);
    for (let i = 0; i < n; i++) { const v = Math.round(alpha[i] * 255); mImg.data[i * 4] = v; mImg.data[i * 4 + 1] = v; mImg.data[i * 4 + 2] = v; mImg.data[i * 4 + 3] = 255; }
    mc.getContext('2d').putImageData(mImg, 0, 0);
    const out = document.createElement('canvas'); out.width = W; out.height = H;
    const octx = out.getContext('2d', { willReadFrequently: true });
    octx.imageSmoothingEnabled = true; octx.imageSmoothingQuality = 'high';
    // Framed: the picture's own rectangle, taken out of the answer at one uniform scale.
    if (frame) drawFramed(octx, mc, aW, aH, frameRectIn(frame, aW, aH), W, H);
    else octx.drawImage(mc, 0, 0, W, H);
    const md = octx.getImageData(0, 0, W, H).data;
    octx.clearRect(0, 0, W, H);
    octx.drawImage(canvas, 0, 0);
    const od = octx.getImageData(0, 0, W, H);
    for (let i = 0, N = W * H; i < N; i++) od.data[i * 4 + 3] = Math.round(od.data[i * 4 + 3] * md[i * 4] / 255);
    octx.putImageData(od, 0, 0);
    console.debug(`[removeBg] ${mode} mask ${aW}x${aH} → ${W}x${H}, subject ${(cover * 100).toFixed(1)}%`);
    return out.toDataURL('image/png');
  };

  // Upload current canvas as a shared stamp
  const _doSaveStamp = async (isHologram = false) => {
    if (drawingPaths.length === 0 && canvasShapes.length === 0 && canvasImages.length === 0) {
      showAlert('Add something to the canvas first, then save it as a stamp.');
      return;
    }
    setStampUploading(true);
    try {
      const cEl = canvasRef.current;
      if (!cEl) { setStampUploading(false); return; }
      const cW = cEl.offsetWidth;
      const cH = cEl.offsetHeight;

      // --- Step 1: composite all visible layers onto a transparent offscreen canvas ---
      const fullCanvas = document.createElement('canvas');
      fullCanvas.width  = cW;
      fullCanvas.height = cH;
      const fullCtx = fullCanvas.getContext('2d', { willReadFrequently: true });

      // Draw canvas images (bottom layer) -- use loadImgSafe to avoid canvas taint
      for (const img of canvasImages) {
        const el = await loadImgSafe(img.url);
        if (el) {
          const left = (img.x / 100) * cW;
          const top  = (img.y / 100) * cH;
          const wPx  = (img.width  / 21)   * cW;
          const hPx  = (img.height / 29.7) * cH;
          const cx   = left + wPx / 2;
          const cy   = top  + hPx / 2;
          fullCtx.save();
          fullCtx.translate(cx, cy);
          fullCtx.rotate(((img.rotation || 0) * Math.PI) / 180);
          if (img.cropShape === 'circle') {
            fullCtx.beginPath();
            fullCtx.ellipse(0, 0, wPx / 2, hPx / 2, 0, 0, Math.PI * 2);
            fullCtx.clip();
          }
          fullCtx.drawImage(el, -wPx / 2, -hPx / 2, wPx, hPx);
          fullCtx.restore();
        }
      }

      // Draw hand-drawn strokes on top (live drawing canvas, same-origin, no CORS issues)
      const drawingEl = drawingCanvasRef.current;
      if (drawingEl && drawingEl.width > 0 && drawingEl.height > 0) {
        fullCtx.drawImage(drawingEl, 0, 0, cW, cH);
      }

      // Draw shapes on top (text, lines, etc. -- skip emoji stamps which live in their own HTML layer)
      await preloadStampShapes(canvasShapes);
      drawShapesOnCanvas(fullCtx, canvasShapes, cW, cH);

      // --- Step 2: find bounding box of non-transparent pixels ---
      // If scan fails (all transparent due to canvas security), fall back to full canvas
      let minX = 0, minY = 0, maxX = cW, maxY = cH;
      try {
        const imgData = fullCtx.getImageData(0, 0, cW, cH);
        const px = imgData.data;
        let bMinX = cW, bMinY = cH, bMaxX = 0, bMaxY = 0;
        for (let y = 0; y < cH; y++) {
          for (let x = 0; x < cW; x++) {
            if (px[(y * cW + x) * 4 + 3] > 8) {
              if (x < bMinX) bMinX = x;
              if (x > bMaxX) bMaxX = x;
              if (y < bMinY) bMinY = y;
              if (y > bMaxY) bMaxY = y;
            }
          }
        }
        if (bMaxX > bMinX && bMaxY > bMinY) {
          // Use detected bounds with padding
          const PAD = 10;
          minX = Math.max(0, bMinX - PAD);
          minY = Math.max(0, bMinY - PAD);
          maxX = Math.min(cW - 1, bMaxX + PAD);
          maxY = Math.min(cH - 1, bMaxY + PAD);
        }
      } catch (_) { /* cross-origin / security error -- use full canvas */ }
      const contentW = maxX - minX;
      const contentH = maxY - minY;

      // --- Step 3: fit the content into a square stamp, up to 1024 px (never upscaled) ---
      const TARGET = Math.min(1024, Math.max(295, Math.max(contentW, contentH)));
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width  = TARGET;
      finalCanvas.height = TARGET;
      const finalCtx = finalCanvas.getContext('2d');
      // Transparent background
      const fitScale = Math.min(TARGET / contentW, TARGET / contentH);
      const drawW = contentW * fitScale;
      const drawH = contentH * fitScale;
      const offsetX = (TARGET - drawW) / 2;
      const offsetY = (TARGET - drawH) / 2;
      finalCtx.drawImage(fullCanvas, minX, minY, contentW, contentH, offsetX, offsetY, drawW, drawH);

      // --- Step 4: upload ---
      const blob = await new Promise(resolve => finalCanvas.toBlob(resolve, 'image/png'));
      const auth = getAuth();
      const uid = auth.currentUser?.uid || 'anon';
      const { storage, db } = getFirebase();
      const sRef = stampStorageRef(storage, `studioStamps/${uid}_${Date.now()}.png`);
      await uploadBytes(sRef, blob);
      const downloadUrl = await getDownloadURL(sRef);
      const docRef = await addDoc(collection(db, 'studioStamps'), {
        url: downloadUrl,
        path: sRef.fullPath,          // the stored file — the url is a session-only object URL
        createdBy: uid,
        createdAt: serverTimestamp(),
        ...(isHologram ? { isHologram: true } : {}),
      });
      // Immediately add to local state so it appears without waiting for Firestore push
      const newStamp = { id: docRef.id, url: downloadUrl, createdBy: uid, ...(isHologram ? { isHologram: true } : {}) };
      const newImg = new Image();
      newImg.crossOrigin = 'anonymous';
      newImg.onload = () => stampImageCacheRef.current.set(downloadUrl, newImg);
      newImg.src = downloadUrl;
      setStudioStamps(prev => [...prev, newStamp]);
      setSelectedEmoji(downloadUrl);
    } catch (err) {
      console.error('Stamp upload failed:', err);
      showAlert('Failed to save stamp: ' + (err?.message || String(err)));
    } finally {
      setStampUploading(false);
    }
  };
  const handleUploadStamp = () => _doSaveStamp(false);
  const handleSaveHologramStamp = () => _doSaveStamp(true);

  // Same as handleUploadStamp but runs background removal via Kling AI before saving
  const handleUploadStampRemoveBg = async () => {
    if (drawingPaths.length === 0 && canvasShapes.length === 0 && canvasImages.length === 0) {
      showAlert('Add something to the canvas first.');
      return;
    }
    setStampRemoving(true);
    try {
      const cEl = canvasRef.current;
      if (!cEl) { setStampRemoving(false); return; }
      const cW = cEl.offsetWidth;
      const cH = cEl.offsetHeight;

      // Source for the stamp: the selected image alone, at its own resolution — otherwise the
      // whole design composited at screen size and trimmed to its content, so the AI sees the
      // design and not an empty sheet.
      const selImg = selectedImage ? canvasImages.find(i => i.id === selectedImage) : null;
      let srcCanvas = null;
      if (selImg) srcCanvas = await _imgToCanvas(selImg.url);

      // Build composite canvas (same logic as handleUploadStamp)
      const fullCanvas = document.createElement('canvas');
      fullCanvas.width  = cW;
      fullCanvas.height = cH;
      const fullCtx = fullCanvas.getContext('2d', { willReadFrequently: true });

      for (const img of (srcCanvas ? [] : canvasImages)) {
        const el = await loadImgSafe(img.url);
        if (el) {
          const left = (img.x / 100) * cW;
          const top  = (img.y / 100) * cH;
          const wPx  = (img.width  / 21)   * cW;
          const hPx  = (img.height / 29.7) * cH;
          const cx   = left + wPx / 2;
          const cy   = top  + hPx / 2;
          fullCtx.save();
          fullCtx.translate(cx, cy);
          fullCtx.rotate(((img.rotation || 0) * Math.PI) / 180);
          if (img.cropShape === 'circle') {
            fullCtx.beginPath();
            fullCtx.ellipse(0, 0, wPx / 2, hPx / 2, 0, 0, Math.PI * 2);
            fullCtx.clip();
          }
          fullCtx.drawImage(el, -wPx / 2, -hPx / 2, wPx, hPx);
          fullCtx.restore();
        }
      }
      if (!srcCanvas) {
        const drawingEl = drawingCanvasRef.current;
        if (drawingEl && drawingEl.width > 0) fullCtx.drawImage(drawingEl, 0, 0, cW, cH);
        await preloadStampShapes(canvasShapes);
        drawShapesOnCanvas(fullCtx, canvasShapes, cW, cH);
      }

      if (!srcCanvas) srcCanvas = trimCanvasToContent(fullCanvas, 6) || fullCanvas;

      // Already transparent (background removed earlier)? Those pixels are exact — keep them.
      const bgRemovedDataUrl = canvasHasTransparency(srcCanvas)
        ? srcCanvas.toDataURL('image/png')
        : await removeImageBg(srcCanvas);

      // Load result and crop to content
      const resultImg = await new Promise((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = reject;
        el.src = bgRemovedDataUrl;
      });
      const resultCanvas = document.createElement('canvas');
      resultCanvas.width  = resultImg.naturalWidth  || cW;
      resultCanvas.height = resultImg.naturalHeight || cH;
      const rCtx = resultCanvas.getContext('2d', { willReadFrequently: true });
      rCtx.drawImage(resultImg, 0, 0);

      // Bounding box crop
      const rW = resultCanvas.width, rH = resultCanvas.height;
      let minX = 0, minY = 0, maxX = rW, maxY = rH;
      try {
        const px = rCtx.getImageData(0, 0, rW, rH).data;
        let bMinX = rW, bMinY = rH, bMaxX = 0, bMaxY = 0;
        for (let y = 0; y < rH; y++) for (let x = 0; x < rW; x++) {
          if (px[(y * rW + x) * 4 + 3] > 8) {
            if (x < bMinX) bMinX = x; if (x > bMaxX) bMaxX = x;
            if (y < bMinY) bMinY = y; if (y > bMaxY) bMaxY = y;
          }
        }
        if (bMaxX > bMinX && bMaxY > bMinY) {
          const PAD = 6;
          minX = Math.max(0, bMinX - PAD); minY = Math.max(0, bMinY - PAD);
          maxX = Math.min(rW - 1, bMaxX + PAD); maxY = Math.min(rH - 1, bMaxY + PAD);
        }
      } catch (_) {}
      const contentW = maxX - minX, contentH = maxY - minY;
      const TARGET = Math.min(1024, Math.max(295, Math.max(contentW, contentH)));
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = TARGET; finalCanvas.height = TARGET;
      const finalCtx = finalCanvas.getContext('2d');
      const fitScale = Math.min(TARGET / contentW, TARGET / contentH);
      const drawW = contentW * fitScale, drawH = contentH * fitScale;
      finalCtx.drawImage(resultCanvas, minX, minY, contentW, contentH,
        (TARGET - drawW) / 2, (TARGET - drawH) / 2, drawW, drawH);

      const blob = await new Promise(resolve => finalCanvas.toBlob(resolve, 'image/png'));
      const auth = getAuth();
      const uid  = auth.currentUser?.uid || 'anon';
      const { storage, db } = getFirebase();
      const sRef = stampStorageRef(storage, `studioStamps/${uid}_${Date.now()}.png`);
      await uploadBytes(sRef, blob);
      const downloadUrl = await getDownloadURL(sRef);
      const docRef = await addDoc(collection(db, 'studioStamps'), {
        url: downloadUrl, path: sRef.fullPath, createdBy: uid, createdAt: serverTimestamp(),
      });
      const newStamp = { id: docRef.id, url: downloadUrl, createdBy: uid };
      const newImg2 = new Image();
      newImg2.crossOrigin = 'anonymous';
      newImg2.onload = () => stampImageCacheRef.current.set(downloadUrl, newImg2);
      newImg2.src = downloadUrl;
      setStudioStamps(prev => [...prev, newStamp]);
      setSelectedEmoji(downloadUrl);
    } catch (err) {
      console.error('Remove BG stamp failed:', err);
      showAlert('Could not make the stamp: ' + (err?.message || 'background removal failed') + ' Select the image and try again.');
    } finally {
      setStampRemoving(false);
    }
  };

  const handleDeleteStamps = async () => {
    if (selectedStampIds.size === 0) return;
    setStampUploading(true);
    try {
      const { db, storage } = getFirebase();
      const stampsToDelete = studioStamps.filter(s => selectedStampIds.has(s.id));
      // Delete Firestore docs and Storage files in parallel
      await Promise.all([
        ...stampsToDelete.map(s => deleteDoc(doc(db, 'studioStamps', s.id))),
        ...stampsToDelete.map(s => {
          try {
            const fileRef = stampStorageRef(storage, s.url);
            return deleteObject(fileRef).catch(() => {}); // ignore if already gone
          } catch { return Promise.resolve(); }
        }),
      ]);
      const deletedUrls = new Set(stampsToDelete.map(s => s.url));
      if (deletedUrls.has(selectedEmoji)) setSelectedEmoji(null);
      setStudioStamps(prev => prev.filter(s => !selectedStampIds.has(s.id)));
      // Also remove any canvas shapes that reference the deleted stamp URLs
      setCanvasShapes(prev => prev.filter(s => !(s.type === 'emoji' && deletedUrls.has(s.emoji))));
      setSelectedStampIds(new Set());
    } catch (err) {
      console.error('Delete stamp failed:', err);
      showAlert('Failed to delete stamp(s).');
    } finally {
      setStampUploading(false);
    }
  };

  // Mark exactly one selected stamp as the hologram; clear hologram flag on any previous one
  const handleMarkAsHologram = async () => {
    if (selectedStampIds.size !== 1) return;
    const [targetId] = [...selectedStampIds];
    setStampUploading(true);
    try {
      const { db } = getFirebase();
      const prevHologram = studioStamps.filter(s => s.isHologram && s.id !== targetId);
      await Promise.all([
        ...prevHologram.map(s => updateDoc(doc(db, 'studioStamps', s.id), { isHologram: false })),
        updateDoc(doc(db, 'studioStamps', targetId), { isHologram: true }),
      ]);
      setStudioStamps(prev => prev.map(s => {
        if (s.id === targetId) return { ...s, isHologram: true };
        if (s.isHologram) return { ...s, isHologram: false };
        return s;
      }));
      setSelectedStampIds(new Set());
    } catch (err) {
      console.error('Mark as hologram failed:', err);
      showAlert('Failed to mark as hologram: ' + err.message);
    } finally {
      setStampUploading(false);
    }
  };

  // -- Hologram stamp: bake the designated hologram stamp into the selected/first base image --
  // ── Stamp embedding ──────────────────────────────────────────────────────────
  // A stamp dropped on an image is composited INTO the image's pixels at native resolution,
  // not kept as a separate overlay. It therefore survives save, print, share, crop and export
  // by construction, and it cannot be moved or deleted on its own. The image keeps a record of
  // every embedded stamp (url, position and size as fractions of the image), and every AI
  // operation in this app re-applies that record to its result — so an AI edit made here
  // cannot strip it either. A faint copy of the stamp is also tiled across the whole image at
  // ~2% opacity: an inpainting pass that paints over the visible stamp leaves that signature
  // behind, which is what makes "just remove the logo" insufficient.
  //
  // Honest limit: nothing makes a mark un-removable by every tool everywhere — a model that
  // regenerates the entire image discards everything in it. What is guaranteed is that the
  // stamp is part of the image rather than a layer, and that this app's own tools never lose it.
  const STAMP_GHOST_ALPHA = 0.02;

  const renderStampsInto = async (baseUrl, stamps) => {
    const baseEl = await loadImgSafe(baseUrl);
    if (!baseEl) return baseUrl;
    const W = baseEl.naturalWidth || baseEl.width, H = baseEl.naturalHeight || baseEl.height;
    const off = document.createElement('canvas'); off.width = W; off.height = H;
    const ctx = off.getContext('2d');
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(baseEl, 0, 0, W, H);
    for (const st of stamps) {
      const el = stampImageCacheRef.current.get(st.url) || await loadImgSafe(st.url);
      if (!el) continue;
      const ar = ((el.naturalHeight || el.height) / (el.naturalWidth || el.width)) || 1;
      const sw = Math.max(8, Math.round(st.fw * W)), sh = Math.round(sw * ar);
      if (st.ghost !== false) {
        ctx.save(); ctx.globalAlpha = STAMP_GHOST_ALPHA;
        const tw = Math.max(48, Math.round(Math.min(W, H) * 0.16)), th = Math.round(tw * ar);
        for (let ty = -th / 2; ty < H; ty += th * 1.5) for (let tx = -tw / 2; tx < W; tx += tw * 1.5) ctx.drawImage(el, tx, ty, tw, th);
        ctx.restore();
      }
      ctx.save(); ctx.globalAlpha = st.alpha ?? 1;
      ctx.drawImage(el, Math.round(st.fx * W - sw / 2), Math.round(st.fy * H - sh / 2), sw, sh);
      ctx.restore();
    }
    return off.toDataURL('image/png');
  };

  /** Re-apply an image's embedded stamps to a new rendition of it (used after every AI edit). */
  const withEmbeddedStamps = async (imgObj, newUrl) =>
    (imgObj?.embeddedStamps?.length ? renderStampsInto(newUrl, imgObj.embeddedStamps) : newUrl);

  /**
   * The picture's pixels have changed for good — so everything that remembers an EARLIER
   * version of them has to let go.
   *
   * Two tools keep an untouched copy beside a picture so their own effect never stacks:
   * brightness/contrast (`_filterOrigUrl`) and the Selfie effects, including Mirror
   * (`_fxOrigUrl`). That is right while those tools own the picture. It is wrong the moment
   * something else rewrites it — the broom, a stamp, a cut. Then the kept copy is a picture
   * from before the change, and the next press of Mirror or of a slider quietly redraws from
   * it: the erasing is undone, the cut comes back, the stamp disappears. It looks like the
   * button mangled the photo, and what it actually did was restore an older one.
   *
   * Clearing the effect keys too, not just the URLs, matters: leaving `_selfieFx` set while
   * dropping the original would make the next render apply that effect a second time, on
   * pixels that already carry it. The effect is baked in now; the slider starts from here.
   */
  const rebased = (img, url) => ({
    ...img, url,
    _filterOrigUrl: undefined, _brightness: undefined, _contrast: undefined,
    _fxOrigUrl: undefined, _selfieFx: undefined, _selfieMirror: undefined,
    _turnOrigUrl: undefined, _turnBakedUrl: undefined, turnX: undefined, turnY: undefined,
  });

  // ── Turning a picture in space (see renderTurnedPicture) ──────────────────────────────────
  // A turn is always drawn from the untouched picture, kept beside it, so turning back and forth
  // never stacks one turn on another. But only while the picture on the sheet is still the one
  // the last turn produced: once anything else has rewritten it — an AI edit, the eraser, a cut —
  // the turn is part of the picture now, and the next turn starts flat from what is there. That
  // is checked here rather than trusted to every tool that writes pixels to call rebased().
  const turnIsLive = (img) => !!img?._turnOrigUrl && img.url === img._turnBakedUrl;
  const turnSourceOf = (img) => (turnIsLive(img) ? img._turnOrigUrl : img.url);
  const turnAnglesOf = (img) => (turnIsLive(img) ? { rx: img.turnX || 0, ry: img.turnY || 0 } : { rx: 0, ry: 0 });
  const imageOrbitRef = useRef(null);                  // { id, sx, sy, rx, ry, cur, el, before }
  const [turnPreview, setTurnPreview] = useState(null); // { id, url } while the handle is held
  const turnPreviewRafRef = useRef(0);
  const bakeImageTurn = async (id, rx, ry, before) => {
    const img = canvasImagesRef.current.find(i => i.id === id);
    if (!img) return;
    const srcUrl = turnSourceOf(img);
    const el = await loadImgSafe(srcUrl);
    if (!el) return;
    const norm = (v) => ((Math.round(v) % 360) + 360) % 360;
    const flat = norm(rx) === 0 && norm(ry) === 0;
    const url = flat ? srcUrl : renderTurnedPicture(el, rx, ry).toDataURL('image/png');
    const prior = before || canvasImagesRef.current;
    const after = canvasImagesRef.current.map(i => (i.id !== id ? i : flat
      ? { ...i, url, _turnOrigUrl: undefined, _turnBakedUrl: undefined, turnX: undefined, turnY: undefined }
      : { ...i, url, _turnOrigUrl: srcUrl, _turnBakedUrl: url, turnX: rx, turnY: ry }));
    setCanvasImages(after);
    canvasImagesRef.current = after;
    pushImageHistory(prior, after);
    markDirty();
  };
  // The handle under a picture: sideways spins it, up and down tilts it — the same gesture and
  // the same rate as the orbit handle under a 3D figure (applyShapeOrbit). While it is held the
  // piece is drawn small and fast; letting go draws it at full size.
  const applyImageOrbit = (x, y) => {
    const g = imageOrbitRef.current;
    if (!g) return;
    const DEG_PER_PX = 0.55;
    g.cur = { rx: g.rx - (y - g.sy) * DEG_PER_PX, ry: g.ry + (x - g.sx) * DEG_PER_PX };
    if (turnPreviewRafRef.current) return;
    turnPreviewRafRef.current = requestAnimationFrame(() => {
      turnPreviewRafRef.current = 0;
      const h = imageOrbitRef.current;
      if (!h || !h.el) return;
      setTurnPreview({ id: h.id, url: renderTurnedPicture(h.el, h.cur.rx, h.cur.ry, 420).toDataURL('image/png') });
    });
  };

  // ── Does this tap land on the picture, or on the nothing around it? ──────────────────
  // A cut-out is a full rectangle of which most is transparent. The element that carries it is
  // that whole rectangle, so a head lifted out of a photograph catches every tap in the empty
  // corners around it: the piece is selected from well outside itself, and a tap on the sheet
  // beside it re-selects it instead of letting go. It reads as a wildly over-sensitive object.
  //
  // So a tap on a cut-out is checked against its alpha first. Miss the paint and this handler
  // does nothing at all — no selecting, and no stopPropagation — so the press carries on to
  // the sheet underneath and does what it would have done if the piece were not there.
  //
  // The map is 64x64 per picture, built once and kept: sampling the full-size PNG on every
  // press would be a getImageData per tap. Until it is ready the answer is yes — being
  // momentarily as grabbable as before is the safe failure.
  //
  // It is asked of EVERY picture, not only the ones flagged as cut-outs. A photograph is
  // opaque edge to edge, so its map says yes everywhere and nothing changes for it; and the
  // flags do not in fact cover the cases that hurt — a PNG sticker dragged in from a folder
  // carries no flag at all and has exactly the same ring of empty rectangle around it. Alpha
  // is the thing actually being asked about, so alpha is what gets asked.
  const ALPHA_N = 64;
  const alphaMaskRef = useRef(new Map());
  const alphaMaskFor = (url) => {
    const m = alphaMaskRef.current;
    if (m.has(url)) return m.get(url);
    m.set(url, 'pending');
    loadImgSafe(url).then((el) => {
      if (!el?.naturalWidth) { m.set(url, null); return; }
      const c = document.createElement('canvas'); c.width = ALPHA_N; c.height = ALPHA_N;
      const g = c.getContext('2d', { willReadFrequently: true });
      g.drawImage(el, 0, 0, ALPHA_N, ALPHA_N);
      const d = g.getImageData(0, 0, ALPHA_N, ALPHA_N).data;
      const a = new Uint8Array(ALPHA_N * ALPHA_N);
      for (let i = 0; i < a.length; i++) a[i] = d[i * 4 + 3];
      m.set(url, a);
    }).catch(() => m.set(url, null));
    return 'pending';
  };
  const tapIsOnPaint = (img, clientX, clientY) => {
    const mask = alphaMaskFor(img.url);
    if (!mask || mask === 'pending') return true;
    const el = canvasRef.current;
    if (!el) return true;
    const r = el.getBoundingClientRect();
    const w = (img.width / 21) * r.width, h = (img.height / 29.7) * r.height;
    if (w <= 0 || h <= 0) return true;
    const left = r.left + (img.x / 100) * r.width, top = r.top + (img.y / 100) * r.height;
    let dx = clientX - (left + w / 2), dy = clientY - (top + h / 2);
    if (img.rotation) {                        // undo the element's own rotation first
      const a = (-img.rotation * Math.PI) / 180;
      const ca = Math.cos(a), sa = Math.sin(a);
      const nx = dx * ca - dy * sa, ny = dx * sa + dy * ca;
      dx = nx; dy = ny;
    }
    const fx = dx / w + 0.5, fy = dy / h + 0.5;
    if (fx < 0 || fx > 1 || fy < 0 || fy > 1) return false;
    const px = Math.min(ALPHA_N - 1, Math.max(0, Math.round(fx * (ALPHA_N - 1))));
    const py = Math.min(ALPHA_N - 1, Math.max(0, Math.round(fy * (ALPHA_N - 1))));
    // One cell of slack all round, so the very edge of a cut-out is still grabbable rather
    // than needing to be hit dead-on.
    for (let qy = py - 1; qy <= py + 1; qy++) {
      for (let qx = px - 1; qx <= px + 1; qx++) {
        if (qx < 0 || qy < 0 || qx >= ALPHA_N || qy >= ALPHA_N) continue;
        if (mask[qy * ALPHA_N + qx] > 24) return true;
      }
    }
    return false;
  };

  /** Topmost image under a sheet position given in percent (rotation ignored for the hit test). */
  const imageAtPercent = (xPct, yPct) => {
    const sorted = [...canvasImagesRef.current].sort((a, b) => (b.layerIndex ?? 1) - (a.layerIndex ?? 1));
    return sorted.find(img => {
      const w = (img.width / 21) * 100, h = (img.height / 29.7) * 100;
      return xPct >= img.x && xPct <= img.x + w && yPct >= img.y && yPct <= img.y + h;
    }) || null;
  };

  const embedStampIntoImage = async (imageId, stampUrl, { fx, fy, fw = null, alpha = 1, ghost = true } = {}) => {
    const img = canvasImagesRef.current.find(i => i.id === imageId);
    if (!img) return;
    setIsSaving(true);
    try {
      const baseEl = await loadImgSafe(img.url);
      if (!baseEl) { showAlert('Could not load the image.'); return; }
      const W = baseEl.naturalWidth || baseEl.width, H = baseEl.naturalHeight || baseEl.height;
      // Keep the stamp's pixels with the image (a stamp's object URL dies with the session).
      let stampData = stampUrl;
      if (!/^data:/i.test(stampUrl)) {
        const stEl = stampImageCacheRef.current.get(stampUrl) || await loadImgSafe(stampUrl);
        if (!stEl) { showAlert('Could not load the stamp.'); return; }
        const sc = document.createElement('canvas');
        sc.width = stEl.naturalWidth || stEl.width; sc.height = stEl.naturalHeight || stEl.height;
        sc.getContext('2d').drawImage(stEl, 0, 0);
        stampData = sc.toDataURL('image/png');
      }
      const stamp = {
        url: stampData,
        fx: Math.max(0, Math.min(1, fx)), fy: Math.max(0, Math.min(1, fy)),
        fw: fw ?? (Math.min(W, H) * 0.18) / W,   // 18% of the shorter side, as a fraction of width
        alpha, ghost,
      };
      const url = await renderStampsInto(img.url, [stamp]);
      const before = canvasImagesRef.current;
      // The stamped image is the new baseline: brightness/contrast bake from _filterOrigUrl,
      // and a pre-stamp baseline would quietly drop the stamp on the next slider move.
      const after = before.map(i => i.id === imageId
        ? { ...rebased(i, url), embeddedStamps: [...(i.embeddedStamps || []), stamp] }
        : i);
      setCanvasImages(after); canvasImagesRef.current = after;
      pushImageHistory(before, after);
      markDirty();
    } catch (err) {
      showAlert('Could not embed the stamp: ' + (err?.message || err));
    } finally {
      setIsSaving(false);
    }
  };

  // PCake: bake the shop's hologram stamp into the photo — top-left, 14% of the shorter side,
  // faint (38%), exactly where and how the old studio put it.
  const handleHologramStamp = async () => {
    const hologramStamp = studioStamps.find(s => s.isHologram);
    if (!hologramStamp) {
      showAlert('No hologram stamp set. Select a stamp in the panel and mark it as hologram first.');
      return;
    }
    const target = canvasImagesRef.current.find(i => i.id === (selectedImage || effectiveImageId))
      || canvasImagesRef.current[0];
    if (!target) { showAlert('Add an image to the canvas first to embed the hologram stamp.'); return; }
    const baseEl = await loadImgSafe(target.url);
    if (!baseEl) { showAlert('Could not load the image.'); return; }
    const W = baseEl.naturalWidth || baseEl.width, H = baseEl.naturalHeight || baseEl.height;
    const S = Math.min(W, H), stampS = S * 0.14, margin = S * 0.03;
    await embedStampIntoImage(target.id, hologramStamp.url, {
      fx: (margin + stampS / 2) / W, fy: (margin + stampS / 2) / H, fw: stampS / W, alpha: 0.38, ghost: false,
    });
  };

  // Update canvas cursor based on active tool
  useEffect(() => {
    if (imageSelectionMode) {
      setCanvasCursor('pointer');
    } else if (activeTool === 'draw') {
      setCanvasCursor((drawingMode === 'pen' || drawingMode === 'pencil') ? 'crosshair' : 'cell');
    } else if (activeTool === 'selection' && selectionMode) {
      setCanvasCursor('crosshair');
    } else if (activeTool === 'resize') {
      setCanvasCursor('default');
    } else {
      setCanvasCursor('default');
    }
  }, [activeTool, drawingMode, selectionMode, imageSelectionMode]);

  useEffect(() => {
    if (activeTool !== 'selection') {
      setIsSelecting(false);
      setSelectionPath([]);
    }
    
    if (activeTool !== 'selection' && activeTool !== 'draw') {
      setCanvasCursor('default');
    }
  }, [activeTool]);

  // Update aspect ratio when image is selected
  useEffect(() => {
    if (selectedImage) {
      const image = canvasImages.find(img => img.id === selectedImage);
      if (image && image.naturalWidth && image.naturalHeight) {
        setAspectRatio(image.naturalWidth / image.naturalHeight);
      }
    }
  }, [selectedImage, canvasImages]);

  // Reset on component mount
  useEffect(() => {
    resetStudio();
  }, []);

  const resetStudio = () => {
    setCanvasImages([]);
    setSelectedImage(null);
    setResizeWidth(10);
    setResizeHeight(10);
    setShowImageControls(null);
    setActiveTool(null);
    setDrawingPaths([]);
    setCurrentPath([]);
    setDrawingColor('#000000');
    setBrushSize(5);
    setDrawingHistory([]);
    setHistoryIndex(-1);
    setShowColorPicker(false);
    setCanvasCursor('default');
    setSelectedBucketImages([]);
    setSelectionMode('rectangle');
    setIsSelecting(false);
    setSelectionStart({ x: 0, y: 0 });
    setSelectionEnd({ x: 0, y: 0 });
    setSelectionPath([]);
    setShowTopControls(false);
    setCurrentControlSection(null);
    setLockAspectRatio(false);
    setAspectRatio(1);
    setImageSelectionMode(false); // Reset image selection mode
    setCanvasShapes([]);
    setSelectedShapeId(null);
    setActiveShapeTool(null);
    setShowShapesPanel(false);
    setCanvasBackground('transparent');
    draggingShapeRef.current = null;
    resizingShapeRef.current = null;
    orbitShapeRef.current = null;
    shapeBeforeRef.current = null;
    draggingInkRef.current = null;
    resizingInkRef.current = null;
    rotatingInkRef.current = null;
    inkBeforeRef.current = null;
    inkDraggedRef.current = false;
    setIsMovingShape(false);
  };

  const pushShapeHistory = (before, after) => {
    markDirty();
    const newHistory = drawingHistoryRef.current.slice(0, historyIndexRef.current + 1);
    newHistory.push({ type: 'shapes', before, after });
    setDrawingHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  // Undo/redo entry for a change that touches only hand-drawn strokes (move, resize, rotate,
  // copy, delete of a selected stroke). Reuses the sheetReplace type the multi-drag already
  // uses for the same job: images and shapes go in unchanged, so an undo only puts the
  // strokes back.
  const pushInkHistory = (before, after) => {
    markDirty();
    const _imgsNow = canvasImagesRef.current;
    const _shpsNow = canvasShapesRef.current;
    const newHistory = drawingHistoryRef.current.slice(0, historyIndexRef.current + 1);
    newHistory.push({ type: 'sheetReplace', imagesBefore: _imgsNow, imagesAfter: _imgsNow, shapesBefore: _shpsNow, shapesAfter: _shpsNow, pathsBefore: before, pathsAfter: after });
    setDrawingHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  /* ---- Ink gesture maths -------------------------------------------------
     Shared by the mouse and touch move handlers so the two can't drift apart.
     x/y are canvas-relative screen pixels; rect is the canvas's own rect.     */

  // Move: the offset was recorded against the stroke's bounding-box origin in screen terms,
  // so the new origin is computed in percent (resolution-independent) and only then turned
  // back into a delta in the path's own pixels -- exactly what the multi-drag does.
  const applyInkDrag = (x, y, rect) => {
    const g = draggingInkRef.current;
    if (!g) return;
    if (!inkDraggedRef.current) {
      // First real movement: this is a MOVE now, not the tap that selected it. Narrow the top
      // bar to the coordinates alone, same as the shape and image drags.
      setCurrentControlSection('selectmove');
      setSelectMoveControlMode('position');
      setShowTopControls(true);
      setIsMovingShape(true);
    }
    inkDraggedRef.current = true;
    setDrawingPaths(prev => prev.map(p => {
      if (p.id !== g.id) return p;
      const o = inkPathOriginPct(p);
      if (!o) return p;
      const newXPct = ((x - g.offsetX) / rect.width) * 100;
      const newYPct = ((y - g.offsetY) / rect.height) * 100;
      const dxPx = ((newXPct - o.xPct) / 100) * p.canvasW;
      const dyPx = ((newYPct - o.yPct) / 100) * p.canvasH;
      return { ...p, path: p.path.map(pt => ({ ...pt, x: pt.x + dxPx, y: pt.y + dyPx })) };
    }));
  };

  // Resize: every point is scaled about the corner opposite the one being dragged, so that
  // corner stays put. Dragging back past the anchor clamps instead of flipping the stroke.
  const applyInkResize = (x, y, rect) => {
    const g = resizingInkRef.current;
    if (!g) return;
    const p = drawingPathsRef.current.find(pp => pp.id === g.id);
    if (!p) return;
    const scX = (p.canvasW || rect.width) / rect.width;
    const scY = (p.canvasH || rect.height) / rect.height;
    const lx = x * scX, ly = y * scY;
    let nw, nh;
    if (g.corner === 'se')      { nw = lx - g.anchor.x; nh = ly - g.anchor.y; }
    else if (g.corner === 'nw') { nw = g.anchor.x - lx; nh = g.anchor.y - ly; }
    else if (g.corner === 'ne') { nw = lx - g.anchor.x; nh = g.anchor.y - ly; }
    else                        { nw = g.anchor.x - lx; nh = ly - g.anchor.y; } // sw
    const MIN_LOCAL_PX = 4;
    let sX = Math.max(nw, MIN_LOCAL_PX) / g.origW;
    let sY = Math.max(nh, MIN_LOCAL_PX) / g.origH;
    if (lockAspectRatio) { const s = Math.min(sX, sY); sX = s; sY = s; }
    setDrawingPaths(prev => prev.map(pp => pp.id !== g.id ? pp : {
      ...pp,
      path: g.origPath.map(pt => ({
        ...pt,
        x: g.anchor.x + (pt.x - g.anchor.x) * sX,
        y: g.anchor.y + (pt.y - g.anchor.y) * sY,
      })),
    }));
  };

  // Rotate: absolute bearing from the stroke's centre to the pointer, applied to the points
  // captured at grab time. Same +90 convention the shape and image rotate handles use, so a
  // stroke turns with the handle exactly the way a figure does.
  const applyInkRotate = (x, y, rect) => {
    const g = rotatingInkRef.current;
    if (!g) return;
    const p = drawingPathsRef.current.find(pp => pp.id === g.id);
    if (!p) return;
    const scX = (p.canvasW || rect.width) / rect.width;
    const scY = (p.canvasH || rect.height) / rect.height;
    const deg = Math.atan2(y * scY - g.cy, x * scX - g.cx) * (180 / Math.PI) + 90;
    const rad = (deg * Math.PI) / 180;
    const ca = Math.cos(rad), sa = Math.sin(rad);
    setInkGestureAngle(Math.round(((deg % 360) + 360) % 360));
    setDrawingPaths(prev => prev.map(pp => pp.id !== g.id ? pp : {
      ...pp,
      path: g.origPath.map(pt => {
        const dx = pt.x - g.cx, dy = pt.y - g.cy;
        return { ...pt, x: g.cx + dx * ca - dy * sa, y: g.cy + dx * sa + dy * ca };
      }),
    }));
  };

  /* ---- Eraser beyond the drawing layer ------------------------------------
     The eraser stroke itself only ever cut the hand-drawn canvas. Figures are SVG elements
     and images are their own <img> layers, both UNDER that canvas, so rubbing over either did
     nothing. These two apply the same rub to those layers.
     `pts` are canvas-relative screen pixels; `widthPx` is the eraser width in the same units. */

  // Figures: the rub is stored ON the figure, in fractions of its own box, and hidden with an
  // SVG mask at render time (see applyErase in renderShapeElement). The figure stays a figure
  // -- selectable, movable, recolourable -- and the hole travels with it.
  const applyEraseToShapes = (pts, widthPx, rect) => {
    if (!pts || !pts.length || !rect) return false;
    const half = widthPx / 2 + 1;
    let sMinX = Infinity, sMinY = Infinity, sMaxX = -Infinity, sMaxY = -Infinity;
    for (const p of pts) {
      if (p.x < sMinX) sMinX = p.x; if (p.x > sMaxX) sMaxX = p.x;
      if (p.y < sMinY) sMinY = p.y; if (p.y > sMaxY) sMaxY = p.y;
    }
    sMinX -= half; sMinY -= half; sMaxX += half; sMaxY += half;
    const before = canvasShapesRef.current;
    let touched = false;
    const after = before.map(s => {
      const bx = (Math.min(s.x1, s.x2) / 100) * rect.width;
      const by = (Math.min(s.y1, s.y2) / 100) * rect.height;
      const bw = (Math.abs(s.x2 - s.x1) / 100) * rect.width;
      const bh = (Math.abs(s.y2 - s.y1) / 100) * rect.height;
      if (bw <= 0 || bh <= 0) return s;
      // A turned figure covers a bigger patch of screen than its own box, so the first test is
      // deliberately loose; the precise test is done below in the figure's own frame.
      const grow = s.rotation ? Math.max(bw, bh) / 2 : 0;
      if (sMaxX < bx - grow || sMinX > bx + bw + grow || sMaxY < by - grow || sMinY > by + bh + grow) return s;
      // The very same conversion the live preview used while the finger was down, so what was
      // shown during the rub is exactly what gets stored.
      const stroke = eraseStrokeForShape(pts, widthPx, s, rect.width, rect.height);
      if (!stroke) return s;
      touched = true;
      return { ...s, erase: [...(s.erase || []), stroke] };
    });
    if (touched) {
      setCanvasShapes(after);
      canvasShapesRef.current = after;
      pushShapeHistory(before, after);
    }
    return touched;
  };

  // Images: there is nothing to mask -- the rub is taken straight out of the picture's own
  // pixels, undoing any rotation first so the hole lands where the finger was.
  const applyEraseToImages = async (pts, widthPx, rect) => {
    if (!pts || !pts.length || !rect) return;
    const half = widthPx / 2 + 1;
    let sMinX = Infinity, sMinY = Infinity, sMaxX = -Infinity, sMaxY = -Infinity;
    for (const p of pts) {
      if (p.x < sMinX) sMinX = p.x; if (p.x > sMaxX) sMaxX = p.x;
      if (p.y < sMinY) sMinY = p.y; if (p.y > sMaxY) sMaxY = p.y;
    }
    sMinX -= half; sMinY -= half; sMaxX += half; sMaxY += half;
    const before = canvasImagesRef.current;
    const updates = new Map();
    for (const img of before) {
      const il = (img.x / 100) * rect.width;
      const it = (img.y / 100) * rect.height;
      const iw = (img.width / 21) * rect.width;
      const ih = (img.height / 29.7) * rect.height;
      if (iw <= 0 || ih <= 0) continue;
      const grow = img.rotation ? Math.max(iw, ih) / 2 : 0;
      if (sMaxX < il - grow || sMinX > il + iw + grow || sMaxY < it - grow || sMinY > it + ih + grow) continue;
      const el = await loadImgSafe(img.url);
      if (!el) continue;
      const nw = el.naturalWidth || el.width, nh = el.naturalHeight || el.height;
      if (!nw || !nh) continue;
      const rot = ((img.rotation || 0) * Math.PI) / 180;
      const ca = Math.cos(-rot), sa = Math.sin(-rot);
      const icx = il + iw / 2, icy = it + ih / 2;
      const sx = nw / iw, sy = nh / ih;
      const local = pts.map(p => {
        const dx = p.x - icx, dy = p.y - icy;
        return { x: ((icx + dx * ca - dy * sa) - il) * sx, y: ((icy + dx * sa + dy * ca) - it) * sy };
      });
      // Same hardened stamp as the ink eraser: a soft edge cut with its own partial alpha
      // would leave a quarter of itself behind as a faint smear.
      const stampC = document.createElement('canvas'); stampC.width = nw; stampC.height = nh;
      const sctx = stampC.getContext('2d', { willReadFrequently: true });
      sctx.strokeStyle = '#000';
      sctx.lineWidth = Math.max(1, widthPx * Math.max(sx, sy));
      sctx.lineCap = 'round';
      sctx.lineJoin = 'round';
      sctx.beginPath();
      sctx.moveTo(local[0].x, local[0].y);
      if (local.length === 1) sctx.lineTo(local[0].x + 0.01, local[0].y);
      else for (let i = 1; i < local.length; i++) sctx.lineTo(local[i].x, local[i].y);
      sctx.stroke();
      const sd = sctx.getImageData(0, 0, nw, nh);
      let any = false;
      for (let i = 3; i < sd.data.length; i += 4) if (sd.data[i] > 0) { sd.data[i] = 255; any = true; }
      if (!any) continue;                       // the rub missed this picture after all
      sctx.putImageData(sd, 0, 0);
      const oc = document.createElement('canvas'); oc.width = nw; oc.height = nh;
      const octx = oc.getContext('2d');
      octx.drawImage(el, 0, 0);
      octx.globalCompositeOperation = 'destination-out';
      octx.drawImage(stampC, 0, 0);
      octx.globalCompositeOperation = 'source-over';
      updates.set(img.id, oc.toDataURL('image/png'));
    }
    if (!updates.size) return;
    const after = before.map(i => updates.has(i.id)
      ? rebased(i, updates.get(i.id))
      : i);
    setCanvasImages(after);
    canvasImagesRef.current = after;
    pushImageHistory(before, after);
    markDirty();
  };

  /**
   * A press on empty sheet while something is selected means "put that one down", not "make
   * another one". It clears the selection and is swallowed -- nothing is created. The tool
   * stays armed, so the very next press draws as usual.
   *
   * Returns true when it consumed the press, so callers can just `return`.
   */
  const consumePressToDeselect = () => {
    // ── Not when a tool wants the press for itself ──────────────────────────────────────
    // This eats the first press on the sheet to put down whatever was selected, and the paths
    // that call it then RETURN — so the press never reaches the tool. With crop armed that was
    // fatal: an image is selected the moment you add one, so choosing Smart Crop and tapping
    // the object spent the tap on deselecting, and because this also clears the control section
    // the crop row vanished with it. Choose crop, touch the thing you want cropped, end up back
    // in the studio having done nothing.
    //
    // The pen is the same story from the other side: it is the one tool where touching the
    // sheet must never deselect, because touching the sheet is how you draw on it.
    //
    // Only those two. An armed FIGURE tool was in this list as well, and it should not have
    // been: a figure tool stays armed after the figure is drawn, so with one in hand nothing
    // could be deselected by pressing the sheet at all — which is most of the time. Touching
    // the sheet puts down whatever was selected, everywhere except the pen.
    if (activeTool === 'selection' || activeTool === 'draw') return false;
    if (!selectedShapeId && !selectedImage && selectedShapeIds.size === 0) return false;
    setSelectedShapeId(null);
    setSelectedShapeIds(new Set());
    setSelectedImage(null);
    setShowImageControls(null);
    setIsMovingShape(false);
    setIsResizingShape(false);
    setIsRotatingShape(false);
    // Those colour and style controls belonged to the object that has just been put down, so
    // they go with it. Drawing the next figure opens them again for that one.
    setCurrentControlSection(null);
    setShowTopControls(false);
    return true;
  };

  // Orbit a 3D figure: sideways spins it, up and down tilts it. Shared by the mouse and touch
  // move handlers. x/y are canvas-relative pixels, matching where the drag started.
  const applyShapeOrbit = (x, y) => {
    const g = orbitShapeRef.current;
    if (!g) return;
    const DEG_PER_PX = 0.55;
    const ry = g.ry + (x - g.sx) * DEG_PER_PX;
    const rx = g.rx - (y - g.sy) * DEG_PER_PX;
    setCanvasShapes(prev => prev.map(s => s.id === g.id ? { ...s, rotX: rx, rotY: ry } : s));
  };

  // Select a hand-drawn stroke: it takes the single-selection slot the figures use, clears any
  // image/multi selection, and opens the same controls a figure opens. shapeClickedRef stops
  // the click that follows the release from clearing it again (handleCanvasClick).
  //
  // The same press also arms the move, exactly as pressing a figure does, so touch-and-drag in
  // one motion works; letting go without moving just leaves it selected (no undo entry is
  // banked unless the points actually changed).
  const selectInkPath = (id, px, py, rect) => {
    shapeClickedRef.current = true;
    setSelectedShapeIds(new Set());
    setSelectedImage(null);
    setShowImageControls(null);
    setSelectedShapeId(id);
    openInkStyleControls(id);
    const p = drawingPathsRef.current.find(pp => pp.id === id);
    const o = p && inkPathOriginPct(p);
    if (o && rect) {
      inkBeforeRef.current = drawingPathsRef.current;
      inkDraggedRef.current = false;
      draggingInkRef.current = {
        id,
        offsetX: px - (o.xPct / 100) * rect.width,
        offsetY: py - (o.yPct / 100) * rect.height,
      };
    }
  };

  // Draw shapes onto an offscreen canvas (used by save/print/share)
  /**
   * Pictures and figures, in the order the screen stacks them.
   *
   * Both come out of the same counter (layerCounterRef) and the sheet paints both with
   * `zIndex: layerIndex`, so on screen there is ONE pile and a figure can sit under a photo.
   * Every export drew all the pictures and then all the figures, which quietly reverses that:
   * a filled rounded square placed UNDER a cut-out photo is painted back over the top of it,
   * and the photo vanishes. Cut a composition like that and you get the empty square; print it
   * and you get the empty square. The item is not "not considered" — it is painted and then
   * buried.
   */
  const layersInOrder = (images, shapes) => [
    ...(images || []).map(i => ({ kind: 'image', item: i, z: i.layerIndex ?? 1 })),
    ...(shapes || []).map(s => ({ kind: 'shape', item: s, z: s.layerIndex ?? 4 })),
  ].sort((a, b) => a.z - b.z);

  const drawShapesOnCanvas = (ctx, shapes, renderW, renderH, offXpct = 0, offYpct = 0, contentWpct = 100, contentHpct = 100) => {
    const toX = p => ((p - offXpct) / contentWpct) * renderW;
    const toY = p => ((p - offYpct) / contentHpct) * renderH;
    const scaleW = renderW / (500 * contentWpct / 100);
    // `ctx` is a PARAMETER here, deliberately shadowing the outer one: a figure that has been
    // rubbed with the eraser is drawn onto a scratch layer of its own first (see the loop
    // below), so the rub can be cut out of that figure alone instead of punching a hole
    // through whatever happens to be behind it.
    const drawOneShape = (ctx, shape) => {
      const x1 = toX(shape.x1), y1 = toY(shape.y1);
      const x2 = toX(shape.x2), y2 = toY(shape.y2);
      const stroke = shape.strokeColor || '#000000';
      const fill = shape.fillColor || 'none';
      const sw = Math.max(0.5, (shape.strokeWidth || 2) * scaleW);
      ctx.save();
      if (shape.rotation) {
        const rcx = (x1 + x2) / 2, rcy = (y1 + y2) / 2;
        ctx.translate(rcx, rcy); ctx.rotate((shape.rotation * Math.PI) / 180); ctx.translate(-rcx, -rcy);
      }
      ctx.strokeStyle = stroke;
      ctx.lineWidth = sw;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.setLineDash(shape.dashed ? [6, 3] : []);
      switch (shape.type) {
        case 'line':
          ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
          break;
        case 'arrow': {
          const ang = Math.atan2(y2 - y1, x2 - x1);
          const aLen = Math.max(10, sw * 5);
          ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(x2, y2);
          ctx.lineTo(x2 - aLen * Math.cos(ang - 0.45), y2 - aLen * Math.sin(ang - 0.45));
          ctx.lineTo(x2 - aLen * Math.cos(ang + 0.45), y2 - aLen * Math.sin(ang + 0.45));
          ctx.closePath(); ctx.fillStyle = stroke; ctx.fill();
          break;
        }
        case 'circle': {
          const cx = (x1 + x2) / 2, cy = (y1 + y2) / 2;
          const rx = Math.abs(x2 - x1) / 2, ry = Math.abs(y2 - y1) / 2;
          ctx.beginPath();
          ctx.ellipse(cx, cy, Math.max(1, rx), Math.max(1, ry), 0, 0, Math.PI * 2);
          if (fill !== 'none') { ctx.fillStyle = fill; ctx.fill(); }
          ctx.stroke();
          break;
        }
        case 'triangle': {
          const tcx = (x1 + x2) / 2;
          ctx.beginPath();
          ctx.moveTo(tcx, Math.min(y1, y2));
          ctx.lineTo(Math.min(x1, x2), Math.max(y1, y2));
          ctx.lineTo(Math.max(x1, x2), Math.max(y1, y2));
          ctx.closePath();
          if (fill !== 'none') { ctx.fillStyle = fill; ctx.fill(); }
          ctx.stroke();
          break;
        }
        case 'rectangle': {
          ctx.beginPath();
          ctx.rect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1));
          if (fill !== 'none') { ctx.fillStyle = fill; ctx.fill(); }
          ctx.stroke();
          break;
        }
        case 'text': {
          const fs = Math.max(8, (shape.fontSize || 12) * scaleW);
          const tf = `${shape.italic ? 'italic ' : ''}${shape.bold ? 'bold ' : ''}${fs}px ${shape.fontFamily || 'Arial'}`;
          ctx.font = tf;
          ctx.fillStyle = stroke;
          ctx.textBaseline = 'top';
          // Same wrapping the screen does, at this canvas's scale — a saved picture has to read
          // exactly like the sheet, and the old single fillText with a maxWidth squeezed the
          // letters together instead of going onto a second line.
          const _tw = Math.abs(x2 - x1);
          const _lines = wrapTextLines(shape.text, tf, _tw);
          const _lh = fs * 1.2;
          const _tx = Math.min(x1, x2), _ty = Math.min(y1, y2);
          // Underline. Canvas has no text-decoration, so it is a rule drawn under each line,
          // as long as that line and no longer — the same thing the screen's SVG shows.
          const _underline = (lx, ly, ln) => {
            const w = ctx.measureText(ln).width;
            if (w <= 0) return;
            ctx.fillRect(lx, ly + fs * 1.08, w, Math.max(1, fs / 16));
          };
          // Raised letters: the side wall first, one stepped copy per pixel of depth, then the
          // face over it. Same construction as the screen (see the SVG text case).
          const _depth = shape.threeD ? text3DDepth(fs) : 0;
          if (_depth > 0) {
            ctx.fillStyle = text3DWall(stroke);
            for (let k = _depth; k >= 1; k--) {
              _lines.forEach((ln, i) => {
                ctx.fillText(ln, _tx + k, _ty + k + i * _lh);
                if (shape.underline) _underline(_tx + k, _ty + k + i * _lh, ln);
              });
            }
            ctx.fillStyle = stroke;
          }
          // Outline first, fill over it — the canvas equivalent of paint-order: stroke.
          const _ow = (Number(shape.outlineWidth) || 0) * scaleW;
          if (_ow > 0) {
            ctx.lineJoin = 'round';
            ctx.lineWidth = _ow * 2;
            ctx.strokeStyle = (shape.fillColor && shape.fillColor !== 'none') ? shape.fillColor : '#ffffff';
            _lines.forEach((ln, i) => ctx.strokeText(ln, _tx, _ty + i * _lh));
          }
          _lines.forEach((ln, i) => {
            ctx.fillText(ln, _tx, _ty + i * _lh);
            if (shape.underline) _underline(_tx, _ty + i * _lh, ln);
          });
          break;
        }
        case 'emoji': {
          const cachedImg = stampImageCacheRef.current.get(shape.emoji);
          if (cachedImg) {
            ctx.drawImage(cachedImg, Math.min(x1,x2), Math.min(y1,y2), Math.abs(x2-x1), Math.abs(y2-y1));
          }
          break;
        }
        case 'freeform': {
          const bx = Math.min(x1, x2), by = Math.min(y1, y2);
          const bw = Math.max(1, Math.abs(x2 - x1)), bh = Math.max(1, Math.abs(y2 - y1));
          ctx.translate(bx, by);
          const shapeClosed = isFreeformClosed(shape);
          const p2 = new Path2D(freeformPath(shape.pts || [], bw, bh, shapeClosed, shape.smooth !== false));
          if (shapeClosed && fill !== 'none') { ctx.fillStyle = fill; ctx.fill(p2); }
          ctx.stroke(p2);
          break;
        }
        default: {
          const bx = Math.min(x1, x2), by = Math.min(y1, y2);
          const bw = Math.max(1, Math.abs(x2 - x1)), bh = Math.max(1, Math.abs(y2 - y1));
          if (isShape2D(shape.type)) {
            const def = SHAPES_2D[shape.type];
            ctx.translate(bx, by);
            const p2 = new Path2D(def.path(bw, bh));
            if (!def.open && fill !== 'none') { ctx.fillStyle = fill; ctx.fill(p2); }
            ctx.stroke(p2);
          } else if (isShape3D(shape.type)) {
            // Same projection the screen uses, so a printed or saved 3D object is turned and
            // lit exactly as it appears on the sheet. The in-plane turn is inside the
            // projection, so undo the ctx rotation applied for it further up.
            if (shape.rotation) {
              const rcx = (x1 + x2) / 2, rcy = (y1 + y2) / 2;
              ctx.translate(rcx, rcy); ctx.rotate((-shape.rotation * Math.PI) / 180); ctx.translate(-rcx, -rcy);
            }
            ctx.translate(bx, by);
            ctx.setLineDash([]);
            ctx.lineJoin = 'round';
            const faces3d = has3DMesh(shape.type)
              ? project3D(shape.type, bw, bh, { ...pose3D(shape), rz: shape.rotation || 0 }, bodyColour3D(shape))
              : [];
            if (!faces3d.length) {
              // Mirror of the screen's fallback: no mesh, so draw the original painted version
              // (and put the in-plane turn back, since this branch doesn't bake it in).
              if (shape.rotation) {
                const rcx2 = (x1 + x2) / 2 - bx, rcy2 = (y1 + y2) / 2 - by;
                ctx.translate(rcx2, rcy2); ctx.rotate((shape.rotation * Math.PI) / 180); ctx.translate(-rcx2, -rcy2);
              }
              const def3 = SHAPES_3D[shape.type];
              const t3 = tones3D(bodyColour3D(shape));
              if (def3) {
                ctx.lineWidth = Math.max(0.5, (shape.strokeWidth || 2) * 0.35 * scaleW);
                ctx.strokeStyle = t3.darker;
                const grad3 = (g) => { g.addColorStop(0, t3.light); g.addColorStop(0.5, t3.base); g.addColorStop(1, t3.darker); return g; };
                for (const p of def3.parts(bw, bh)) {
                  const pp = new Path2D(p.d);
                  let fs3 = t3[p.fill] || t3.base;
                  if (p.fill === 'radial') fs3 = grad3(ctx.createRadialGradient(bw * 0.35, bh * 0.3, Math.min(bw, bh) * 0.05, bw * 0.5, bh * 0.5, Math.max(bw, bh) * 0.75));
                  else if (p.fill === 'hgrad') fs3 = grad3(ctx.createLinearGradient(0, 0, bw, 0));
                  else if (p.fill === 'vgrad') fs3 = grad3(ctx.createLinearGradient(0, 0, 0, bh));
                  ctx.fillStyle = fs3;
                  ctx.fill(pp, p.evenOdd ? 'evenodd' : 'nonzero');
                  ctx.stroke(pp);
                }
                // The ball's seams, as on screen.
                if (shape.type === 'sphere') {
                  ctx.save();
                  ctx.globalAlpha *= 0.55;
                  ctx.strokeStyle = t3.darker;
                  ctx.lineWidth = Math.max(0.8 * scaleW, Math.min(bw, bh) * 0.014);
                  ctx.lineCap = 'round'; ctx.lineJoin = 'round';
                  ctx.beginPath();
                  for (const run of sphereSeams3D(bw, bh, pose3D(shape))) {
                    ctx.moveTo(run[0][0], run[0][1]);
                    for (let i = 1; i < run.length; i++) ctx.lineTo(run[i][0], run[i][1]);
                  }
                  ctx.stroke();
                  ctx.restore();
                }
              }
              break;
            }
            ctx.lineWidth = Math.max(0.5, 0.7 * scaleW);
            for (const f of faces3d) {
              ctx.beginPath();
              ctx.moveTo(f.pts[0][0], f.pts[0][1]);
              for (let i = 1; i < f.pts.length; i++) ctx.lineTo(f.pts[i][0], f.pts[i][1]);
              ctx.closePath();
              ctx.fillStyle = f.fill;
              ctx.strokeStyle = f.fill;   // hides the seams between neighbouring faces
              ctx.fill();
              ctx.stroke();
            }
          }
          break;
        }
      }
      ctx.restore();

      // Caption (see renderShapeElement's matching block): turned with the figure, about the
      // figure's own centre, so a saved, printed or shared picture matches the live view.
      if (shape.type !== 'text' && shape.text) {
        const cx = (x1 + x2) / 2, cy = (y1 + y2) / 2;
        const lines = String(shape.text).split('\n').slice(0, 6);
        const capFs = Math.max(9, Math.min(28, Math.min(Math.abs(x2 - x1), Math.abs(y2 - y1)) * 0.18)) || 12;
        const lh = capFs * 1.15;
        const y0 = cy - ((lines.length - 1) * lh) / 2;
        ctx.save();
        const capRot = shape.textLevel ? 0 : (shape.rotation || 0);
        if (capRot) {
          ctx.translate(cx, cy);
          ctx.rotate((capRot * Math.PI) / 180);
          ctx.translate(-cx, -cy);
        }
        // The caption's own typeface, exactly as the screen shows it (see renderShapeElement's
        // matching block). Nothing set means Arial at weight 600, which is what it always was.
        const capInk = shape.captionColor || '#1a1a1a';
        ctx.font = `${shape.captionItalic ? 'italic ' : ''}${shape.captionBold ? 'bold' : '600'} ${capFs}px ${shape.captionFont || 'Arial, sans-serif'}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.lineJoin = 'round';
        ctx.setLineDash([]);
        ctx.lineWidth = Math.max(1.5, capFs * 0.14) * 2;
        ctx.strokeStyle = 'rgba(255,255,255,0.8)';
        // Canvas has no text-decoration, so an underline is a rule drawn under each line, as
        // long as that line and no longer — the same thing the screen's SVG shows. The lines are
        // centred, so it starts half a line-width left of the middle.
        const capUnderline = (lx, ly, ln) => {
          if (!shape.captionUnderline) return;
          const w = ctx.measureText(ln).width;
          if (w > 0) ctx.fillRect(lx - w / 2, ly + capFs * 0.58, w, Math.max(1, capFs / 16));
        };
        // Raised letters: the side wall first, one stepped copy per pixel of depth, then the
        // face over it. Same construction as the text tool's own 3D.
        const capDep = shape.caption3D ? text3DDepth(capFs) : 0;
        if (capDep > 0) {
          ctx.fillStyle = text3DWall(capInk);
          for (let k = capDep; k >= 1; k--) {
            lines.forEach((line, i) => {
              ctx.fillText(line, cx + k, y0 + i * lh + k);
              capUnderline(cx + k, y0 + i * lh + k, line);
            });
          }
        }
        ctx.fillStyle = capInk;
        lines.forEach((line, i) => {
          const ly = y0 + i * lh;
          ctx.strokeText(line, cx, ly);
          ctx.fillText(line, cx, ly);
          capUnderline(cx, ly, line);
        });
        ctx.restore();
      }
    };

    shapes.forEach(shape => {
      const eraseStrokes = Array.isArray(shape.erase) ? shape.erase : null;
      if (!eraseStrokes || !eraseStrokes.length) { drawOneShape(ctx, shape); return; }
      // Rubbed figure: draw it alone, cut the rub out of it, then lay the result down. This is
      // what keeps a saved or printed sheet identical to what is on screen, where the same hole
      // is done with an SVG mask (applyErase in renderShapeElement).
      const sc = document.createElement('canvas');
      sc.width = ctx.canvas.width; sc.height = ctx.canvas.height;
      const sctx = sc.getContext('2d');
      // Carry the destination's transform across so the figure lands in the same place.
      if (typeof ctx.getTransform === 'function') sctx.setTransform(ctx.getTransform());
      drawOneShape(sctx, shape);
      const bx = toX(Math.min(shape.x1, shape.x2)), by = toY(Math.min(shape.y1, shape.y2));
      const bw = Math.abs(toX(shape.x2) - toX(shape.x1)) || 1;
      const bh = Math.abs(toY(shape.y2) - toY(shape.y1)) || 1;
      const unit = Math.min(bw, bh) || 1;
      sctx.save();
      if (shape.rotation) {
        const rcx = bx + bw / 2, rcy = by + bh / 2;
        sctx.translate(rcx, rcy); sctx.rotate((shape.rotation * Math.PI) / 180); sctx.translate(-rcx, -rcy);
      }
      sctx.globalCompositeOperation = 'destination-out';
      sctx.setLineDash([]);
      sctx.lineCap = 'round';
      sctx.lineJoin = 'round';
      sctx.strokeStyle = 'rgba(0,0,0,1)';
      for (const st of eraseStrokes) {
        const pts = st.pts || [];
        if (!pts.length) continue;
        sctx.lineWidth = Math.max(1, (st.w || 0.05) * unit);
        sctx.beginPath();
        sctx.moveTo(bx + pts[0].x * bw, by + pts[0].y * bh);
        if (pts.length === 1) sctx.lineTo(bx + pts[0].x * bw + 0.01, by + pts[0].y * bh);
        else for (let i = 1; i < pts.length; i++) sctx.lineTo(bx + pts[i].x * bw, by + pts[i].y * bh);
        sctx.stroke();
      }
      sctx.restore();
      sctx.globalCompositeOperation = 'source-over';
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(sc, 0, 0);
      ctx.restore();
    });
  };

  // ── Auto crop: one tap cuts the main object out of the selected image as its own movable
  // layer. The object mask comes from the AI cut-out when a server is configured, else from
  // keying the flat border colour locally; an image that already has transparency uses its
  // own alpha. Everything downstream is the smart-crop path (mask + bounds → endSelection).
  // Drawn ink (pen/pencil/spray/fill strokes) has no image to key: the drawing layer is
  // rendered at screen density, strokes a few px apart are bridged, and the largest connected
  // drawing becomes the object. Its pixels are lifted into a new layer and erased from the
  // drawing (a mask path), so the drawn object can be moved like any image.
  const runInkAutoCrop = async (rect, seedBoxPx = null) => {
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    const W = Math.max(1, Math.round(rect.width * dpr)), H = Math.max(1, Math.round(rect.height * dpr));
    const lc = document.createElement('canvas'); lc.width = W; lc.height = H;
    const lctx = lc.getContext('2d', { willReadFrequently: true });
    drawDrawingLayer(lctx, drawingPaths, W, H);
    const d = lctx.getImageData(0, 0, W, H).data;
    const on = new Uint8Array(W * H);
    let n = 0;
    for (let i = 0; i < W * H; i++) if (d[i * 4 + 3] > 40) { on[i] = 1; n++; }
    if (n < 20) throw new Error('nothing is drawn on the sheet');
    // Bridge gaps between strokes (separable box dilation), then label connected drawings.
    const r = Math.max(2, Math.round(6 * dpr));
    const tmp = new Uint8Array(W * H), dil = new Uint8Array(W * H);
    for (let y = 0; y < H; y++) {
      let run = 0; const row = y * W;
      for (let x = 0; x < W + r; x++) {
        if (x < W && on[row + x]) run = 2 * r + 1; else if (run > 0) run--;
        const tx = x - r; if (tx >= 0 && run > 0) tmp[row + tx] = 1;
      }
    }
    for (let x = 0; x < W; x++) {
      let run = 0;
      for (let y = 0; y < H + r; y++) {
        if (y < H && tmp[y * W + x]) run = 2 * r + 1; else if (run > 0) run--;
        const ty = y - r; if (ty >= 0 && run > 0) dil[ty * W + x] = 1;
      }
    }
    const label = new Int32Array(W * H);
    const queue = new Int32Array(W * H);
    let best = 0, bestInk = 0, next = 1;
    const comps = [];
    for (let i = 0; i < W * H; i++) {
      if (!dil[i] || label[i]) continue;
      const id = next++;
      let head = 0, tail = 0, ink = 0;
      queue[tail++] = i; label[i] = id;
      while (head < tail) {
        const q = queue[head++];
        if (on[q]) ink++;
        const x = q % W, y = (q - x) / W;
        if (x > 0     && dil[q - 1] && !label[q - 1]) { label[q - 1] = id; queue[tail++] = q - 1; }
        if (x < W - 1 && dil[q + 1] && !label[q + 1]) { label[q + 1] = id; queue[tail++] = q + 1; }
        if (y > 0     && dil[q - W] && !label[q - W]) { label[q - W] = id; queue[tail++] = q - W; }
        if (y < H - 1 && dil[q + W] && !label[q + W]) { label[q + W] = id; queue[tail++] = q + W; }
      }
      if (ink > bestInk) { bestInk = ink; best = id; }
      comps.push({ id, ink });
    }
    if (!best) throw new Error('no drawing was found');
    if (seedBoxPx) {
      // Pick whichever component overlaps the requested box the most, falling back to the
      // globally largest drawing if nothing under the box was found.
      const sx0 = seedBoxPx.x * dpr, sy0 = seedBoxPx.y * dpr;
      const sx1 = sx0 + seedBoxPx.w * dpr, sy1 = sy0 + seedBoxPx.h * dpr;
      const overlap = new Map();
      for (let y = Math.max(0, Math.floor(sy0)); y < Math.min(H, Math.ceil(sy1)); y++) {
        for (let x = Math.max(0, Math.floor(sx0)); x < Math.min(W, Math.ceil(sx1)); x++) {
          const id = label[y * W + x];
          if (id) overlap.set(id, (overlap.get(id) || 0) + 1);
        }
      }
      let seedBest = 0, seedBestN = 0;
      for (const [id, cnt] of overlap) if (cnt > seedBestN) { seedBestN = cnt; seedBest = id; }
      if (seedBest) best = seedBest;
    }
    const mc = document.createElement('canvas'); mc.width = W; mc.height = H;
    const mctx = mc.getContext('2d', { willReadFrequently: true });
    const md = mctx.createImageData(W, H);
    let x0 = W, y0 = H, x1 = -1, y1 = -1;
    for (let i = 0; i < W * H; i++) {
      // Gate on the connected-component label, not the >40 "on" threshold: `on` is deliberately
      // strict so faint stray pixels don't seed a bogus component, but the dilation above already
      // pulled every pixel touching this stroke -- including its softer antialiased rim -- into
      // the SAME labelled component. Copying the ORIGINAL alpha for every labelled pixel (rather
      // than stamping a hard 255) means the mask exactly cancels what was drawn, rim included, so
      // lifting/erasing a hand-drawn line leaves nothing faint behind, and the lifted copy keeps
      // the stroke's real antialiased edge instead of a jagged hard-edged silhouette.
      if (label[i] !== best) continue;
      const a = d[i * 4 + 3];
      if (!a) continue;
      md.data[i * 4] = md.data[i * 4 + 1] = md.data[i * 4 + 2] = 255; md.data[i * 4 + 3] = a;
      const x = i % W, y = (i - x) / W;
      if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
    }
    mctx.putImageData(md, 0, 0);
    smartCropMaskRef.current = {
      maskCanvas: mc, imgLeft: 0, imgTop: 0, rendW: rect.width, rendH: rect.height,
      targetImgId: null, inkOnly: true,
      inkBox: { x: x0 / dpr, y: y0 / dpr, w: (x1 - x0 + 1) / dpr, h: (y1 - y0 + 1) / dpr },
      inkBoxPx: { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 },
    };
    const pad = 2;
    setSelectionStart({ x: Math.max(1, x0 / dpr - pad),                     y: Math.max(1, y0 / dpr - pad) });
    setSelectionEnd  ({ x: Math.min(rect.width, (x1 + 1) / dpr + pad),      y: Math.min(rect.height, (y1 + 1) / dpr + pad) });
    smartDetectedRef.current = true;
    setSmartDetected(true);
    setIsSelecting(true);
    pendingAutoCutRef.current = true;
  };

  /**
   * ── Several objects, several layers ─────────────────────────────────────────────────────
   *
   * Auto Crop hands back one cut-out containing everything it decided was not background. On a
   * picture with three things standing on a table that is three things in one picture, which
   * cannot be moved apart, resized apart or deleted apart — the objects were identified and then
   * stapled together. This parts them.
   *
   * The work is done on the finished cut-out rather than inside the detector: a cut-out is
   * transparent everywhere that is not object, so the pieces are simply its connected runs of
   * opaque pixels, and nothing about how they were found needs to be re-decided here.
   *
   * Left alone when there is only one piece, which is most pictures. Specks are ignored — a
   * stray dozen pixels is not a thing anybody wants as a layer — and the count is capped, so a
   * mottled detection cannot scatter the sheet with fragments.
   */
  const AUTO_SPLIT_MAX = 6;
  const splitCutoutIntoPieces = async () => {
    // The cut-out that endSelection('copy') has just appended. Waited for rather than assumed:
    // it is added by a state update, and the ref that mirrors the list catches up on the next
    // render — read a tick too early and the cut-out is simply not there yet, which is what
    // "it found three objects and still gave me one layer" looked like.
    let parent = null;
    for (let tries = 0; tries < 30; tries++) {
      const list = canvasImagesRef.current;
      const last = list[list.length - 1];
      if (last && last.source === 'crop') { parent = last; break; }
      await new Promise(r => setTimeout(r, 30));
    }
    if (!parent) return;
    const el = await loadImgSafe(parent.url);
    const W = el?.naturalWidth || 0, H = el?.naturalHeight || 0;
    if (!W || !H || W * H > 36e6) return;                 // nothing to read, or too big to scan
    const c = document.createElement('canvas'); c.width = W; c.height = H;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(el, 0, 0);
    const data = ctx.getImageData(0, 0, W, H).data;

    // ── Where one object stops and the next begins ─────────────────────────────────────────
    // The detector parts touching objects with a seam two pixels wide, in its own sample. The
    // cut-out is then drawn at the picture's full size, smoothly, and a two-pixel gap comes out
    // as a soft dip in the alpha rather than a hole: its middle is still faintly opaque. Read at
    // "anything above 20", the dip joined everything back up, and the figures on a cake came
    // back welded to the cake (and to each other) in one piece.
    //
    // So the pieces are found on the SOLID part of the cut-out (alpha above half), where the
    // seams do part them, and then every soft pixel — the rim, the seam itself, a sliver too
    // small to be a piece — is handed to the piece it touches. Nothing that was cut out is lost.
    const SOLID = 128, SOFT = 20;
    const label = new Int32Array(W * H);
    const queue = new Int32Array(W * H);
    const cores = [];
    let id = 0, opaque = 0;
    for (let i = 0; i < W * H; i++) if (data[i * 4 + 3] > SOFT) opaque++;
    if (!opaque) return;
    for (let start = 0; start < W * H; start++) {
      if (label[start] || data[start * 4 + 3] <= SOLID) continue;
      id++;
      let head = 0, tail = 0;
      queue[tail++] = start; label[start] = id;
      while (head < tail) {
        const q = queue[head++]; const qx = q % W;
        if (qx > 0     && data[(q - 1) * 4 + 3] > SOLID && !label[q - 1]) { label[q - 1] = id; queue[tail++] = q - 1; }
        if (qx < W - 1 && data[(q + 1) * 4 + 3] > SOLID && !label[q + 1]) { label[q + 1] = id; queue[tail++] = q + 1; }
        if (q >= W     && data[(q - W) * 4 + 3] > SOLID && !label[q - W]) { label[q - W] = id; queue[tail++] = q - W; }
        if (q < W * (H - 1) && data[(q + W) * 4 + 3] > SOLID && !label[q + W]) { label[q + W] = id; queue[tail++] = q + W; }
      }
      cores.push({ id, n: tail });
    }
    // A piece worth having: a fiftieth of what was cut out, and at least a few hundred pixels.
    const floor = Math.max(300, opaque * 0.02);
    const kept = cores.filter(p => p.n >= floor).sort((a, b) => b.n - a.n).slice(0, AUTO_SPLIT_MAX);
    if (kept.length < 2) return;                          // one thing: leave it exactly as it is
    // Grow the kept pieces, all at once, over everything else that is not transparent.
    const keepId = new Int32Array(id + 1);
    kept.forEach((p, k) => { keepId[p.id] = k + 1; });
    const own = new Int32Array(W * H);
    let head = 0, tail = 0;
    for (let i = 0; i < W * H; i++) {
      const k = label[i] ? keepId[label[i]] : 0;
      if (k) { own[i] = k; queue[tail++] = i; }
    }
    while (head < tail) {
      const q = queue[head++]; const qx = q % W, k = own[q];
      if (qx > 0     && !own[q - 1] && data[(q - 1) * 4 + 3] > SOFT) { own[q - 1] = k; queue[tail++] = q - 1; }
      if (qx < W - 1 && !own[q + 1] && data[(q + 1) * 4 + 3] > SOFT) { own[q + 1] = k; queue[tail++] = q + 1; }
      if (q >= W     && !own[q - W] && data[(q - W) * 4 + 3] > SOFT) { own[q - W] = k; queue[tail++] = q - W; }
      if (q < W * (H - 1) && !own[q + W] && data[(q + W) * 4 + 3] > SOFT) { own[q + W] = k; queue[tail++] = q + W; }
    }
    const keep = kept.map((p, k) => ({ id: k + 1, x0: W, y0: H, x1: -1, y1: -1 }));
    for (let i = 0; i < W * H; i++) {
      const k = own[i];
      if (!k) continue;
      const b = keep[k - 1], x = i % W, y = (i - x) / W;
      if (x < b.x0) b.x0 = x; if (x > b.x1) b.x1 = x;
      if (y < b.y0) b.y0 = y; if (y > b.y1) b.y1 = y;
    }
    label.set(own);

    const pxPerPctX = W / ((parent.width / 21) * 100);
    const pxPerPctY = H / ((parent.height / 29.7) * 100);
    const made = [];
    for (const p of keep) {
      const pw = p.x1 - p.x0 + 1, ph = p.y1 - p.y0 + 1;
      const pc = document.createElement('canvas'); pc.width = pw; pc.height = ph;
      const pctx = pc.getContext('2d');
      const out = pctx.createImageData(pw, ph);
      for (let y = 0; y < ph; y++) {
        for (let x = 0; x < pw; x++) {
          const si = ((y + p.y0) * W + (x + p.x0)) * 4, di = (y * pw + x) * 4;
          if (label[(y + p.y0) * W + (x + p.x0)] !== p.id) continue;   // only THIS piece
          out.data[di] = data[si]; out.data[di + 1] = data[si + 1];
          out.data[di + 2] = data[si + 2]; out.data[di + 3] = data[si + 3];
        }
      }
      pctx.putImageData(out, 0, 0);
      made.push({
        id: Date.now() + made.length,
        layerIndex: ++layerCounterRef.current,
        url: pc.toDataURL('image/png'),
        name: 'canvas-crop', size: 'Canvas crop', source: 'crop', cutout: true,
        // Each piece keeps the place it occupied in the picture it came from, so the group
        // still reads as the scene it was — they are simply separate now.
        x: parent.x + p.x0 / pxPerPctX,
        y: parent.y + p.y0 / pxPerPctY,
        width:  parent.width  * (pw / W),
        height: parent.height * (ph / H),
        naturalWidth: pw, naturalHeight: ph,
      });
    }
    const before = canvasImagesRef.current;
    const after = [...before.filter(i => i.id !== parent.id), ...made];
    setCanvasImages(after); canvasImagesRef.current = after;
    pushImageHistory(before, after);
    setSelectedImage(made[made.length - 1].id);
    markDirty();
    flash(`${made.length} objects, each its own layer`);
  };

  const runAutoCrop = async () => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    if (selectedShapeId && !selectedImage) { flash('This figure is already a movable object — use Move to drag it'); return; }
    if (smartCropRunning) return;
    const hasInk = drawingPaths.some(p => p.type !== 'mask' && p.color !== 'eraser');
    const target = (selectedImage && canvasImages.find(i => i.id === selectedImage))
      || (hasInk ? null : [...canvasImages].sort((a, b) => (b.layerIndex ?? 1) - (a.layerIndex ?? 1))[0]);
    if (!target && !hasInk) { showAlert('Draw something or add an image first.'); return; }
    setAutoCropActive(true);
    if (!target) {
      setSmartCropRunning(true);
      setSelectionMode('smart');
      setIsSelecting(false);
      setSelectionPath([]);
      try { await runInkAutoCrop(rect); }
      catch (err) { showAlert('Auto crop: ' + (err?.message || err)); setSelectionMode('rectangle'); setAutoCropActive(false); }
      finally { setSmartCropRunning(false); }
      return;
    }
    setSelectionMode('smart');
    setIsSelecting(false);
    setSelectionPath([]);
    // Auto Crop has no user-drawn click point to seed detection from. Rather than the old
    // flat "AI cutout, then take its opaque bounding box" approach -- which produced a plain
    // rectangle whenever the cutout wasn't cleanly transparent -- hand off to the same
    // silhouette-tracing pipeline Smart Crop uses (background flood-fill / AI matting, hole
    // fill, contour trace), seeded at the image's own centre since that's where a single
    // subject sits in the vast majority of photos. This is what makes the crop -- and the
    // preview outline shown while detecting -- hug the object's actual edge.
    smartDetectedRef.current = false;
    setSmartDetected(false);
    smartCropMaskRef.current = null;
    try {
      const imgLeft = (target.x / 100) * rect.width, imgTop = (target.y / 100) * rect.height;
      const rendW = (target.width / 21) * rect.width, rendH = (target.height / 29.7) * rect.height;
      // A real border round whatever it finds. Auto Crop is aimed by nobody, so cutting flush
      // to the traced outline gives a subject with its edges shaved — which is what "no border"
      // meant. Three and a half per cent of the object either side is enough to read as a
      // deliberate margin without floating the subject in space.
      // fromAuto also tells the detector that the "tap" at the centre is a guess rather than
      // someone pointing at something — so it must not use it to decide the matte's polarity.
      cropOptsRef.current = { padFrac: 0.035, fromAuto: true };
      await runSmartCrop(imgLeft + rendW / 2, imgTop + rendH / 2);
      cropOptsRef.current = { padFrac: 0.01 };
      const m = smartCropMaskRef.current;
      if (!smartDetectedRef.current || !m) {
        throw new Error('no clearly separate object was found — use Smart or Freeform crop instead');
      }
      // ── Is what it found actually an object? ──────────────────────────────────────────
      // Seeded at the middle of the picture with nobody pointing at anything, the colour test
      // will happily settle on a patch of cheek, a patch of sky, a slightly different square of
      // wall — and hand back a crop taken from the middle of the picture that contains none of
      // the subject. That is the "crops the image within the image" complaint, and it is worth
      // refusing rather than doing: Smart Crop, where a finger says what to look at, is one tap
      // away and gets it right.
      //
      // Two ways of being wrong, and both are cheap to spot. Too small: under a twelfth of the
      // picture is a detail, not a subject. Too ragged: a shape filling less than a third of
      // its own bounding box is not one thing, it is a scatter of same-coloured pieces.
      if (m.fgFrac < 0.085) {
        throw new Error('what it found is too small to be the subject — try Smart crop and tap the object');
      }
      if (m.boxFill < 0.22) {
        throw new Error('no single object stood out — try Smart crop and tap the object');
      }
      // ── "It found everything" is about PIXELS, not about the box ────────────────────────
      // This used to refuse when the object's bounding BOX covered most of the picture. That is
      // not evidence of anything: photograph a cake so it fills the frame — which is how anyone
      // photographs a cake — and its box legitimately runs corner to corner. Perfectly good
      // detections were turned away with "could not tell the subject from the background",
      // which was both wrong and insulting to a picture that could not be clearer.
      //
      // A detection that genuinely collapsed marks nearly every PIXEL as object, not merely a
      // large box around them. A real subject, however big, leaves some backdrop showing; at
      // 97% there is none left, and there was nothing to find. A cut-out is exempt: there,
      // every opaque pixel being the object is the correct answer rather than a failure.
      // Measured on the biggest single object, and at a level a real subject does not reach.
      // Without a proper cut-out to work from, the colour test on a PHOTOGRAPH marks the person
      // and the hallway behind them as one thing, because they are a continuum of tone — and
      // what came back was the whole photograph again with a speck beside it, offered as though
      // something had been found. A drawing or a product on a plain ground is unaffected: there
      // the colour test finds the object and leaves the ground, which is what it is for.
      const _whole = m.leadFrac ?? m.fgFrac;
      if (_whole >= 0.92 && !m.hadAlpha) {
        throw new Error('could not tell the subject from the background — try Smart crop and draw round it');
      }
      // ── No method, no answer ──────────────────────────────────────────────────────────
      // Two things can separate an object here. The cut-out service, which works on anything —
      // and the local colour test, which needs a backdrop that is actually one colour: a drawing
      // on white, a product on a plain sheet. On a PHOTOGRAPHED SCENE it has nothing to go on,
      // because a face and the wall behind it are a continuum of tone, and what it returns is
      // slabs of the picture. Auto crop was then cutting those slabs into four "objects" and
      // presenting them as a result. When the service has not answered and the backdrop is not
      // flat, there is no method in the building, and saying so is the only honest output.
      if (!m.aiUsed && !m.hadAlpha && (m.bgFlat ?? 0) < 0.8) {
        throw new Error(m.aiWhy
          ? `the cut-out service could not do it — ${m.aiWhy}`
          : 'this needs the cut-out service, which did not answer — try Smart crop and draw round it');
      }
      pendingAutoCutRef.current = true;
    } catch (err) {
      cropOptsRef.current = { padFrac: 0.01 };
      showAlert('Auto crop: ' + (err?.message || err));
      setSelectionMode('rectangle');
      setAutoCropActive(false);
      setSmartCropRunning(false);
      smartDetectedRef.current = false;
      setSmartDetected(false);
      setIsSelecting(false);
      setSelectionPath([]);
    }
  };
  useEffect(() => {
    if (!pendingAutoCutRef.current || !isSelecting || selectionMode !== 'smart' || !smartCropMaskRef.current) return;
    pendingAutoCutRef.current = false;
    const _autoSplit = true;
    // ── Copy, not cut ────────────────────────────────────────────────────────────────────
    // Auto Crop used to LIFT the object: you got your cut-out, and the picture you ran it on
    // was left with a hole in it, filled with a guess at the colour behind. That is a
    // destructive edit performed by a button whose promise is only "find me the object", and
    // when the detection is wrong — which, aimed by nobody, it sometimes is — it damages the
    // original to produce a cut-out of the wrong thing. The picture it was run on is now left
    // exactly as it was, and the object arrives beside it.
    (async () => {
      await endSelection('copy');
      // Several things standing on the same table are several answers, not one — part them.
      if (_autoSplit) { try { await splitCutoutIntoPieces(); } catch (e) { console.warn('Auto crop: could not part the pieces.', e?.message || e); } }
      // ...and crop puts itself down afterwards. Finishing a crop and being left inside the
      // crop tool, with the thing you just made selected, is a tool that will not end.
      setActiveTool(null);
      setAutoCropActive(false);
      setSelectionMode('rectangle');
      setIsSelecting(false);
      setSelectionPath([]);
      setSelectionTouchPos(null);
      smartCropMaskRef.current = null;
      smartDetectedRef.current = false;
      setSmartDetected(false);
      setCanvasCursor('default');
      // The control section is deliberately left alone: the new object is selected, and its
      // own controls are what should be on screen now.
    })();
  }, [isSelecting, selectionStart, selectionEnd, selectionMode]);

  /**
   * One press, one copy.
   *
   * This is slow — it composites the sheet at print resolution, applies the mask and encodes a
   * PNG — and for all of that time it is still armed. React attaches touch listeners passively,
   * so a tap on a phone arrives as a touch AND as the mouse event the browser synthesises after
   * it; the effect that finishes an Auto Crop can land in the same window; and a second press
   * on a button that has not visibly finished anything yet is the natural thing to do. Every one
   * of those produced another copy of the same crop, stacked on the last.
   *
   * The latch is a ref rather than state because it has to be true for the NEXT event in the
   * same tick, not after the next render.
   */
  const endSelectionBusyRef = useRef(false);
  const cropDoneAtRef = useRef(0);
  const endSelection = async (mode = 'copy') => {
    // Busy covers a second call arriving DURING the first. It is not enough on its own: a tap
    // on a phone produces a touch and then, up to a third of a second later, a mouse click the
    // browser synthesises from it — and that gap is long enough for the first copy to have
    // finished. The state that would have refused the second one (isSelecting, and the mask)
    // is cleared by setState at the end of a copy, which does not take effect until the next
    // render, so the late click still sees a live selection and makes another copy of it.
    //
    // So: nothing within a moment of the last one. Two DISTINCT crops cannot happen that fast —
    // the second needs a fresh detection, which takes seconds — so this can only ever catch
    // the echo.
    if (endSelectionBusyRef.current) return;
    if (Date.now() - cropDoneAtRef.current < 700) return;
    endSelectionBusyRef.current = true;
    try {
      return await endSelectionInner(mode);
    } finally {
      endSelectionBusyRef.current = false;
      cropDoneAtRef.current = Date.now();
    }
  };
  // Handle ending selection and crop image.
  // mode = 'copy' (default): keep originals, add a new layer with the selected pixels.
  // mode = 'cut'           : add the new layer AND erase the selected area from the
  //                          underlying image(s) (clipboard-style cut).
  const endSelectionInner = async (mode = 'copy') => {
    if ((!isSelecting && !(selectionMode === 'freeform' && selectionPath.length >= 3)) || !selectionMode || (canvasImages.length === 0 && drawingPaths.length === 0 && canvasShapes.length === 0)) {
      setIsSelecting(false);
      setSelectionPath([]);
      return;
    }

    try {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();

      // Get selection bounds
      let minX, minY, maxX, maxY;

      if (selectionMode === 'rectangle' || selectionMode === 'circle' || selectionMode === 'smart') {
        if (!selectionStart.x || !selectionStart.y || !selectionEnd.x || !selectionEnd.y) {
          setIsSelecting(false);
          setSelectionPath([]);
          return;
        }
        minX = Math.min(selectionStart.x, selectionEnd.x);
        minY = Math.min(selectionStart.y, selectionEnd.y);
        maxX = Math.max(selectionStart.x, selectionEnd.x);
        maxY = Math.max(selectionStart.y, selectionEnd.y);
      } else if (selectionMode === 'freeform') {
        if (selectionPath.length < 3) {
          setIsSelecting(false);
          setSelectionPath([]);
          return;
        }
        minX = Math.min(...selectionPath.map(p => p.x));
        minY = Math.min(...selectionPath.map(p => p.y));
        maxX = Math.max(...selectionPath.map(p => p.x));
        maxY = Math.max(...selectionPath.map(p => p.y));
      } else {
        setIsSelecting(false);
        setSelectionPath([]);
        return;
      }

      const width  = maxX - minX;
      const height = maxY - minY;

      // Smart-mode boxes (object/ink detection) are already validated by their own pixel-count
      // checks -- a thin straight line or a slim object is a legitimate, deliberately narrow
      // selection there, not an accidental tiny drag.
      if (selectionMode !== 'smart' && (width < 10 || height < 10)) {
        showAlert('Selection too small. Please select a larger area.');
        setIsSelecting(false);
        setSelectionPath([]);
        return;
      }

      // --- Render the selected region of the sheet to an offscreen canvas at NATIVE detail ---
      // The working density is the sharpest image under the selection (its own pixels per
      // screen pixel), never below 150 dpi of the paper, so a crop keeps the original
      // resolution — it used to be a fixed 150 dpi, which downsampled every photo it touched.
      // Only the selection's box is allocated; drawing happens in sheet coordinates through a
      // translated context, so nothing below needs to know.
      //
      // ── One density per axis, not one for both ──────────────────────────────────────────
      // A picture on the sheet is not always sitting at its own proportions: a box dragged by
      // a corner, a size read out of a saved file, a piece from an earlier crop — any of them
      // can leave a layer a percent or so taller than its pixels. That is fine on screen, it
      // is what the sheet shows. But the working density used to be taken from the WIDTHS
      // alone and then used for both axes, so that picture was laid down 1811 pixels tall when
      // it owns 1788 — a one per cent vertical rescale, which the snap below (deliberately
      // ±1 pixel, so neighbouring pictures cannot drift apart) could not rescue.
      //
      // One per cent is not a small loss. Bilinear resampling does not care how far off the
      // grid it is: the same measurement that justified rounding the edges — 427 sharp, 200
      // resampled — applies to a rescale of a hundredth exactly as it does to a rescale of a
      // half. So every crop of such a picture came back soft, and a piece cropped from a piece
      // came back softer, which is the "the crop is duller than the original" complaint.
      //
      // Asking the heights their own question costs nothing and makes the ordinary case — one
      // picture, any selection — a true copy in BOTH directions. The output's pixels then
      // carry the same slight stretch the layer already had, and its box is the selection, so
      // what comes back looks exactly like what was on screen. Sharper, not different.
      const _ps = PRINT_SIZES[printSize];
      let cropScaleX = (150 / 2.54) * _ps.width  / rect.width;
      let cropScaleY = (150 / 2.54) * _ps.height / rect.height;
      for (const img of canvasImages) {
        const ix = (img.x / 100) * rect.width, iy = (img.y / 100) * rect.height;
        const iw = (img.width / 21) * rect.width, ih = (img.height / 29.7) * rect.height;
        if (ix > maxX || ix + iw < minX || iy > maxY || iy + ih < minY) continue;   // not under the selection
        if (img.naturalWidth  && iw > 0) cropScaleX = Math.max(cropScaleX, img.naturalWidth  / iw);
        if (img.naturalHeight && ih > 0) cropScaleY = Math.max(cropScaleY, img.naturalHeight / ih);
      }
      // ≤ 30 MP allocation. Both axes come down together so the pair keeps its ratio — pulling
      // only one in would reintroduce exactly the stretch this is here to avoid.
      const _over = Math.sqrt((width * cropScaleX * height * cropScaleY) / 30e6);
      if (_over > 1) { cropScaleX /= _over; cropScaleY /= _over; }
      const hiResW = rect.width  * cropScaleX;
      const hiResH = rect.height * cropScaleY;
      const hiResScaleX = cropScaleX;
      const hiResScaleY = cropScaleY;
      const cropX = Math.round(minX * cropScaleX);
      const cropY = Math.round(minY * cropScaleY);
      const cropW = Math.max(1, Math.round(width  * cropScaleX));
      const cropH = Math.max(1, Math.round(height * cropScaleY));

      const offCanvas = document.createElement('canvas');
      offCanvas.width  = cropW;
      offCanvas.height = cropH;
      const offCtx = offCanvas.getContext('2d');
      offCtx.imageSmoothingEnabled = true;
      offCtx.imageSmoothingQuality = 'high';
      offCtx.translate(-cropX, -cropY);

      // IMPORTANT: do NOT fill a background colour. Keeping the offscreen canvas
      // transparent means the crop only captures actual image/drawing/shape pixels.
      // When the cropped piece is placed back on a same-coloured canvas, the live
      // canvas shows through transparent areas — no baked-in white rectangle that
      // would otherwise look "raised" against the live background.

      // Pictures AND figures, in one pile, in the order the screen stacks them — see
      // layersInOrder. Drawing every picture and then every figure is what made a crop of a
      // cut-out sitting on a coloured square come back as the bare square.
      for (const _layer of layersInOrder(canvasImages, canvasShapes)) {
        if (_layer.kind === 'shape') { drawShapesOnCanvas(offCtx, [_layer.item], hiResW, hiResH); continue; }
        const img = _layer.item;
        const image = await loadImgForOutput(img.url);
        if (!image) continue;
        // ── Land on whole pixels ──────────────────────────────────────────────────────
        // The working density above is chosen so a photograph comes out at its own
        // resolution — and it does; the crop of a 1600px picture really is 1600px wide.
        // It was still visibly softer than the original, and the reason is not the size at
        // all: these four numbers are floating point, so the picture was being laid down a
        // third of a pixel off the grid. A destination that is not pixel-aligned makes the
        // browser resample the WHOLE image bilinearly, at 1:1, for nothing.
        //
        // Measured on a two-pixel checkerboard: 427 sharp drawn on the grid, 200 drawn at
        // (0.37, 0.21) — the same 200 the crop was producing. Rounding the edges (not the
        // width, so neighbouring pictures cannot drift apart) moves content by at most half
        // a pixel and gives back every bit of that detail.
        const _l = (img.x / 100) * hiResW;
        const _t = (img.y / 100) * hiResH;
        const _r = _l + (img.width  / 21)   * hiResW;
        const _b = _t + (img.height / 29.7) * hiResH;
        let   imgXPx      = Math.round(_l);
        let   imgYPx      = Math.round(_t);
        let   imgWidthPx  = Math.max(1, Math.round(_r) - imgXPx);
        let   imgHeightPx = Math.max(1, Math.round(_b) - imgYPx);
        // …and when that lands near the picture's own size, use its own size exactly.
        //
        // ── Why "near" and not "within a pixel" ───────────────────────────────────────────
        // This used to snap only on a difference of one pixel, on the assumption that a bigger
        // gap meant a real resize. Measured, that assumption is expensive. Drawing a 1788-pixel
        // picture into 1789 keeps 49% of its detail; into 1796 (half a per cent), 60%; into
        // 1812 (one and a third per cent), 61%. In other words the cost is not proportional to
        // the mismatch at all — ANY mismatch is a full bilinear resample and takes about 40% of
        // the detail with it, and a mismatch of one pixel is no cheaper than a mismatch of
        // twenty-four. (Native, for comparison: 100%.)
        //
        // And mismatches of a per cent or so are ordinary. A layer's box drifts from its pixel
        // proportions whenever a corner is dragged, whenever a size is read out of a saved
        // file, whenever a piece is cut from a piece. Those are exactly the pictures people
        // crop, and every one of them was coming back soft.
        //
        // So the tolerance is 2% of the picture's own size (and never more than 64 pixels,
        // so a very large image cannot be moved far). Inside that, the picture is laid down at
        // its own size and the difference is split between the two edges, which puts the
        // content at most 1% out of place — under a pixel on screen at any normal density —
        // and gives back the 40%.
        const _nw = image.naturalWidth || image.width, _nh = image.naturalHeight || image.height;
        const _snap = (have, want) => (want && Math.abs(have - want) <= Math.min(64, want * 0.02));
        if (_snap(imgWidthPx,  _nw)) { imgXPx -= Math.round((_nw - imgWidthPx)  / 2); imgWidthPx  = _nw; }
        if (_snap(imgHeightPx, _nh)) { imgYPx -= Math.round((_nh - imgHeightPx) / 2); imgHeightPx = _nh; }
        const rot = ((img.rotation || 0) * Math.PI) / 180;
        if (rot) {
          const cx = imgXPx + imgWidthPx / 2;
          const cy = imgYPx + imgHeightPx / 2;
          offCtx.save();
          offCtx.translate(cx, cy);
          offCtx.rotate(rot);
          if (img.cropShape === 'circle') {
            offCtx.beginPath();
            offCtx.ellipse(0, 0, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2);
            offCtx.clip();
          }
          offCtx.drawImage(image, -imgWidthPx / 2, -imgHeightPx / 2, imgWidthPx, imgHeightPx);
          offCtx.restore();
        } else if (img.cropShape === 'circle') {
          offCtx.save();
          offCtx.beginPath();
          offCtx.ellipse(imgXPx + imgWidthPx / 2, imgYPx + imgHeightPx / 2, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2);
          offCtx.clip();
          offCtx.drawImage(image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
          offCtx.restore();
        } else {
          offCtx.drawImage(image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
        }
      }

      // Hand-drawn strokes last. They were already above every picture; they are now above the
      // figures too, which is the one ordering this change moves. A pen stroke is the most
      // deliberate mark on the sheet, so when it and a decoration disagree the stroke wins.
      drawDrawingLayer(offCtx, drawingPaths, cropW, cropH,
        (minX / rect.width) * 100, (minY / rect.height) * 100, (width / rect.width) * 100, (height / rect.height) * 100);

      // --- Extract the selected region into a crop canvas (at hi-res coords) ---
      const cropCanvas = document.createElement('canvas');
      cropCanvas.width  = cropW;
      cropCanvas.height = cropH;
      const cropCtx = cropCanvas.getContext('2d');
      cropCtx.imageSmoothingEnabled = true;
      cropCtx.imageSmoothingQuality = 'high';

      if (selectionMode === 'circle') {
        cropCtx.save();
        cropCtx.beginPath();
        cropCtx.ellipse(cropW / 2, cropH / 2, cropW / 2, cropH / 2, 0, 0, Math.PI * 2);
        cropCtx.clip();
      }
      if (selectionMode === 'freeform' && selectionPath.length >= 3) {
        cropCtx.save();
        cropCtx.beginPath();
        const scaledPath = selectionPath.map(pt => ({
          x: (pt.x - minX) * hiResScaleX,
          y: (pt.y - minY) * hiResScaleY,
        }));
        cropCtx.moveTo(scaledPath[0].x, scaledPath[0].y);
        for (let k = 1; k < scaledPath.length; k++) cropCtx.lineTo(scaledPath[k].x, scaledPath[k].y);
        cropCtx.closePath();
        cropCtx.clip();
      }
      // Note: smart crop uses pixel-level mask (destination-in) below instead of a polygon clip.
      // A polygon clip only captures left/right extremes per row and incorrectly includes
      // concave interior gaps (e.g. armpit area between arm and body).

      cropCtx.drawImage(offCanvas, 0, 0);

      if (selectionMode === 'circle' || selectionMode === 'freeform') {
        cropCtx.restore();
      }

      // Smart crop: apply pixel-level foreground mask so only the detected object pixels
      // remain (transparent elsewhere). This handles concave shapes correctly.
      if (selectionMode === 'smart' && smartCropMaskRef.current) {
        const { maskCanvas: mc, imgLeft: il, imgTop: it, rendW: rw, rendH: rh } = smartCropMaskRef.current;
        const mX = (il - minX) * hiResScaleX;
        const mY = (it - minY) * hiResScaleY;
        const mW = rw * hiResScaleX;
        const mH = rh * hiResScaleY;
        cropCtx.globalCompositeOperation = 'destination-in';
        cropCtx.drawImage(mc, mX, mY, mW, mH);
        cropCtx.globalCompositeOperation = 'source-over';
      }

      const croppedDataUrl = cropCanvas.toDataURL('image/png');

      const newImage = {
        id: Date.now(),
        layerIndex: ++layerCounterRef.current,
        url: croppedDataUrl,
        name: 'canvas-crop',
        size: 'Canvas crop',
        x: (minX / rect.width)  * 100,
        y: (minY / rect.height) * 100,
        width:  (width  / rect.width)  * 21,
        height: (height / rect.height) * 29.7,
        source: 'crop',
        // Anything but a straight rectangle comes away with transparent edges — it is the SHAPE
        // of the thing, and must not be given a rectangle back (see .is-cutout in the CSS).
        cutout: selectionMode !== 'rectangle',
        cropShape: selectionMode === 'circle' ? 'circle' : selectionMode === 'freeform' ? 'freeform' : undefined,
        naturalWidth:  cropW,
        naturalHeight: cropH,
      };

      // For 'cut' mode: also erase the selected area from each underlying image so
      // the new piece behaves like a clipboard cut (the source has a transparent hole).
      // The new piece itself is still added on top.
      let baseImages = canvasImagesRef.current;
      let inkMaskPath = null;
      if (mode === 'cut') {
        // Build a polygon (or use the smart mask) representing the selection in canvas-CSS coords.
        let canvasPolygon = null;
        let smartTargetId = null;
        if (selectionMode === 'rectangle') {
          canvasPolygon = [
            { x: minX, y: minY }, { x: maxX, y: minY },
            { x: maxX, y: maxY }, { x: minX, y: maxY },
          ];
        } else if (selectionMode === 'circle') {
          const ccx = (selectionStart.x + selectionEnd.x) / 2;
          const ccy = (selectionStart.y + selectionEnd.y) / 2;
          const crx = Math.abs(selectionEnd.x - selectionStart.x) / 2;
          const cry = Math.abs(selectionEnd.y - selectionStart.y) / 2;
          const N = 64;
          canvasPolygon = [];
          for (let i = 0; i < N; i++) {
            const a = (i / N) * Math.PI * 2;
            canvasPolygon.push({ x: ccx + crx * Math.cos(a), y: ccy + cry * Math.sin(a) });
          }
        } else if (selectionMode === 'freeform' && selectionPath.length >= 3) {
          canvasPolygon = selectionPath;
        } else if (selectionMode === 'smart' && smartCropMaskRef.current) {
          smartTargetId = smartCropMaskRef.current.targetImgId;
        }

        // Polygon bbox for cheap intersection test
        let pbMinX = Infinity, pbMinY = Infinity, pbMaxX = -Infinity, pbMaxY = -Infinity;
        if (canvasPolygon) {
          for (const p of canvasPolygon) {
            if (p.x < pbMinX) pbMinX = p.x;
            if (p.x > pbMaxX) pbMaxX = p.x;
            if (p.y < pbMinY) pbMinY = p.y;
            if (p.y > pbMaxY) pbMaxY = p.y;
          }
        }

        const updates = new Map(); // id -> new url
        const smartMask = smartCropMaskRef.current; // capture before reset
        if (selectionMode === 'smart' && smartMask?.inkOnly) {
          // The object came off the drawing layer: erase exactly those pixels there.
          const { maskCanvas, inkBox, inkBoxPx } = smartMask;
          const pc = document.createElement('canvas'); pc.width = inkBoxPx.w; pc.height = inkBoxPx.h;
          const pctx = pc.getContext('2d', { willReadFrequently: true });
          pctx.drawImage(maskCanvas, inkBoxPx.x, inkBoxPx.y, inkBoxPx.w, inkBoxPx.h, 0, 0, inkBoxPx.w, inkBoxPx.h);
          // destination-out leaves dst x (1 - a). Erasing a half-transparent antialiased rim
          // pixel with its OWN half alpha therefore leaves a x (1 - a) = 25% of it behind --
          // that is the faint pencil ghost that survived lifting a drawing and could not be
          // rubbed out afterwards, because every later eraser pass left its own rim the same
          // way. The lifted COPY still wants the soft alpha (it is composed from maskCanvas
          // itself, further up), but the ERASE pass has to be fully opaque wherever there is
          // any ink at all, so the stroke goes completely.
          const pd = pctx.getImageData(0, 0, pc.width, pc.height);
          for (let i = 3; i < pd.data.length; i += 4) if (pd.data[i] > 0) pd.data[i] = 255;
          pctx.putImageData(pd, 0, 0);
          const maskUrl = pc.toDataURL('image/png');
          await new Promise(res => { const im = new Image(); im.onload = () => { _maskImgCache.set(maskUrl, im); res(); }; im.onerror = res; im.src = maskUrl; });
          const maskPath = { id: Date.now() + 1, type: 'mask', mask: maskUrl, x: inkBox.x, y: inkBox.y, w: inkBox.w, h: inkBox.h, path: [], canvasW: rect.width, canvasH: rect.height };
          setDrawingPaths(prev => [...prev, maskPath]);
          inkMaskPath = maskPath; // undone/redone together with the new layer (see below)
        } else if (canvasPolygon && drawingPaths.length > 0) {
          // Rectangle / circle / freeform selections had NO awareness of the ink layer at all:
          // cutting or deleting a selection over hand-drawn strokes only ever erased image
          // pixels underneath -- never the ink itself, which lives on its own layer that
          // always renders on top -- so the strokes stayed fully visible after the "removal",
          // the trace that never went away. Erase the selected region from the ink layer too,
          // the same destination-out mask technique smart-select's ink case already uses above.
          const mw = Math.max(1, Math.ceil(width));
          const mh = Math.max(1, Math.ceil(height));
          const pc = document.createElement('canvas');
          pc.width = mw; pc.height = mh;
          const pctx = pc.getContext('2d');
          pctx.beginPath();
          pctx.moveTo(canvasPolygon[0].x - minX, canvasPolygon[0].y - minY);
          for (let i = 1; i < canvasPolygon.length; i++) pctx.lineTo(canvasPolygon[i].x - minX, canvasPolygon[i].y - minY);
          pctx.closePath();
          pctx.fillStyle = '#fff';
          pctx.fill();
          // Same reasoning as the ink-lift mask above: the polygon's own antialiased border
          // would otherwise leave a faint outline of the cut behind on the drawing layer.
          {
            const pd2 = pctx.getImageData(0, 0, pc.width, pc.height);
            for (let i = 3; i < pd2.data.length; i += 4) if (pd2.data[i] > 0) pd2.data[i] = 255;
            pctx.putImageData(pd2, 0, 0);
          }
          const maskUrl = pc.toDataURL('image/png');
          await new Promise(res => { const im = new Image(); im.onload = () => { _maskImgCache.set(maskUrl, im); res(); }; im.onerror = res; im.src = maskUrl; });
          const maskPath = { id: Date.now() + 1, type: 'mask', mask: maskUrl, x: minX, y: minY, w: width, h: height, path: [], canvasW: rect.width, canvasH: rect.height };
          setDrawingPaths(prev => [...prev, maskPath]);
          inkMaskPath = maskPath;
        }
        for (const img of baseImages) {
          if (smartMask?.inkOnly) break;
          if (smartTargetId != null && img.id !== smartTargetId) continue;

          const imgLeftPx   = (img.x      / 100)  * rect.width;
          const imgTopPx    = (img.y      / 100)  * rect.height;
          const imgWidthPx  = (img.width  / 21)   * rect.width;
          const imgHeightPx = (img.height / 29.7) * rect.height;

          if (canvasPolygon) {
            if (pbMaxX < imgLeftPx || pbMinX > imgLeftPx + imgWidthPx ||
                pbMaxY < imgTopPx  || pbMinY > imgTopPx + imgHeightPx) continue;
          }

          const imgEl = await loadImgSafe(img.url);
          if (!imgEl) continue;
          const naturalW = imgEl.naturalWidth || imgEl.width;
          const naturalH = imgEl.naturalHeight || imgEl.height;
          if (!naturalW || !naturalH) continue;

          const oc = document.createElement('canvas');
          oc.width = naturalW;
          oc.height = naturalH;
          const ecx = oc.getContext('2d');
          ecx.imageSmoothingEnabled = true;
          ecx.imageSmoothingQuality = 'high';
          ecx.drawImage(imgEl, 0, 0);

          const rot = ((img.rotation || 0) * Math.PI) / 180;
          const sin = Math.sin(-rot), cos = Math.cos(-rot);
          const cxImg = imgLeftPx + imgWidthPx / 2;
          const cyImg = imgTopPx + imgHeightPx / 2;
          const sx = naturalW / imgWidthPx;
          const sy = naturalH / imgHeightPx;

          ecx.globalCompositeOperation = 'destination-out';

          if (selectionMode === 'smart' && smartMask) {
            const { maskCanvas, imgLeft: il, imgTop: it, rendW, rendH, bgColor } = smartMask;
            const place = (c) => {
              c.save();
              c.translate(naturalW / 2, naturalH / 2);
              c.scale(sx, sy);
              c.rotate(-rot);
              c.translate(-cxImg, -cyImg);
              c.drawImage(maskCanvas, il, it, rendW, rendH);
              c.restore();
            };
            if (bgColor) {
              // The thing stood on a backdrop of one colour, so lifting it off leaves that
              // colour behind — which is what was actually behind it. A transparent hole in the
              // shape of the object was the tell-tale that something had been taken away, and
              // on a white sheet it did not even look like a hole: it looked like the object had
              // been bleached. The silhouette is cut to shape first, filled with the backdrop's
              // colour, and laid back into the gap.
              const patch = document.createElement('canvas');
              patch.width = naturalW; patch.height = naturalH;
              const pcx = patch.getContext('2d');
              place(pcx);
              pcx.globalCompositeOperation = 'source-in';
              pcx.fillStyle = bgColor;
              pcx.fillRect(0, 0, naturalW, naturalH);
              ecx.globalCompositeOperation = 'source-over';
              ecx.drawImage(patch, 0, 0);
            } else {
              place(ecx);
            }
          } else if (canvasPolygon && canvasPolygon.length >= 3) {
            const localPoly = canvasPolygon.map((p) => {
              const dx = p.x - cxImg, dy = p.y - cyImg;
              const ux = dx * cos - dy * sin;
              const uy = dx * sin + dy * cos;
              const ix = ux + imgWidthPx / 2;
              const iy = uy + imgHeightPx / 2;
              return { x: ix * sx, y: iy * sy };
            });
            ecx.beginPath();
            ecx.moveTo(localPoly[0].x, localPoly[0].y);
            for (let i = 1; i < localPoly.length; i++) ecx.lineTo(localPoly[i].x, localPoly[i].y);
            ecx.closePath();
            ecx.fill();
          }

          ecx.globalCompositeOperation = 'source-over';
          updates.set(img.id, oc.toDataURL('image/png'));
        }

        if (updates.size > 0) {
          baseImages = baseImages.map(im => updates.has(im.id)
            ? rebased(im, updates.get(im.id))
            : im);
        }
      }

      const nextImages = [...baseImages, newImage];
      if (inkMaskPath) {
        markDirty();
        const nh = drawingHistoryRef.current.slice(0, historyIndexRef.current + 1);
        nh.push({ type: 'image', before: canvasImagesRef.current, after: nextImages, maskPath: inkMaskPath });
        setDrawingHistory(nh);
        setHistoryIndex(nh.length - 1);
      } else {
        pushImageHistory(canvasImagesRef.current, nextImages);
      }
      setCanvasImages(nextImages);
      setSelectedImage(newImage.id);
      setResizeWidth(newImage.width);
      setResizeHeight(newImage.height);

      setIsSelecting(false);
      setSelectionPath([]);
      setSelectionStart({ x: 0, y: 0 });
      setSelectionEnd({ x: 0, y: 0 });
      setSelectionMode('rectangle');
      smartDetectedRef.current = false;
      smartCropMaskRef.current = null;
      setSmartDetected(false);
      setAutoCropActive(false);
      setActiveTool(null);
      setCurrentControlSection(null);
      setShowTopControls(false);
      setImageSelectionMode(false);
      setCanvasCursor('pointer');

    } catch (error) {
      console.error('Error during canvas crop:', error);
      showAlert('Failed to crop. Please try again.');
      setIsSelecting(false);
      setSelectionPath([]);
      setSelectionStart({ x: 0, y: 0 });
      setSelectionEnd({ x: 0, y: 0 });
      smartDetectedRef.current = false;
      smartCropMaskRef.current = null;
      setAutoCropActive(false);
      setSmartDetected(false);
    }
  };

  // Clipboard-style cut: keep the selected piece as a new image AND erase it from the
  // underlying source(s). Implementation lives inside endSelection for state coherence.
  const cutSelection = () => endSelection('cut');

  // Help panel: hand the app's public link to whatever the platform does best -- the OS
  // share sheet when one exists, the clipboard otherwise -- rather than only ever doing one.
  const shareAppLink = async () => {
    try {
      const res = await Share.share({ title: 'PCake', text: 'Try PCake', url: APP_SHARE_URL });
      if (res?.activityType === 'cancelled') return;
      if (res?.activityType === 'web-share') return;
    } catch { /* fall through to copy */ }
    await copyAppLink();
  };
  const copyAppLink = async () => {
    try {
      await navigator.clipboard.writeText(APP_SHARE_URL);
      setHelpLinkCopied(true);
      setTimeout(() => setHelpLinkCopied(false), 2000);
    } catch {
      showAlert('Could not copy the link -- select and copy it manually.');
    }
  };

  // Clear canvas: the first tap warns (the way Close does with unsaved work) and the broom
  // blinks; a second tap within a few seconds clears. Touching the sheet or Esc cancels.
  // Drag one node of a freeform figure. Points are fractions of the figure's box; when the
  // drag ends the box is refitted around the new outline and the change goes into history.
  const startNodeDrag = (e, shapeId, index) => {
    e.stopPropagation();
    if (e.cancelable) e.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const before = canvasShapesRef.current;
    nodeDragRef.current = { shapeId, index };
    const move = (ev) => {
      const t = ev.touches ? ev.touches[0] : ev;
      const xPct = ((t.clientX - rect.left) / rect.width) * 100, yPct = ((t.clientY - rect.top) / rect.height) * 100;
      setCanvasShapes(prev => {
        const next = prev.map(sh => {
          if (sh.id !== shapeId) return sh;
          const bx = Math.min(sh.x1, sh.x2), by = Math.min(sh.y1, sh.y2);
          const bw = Math.abs(sh.x2 - sh.x1) || 0.5, bh = Math.abs(sh.y2 - sh.y1) || 0.5;
          const pts = sh.pts.map((p, i) => i === index ? { x: (xPct - bx) / bw, y: (yPct - by) / bh } : p);
          return { ...sh, pts };
        });
        canvasShapesRef.current = next;
        return next;
      });
      if (ev.cancelable) ev.preventDefault();
    };
    const up = () => {
      document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up);
      document.removeEventListener('touchmove', move); document.removeEventListener('touchend', up);
      nodeDragRef.current = null;
      const after = canvasShapesRef.current.map(sh => {
        if (sh.id !== shapeId) return sh;
        const bx = Math.min(sh.x1, sh.x2), by = Math.min(sh.y1, sh.y2);
        const bw = Math.abs(sh.x2 - sh.x1) || 0.5, bh = Math.abs(sh.y2 - sh.y1) || 0.5;
        return { ...sh, ...normalizeFreeform(sh.pts.map(p => ({ x: bx + p.x * bw, y: by + p.y * bh }))) };
      });
      setCanvasShapes(after); canvasShapesRef.current = after;
      pushShapeHistory(before, after);
    };
    document.addEventListener('mousemove', move); document.addEventListener('mouseup', up);
    document.addEventListener('touchmove', move, { passive: false }); document.addEventListener('touchend', up);
  };

  // Put every tool down: nothing active, nothing selected. The pointer button and Esc use it.
  const neutralizeTools = () => {
    setPositionMode(false);   // putting the tool down also leaves Move
    setMagicSubTool(null);    // ...and closes the AI chat bar
    setActiveTool(null);
    setActiveShapeTool(null);
    setSelectedShapeId(null);
    setSelectedShapeIds(new Set());
    setSelectedImage(null);
    setShowImageControls(null);
    setSelectedEmoji(null);
    setFillModeActive(false);
    setImageSelectionMode(false);
    setIsDrawingShape(false);
    setShapeDrawStart(null); setShapeDrawEnd(null);
    setCurrentControlSection(null);
    setShowTopControls(false);
    setShowShapeColorPicker(false); setShowShapeColorShades(false);
    setShowColorPicker(false); setShowColorShades(false);
    setShowFiguresMenu(false); setShowEmojiPopdown(false);
    setOpenGroup(null);
    setCanvasCursor('default');
  };
  /**
   * Put crop down and leave everything as it was.
   *
   * There was no way to do this. Once crop was chosen the tools row was replaced by the crop
   * row, the crop row had five ways to crop and none to stop, and pressing the crop button
   * again did nothing — so short of pressing Escape, which a phone has not got, changing your
   * mind was not one of the things you could do.
   */
  const exitCrop = () => {
    setActiveTool(null);
    setSelectionMode('rectangle');
    setAutoCropActive(false);
    setIsSelecting(false);
    setSelectionPath([]);
    setSelectionTouchPos(null);
    cropPathRef.current = [];
    smartCropMaskRef.current = null;
    smartDetectedRef.current = false;
    setSmartDetected(false);
    pendingAutoCutRef.current = false;
    setCurrentControlSection(null);
    setShowTopControls(false);
    setCanvasCursor('default');
  };
  const cancelPendingClear = () => {
    if (pendingClearTimerRef.current) { clearTimeout(pendingClearTimerRef.current); pendingClearTimerRef.current = null; }
    setPendingClear(false);
  };
  const clearCanvas = () => {
    const hasContent = sheetHasSomething();
    if (!hasContent) { showAlert('Canvas is already empty.'); return; }
    // Clearing on purpose is one of the two things that forgets the kept sheet — see the
    // autosave above. (The other is closing the app through its own close button.)
    if (pendingClear) { cancelPendingClear(); resetStudio(); clearSession(); return; }
    setPendingClear(true);
    setCurrentControlSection(null);
    setShowTopControls(true);
    if (pendingClearTimerRef.current) clearTimeout(pendingClearTimerRef.current);
    pendingClearTimerRef.current = setTimeout(() => { pendingClearTimerRef.current = null; setPendingClear(false); }, 8000);
  };

  // What Save / Share / Print work on: the selected image alone, else the selected drawn
  // object alone, else everything on the sheet.
  const exportSelection = ({ lonePhoto = false } = {}) => {
    const whole = { images: canvasImages, paths: drawingPaths, shapes: canvasShapes, image: null, shape: null };
    // ── Two things that are never what you meant to save ────────────────────────────────
    // Selfie Mode: every effect, every prop, the mirror — all of it works on the picture you
    // have TAPPED, so something is selected almost the whole time you are in here. Saving in
    // that state used to hand back the one photo and throw away the cap, the shades, the
    // writing and the rest of the sheet. In Selfie Mode the sheet is the piece of work.
    //
    // The exception is asking for a PICTURE of the one you have tapped — "Save to Cloud
    // (Image)" with a photo selected. That is not a sheet at all; it is that face, cropped to
    // its own edges, with everything sitting on it. Its props come with it, because a cap on
    // somebody's head is part of the picture of them and was put there to be; the rest of the
    // sheet does not, because it was never what was asked for.
    if (selfieMode) {
      // Spelled out rather than calling selfiePhoto(), which is declared further down the file:
      // this runs during render, before that line has been reached.
      const lone = lonePhoto
        ? (canvasImages.find(i => i.id === selectedImage && i.source !== 'prop') || null)
        : null;
      if (!lone) return whole;
      return {
        images: [lone, ...canvasImages.filter(i => i.source === 'prop' && i.parentId === lone.id)],
        paths: [], shapes: [], image: lone, shape: null,
      };
    }
    const image = selectedImage ? canvasImages.find(i => i.id === selectedImage) : null;
    // And a prop is part of a picture rather than a picture: a pair of sunglasses on its own,
    // saved as the whole of what you made, is never the answer.
    if (image && image.source !== 'prop') return { images: [image], paths: [], shapes: [], image, shape: null };
    if (image) return whole;
    const shape = selectedShapeId ? canvasShapes.find(sh => sh.id === selectedShapeId) : null;
    if (shape) return { images: [], paths: [], shapes: [shape], image: null, shape };
    return whole;
  };
  /**
   * Is "Save to Cloud (Image)" a different thing from "Save to Cloud" right now?
   *
   * Only for a save that covers the whole sheet — nothing picked out, or Selfie Mode, where the
   * sheet IS the piece of work — because that is the save that also stores the sheet behind the
   * picture. Pick out one photo or one figure and a picture is all that ever goes up, so there
   * would be two names for one thing. Read from exportSelection so it cannot drift from what
   * saving actually does.
   */
  const saveCloudImageAvailable = (() => { const s = exportSelection(); return !s.image && !s.shape; })();
  // Pick one photo out of the sheet while that option is chosen and it leaves the list; the
  // choice has to leave with it, or the button would be sitting on a destination that is no
  // longer there.
  useEffect(() => {
    if (saveType === 'bucket-image' && !saveCloudImageAvailable) setSaveType('bucket');
  }, [saveType, saveCloudImageAvailable]);
  /**
   * The box a lone figure is exported in.
   *
   * A little margin is needed or the stroke — which straddles the geometric edge — and an
   * arrow's head get sliced off. It used to be a flat 1.5% of the sheet on every side, which
   * is 3mm across and 4.5mm down: on a 4cm figure that is a fifth of its height, and since the
   * exported box is also what the file DECLARES as its size, a 7.25 x 4.36cm rectangle saved
   * itself as 7.5 x 5.25. The margin is now only what the ink actually needs: half the stroke
   * either side, plus a head allowance for the shapes that have one.
   */
  const shapeExportPad = (sh) => {
    const sw = Number(sh?.strokeWidth) || 2;
    // Sheet percent, against the ~500px reference the stroke widths are authored in.
    const ink = (sw / 2 + 0.5) * 0.2;
    const head = (sh?.type === 'arrow' || sh?.type === 'line') ? sw * 0.5 : 0;
    return Math.min(3, ink + head);
  };
  /**
   * The box a lone figure is cropped to on the way out — the ink the figure actually puts on
   * the sheet, not the box it was dragged out in.
   *
   * Those two used to be assumed the same, and for an upright rectangle they nearly are. They
   * are not for the two cases below, and cropping to the drag box quietly sliced a quarter off
   * a saved 3D figure.
   */
  const shapeExportBounds = (sh) => {
    const PS = PRINT_SIZES[printSize];
    const pad = shapeExportPad(sh);
    let x0 = Math.min(sh.x1, sh.x2), x1 = Math.max(sh.x1, sh.x2);
    let y0 = Math.min(sh.y1, sh.y2), y1 = Math.max(sh.y1, sh.y2);
    if (isShape3D(sh.type) && has3DMesh(sh.type)) {
      // A mesh turned in space reaches past the front face it was sized by — see bounds3D. The
      // in-plane turn is baked into the projection for these, so it is covered here and must
      // not be applied a second time below.
      const b = bounds3D(sh.type, { ...pose3D(sh), rz: sh.rotation || 0 });
      const w = x1 - x0, h = y1 - y0;
      const nx0 = x0 + b.x0 * w, nx1 = x0 + b.x1 * w;
      const ny0 = y0 + b.y0 * h, ny1 = y0 + b.y1 * h;
      x0 = nx0; x1 = nx1; y0 = ny0; y1 = ny1;
    } else if (sh.rotation) {
      // A turned rectangle's corners swing outside its upright box. Percent across the sheet
      // and percent down it are different lengths, so the turn is worked out in centimetres
      // and converted back.
      const a = (sh.rotation * Math.PI) / 180;
      const ca = Math.abs(Math.cos(a)), sa = Math.abs(Math.sin(a));
      const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
      const hw = ((x1 - x0) / 200) * PS.width, hh = ((y1 - y0) / 200) * PS.height;
      const nhw = ((hw * ca + hh * sa) / PS.width) * 100;
      const nhh = ((hw * sa + hh * ca) / PS.height) * 100;
      x0 = cx - nhw; x1 = cx + nhw; y0 = cy - nhh; y1 = cy + nhh;
    }
    return {
      minX: Math.max(0,   x0 - pad),
      minY: Math.max(0,   y0 - pad),
      maxX: Math.min(100, x1 + pad),
      maxY: Math.min(100, y1 + pad),
    };
  };
  /**
   * The size a saved, shared or printed file DECLARES of itself, in centimetres.
   *
   * Normally that is simply the box the picture was rendered from. A lone figure is the
   * exception: its rendered box is a hair bigger than the figure, because the stroke straddles
   * the geometric edge and would otherwise be sliced off — but the size the studio SHOWS for a
   * figure, and the size typed into its W and H boxes, is the geometric one. So that is what
   * the file declares: draw 15 x 10, save it, add it again, and W and H still read 15 x 10.
   * The half-stroke of padding rides along inside those 15cm, at a cost of a fifth of a
   * millimetre and no lost detail.
   *
   * Lines and arrows keep the rendered box: their ink genuinely reaches past the endpoints (an
   * arrowhead is drawn beyond x2,y2), they are measured by length and angle rather than W and
   * H, and a vertical one has no geometric width at all to declare. So do turned figures and
   * 3D ones, whose ink reaches well outside the box — there the picture's own footprint is the
   * honest size, and declaring the smaller box would squeeze the figure on the way back in.
   */
  const declaredSizeCm = (sh, { minX, minY, maxX, maxY }, PS) => {
    // A 3D figure counts as plain even when it is turned: its turn happens inside the projection,
    // which is fitted to the box (see project3D), so the box is still the whole of it. A turned
    // FLAT figure is the opposite — its corners genuinely swing outside the box.
    const plain = sh && sh.type !== 'line' && sh.type !== 'arrow'
      && (!sh.rotation || isShape3D(sh.type))
      && Math.abs(sh.x2 - sh.x1) > 0.01 && Math.abs(sh.y2 - sh.y1) > 0.01;
    return {
      w: ((plain ? Math.abs(sh.x2 - sh.x1) : (maxX - minX)) / 100) * PS.width,
      h: ((plain ? Math.abs(sh.y2 - sh.y1) : (maxY - minY)) / 100) * PS.height,
    };
  };
  // Copy an image's own bytes to the system clipboard (PNG — the only format clipboards take).
  const copyImageToClipboard = async (img) => {
    if (!navigator.clipboard?.write || !window.ClipboardItem) { showAlert('Copying images is not supported in this browser.'); return false; }
    try {
      let blob = await fetch(img.url).then(r => r.blob());
      if (blob.type !== 'image/png') {
        const el = await loadImgSafe(img.url);
        const c = document.createElement('canvas'); c.width = el.naturalWidth; c.height = el.naturalHeight;
        c.getContext('2d').drawImage(el, 0, 0);
        blob = await new Promise(res => c.toBlob(res, 'image/png'));
      }
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      flash('Image copied — Ctrl+V pastes it here or in any app');
      return true;
    } catch (e) {
      console.error('Copy image failed:', e);
      showAlert('Could not copy the image.');
      return false;
    }
  };

  // Chat sharing
  const handleChatShare = async () => {
    if (!sheetHasSomething()) {
      showAlert('Please add images or drawings to the canvas first.');
      return;
    }
    
    setIsSaving(true);
    setSaveProgress(0);

    try {
      const imagesToRender = selectedImage
        ? canvasImages.filter(i => i.id === selectedImage)
        : canvasImages;
      const pathsToRender = selectedImage ? [] : drawingPaths;
      const shapesToRender = selectedImage ? [] : canvasShapes;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Calculate bounding box of all content
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      
      // Get image bounds
      imagesToRender.forEach(img => {
        const imgLeft = img.x;
        const imgRight = img.x + (img.width / 21 * 100);
        const imgTop = img.y;
        const imgBottom = img.y + (img.height / 29.7 * 100);
        
        minX = Math.min(minX, imgLeft);
        minY = Math.min(minY, imgTop);
        maxX = Math.max(maxX, imgRight);
        maxY = Math.max(maxY, imgBottom);
      });
      
      // Get drawing bounds (in percentage)
      pathsToRender.forEach(path => {
        const pCanvasW = path.canvasW || 800;
        const pCanvasH = path.canvasH || (800 * A4_RATIO);
        path.path.forEach(point => {
          const pointXPercent = (point.x / pCanvasW) * 100;
          const pointYPercent = (point.y / pCanvasH) * 100;
          
          minX = Math.min(minX, pointXPercent);
          minY = Math.min(minY, pointYPercent);
          maxX = Math.max(maxX, pointXPercent);
          maxY = Math.max(maxY, pointYPercent);
        });
      });
      
      // Only pad when there are multiple items ? prevents edge clipping.
      // Single-item saves are pixel-exact with no whitespace margin.
      // Include shape bounds
      shapesToRender.forEach(s => {
        minX = Math.min(minX, s.x1, s.x2);
        minY = Math.min(minY, s.y1, s.y2);
        maxX = Math.max(maxX, s.x1, s.x2);
        maxY = Math.max(maxY, s.y1, s.y2);
      });
      const hasMultipleItems = (imagesToRender.length + pathsToRender.length + shapesToRender.length) > 1;
      const isSingleCircle = imagesToRender.length === 1 && imagesToRender[0].cropShape === 'circle' && pathsToRender.length === 0 && shapesToRender.length === 0;
      const padding = hasMultipleItems ? 2 : 0;
      minX = Math.max(0, minX - padding);
      minY = Math.max(0, minY - padding);
      maxX = Math.min(100, maxX + padding);
      maxY = Math.min(100, maxY + padding);
      
      // Calculate content dimensions
      const contentWidth = maxX - minX;
      const contentHeight = maxY - minY;
      
      const dpi = 150;
      const widthInPx = contentWidth * (21 * (dpi / 2.54)) / 100;
      const heightInPx = contentHeight * (29.7 * (dpi / 2.54)) / 100;
      
      canvas.width = widthInPx;
      canvas.height = heightInPx;
      
      if (!isSingleCircle) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, widthInPx, heightInPx);
      }
      setSaveProgress(30);

      // ── The picture the model is shown is the picture on the sheet ──────────────────────
      // Loaded first, drawn second. The loads can happen all at once, but the DRAWING has to
      // follow the sheet's own order (see layersInOrder) — this used to paint every picture
      // and then every figure, so a cut-out standing on a coloured figure was buried under it
      // and the model was asked about a composition the user had never seen.
      const _loaded = await Promise.all(imagesToRender.map(async (img) => {
        const image = await loadImgForOutput(img.url).catch(() => null);
        if (!image) console.error('Failed to load image for chat:', img.url);
        return image;
      }));
      setSaveProgress(60);

      for (const _layer of layersInOrder(imagesToRender, shapesToRender)) {
        if (_layer.kind === 'shape') {
          drawShapesOnCanvas(ctx, [_layer.item], widthInPx, heightInPx, minX, minY, contentWidth, contentHeight);
          continue;
        }
        const img = _layer.item;
        const image = _loaded[imagesToRender.indexOf(img)];
        if (!image) continue;
        // Calculate image position relative to content bounds
        const imgXPx = ((img.x - minX) / contentWidth) * widthInPx;
        const imgYPx = ((img.y - minY) / contentHeight) * heightInPx;
        const imgWidthPx = (img.width / 21) * (widthInPx / contentWidth) * 100;
        const imgHeightPx = (img.height / 29.7) * (heightInPx / contentHeight) * 100;

        const rot = ((img.rotation || 0) * Math.PI) / 180;
        if (rot) {
          const cx = imgXPx + imgWidthPx / 2;
          const cy = imgYPx + imgHeightPx / 2;
          ctx.save();
          ctx.translate(cx, cy);
          ctx.rotate(rot);
          if (img.cropShape === 'circle') {
            ctx.beginPath();
            ctx.ellipse(0, 0, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2);
            ctx.clip();
          }
          ctx.drawImage(image, -imgWidthPx / 2, -imgHeightPx / 2, imgWidthPx, imgHeightPx);
          ctx.restore();
        } else if (img.cropShape === 'circle') {
          ctx.save();
          ctx.beginPath();
          ctx.ellipse(imgXPx + imgWidthPx / 2, imgYPx + imgHeightPx / 2, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2);
          ctx.clip();
          ctx.drawImage(image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
          ctx.restore();
        } else {
          ctx.drawImage(image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
        }
      }
      setSaveProgress(90);

      // Drawings last — they were already above every picture, and are now above the figures too.
      drawDrawingLayer(ctx, pathsToRender, widthInPx, heightInPx, minX, minY, contentWidth, contentHeight);

      setSaveProgress(85);
      const chatFileExt = isSingleCircle ? 'png' : 'jpg';
      const chatContentType = isSingleCircle ? 'image/png' : 'image/jpeg';
      const dataUrl = isSingleCircle ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.8);

      // Upload to Firebase Storage so the chat message stores a URL (not a large base64 string)
      const auth = getAuth();
      const uid = auth.currentUser?.uid || 'unknown';
      const timestamp = Date.now();
      const chatFileName = `studio_chat/${uid}/${timestamp}.${chatFileExt}`;
      const blob = await fetch(dataUrl).then(r => r.blob());
      const chatFile = new File([blob], `${fileName}-chat.${chatFileExt}`, { type: chatContentType });
      await uploadToStorage(chatFileName, chatFile, chatContentType, getEnvStorageBucket());
      setSaveProgress(95);
      const downloadUrl = await getDownloadUrl(chatFileName, getEnvStorageBucket());
      setSaveProgress(100);

      const chatImageData = {
        url: downloadUrl,
        fileName: `${fileName}-chat.${chatFileExt}`,
        timestamp: new Date().toISOString(),
        source: 'Image Studio',
        type: canvasImages.length > 1 ? 'collage' : 'single'
      };

      sessionStorage.setItem('chatImageData', JSON.stringify(chatImageData));
      
      const canvasState = {
        canvasImages,
        drawingPaths,
        canvasShapes,
        selectedImage,
        fileName,
        saveType,
        resizeWidth,
        resizeHeight,
        printSize,
      };
      
      try { localStorage.setItem('imageStudioState', JSON.stringify(canvasState)); } catch { /* too big to keep — the share still goes */ }
      
      navigate(isStaff ? '/admin-chat' : '/user-chat');
      
      setTimeout(() => {
        setIsSaving(false);
        setSaveProgress(0);
        if (activeTool !== 'magic-wand') {
          setActiveTool(null);
          setCurrentControlSection(null);
          setShowTopControls(false);
        } else {
          setCurrentControlSection('magic-wand');
        }
      }, 500);

    } catch (error) {
      console.error('Chat share error:', error);
      setIsSaving(false);
      setSaveProgress(0);
      if (activeTool !== 'magic-wand') {
        setActiveTool(null);
        setCurrentControlSection(null);
        setShowTopControls(false);
      } else {
        setCurrentControlSection('magic-wand');
      }
      showAlert(`Failed to prepare image for chat: ${error?.message || error}`);
    }
  };

  // Restore canvas state
  useEffect(() => {
    const savedCanvas = localStorage.getItem('imageStudioState');
    if (savedCanvas) {
      try {
        const { canvasImages: savedImages, drawingPaths: savedPaths, canvasShapes: savedShapes, selectedImage: savedSelected, fileName: savedFileName, printSize: savedPrintSize } = JSON.parse(savedCanvas);
        if ((savedImages && savedImages.length > 0) || (savedPaths && savedPaths.length > 0) || (savedShapes && savedShapes.length > 0)) {
          setCanvasImages(savedImages || []);
          setDrawingPaths(savedPaths || []);
          if (savedShapes && savedShapes.length > 0) setCanvasShapes(savedShapes);
          setFileName(savedFileName || DEFAULT_IMAGE_NAME);
          if (savedPrintSize && PRINT_SIZES[savedPrintSize]) setPrintSize(savedPrintSize);
          if (savedSelected) {
            setSelectedImage(savedSelected);
          }
        }
      } catch (error) {
        console.error('Failed to restore canvas:', error);
      }
      localStorage.removeItem('imageStudioState');
    }
  }, []);

  // ── The sheet survives the app being put down ─────────────────────────────────────────────
  //
  // On a phone the back gesture does not close an app, it backgrounds it, and the browser is
  // then free to throw the page away to reclaim memory — no warning, no question. Come back a
  // few minutes later and the canvas is empty with an afternoon's work in it. So the whole
  // sheet is written down as it goes (see lib/session.js) and read back on the way in. Only
  // closing the app on purpose, or clearing the canvas, forgets it: both of those are somebody
  // saying they are finished, and backgrounding is not.
  const sessionReadyRef  = useRef(false);   // don't autosave over a session we haven't read yet
  const sessionCacheRef  = useRef(new Map());
  const sessionTimerRef  = useRef(null);
  const sessionBusyRef   = useRef(false);
  const writeSessionRef  = useRef(() => {});

  const writeSession = async () => {
    // On the way out on purpose — see closeApp. Closing is a going-away like any other, so the
    // flush below would otherwise write the sheet back out microseconds after the close deleted
    // it, and the app would open on it again next time.
    if (sessionClosingRef.current) return;
    if (!sessionReadyRef.current || sessionBusyRef.current) return;
    const imgs = canvasImagesRef.current || [];
    const shps = canvasShapesRef.current || [];
    const pths = drawingPathsRef.current || [];
    // An empty sheet is not work in progress; leaving a stale one behind would mean an old
    // canvas reappearing over a deliberately emptied one.
    if (!imgs.length && !shps.length && !pths.length) { await clearSession(); return; }
    sessionBusyRef.current = true;
    try {
      await saveSession(await buildProject(
        { printSize, canvasBackground, canvasBgFx, images: imgs, shapes: shps, paths: pths },
        // The cache keeps a photograph from being re-encoded on every keystroke's worth of
        // editing; keepOriginals carries the untouched copy an effect can be undone from, so
        // coming back leaves you exactly where you were rather than one step past it.
        { urlCache: sessionCacheRef.current, keepOriginals: true },
      ));
    } catch { /* best effort, always */ }
    finally { sessionBusyRef.current = false; }
  };
  writeSessionRef.current = writeSession;   // the listeners below must never call a stale one

  // Read it back, once, and only onto a canvas that is still empty.
  useEffect(() => {
    if (fromCustomRef.current) return undefined;   // PCake: Custom edits are never autosaved
    let alive = true;
    (async () => {
      try {
        const data = await loadSession();
        const hasWork = isProject(data)
          && ((data.images?.length || 0) + (data.shapes?.length || 0) + (data.paths?.length || 0)) > 0;
        const canvasEmpty = !canvasImagesRef.current.length
          && !canvasShapesRef.current.length && !drawingPathsRef.current.length;
        if (alive && hasWork && canvasEmpty) {
          if (data.printSize && PRINT_SIZES[data.printSize]) setPrintSize(data.printSize);
          setCanvasBackground(data.canvasBackground ?? 'transparent');
          setCanvasBgFx(data.canvasBgFx ?? null);
          const imgs = Array.isArray(data.images) ? data.images : [];
          setCanvasImages(imgs); canvasImagesRef.current = imgs;
          const shs = Array.isArray(data.shapes) ? data.shapes : [];
          setCanvasShapes(shs); canvasShapesRef.current = shs;
          const pth = Array.isArray(data.paths) ? data.paths : [];
          setDrawingPaths(pth); drawingPathsRef.current = pth;
          // Anything placed from now on must sit above what came back.
          layerCounterRef.current = Math.max(
            layerCounterRef.current,
            ...imgs.map(i => i.layerIndex || 0),
            ...shs.map(sh => sh.layerIndex || 0),
          );
          // It came back unsaved, because it IS unsaved — the close warning still applies.
          markDirty();
        }
      } finally {
        if (alive) sessionReadyRef.current = true;
      }
    })();
    return () => { alive = false; };
  }, []);

  // Write it down shortly after anything changes...
  useEffect(() => {
    if (!sessionReadyRef.current) return undefined;
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    sessionTimerRef.current = setTimeout(() => {
      sessionTimerRef.current = null;
      writeSessionRef.current();
    }, 1200);
    return () => { if (sessionTimerRef.current) { clearTimeout(sessionTimerRef.current); sessionTimerRef.current = null; } };
  }, [canvasImages, canvasShapes, drawingPaths, printSize, canvasBackground, canvasBgFx]);

  // ...and immediately when the app goes away, which on a phone is the moment that matters:
  // there may be no later. visibilitychange is the one event a mobile browser reliably fires
  // before it is allowed to discard the page; beforeunload is not, on any phone.
  useEffect(() => {
    const flush = () => {
      if (sessionTimerRef.current) { clearTimeout(sessionTimerRef.current); sessionTimerRef.current = null; }
      writeSessionRef.current();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') { flush(); return; }
      // Back on screen after the close button was used. A phone often does not destroy a page
      // it is asked to close — it backgrounds it and hands the same one back when the app is
      // opened again, with every object still sitting there. That looks exactly like "I closed
      // it and it came back", so the close is finished properly here instead: an app that was
      // closed opens empty. Only ever reached with the latch on, which nothing but closeApp
      // sets, so merely switching away and back leaves the sheet alone.
      if (sessionClosingRef.current) {
        sessionClosingRef.current = false;
        resetStudio();
        clearSession();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
    };
  }, []);

  // The folder the last listing actually looked in. "No images in Cloud" is the same message
  // whether the account is genuinely empty or the app searched the wrong place (an anonymous
  // device id instead of the signed-in account, say), and the two need telling apart.
  const lastBucketFolderRef = useRef('');

  // Load bucket images
  const loadBucketImages = async (forceReload = false) => {
    try {
      // Wait for a stored session to come back before naming the folder — see cloudAuthReady.
      await cloudAuthReady();
      const _bucketUser = getAuth().currentUser;
      // An administrator browses the whole library; everyone else sees their own folder. The
      // role comes from the signed-in account, not from a prop — see the note at the save.
      const _bucketFolder = isCloudAdmin() ? 'istudio' : `istudio/${_bucketUser?.uid}`;
      lastBucketFolderRef.current = _bucketFolder;
      const _cacheKey = `${_bucketFolder}:${getEnvStorageBucket()}`;

      // Return cached result immediately if available and not forced
      if (!forceReload && _bucketImagesCacheKey === _cacheKey && _bucketImagesCache) {
        setBucketImages(_bucketImagesCache);
        return _bucketImagesCache;
      }

      setIsLoading(true);
      const { items } = await listObjectsWithMetadata(_bucketFolder, getEnvStorageBucket());
      const imageFiles = items.filter(item =>
        item.name.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i)
      );
      // Which of them were saved as a whole sheet, and can therefore be opened to carry on
      // working rather than just placed as a picture.
      const _projectBases = new Set(
        items.map(it => projectBaseOf(it.name)).filter(Boolean),
      );

      const imagesWithPreviews = await Promise.all(
        imageFiles.map(async (item) => {
          try {
            // A link, not the picture: this runs once per item in the library, and reading the
            // content now reads the bytes (that is how a saved size is recovered), which for a
            // grid of thumbnails would mean downloading the whole library to draw it.
            const previewUrl = await getDownloadUrl(item.name, getEnvStorageBucket());
            return {
              ...item,
              previewUrl,
              fileName: item.name,
              displayName: item.name.split('/').pop(),
              // A sheet belongs to the picture whose name it carries, exactly. Nothing else.
              //
              // There was a fallback here for sheets named the old way, trusted whenever only
              // one picture answered to that base name — and it went wrong immediately: delete
              // the picture a sheet belonged to, leave the sheet behind, and the remaining
              // picture became the only claimant and inherited it. A single photo was then
              // labelled "sheet" and opened as somebody else's whole canvas, deleted pictures
              // and all. A guess about which picture a file belongs to is not worth making;
              // the worst an exact match can do is not offer to reopen an old sheet.
              hasProject: _projectBases.has(item.name),
            };
          } catch (error) {
            return {
              ...item,
              previewUrl: null,
              fileName: item.name,
              displayName: item.name.split('/').pop()
            };
          }
        })
      );

      _bucketImagesCache = imagesWithPreviews;
      _bucketImagesCacheKey = _cacheKey;
      setBucketImages(imagesWithPreviews);
      return imagesWithPreviews;
    } catch (error) {
      console.error('Failed to load bucket images:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // ── The library follows the connection ──────────────────────────────────────────────────
  // Going offline with the panel open swaps which store it is reading from, and going back
  // online swaps it back; without this the panel keeps showing whichever list it happened to
  // fetch first, which is the one case where "this device's library" could be mistaken for
  // the account's. The cached list is keyed by bucket, so it is already right — this just
  // makes the panel ask again at the moment the answer changes.
  useEffect(() => {
    if (!showBucketModal) return;
    loadBucketImages(true);
  }, [online, showBucketModal]);

  // Detect if a loaded image is circle-cropped by checking corner transparency
  const detectCircleCrop = (img) => {
    try {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (!w || !h) return undefined;

      // Sample at a manageable resolution while preserving aspect ratio
      const maxSide = 200;
      const scale = Math.min(maxSide / w, maxSide / h, 1);
      const cw = Math.round(w * scale);
      const ch = Math.round(h * scale);

      const tc = document.createElement('canvas');
      tc.width  = cw;
      tc.height = ch;
      const tCtx = tc.getContext('2d', { willReadFrequently: true });
      tCtx.drawImage(img, 0, 0, cw, ch);

      const px = (x, y) => tCtx.getImageData(
        Math.max(0, Math.min(cw - 1, Math.round(x))),
        Math.max(0, Math.min(ch - 1, Math.round(y))), 1, 1
      ).data;

      // 1. Center pixel must be fully opaque — rules out fully-transparent images
      if (px(cw / 2, ch / 2)[3] < 200) return undefined;

      // 2. All four corner clusters must be transparent
      const m = Math.max(1, Math.floor(Math.min(cw, ch) * 0.08));
      const corners = [px(m, m), px(cw - m - 1, m), px(m, ch - m - 1), px(cw - m - 1, ch - m - 1)];
      if (!corners.every(p => p[3] < 30)) return undefined;

      // 3. Strict ellipse-boundary test: sample 12 angles around the expected ellipse.
      //    Points at 85 % radius must be opaque; points at 105 % radius (if in-canvas)
      //    must be transparent. A random logo/badge with transparent corners rarely
      //    satisfies both conditions across all 12 sampled angles.
      const rx = cw / 2, ry = ch / 2;
      const cx = cw / 2, cy = ch / 2;
      for (let i = 0; i < 12; i++) {
        const angle = (i / 12) * Math.PI * 2;
        const cosA  = Math.cos(angle);
        const sinA  = Math.sin(angle);

        // Inside the ellipse — must be opaque
        const xi = cx + cosA * rx * 0.85;
        const yi = cy + sinA * ry * 0.85;
        if (px(xi, yi)[3] < 150) return undefined;

        // Outside the ellipse (but still within canvas) — must be transparent
        const xo = cx + cosA * rx * 1.05;
        const yo = cy + sinA * ry * 1.05;
        if (xo >= 0 && xo < cw && yo >= 0 && yo < ch) {
          if (px(xo, yo)[3] > 30) return undefined;
        }
      }

      return 'circle';
    } catch (e) {
      // getImageData blocked by CORS — skip detection
    }
    return undefined;
  };

  // Handle local file upload with proper aspect ratio calculation (supports multi-select)
  const handleLocalFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    if (!imageFiles.length) {
      showAlert('Please select image files');
      return;
    }

    const psW = PRINT_SIZES[printSize].width;
    const psH = PRINT_SIZES[printSize].height;
    const _fillFrac = window.innerWidth <= 768 ? 0.45 : 0.38;
    // Cascade offset per image so multiple images don't fully overlap
    const OFFSET_STEP = 3; // percent of canvas per image

    // Read a single file and compute its canvas image object
    const loadOne = (file, index) => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = async () => {
          const naturalAspectRatio = img.naturalWidth / img.naturalHeight;
          const imageCanvasAR = naturalAspectRatio * psH / psW;
          let width, height;
          // A picture this studio saved carries the size it was edited to. Bring it back at
          // that size: edit something to 15 x 10 cm, save it, add it again, and it is 15 x 10
          // again. Only our own files are taken at their word — see readPhysicalSize — so an
          // ordinary photo, whose 72 dpi is a default nobody chose, still arrives at a sensible
          // fraction of the sheet as before.
          const declared = await readPhysicalSize(file).catch(() => null);
          if (declared && declared.cmW > 0 && declared.cmH > 0) {
            width  = (declared.cmW / psW) * 21;
            height = (declared.cmH / psH) * 29.7;
          } else if (imageCanvasAR >= 1) {
            width  = _fillFrac * 21;
            height = _fillFrac * psW * 29.7 / (naturalAspectRatio * psH);
          } else {
            height = _fillFrac * 29.7;
            width  = naturalAspectRatio * _fillFrac * psH * 21 / psW;
          }
          width  = Math.max(0.5, Math.min(21,   width));
          height = Math.max(0.5, Math.min(29.7, height));

          const baseX = 50 - (width  / 21   * 50);
          const baseY = 50 - (height / 29.7 * 50);
          resolve({
            id: Date.now() + index * 10,
            url: event.target.result,
            name: file.name,
            size: formatFileSize(file.size),
            x: Math.min(baseX + index * OFFSET_STEP, 70),
            y: Math.min(baseY + index * OFFSET_STEP, 70),
            width,
            height,
            source: 'local',
            cropShape: detectCircleCrop(img),
            naturalWidth:  img.naturalWidth,
            naturalHeight: img.naturalHeight,
          });
        };
        img.onerror = () => {
          resolve({
            id: Date.now() + index * 10,
            url: event.target.result,
            name: file.name,
            size: formatFileSize(file.size),
            x: 20 + index * OFFSET_STEP,
            y: 20 + index * OFFSET_STEP,
            width: 10,
            height: 10,
            source: 'local',
          });
        };
        img.src = event.target.result;
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });

    const results = await Promise.all(imageFiles.map((f, i) => loadOne(f, i)));
    const validImages = results.filter(Boolean).map(img => ({
      ...img,
      layerIndex: ++layerCounterRef.current,
    }));
    if (!validImages.length) {
      showAlert('Error reading image files');
      return;
    }

    const newList = [...canvasImagesRef.current, ...validImages];
    pushImageHistory(canvasImagesRef.current, newList);
    setCanvasImages(newList);

    const last = validImages[validImages.length - 1];
    setSelectedImage(last.id);
    setResizeWidth(last.width);
    setResizeHeight(last.height);
    setAspectRatio((last.naturalWidth || last.width) / (last.naturalHeight || last.height));
    setImageSelectionMode(false);
    if (activeTool !== 'magic-wand') {
      setActiveTool(null);
      setCurrentControlSection(null);
      setShowTopControls(false);
    }
    setCanvasCursor('pointer');
  };

  // Upload multiple local files directly to Cloud without adding them to the canvas
  const handleDirectCloudUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    if (!imageFiles.length) {
      showAlert('Please select image files');
      return;
    }

    setIsSaving(true);
    setSaveProgress(0);
    setSaveMessage(null);

    try {
      await cloudAuthReady();          // never upload into the device's folder by mistake
      const _saveUser = getAuth().currentUser;
      const _saveFolder = `istudio/${_saveUser?.uid}`;

      const { items } = await listObjectsInFolder(_saveFolder, getEnvStorageBucket());
      const existingFiles = new Set(items.map(item => item.name));

      let uploaded = 0;
      for (const file of imageFiles) {
        const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
        const baseName = file.name.replace(/\.[^.]+$/, '').replace(/\s+/g, '_');
        let destName = `${_saveFolder}/${baseName}.${ext}`;
        let counter = 1;
        while (existingFiles.has(destName)) {
          destName = `${_saveFolder}/${baseName}_${counter}.${ext}`;
          counter++;
        }
        existingFiles.add(destName);
        await uploadToStorage(destName, file, file.type, getEnvStorageBucket());
        uploaded++;
        setSaveProgress(Math.round((uploaded / imageFiles.length) * 100));
      }

      _bucketImagesCache = null;
      setSaveMessage({
        text: `${uploaded} image${uploaded > 1 ? 's' : ''} uploaded to Cloud`,
        ok: true
      });
    } catch (err) {
      console.error('Direct cloud upload failed:', err);
      setSaveMessage({ text: 'Upload failed. Please try again.', ok: false });
    } finally {
      setIsSaving(false);
      setSaveProgress(0);
    }
  };

  // Handle bucket image select with proper aspect ratio
  const handleBucketImageSelect = async (imageFileName) => {
    try {
      setIsLoading(true);
      const result = await readObjectContent(imageFileName, getEnvStorageBucket());
      
      // Load image to get dimensions (crossOrigin needed for pixel detection)
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = result.downloadUrl;
      });
      
      const naturalAspectRatio = img.naturalWidth / img.naturalHeight;
      const cropShape = detectCircleCrop(img);
      
      // The size it was edited to. A picture this studio saved says so in the file itself, so
      // ask it rather than assuming: that is what makes 15 x 10 cm come back as 15 x 10 cm.
      // Failing that, fall back to the old assumption of 300 DPI, which is the density the
      // studio exports at anyway; a raw phone photo exceeds the paper and is scaled to fit,
      // both axes by the same factor so the shape is kept.
      const _declared = await readPhysicalSize(result.blob).catch(() => null);
      const _psW1 = PRINT_SIZES[printSize].width, _psH1 = PRINT_SIZES[printSize].height;
      // Stored sizes are in A4-portrait space (width / 21 = the fraction of the page), so a
      // real-world measurement has to be expressed against the CURRENT page, not assumed A4.
      const rawW = _declared ? (_declared.cmW / _psW1) * 21   : (img.naturalWidth  * 2.54 / 300);
      const rawH = _declared ? (_declared.cmH / _psH1) * 29.7 : (img.naturalHeight * 2.54 / 300);
      // A size the file DECLARES is a measurement, not a suggestion: clamp each side to the
      // paper and leave it otherwise alone. Scaling both sides by a common factor — which is
      // what this did — quietly shrinks a picture that is already exactly the size of the page,
      // because a declared size is stored as whole pixels-per-metre and comes back as 20.999
      // rather than 21. A whole sheet saved to the cloud then came back a little smaller every
      // time. The common factor is still right for a picture that never declared anything: a
      // phone photo at an assumed 300 dpi is far bigger than the paper and has to be scaled to
      // fit, both sides together, or its shape changes.
      const fitScale = _declared ? 1 : Math.min(1, 21 / rawW, 29.7 / rawH);
      let width  = Math.max(0.5, Math.min(21,   rawW * fitScale));
      let height = Math.max(0.5, Math.min(29.7, rawH * fitScale));
      
      const newImage = {
        id: Date.now(),
        layerIndex: ++layerCounterRef.current,
        url: result.downloadUrl,
        name: imageFileName.split('/').pop(),
        size: formatFileSize(result.size || 0),
        x: Math.max(0, 50 - (width / 21 * 50)),
        y: Math.max(0, 50 - (height / 29.7 * 50)),
        width: width,
        height: height,
        source: 'bucket',
        cropShape,
        bucketPath: imageFileName,
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight
      };
      
      pushImageHistory(canvasImagesRef.current, [...canvasImagesRef.current, newImage]);
      setCanvasImages(prev => [...prev, newImage]);
      setSelectedImage(newImage.id);
      setResizeWidth(width);
      setResizeHeight(height);
      setAspectRatio(naturalAspectRatio);
      setShowBucketModal(false);
      setBucketModalMode('select');
      setSelectedBucketImages([]);
      setImageSelectionMode(false);
      if (activeTool !== 'magic-wand') {
        setActiveTool(null);
        setCurrentControlSection(null);
        setShowTopControls(false);
      }
      setCanvasCursor('pointer');
      
    } catch (error) {
      console.error('Error loading bucket image:', error);
      showAlert(`Failed to load image from Cloud: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Open bucket modal for deletion
  const handleOpenBucketForDelete = async () => {
    setIsLoading(true);
    const images = await loadBucketImages();
    if (images.length === 0) {
      showAlert('No images found in Cloud to delete');
      setIsLoading(false);
      return;
    }
    setBucketModalMode('delete');
    setSelectedBucketImages([]);
    setBucketSearch('');
    setShowBucketModal(true);
    setIsLoading(false);
  };

  // Handle connect options 
  const handleConnectOption = (option) => {
    setIsConnectMenuOpen(false);

    // Cloud actions need a real signed-in account -- local/anonymous identity isn't enough to
    // find your files again from another device. When cloud auth isn't configured for this
    // build at all, skip the gate entirely rather than showing a sign-in box that can't work.
    // `authUser` is React state, so it is still null on the tick the sign-in modal resumes the
    // action that opened it — which bounced a freshly signed-in user straight back to the
    // sign-in panel. Ask the auth module itself as well: it knows the moment the password lands.
    const _signedIn = !!authUser || getAuth().currentUser?.isAnonymous === false;
    if ((option === 'bucket' || option === 'upload-cloud') && isCloudAuthEnabled() && !_signedIn) {
      pendingAuthActionRef.current = option;
      setAuthMode('signin');
      setAuthError(null);
      setAuthNotice(null);
      setShowAuthModal(true);
      return;
    }

    if (option === 'local') {
      fileInputRef.current.click();
      setShowTopControls(false);
      setCurrentControlSection(null);
    } else if (option === 'camera') {
      // The camera is opened by the page, not handed off to the phone's camera app. That is what
      // makes the permission the APP's — given once here, an embedded Eyelid session uses the
      // same camera on the same origin without asking again (see lib/camera.js). The old handoff
      // granted the page nothing, so every other use had to ask for itself.
      setShowTopControls(false);
      setCurrentControlSection(null);
      openSelfieCamera(selfieFacing);
    } else if (option === 'bucket') {
      handleConnectBucket();
    } else if (option === 'upload-cloud') {
      cloudUploadInputRef.current.click();
      setShowTopControls(false);
      setCurrentControlSection(null);
    }
  };

  // Sign in / sign up submit -- resumes whatever Cloud action opened the modal, if any.
  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    if (!authEmail || !authPassword) { setAuthError('Enter your email and password.'); return; }
    setAuthBusy(true);
    setAuthError(null);
    setAuthNotice(null);
    try {
      const resumePending = () => {
        setShowAuthModal(false);
        setAuthPassword('');
        const pending = pendingAuthActionRef.current;
        pendingAuthActionRef.current = null;
        if (pending === 'save-bucket') handleSave();          // the Save panel sent us here
        else if (pending) handleConnectOption(pending);
      };
      if (authMode === 'signup') {
        const data = await signUp(authEmail, authPassword);
        if (data?.session) {
          resumePending();
        } else {
          // This Supabase project has email confirmation on -- no session yet.
          setAuthNotice('Check your inbox to confirm your email, then sign in.');
          setAuthMode('signin');
        }
      } else {
        await signIn(authEmail, authPassword);
        resumePending();
      }
    } catch (err) {
      setAuthError(err.message || String(err));
    } finally {
      setAuthBusy(false);
    }
  };

  // Handle connect to bucket
  const handleConnectBucket = async () => {
    setIsLoading(true);
    try {
      const images = await loadBucketImages();
      if (images.length === 0) {
        // The folder that was searched belongs in the console, not in front of the person. It
        // was put in the message to tell "genuinely empty" apart from "looked in the wrong
        // place", which is a question a developer asks — and the answer was a raw account id
        // shown to someone who only wanted to open a picture.
        console.warn('Cloud library is empty. Looked in:', lastBucketFolderRef.current || '(unknown folder)');
        showAlert('Your Cloud library is empty — save a picture first.');
        return;
      }
      setBucketModalMode('select');
      setSelectedBucketImages([]);
      setBucketSearch('');
      setShowBucketModal(true);
      setShowTopControls(false);
      setCurrentControlSection(null);
    } catch (error) {
      console.error('Error loading bucket images:', error);
      showAlert('Failed to load Cloud images');
    } finally {
      setIsLoading(false);
    }
  };

  // Image history helper ? safe to call from async contexts via refs
  const pushImageHistory = (before, after) => {
    markDirty();
    const newHistory = drawingHistoryRef.current.slice(0, historyIndexRef.current + 1);
    newHistory.push({ type: 'image', before, after });
    setDrawingHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  // Mouse event handlers
  // ── Crop tool: ONE pointer implementation for mouse and touch ─────────────────
  // These three are the only place crop input is interpreted. handleMouseMove/Up and the
  // touch handlers call them with a canvas-relative point and nothing else, so the crop
  // tools cannot behave differently on a phone than on a desktop. They used to: the touch
  // path was a separate copy that lifted the drawing tip 40px above the finger (so a lasso
  // never went where you touched), used a different rectangle hit zone, and had a smart-crop
  // fast path with its own state. All of that is gone; what remains is the desktop behaviour.
  // Each returns true when it consumed the event.
  //
  // ── Where the pen draws, on a touch screen ──────────────────────────────────
  // A fingertip covers about a centimetre of glass, and the line a lasso is drawing came out
  // from under the middle of it — so the one thing you need to see while tracing an object was
  // the one thing hidden. The DRAWN POINT is therefore lifted away from the finger: the pen's
  // tip is that point, standing above and to one side, and the outline appears there. You aim
  // with the pen, not with the finger, exactly as you would holding a real one.
  //
  // The side is decided once, when the stroke starts, from whichever half of the sheet the
  // finger is in — a side that flipped mid-stroke would put a kink in the line — and the lift
  // goes downwards instead when the stroke starts near the top edge. A mouse gets no offset at
  // all: a cursor is a few transparent pixels, and moving the line away from it would be wrong.
  const penOffsetRef = useRef({ dx: 0, dy: 0 });
  const startPenOffset = (x, y) => {
    if (!COARSE_POINTER || (selectionMode !== 'smart' && selectionMode !== 'freeform')) {
      penOffsetRef.current = { dx: 0, dy: 0 };
      return;
    }
    const w = canvasRef.current?.offsetWidth || 400;
    penOffsetRef.current = { dx: (x > w / 2 ? -26 : 26), dy: y < 44 ? 40 : -40 };
  };
  /** The point the line is actually drawn at, for the finger that is at (x, y). */
  const penPoint = (x, y) => {
    const { dx, dy } = penOffsetRef.current;
    if (!dx && !dy) return { x, y };
    const w = canvasRef.current?.offsetWidth || 400;
    const h = canvasRef.current?.offsetHeight || 560;
    return { x: Math.max(0, Math.min(w, x + dx)), y: Math.max(0, Math.min(h, y + dy)) };
  };

  /**
   * Is there a crop box on screen to take hold of?
   *
   * This used to be asked as `selectionStart.x !== 0`, reading a COORDINATE as a "nothing placed
   * yet" sentinel. x = 0 is not nothing — it is the left edge of the sheet, which is exactly
   * where the box lands whenever the picture it was placed around is flush left (cropStartBounds
   * returns (img.x / 100) * W, and img.x = 0 gives 0). In that position every press skipped the
   * move/resize branch entirely and fell through to "first placement", which sets start AND end
   * to the pointer: the box collapsed to a point and the selection was gone. Dragging a corner
   * could never work there, and nothing about it was fixable from the UI.
   *
   * `isSelecting` is the flag that actually means "a box is up"; the second test rules out a
   * zero-size one, which is the only case first placement is for.
   */
  const cropHasBox = () =>
    isSelecting && (selectionEnd.x !== selectionStart.x || selectionEnd.y !== selectionStart.y);

  const cropPointerDown = (rawX, rawY) => {
    if (activeTool !== 'selection' || !selectionMode) return false;
    startPenOffset(rawX, rawY);
    const { x, y } = penPoint(rawX, rawY);

    // Smart crop: draw a lasso around the object. If a detection result is already showing,
    // the press is ignored so the user can tap ✓ (or Reset) instead of restarting.
    if (selectionMode === 'smart') {
      if (smartDetectedRef.current || smartCropBusyRef.current) return true;
      smartDetectedRef.current = false;
      setSmartDetected(false);
      setIsSelecting(true);
      cropActiveRef.current = true;
      cropPathRef.current = [{ x, y }];
      setSelectionPath([{ x, y }]);
      setSelectionStart({ x, y });
      setSelectionEnd({ x, y });
      setSelectionTouchPos({ x, y });
      return true;
    }

    setIsSelecting(true);

    // Circle crop: the overlay is pre-placed; a press moves it (inside) or resizes it (rim).
    if (selectionMode === 'circle' && cropHasBox()) {
      const cx = (selectionStart.x + selectionEnd.x) / 2;
      const cy = (selectionStart.y + selectionEnd.y) / 2;
      const rx = Math.abs(selectionEnd.x - selectionStart.x) / 2;
      const ry = Math.abs(selectionEnd.y - selectionStart.y) / 2;
      const dx = x - cx, dy = y - cy;
      const normDist = (rx > 0 && ry > 0) ? Math.sqrt((dx / rx) ** 2 + (dy / ry) ** 2) : 2;
      if (normDist > 1.3) return true;                       // well outside — leave it alone
      const type = normDist < 0.85 ? 'move' : 'resize';
      circleCropInteractRef.current = { type, startX: x, startY: y, origStart: { ...selectionStart }, origEnd: { ...selectionEnd } };
      // Where the rim was taken hold of, so the drag can be read as "outward" or "inward"
      // rather than as "right and down" — see the move handler.
      circleCropInteractRef.current.grabDist = Math.max(1, Math.hypot(x - cx, y - cy));
      cropDragMovedRef.current = false;
      setCanvasCursor(type === 'move' ? 'grabbing' : 'nwse-resize');
      return true;
    }

    // Rectangle crop: same idea, with a near-edge band that resizes.
    if (selectionMode === 'rectangle' && cropHasBox()) {
      const rx1 = Math.min(selectionStart.x, selectionEnd.x), rx2 = Math.max(selectionStart.x, selectionEnd.x);
      const ry1 = Math.min(selectionStart.y, selectionEnd.y), ry2 = Math.max(selectionStart.y, selectionEnd.y);
      const E = CROP_EDGE_PX;
      if (x < rx1 - E || x > rx2 + E || y < ry1 - E || y > ry2 + E) return true;   // outside
      const inside = x > rx1 + E && x < rx2 - E && y > ry1 + E && y < ry2 - E;
      const type = inside ? 'move' : 'resize';
      // WHICH edges were taken hold of. The drag used to move the far corner whatever you had
      // hold of, so taking the left edge and pulling left — plainly "make it wider" — moved the
      // right edge left instead and the box shrank. Each grabbed edge now follows the finger.
      const gripX = Math.abs(x - rx1) <= E ? -1 : Math.abs(x - rx2) <= E ? 1 : 0;
      const gripY = Math.abs(y - ry1) <= E ? -1 : Math.abs(y - ry2) <= E ? 1 : 0;
      circleCropInteractRef.current = {
        type, startX: x, startY: y,
        origStart: { ...selectionStart }, origEnd: { ...selectionEnd },
        gripX: (gripX || gripY) ? gripX : 1, gripY: (gripX || gripY) ? gripY : 1,
      };
      cropDragMovedRef.current = false;
      setCanvasCursor(type === 'move' ? 'grabbing' : 'nwse-resize');
      return true;
    }

    // First placement / freeform start.
    setSelectionStart({ x, y });
    if (selectionMode === 'freeform') {
      cropActiveRef.current = true;
      cropPathRef.current = [{ x, y }];
      setSelectionPath([{ x, y }]);
      setSelectionTouchPos({ x, y });
    } else {
      setSelectionEnd({ x, y });
    }
    return true;
  };

  const cropPointerMove = (rawX, rawY) => {
    if (activeTool !== 'selection' || !selectionMode) return false;
    const { x, y } = penPoint(rawX, rawY);

    // Path tools are gated on the ref, not `isSelecting` — see cropPathRef above.
    if (cropActiveRef.current && selectionMode === 'smart') {
      if (smartDetectedRef.current) return true;                 // result showing — frozen
      const last = cropPathRef.current[cropPathRef.current.length - 1];
      if (!last || Math.abs(x - last.x) >= 3 || Math.abs(y - last.y) >= 3) {
        cropPathRef.current = [...cropPathRef.current, { x, y }];
        setSelectionPath(prev => [...prev, { x, y }]);
        setSelectionEnd({ x, y });
      }
      setSelectionTouchPos({ x, y });
      return true;
    }
    if (cropActiveRef.current && selectionMode === 'freeform') {
      cropPathRef.current = [...cropPathRef.current, { x, y }];
      setSelectionPath(prev => [...prev, { x, y }]);
      setSelectionTouchPos({ x, y });
      return true;
    }

    if (!isSelecting) return false;

    if (selectionMode === 'circle' || selectionMode === 'rectangle') {
      const it = circleCropInteractRef.current;
      if (it) {
        cropDragMovedRef.current = true;     // a real drag — the click it ends with is not a click
        const { type, startX, startY, origStart, origEnd } = it;
        const dx = x - startX, dy = y - startY;
        if (type === 'move') {
          setSelectionStart({ x: origStart.x + dx, y: origStart.y + dy });
          setSelectionEnd  ({ x: origEnd.x   + dx, y: origEnd.y   + dy });
        } else if (selectionMode === 'circle') {
          // Grow away from the centre, shrink towards it — whichever side of the rim is being
          // held. It used to add the drag's x to the radius and its y to the other radius, so
          // pulling the LEFT rim leftwards (out) subtracted from the radius and the circle got
          // smaller: right on one side of it, backwards on the other.
          //
          // ── Each radius moves on its own ──────────────────────────────────────────────────
          // The fix for that scaled BOTH radii by ONE number — the distance from the centre over
          // the distance it was grabbed at. That is uniform scaling, so the ratio between the two
          // radii is mathematically invariant: whatever shape the oval started as, it stayed, and
          // an oval could never be pulled back to round. Since it is born around a picture's
          // bounding box, it was always born oval, and round was simply unreachable.
          //
          // So the grab is read per axis instead. The weights are where on the rim it was taken
          // hold of: ~1 at the left/right rim and ~0 at the top/bottom, and both on the diagonal.
          // A side handle therefore changes one radius and leaves the other where it was, a
          // corner changes both, and the shape follows the drag in whatever direction it goes.
          const origRx = Math.abs(origEnd.x - origStart.x) / 2;
          const origRy = Math.abs(origEnd.y - origStart.y) / 2;
          const cx = (origStart.x + origEnd.x) / 2, cy = (origStart.y + origEnd.y) / 2;
          const wx = Math.min(1, Math.abs(startX - cx) / Math.max(1, origRx));
          const wy = Math.min(1, Math.abs(startY - cy) / Math.max(1, origRy));
          // 0.35 ≈ 20° off the axis: anything nearer the side than that is a side grab.
          const newRx = wx > 0.35 ? Math.max(10, Math.abs(x - cx)) : origRx;
          const newRy = wy > 0.35 ? Math.max(10, Math.abs(y - cy)) : origRy;
          setSelectionStart({ x: cx - newRx, y: cy - newRy });
          setSelectionEnd  ({ x: cx + newRx, y: cy + newRy });
        } else {
          // Each edge that was taken hold of follows the finger; the opposite edge stays put.
          const MIN = 20;
          let nx1 = Math.min(origStart.x, origEnd.x), nx2 = Math.max(origStart.x, origEnd.x);
          let ny1 = Math.min(origStart.y, origEnd.y), ny2 = Math.max(origStart.y, origEnd.y);
          if (it.gripX < 0) nx1 = Math.min(nx1 + dx, nx2 - MIN);
          else if (it.gripX > 0) nx2 = Math.max(nx2 + dx, nx1 + MIN);
          if (it.gripY < 0) ny1 = Math.min(ny1 + dy, ny2 - MIN);
          else if (it.gripY > 0) ny2 = Math.max(ny2 + dy, ny1 + MIN);
          setSelectionStart({ x: nx1, y: ny1 });
          setSelectionEnd  ({ x: nx2, y: ny2 });
        }
      }
      return true;   // no interaction in progress → the overlay stays put
    }
    return true;     // a path tool with the gate down: consumed, nothing to draw
  };

  const cropPointerUp = () => {
    if (activeTool !== 'selection' || !selectionMode) return false;
    cropActiveRef.current = false;
    if (selectionMode === 'freeform' || selectionMode === 'smart') setSelectionTouchPos(null);

    // Circle / rectangle: releasing ends the drag only; the crop itself runs from the toolbar.
    if (selectionMode === 'circle' || selectionMode === 'rectangle') {
      circleCropInteractRef.current = null;
      setCanvasCursor('move');
      return true;
    }
    // Freeform: keep the drawn path visible so the user can pick Copy or Cut. `isSelecting`
    // goes off so later moves can't extend the polygon (the buttons key off the path length).
    if (selectionMode === 'freeform') {
      setIsSelecting(false);
      return true;
    }
    // Smart: lasso released → detect inside its bounding box.
    if (selectionMode === 'smart') {
      // Already detected, or a detection is already running from the twin event this same
      // release produced (see smartCropBusyRef) — leave it be rather than resetting it below.
      if (smartDetectedRef.current || smartCropBusyRef.current) return true;
      const path = cropPathRef.current;
      cropPathRef.current = [];
      if (path.length > 3) {
        const xs = path.map(p => p.x), ys = path.map(p => p.y);
        const sx = Math.min(...xs), sy = Math.min(...ys);
        const sw = Math.max(...xs) - sx, sh = Math.max(...ys) - sy;
        if (sw > 10 && sh > 10) {
          runSmartCrop(sx + sw / 2, sy + sh / 2, { x: sx, y: sy, w: sw, h: sh }, path);
          return true;
        }
      }
      smartDetectedRef.current = false;
      setSmartDetected(false);
      setIsSelecting(false);
      setSelectionStart({ x: 0, y: 0 }); setSelectionEnd({ x: 0, y: 0 });
      setSelectionPath([]);
      return true;
    }
    return false;
  };

  const handleMouseDown = (e, imageId, action) => {
    if (pendingNavPath) setPendingNavPath(null);
    if (pendingClear) cancelPendingClear();
    markDirty();
    e.preventDefault();
    e.stopPropagation();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const image = canvasImages.find(img => img.id === imageId);
    if (!image) return;

    // Multi-drag: pressing on one of the already-multi-selected images/shapes
    if (imageSelectionMode && selectedShapeIds.size > 1 && selectedShapeIds.has(imageId)) {
      shapeBeforeRef.current = canvasShapesRef.current;
      dragStartImagesRef.current = canvasImagesRef.current;
      dragStartPathsRef.current = drawingPaths;
      const _msShapes = canvasShapesRef.current.filter(s => selectedShapeIds.has(s.id));
      const _msImages = canvasImagesRef.current.filter(i => selectedShapeIds.has(i.id));
      const _msInk    = drawingPaths.filter(p => selectedShapeIds.has(p.id));
      multiDragRef.current = [
        ..._msShapes.map(s => ({ id: s.id, offsetX: x - (s.x1 / 100) * rect.width, offsetY: y - (s.y1 / 100) * rect.height })),
        ..._msImages.map(i => ({ id: i.id, offsetX: x - (i.x / 100) * rect.width, offsetY: y - (i.y / 100) * rect.height, isImage: true })),
        ..._msInk.map(p => {
          const o = inkPathOriginPct(p);
          return o && { id: p.id, offsetX: x - (o.xPct / 100) * rect.width, offsetY: y - (o.yPct / 100) * rect.height, isInk: true };
        }).filter(Boolean),
      ];
      return;
    }
    
    // Select the image if it's not already selected
    if (selectedImage !== imageId) {
      setSelectedImage(imageId);
      setResizeWidth(image.width);
      setResizeHeight(image.height);
      setShowImageControls(imageId);
    }

    // Clicking an image while brightness/contrast is active deactivates the sub-tool
    if (magicSubTool === 'brightness' || magicSubTool === 'contrast') {
      setMagicSubTool(null);
      setCurrentControlSection(null);
      setShowTopControls(false);
    }

    if (action === 'move') {
      dragStartImagesRef.current = canvasImagesRef.current;
      setDraggingImage(imageId);
      dragOffsetRef.current = ({
        x: x - (image.x * rect.width / 100),
        y: y - (image.y * rect.height / 100)
      });
      // Plain move ? keep current tool/section, do not activate resize
    } else if (action === 'resize') {
      resizeStartImagesRef.current = canvasImagesRef.current;
      setResizingImage(imageId);
      dragOffsetRef.current = ({ x, y });
      
      // Show resize controls when resize handle is clicked
      setActiveTool('resize');
      setCurrentControlSection(null);
      setShowTopControls(false);
      setImageSelectionMode(false);
    }
  };

  // Touch handlers
  const handleTouchStart = (e, imageId, action) => {
    if (!e.touches[0]) return;
    e.preventDefault();
    
    const touch = e.touches[0];
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    
    if (action === 'move' || action === 'resize') {
      const image = canvasImages.find(img => img.id === imageId);
      if (!image) return;

      // Multi-drag: pressing on one of the already-multi-selected images/shapes
      if (imageSelectionMode && selectedShapeIds.size > 1 && selectedShapeIds.has(imageId)) {
        shapeBeforeRef.current = canvasShapesRef.current;
        dragStartImagesRef.current = canvasImagesRef.current;
        dragStartPathsRef.current = drawingPaths;
        const _msShapes = canvasShapesRef.current.filter(s => selectedShapeIds.has(s.id));
        const _msImages = canvasImagesRef.current.filter(i => selectedShapeIds.has(i.id));
        const _msInk    = drawingPaths.filter(p => selectedShapeIds.has(p.id));
        multiDragRef.current = [
          ..._msShapes.map(s => ({ id: s.id, offsetX: x - (s.x1 / 100) * rect.width, offsetY: y - (s.y1 / 100) * rect.height })),
          ..._msImages.map(i => ({ id: i.id, offsetX: x - (i.x / 100) * rect.width, offsetY: y - (i.y / 100) * rect.height, isImage: true })),
          ..._msInk.map(p => {
            const o = inkPathOriginPct(p);
            return o && { id: p.id, offsetX: x - (o.xPct / 100) * rect.width, offsetY: y - (o.yPct / 100) * rect.height, isInk: true };
          }).filter(Boolean),
        ];
        return;
      }
      
      // Select the image if it's not already selected
      if (selectedImage !== imageId) {
        setSelectedImage(imageId);
        setResizeWidth(image.width);
        setResizeHeight(image.height);
        setShowImageControls(imageId);
      }
      
      if (action === 'move') {
        dragStartImagesRef.current = canvasImagesRef.current;
        setDraggingImage(imageId);
        dragOffsetRef.current = ({
          x: x - (image.x * rect.width / 100),
          y: y - (image.y * rect.height / 100)
        });
        // Plain touch move ? keep current tool/section, do not activate resize
      }
    } else if (activeTool === 'draw' && (drawingMode === 'pen' || drawingMode === 'pencil' || drawingMode === 'eraser')) {
      setIsDrawing(true);
      lastPenPointRef.current = { x, y }; setCurrentPath([{ x, y }]);
    } else if (cropPointerDown(x, y)) {
      // crop handled by the shared implementation
    }
  };

  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Crop first, in the same position handleTouchMove checks it, so the two handlers
    // resolve a move in the same order.
    if (cropPointerMove(x, y)) return;

    // Marquee selection drag
    if (isMarqueeSelectingRef.current) {
      const xPct = (x / rect.width) * 100;
      const yPct = (y / rect.height) * 100;
      const mq = { ...marqueeRectRef.current, x2: xPct, y2: yPct };
      marqueeRectRef.current = mq;
      setMarqueeRect(mq);
      return;
    }

    // Multi-shape drag
    if (multiDragRef.current) {
      shapeDraggedRef.current = true;
      const items = multiDragRef.current;
      setCanvasShapes(prev => prev.map(s => {
        const item = items.find(m => m.id === s.id);
        if (!item) return s;
        const newX1 = ((x - item.offsetX) / rect.width) * 100;
        const newY1 = ((y - item.offsetY) / rect.height) * 100;
        const dx = newX1 - s.x1, dy = newY1 - s.y1;
        return { ...s, x1: s.x1 + dx, y1: s.y1 + dy, x2: s.x2 + dx, y2: s.y2 + dy };
      }));
      setCanvasImages(prev => prev.map(img => {
        const item = items.find(m => m.id === img.id && m.isImage);
        if (!item) return img;
        return { ...img, x: ((x - item.offsetX) / rect.width) * 100, y: ((y - item.offsetY) / rect.height) * 100 };
      }));
      // Ink strokes swept up into the same multi-drag (a marquee that covered both a shape/
      // image and hand-drawn lines) -- points are stored in the path's OWN canvasW/canvasH
      // pixels, so the move is computed in percent (inkPathOriginPct) then converted back.
      setDrawingPaths(prev => prev.map(p => {
        const item = items.find(m => m.id === p.id && m.isInk);
        if (!item) return p;
        const o = inkPathOriginPct(p);
        if (!o) return p;
        const newXPct = ((x - item.offsetX) / rect.width) * 100;
        const newYPct = ((y - item.offsetY) / rect.height) * 100;
        const dxPx = ((newXPct - o.xPct) / 100) * p.canvasW;
        const dyPx = ((newYPct - o.yPct) / 100) * p.canvasH;
        return { ...p, path: p.path.map(pt => ({ ...pt, x: pt.x + dxPx, y: pt.y + dyPx })) };
      }));
      return;
    }

    // Shape resizing
    if (orbitShapeRef.current) { applyShapeOrbit(x, y); return; }
    if (imageOrbitRef.current) { applyImageOrbit(x, y); return; }

    if (rotatingShapeRef.current) {
      const { id, cx, cy } = rotatingShapeRef.current;
      const deg = Math.atan2(y - cy, x - cx) * (180 / Math.PI) + 90;
      setCanvasShapes(prev => prev.map(s => s.id === id ? { ...s, rotation: deg } : s));
      return;
    }

    if (resizingShapeRef.current) {
      const { corner, id } = resizingShapeRef.current;
      const xPct = (x / rect.width) * 100;
      const yPct = (y / rect.height) * 100;
      // Pre-compute font size sync for text/emoji shapes
      const _rsShape = canvasShapesRef.current?.find(s => s.id === id);
      // Plain text sizes itself in resizeTextShape below; this is for emoji and smileys.
      const _rsTextOnly = _rsShape && _rsShape.type === 'text' && !isEmojiGlyph(_rsShape);
      if (_rsTextOnly) {
        const _s0 = shapeBeforeRef.current?.find(s => s.id === id) || _rsShape;
        const _r = resizeTextShape(_s0, corner, xPct, yPct, rect);
        if (_r.fontSize !== _rsShape.fontSize) setShapeFontSize(Math.round(_r.fontSize));
        setCanvasShapes(prev => prev.map(s => (s.id === id ? _r : s)));
        return;
      }
      if (_rsShape && (_rsShape.type === 'text' || _rsShape.type === 'emoji')) {
        let _ny1 = _rsShape.y1, _ny2 = _rsShape.y2;
        if (corner === 'se' || corner === 'p2' || corner === 'sw') { _ny2 = yPct; }
        else if (corner === 'ne' || corner === 'nw' || corner === 'p1') { _ny1 = yPct; }
        else if (corner === 'n') { _ny1 = yPct; }
        else if (corner === 's') { _ny2 = yPct; }
        const _newFs = Math.max(8, Math.round(Math.abs(_ny2 - _ny1) / 100 * rect.height));
        setShapeFontSize(_newFs);
      }
      setCanvasShapes(prev => prev.map(s => {
        if (s.id !== id) return s;
        const ns = { ...s };
        if (corner === 'se' || corner === 'p2') { ns.x2 = xPct; ns.y2 = yPct; }
        else if (corner === 'sw') { ns.x1 = xPct; ns.y2 = yPct; }
        else if (corner === 'ne') { ns.x2 = xPct; ns.y1 = yPct; }
        else if (corner === 'nw' || corner === 'p1') { ns.x1 = xPct; ns.y1 = yPct; }
        else if (corner === 'n') ns.y1 = yPct;
        else if (corner === 's') ns.y2 = yPct;
        else if (corner === 'e') ns.x2 = xPct;
        else if (corner === 'w') ns.x1 = xPct;
        if (lockAspectRatio && (corner === 'se' || corner === 'sw' || corner === 'ne' || corner === 'nw' || corner === 'p1' || corner === 'p2')) {
          const origW = Math.abs(s.x2 - s.x1);
          const origH = Math.abs(s.y2 - s.y1);
          if (origW > 0 && origH > 0) {
            const ratio = origW / origH;
            const newW = Math.abs(ns.x2 - ns.x1);
            const newH = Math.abs(ns.y2 - ns.y1);
            const wChanged = Math.abs(newW - origW) >= Math.abs(newH - origH);
            const signX = ns.x2 >= ns.x1 ? 1 : -1;
            const signY = ns.y2 >= ns.y1 ? 1 : -1;
            if (wChanged) { const lockedH = newW / ratio; if (corner === 'nw' || corner === 'ne' || corner === 'p1') ns.y1 = ns.y2 - signY * lockedH; else ns.y2 = ns.y1 + signY * lockedH; } else { const lockedW = newH * ratio; if (corner === 'nw' || corner === 'sw' || corner === 'p1') ns.x1 = ns.x2 - signX * lockedW; else ns.x2 = ns.x1 + signX * lockedW; }
          }
        }
        // Apply aspect ratio lock for corner handles
        if (lockAspectRatio && (corner === 'se' || corner === 'sw' || corner === 'ne' || corner === 'nw' || corner === 'p1' || corner === 'p2')) {
          const origW = Math.abs(s.x2 - s.x1);
          const origH = Math.abs(s.y2 - s.y1);
          if (origW > 0 && origH > 0) {
            const ratio = origW / origH;
            const newW = Math.abs(ns.x2 - ns.x1);
            const newH = Math.abs(ns.y2 - ns.y1);
            const wChanged = Math.abs(newW - origW) >= Math.abs(newH - origH);
            const signX = ns.x2 >= ns.x1 ? 1 : -1;
            const signY = ns.y2 >= ns.y1 ? 1 : -1;
            if (wChanged) {
              const lockedH = newW / ratio;
              if (corner === 'nw' || corner === 'ne' || corner === 'p1') ns.y1 = ns.y2 - signY * lockedH;
              else ns.y2 = ns.y1 + signY * lockedH;
            } else {
              const lockedW = newH * ratio;
              if (corner === 'nw' || corner === 'sw' || corner === 'p1') ns.x1 = ns.x2 - signX * lockedW;
              else ns.x2 = ns.x1 + signX * lockedW;
            }
          }
        }        if (ns.type === 'text' || ns.type === 'emoji') {
          const newFs = Math.max(8, Math.round(Math.abs(ns.y2 - ns.y1) / 100 * rect.height));
          // Text is NOT scaled by its box: a wider box takes more words per line, a narrower one
          // fewer, and the letters stay the size they were set to (see wrapTextLines). Only an
          // emoji — a picture, not writing — grows with its box, whether it was placed as a
          // stamp or as one of the smiley characters (see isEmojiGlyph).
          if (ns.type === 'emoji') { ns.fontSize = newFs; ns.emojiSize = newFs; }
          else if (isEmojiGlyph(ns)) {
            ns.fontSize = emojiFsForBox(
              Math.abs(ns.x2 - ns.x1) / 100 * rect.width,
              Math.abs(ns.y2 - ns.y1) / 100 * rect.height,
            );
          }
        }
        return ns;
      }));
      return;
    }

    // Shape dragging
    if (draggingShapeRef.current) {
      const { id, offsetX, offsetY } = draggingShapeRef.current;
      const xPct = ((x - offsetX) / rect.width) * 100;
      // First real movement of this press: this is now unmistakably a MOVE, not just the tap
      // that selected it -- narrow the top bar down to the coordinates alone (buttons and any
      // colour palette hide themselves while selectMoveControlMode/currentControlSection stay
      // this way; see the JSX). Harmless to call on every move tick -- React skips the re-render
      // once the values stop changing.
      if (!shapeDraggedRef.current) { setCurrentControlSection('selectmove'); setSelectMoveControlMode('position'); setShowTopControls(true); setIsMovingShape(true); }
      shapeDraggedRef.current = true;
      const yPct = ((y - offsetY) / rect.height) * 100;
      const _prevS = canvasShapesRef.current?.find(s => s.id === id);
      if (_prevS) {
        const _ddx = xPct - _prevS.x1, _ddy = yPct - _prevS.y1;
        if (Math.abs(_ddx) > 0.05) lastDragDirRef.current.x = _ddx > 0 ? 1 : -1;
        if (Math.abs(_ddy) > 0.05) lastDragDirRef.current.y = _ddy > 0 ? 1 : -1;
      }
      setCanvasShapes(prev => prev.map(s => {
        if (s.id !== id) return s;
        const dx = xPct - s.x1;
        const dy = yPct - s.y1;
        return { ...s, x1: s.x1 + dx, y1: s.y1 + dy, x2: s.x2 + dx, y2: s.y2 + dy };
      }));
      return;
    }

    // Ink rotate / resize / drag -- the hand-drawn twins of the three blocks above. A stroke
    // is pixels on one shared canvas, so there is no element to transform: every gesture
    // rewrites the stored points, always from the snapshot taken when the gesture began
    // (origPath) so repeated ticks can't compound rounding into drift.
    if (rotatingInkRef.current) { applyInkRotate(x, y, rect); return; }
    if (resizingInkRef.current) { applyInkResize(x, y, rect); return; }
    if (draggingInkRef.current) { applyInkDrag(x, y, rect); return; }

    // Shape drawing preview
    if (isDrawingShape) {
      const _p = { x: (x / rect.width) * 100, y: (y / rect.height) * 100 };
      if (isFreeTool(activeShapeTool)) { freeDraftRef.current = [...freeDraftRef.current, _p]; setFreeDraft(freeDraftRef.current); }
      setShapeDrawEnd(_p);
      return;
    }

    if (isDrawing && drawingMode === 'spray') {
      const N = 30, radius = brushSize * 1.2;
      const newDots = [];
      for (let i = 0; i < N; i++) {
        // Uniform disc distribution (not sqrt) keeps dots concentrated at centre
        const angle = (i / N) * Math.PI * 2 + Math.random() * 0.4;
        const r = (0.2 + Math.random() * 0.8) * radius;
        newDots.push({ x: x + r * Math.cos(angle), y: y + r * Math.sin(angle) });
      }
      setCurrentPath(prev => [...prev, ...newDots]);
      return;
    }
    if (isDrawing && (drawingMode === 'pen' || drawingMode === 'pencil' || drawingMode === 'eraser')) {
      // Interpolate intermediate points to avoid gaps on fast strokes
      const last = lastPenPointRef.current || currentPath[currentPath.length - 1];
      lastPenPointRef.current = { x, y };
      const interp = [];
      if (last) {
        const dx = x - last.x, dy = y - last.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const step = Math.max(1, brushSize * 0.3);
        if (dist > step) {
          const steps = Math.ceil(dist / step);
          for (let k = 1; k < steps; k++) {
            interp.push({ x: last.x + dx * k / steps, y: last.y + dy * k / steps });
          }
        }
      }
      setCurrentPath(prev => [...prev, ...interp, { x, y }]);
      if (drawingMode === 'eraser') setEraserPos({ x, y });
      return;
    }
    if (activeTool === 'draw' && drawingMode === 'eraser' && !isDrawing) {
      setEraserPos({ x, y });
    }
    
    if (draggingImage) {
      // Narrow the top bar to just the coordinates while an actual move is in progress. Safe
      // to call every move tick -- React skips the re-render once the mode's already set.
      setCurrentControlSection('selectmove'); setSelectMoveControlMode('position'); setShowTopControls(true);
      const _newImgX = ((x - dragOffsetRef.current.x) / rect.width) * 100;
      const _newImgY = ((y - dragOffsetRef.current.y) / rect.height) * 100;
      const _curDragImg = canvasImagesRef.current?.find(i => i.id === draggingImage);
      if (_curDragImg) {
        const _didx = _newImgX - _curDragImg.x, _didy = _newImgY - _curDragImg.y;
        if (Math.abs(_didx) > 0.05) lastDragDirRef.current.x = _didx > 0 ? 1 : -1;
        if (Math.abs(_didy) > 0.05) lastDragDirRef.current.y = _didy > 0 ? 1 : -1;
      }
      setCanvasImages(prev => prev.map(img => {
        if (img.id === draggingImage) {
          const newX = ((x - dragOffsetRef.current.x) / rect.width) * 100;
          const newY = ((y - dragOffsetRef.current.y) / rect.height) * 100;
          // Clamp so the visual (rotated) bounding box stays within the canvas
          const _rotRad = ((img.rotation || 0) * Math.PI) / 180;
          const _absC = Math.abs(Math.cos(_rotRad));
          const _absS = Math.abs(Math.sin(_rotRad));
          const _wpx = (img.width  / 21)   * rect.width;
          const _hpx = (img.height / 29.7) * rect.height;
          const _hvW = _wpx / 2 * _absC + _hpx / 2 * _absS;
          const _hvH = _wpx / 2 * _absS + _hpx / 2 * _absC;
          const _xMin = (_hvW - _wpx / 2) / rect.width  * 100;
          const _xMax = (rect.width  - _hvW - _wpx / 2) / rect.width  * 100;
          const _yMin = (_hvH - _hpx / 2) / rect.height * 100;
          const _yMax = (rect.height - _hvH - _hpx / 2) / rect.height * 100;
          return {
            ...img,
            x: Math.max(_xMin, Math.min(_xMax, newX)),
            y: Math.max(_yMin, Math.min(_yMax, newY))
          };
        }
        return img;
      }));
    }
    
    if (resizingImage) {
      const dir = resizeDirectionRef.current || 'se';
      // Capture previous position BEFORE updating ref so the updater closure is correct
      const prevX = dragOffsetRef.current.x;
      const prevY = dragOffsetRef.current.y;
      dragOffsetRef.current = { x, y };
      setCanvasImages(prev => prev.map(img => {
        if (img.id !== resizingImage) return img;
        const deltaX = x - prevX;
        const deltaY = y - prevY;
        // Un-rotate screen deltas into image-local space for dimension changes
        const rotRad = ((img.rotation || 0) * Math.PI) / 180;
        const cosR = Math.cos(rotRad);
        const sinR = Math.sin(rotRad);
        const localDX = deltaX * cosR + deltaY * sinR;
        const localDY = -deltaX * sinR + deltaY * cosR;
        const deltaCmX = (localDX / rect.width) * 21;
        const deltaCmY = (localDY / rect.height) * 29.7;
        let newWidth = img.width;
        let newHeight = img.height;
        switch (dir) {
          case 'se': newWidth = Math.max(1, Math.min(21, img.width + deltaCmX)); newHeight = Math.max(1, Math.min(29.7, img.height + deltaCmY)); break;
          case 'sw': newWidth = Math.max(1, Math.min(21, img.width - deltaCmX)); newHeight = Math.max(1, Math.min(29.7, img.height + deltaCmY)); break;
          case 'ne': newWidth = Math.max(1, Math.min(21, img.width + deltaCmX)); newHeight = Math.max(1, Math.min(29.7, img.height - deltaCmY)); break;
          case 'nw': newWidth = Math.max(1, Math.min(21, img.width - deltaCmX)); newHeight = Math.max(1, Math.min(29.7, img.height - deltaCmY)); break;
          case 'n':  newHeight = Math.max(1, Math.min(29.7, img.height - deltaCmY)); break;
          case 's':  newHeight = Math.max(1, Math.min(29.7, img.height + deltaCmY)); break;
          case 'e':  newWidth = Math.max(1, Math.min(21, img.width + deltaCmX)); break;
          case 'w':  newWidth = Math.max(1, Math.min(21, img.width - deltaCmX)); break;
        }
        // Aspect ratio lock
        if (lockAspectRatio && img.width > 0 && img.height > 0) {
          const _ar = img.width / img.height;
          if (dir === 'e' || dir === 'w') {
            newHeight = Math.max(1, Math.min(29.7, newWidth / _ar));
          } else if (dir === 'n' || dir === 's') {
            newWidth = Math.max(1, Math.min(21, newHeight * _ar));
          } else {
            if (Math.abs(newWidth - img.width) >= Math.abs(newHeight - img.height)) {
              newHeight = Math.max(1, Math.min(29.7, newWidth / _ar));
            } else {
              newWidth = Math.max(1, Math.min(21, newHeight * _ar));
            }
          }
        }
        // Anchor-point position correction: keeps the opposite corner fixed under any rotation.
        // aX/aY are the normalized coords {-1,0,1} of the corner that must NOT move.
        const _anchors = { se:[-1,-1], sw:[1,-1], ne:[-1,1], nw:[1,1], n:[0,1], s:[0,-1], e:[-1,0], w:[1,0] };
        const [aX, aY] = _anchors[dir] || [-1,-1];
        const dw_px = (newWidth  - img.width)  / 21   * rect.width;
        const dh_px = (newHeight - img.height) / 29.7 * rect.height;
        const dx_px = -dw_px / 2 * (1 + aX * cosR) + dh_px / 2 * aY * sinR;
        const dy_px = -dh_px / 2 * (1 + aY * cosR) - dw_px / 2 * aX * sinR;
        const newX = img.x + dx_px / rect.width  * 100;
        const newY = img.y + dy_px / rect.height * 100;
        // Rotation-aware minimum bounds so the layout box can go negative when needed
        const _absC = Math.abs(cosR);
        const _absS = Math.abs(sinR);
        const _nWpx = (newWidth  / 21)   * rect.width;
        const _nHpx = (newHeight / 29.7) * rect.height;
        const _xMin = (_nWpx / 2 * _absC + _nHpx / 2 * _absS - _nWpx / 2) / rect.width  * 100;
        const _yMin = (_nWpx / 2 * _absS + _nHpx / 2 * _absC - _nHpx / 2) / rect.height * 100;
        setResizeWidth(newWidth);
        setResizeHeight(newHeight);
        return { ...img, width: newWidth, height: newHeight, x: Math.max(_xMin, newX), y: Math.max(_yMin, newY) };
      }));
    }

    if (rotatingImage && rotateImageCenterRef.current) {
      const { cx, cy } = rotateImageCenterRef.current;
      const deg = Math.atan2(y - cy, x - cx) * (180 / Math.PI) + 90;
      setCanvasImages(prev => prev.map(i => i.id === rotatingImage ? { ...i, rotation: deg } : i));
      return;
    }
  };

  // Touch move handler
  const handleTouchMove = (e) => {
    if (!e.touches[0]) return;
    e.preventDefault();
    
    const touch = e.touches[0];
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;

    if (cropPointerMove(x, y)) return;

    // Marquee selection drag (touch)
    if (isMarqueeSelectingRef.current) {
      const xPct = (x / rect.width) * 100;
      const yPct = (y / rect.height) * 100;
      const mq = { ...marqueeRectRef.current, x2: xPct, y2: yPct };
      marqueeRectRef.current = mq;
      setMarqueeRect(mq);
      return;
    }

    // Multi-shape drag (touch)
    if (multiDragRef.current) {
      shapeDraggedRef.current = true;
      const items = multiDragRef.current;
      setCanvasShapes(prev => prev.map(s => {
        const item = items.find(m => m.id === s.id);
        if (!item) return s;
        const newX1 = ((x - item.offsetX) / rect.width) * 100;
        const newY1 = ((y - item.offsetY) / rect.height) * 100;
        const dx = newX1 - s.x1, dy = newY1 - s.y1;
        return { ...s, x1: s.x1 + dx, y1: s.y1 + dy, x2: s.x2 + dx, y2: s.y2 + dy };
      }));
      setCanvasImages(prev => prev.map(img => {
        const item = items.find(m => m.id === img.id && m.isImage);
        if (!item) return img;
        return { ...img, x: ((x - item.offsetX) / rect.width) * 100, y: ((y - item.offsetY) / rect.height) * 100 };
      }));
      // Ink strokes swept up into the same multi-drag (a marquee that covered both a shape/
      // image and hand-drawn lines) -- points are stored in the path's OWN canvasW/canvasH
      // pixels, so the move is computed in percent (inkPathOriginPct) then converted back.
      setDrawingPaths(prev => prev.map(p => {
        const item = items.find(m => m.id === p.id && m.isInk);
        if (!item) return p;
        const o = inkPathOriginPct(p);
        if (!o) return p;
        const newXPct = ((x - item.offsetX) / rect.width) * 100;
        const newYPct = ((y - item.offsetY) / rect.height) * 100;
        const dxPx = ((newXPct - o.xPct) / 100) * p.canvasW;
        const dyPx = ((newYPct - o.yPct) / 100) * p.canvasH;
        return { ...p, path: p.path.map(pt => ({ ...pt, x: pt.x + dxPx, y: pt.y + dyPx })) };
      }));
      return;
    }

    // Image rotating (touch)
    if (rotatingImage && rotateImageCenterRef.current) {
      const { cx, cy } = rotateImageCenterRef.current;
      const deg = Math.atan2(y - cy, x - cx) * (180 / Math.PI) + 90;
      setCanvasImages(prev => prev.map(i => i.id === rotatingImage ? { ...i, rotation: deg } : i));
      return;
    }

    // Shape resizing (touch)
    if (orbitShapeRef.current) { applyShapeOrbit(x, y); return; }
    if (imageOrbitRef.current) { applyImageOrbit(x, y); return; }

    if (rotatingShapeRef.current) {
      const { id, cx, cy } = rotatingShapeRef.current;
      const deg = Math.atan2(y - cy, x - cx) * (180 / Math.PI) + 90;
      setCanvasShapes(prev => prev.map(s => s.id === id ? { ...s, rotation: deg } : s));
      return;
    }

    if (resizingShapeRef.current) {
      const { corner, id } = resizingShapeRef.current;
      const xPct = (x / rect.width) * 100;
      const yPct = (y / rect.height) * 100;
      // Pre-compute font size sync for text/emoji shapes
      const _rsTShape = canvasShapesRef.current?.find(s => s.id === id);
      // Plain text sizes itself in resizeTextShape below; this is for emoji and smileys.
      const _rsTextOnly = _rsTShape && _rsTShape.type === 'text' && !isEmojiGlyph(_rsTShape);
      if (_rsTextOnly) {
        const _s0 = shapeBeforeRef.current?.find(s => s.id === id) || _rsTShape;
        const _r = resizeTextShape(_s0, corner, xPct, yPct, rect);
        if (_r.fontSize !== _rsTShape.fontSize) setShapeFontSize(Math.round(_r.fontSize));
        setCanvasShapes(prev => prev.map(s => (s.id === id ? _r : s)));
        return;
      }
      if (_rsTShape && (_rsTShape.type === 'text' || _rsTShape.type === 'emoji')) {
        let _ny1 = _rsTShape.y1, _ny2 = _rsTShape.y2;
        if (corner === 'se' || corner === 'p2' || corner === 'sw') { _ny2 = yPct; }
        else if (corner === 'ne' || corner === 'nw' || corner === 'p1') { _ny1 = yPct; }
        else if (corner === 'n') { _ny1 = yPct; }
        else if (corner === 's') { _ny2 = yPct; }
        const _newFsT = Math.max(8, Math.round(Math.abs(_ny2 - _ny1) / 100 * rect.height));
        setShapeFontSize(_newFsT);
      }
      setCanvasShapes(prev => prev.map(s => {
        if (s.id !== id) return s;
        const ns = { ...s };
        if (corner === 'se' || corner === 'p2') { ns.x2 = xPct; ns.y2 = yPct; }
        else if (corner === 'sw') { ns.x1 = xPct; ns.y2 = yPct; }
        else if (corner === 'ne') { ns.x2 = xPct; ns.y1 = yPct; }
        else if (corner === 'nw' || corner === 'p1') { ns.x1 = xPct; ns.y1 = yPct; }
        else if (corner === 'n') ns.y1 = yPct;
        else if (corner === 's') ns.y2 = yPct;
        else if (corner === 'e') ns.x2 = xPct;
        else if (corner === 'w') ns.x1 = xPct;
        if (lockAspectRatio && (corner === 'se' || corner === 'sw' || corner === 'ne' || corner === 'nw' || corner === 'p1' || corner === 'p2')) {
          const origW = Math.abs(s.x2 - s.x1);
          const origH = Math.abs(s.y2 - s.y1);
          if (origW > 0 && origH > 0) {
            const ratio = origW / origH;
            const newW = Math.abs(ns.x2 - ns.x1);
            const newH = Math.abs(ns.y2 - ns.y1);
            const wChanged = Math.abs(newW - origW) >= Math.abs(newH - origH);
            const signX = ns.x2 >= ns.x1 ? 1 : -1;
            const signY = ns.y2 >= ns.y1 ? 1 : -1;
            if (wChanged) { const lockedH = newW / ratio; if (corner === 'nw' || corner === 'ne' || corner === 'p1') ns.y1 = ns.y2 - signY * lockedH; else ns.y2 = ns.y1 + signY * lockedH; } else { const lockedW = newH * ratio; if (corner === 'nw' || corner === 'sw' || corner === 'p1') ns.x1 = ns.x2 - signX * lockedW; else ns.x2 = ns.x1 + signX * lockedW; }
          }
        }
        // Apply aspect ratio lock for corner handles
        if (lockAspectRatio && (corner === 'se' || corner === 'sw' || corner === 'ne' || corner === 'nw' || corner === 'p1' || corner === 'p2')) {
          const origW = Math.abs(s.x2 - s.x1);
          const origH = Math.abs(s.y2 - s.y1);
          if (origW > 0 && origH > 0) {
            const ratio = origW / origH;
            const newW = Math.abs(ns.x2 - ns.x1);
            const newH = Math.abs(ns.y2 - ns.y1);
            const wChanged = Math.abs(newW - origW) >= Math.abs(newH - origH);
            const signX = ns.x2 >= ns.x1 ? 1 : -1;
            const signY = ns.y2 >= ns.y1 ? 1 : -1;
            if (wChanged) {
              const lockedH = newW / ratio;
              if (corner === 'nw' || corner === 'ne' || corner === 'p1') ns.y1 = ns.y2 - signY * lockedH;
              else ns.y2 = ns.y1 + signY * lockedH;
            } else {
              const lockedW = newH * ratio;
              if (corner === 'nw' || corner === 'sw' || corner === 'p1') ns.x1 = ns.x2 - signX * lockedW;
              else ns.x2 = ns.x1 + signX * lockedW;
            }
          }
        }        if (ns.type === 'text' || ns.type === 'emoji') {
          const newFs = Math.max(8, Math.round(Math.abs(ns.y2 - ns.y1) / 100 * rect.height));
          // Same rule as the mouse twin above: writing keeps its size, a picture grows.
          if (ns.type === 'emoji') { ns.fontSize = newFs; ns.emojiSize = newFs; }
          else if (isEmojiGlyph(ns)) {
            ns.fontSize = emojiFsForBox(
              Math.abs(ns.x2 - ns.x1) / 100 * rect.width,
              Math.abs(ns.y2 - ns.y1) / 100 * rect.height,
            );
          }
        }
        return ns;
      }));
      return;
    }

    // Shape dragging (touch)
    if (draggingShapeRef.current) {
      const { id, offsetX, offsetY } = draggingShapeRef.current;
      const xPct = ((x - offsetX) / rect.width) * 100;
      const yPct = ((y - offsetY) / rect.height) * 100;
      // See the mouse-move twin of this block: first real movement narrows the top bar to
      // just the coordinates.
      if (!shapeDraggedRef.current) { setCurrentControlSection('selectmove'); setSelectMoveControlMode('position'); setShowTopControls(true); setIsMovingShape(true); }
      shapeDraggedRef.current = true;
      const _prevST = canvasShapesRef.current?.find(s => s.id === id);
      if (_prevST) {
        const _ddx = xPct - _prevST.x1, _ddy = yPct - _prevST.y1;
        if (Math.abs(_ddx) > 0.05) lastDragDirRef.current.x = _ddx > 0 ? 1 : -1;
        if (Math.abs(_ddy) > 0.05) lastDragDirRef.current.y = _ddy > 0 ? 1 : -1;
      }
      setCanvasShapes(prev => prev.map(s => {
        if (s.id !== id) return s;
        const dx = xPct - s.x1; const dy = yPct - s.y1;
        return { ...s, x1: s.x1 + dx, y1: s.y1 + dy, x2: s.x2 + dx, y2: s.y2 + dy };
      }));
      return;
    }

    // Ink rotate / resize / drag (touch) -- see the mouse-move twins of these three.
    if (rotatingInkRef.current) { applyInkRotate(x, y, rect); return; }
    if (resizingInkRef.current) { applyInkResize(x, y, rect); return; }
    if (draggingInkRef.current) { applyInkDrag(x, y, rect); return; }

    // Shape draw preview (touch)
    if (isDrawingShape) {
      const _p = { x: (x / rect.width) * 100, y: (y / rect.height) * 100 };
      if (isFreeTool(activeShapeTool)) { freeDraftRef.current = [...freeDraftRef.current, _p]; setFreeDraft(freeDraftRef.current); }
      setShapeDrawEnd(_p);
      return;
    }

    if (isDrawing && drawingMode === 'spray') {
      const N = 30, radius = brushSize * 1.2;
      const newDots = [];
      for (let i = 0; i < N; i++) {
        const angle = (i / N) * Math.PI * 2 + Math.random() * 0.4;
        const r = (0.2 + Math.random() * 0.8) * radius;
        newDots.push({ x: x + r * Math.cos(angle), y: y + r * Math.sin(angle) });
      }
      setCurrentPath(prev => [...prev, ...newDots]);
      return;
    }
    if (isDrawing && (drawingMode === 'pen' || drawingMode === 'pencil' || drawingMode === 'eraser')) {
      // Interpolate intermediate points for sharp eraser edges on touch
      const last = lastPenPointRef.current || currentPath[currentPath.length - 1];
      lastPenPointRef.current = { x, y };
      const interp = [];
      if (last) {
        const dx = x - last.x, dy = y - last.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const step = Math.max(1, brushSize * 0.3);
        if (dist > step) {
          const steps = Math.ceil(dist / step);
          for (let k = 1; k < steps; k++) {
            interp.push({ x: last.x + dx * k / steps, y: last.y + dy * k / steps });
          }
        }
      }
      setCurrentPath(prev => [...prev, ...interp, { x, y }]);
      if (drawingMode === 'eraser') setEraserPos({ x, y });
      return;
    }
    if (activeTool === 'draw' && drawingMode === 'eraser' && !isDrawing) {
      setEraserPos({ x, y });
    }
    
    if (draggingImage) {
      // See the mouse-move twin of this block.
      setCurrentControlSection('selectmove'); setSelectMoveControlMode('position'); setShowTopControls(true);
      const _newImgXT = ((x - dragOffsetRef.current.x) / rect.width) * 100;
      const _newImgYT = ((y - dragOffsetRef.current.y) / rect.height) * 100;
      const _curDragImgT = canvasImagesRef.current?.find(i => i.id === draggingImage);
      if (_curDragImgT) {
        const _didxT = _newImgXT - _curDragImgT.x, _didyT = _newImgYT - _curDragImgT.y;
        if (Math.abs(_didxT) > 0.05) lastDragDirRef.current.x = _didxT > 0 ? 1 : -1;
        if (Math.abs(_didyT) > 0.05) lastDragDirRef.current.y = _didyT > 0 ? 1 : -1;
      }
      setCanvasImages(prev => prev.map(img => {
        if (img.id === draggingImage) {
          const newX = ((x - dragOffsetRef.current.x) / rect.width) * 100;
          const newY = ((y - dragOffsetRef.current.y) / rect.height) * 100;
          // Clamp so the visual (rotated) bounding box stays within the canvas
          const _rotRad = ((img.rotation || 0) * Math.PI) / 180;
          const _absC = Math.abs(Math.cos(_rotRad));
          const _absS = Math.abs(Math.sin(_rotRad));
          const _wpx = (img.width  / 21)   * rect.width;
          const _hpx = (img.height / 29.7) * rect.height;
          const _hvW = _wpx / 2 * _absC + _hpx / 2 * _absS;
          const _hvH = _wpx / 2 * _absS + _hpx / 2 * _absC;
          const _xMin = (_hvW - _wpx / 2) / rect.width  * 100;
          const _xMax = (rect.width  - _hvW - _wpx / 2) / rect.width  * 100;
          const _yMin = (_hvH - _hpx / 2) / rect.height * 100;
          const _yMax = (rect.height - _hvH - _hpx / 2) / rect.height * 100;
          return {
            ...img,
            x: Math.max(_xMin, Math.min(_xMax, newX)),
            y: Math.max(_yMin, Math.min(_yMax, newY))
          };
        }
        return img;
      }));
    }
    
    if (resizingImage) {
      const dir = resizeDirectionRef.current || 'se';
      // Capture previous position BEFORE updating ref so the updater closure is correct
      const prevX = dragOffsetRef.current.x;
      const prevY = dragOffsetRef.current.y;
      dragOffsetRef.current = { x, y };
      setCanvasImages(prev => prev.map(img => {
        if (img.id !== resizingImage) return img;
        const deltaX = x - prevX;
        const deltaY = y - prevY;
        // Un-rotate screen deltas into image-local space for dimension changes
        const rotRad = ((img.rotation || 0) * Math.PI) / 180;
        const cosR = Math.cos(rotRad);
        const sinR = Math.sin(rotRad);
        const localDX = deltaX * cosR + deltaY * sinR;
        const localDY = -deltaX * sinR + deltaY * cosR;
        const deltaCmX = (localDX / rect.width) * 21;
        const deltaCmY = (localDY / rect.height) * 29.7;
        let newWidth = img.width;
        let newHeight = img.height;
        switch (dir) {
          case 'se': newWidth = Math.max(1, Math.min(21, img.width + deltaCmX)); newHeight = Math.max(1, Math.min(29.7, img.height + deltaCmY)); break;
          case 'sw': newWidth = Math.max(1, Math.min(21, img.width - deltaCmX)); newHeight = Math.max(1, Math.min(29.7, img.height + deltaCmY)); break;
          case 'ne': newWidth = Math.max(1, Math.min(21, img.width + deltaCmX)); newHeight = Math.max(1, Math.min(29.7, img.height - deltaCmY)); break;
          case 'nw': newWidth = Math.max(1, Math.min(21, img.width - deltaCmX)); newHeight = Math.max(1, Math.min(29.7, img.height - deltaCmY)); break;
          case 'n':  newHeight = Math.max(1, Math.min(29.7, img.height - deltaCmY)); break;
          case 's':  newHeight = Math.max(1, Math.min(29.7, img.height + deltaCmY)); break;
          case 'e':  newWidth = Math.max(1, Math.min(21, img.width + deltaCmX)); break;
          case 'w':  newWidth = Math.max(1, Math.min(21, img.width - deltaCmX)); break;
        }
        // Aspect ratio lock
        if (lockAspectRatio && img.width > 0 && img.height > 0) {
          const _ar = img.width / img.height;
          if (dir === 'e' || dir === 'w') {
            newHeight = Math.max(1, Math.min(29.7, newWidth / _ar));
          } else if (dir === 'n' || dir === 's') {
            newWidth = Math.max(1, Math.min(21, newHeight * _ar));
          } else {
            if (Math.abs(newWidth - img.width) >= Math.abs(newHeight - img.height)) {
              newHeight = Math.max(1, Math.min(29.7, newWidth / _ar));
            } else {
              newWidth = Math.max(1, Math.min(21, newHeight * _ar));
            }
          }
        }
        // Anchor-point position correction: keeps the opposite corner fixed under any rotation.
        // aX/aY are the normalized coords {-1,0,1} of the corner that must NOT move.
        const _anchors = { se:[-1,-1], sw:[1,-1], ne:[-1,1], nw:[1,1], n:[0,1], s:[0,-1], e:[-1,0], w:[1,0] };
        const [aX, aY] = _anchors[dir] || [-1,-1];
        const dw_px = (newWidth  - img.width)  / 21   * rect.width;
        const dh_px = (newHeight - img.height) / 29.7 * rect.height;
        const dx_px = -dw_px / 2 * (1 + aX * cosR) + dh_px / 2 * aY * sinR;
        const dy_px = -dh_px / 2 * (1 + aY * cosR) - dw_px / 2 * aX * sinR;
        const newX = img.x + dx_px / rect.width  * 100;
        const newY = img.y + dy_px / rect.height * 100;
        // Rotation-aware minimum bounds so the layout box can go negative when needed
        const _absC = Math.abs(cosR);
        const _absS = Math.abs(sinR);
        const _nWpx = (newWidth  / 21)   * rect.width;
        const _nHpx = (newHeight / 29.7) * rect.height;
        const _xMin = (_nWpx / 2 * _absC + _nHpx / 2 * _absS - _nWpx / 2) / rect.width  * 100;
        const _yMin = (_nWpx / 2 * _absS + _nHpx / 2 * _absC - _nHpx / 2) / rect.height * 100;
        setResizeWidth(newWidth);
        setResizeHeight(newHeight);
        return { ...img, width: newWidth, height: newHeight, x: Math.max(_xMin, newX), y: Math.max(_yMin, newY) };
      }));
    }
  };

  // Update handleMouseUp function
  const handleMouseUp = () => {
    // Marquee selection end -- find all shapes fully inside the drawn rectangle
    if (isMarqueeSelectingRef.current) {
      isMarqueeSelectingRef.current = false;
      const mq = marqueeRectRef.current;
      marqueeRectRef.current = null;
      setMarqueeRect(null);
      if (mq) {
        const minX = Math.min(mq.x1, mq.x2), maxX = Math.max(mq.x1, mq.x2);
        const minY = Math.min(mq.y1, mq.y2), maxY = Math.max(mq.y1, mq.y2);
        if (maxX - minX > 1 || maxY - minY > 1) {
          const _sel = canvasShapesRef.current.filter(s => {
            const sx1 = Math.min(s.x1, s.x2), sx2 = Math.max(s.x1, s.x2);
            const sy1 = Math.min(s.y1, s.y2), sy2 = Math.max(s.y1, s.y2);
            return sx1 <= maxX && sx2 >= minX && sy1 <= maxY && sy2 >= minY;
          });
          const _selImgs = canvasImagesRef.current.filter(img => {
            const ix1 = img.x, iy1 = img.y;
            const ix2 = img.x + (img.width / 21) * 100;
            const iy2 = img.y + (img.height / 29.7) * 100;
            return ix1 <= maxX && ix2 >= minX && iy1 <= maxY && iy2 >= minY;
          });
          // Hand-drawn ink strokes overlapping the box -- included alongside shapes/images so
          // a marquee that catches both can select and move all of them together (previously
          // only the closed shapes/images moved; any lines under the same box were left behind).
          // Only when at least one shape/image is also caught: a box over ink ALONE keeps using
          // the smart-lift branch further down (ids.length === 0), unchanged.
          const _selInk = (_sel.length > 0 || _selImgs.length > 0) ? drawingPaths.filter(p => {
            if (p.type === 'mask' || p.color === 'eraser') return false;
            const b = inkPathBBoxPct(p);
            return b && b.x1 <= maxX && b.x2 >= minX && b.y1 <= maxY && b.y2 >= minY;
          }) : [];
          const ids = [..._sel.map(s => s.id), ..._selImgs.map(i => i.id), ..._selInk.map(p => p.id)];
          setSelectedShapeIds(new Set(ids));
          if (ids.length === 1) {
            const _singleIsImg = _selImgs.length === 1 && _sel.length === 0;
            if (_singleIsImg) {
              setSelectedImage(_selImgs[0].id);
              setResizeWidth(_selImgs[0].width);
              setResizeHeight(_selImgs[0].height);
              setSelectedShapeId(null);
            } else {
              setSelectedShapeId(ids[0]);
              setSelectedImage(null);
            }
            setCurrentControlSection('selectmove');
            setSelectMoveControlMode('position');
            setShowTopControls(true);
            marqueeJustFinishedRef.current = true;
          } else if (ids.length > 1) {
            setSelectedShapeId(null);
            setSelectedImage(null);
            setCurrentControlSection('selectmove');
            setSelectMoveControlMode('position');
            setShowTopControls(true);
            marqueeJustFinishedRef.current = true;
          } else if (drawingPaths.some(p => p.type !== 'mask' && p.color !== 'eraser')) {
            // Nothing vector-shaped under the drag box, but there is hand-drawn ink there --
            // lift just the stroke(s) the box was dragged over into their own movable layer,
            // the same way Auto Crop lifts a drawing, so a plain drawn line becomes selectable.
            const canvas = canvasRef.current;
            const rect = canvas && canvas.getBoundingClientRect();
            if (rect) {
              const seedBoxPx = {
                x: (minX / 100) * rect.width, y: (minY / 100) * rect.height,
                w: ((maxX - minX) / 100) * rect.width, h: ((maxY - minY) / 100) * rect.height,
              };
              setSmartCropRunning(true);
              setSelectionMode('smart');
              setIsSelecting(false);
              setSelectionPath([]);
              runInkAutoCrop(rect, seedBoxPx)
                .then(() => { marqueeJustFinishedRef.current = true; })
                .catch(() => { setSelectionMode('rectangle'); })
                .finally(() => setSmartCropRunning(false));
            }
          }
        }
      }
      return;
    }

    // Multi-shape/image drag end
    if (multiDragRef.current) {
      multiDragRef.current = null;
      shapeDraggedRef.current = false;
      if (shapeBeforeRef.current) {
        pushShapeHistory(shapeBeforeRef.current, canvasShapesRef.current);
        shapeBeforeRef.current = null;
      }
      if (dragStartImagesRef.current) {
        pushImageHistory(dragStartImagesRef.current, canvasImagesRef.current);
        dragStartImagesRef.current = null;
      }
      // Ink strokes that rode along in the same multi-drag -- reuse the sheetReplace undo/
      // redo type (images/shapes before==after here, so undo only moves the paths back).
      if (dragStartPathsRef.current && dragStartPathsRef.current !== drawingPaths) {
        const _imgsNow = canvasImagesRef.current;
        const _shpsNow = canvasShapesRef.current;
        const newHistory = drawingHistoryRef.current.slice(0, historyIndexRef.current + 1);
        newHistory.push({ type: 'sheetReplace', imagesBefore: _imgsNow, imagesAfter: _imgsNow, shapesBefore: _shpsNow, shapesAfter: _shpsNow, pathsBefore: dragStartPathsRef.current, pathsAfter: drawingPaths });
        setDrawingHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
      }
      dragStartPathsRef.current = null;
      markDirty();
      return;
    }

    // 3D orbit end -- same shape as the rotate end below it.
    if (orbitShapeRef.current) {
      const { id: _orbId } = orbitShapeRef.current;
      orbitShapeRef.current = null;
      if (shapeBeforeRef.current) {
        pushShapeHistory(shapeBeforeRef.current, canvasShapesRef.current);
        shapeBeforeRef.current = null;
      }
      markDirty();
      openShapeStyleControls(_orbId);
      return;
    }

    // Picture turn end: draw it at full size and bank it for undo. The small preview stays up
    // until the full one is ready, so nothing flickers back to flat in between.
    if (imageOrbitRef.current) {
      const g = imageOrbitRef.current;
      imageOrbitRef.current = null;
      if (turnPreviewRafRef.current) { cancelAnimationFrame(turnPreviewRafRef.current); turnPreviewRafRef.current = 0; }
      if (g.cur && (g.cur.rx !== g.rx || g.cur.ry !== g.ry)) {
        bakeImageTurn(g.id, g.cur.rx, g.cur.ry, g.before).finally(() => setTurnPreview(null));
      } else {
        setTurnPreview(null);
      }
      return;
    }

    // Shape rotate end
    if (rotatingShapeRef.current) {
      const { id: _rotId } = rotatingShapeRef.current;
      if (shapeBeforeRef.current) {
        pushShapeHistory(shapeBeforeRef.current, canvasShapesRef.current);
        shapeBeforeRef.current = null;
      }
      rotatingShapeRef.current = null;
      markDirty();
      // Keep the angle on screen after the turn, the way an image does. openShapeStyleControls
      // is still called for what it syncs — the figure's own colours into the palette — but it
      // also switches the bar to that palette, and jumping away the instant you let go is what
      // made the numbers feel like they would not stay.
      openShapeStyleControls(_rotId);
      setCurrentControlSection('selectmove');
      setSelectMoveControlMode('rotate');
      setShowTopControls(true);
      return;
    }

    // Shape resize end
    if (resizingShapeRef.current) {
      const { id: _rszId } = resizingShapeRef.current;
      if (shapeBeforeRef.current) {
        pushShapeHistory(shapeBeforeRef.current, canvasShapesRef.current);
        shapeBeforeRef.current = null;
      }
      resizingShapeRef.current = null;
      markDirty();
      openShapeStyleControls(_rszId);      // sync the palette state...
      setCurrentControlSection('selectmove');   // ...but leave W and H on screen
      setSelectMoveControlMode('resize');
      setShowTopControls(true);
      return;
    }

    // Shape drag end
    if (draggingShapeRef.current) {
      const { id } = draggingShapeRef.current;
      draggingShapeRef.current = null;
      const didDrag = shapeDraggedRef.current;
      shapeDraggedRef.current = false;
      setIsMovingShape(false);
      // The deferred half of the "one underneath" gesture — see the mousedown twin. Only a press
      // that never moved counts, and a text shape being double-tapped into its editor is left
      // alone, since that tap means something else entirely.
      const cycle = cycleUnderRef.current;
      cycleUnderRef.current = null;
      if (!didDrag && cycle && cycle.from === id && cycle.to !== id && textEditCandidateRef.current !== id) {
        setSelectedShapeId(cycle.to);
      }
      if (shapeBeforeRef.current) {
        pushShapeHistory(shapeBeforeRef.current, canvasShapesRef.current);
        shapeBeforeRef.current = null;
      }
      markDirty();
      // Double-tap/click on already-selected text shape -> enter inline edit mode
      if (textEditCandidateRef.current === id && !didDrag) {
        const now = Date.now();
        const lastTap = textLastTapRef.current;
        if (lastTap.id === id && now - lastTap.time < 500) {
          // Second tap within 500ms -> open editor
          const shape = canvasShapesRef.current.find(s => s.id === id);
          if (shape) {
            if (shape.strokeColor) setShapeStrokeColor(shape.strokeColor);
            if (shape.fontSize)    setShapeFontSize(shape.fontSize);
            if (shape.fontFamily)  setShapeFontFamily(shape.fontFamily);
            if (shape.bold   != null) setShapeTextBold(shape.bold);
            if (shape.italic != null) setShapeTextItalic(shape.italic);
            setShapeTextUnderline(!!shape.underline);
            setShapeText3D(!!shape.threeD);
            setPendingTextShapeId(shape.id);
            setPendingTextValue(shape.text || '');
            // The box's top-left, whichever pair of corners happens to hold it — the editor has
            // to open exactly over the text it is editing, and a box dragged right-to-left or
            // bottom-to-top stores its corners the other way round.
            setPendingTextPos({ x: Math.min(shape.x1, shape.x2), y: Math.min(shape.y1, shape.y2) });
          }
          textLastTapRef.current = { id: null, time: 0 };
        } else {
          textLastTapRef.current = { id, time: now };
        }
      }
      textEditCandidateRef.current = null;
      // The coordinates-only view (switched on at the first sign of real movement, above in
      // handleMouseMove/handleTouchMove) was only ever for the moment of the move itself --
      // letting go hands the palette and copy/delete/add/close back.
      if (didDrag) {
        textLastTapRef.current = { id: null, time: 0 }; // reset double-tap on drag
        openShapeStyleControls(id);
      }
      return;
    }

    // Ink rotate / resize / drag end -- one block for all three, the same way the shape
    // versions above each end: bank the undo entry, drop the gesture, and hand the full
    // controls back (the reduced A / W-H / X-Y readout was only for the gesture itself).
    if (rotatingInkRef.current || resizingInkRef.current || draggingInkRef.current) {
      const _inkGesture = rotatingInkRef.current || resizingInkRef.current || draggingInkRef.current;
      const _inkId = _inkGesture.id;
      rotatingInkRef.current = null;
      resizingInkRef.current = null;
      draggingInkRef.current = null;
      inkDraggedRef.current = false;
      setIsMovingShape(false);
      setIsResizingShape(false);
      setIsRotatingShape(false);
      if (inkBeforeRef.current && inkBeforeRef.current !== drawingPathsRef.current) {
        pushInkHistory(inkBeforeRef.current, drawingPathsRef.current);
      }
      inkBeforeRef.current = null;
      markDirty();
      openInkStyleControls(_inkId);
      return;
    }

    // Shape draw finalize
    if (isDrawingShape && shapeDrawStart && shapeDrawEnd) {
      const dx = Math.abs(shapeDrawEnd.x - shapeDrawStart.x);
      const dy = Math.abs(shapeDrawEnd.y - shapeDrawStart.y);
      let freeDone = false;
      if (isFreeTool(activeShapeTool) && freeDraftRef.current.length >= 3) {
        // a closed loop ends where it starts, so judge the drag by the path's extent, not its ends
        let fx1 = Infinity, fy1 = Infinity, fx2 = -Infinity, fy2 = -Infinity;
        for (const p of freeDraftRef.current) { if (p.x < fx1) fx1 = p.x; if (p.x > fx2) fx2 = p.x; if (p.y < fy1) fy1 = p.y; if (p.y > fy2) fy2 = p.y; }
        freeDone = (fx2 - fx1 > 0.5 || fy2 - fy1 > 0.5);
      }
      if (freeDone || dx > 0.5 || dy > 0.5) {
        let newShape;
        if (freeDone) {
          // Freeform figure: the dragged path, simplified to its nodes (≈3 px tolerance), fitted
          // to a box so the usual move / resize / rotate all apply. Nodes can be dragged after.
          const _rf = canvasRef.current.getBoundingClientRect();
          const px = freeDraftRef.current.map(p => ({ x: p.x / 100 * _rf.width, y: p.y / 100 * _rf.height }));
          const simp = simplifyPoints(px, 3).map(p => ({ x: p.x / _rf.width * 100, y: p.y / _rf.height * 100 }));
          newShape = {
            id: Date.now(), type: 'freeform', closed: activeShapeTool === 'freeshape', smooth: true,
            ...normalizeFreeform(simp),
            strokeColor: shapeStrokeColor, fillColor: shapeFillColor,
            strokeWidth: shapeStrokeWidth, dashed: shapeDashed, zOrder: canvasShapes.length,
            layerIndex: ++layerCounterRef.current,
          };
        } else newShape = {
          id: Date.now(), type: activeShapeTool,
          x1: shapeDrawStart.x, y1: shapeDrawStart.y,
          x2: shapeDrawEnd.x,   y2: shapeDrawEnd.y,
          strokeColor: shapeStrokeColor, fillColor: shapeFillColor,
          strokeWidth: shapeStrokeWidth, dashed: shapeDashed, zOrder: canvasShapes.length,
          layerIndex: ++layerCounterRef.current,
        };
        const _before = canvasShapesRef.current;
        const _after = [..._before, newShape];
        setCanvasShapes(_after);
        setSelectedShapeId(newShape.id);
        pushShapeHistory(_before, _after);
        // The figure tool stays active (draw another without reopening the menu) and the new
        // figure is selected with its colour & style controls open.
        shapeJustDrawnRef.current = true;
        setCurrentControlSection('shapes-draw');
        setShowTopControls(true);
      }
      setIsDrawingShape(false);
      setShowToolsPanel(false);
      setShapeDrawStart(null);
      setShapeDrawEnd(null);
      freeDraftRef.current = []; setFreeDraft([]);
      return;
    }

    if (cropPointerUp()) return;
    
    if (isDrawing && currentPath.length > 0 && (drawingMode === 'pen' || drawingMode === 'pencil') && canvasRef.current) {
      // A hand-drawn CLOSED stroke becomes a freeform figure: it can be selected, moved,
      // resized, filled with the colour tool and its nodes dragged — instead of dead pixels.
      const _rc = canvasRef.current.getBoundingClientRect();
      const first = currentPath[0], last = currentPath[currentPath.length - 1];
      let bx1 = Infinity, by1 = Infinity, bx2 = -Infinity, by2 = -Infinity;
      for (const p of currentPath) { if (p.x < bx1) bx1 = p.x; if (p.x > bx2) bx2 = p.x; if (p.y < by1) by1 = p.y; if (p.y > by2) by2 = p.y; }
      const extent = Math.max(bx2 - bx1, by2 - by1);
      const gap = Math.hypot(last.x - first.x, last.y - first.y);
      if (currentPath.length >= 8 && extent > 24 && gap <= Math.max(14, extent * 0.12)) {
        const simp = simplifyPoints(currentPath, 2).map(p => ({ x: p.x / _rc.width * 100, y: p.y / _rc.height * 100 }));
        const shape = {
          id: Date.now(), type: 'freeform', closed: true, smooth: true, hand: true,
          ...normalizeFreeform(simp),
          strokeColor: drawingColor, fillColor: 'none', strokeWidth: brushSize, dashed: false,
          zOrder: canvasShapes.length, layerIndex: ++layerCounterRef.current,
        };
        const _b = canvasShapesRef.current, _a = [..._b, shape];
        setCanvasShapes(_a); canvasShapesRef.current = _a;
        pushShapeHistory(_b, _a);
        penStrokeDrawnRef.current = true;
        setCurrentPath([]);
        setIsDrawing(false);
        return;
      }
    }
    if (isDrawing && currentPath.length > 1) penStrokeDrawnRef.current = true;
    if (isDrawing && currentPath.length > 0) {
      const _rect = canvasRef.current ? canvasRef.current.getBoundingClientRect() : null;
      const newPath = {
        id: Date.now(),
        path: currentPath,
        color: drawingMode === 'eraser' ? 'eraser' : drawingColor,
        size: brushSize,
        type: drawingMode,
        canvasW: _rect ? _rect.width : 800,
        canvasH: _rect ? _rect.height : 800 * A4_RATIO,
      };
      
      setDrawingPaths(prev => [...prev, newPath]);
      markDirty();
      const newHistory = drawingHistory.slice(0, historyIndex + 1);
      newHistory.push({ type: 'draw', path: newPath });
      setDrawingHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);

      // The stroke above only cuts the hand-drawn layer. Figures and images live on their own
      // layers underneath it, so the same rub has to be applied to them separately or rubbing
      // over a picture or a circle does visibly nothing.
      if (drawingMode === 'eraser' && _rect) {
        applyEraseToShapes(currentPath, brushSize, _rect);
        applyEraseToImages(currentPath, brushSize, _rect);
      }

      setCurrentPath([]);
    }
    
    const wasDragging = !!draggingImage;
    const wasResizing = !!resizingImage;
    const wasRotatingImage = !!rotatingImage;
    const _gestureImgId = draggingImage || resizingImage || rotatingImage;

    // Did the picture actually change, or was this only the press that selected it? The snapshot
    // taken when the press began is the honest answer — a press that moved nothing leaves every
    // object exactly as it was. It matters below: a gesture that moved something has to absorb
    // the click that follows the release, and a press that moved nothing must NOT, or the very
    // next tap on the sheet is eaten and the picture takes two taps to put down.
    const _gestureBefore = dragStartImagesRef.current || resizeStartImagesRef.current
      || rotateImageCenterRef.current?.before || null;
    const _gestureMoved = !!_gestureBefore && (() => {
      const a = _gestureBefore.find(i => i.id === _gestureImgId);
      const b = canvasImagesRef.current.find(i => i.id === _gestureImgId);
      if (!a || !b) return true;
      return a.x !== b.x || a.y !== b.y || a.width !== b.width || a.height !== b.height
        || (a.rotation || 0) !== (b.rotation || 0);
    })();

    if (wasDragging && dragStartImagesRef.current) {
      pushImageHistory(dragStartImagesRef.current, canvasImagesRef.current);
      dragStartImagesRef.current = null;
    }
    if (wasResizing && resizeStartImagesRef.current) {
      pushImageHistory(resizeStartImagesRef.current, canvasImagesRef.current);
      resizeStartImagesRef.current = null;
    }
    if (wasRotatingImage && rotateImageCenterRef.current?.before) {
      pushImageHistory(rotateImageCenterRef.current.before, canvasImagesRef.current);
      rotateImageCenterRef.current = null;
    }

    setDraggingImage(null);
    setResizingImage(null);
    setRotatingImage(null);
    resizeDirectionRef.current = null;
    setIsDrawing(false);
    // Keep isSelecting true for circle, rectangle and smart crop so the overlay stays visible
    if ((selectionMode !== 'circle' && selectionMode !== 'rectangle' && selectionMode !== 'smart') || activeTool !== 'selection') {
      setIsSelecting(false);
    }

    // Gesture over (move, resize or rotate): the readout STAYS on what the gesture was about —
    // W and H after a resize, the angle after a turn, X and Y after a move.
    //
    // It used to close instead, on the reasoning that the reduced readout "was only ever for
    // the moment of the gesture itself". That is backwards, and the figure path a few blocks up
    // already says so in as many words: "...but leave W and H on screen". The number you want
    // to read is the one you have just finished setting. Taking hold of an edge and watching
    // the size appear, move, and then vanish at the instant you let go means never being able
    // to check it — and worse, never being able to correct it, because the +/- steppers that
    // would nudge it to a round 8.0 disappear with it. A fill is its own case: its colour
    // palette is what it opens with.
    if (wasResizing || wasRotatingImage || wasDragging) {
      const _gImg = canvasImagesRef.current.find(i => i.id === _gestureImgId);
      // Letting go is not the same as tapping the sheet. A drag that starts on the picture and
      // ends past its edge — moving it across the page, pulling a corner outward — releases over
      // the sheet, and the click that follows the release was being read as "put it down": the
      // object deselected itself the moment the move finished, taking Copy, Delete and the
      // readout with it. The figures have absorbed that click for a long time; pictures now do.
      // Only a gesture that actually moved something, though — see _gestureMoved.
      if (_gestureMoved) shapeClickedRef.current = true;
      if (_gImg && _gImg.name === 'fill') {
        setCurrentControlSection('draw');
        setShowTopControls(true);
      } else if (_gImg) {
        setCurrentControlSection('selectmove');
        setSelectMoveControlMode(wasResizing ? 'resize' : wasRotatingImage ? 'rotate' : 'position');
        setShowTopControls(true);
      } else {
        setCurrentControlSection(null);
        setShowTopControls(false);
      }
    }
  };

  // Handle touch end
  const handleTouchEnd = () => {
    // Everything, crop included, goes through handleMouseUp: the crop tools have a single
    // pointer implementation now (cropPointerUp), so a touch release and a mouse release
    // are the same event.
    handleMouseUp();
  };

  // Handle image click
  const handleImageClick = (imageId, e) => {
    e.stopPropagation();
    const image = canvasImages.find(img => img.id === imageId);
    // A name is waiting to be placed -- this tap marks where it goes, instead of the
    // normal select/chat/brightness behavior below.
    if (namePlacementPending && imageId === effectiveImageId && image && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const xPct = ((e.clientX - rect.left) / rect.width) * 100;
      const yPct = ((e.clientY - rect.top) / rect.height) * 100;
      const widthPct = (image.width / 21) * 100;
      const heightPct = (image.height / 29.7) * 100;
      const fx = widthPct > 0 ? Math.max(0, Math.min(1, (xPct - image.x) / widthPct)) : 0.5;
      const fy = heightPct > 0 ? Math.max(0, Math.min(1, (yPct - image.y) / heightPct)) : 0.5;
      commitNamePlacement(fx, fy);
      return;
    }
    if (image) {
      setSelectedImage(imageId);
      setSelectedShapeId(null);
      setResizeWidth(image.width);
      setResizeHeight(image.height);
      setShowImageControls(imageId);
      
      // If we're in crop selection mode, stay in crop mode
      if (activeTool === 'selection') {
        // Stay in crop selection mode
        setCurrentControlSection('selection');
        setShowTopControls(true);
        setImageSelectionMode(false);
        setCanvasCursor('crosshair');
      } else if (activeTool === 'draw') {
        // Stay in draw mode
        setCurrentControlSection('draw');
        setShowTopControls(true);
        setImageSelectionMode(false);
      } else if (magicSubTool === 'chat') {
        // AI Edit open: a click toggles the image into the chat's selection (max 2, oldest
        // evicted). Selection controls stay up so the image can still be moved meanwhile.
        setMagicChatImages(prev => prev.includes(imageId)
          ? prev.filter(id => id !== imageId)
          : (prev.length >= 2 ? [...prev.slice(1), imageId] : [...prev, imageId]));
        setCurrentControlSection('selectmove');
        setSelectMoveControlMode(prev => prev === 'resize' ? 'resize' : 'position');
        setShowTopControls(true);
      } else if (imageSelectionMode) {
        // Already in select/move mode -- keep it, refresh position display
        setCurrentControlSection('selectmove');
        setSelectMoveControlMode(prev => prev === 'resize' ? 'resize' : 'position');
        setShowTopControls(true);
      } else if (activeTool === 'magic-wand') {
        // In magic wand mode -- select the image without clearing the tool
        if (magicSubTool === 'chat') {
          // Chat mode: toggle image in selection; max 2 images (FIFO eviction)
          setMagicChatImages(prev => {
            if (prev.includes(imageId)) return prev.filter(id => id !== imageId);
            const next = prev.length >= 2 ? [...prev.slice(1), imageId] : [...prev, imageId];
            return next;
          });
        }
        setImageSelectionMode(false);
        // Clicking an image while brightness/contrast is active deactivates the sub-tool
        if (magicSubTool === 'brightness' || magicSubTool === 'contrast') {
          setMagicSubTool(null);
          setCurrentControlSection(null);
          setShowTopControls(false);
        } else if (magicSubTool === 'color') { setCurrentControlSection('magic-color'); setShowTopControls(true); }
        else { setCurrentControlSection(null); setShowTopControls(false); }
        setCanvasCursor('pointer');
      } else {
        // No active tool — just select the image.
        //
        // Selecting it used to CLOSE the top bar, which is where Copy and Delete live. They
        // only reappeared once the picture had been nudged, because the drag handler opens the
        // bar on its way past. So the rule was, in practice, "select it and then move it a
        // little": pick something up and the two things you most want to do with it are gone.
        // Anything selected shows its controls, at once, without being moved first.
        setActiveTool(null);
        setImageSelectionMode(false);
        if (image.name === 'fill') {
          // Fill shapes: show colour palette so the user can repaint
          setCurrentControlSection('draw');
        } else {
          setCurrentControlSection('selectmove');
          setSelectMoveControlMode('position');
        }
        setShowTopControls(true);
        setCanvasCursor('pointer');
      }
    }
  };

  // Handle image copy
  const handleCopyImage = (imageId, e) => {
    e.stopPropagation();
    const image = canvasImages.find(img => img.id === imageId);
    if (image) {
      const newImage = {
        ...image,
        id: Date.now(),
        layerIndex: ++layerCounterRef.current,
        x: image.x + 2,
        y: image.y + 2,
      };
      pushImageHistory(canvasImages, [...canvasImages, newImage]);
      setCanvasImages(prev => [...prev, newImage]);
      setSelectedImage(newImage.id);
      setShowImageControls(newImage.id);
    }
  };

  /**
   * Where a copy lands.
   *
   * It was two per cent of the sheet — about a centimetre — which on anything but a tiny figure
   * reads as the original having gained a shadow rather than as a second object. Far enough to
   * be obviously separate, and never pushed off the page: hard against an edge, it steps the
   * other way instead.
   */
  const COPY_OFFSET = 4;
  const copyOffsetFor = (s) => {
    const dx = (Math.max(s.x1, s.x2) + COPY_OFFSET <= 100) ? COPY_OFFSET : -COPY_OFFSET;
    const dy = (Math.max(s.y1, s.y2) + COPY_OFFSET <= 100) ? COPY_OFFSET : -COPY_OFFSET;
    return { x1: s.x1 + dx, y1: s.y1 + dy, x2: s.x2 + dx, y2: s.y2 + dy };
  };

  // ── Join: overlapping objects made into one ─────────────────────────────────────────────
  // Select more than one object by dragging a box round them (or shift-clicking) and they move
  // together, as before. When any two of them OVERLAP, a Join button appears in that same row.
  // Pressing it flattens the selection into ONE picture on the sheet: one object to move, scale
  // and undo, rather than a stack of separate ones that drift apart the moment one is touched.
  //
  // The picture is drawn the way Save draws the sheet (the same layer order, the same brightness,
  // the same drawing helpers), cropped to the box the selection occupies. Transparency is kept
  // wherever nothing was drawn, so a cut-out stays a cut-out.
  //
  // What it gives up: the parts are pixels now. A joined figure keeps its colour and outline but
  // can no longer be restyled as a figure, and a joined stroke can no longer be edited as a
  // stroke. Undo puts every part back exactly as it was.

  // A box in centimetres on the sheet round an object that may be turned. The four corners turn
  // about the middle, so the box of a tilted picture covers all of it, not just its unturned
  // rectangle.
  const turnedBoxCm = (x, y, w, h, deg) => {
    const t = ((deg || 0) * Math.PI) / 180;
    const c = Math.cos(t), s = Math.sin(t);
    const cx = x + w / 2, cy = y + h / 2;
    let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
    for (const [dx, dy] of [[-w / 2, -h / 2], [w / 2, -h / 2], [w / 2, h / 2], [-w / 2, h / 2]]) {
      const px = cx + dx * c - dy * s, py = cy + dx * s + dy * c;
      if (px < x1) x1 = px;
      if (px > x2) x2 = px;
      if (py < y1) y1 = py;
      if (py > y2) y2 = py;
    }
    return { x1, y1, x2, y2 };
  };

  // The selected objects, each with its box in centimetres. A stroke is measured from its points,
  // padded by half its line width, because that is how far its ink reaches.
  const joinParts = (paths) => {
    const PS = PRINT_SIZES[printSize] || { width: 21, height: 29.7 };
    const ids = selectedShapeIds;
    const imgs = canvasImagesRef.current.filter(i => ids.has(i.id));
    const shapes = canvasShapesRef.current.filter(s => ids.has(s.id));
    const strokes = (paths || []).filter(p => ids.has(p.id) && p.type !== 'mask' && p.color !== 'eraser');
    const parts = [];
    imgs.forEach(img => parts.push({ kind: 'image', item: img, box: turnedBoxCm(
      img.x / 100 * PS.width, img.y / 100 * PS.height,
      (img.width / 21) * PS.width, (img.height / 29.7) * PS.height, img.rotation) }));
    shapes.forEach(sh => {
      const x1 = Math.min(sh.x1, sh.x2), x2 = Math.max(sh.x1, sh.x2);
      const y1 = Math.min(sh.y1, sh.y2), y2 = Math.max(sh.y1, sh.y2);
      parts.push({ kind: 'shape', item: sh, box: turnedBoxCm(
        x1 / 100 * PS.width, y1 / 100 * PS.height, (x2 - x1) / 100 * PS.width, (y2 - y1) / 100 * PS.height, sh.rotation) });
    });
    strokes.forEach(p => {
      const b = inkPathBBoxPct(p);
      if (!b) return;
      const padX = ((p.size || 4) / 2) / (p.canvasW || 1) * PS.width;
      const padY = ((p.size || 4) / 2) / (p.canvasH || 1) * PS.height;
      parts.push({ kind: 'ink', item: p, box: {
        x1: b.x1 / 100 * PS.width - padX, y1: b.y1 / 100 * PS.height - padY,
        x2: b.x2 / 100 * PS.width + padX, y2: b.y2 / 100 * PS.height + padY } });
    });
    return { PS, imgs, shapes, strokes, parts };
  };

  // True when at least two of the selected objects share some of the sheet.
  const selectionOverlaps = (paths) => {
    if (selectedShapeIds.size < 2) return false;
    const { parts } = joinParts(paths);
    for (let a = 0; a < parts.length; a++) {
      const p = parts[a].box;
      for (let b = a + 1; b < parts.length; b++) {
        const q = parts[b].box;
        if (p.x1 < q.x2 && q.x1 < p.x2 && p.y1 < q.y2 && q.y1 < p.y2) return true;
      }
    }
    return false;
  };

  // One undo entry for a change to pictures, figures and strokes together: the same entry the
  // multi-drag and the ink moves already use.
  const pushSheetHistory = (imgsBefore, imgsAfter, shpsBefore, shpsAfter, pathsBefore, pathsAfter) => {
    markDirty();
    const newHistory = drawingHistoryRef.current.slice(0, historyIndexRef.current + 1);
    newHistory.push({ type: 'sheetReplace', imagesBefore: imgsBefore, imagesAfter: imgsAfter,
      shapesBefore: shpsBefore, shapesAfter: shpsAfter, pathsBefore, pathsAfter });
    setDrawingHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const joinSelectedObjects = async () => {
    const snapImages = canvasImagesRef.current;
    const snapShapes = canvasShapesRef.current;
    const snapPaths = drawingPathsRef.current;
    const picked = new Set(selectedShapeIds);
    const { PS, imgs, shapes, strokes, parts } = joinParts(snapPaths);
    // Only a selection whose objects overlap can be joined; the button is shown for nothing else.
    if (parts.length < 2 || !selectionOverlaps(snapPaths)) return;

    // The pictures are loaded first. One that will not load stops the join rather than being left
    // out of it: a joined object that has quietly lost a part is worse than no join at all.
    const loaded = await Promise.all(imgs.map(i => loadImgForOutput(i.url)));
    if (loaded.some(el => !el)) { console.error('Join stopped: a picture would not load.'); return; }

    // The box the whole selection occupies, in centimetres.
    let ux1 = Infinity, uy1 = Infinity, ux2 = -Infinity, uy2 = -Infinity;
    parts.forEach(({ box }) => {
      if (box.x1 < ux1) ux1 = box.x1;
      if (box.y1 < uy1) uy1 = box.y1;
      if (box.x2 > ux2) ux2 = box.x2;
      if (box.y2 > uy2) uy2 = box.y2;
    });
    const spanW = Math.max(ux2 - ux1, 0.05), spanH = Math.max(uy2 - uy1, 0.05);

    // Resolution: the print's own 300 dpi as a floor, more where a picture has more pixels than
    // that, never beyond 600 dpi, and never a side longer than 4000 pixels.
    let pxPerCm = 300 / 2.54;
    imgs.forEach((img, k) => {
      const px = loaded[k].naturalWidth || loaded[k].width;
      const cmW = (img.width / 21) * PS.width;
      if (px && cmW > 0) pxPerCm = Math.max(pxPerCm, px / cmW);
    });
    pxPerCm = Math.min(pxPerCm, 600 / 2.54, 4000 / Math.max(spanW, spanH));
    const outW = Math.max(1, Math.round(spanW * pxPerCm));
    const outH = Math.max(1, Math.round(spanH * pxPerCm));

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    // The shared drawing helpers take a box as a share of the sheet, in per cent.
    const offXpct = ux1 / PS.width * 100, offYpct = uy1 / PS.height * 100;
    const wPct = spanW / PS.width * 100, hPct = spanH / PS.height * 100;
    // Pictures and figures in the order Save paints them; the strokes go on top of all of it.
    for (const L of layersInOrder(imgs, shapes)) {
      if (L.kind === 'shape') {
        drawShapesOnCanvas(ctx, [L.item], outW, outH, offXpct, offYpct, wPct, hPct);
        continue;
      }
      const img = L.item;
      const el = loaded[imgs.indexOf(img)];
      const wC = (img.width / 21) * PS.width, hC = (img.height / 29.7) * PS.height;
      const w = wC * pxPerCm, h = hC * pxPerCm;
      ctx.save();
      ctx.translate((img.x / 100 * PS.width + wC / 2 - ux1) * pxPerCm, (img.y / 100 * PS.height + hC / 2 - uy1) * pxPerCm);
      if (img.rotation) ctx.rotate((img.rotation * Math.PI) / 180);
      if (img.cropShape === 'circle') { ctx.beginPath(); ctx.ellipse(0, 0, w / 2, h / 2, 0, 0, Math.PI * 2); ctx.clip(); }
      drawImageHQ(ctx, el, -w / 2, -h / 2, w, h);
      ctx.restore();
    }
    drawDrawingLayer(ctx, strokes, outW, outH, offXpct, offYpct, wPct, hPct);

    // The joined picture takes the place of the topmost part it was made from, so anything that
    // was above the selection stays above it.
    const zs = [...imgs.map(i => i.layerIndex ?? 1), ...shapes.map(s => s.layerIndex ?? 4)];
    const joined = {
      id: Date.now(),
      layerIndex: zs.length ? Math.max(...zs) : ++layerCounterRef.current,
      url: canvas.toDataURL('image/png'),
      name: 'Joined', size: '', source: 'join',
      cutout: shapes.length > 0 || strokes.length > 0 || imgs.some(i => i.cutout || i.source === 'crop' || i.source === 'prop'),
      x: ux1 / PS.width * 100,
      y: uy1 / PS.height * 100,
      width: spanW / PS.width * 21,
      height: spanH / PS.height * 29.7,
      rotation: 0,
      cropShape: null,
      naturalWidth: outW,
      naturalHeight: outH,
    };
    const imagesAfter = [...snapImages.filter(i => !picked.has(i.id)), joined];
    const shapesAfter = snapShapes.filter(s => !picked.has(s.id));
    const pathsAfter = snapPaths.filter(p => !picked.has(p.id));
    pushSheetHistory(snapImages, imagesAfter, snapShapes, shapesAfter, snapPaths, pathsAfter);
    canvasImagesRef.current = imagesAfter; setCanvasImages(imagesAfter);
    canvasShapesRef.current = shapesAfter; setCanvasShapes(shapesAfter);
    drawingPathsRef.current = pathsAfter; setDrawingPaths(pathsAfter);
    setSelectedShapeIds(new Set());
    setSelectedShapeId(null);
    setSelectedImage(joined.id);
    setResizeWidth(joined.width);
    setResizeHeight(joined.height);
    setShowImageControls(joined.id);
  };

  const handleCopyShape = () => {
    const shape = canvasShapes.find(s => s.id === selectedShapeId);
    if (shape) {
      const newShape = {
        ...shape,
        id: Date.now(),
        ...copyOffsetFor(shape),
        zOrder: canvasShapes.length,
        layerIndex: ++layerCounterRef.current,
      };
      const before = canvasShapesRef.current;
      const after = [...before, newShape];
      setCanvasShapes(after);
      setSelectedShapeId(newShape.id);
      pushShapeHistory(before, after);
      return;
    }
    // No figure with that id -- it is a selected hand-drawn stroke. Same 2%-of-the-sheet
    // offset as a figure copy, expressed in the stroke's own pixels.
    const ink = drawingPathsRef.current.find(p => p.id === selectedShapeId);
    if (ink && ink.path && ink.path.length) {
      const dx = (ink.canvasW || 0) * 0.02;
      const dy = (ink.canvasH || 0) * 0.02;
      const before = drawingPathsRef.current;
      const newInk = { ...ink, id: Date.now(), path: ink.path.map(pt => ({ ...pt, x: pt.x + dx, y: pt.y + dy })) };
      const after = [...before, newInk];
      setDrawingPaths(after);
      setSelectedShapeId(newInk.id);
      pushInkHistory(before, after);
    }
  };

  // Handle image delete
  const handleDeleteImage = (imageId) => {
    const image = canvasImages.find(img => img.id === imageId);
    if (!image) return;

    setSelectedImage(imageId);
    setShowImageControls(imageId);
    setActiveTool('delete');
    setCurrentControlSection('delete');
    setShowTopControls(true);
  };

  // Handle bulk delete options
  const handleBulkDeleteOption = (option) => {
    if (!selectedImage) return;

    if (option === 'multiple') {
      // Open bucket for multiple delete
      setIsBulkDeleteModalOpen(true);
    }
  };

  /**
   * A picture's sheet file goes with the picture.
   *
   * Deleting from the Cloud removed the picture and left its .cstudio.json behind. An orphan
   * like that does not sit quietly: the next picture whose name it matches inherits it, gets
   * labelled "sheet", and opening it restores a sheet made of pictures that are no longer
   * there — including the one that was just deleted. Which is what happened.
   *
   * Both spellings are removed: the current one, and the one used before the name carried the
   * picture's extension, so old orphans are cleared out as their pictures go.
   */
  const deleteSidecars = async (imagePath) => {
    for (const p of [projectPathFor(imagePath), legacyProjectPathFor(imagePath)]) {
      try { await deleteFromStorage(p, getEnvStorageBucket()); }
      catch { /* there may not be one, and that is the normal case */ }
    }
  };

  // Delete from bucket
  const handleDeleteFromBucket = async (image) => {
    if (deleting) return;
    
    setDeleting(true);
    try {
      const imagePath = image.bucketPath || image.name;

      await deleteFromStorage(imagePath, getEnvStorageBucket());
      await deleteSidecars(imagePath);

      // Remove from canvas, and with it anything filed under it — a pair of shades has no
      // meaning once the face is gone.
      setCanvasImages(prev => prev.filter(img => img.id !== selectedImage && img.parentId !== selectedImage));
      setSelectedImage(null);
      setShowImageControls(null);
      setActiveTool(null);
      setCurrentControlSection(null);
      setShowTopControls(false);
      
      // Reload bucket images (force: file was deleted)
      await loadBucketImages(true);
      
    } catch (error) {
      console.error('Failed to delete image:', error);
      showAlert(`Failed to delete image: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Delete from cloud only (keep on canvas)
  const handleDeleteFromBucketCloudOnly = async (image) => {
    if (deleting) return;
    setDeleting(true);
    try {
      const imagePath = image.bucketPath || image.name;
      await deleteFromStorage(imagePath, getEnvStorageBucket());
      await deleteSidecars(imagePath);
      setActiveTool(null);
      setCurrentControlSection(null);
      setShowTopControls(false);
      await loadBucketImages(true);
    } catch (error) {
      console.error('Failed to delete image from Cloud:', error);
      showAlert(`Failed to delete image: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Delete multiple images from bucket
  const handleDeleteMultipleFromBucket = async () => {
    if (selectedBucketImages.length === 0) {
      showAlert('Please select images to delete');
      return;
    }

    setDeleting(true);
    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error('No authenticated user found');
      
      let deletedCount = 0;
      let errorCount = 0;

      // Delete each selected image
      for (const imageFileName of selectedBucketImages) {
        try {
          await deleteFromStorage(imageFileName, getEnvStorageBucket());
          await deleteSidecars(imageFileName);

          deletedCount++;
          
          // Remove from canvas if it exists there
          const fileNameOnly = imageFileName.split('/').pop();
          setCanvasImages(prev => prev.filter(img => img.name !== fileNameOnly && img.bucketPath !== imageFileName));
          
        } catch (error) {
          console.error(`Failed to delete ${imageFileName}:`, error);
          errorCount++;
        }
      }

      // Silently reload bucket images (no modal/alert — list updates in place)
      await loadBucketImages(true);

      // Reset selection and confirm state
      setSelectedBucketImages([]);
      setConfirmDeleteBucket(false);

      // Show inline feedback in the header center slot, auto-clear after 3 s
      const msg = errorCount === 0
        ? { text: `Deleted ${deletedCount} image${deletedCount !== 1 ? 's' : ''}`, ok: true }
        : { text: `Deleted ${deletedCount}, failed ${errorCount}`, ok: false };
      setBucketDeleteMsg(msg);
      setTimeout(() => setBucketDeleteMsg(null), 3000);

    } catch (error) {
      console.error('Failed to delete images from bucket:', error);
      setBucketDeleteMsg({ text: `Delete failed: ${error.message}`, ok: false });
      setTimeout(() => setBucketDeleteMsg(null), 4000);
    } finally {
      setDeleting(false);
    }
  };

  // Insert multiple images from bucket to canvas
  /**
   * Put a saved sheet back the way it was left: every object separate, a heading still a
   * heading, a figure still draggable. Replaces what is on the canvas, which is what opening a
   * piece of work means — the picture that was saved with it is not placed, because everything
   * in it is here as objects instead.
   */
  const restoreProject = async (projectPath) => {
    const res = await readObjectContent(projectPath, getEnvStorageBucket());
    // The bytes come back with the read now, so the sheet is not downloaded a second time.
    const data = res.blob
      ? JSON.parse(await res.blob.text())
      : await fetch(res.downloadUrl).then(r => r.json());
    if (!isProject(data)) throw new Error('that file is not a studio sheet');
    resetStudio();
    if (data.printSize && PRINT_SIZES[data.printSize]) setPrintSize(data.printSize);
    setCanvasBackground(data.canvasBackground ?? 'transparent');
    setCanvasBgFx(data.canvasBgFx ?? null);
    const imgs = Array.isArray(data.images) ? data.images : [];
    setCanvasImages(imgs); canvasImagesRef.current = imgs;
    const shs = Array.isArray(data.shapes) ? data.shapes : [];
    setCanvasShapes(shs); canvasShapesRef.current = shs;
    setDrawingPaths(Array.isArray(data.paths) ? data.paths : []);
    // Anything placed later must sit above what came back, so the counter starts past the
    // highest layer in the sheet rather than at 1000 again.
    layerCounterRef.current = Math.max(
      layerCounterRef.current,
      ...imgs.map(i => i.layerIndex || 0),
      ...shs.map(sh => sh.layerIndex || 0),
    );
    setShowBucketModal(false);
    setSelectedBucketImages([]);
    isSavedRef.current = true;
  };

  const handleInsertMultipleFromBucket = async () => {
    if (selectedBucketImages.length === 0) {
      showAlert('Please select images to insert');
      return;
    }
    // One item, saved as a whole sheet: open the sheet rather than placing its picture.
    if (selectedBucketImages.length === 1) {
      const only = selectedBucketImages[0];
      const entry = (bucketImages || []).find(b => b.fileName === only);
      if (entry?.hasProject) {
        setIsLoading(true);
        try {
          await restoreProject(projectPathFor(only));
          return;
        } catch (e) {
          showAlert(`Could not open that sheet: ${e.message}`);
        } finally {
          setIsLoading(false);
        }
      }
    }
    setIsLoading(true);
    const newImages = [];
    for (const imageFileName of selectedBucketImages) {
      try {
        const result = await readObjectContent(imageFileName, getEnvStorageBucket());
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = result.downloadUrl;
        });
        const naturalAspectRatio = img.naturalWidth / img.naturalHeight;
        const cropShape = detectCircleCrop(img);
        // The size it was last edited to, taken from the file itself. This is the path that
        // runs when images are picked from the cloud library — the single-image path had this
        // and this one did not, which is why a cloud round trip lost the size while a folder
        // one kept it. Failing that, the old assumption of 300 DPI, scaled to fit the page.
        const _declared2 = await readPhysicalSize(result.blob).catch(() => null);
        const _ps2W = PRINT_SIZES[printSize].width, _ps2H = PRINT_SIZES[printSize].height;
        const rawW2 = _declared2 ? (_declared2.cmW / _ps2W) * 21   : img.naturalWidth  * 2.54 / 300;
        const rawH2 = _declared2 ? (_declared2.cmH / _ps2H) * 29.7 : img.naturalHeight * 2.54 / 300;
        // Same rule as the single-image path above: a declared size is clamped, never scaled.
        const fitScale2 = _declared2 ? 1 : Math.min(1, 21 / rawW2, 29.7 / rawH2);
        const width  = Math.max(0.5, Math.min(21,   rawW2 * fitScale2));
        const height = Math.max(0.5, Math.min(29.7, rawH2 * fitScale2));
        newImages.push({
          id: Date.now() + newImages.length,
          layerIndex: ++layerCounterRef.current,
          url: result.downloadUrl,
          name: imageFileName.split('/').pop(),
          size: formatFileSize(result.size || 0),
          x: Math.max(0, 50 - (width / 21 * 50)),
          y: Math.max(0, 50 - (height / 29.7 * 50)),
          width,
          height,
          source: 'bucket',
          cropShape,
          bucketPath: imageFileName,
          naturalWidth: img.naturalWidth,
          naturalHeight: img.naturalHeight,
        });
      } catch (error) {
        console.error(`Failed to load ${imageFileName}:`, error);
      }
    }
    if (newImages.length > 0) {
      const before = canvasImagesRef.current;
      const after = [...before, ...newImages];
      pushImageHistory(before, after);
      setCanvasImages(after);
      const last = newImages[newImages.length - 1];
      setSelectedImage(last.id);
      setResizeWidth(last.width);
      setResizeHeight(last.height);
      setAspectRatio(last.naturalWidth / last.naturalHeight);
    }
    setSelectedBucketImages([]);
    setImageSelectionMode(false);
    if (activeTool !== 'magic-wand') {
      setActiveTool(null);
      setCurrentControlSection(null);
      setShowTopControls(false);
    }
    setCanvasCursor('pointer');
    setIsLoading(false);
    setShowBucketModal(false);
  };

  // Apply resizeWidth/resizeHeight to the selected canvas image.
  // Aspect-ratio lock is handled at the call-site (step buttons / drag), so just apply values here.
  useEffect(() => {
    // Skip during active drag resize — the drag handler updates canvasImages directly each frame.
    // Running here during drag would overwrite with the previous render's stale dimensions (jitter).
    if (!selectedImage || resizingImage) return;
    // Selecting an object must never RESIZE it. This effect fires on a change of selection too,
    // and at that moment the W/H boxes still hold the previous object's numbers — or, for a
    // picture that was just added and selected in the same breath, their 10x10 starting value.
    // It stamped those onto the new object: every AI picture arrived at 10 x 10 cm however big
    // it had been made, and every newly selected image was silently resized to whatever the
    // boxes last held. So the first pass after a change of selection adopts the object's size
    // instead of imposing one, and only later passes — a number actually typed or stepped —
    // write back to the object.
    if (lastResizeImgRef.current !== selectedImage) {
      lastResizeImgRef.current = selectedImage;
      const _sel = canvasImagesRef.current.find(i => i.id === selectedImage);
      if (_sel && (_sel.width !== resizeWidth || _sel.height !== resizeHeight)) {
        setResizeWidth(_sel.width);
        setResizeHeight(_sel.height);
      }
      return;
    }
    setCanvasImages(prev => prev.map(img => (
      img.id === selectedImage ? { ...img, width: resizeWidth, height: resizeHeight } : img
    )));
  }, [resizeWidth, resizeHeight, selectedImage, resizingImage]);

  // Handle canvas click
  // Fill: a replayable drawing step. It is stored as a path of type 'fill' (point + colour) and
  // flood-filled in order with the other strokes — live, in exports at any resolution, in undo.
  const lastFillRef = useRef(null); // { t, x, y } -- see the one-tap-one-fill guard below
  const handleFillClick = (e) => {
    const drawCanvas = drawingCanvasRef.current;
    const container = canvasRef.current;
    if (!drawCanvas || !container) return;
    // One tap = one fill. A single gesture reaches this from up to three places -- the shape's
    // own mousedown/touchstart, the canvas touchstart, and the click that follows a tap -- so
    // an unguarded tap stacked two or three identical fillmask layers on top of each other and
    // pushed a history entry for each (undo then needed as many presses as the fill had run).
    // Worse, two landing in the same millisecond both took id: Date.now(), leaving two layers
    // sharing one id, which anything keyed by id then treated as a single item.
    // Only ever collapses the SAME colour on the SAME spot: the duplicate calls of one gesture
    // are always identical, while deliberately re-filling a region in a new colour must always
    // go through -- including straight after the first fill.
    const _fnow = Date.now(), _flast = lastFillRef.current;
    if (_flast && _flast.color === drawingColor && _fnow - _flast.t < 400
        && Math.abs(e.clientX - _flast.x) < 12 && Math.abs(e.clientY - _flast.y) < 12) return;
    lastFillRef.current = { t: _fnow, x: e.clientX, y: e.clientY, color: drawingColor };
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) return;
    // A closed figure (hand-drawn or from the picker) under the tap takes the colour as its
    // fill. A 3D object (cylinder, arrow3D, ...) is always a solid body -- there is no "open"
    // variant of one -- so it's closed by definition; hit-testing it unions all of its drawn
    // parts (body + end caps etc.) rather than a single path.
    {
      const probeCtx = document.createElement('canvas').getContext('2d');
      const hit = [...canvasShapesRef.current]
        .sort((a, b) => (b.layerIndex ?? 4) - (a.layerIndex ?? 4))
        .find(sh => {
          const closed = isFreeformClosed(sh) || sh.type === 'circle' || sh.type === 'rectangle' || sh.type === 'triangle'
            || isShape3D(sh.type) || (isShape2D(sh.type) && !SHAPES_2D[sh.type].open);
          if (!closed) return false;
          const bx = Math.min(sh.x1, sh.x2) / 100 * rect.width, by = Math.min(sh.y1, sh.y2) / 100 * rect.height;
          const bw = Math.abs(sh.x2 - sh.x1) / 100 * rect.width, bh = Math.abs(sh.y2 - sh.y1) / 100 * rect.height;
          probeCtx.setTransform(1, 0, 0, 1, bx, by);
          if (isShape3D(sh.type)) {
            return SHAPES_3D[sh.type].parts(bw, bh).some(p => probeCtx.isPointInPath(new Path2D(p.d), x, y));
          }
          let d;
          if (sh.type === 'freeform') d = freeformPath(sh.pts || [], bw, bh, true, sh.smooth !== false);
          else if (sh.type === 'circle') d = `M0,${bh / 2}A${bw / 2} ${bh / 2} 0 1 0 ${bw},${bh / 2}A${bw / 2} ${bh / 2} 0 1 0 0,${bh / 2}Z`;
          else if (sh.type === 'rectangle') d = `M0,0H${bw}V${bh}H0Z`;
          else if (sh.type === 'triangle') d = `M${bw / 2},0L0,${bh}H${bw}Z`;
          else d = SHAPES_2D[sh.type].path(bw, bh);
          return probeCtx.isPointInPath(new Path2D(d), x, y);
        });
      if (hit) {
        const _b = canvasShapesRef.current;
        const _a = _b.map(sh => sh.id === hit.id
          ? {
            ...sh,
            fillColor: drawingColor,
            ...(sh.type === 'freeform' ? { closed: true } : {}),
            // A 3D object is one coloured body (see setShapeColor) -- its stroke takes the
            // same colour, or the fill would only recolour the gradient tint, not the body.
            ...(isShape3D(sh.type) ? { strokeColor: drawingColor } : {}),
          }
          : sh);
        setCanvasShapes(_a); canvasShapesRef.current = _a;
        pushShapeHistory(_b, _a);
        return;
      }
    }
    // No shape claimed the tap. Fall back to the hand-drawn ink layer -- but a region can be
    // bounded partly by a vector shape's edge (a loop drawn to touch another shape, say):
    // shapes render on their own SVG layer, not onto this canvas, so without help the flood
    // fill would see nothing there and leak straight through. Render the shapes onto a same-
    // size scratch canvas first and pass it in purely as extra "wall" pixels for the boundary
    // search -- never painted or persisted, just there so the search can see where they are.
    const wallsCanvas = document.createElement('canvas');
    wallsCanvas.width = drawCanvas.width; wallsCanvas.height = drawCanvas.height;
    drawShapesOnCanvas(wallsCanvas.getContext('2d'), canvasShapesRef.current, drawCanvas.width, drawCanvas.height);
    const probe = document.createElement('canvas');
    probe.width = drawCanvas.width; probe.height = drawCanvas.height;
    const pctx = probe.getContext('2d', { willReadFrequently: true });
    pctx.drawImage(drawCanvas, 0, 0);
    const result = computeFillMask(
      pctx, x * (drawCanvas.width / rect.width), y * (drawCanvas.height / rect.height), drawingColor, wallsCanvas,
    );
    if (!result) { flash('Tap inside a closed drawing to fill it'); return; }
    // Baked to a fixed bitmap right now, while both the ink and any bounding shapes are known
    // -- a live replay (like the old 'fill' type) has no way to see a shape's edge later, so
    // it would recompute wrong (or fail) the moment this same drawing is re-rendered.
    const maskUrl = result.mc.toDataURL('image/png');
    const newPath = {
      id: Date.now(), type: 'fillmask', mask: maskUrl,
      x: 0, y: 0, w: rect.width, h: rect.height, path: [],
      canvasW: rect.width, canvasH: rect.height,
    };
    setDrawingPaths(prev => [...prev, newPath]);
    markDirty();
    const newHistory = drawingHistory.slice(0, historyIndex + 1);
    newHistory.push({ type: 'draw', path: newPath });
    setDrawingHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const isInkSel = !!selectedShapeId
    && !canvasShapes.some(s => s.id === selectedShapeId)
    && drawingPaths.some(p => p.id === selectedShapeId);
  const applyShapeColour = (c) => {
    if (isInkSel) {
      setShapeStrokeColor(c);
      // mask/fillmask strokes are baked bitmaps -- there is no live colour to change.
      setDrawingPaths(prev => prev.map(p => (p.id === selectedShapeId && p.type !== 'mask' && p.type !== 'fillmask' && p.color !== 'eraser')
        ? { ...p, color: c } : p));
      markDirty();
      setShowShapeColorShades(false);
      setShowShapeColorPicker(false);
      return;
    }
    // A 3D object is one coloured body: whichever swatch is used, it takes the colour.
    const selShape3D = selectedShapeId && isShape3D(canvasShapes.find(sh => sh.id === selectedShapeId)?.type);
    if (selShape3D || isShape3D(activeShapeTool)) {
      if (c !== 'none') {
        setShapeFillColor(c); setShapeStrokeColor(c);
        if (selectedShapeId) setCanvasShapes(prev => prev.map(sh => sh.id === selectedShapeId ? { ...sh, fillColor: c, strokeColor: c } : sh));
      }
      setShowShapeColorShades(false);
      setShowShapeColorPicker(false);
      return;
    }
    if (shapeColorTarget === 'stroke') {
      setShapeStrokeColor(c);
      if (selectedShapeId) setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, strokeColor: c } : s));
      else if (selectedShapeIds.size > 0) setCanvasShapes(prev => prev.map(s => selectedShapeIds.has(s.id) ? { ...s, strokeColor: c } : s));
    } else {
      setShapeFillColor(c);
      // Applying an actual fill to a freeform shape that was only *effectively*
      // closed (drawn open, but its own start/end nearly meet -- see
      // isFreeformClosed) locks that in as a real closed shape, so it keeps its
      // fill everywhere (and keeps offering the fill control) even if a later
      // node edit pulls the ends apart again.
      const promote = c !== 'none' ? (s) => (s.type === 'freeform' ? { ...s, fillColor: c, closed: true } : { ...s, fillColor: c }) : (s) => ({ ...s, fillColor: c });
      if (selectedShapeId) setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? promote(s) : s));
      else if (selectedShapeIds.size > 0) setCanvasShapes(prev => prev.map(s => selectedShapeIds.has(s.id) ? promote(s) : s));
    }
    setShowShapeColorShades(false);
    setShowShapeColorPicker(false);
  };

  // ── Colours a person makes for themselves ──────────────────────────────────────────────────
  // The last two circles of every palette. The first holds a colour mixed in the mixer (the hex box
  // takes one too); the second takes a colour from the sheet with the eyedropper. Both open the same
  // shades as the built-in colours, and a shade is applied through the same path, so a colour made or
  // picked here is used exactly as a palette colour is.
  const shadesFor = (key) => (key === CUSTOM_KEY ? shadesOfColor(customSwatch || DEFAULT_CUSTOM) : (colorShades[key] || shadesOfColor(key)));
  const aiShadesFor = (key) => {
    if (key === CUSTOM_KEY) return shadesOfColor(customSwatch || DEFAULT_CUSTOM);
    if (key === PICK_KEY) return shadesOfColor(pickSwatch || '#FFFFFF');
    return cakePalette[key];
  };
  const aiColourName = (key) => (key === CUSTOM_KEY ? 'custom colour' : key === PICK_KEY ? 'picked colour' : key);

  // Starts the eyedropper from a palette. `route` says where the colour lands: 'draw' (the drawing
  // colour), 'shape' (the selected figure, or the next one drawn), 'ai' (the shades of the picked
  // colour, for the colour change), 'remove' (the colour for the background replacement, confirmed
  // by its own button) or 'bg' (the sheet's own colour). The palettes close so the sheet is clear.
  const startEyedropper = (route) => {
    prevCursorRef.current = canvasCursor;
    setEyedropperRoute(route);
    setEyedropperActive(true);
    setCanvasCursor(PICK_CURSOR);
    setShowColorPicker(false);
    setShowColorShades(false);
    setShowShapeColorPicker(false);
    setShowShapeColorShades(false);
    setShowColorPopdown(false);
    setShowBgColorPopdown(false);
    setShowLayoutMenu(false);
    setOpenGroup(null);
  };

  // The colour of one picture at a point, read from the picture itself, or null where it is
  // transparent or cannot be read. ox and oy are the point, measured from the picture's top-left.
  const sampleImageAt = async (img, ox, oy, iw, ih) => {
    try {
      const el = new Image();
      el.crossOrigin = 'anonymous';
      await new Promise((res, rej) => { el.onload = res; el.onerror = rej; el.src = img.url; });
      // One pixel of a photograph is noise: a speck of highlight, a dark grain, the JPEG's own
      // blocks. The colour a person means is the colour of the patch under the dropper, so a
      // small square of the picture's own pixels (5 x 5) is averaged, skipping transparent ones.
      const nx = Math.round(ox * (el.naturalWidth / iw)), ny = Math.round(oy * (el.naturalHeight / ih));
      const R = 2, S = R * 2 + 1;
      const tc = document.createElement('canvas');
      tc.width = S; tc.height = S;
      const tctx = tc.getContext('2d', { willReadFrequently: true });
      tctx.drawImage(el, R - nx, R - ny);
      const d = tctx.getImageData(0, 0, S, S).data;
      const centre = (R * S + R) * 4;
      if (d[centre + 3] <= 10) return null;
      let r = 0, g = 0, b = 0, n = 0;
      for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] <= 10) continue;
        r += d[i]; g += d[i + 1]; b += d[i + 2]; n++;
      }
      return n ? normaliseHex(rgbToHex(Math.round(r / n), Math.round(g / n), Math.round(b / n))) : null;
    } catch {
      return null;
    }
  };

  // The colour under a point on the sheet, as "#RRGGBB", or null when nothing with paint is there.
  // Strokes are looked at first, since they lie over everything; then pictures and figures from the
  // top layer down; then the sheet itself. A picture's transparent part does not count, so the pick
  // goes on to whatever lies beneath it.
  const pickColourAt = async (cx, cy, rect) => {
    const dc = drawingCanvasRef.current;
    if (dc && dc.width && dc.height) {
      // The drawing layer's own pixel size is not its size on screen, so the point is scaled in.
      const dr = dc.getBoundingClientRect();
      const sx = dr.width ? dc.width / dr.width : 1, sy = dr.height ? dc.height / dr.height : 1;
      const px0 = Math.min(dc.width - 1, Math.max(0, Math.floor(cx * sx)));
      const py0 = Math.min(dc.height - 1, Math.max(0, Math.floor(cy * sy)));
      try {
        const px = dc.getContext('2d', { willReadFrequently: true }).getImageData(px0, py0, 1, 1).data;
        if (px[3] > 10) return normaliseHex(rgbToHex(px[0], px[1], px[2]));
      } catch { /* an unreadable layer: go on to what lies beneath */ }
    }
    const W = rect.width, H = rect.height;
    const objects = [
      ...canvasShapesRef.current.map(sh => ({ z: sh.layerIndex ?? 4, shape: sh })),
      ...canvasImagesRef.current.map(img => ({ z: img.layerIndex ?? 1, img })),
    ].sort((a, b) => b.z - a.z);
    for (const o of objects) {
      if (o.shape) {
        const sh = o.shape;
        const x1 = Math.min(sh.x1, sh.x2) / 100 * W, x2 = Math.max(sh.x1, sh.x2) / 100 * W;
        const y1 = Math.min(sh.y1, sh.y2) / 100 * H, y2 = Math.max(sh.y1, sh.y2) / 100 * H;
        if (cx < x1 || cx > x2 || cy < y1 || cy > y2) continue;
        const isText = sh.type === 'text' || sh.type === 'emoji';
        const filled = !isText && !!sh.fillColor && sh.fillColor !== 'none';
        // An open figure is paint only along its outline; its inside belongs to whatever is beneath.
        const edge = (sh.strokeWidth || 2) / 2 + 3;
        const inside = cx > x1 + edge && cx < x2 - edge && cy > y1 + edge && cy < y2 - edge;
        if (!filled && !isText && inside) continue;
        const colour = filled ? sh.fillColor : sh.strokeColor;
        if (colour && colour !== 'none') return normaliseHex(colour) || colour;
        continue;
      }
      const img = o.img;
      const il = img.x / 100 * W, it = img.y / 100 * H;
      const iw = img.width / 21 * W, ih = img.height / 29.7 * H;
      if (cx < il || cx > il + iw || cy < it || cy > it + ih) continue;
      const hex = await sampleImageAt(img, cx - il, cy - it, iw, ih);
      if (hex) return hex;
    }
    if (canvasBackground && canvasBackground.startsWith('#')) return normaliseHex(canvasBackground);
    // A sheet with no colour of its own is shown as white paper, so that is the colour taken.
    return '#FFFFFF';
  };

  finishPickRef.current = async (clientX, clientY) => {
    const route = eyedropperRoute;
    const rect = canvasRef.current && canvasRef.current.getBoundingClientRect();
    setEyedropperActive(false);
    setEyedropperRoute(null);
    setCanvasCursor(route ? (prevCursorRef.current ?? 'default') : 'crosshair');
    shapeClickedRef.current = false;   // the figure under the pick must not absorb the next click
    const picked = rect ? await pickColourAt(clientX - rect.left, clientY - rect.top, rect) : null;
    if (picked) {
      setPickSwatch(picked);
      if (route === 'shape') applyShapeColour(picked);
      else if (route === 'ai') { setMagicCakeColorBase(PICK_KEY); setShowColorPopdown(true); }
      else if (route === 'remove') { setBgRemovePending({ hex: picked, label: 'picked' }); setShowBgColorPopdown(true); }
      else if (route === 'bg') { setCanvasBackground(picked.toLowerCase()); markDirty(); }
      else { setDrawingColor(picked); setPickedColor(picked); }
    } else if (route === 'remove') {
      setShowBgColorPopdown(true);   // nothing taken here: the popdown comes back as it was
    }
  };
  // Escape puts the dropper away without taking anything.
  cancelPickRef.current = () => {
    const route = eyedropperRoute;
    setEyedropperActive(false);
    setEyedropperRoute(null);
    setCanvasCursor(route ? (prevCursorRef.current ?? 'default') : 'crosshair');
    if (route === 'remove') setShowBgColorPopdown(true);
  };

  const handleCanvasClick = async (e) => {
    if (pendingNavPath) setPendingNavPath(null);
    if (pendingClear) cancelPendingClear();

    // Eyedropper: take the colour under the pointer. The palette route says where it lands.
    // (The press is normally taken earlier, in the capture phase; this is only a fallback.)
    if (eyedropperActive) {
      await finishPickRef.current?.(e.clientX, e.clientY);
      return;
    }

    setShowToolsPanel(false);
    // Reset save/share/print active state on canvas click
    if (['save', 'share', 'print'].includes(activeTool)) {
      setActiveTool(null);
      setCurrentControlSection(null);
      setShowTopControls(false);
      return;
    }
    // Skip if a marquee drag just finished -- the click fires right after mouseup
    if (marqueeJustFinishedRef.current) {
      marqueeJustFinishedRef.current = false;
      return;
    }
    // No tool active: a click on empty canvas deselects. This must NOT run while a tool is
    // active — the click event fires right after the mouseup that ends a crop drag (or a
    // brush stroke, or a shape), and clearing selection + activeTool here threw the user out
    // of the tool on every release. Selection being always-on used to make this condition
    // true unconditionally; the tool branches below are what should win when a tool is set.
    if (!activeTool) {
      if (shapeClickedRef.current) {
        shapeClickedRef.current = false;
        shapeDraggedRef.current = false;
        return;
      }
      setMagicSubTool(null);
      setSelectedImage(null);
      setShowImageControls(null);
      setSelectedShapeId(null);
      setSelectedShapeIds(new Set());
      setActiveShapeTool(null);
      setImageSelectionMode(false);
      setActiveTool(null);
      setCurrentControlSection(null);
      setCanvasCursor('default');
      setShowTopControls(false);
    } else if (activeTool === 'selection') {
      // Smart mode: canvas background click resets if not actively selecting/detecting
      if (selectionMode === 'smart') {
        // ── The tap that starts a detection must not also cancel it ─────────────────────
        // A tap is a pointerdown, a pointerup and then a click. The pointerup is what starts
        // the detection; the click arrives a moment later, and at that moment isSelecting is
        // still false and smartCropRunning — a piece of STATE, set by the detection that has
        // only just begun — has not come back yet. So this read it as "clicked the empty sheet"
        // and tore the whole tool down: pick Smart Crop, tap the object, and you were back in
        // the studio with nothing detected and nothing to show for it.
        //
        // smartCropBusyRef is set synchronously the instant a detection starts, which is what
        // makes it the right thing to ask.
        if (!isSelecting && !smartCropRunning && !smartCropBusyRef.current) {
          smartDetectedRef.current = false;
          setSmartDetected(false);
          setSelectionStart({ x: 0, y: 0 }); setSelectionEnd({ x: 0, y: 0 });
          setSelectionPath([]);
          setActiveTool(null); setSelectionMode('rectangle');
          setCurrentControlSection(null); setShowTopControls(false);
          setCanvasCursor('default');
        }
        return;
      }
      // Freeform mode: if a path was drawn, keep it visible so Copy/Cut buttons remain.
      // Only reset on a clean click without a drawn path (user clicking the canvas to cancel).
      if (selectionMode === 'freeform') {
        if (selectionPath.length > 2 || isSelecting) {
          return; // preserve the freeform shape & Copy/Cut buttons
        }
        // empty path → fall through to the catch-all reset below
      }
      // Canvas background click: reset selection overlay and deactivate mode
      // Circle / rectangle crop: clicking OUTSIDE the shape cancels; inside/edge is handled by mousedown drag.
      if (selectionMode === 'circle' || selectionMode === 'rectangle') {
        // The tail of a drag, not a click on the sheet. Dropped here rather than cancelling —
        // letting go of a handle has never meant "throw this away", and where the pointer
        // happens to be at that instant is not a decision the person made.
        if (cropDragMovedRef.current) { cropDragMovedRef.current = false; return; }
        const canvas = canvasRef.current;
        // cropHasBox, not `selectionStart.x !== 0` — the same coordinate-as-sentinel mistake
        // that broke the corner drag, and worse here. With the box's left edge at x = 0 this
        // guard failed, the inside/near-edge test below was skipped entirely, and every click
        // fell through to "clicked outside → cancel": isSelecting off, the box zeroed, and
        // selectionMode forced back to 'rectangle'. Re-entering crop then placed a fresh
        // RECTANGLE around the whole content — which is why a finished round selection turned
        // itself into a box covering the whole image.
        if (canvas && cropHasBox()) {
          const _rect = canvas.getBoundingClientRect();
          const cx = e.clientX - _rect.left;
          const cy = e.clientY - _rect.top;
          if (selectionMode === 'circle') {
            const ecx = (selectionStart.x + selectionEnd.x) / 2;
            const ecy = (selectionStart.y + selectionEnd.y) / 2;
            const erx = Math.abs(selectionEnd.x - selectionStart.x) / 2;
            const ery = Math.abs(selectionEnd.y - selectionStart.y) / 2;
            const normDist = (erx > 0 && ery > 0) ? Math.sqrt(((cx - ecx) / erx) ** 2 + ((cy - ecy) / ery) ** 2) : 2;
            if (normDist <= 1.3) return; // inside or near edge — mousedown drag handled it
          } else {
            const rx1 = Math.min(selectionStart.x, selectionEnd.x);
            const ry1 = Math.min(selectionStart.y, selectionEnd.y);
            const rx2 = Math.max(selectionStart.x, selectionEnd.x);
            const ry2 = Math.max(selectionStart.y, selectionEnd.y);
            const EDGE = 16;
            if (cx >= rx1 - EDGE && cx <= rx2 + EDGE && cy >= ry1 - EDGE && cy <= ry2 + EDGE) return; // inside/near edge
          }
        }
        // Click was outside — cancel crop
        setIsSelecting(false);
        setSelectionStart({ x: 0, y: 0 }); setSelectionEnd({ x: 0, y: 0 });
        setSelectionPath([]);
        setActiveTool(null); setSelectionMode('rectangle');
        setCurrentControlSection(null); setShowTopControls(false);
        setCanvasCursor('default');
        return;
      }
      setIsSelecting(false);
      setSelectionStart({ x: 0, y: 0 });
      setSelectionEnd({ x: 0, y: 0 });
      setSelectionPath([]);
      setActiveTool(null);
      setSelectionMode('rectangle');
      setCurrentControlSection(null);
      setShowTopControls(false);
      setCanvasCursor('default');
      return;
    } else if (activeTool === 'draw') {
      if (fillModeActive) { handleFillClick(e); return; }
      // A tap (no stroke drawn) on an image selects it so it can be dragged; the pen stays.
      if (!penStrokeDrawnRef.current && canvasRef.current) {
        const rc = canvasRef.current.getBoundingClientRect();
        const hitImg = imageAtPercent(((e.clientX - rc.left) / rc.width) * 100, ((e.clientY - rc.top) / rc.height) * 100);
        if (hitImg && hitImg.id !== selectedImage) { setSelectedImage(hitImg.id); setShowImageControls(hitImg.id); setSelectedShapeId(null); }
      }
      penStrokeDrawnRef.current = false;
      // Stay in draw mode -- keep controls visible
      return;
    } else if (activeTool === 'shapes') {
      if (shapeJustDrawnRef.current) { shapeJustDrawnRef.current = false; shapeDraggedRef.current = false; return; }
      // A tap (nothing drawn) on an image selects it so it can be dragged; the figure tool stays.
      if (!shapeDraggedRef.current && !isDrawingShape && canvasRef.current && !['text', 'emoji'].includes(activeShapeTool)) {
        const rc = canvasRef.current.getBoundingClientRect();
        const hitImg = imageAtPercent(((e.clientX - rc.left) / rc.width) * 100, ((e.clientY - rc.top) / rc.height) * 100);
        if (hitImg) {
          if (hitImg.id !== selectedImage) { setSelectedImage(hitImg.id); setShowImageControls(hitImg.id); setSelectedShapeId(null); }
          shapeDraggedRef.current = false;
          return;
        }
      }
      if (shapeClickedRef.current) {
        shapeClickedRef.current = false;
        shapeDraggedRef.current = false;
        return;
      }
      // Create text at click position
      if (activeShapeTool === 'text' && !shapeDraggedRef.current) {
        // A selected object means this tap puts it down; only an empty selection makes text.
        if (!selectedShapeId && !selectedImage && selectedShapeIds.size === 0) {
          const canvas = canvasRef.current;
          if (canvas) {
            const rect = canvas.getBoundingClientRect();
            const xPct = Math.max(0, Math.min(90, ((e.clientX - rect.left) / rect.width) * 100));
            const _rawYPct = ((e.clientY - rect.top) / rect.height) * 100;
            const y1Pct = Math.max(0, Math.min(95, _rawYPct - (shapeFontSize * 0.85 / rect.height) * 100));
            setPendingTextPos({ x: xPct, y: y1Pct });
            setPendingTextValue('');
          }
        }
        setSelectedShapeId(null);
        setActiveShapeTool(null);
        setActiveTool(null);
        setCanvasCursor('default');
        setCurrentControlSection(null);
        setShowTopControls(false);
        setShowToolsPanel(false);
        shapeDraggedRef.current = false;
        return;
      }
      if (!isDrawingShape) {
        // A tap on empty sheet deselects the current figure but keeps the tool active —
        // the pointer button (or Esc) is what puts the tool down.
        setSelectedShapeId(null);
        setShowShapeColorPicker(false);
        setShowShapeColorShades(false);
        setCurrentControlSection('shapes-draw');
        setShowTopControls(true);
        setShowToolsPanel(false);
      }
      shapeDraggedRef.current = false;
      return;
    } else if (activeTool === 'magic-wand') {
      // Clicking canvas while brightness/contrast is active deactivates the sub-tool
      if (magicSubTool === 'brightness' || magicSubTool === 'contrast') {
        setMagicSubTool(null);
      }
      setSelectedImage(null);
      setShowImageControls(null);
      setSelectedShapeId(null);
      setShowTopControls(false);
      setCurrentControlSection(null);
    } else {
      // For other tools, just clear selection
      setSelectedImage(null);
      setShowImageControls(null);
      setSelectedShapeId(null);
      setShowTopControls(false);
      setCurrentControlSection(null);
      if (magicSubTool === 'brightness' || magicSubTool === 'contrast') setMagicSubTool(null);
    }
  };

  // Update startDrawing function
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    if (activeTool === 'selection' && selectionMode) {
      e.preventDefault();
      e.stopPropagation();
      cropPointerDown(x, y);
      return;
    }

    // Marquee select: drag on empty canvas with NO tool active. This must come after the
    // tool branches — selection is always on now, so putting it first (as it once was)
    // made every press on the canvas start a marquee and nothing else ever ran.
    if (imageSelectionMode && !activeTool) {
      // A press that lands ON a hand-drawn line selects that stroke instead of starting a
      // marquee -- the same rule the figures follow (touch the line, not the empty space
      // inside it), except a stroke has no interior of its own, so the line is the target.
      // A press on empty sheet misses and falls through to the marquee below, unchanged.
      const _inkHit = eyedropperActive ? null : inkPathAtPoint(x, y, rect);
      if (_inkHit != null) { selectInkPath(_inkHit, x, y, rect); return; }
      const xPct = (x / rect.width) * 100;
      const yPct = (y / rect.height) * 100;
      isMarqueeSelectingRef.current = true;
      const mq = { x1: xPct, y1: yPct, x2: xPct, y2: yPct };
      marqueeRectRef.current = mq;
      setMarqueeRect(mq);
      setSelectedShapeIds(new Set());
      setSelectedShapeId(null);
      setShowTopControls(false);
      setCurrentControlSection(null);
      return;
    }

    if (eyedropperActive) return; // click handled by handleCanvasClick

    if (activeTool !== 'draw' || (drawingMode !== 'pen' && drawingMode !== 'pencil' && drawingMode !== 'eraser' && drawingMode !== 'spray')) {
      // Shape drawing
      if (activeTool === 'shapes' && activeShapeTool) {
        const xPct = (x / rect.width) * 100;
        const yPct = (y / rect.height) * 100;
        if (activeShapeTool === 'text') {
          // Text is created on click (handleCanvasClick), not mousedown
        } else if (activeShapeTool === 'emoji') {
          if (consumePressToDeselect()) {
            setActiveShapeTool(null);
            setActiveTool(null);
            setCanvasCursor('default');
            setCurrentControlSection(null);
            setShowTopControls(false);
            return;
          }
          if (!selectedEmoji) {
            setActiveShapeTool(null);
            setActiveTool(null);
            setCanvasCursor('default');
            return;
          }
          // Dropped on an image: embed into its pixels instead of adding an overlay shape.
          {
            const _hit = imageAtPercent(xPct, yPct);
            if (_hit) {
              const _fx = (xPct - _hit.x) / ((_hit.width / 21) * 100);
              const _fy = (yPct - _hit.y) / ((_hit.height / 29.7) * 100);
              embedStampIntoImage(_hit.id, selectedEmoji, { fx: _fx, fy: _fy });
              setSelectedEmoji(null);
              setActiveShapeTool(null);
              setActiveTool(null);
              setCanvasCursor('default');
              return;
            }
          }
          const _eBefore = canvasShapesRef.current;
          const _cW = canvasRef.current?.offsetWidth  || 595;
          const _cH = canvasRef.current?.offsetHeight || 841;
          const _stampW = 20; // 20% of canvas width
          const _stampH = (_cW / _cH) > 1 ? 20 : 20 * (_cW / _cH); // keep square-ish
          const newShape = {
            id: Date.now(), type: 'emoji',
            x1: xPct - _stampW / 2, y1: yPct - _stampH / 2,
            x2: xPct + _stampW / 2, y2: yPct + _stampH / 2,
            strokeColor: shapeStrokeColor, fillColor: 'none',
            strokeWidth: shapeStrokeWidth, dashed: shapeDashed, emoji: selectedEmoji, emojiSize: 0,
            zOrder: canvasShapes.length, layerIndex: ++layerCounterRef.current,
          };
          const _eAfter = [..._eBefore, newShape];
          setCanvasShapes(_eAfter);
          setSelectedShapeId(newShape.id);
          pushShapeHistory(_eBefore, _eAfter);
          markDirty();
          setSelectedEmoji(null);
          setActiveShapeTool(null);
          setActiveTool(null);
          setCanvasCursor('default');
          setShowToolsPanel(false);
        } else {
          // Something is selected: this press puts it down instead of starting another figure.
          if (consumePressToDeselect()) return;
          if (isFreeTool(activeShapeTool)) { freeDraftRef.current = [{ x: xPct, y: yPct }]; setFreeDraft(freeDraftRef.current); }
          setIsDrawingShape(true);
          setShapeDrawStart({ x: xPct, y: yPct });
          setShapeDrawEnd({ x: xPct, y: yPct });
        }
      }
      return;
    }

    if (!fillModeActive) {
      setIsDrawing(true);
      if (drawingMode === 'spray') {
        const N = 25, radius = brushSize * 1.2;
        const initDots = [];
        for (let i = 0; i < N; i++) {
          const angle = (i / N) * Math.PI * 2 + Math.random() * 0.4;
          const r = (0.2 + Math.random() * 0.8) * radius;
          initDots.push({ x: x + r * Math.cos(angle), y: y + r * Math.sin(angle) });
        }
        lastPenPointRef.current = { x, y }; setCurrentPath(initDots);
      } else {
        lastPenPointRef.current = { x, y }; setCurrentPath([{ x, y }]);
      }
    }
  };

  // Undo function
  const handleUndo = () => {
    if (historyIndex < 0) return;
    
    const lastAction = drawingHistory[historyIndex];
    if (lastAction.type === 'draw') {
      setDrawingPaths(prev => prev.filter(p => p.id !== lastAction.path.id));
    } else if (lastAction.type === 'image') {
      setCanvasImages(lastAction.before);
      setSelectedImage(null);
      if (lastAction.maskPath) setDrawingPaths(prev => prev.filter(p => p.id !== lastAction.maskPath.id));
    } else if (lastAction.type === 'shapes') {
      setCanvasShapes(lastAction.before);
      setSelectedShapeId(null);
    } else if (lastAction.type === 'sheetReplace') {
      setCanvasImages(lastAction.imagesBefore);
      setCanvasShapes(lastAction.shapesBefore);
      setDrawingPaths(lastAction.pathsBefore);
      setSelectedImage(null);
      setSelectedShapeId(null);
    }

    setHistoryIndex(prev => prev - 1);
  };

  // Redo function
  const handleRedo = () => {
    if (historyIndex >= drawingHistory.length - 1) return;
    
    const nextAction = drawingHistory[historyIndex + 1];
    if (nextAction.type === 'draw') {
      setDrawingPaths(prev => [...prev, nextAction.path]);
    } else if (nextAction.type === 'image') {
      setCanvasImages(nextAction.after);
      setSelectedImage(null);
      if (nextAction.maskPath) setDrawingPaths(prev => prev.some(p => p.id === nextAction.maskPath.id) ? prev : [...prev, nextAction.maskPath]);
    } else if (nextAction.type === 'shapes') {
      setCanvasShapes(nextAction.after);
      setSelectedShapeId(null);
    } else if (nextAction.type === 'sheetReplace') {
      setCanvasImages(nextAction.imagesAfter);
      setCanvasShapes(nextAction.shapesAfter);
      setDrawingPaths(nextAction.pathsAfter);
      setSelectedImage(null);
      setSelectedShapeId(null);
    }

    setHistoryIndex(prev => prev + 1);
  };

  // AI Magic Wand -- activates (toolbar handles sub-tools)
  const handleMagicWand = () => { /* tool activated via handleToolSelect */ };

  // Helper: load an img.src into a canvas element
  const _imgToCanvas = (src) => new Promise((resolve, reject) => {
    const el = new Image();
    el.crossOrigin = 'anonymous';
    el.onload = () => {
      const c = document.createElement('canvas');
      c.width = el.naturalWidth; c.height = el.naturalHeight;
      c.getContext('2d').drawImage(el, 0, 0);
      resolve(c);
    };
    el.onerror = () => reject(new Error('Failed to load image (CORS or invalid URL)'));
    el.src = src;
  });

  // Normalize an AI result data-URL to exactly targetWxtargetH pixels.
  // Some AI models return at a different size or aspect ratio than what was sent.
  // This letterboxes the result so the image content framing and freespace are preserved.
  // Bring an AI result back to EXACTLY the source's pixel size — the app's promise is that
  // no reproduction changes an image's size or resolution. Two rules, and neither is a crop:
  //   • aspect off by ≤ 8 % (the usual model drift): stretch to the target. Imperceptible,
  //     and every pixel of content is kept at its original scale.
  //   • aspect off by more: fit the whole result inside the target and fill the leftover
  //     bands by extending the edge rows/columns. Nothing is zoomed, nothing is distorted.
  // Centre-cropping — what Enhance used to do — is a zoom: it threw away the margins and
  // the subject came back visibly larger. Letterboxing with transparent bands was rejected
  // earlier because the image looked smaller on the sheet; edge-extension keeps the frame
  // full without either problem.
  //
  // With a `frame` (anything sent through prepareImageForKling / frameForAi) none of that
  // guessing applies: the answer is mapped back exactly — see frameForAi — and a cut-out gets
  // its transparency back according to `cutout` ('keep' | 'key' | 'opaque', see restoreCutout).
  const normalizeAiResult = (dataUrl, targetW, targetH, frame = null, cutout = 'key') => new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const sw = img.naturalWidth || img.width, sh = img.naturalHeight || img.height;
      if (frame) {
        try {
          const c = document.createElement('canvas');
          c.width = targetW; c.height = targetH;
          const ctx = c.getContext('2d', { willReadFrequently: true });
          ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
          // Underneath, the picture as it was — it only shows if the model cut deeper than
          // the margin, and then it fills the strip instead of leaving a hole.
          if (frame.srcEl && !frame.hasAlpha) ctx.drawImage(frame.srcEl, 0, 0, targetW, targetH);
          drawFramed(ctx, img, sw, sh, frameRectIn(frame, sw, sh), targetW, targetH);
          if (frame.hasAlpha && frame.srcEl && cutout !== 'opaque') restoreCutout(ctx, targetW, targetH, frame.srcEl, cutout);
          resolve(c.toDataURL('image/png'));
          return;
        } catch (e) {
          console.warn('[ai] framed normalize failed, falling back:', e);
        }
      }
      if (sw === targetW && sh === targetH) { resolve(dataUrl); return; }
      const c = document.createElement('canvas');
      c.width = targetW; c.height = targetH;
      const ctx = c.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      const srcAspect = sw / sh, dstAspect = targetW / targetH;
      const drift = Math.abs(srcAspect / dstAspect - 1);
      if (drift <= 0.08) {
        ctx.drawImage(img, 0, 0, targetW, targetH);
      } else {
        // Fit inside, then extend the edges into the bands.
        const scale = Math.min(targetW / sw, targetH / sh);
        const dw = Math.round(sw * scale), dh = Math.round(sh * scale);
        const dx = Math.round((targetW - dw) / 2), dy = Math.round((targetH - dh) / 2);
        ctx.drawImage(img, dx, dy, dw, dh);
        if (dx > 0) {   // vertical bands left/right: stretch the outermost column
          ctx.drawImage(img, 0, 0, 1, sh, 0, dy, dx, dh);
          ctx.drawImage(img, sw - 1, 0, 1, sh, dx + dw, dy, targetW - dx - dw, dh);
        }
        if (dy > 0) {   // horizontal bands top/bottom: stretch the outermost row (full width)
          ctx.drawImage(c, 0, dy, targetW, 1, 0, 0, targetW, dy);
          ctx.drawImage(c, 0, dy + dh - 1, targetW, 1, 0, dy + dh, targetW, targetH - dy - dh);
        }
      }
      resolve(c.toDataURL('image/png'));
    };
    img.onerror = () => resolve(dataUrl); // fallback: use result as-is
    img.src = dataUrl;
  });

  // (Kept only for reference / fallback experiments -- see prepareCompositeForKling.)
  // eslint-disable-next-line no-unused-vars
  const cropCompositeToEditPanel = (dataUrl, sentW, sentH, insetBox, outW, outH) => new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const sx = img.naturalWidth  / sentW;
      const sy = img.naturalHeight / sentH;
      const ix = Math.max(0, Math.round(insetBox.x * sx));
      const iy = Math.max(0, Math.round(insetBox.y * sy));
      const iw = Math.min(img.naturalWidth  - ix, Math.round(insetBox.w * sx));
      const ih = Math.min(img.naturalHeight - iy, Math.round(insetBox.h * sy));

      // Draw the full result first
      const work = document.createElement('canvas');
      work.width = img.naturalWidth; work.height = img.naturalHeight;
      const wctx = work.getContext('2d');
      wctx.drawImage(img, 0, 0);

      // Inpaint the inset area: tile/stretch the strip of pixels just to the LEFT of the
      // inset into the inset region. This usually matches the surrounding scene reasonably.
      const sampleW = Math.max(8, Math.min(ix, Math.round(iw * 0.6)));
      if (sampleW > 0 && iw > 0 && ih > 0) {
        const sampleX = Math.max(0, ix - sampleW);
        wctx.imageSmoothingEnabled = true;
        wctx.imageSmoothingQuality = 'high';
        // Stretch the sample strip horizontally to cover the inset
        wctx.drawImage(work, sampleX, iy, sampleW, ih, ix, iy, iw, ih);
        // Light blur pass over the seam by re-drawing a wider blend strip
        wctx.globalAlpha = 0.55;
        wctx.drawImage(work, sampleX, iy, sampleW, ih, Math.max(0, ix - 4), iy, iw + 8, ih);
        wctx.globalAlpha = 1;
      }

      // Scale to output size
      const c = document.createElement('canvas');
      c.width = outW; c.height = outH;
      const ctx = c.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(work, 0, 0, outW, outH);
      resolve(c.toDataURL('image/png'));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });

  // Center-crop a data URL to match targetAspect (w/h), then scale to outW × outH.
  // Used by beauty/enhance to undo Kling's aspect-ratio snapping (Kling rounds to the
  // nearest of its supported ratios — 1:1, 4:3, 3:2, etc. — which slightly distorts
  // the returned image if the original doesn't land exactly on one of those ratios).
  // Same policy as normalizeAiResult. This used to centre-crop to the target aspect, which is
  // a zoom — Enhance came back with the subject enlarged. targetAspect is implied by outW/outH.
  const cropToAspect = (dataUrl, _targetAspect, outW, outH, frame, cutout) => normalizeAiResult(dataUrl, outW, outH, frame, cutout);
  const handleMagicRemoveBg = async () => {
    if (!effectiveImageId) { showAlert('Please select an image first'); return; }
    const img = canvasImagesRef.current.find(i => i.id === effectiveImageId);
    if (!img) return;
    setMagicProcessing(true);
    try {
      const tmpCanvas = await _imgToCanvas(img.url);
      const result = await withEmbeddedStamps(img, await removeImageBg(tmpCanvas));
      const before = canvasImagesRef.current;
      const after = before.map(i => i.id === effectiveImageId ? { ...i, url: result } : i);
      setCanvasImages(after);
      canvasImagesRef.current = after;
      pushImageHistory(before, after);
    } catch (err) {
      showAlert('Background removal failed: ' + (err?.message || String(err) || 'Unknown error'));
    } finally {
      setMagicProcessing(false);
    }
  };

  // sessionStorage is the hand-off channel back to the Custom page, and it caps out around
  // 5MB per origin. Every AI edit replaces the image with a full-resolution PNG data URL,
  // which can exceed that — and setItem THROWS when it does. That throw used to escape the
  // Return-to-Custom handler and kill the navigate() after it, so the button just did
  // nothing once any AI edit had been applied. Store the largest version that fits,
  // shrinking progressively, and never let a storage failure trap the user in the studio.
  const stashReturnImage = async (src) => {
    const tryPut = (val) => {
      try { sessionStorage.setItem('studio_returned_image', val); return true; } catch { return false; }
    };
    if (tryPut(src)) return true;
    try {
      const el = await loadImgSafe(src);
      if (el) {
        for (const maxDim of [1600, 1200, 900, 600]) {
          const scale = Math.min(1, maxDim / Math.max(el.naturalWidth, el.naturalHeight));
          const c = document.createElement('canvas');
          c.width = Math.max(1, Math.round(el.naturalWidth * scale));
          c.height = Math.max(1, Math.round(el.naturalHeight * scale));
          c.getContext('2d').drawImage(el, 0, 0, c.width, c.height);
          // PNG, not JPEG — background removal produces transparency that JPEG would
          // flatten to black.
          if (tryPut(c.toDataURL('image/png'))) return true;
        }
      }
    } catch { /* fall through to the warning */ }
    try { sessionStorage.removeItem('studio_returned_image'); } catch { /* ignore */ }
    setMagicChatError('The edited image was too large to carry back to Custom — save it from here instead.');
    return false;
  };

  // Converts raw API response errors into user-friendly messages
  const friendlyApiError = (res, data) => {
    const backendMsg = data?.error || '';
    // 402 = subscription / credits / auth error surfaced by the backend
    if (res?.status === 402) return backendMsg || 'AI subscription is not active or has insufficient credits.';
    // 429 from our rate limiter (no specific backend message) vs. Kling rate limit (has one)
    if (res?.status === 429) return backendMsg || 'The AI is busy right now -- please wait a moment and try again';
    // 501 = feature not configured (e.g. missing GEMINI_API_KEY)
    if (res?.status === 501) return backendMsg || 'This AI feature is not configured on the server.';
    // 504 = poll timeout
    if (res?.status === 504) return backendMsg || 'The AI took too long to respond -- please try again';
    // 502/503 = upstream failure; show backend message if it's informative, else generic
    if (res?.status === 503 || res?.status === 502) return backendMsg || 'AI service is temporarily unavailable -- try again shortly';
    if (res?.status === 401 || res?.status === 403) return 'AI service authorisation error -- please contact support';
    return backendMsg || 'AI edit failed';
  };

  // Single-image AI edit, and the one funnel every magic button goes through. Which editor
  // answers is the server's business (AI_IMAGE_PROVIDER, set from OneAdmin) with one
  // exception: the three-strikes rule below, which only this side can see. Aspect ratio is
  // preserved by the prompt plus the normalizeAiResult step at the call sites.
  // layerId defaults to the selected image, which is what every caller but the whole-sheet
  // edit is acting on. origW/origH, when the caller has them, let Kling be asked for the
  // nearest aspect ratio it supports rather than guessing one.
  const runSingleImageAiEdit = async ({ imageBase64, prompt, sketchGuided, layerId, origW, origH, w, h }) => {
    const provider = aiProviderFor(layerId === undefined ? effectiveImageId : layerId, prompt);
    // origW/origH is the picture's true size and w/h the size actually sent; nine of the ten
    // call sites pass only the second pair, so taking just the first quietly left every Kling
    // request without an aspect ratio and let Kling pick its own. Either will do — it is the
    // RATIO that is being asked for, and clamping preserved it.
    // Since the picture goes inside a frame (frameForAi), w/h are the frame's and come first:
    // that is the shape the model sees, so it is the shape an editor that needs telling is told.
    const aw = w > 0 ? w : origW, ah = h > 0 ? h : origH;
    const out = await aiEditImage(imageBase64, prompt, undefined, {
      ...(sketchGuided ? { sketchGuided: true } : {}),
      ...(provider ? { provider } : {}),
      ...(aw > 0 && ah > 0 ? { imageWidth: aw, imageHeight: ah } : {}),
      // Kling answers in its own time, so the waiting message has to stay up while it does.
      onWait: () => setMagicChatStep('waiting'),
    });
    setMagicChatStep('applying');
    return { success: true, imageBase64: out };
  };

  // Magic: remove cake plate via Kling AI
  const handleMagicRemovePlate = async () => {
    if (!effectiveImageId) { showAlert('Please select an image first'); return; }
    const img = canvasImagesRef.current.find(i => i.id === effectiveImageId);
    if (!img) return;
    setMagicProcessing(true);
    setMagicChatError('');
    setMagicChatStep('preparing');
    let waitingTimer = null;
    let tempStorageRef = null;
    try {
      const { imageBase64, w, h, origW, origH, tempRef, frame } = await prepareImageForKling(img, { suffix: 'plate' });
      tempStorageRef = tempRef;
      setMagicChatStep('sending');
      waitingTimer = setTimeout(() => setMagicChatStep('waiting'), 5000);
      const data = await runSingleImageAiEdit({
        imageBase64, w, h,
        prompt: 'Remove only the plate, board, or cake drum that the cake is sitting on. Do NOT remove or alter the background in any way -- keep it exactly as it is. Do NOT make the background transparent. The cake and all its decorations must stay completely unchanged. Fill the area where the plate was with the background that is already visible around it.',
      });
      clearTimeout(waitingTimer);
      waitingTimer = null;
      // Normalize result to sent dimensions -- preserves same pixel framing and freespace
      const normedPlate = await normalizeAiResult(data.imageBase64, origW, origH, frame, 'key');
      const before = canvasImagesRef.current;
      const after = before.map(i => i.id === effectiveImageId ? { ...i, url: normedPlate } : i);
      setCanvasImages(after);
      canvasImagesRef.current = after;
      pushImageHistory(before, after);
      setMagicChatStep('');
    } catch (err) {
      if (waitingTimer) { clearTimeout(waitingTimer); waitingTimer = null; }
      setMagicChatStep('');
      setMagicChatError('Plate removal failed: ' + err.message);
    } finally {
      setMagicProcessing(false);
      if (tempStorageRef) { deleteObject(tempStorageRef).catch(() => {}); }
    }
  };

  // Magic: AI photo enhance -- maximise sharpness and quality without changing content
  const handleMagicEnhance = async () => {
    if (!effectiveImageId) { showAlert('Please select an image first'); return; }
    const img = canvasImagesRef.current.find(i => i.id === effectiveImageId);
    if (!img) return;
    setMagicProcessing(true);
    setMagicChatStep('preparing');
    setMagicChatError('');
    let tempRef = null;
    let waitingTimer = null;
    try {
      // Use max:1920 (Kling v3 supports up to 1920px) for the highest quality input.
      // Default max:1024 was limiting the output resolution unnecessarily.
      const { imageBase64, w, h, origW, origH, tempRef: _tempRef, frame } = await prepareImageForKling(img, { max: 1920, suffix: 'enhance' });
      tempRef = _tempRef;

      // Record the original aspect ratio so we can center-crop the result back to it.
      // Kling snaps to its nearest supported ratio (1:1, 4:3, 3:2, 16:9…) which can
      // slightly distort the image if the original doesn't land exactly on one of them.
      const origNatW = img.naturalWidth  || w;
      const origNatH = img.naturalHeight || h;
      const origAspect = origNatW / origNatH;

      setMagicChatStep('sending');
      waitingTimer = setTimeout(() => setMagicChatStep('waiting'), 5000);
      const data = await runSingleImageAiEdit({
        imageBase64, w, h, imageFidelity: 0.95,
        prompt: 'Enhance this photo to the highest possible quality. Maximise sharpness, clarity, and fine detail. Correct any noise, blur, or compression artefacts. Optimise brightness, contrast, and colour balance for a vivid, professional look. Make textures, edges, and fine details crisp and clean. Do NOT change the subject, composition, framing, design, decorations, text, or aspect ratio of the image in any way.',
      });
      clearTimeout(waitingTimer); waitingTimer = null;

      // Center-crop the result back to the original aspect ratio (undoes Kling's ratio
      // snapping), then scale to the prepared resolution (w × h) so the URL has the
      // full enhanced resolution. Keep canvas frame (width/height in cm) unchanged.
      const enhanced = await withEmbeddedStamps(img, await cropToAspect(data.imageBase64, origAspect, origW, origH, frame, 'keep'));

      const before = canvasImagesRef.current;
      const after = before.map(i => i.id === effectiveImageId
        ? { ...i, url: enhanced, naturalWidth: origW, naturalHeight: origH }
        : i
      );
      setCanvasImages(after);
      canvasImagesRef.current = after;
      pushImageHistory(before, after);
      setMagicChatStep('');
    } catch (err) {
      if (waitingTimer) { clearTimeout(waitingTimer); }
      setMagicChatStep('');
      setMagicChatError('Enhance failed: ' + err.message);
    } finally {
      setMagicProcessing(false);
      if (tempRef) deleteObject(tempRef).catch(() => {});
    }
  };

  // Magic: remove background from selected image -- tries remove.bg first, falls back to Kling AI
  const handleMagicRemoveImageBg = async () => {
    if (!effectiveImageId) { showAlert('Please select an image first'); return; }
    const img = canvasImagesRef.current.find(i => i.id === effectiveImageId);
    if (!img) return;
    setMagicProcessing(true);
    setMagicChatStep('preparing');
    setMagicChatError('');
    let tempRef = null;
    try {
      // remove.bg is the only reliable way to get true alpha transparency.
      // If it's unavailable, we show an error rather than falling back to Kling,
      // which cannot produce real transparent output.
      const tmpCanvas = await _imgToCanvas(img.url);
      const resultUrl = await removeImageBg(tmpCanvas);
      const before = canvasImagesRef.current;
      const after = before.map(i => i.id === effectiveImageId ? { ...i, url: resultUrl } : i);
      setCanvasImages(after);
      canvasImagesRef.current = after;
      pushImageHistory(before, after);
      setMagicChatStep('');
    } catch (err) {
      setMagicChatStep('');
      const msg = err.message || '';
      setMagicChatError(
        msg.includes('502') || msg.includes('remove.bg')
          ? 'Background removal is temporarily unavailable. Please try again later.'
          : 'BG removal failed: ' + msg
      );
    } finally {
      setMagicProcessing(false);
      if (tempRef) deleteObject(tempRef).catch(() => {});
    }
  };

  // Magic: remove background then fill with a solid colour
  const handleMagicRemoveImageBgWithColor = async (hexColor, colorLabel) => {
    if (!effectiveImageId) { showAlert('Please select an image first'); return; }
    const img = canvasImagesRef.current.find(i => i.id === effectiveImageId);
    if (!img) return;
    setMagicProcessing(true);
    setMagicChatStep('preparing');
    setMagicChatError('');
    let tempRef = null;
    try {
      // Use Kling AI to directly replace the background with the target colour.
      // (remove.bg is skipped here -- Kling cannot produce real transparency,
      // so the two-step transparent-then-composite approach doesn't work reliably.)
      setMagicChatStep('sending');
      const { imageBase64, w, h, origW, origH, tempRef: _tempRef, frame } = await prepareImageForKling(img, { suffix: 'bgcol' });
      tempRef = _tempRef;
      const waitTimer = setTimeout(() => setMagicChatStep('waiting'), 5000);
      const data = await runSingleImageAiEdit({
        imageBase64, w, h,
        prompt: `Remove the existing background from this image completely, then fill the entire background with a solid, flat, uniform ${colorLabel} colour (${hexColor}). The background should be a single clean ${colorLabel} colour with no gradients, shadows, or textures. Keep the main subject completely unchanged -- do not alter its shape, colours, text, or any details in any way.`,
      });
      clearTimeout(waitTimer);
      // A new background is the point of this one: the answer stays opaque.
      const normedResult = await withEmbeddedStamps(img, await normalizeAiResult(data.imageBase64, origW, origH, frame, 'opaque'));
      const before = canvasImagesRef.current;
      const after = before.map(i => i.id === effectiveImageId ? { ...i, url: normedResult } : i);
      setCanvasImages(after);
      canvasImagesRef.current = after;
      pushImageHistory(before, after);
      setMagicChatStep('');
    } catch (err) {
      setMagicChatStep('');
      setMagicChatError('BG colour fill failed: ' + err.message);
    } finally {
      setMagicProcessing(false);
      if (tempRef) deleteObject(tempRef).catch(() => {});
    }
  };

  // Magic: change cake colour via Kling AI
  // ── The colour asked for, not the colour the model felt like ─────────────────────────────
  // An image model told "make it #2E9A3B" paints something in that direction — lighter one
  // time, duller the next — so the colour wandered with every try and never matched the swatch.
  // What it does reliably is decide WHICH pixels are the subject and keep their shading. So the
  // pixels it changed are found by comparing with the picture before, and they are set to the
  // exact colour, each keeping its own light and shade relative to the rest (a fold stays darker
  // than the face round it). Pixels it left alone are left alone, and the edge blends.
  const matchColourToTarget = async (beforeUrl, afterUrl, hex) => {
    const t = hexToRgb(hex);
    if (!t) return afterUrl;
    const [bi, ai] = await Promise.all([loadImgSafe(beforeUrl), loadImgSafe(afterUrl)]);
    if (!bi || !ai) return afterUrl;
    const W = ai.naturalWidth || ai.width, H = ai.naturalHeight || ai.height;
    if (!W || !H || W * H > 36e6) return afterUrl;
    const ca = document.createElement('canvas'); ca.width = W; ca.height = H;
    const xa = ca.getContext('2d', { willReadFrequently: true });
    xa.drawImage(ai, 0, 0, W, H);
    const A = xa.getImageData(0, 0, W, H);
    const cb = document.createElement('canvas'); cb.width = W; cb.height = H;
    const xb = cb.getContext('2d', { willReadFrequently: true });
    xb.drawImage(bi, 0, 0, W, H);
    const B = xb.getImageData(0, 0, W, H).data;
    const a = A.data, n = W * H;
    const lum = (r, g, b) => 0.2126 * r + 0.7152 * g + 0.0722 * b;
    const wt = new Float32Array(n);
    let sw = 0, sl = 0;
    for (let i = 0; i < n; i++) {
      const p = i * 4;
      if (a[p + 3] <= 20) continue;
      const diff = Math.abs(a[p] - B[p]) + Math.abs(a[p + 1] - B[p + 1]) + Math.abs(a[p + 2] - B[p + 2]);
      let w = Math.max(0, Math.min(1, (diff - 45) / 60));
      // Asked again for the colour it already is, the model nudges it a little — not enough to
      // count as a change above, but enough to drift a shade per try. A pixel that was already
      // close to the colour asked for, and that the model touched at all, is held to it too.
      if (w < 1 && diff > 12) {
        const near = Math.abs(B[p] - t.r) + Math.abs(B[p + 1] - t.g) + Math.abs(B[p + 2] - t.b);
        if (near < 90) w = 1;
      }
      if (!w) continue;
      wt[i] = w; sw += w; sl += w * lum(a[p], a[p + 1], a[p + 2]);
    }
    // The model changed (next to) nothing: nothing to correct.
    if (sw < n * 0.002) return afterUrl;
    const meanL = Math.max(1, sl / sw);
    for (let i = 0; i < n; i++) {
      const w = wt[i];
      if (!w) continue;
      const p = i * 4;
      const k = lum(a[p], a[p + 1], a[p + 2]) / meanL;
      a[p]     = Math.round(a[p]     * (1 - w) + Math.min(255, t.r * k) * w);
      a[p + 1] = Math.round(a[p + 1] * (1 - w) + Math.min(255, t.g * k) * w);
      a[p + 2] = Math.round(a[p + 2] * (1 - w) + Math.min(255, t.b * k) * w);
    }
    xa.putImageData(A, 0, 0);
    return ca.toDataURL('image/png');
  };

  const handleMagicColorChange = async (shade, colorName) => {
    if (!effectiveImageId) { showAlert('Please select an image first'); return; }
    const img = canvasImagesRef.current.find(i => i.id === effectiveImageId);
    if (!img) return;
    setMagicProcessing(true);
    setMagicChatStep('preparing');
    setMagicChatError('');
    let tempRef = null;
    try {
      const { imageBase64, w, h, origW, origH, tempRef: _tempRef, frame } = await prepareImageForKling(img, { suffix: 'color' });
      tempRef = _tempRef;
      setMagicChatStep('sending');
      const waitTimer = setTimeout(() => setMagicChatStep('waiting'), 5000);
      const data = await runSingleImageAiEdit({
        imageBase64, w, h,
        prompt: `Change the dominant colour of the main subject to ${colorName} (${shade}). Keep the subject's shape, structure, details, text, and everything else in the picture completely unchanged. Only recolour the main subject.`,
      });
      clearTimeout(waitTimer);
      const recoloured = await normalizeAiResult(data.imageBase64, origW, origH, frame, 'keep');
      let exact = recoloured;
      try { exact = await matchColourToTarget(img.url, recoloured, shade); }
      catch (e) { console.warn('Colour change: could not match the exact colour.', e?.message || e); }
      const normedColor = await withEmbeddedStamps(img, exact);
      const before = canvasImagesRef.current;
      // Always update the existing image in-place -- never add a new one
      const after = before.map(i => i.id === effectiveImageId ? { ...i, url: normedColor } : i);
      setCanvasImages(after);
      canvasImagesRef.current = after;
      pushImageHistory(before, after);
      setMagicChatStep('');
    } catch (err) {
      setMagicChatStep('');
      setMagicChatError('Colour change failed: ' + err.message);
    } finally {
      setMagicProcessing(false);
      if (tempRef) deleteObject(tempRef).catch(() => {});
    }
  };

  // Ask the backend how many tiers the cake actually has. The running count can't be
  // trusted on its own: it assumes every photo starts as a single tier, so a photo that
  // was already 2-tier reads as 1 (and a tier the model silently declined to add still
  // increments it). Removing a tier needs the real number, otherwise we'd refuse on a
  // multi-tier cake or try to remove one from a single-tier cake. Returns null when it
  // can't tell, so the caller can decide rather than acting on a wrong number.
  const detectCakeTiers = async (imgObj) => {
    try {
      const el = await loadImgSafe(imgObj?.url || '');
      if (!el) return null;
      const scale = Math.min(1, 768 / Math.max(el.naturalWidth, el.naturalHeight));
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(el.naturalWidth * scale));
      c.height = Math.max(1, Math.round(el.naturalHeight * scale));
      c.getContext('2d').drawImage(el, 0, 0, c.width, c.height);
      // The generic describe action returns prose, so ask a narrow question and read a
      // single integer out of the answer. Anything unparseable means "don't know", which
      // the caller already handles by assuming one tier.
      const answer = await aiDescribe(
        c.toDataURL('image/jpeg', 0.85),
        'How many separate stacked tiers does this cake have? Reply with a single digit only. If this is not a cake, reply 0.',
      );
      const n = parseInt(String(answer).match(/\d+/)?.[0] ?? '', 10);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch { return null; }
  };

  // The tier count to work from: whatever we've already established for this image,
  // else ask the backend, else fall back to assuming a single tier.
  const resolveTierCount = async (imgObj) => {
    const known = magicTierCounts[effectiveImageId];
    if (typeof known === 'number') return known;
    const detected = await detectCakeTiers(imgObj);
    const count = detected ?? 1;
    setMagicTierCounts(prev => ({ ...prev, [effectiveImageId]: count }));
    return count;
  };

  // After a tier edit, cache what the AI ACTUALLY produced -- never what we asked for.
  // Trusting the request is what made repeat taps drift: the model often declines to go past
  // 2 tiers, but the count kept climbing anyway, so the next prompt opened with a false
  // premise ("it currently has 4") and every later add/remove was computed off a cake that
  // never existed. Re-reading the result keeps the count honest, at the cost of one extra
  // detect per edit. Falls back to the requested count only when detection itself fails.
  const reconcileTierCount = async (newUrl, requested) => {
    const actual = await detectCakeTiers({ url: newUrl });
    const count = (typeof actual === 'number' && actual > 0) ? actual : requested;
    setMagicTierCounts(prev => ({ ...prev, [effectiveImageId]: count }));
    return count;
  };

  // Magic: add a cake tier (Buyer AI toolbar) -- each tap stacks one more tier onto
  // the current image, chaining off the last edit so tiers accumulate over repeat taps.
  const handleMagicAddTier = async () => {
    if (!effectiveImageId) { showAlert('Please select an image first'); return; }
    const img = canvasImagesRef.current.find(i => i.id === effectiveImageId);
    if (!img) return;
    setMagicProcessing(true);
    setMagicChatStep('preparing');
    setMagicChatError('');
    let tempRef = null;
    try {
      const { imageBase64, w, h, origW, origH, tempRef: _tempRef, frame } = await prepareImageForKling(img, { suffix: 'tier' });
      tempRef = _tempRef;
      setMagicChatStep('sending');
      const waitTimer = setTimeout(() => setMagicChatStep('waiting'), 5000);
      // State the target as an ABSOLUTE count, and allow the camera to pull back.
      // "Add one more tier" is relative — the model has to infer what's already there and
      // often just returns the picture unchanged. Worse, the old prompt also said "do not
      // zoom or reframe" while asking for a TALLER cake: fine at 2 tiers where there's
      // headroom, impossible at 3 (it can't fit), so the model silently dropped the tier.
      // Pulling back is about what's IN the frame; the output pixel size is still pinned
      // to origW/origH by normalizeAiResult, so the photo itself is not resized.
      const targetTiers = (await resolveTierCount(img)) + 1;
      const data = await runSingleImageAiEdit({
        imageBase64, w, h,
        // Result-first, same reasoning as the remove prompt: at 3+ tiers the model tends to
        // quietly return the original rather than restructure the cake, so the required tier
        // count leads and is restated as a hard requirement.
        prompt: `The finished image must show a cake with exactly ${targetTiers} tiers stacked vertically — count them: ${targetTiers} — largest at the bottom and smallest at the top. The cake in this photo currently has ${targetTiers - 1}; add one more tier beneath the existing ${targetTiers - 1 === 1 ? 'one' : 'ones'} so the stack becomes ${targetTiers} tiers tall. This is a required change — do not return the picture with only ${targetTiers - 1} tiers. Every tier must share the exact same design, decorations, colours and frosting style as the original. Keep the same background, lighting and camera angle, and do not crop the cake — zoom out as much as needed so the whole ${targetTiers}-tier cake is fully visible with a little space around it.`,
      });
      clearTimeout(waitTimer);
      const normedTier = await normalizeAiResult(data.imageBase64, origW, origH, frame, 'key');
      const before = canvasImagesRef.current;
      const after = before.map(i => i.id === effectiveImageId ? { ...i, url: normedTier } : i);
      setCanvasImages(after);
      canvasImagesRef.current = after;
      pushImageHistory(before, after);
      const got = await reconcileTierCount(normedTier, targetTiers);
      setMagicChatStep('');
      // Say so when the model didn't deliver. Silence here reads as "the button is broken",
      // and the user taps again — which previously just pushed the count further from reality.
      if (got < targetTiers) {
        setMagicChatError(`The AI couldn't stack a ${targetTiers}${targetTiers === 3 ? 'rd' : 'th'} tier — the cake still has ${got}. Tap again to retry.`);
      }
    } catch (err) {
      setMagicChatStep('');
      setMagicChatError('Add tier failed: ' + err.message);
    } finally {
      setMagicProcessing(false);
      if (tempRef) deleteObject(tempRef).catch(() => {});
    }
  };

  // Magic: remove a cake tier -- the inverse of handleMagicAddTier. Refuses when the cake
  // is already a single tier, checking the ACTUAL cake (see resolveTierCount) rather than
  // trusting the running count, which can be wrong for a photo that arrived multi-tier.
  const handleMagicRemoveTier = async () => {
    if (!effectiveImageId) { showAlert('Please select an image first'); return; }
    const img = canvasImagesRef.current.find(i => i.id === effectiveImageId);
    if (!img) return;
    setMagicProcessing(true);
    setMagicChatStep('preparing');
    setMagicChatError('');
    let tempRef = null;
    try {
      const current = await resolveTierCount(img);
      if (current <= 1) {
        setMagicChatStep('');
        setMagicChatError('This cake already has a single tier — there is nothing to remove.');
        return;
      }
      const { imageBase64, w, h, origW, origH, tempRef: _tempRef, frame } = await prepareImageForKling(img, { suffix: 'untier' });
      tempRef = _tempRef;
      setMagicChatStep('sending');
      const waitTimer = setTimeout(() => setMagicChatStep('waiting'), 5000);
      const targetTiers = current - 1;
      const data = await runSingleImageAiEdit({
        imageBase64, w, h,
        // State the REQUIRED RESULT first and repeat the number: phrased as an operation
        // ("remove the bottom tier") the model treats it as optional and often returns the
        // photo essentially unchanged. The count is what's being checked afterwards, so it's
        // what the prompt has to lead with.
        prompt: `The finished image must show a cake with exactly ${targetTiers} tier${targetTiers === 1 ? '' : 's'} — count them: ${targetTiers}. The cake in this photo currently has ${current} tiers stacked vertically; take off the topmost tier so that only the lower ${targetTiers === 1 ? 'single tier' : `${targetTiers} tiers`} remain, largest at the bottom. This is a required change — do not return the picture with ${current} tiers still on it. The remaining tier${targetTiers === 1 ? '' : 's'} must keep the exact same design, decorations, colours and frosting style, including any writing on the cake or the board. Keep the same background, lighting and camera angle, and do not crop the cake — frame it so the whole ${targetTiers}-tier cake is fully visible with a little space around it.`,
      });
      clearTimeout(waitTimer);
      const normedTier = await normalizeAiResult(data.imageBase64, origW, origH, frame, 'key');
      const before = canvasImagesRef.current;
      const after = before.map(i => i.id === effectiveImageId ? { ...i, url: normedTier } : i);
      setCanvasImages(after);
      canvasImagesRef.current = after;
      pushImageHistory(before, after);
      const got = await reconcileTierCount(normedTier, targetTiers);
      setMagicChatStep('');
      if (got > targetTiers) {
        setMagicChatError(`The AI couldn't take a tier off — the cake still has ${got}. Tap again to retry.`);
      }
    } catch (err) {
      setMagicChatStep('');
      setMagicChatError('Remove tier failed: ' + err.message);
    } finally {
      setMagicProcessing(false);
      if (tempRef) deleteObject(tempRef).catch(() => {});
    }
  };

  // Maps a click position (0-1 fraction within the image's own bounds) to a human region description
  const describePosition = (fx, fy) => {
    const col = fx < 0.34 ? 'left' : fx < 0.67 ? 'center' : 'right';
    const row = fy < 0.34 ? 'top' : fy < 0.67 ? 'middle' : 'bottom';
    if (row === 'middle' && col === 'center') return 'center';
    return `${row}-${col}`;
  };

  // Shared AI call for adding/replacing the name written on the cake
  // Spell the text out character by character. Image models routinely drop or duplicate
  // characters when rendering text ("Mouni 11" came back as "Mouni 111"); giving the exact
  // characters, the exact count, and an explicit "no extra characters" is the most reliable
  // way to hold them to it.
  const exactTextRule = (name) =>
    `The text must read exactly "${name}" — that is the ${name.length} characters ${name.split('').map(c => (c === ' ' ? '(space)' : c)).join('-')}, with no extra letters, digits, punctuation or repeated characters, and nothing else written anywhere on the cake.`;

  // Read back what the model ACTUALLY wrote and compare it to what was asked for. The
  // generator paints glyph shapes rather than typing characters, so it can render the
  // wrong letters ("Rishav" -> "Rushav") with no idea anything went wrong. Deliberately
  // fire-and-forget: the edited image is already on the canvas, so this never delays the
  // result -- it just raises a flag afterwards if the text came out wrong.
  const verifyCakeText = (dataUrl, expected) => {
    const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
    void (async () => {
      try {
        const el = await loadImgSafe(dataUrl);
        if (!el) return;
        const scale = Math.min(1, 768 / Math.max(el.naturalWidth, el.naturalHeight));
        const c = document.createElement('canvas');
        c.width = Math.max(1, Math.round(el.naturalWidth * scale));
        c.height = Math.max(1, Math.round(el.naturalHeight * scale));
        c.getContext('2d').drawImage(el, 0, 0, c.width, c.height);
        const got = await aiReadText(c.toDataURL('image/jpeg', 0.85));
        if (!got) return;                                    // couldn't read it — don't cry wolf
        if (norm(got) !== norm(expected)) {
          setMagicChatError(`The AI wrote "${got}" instead of "${expected}" — tap the name button to try again.`);
        }
      } catch { /* verification is best-effort */ }
    })();
  };

  const runNameEdit = async (prompt, appliedName) => {
    if (!effectiveImageId) return;
    const img = canvasImagesRef.current.find(i => i.id === effectiveImageId);
    if (!img) return;
    setMagicProcessing(true);
    setMagicChatStep('preparing');
    setMagicChatError('');
    let tempRef = null;
    try {
      const { imageBase64, w, h, origW, origH, tempRef: _tempRef, frame } = await prepareImageForKling(img, { suffix: 'name' });
      tempRef = _tempRef;
      setMagicChatStep('sending');
      const waitTimer = setTimeout(() => setMagicChatStep('waiting'), 5000);
      const data = await runSingleImageAiEdit({ imageBase64, prompt });
      clearTimeout(waitTimer);
      const normedName = await normalizeAiResult(data.imageBase64, origW, origH, frame, 'keep');
      const before = canvasImagesRef.current;
      const after = before.map(i => i.id === effectiveImageId ? { ...i, url: normedName } : i);
      setCanvasImages(after);
      canvasImagesRef.current = after;
      pushImageHistory(before, after);
      setMagicCakeNames(prev => ({ ...prev, [effectiveImageId]: appliedName }));
      setMagicChatStep('');
      verifyCakeText(normedName, appliedName);
    } catch (err) {
      setMagicChatStep('');
      setMagicChatError('Name update failed: ' + err.message);
    } finally {
      setMagicProcessing(false);
      if (tempRef) deleteObject(tempRef).catch(() => {});
    }
  };

  // Called when the user submits the Name popup -- replaces immediately if a name was
  // already added this session, otherwise waits for a tap on the cake to place it.
  const handleMagicNameApply = () => {
    const name = pendingNameValue.trim();
    if (!name || !effectiveImageId) return;
    setShowNamePopup(false);
    const prevName = magicCakeNames[effectiveImageId];
    if (prevName) {
      // Split the replace into two explicit steps and name the exact failure mode. Asking
      // to "remove X and write Y" in one breath gets satisfied by OVERLAYING Y on top of X,
      // which leaves a stray stroke behind — that's how "10" kept coming back as "110".
      runNameEdit(`The cake currently has the text "${prevName}" piped on it. STEP 1 — completely erase it: that area must end up as clean, smooth, unbroken icing with no letters, digits, outlines, shadows or leftover strokes of any kind. STEP 2 — on that now-blank area, pipe the new text. ${exactTextRule(name)} Critically, do NOT leave any part of "${prevName}" visible, and do NOT merge the old and new text (for example leaving a stray digit so "10" becomes "110"). Keep the same piping style, position, and everything else about the cake (design, colours, decorations, background) unchanged.`, name);
      setPendingNameValue('');
    } else {
      setNamePlacementPending(name);
      setMagicChatStep('placing-name');
    }
  };

  // Called from handleImageClick once the user taps the cake to place a pending name
  const commitNamePlacement = (fx, fy) => {
    const name = namePlacementPending;
    setNamePlacementPending(null);
    setPendingNameValue('');
    if (!name) return;
    const region = describePosition(fx, fy);
    runNameEdit(`Write "${name}" onto the cake in an elegant icing/piping style consistent with the cake's decoration, positioned at the ${region} of the cake. ${exactTextRule(name)} Keep everything else about the cake (design, colours, decorations, background, other tiers) exactly the same.`, name);
  };

  // Magic: apply brightness + contrast to selected image and commit
  // Slider commit. One handler for mouse, touch and pen: the sliders fire this from
  // onPointerUp (plus onKeyUp for keyboard nudges). They used to bind BOTH onMouseUp and
  // onTouchEnd, and a phone delivers touch events followed by synthesised mouse events —
  // so one release could bake the filter twice and push two undo entries, while on some
  // Android builds touchend arrived before the range input had its final value. Baking
  // from the stored original means the maths never stacks, but the double history entry
  // was real; the guard below makes a repeat commit of the same values a no-op regardless.
  const applyMagicFilter = async (brightness, contrast) => {
    const br = brightness ?? magicBrightness;
    const ct = contrast ?? magicContrast;
    if (!selectedImage) { showAlert('Please select an image first'); return; }
    const img = canvasImagesRef.current.find(i => i.id === selectedImage);
    if (!img) return;
    if (img._brightness === br && img._contrast === ct) {
      setMagicFilterPreview({ on: false, brightness: 0, contrast: 0 });
      return;
    }
    // When both values are zero, restore original image (undo all filter changes)
    if (br === 0 && ct === 0) {
      const origUrl = magicFilterOrigUrlRef.current;
      if (origUrl && origUrl !== img.url) {
        const before = canvasImagesRef.current;
        const after = before.map(i => i.id === selectedImage
          ? { ...i, url: origUrl, _filterOrigUrl: undefined, _brightness: undefined, _contrast: undefined }
          : i);
        setCanvasImages(after);
        canvasImagesRef.current = after;
        pushImageHistory(before, after);
      }
      setMagicFilterPreview({ on: false, brightness: 0, contrast: 0 });
      return;
    }
    // Always bake from the stored original URL so adjustments never stack
    const origUrl = img._filterOrigUrl || img.url;
    magicFilterOrigUrlRef.current = origUrl;
    const sourceUrl = origUrl;
    setMagicProcessing(true);
    try {
      const el = new Image();
      el.crossOrigin = 'anonymous';
      await new Promise((res, rej) => { el.onload = res; el.onerror = rej; el.src = sourceUrl; });
      const tmpCanvas = document.createElement('canvas');
      tmpCanvas.width = el.naturalWidth; tmpCanvas.height = el.naturalHeight;
      const ctx = tmpCanvas.getContext('2d');
      // -50->+50 range: map to CSS filter values (0.5->1.5 for brightness, 0.5->1.5 for contrast)
      const brF = 1 + br / 50;
      const ctF = 1 + ct / 50;
      ctx.filter = `brightness(${brF}) contrast(${ctF})`;
      ctx.drawImage(el, 0, 0);
      const result = tmpCanvas.toDataURL('image/png');
      // ── Do not let go of the preview until the baked pixels can be shown ─────────────
      // Swapping the src and clearing the filter happen in one commit, but the browser still
      // has to decode the new picture. For those frames the OLD pixels are on screen with the
      // filter already taken off — the picture flicks back to unadjusted and then jumps to the
      // new value. Decoding first makes the swap a single frame with nothing in between.
      try {
        const pre = new Image();
        pre.src = result;
        if (pre.decode) await pre.decode();
      } catch { /* a browser without decode(): the swap is merely as it was */ }
      const before = canvasImagesRef.current;
      // Store applied values and original URL on the image so re-opening the tool restores them
      const after = before.map(i => i.id === selectedImage
        ? { ...i, url: result, _filterOrigUrl: origUrl, _brightness: br, _contrast: ct }
        : i);
      setCanvasImages(after);
      canvasImagesRef.current = after;
      pushImageHistory(before, after);
      // Clear preview overlay — filter is now baked into the image URL
      setMagicFilterPreview({ on: false, brightness: 0, contrast: 0 });
    } catch (err) {
      showAlert('Filter failed: ' + err.message);
    } finally {
      setMagicProcessing(false);
    }
  };

  // Magic: AI chat edit via Kling
  const handleMagicChat = async () => {
    if (!magicChatInput.trim()) return;
    // Determine which images to process: manual selection (filtered to still-existing IDs),
    // falling back to auto Y-sorted canvas images (up to 2) when selection is empty or stale.
    const validSelected = magicChatImages.filter(id => canvasImagesRef.current.find(i => i.id === id));
    // ── The picture that is selected is the picture that gets edited ──────────────────────
    // There are two separate notions of "chosen" here: the chat's own picker (tap an image
    // while AI Edit is open and it gets a blue border — that is magicChatImages, and it holds
    // up to two for compositing), and the ordinary red-box selection everybody else uses.
    // Only the first was consulted. So the normal way round — tap a picture, open AI Edit,
    // type — left the picker empty and fell through to the guess below, which takes the two
    // TOPMOST pictures on the sheet and hands them to the two-image path as "edit this one,
    // use that one as a reference". Hence: it changed the first picture, and it made a
    // nonsense of the request, because the request was never about two pictures at all.
    //
    // The ordinary selection now comes in between. The guess is still there for a sheet where
    // nothing at all is chosen — two pictures and no selection is a reasonable compositing
    // request — but it no longer overrules something the user has actually pointed at.
    const _selId = (effectiveImageId && canvasImagesRef.current.some(i => i.id === effectiveImageId))
      ? effectiveImageId : null;
    const imgIds = validSelected.length > 0
      ? validSelected
      : _selId
      ? [_selId]
      : canvasImagesRef.current.slice().sort((a, b) => (a.y ?? 0) - (b.y ?? 0)).slice(0, 2).map(i => i.id);
    // Nothing to edit is no longer a dead end: with an empty canvas the prompt becomes the
    // whole request and the AI MAKES the picture instead of refusing to change one.
    if (imgIds.length === 0) {
      const promptText = magicChatInput.trim();
      setMagicPromptHistory(prev => [promptText, ...prev.filter(p => p !== promptText)].slice(0, 10));
      setMagicChatError('');
      setMagicChatLoading(true);
      setMagicChatStep('sending');
      let waitTimer = setTimeout(() => setMagicChatStep('waiting'), 5000);
      try {
        // Ask for a picture shaped like the sheet it is about to land on, so a portrait page
        // doesn't get a square image dropped into the middle of it.
        const ps = PRINT_SIZES[printSize];
        const aspect = ps ? `${Math.round(ps.width)}:${Math.round(ps.height)}` : '';
        const dataUrl = await aiGenerateImage(promptText, aspect);
        clearTimeout(waitTimer); waitTimer = null;
        setMagicChatStep('applying');
        const dims = await new Promise((resolve) => {
          const im = new Image();
          im.onload = () => resolve({ w: im.naturalWidth, h: im.naturalHeight });
          im.onerror = () => resolve({ w: 1024, h: 1024 });
          im.src = dataUrl;
        });
        // Nothing else is on the sheet, so the picture gets the whole sheet: fitted to the page
        // and centred, keeping its own proportions — it was landing at 40% of the width in the
        // middle of an otherwise empty page, which is a thumbnail of the thing that was asked
        // for. Since the request above already asks for the page's own shape, a fit normally
        // comes out as the full sheet; an AI that returns a different shape is letterboxed
        // rather than stretched.
        const _sheetAR = ps ? ps.width / ps.height : 21 / 29.7;
        const _imgAR = dims.w / Math.max(1, dims.h);
        // Fractions of the sheet, then the sizes themselves. Every image on the sheet states
        // its size the same way — width as centimetres of a 21cm-wide page, height of a 29.7cm
        // one — so a fraction of 1 is the full sheet whatever page size is in use.
        const _fw = _imgAR > _sheetAR ? 1 : _imgAR / _sheetAR;
        const _fh = _imgAR > _sheetAR ? _sheetAR / _imgAR : 1;
        const wCm = 21 * _fw, hCm = 29.7 * _fh;
        const before = canvasImagesRef.current;
        const newImage = {
          id: Date.now(),
          layerIndex: ++layerCounterRef.current,
          url: dataUrl,
          name: 'ai-image',
          size: '',
          x: Math.max(0, (1 - _fw) * 50),
          y: Math.max(0, (1 - _fh) * 50),
          width: wCm,
          height: hCm,
          source: 'ai',
          naturalWidth: dims.w,
          naturalHeight: dims.h,
        };
        const after = [...before, newImage];
        setCanvasImages(after); canvasImagesRef.current = after;
        pushImageHistory(before, after);
        setSelectedImage(newImage.id);
        markDirty();
        setMagicChatInput('');
      } catch (err) {
        setMagicChatError(err.message || 'Could not generate that image.');
      } finally {
        if (waitTimer) clearTimeout(waitTimer);
        setMagicChatLoading(false);
        setMagicChatStep('');
      }
      return;
    }
    // If selection had stale IDs, clear them so the UI doesn't show phantom selections
    if (magicChatImages.length > 0 && validSelected.length === 0) setMagicChatImages([]);
    const userMsg = { role: 'user', content: magicChatInput.trim() };
    // Add to history (dedup, max 10, most recent first)
    setMagicPromptHistory(prev => {
      const filtered = prev.filter(p => p !== userMsg.content);
      return [userMsg.content, ...filtered].slice(0, 10);
    });
    // Don't clear input -- keep prompt visible in textarea during processing
    setMagicChatError('');
    setMagicChatLoading(true);
    setMagicChatStep('preparing');


    let lastNewImageId = null;
    const overallErrors = [];

    // ── Instructions the model can read, not words it can paint ──────────────────────────
    // Appended to every user prompt to stop the model reframing or zooming the result. The
    // instruction itself is unchanged; the way it is WORDED is not, and that was the bug.
    //
    // The server wraps what arrives here in `Instruction: "…"`, so this whole paragraph is
    // handed to an image model as the literal thing being asked for. Written as it was — a
    // shouted, colon-terminated IMPORTANT: at the front — it had exactly the shape of a
    // caption, and on any request that involved writing on the picture (a name on a cake, a
    // label, a sign) the model duly wrote "Important" onto it. Plain lower-case prose reads
    // as guidance instead, and the shorter it is the less of it there is to leak: the server
    // already says "keep the composition, framing, aspect ratio and size of the original",
    // so the repetition that was here earned nothing.
    const FRAMING_SUFFIX = ' Keep the framing of the original exactly as it is — the same zoom, camera distance, crop and composition — with the subject at the same position and size within the frame. Fill anything removed seamlessly with the surrounding background, and change nothing else.';
    const FRAMING_NEG = 'zoomed in, cropped, reframed, different composition, different camera distance, panned, shifted subject, changed framing, distorted, deformed, low quality, blurry, watermark';
    // Appended in whole-sheet mode (see below): without this, the model treats the hand-drawn
    // sketch as loose creative inspiration and produces its own take on the prompt, largely
    // ignoring the sketch's actual lines -- e.g. "create a face out of it" over a landscape
    // photo with a rough hand-drawn face on top came back as the model's OWN generic
    // rock-formation face, not a face following the sketch's own eyes/nose/mouth placement.
    // Same rewording, same reason — see FRAMING_SUFFIX above.
    const WHOLE_SHEET_SUFFIX = ' The picture already combines a hand-drawn sketch with the photo or photos underneath it on the same sheet. Treat the sketch as a precise, literal guide rather than a loose suggestion: follow its exact outline, proportions and the placement of whatever it marks — eyes, nose, mouth, or any other lines — instead of inventing your own composition. Render that exact sketch, filled in with the colours, textures and content of the photos beneath it.';

    // Helper: resize the image to a model-friendly size and encode it. Also passes through
    // origW/origH — the image's TRUE size — so the result can be normalized back to it and
    // the edit never changes the photo's dimensions. Nothing is uploaded: the bytes go
    // straight into the request body.
    const prepareForAi = async (img) => {
      const { imageBase64, w, h, origW, origH, frame } = await prepareImageForKling(img);
      return { imageBase64, w, h, origW, origH, frame, tempStorageRef: null };
    };

    // Whole-sheet mode: there's a hand-drawn shape and/or ink stroke on the sheet, so the
    // request is about the WHOLE composition, not just whichever image(s) the auto-pick-up-
    // to-2 fallback above happened to grab. This has to be decided before imgIds.length is
    // even looked at -- gating it on "imgIds.length === 1" broke the moment the fallback
    // picked up 2 images (a photo plus, say, another photo) alongside a drawing: it fell
    // through to the 2-image edit/reference mode below, which -- like every other path here
    // before this fix -- only ever sends IMAGE pixels and has no idea shapes/ink exist. The
    // one case that still defers to an explicit 2-image pick is when the user manually chose
    // 2 images for AI Edit themselves (validSelected) -- that's a deliberate choice to keep.
    const hasDrawnContent = canvasShapesRef.current.length > 0 || drawingPaths.length > 0;
    if (hasDrawnContent && validSelected.length < 2) {
      // Flatten every image, shape and ink stroke on the sheet into one picture (like Save
      // does) and send THAT, so a prompt like "create a boy out of it" can actually see and
      // build on the drawing instead of only ever getting the isolated photo next to it.
      let waitingTimer = null;
      try {
        setMagicChatStep('preparing');
        const { imageBase64, w, h, frame: sheetFrame } = await prepareFullSheetForAi();
        setMagicChatStep('sending');
        waitingTimer = setTimeout(() => setMagicChatStep('waiting'), 5000);
        // layerId 'sheet': this edit is the whole page flattened, not any one layer, so its
        // repeats are counted against the sheet rather than against whatever happens to be
        // selected at the time.
        const data = await runSingleImageAiEdit({
          imageBase64, prompt: userMsg.content + WHOLE_SHEET_SUFFIX, sketchGuided: true,
          layerId: 'sheet', origW: w, origH: h, w: sheetFrame.pw, h: sheetFrame.ph,
        });
        clearTimeout(waitingTimer); waitingTimer = null;
        const normedSheet = await normalizeAiResult(data.imageBase64, w, h, sheetFrame, 'opaque');
        const newImage = {
          id: Date.now(),
          layerIndex: ++layerCounterRef.current,
          url: normedSheet,
          name: 'ai-edit',
          size: '',
          x: 0, y: 0, width: 21, height: 29.7,
          source: 'ai',
          naturalWidth: w,
          naturalHeight: h,
        };
        if (fromCustom) {
          // fromCustom expects exactly one final image on the canvas to hand back, same as
          // the single-image mode's fromCustom branch below -- replace everything with it.
          const imagesBefore = canvasImagesRef.current;
          const shapesBefore = canvasShapesRef.current;
          const pathsBefore = drawingPaths;
          const imagesAfter = [newImage];
          setCanvasImages(imagesAfter); canvasImagesRef.current = imagesAfter;
          setCanvasShapes([]); canvasShapesRef.current = [];
          setDrawingPaths([]);
          setSelectedShapeId(null);
          const newHistory = drawingHistoryRef.current.slice(0, historyIndexRef.current + 1);
          newHistory.push({ type: 'sheetReplace', imagesBefore, imagesAfter, shapesBefore, shapesAfter: [], pathsBefore, pathsAfter: [] });
          setDrawingHistory(newHistory);
          setHistoryIndex(newHistory.length - 1);
        } else {
          // Every other AI-edit path in this function adds its result as a NEW layer and
          // leaves the source(s) untouched -- this used to be the one exception, replacing
          // the sketch and photo(s) outright, which read as "the AI edit destroyed my
          // drawing" even though it had actually used it. Match the rest: the sketch and
          // photo(s) stay exactly as they were, and the sheet-sized result lands on top as
          // its own layer (movable/deletable like any other image, and still reveals what's
          // underneath once moved).
          const before = canvasImagesRef.current;
          const after = [...before, newImage];
          setCanvasImages(after); canvasImagesRef.current = after;
          pushImageHistory(before, after);
        }
        markDirty();
        lastNewImageId = newImage.id;
      } catch (err) {
        if (waitingTimer) clearTimeout(waitingTimer);
        overallErrors.push(err.message);
      }
    } else if (imgIds.length === 2) {
      // -- Two-image mode: sort by Y so topmost image = Edit, bottommost = Reference --
      // Routes to the Gemini multi-image endpoint -- Kling v3 doesn't reliably support
      // cross-image compositing (it either ignores the REF or just preserves the EDIT
      // unchanged). Gemini 2.5 Flash Image natively handles "take subject from image B,
      // put it into image A" prompts.
      const [editId, refId] = [...imgIds].sort((a, b) => {
        const ia = canvasImagesRef.current.find(i => i.id === a);
        const ib = canvasImagesRef.current.find(i => i.id === b);
        return (ia?.y ?? 0) - (ib?.y ?? 0);
      });
      const editImgObj = canvasImagesRef.current.find(i => i.id === editId);
      const refImgObj  = canvasImagesRef.current.find(i => i.id === refId);
      let tempRef0 = null, tempRef1 = null;
      let waitingTimer = null;
      try {
        setMagicChatStep('preparing');
        // Upload both images to temp storage so the backend can fetch them
        const [prepared0, prepared1] = await Promise.all([
          prepareForAi(editImgObj),
          prepareForAi(refImgObj),
        ]);
        tempRef0 = prepared0.tempStorageRef;
        tempRef1 = prepared1.tempStorageRef;

        setMagicChatStep('sending');
        waitingTimer = setTimeout(() => setMagicChatStep('waiting'), 5000);
        const data = {
          success: true,
          imageBase64: await aiEditImage(
            prepared0.imageBase64,
            userMsg.content + FRAMING_SUFFIX,
            prepared1.imageBase64,
            {
              // No three-strikes escalation on this path, deliberately. Kling v3 does not
              // composite two pictures — it drops one of them — so escalating here would
              // return an edit of the first image with the reference silently ignored and
              // report it as a success, which is worse than a fourth honest refusal. Gemini
              // is the only editor that does this job, which is why this branch exists at all.
              onWait: () => setMagicChatStep('waiting'),
            },
          ),
        };
        clearTimeout(waitingTimer); waitingTimer = null;
        setMagicChatStep('applying');

        // Normalize to original EDIT dimensions so it slots back on canvas cleanly
        // Fall back to the EDIT image's true size (origW/origH), never the clamped
        // upload size — that would shrink the result when naturalWidth isn't set.
        const editNatW = editImgObj?.naturalWidth  || prepared0.origW;
        const editNatH = editImgObj?.naturalHeight || prepared0.origH;
        const srcImg = canvasImagesRef.current.find(i => i.id === editId);
        const normedTwo = await withEmbeddedStamps(srcImg, await normalizeAiResult(data.imageBase64, editNatW, editNatH, prepared0.frame, 'key'));
        const newImage = {
          id: Date.now(),
          layerIndex: ++layerCounterRef.current,
          url: normedTwo,
          name: 'ai-edit',
          size: '',
          x: Math.min((srcImg?.x ?? 0) + 2, 79),
          y: Math.min((srcImg?.y ?? 0) + 2, 70),
          width: srcImg?.width ?? 10,
          height: srcImg?.height ?? 10,
          source: 'ai',
          naturalWidth: editNatW,
          naturalHeight: editNatH,
          embeddedStamps: srcImg?.embeddedStamps,
        };
        const before = canvasImagesRef.current;
        const after = [...before, newImage];
        setCanvasImages(after); canvasImagesRef.current = after;
        pushImageHistory(before, after);
        lastNewImageId = newImage.id;
      } catch (err) {
        if (waitingTimer) clearTimeout(waitingTimer);
        overallErrors.push(err.message);
      } finally {
        if (tempRef0) deleteObject(tempRef0).catch(() => {});
        if (tempRef1) deleteObject(tempRef1).catch(() => {});
      }
    } else {
      // -- Single-image mode (or fallback): edit each image independently ---------
      for (let idx = 0; idx < imgIds.length; idx++) {
        const imgId = imgIds[idx];
        const img = canvasImagesRef.current.find(i => i.id === imgId);
        if (!img) continue;

        let waitingTimer = null;
        let tempStorageRef = null;
        try {
          setMagicChatStep('preparing');
          const { imageBase64, w, h, origW, origH, frame, tempStorageRef: ref } = await prepareForAi(img);
          tempStorageRef = ref;

          setMagicChatStep('sending');
          waitingTimer = setTimeout(() => setMagicChatStep('waiting'), 5000);
          const data = await runSingleImageAiEdit({
            imageBase64, prompt: userMsg.content + FRAMING_SUFFIX, w, h, imageFidelity: 0.98, negativePrompt: FRAMING_NEG,
          });
          clearTimeout(waitingTimer); waitingTimer = null;
          // Normalize to sent dimensions -- preserves same pixel framing and freespace as original
          const normedChat = await withEmbeddedStamps(img, await normalizeAiResult(data.imageBase64, origW, origH, frame, 'key'));
          const before = canvasImagesRef.current;
          if (fromCustom) {
            // In fromCustom mode replace in-place -- only one image to return
            const after = before.map(i => i.id === imgId ? { ...i, url: normedChat } : i);
            setCanvasImages(after); canvasImagesRef.current = after;
            pushImageHistory(before, after);
          } else {
            const srcImg = canvasImagesRef.current.find(i => i.id === imgId);
            const OFFSET = 2 + idx * 2;
            const newImage = {
              id: Date.now() + idx,
              layerIndex: ++layerCounterRef.current,
              url: normedChat,
              name: 'ai-edit',
              size: '',
              x: Math.min(((srcImg?.x ?? 0) + OFFSET), 79),
              y: Math.min(((srcImg?.y ?? 0) + OFFSET), 70),
              width: srcImg?.width ?? 10,
              height: srcImg?.height ?? 10,
              source: 'ai',
              naturalWidth: origW,
              naturalHeight: origH,
              embeddedStamps: srcImg?.embeddedStamps,
            };
            const after = [...before, newImage];
            setCanvasImages(after); canvasImagesRef.current = after;
            pushImageHistory(before, after);
            lastNewImageId = newImage.id;
          }
        } catch (err) {
          if (waitingTimer) { clearTimeout(waitingTimer); waitingTimer = null; }
          overallErrors.push(`Image ${idx + 1}: ${err.message}`);
        } finally {
          if (tempStorageRef) deleteObject(tempStorageRef).catch(() => {});
        }
      }
    }

    if (lastNewImageId) setSelectedImage(lastNewImageId);
    setMagicChatInput('');
    setMagicChatStep('');
    if (overallErrors.length > 0) {
      setMagicChatError(overallErrors.join(' . '));
    }
    setMagicChatLoading(false);
  };

  // Save function
  const handleSave = async () => {
    if (canvasImages.length === 0 && drawingPaths.length === 0 && canvasShapes.length === 0) {
      setSaveMessage({ text: 'Please add images or drawings first.', ok: false });
      return;
    }

    // Saving to the Cloud needs a real account, exactly as the + menu's Cloud options do. This
    // path had no such gate, so a signed-out save went quietly into the anonymous per-device
    // folder and looked lost the moment anyone signed in. Ask, then resume the save itself.
    if ((saveType === 'bucket' || saveType === 'bucket-image') && isCloudAuthEnabled()) {
      await cloudAuthReady();
      if (getAuth().currentUser?.isAnonymous !== false) {
        pendingAuthActionRef.current = 'save-bucket';
        setAuthMode('signin');
        setAuthError(null);
        setAuthNotice(null);
        setShowAuthModal(true);
        return;
      }
    }

    setIsSaving(true);
    setSaveProgress(0);

    try {
      // "Save to Cloud (Image)" with a photo tapped in Selfie Mode means THAT photo, not the
      // sheet it happens to be sitting on — see exportSelection.
      const exportSel = exportSelection({ lonePhoto: saveType === 'bucket-image' });
      const imagesToRender = exportSel.images;
      const pathsToRender  = exportSel.paths;
      const shapesToRender = exportSel.shapes;
      const single   = exportSel.image;
      const selShape = exportSel.shape;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // ── What gets saved, and at what size ─────────────────────────────────────
      // Rule: an image is never resampled below its own pixels.
      //   • One image selected → that image alone, at its NATIVE resolution. If it is untouched
      //     (no rotation, no circle crop) the original file bytes are saved as they are.
      //   • One drawn object selected → that object alone (its box, 300 dpi of its cm size).
      //   • Nothing selected → the WHOLE sheet, dense enough that the sharpest image on it keeps
      //     its native pixels (never below 300 dpi, capped at 600 dpi).
      const PS = PRINT_SIZES[printSize];
      const loadedImages = await Promise.all(imagesToRender.map(i => loadImgForOutput(i.url)));
      let nativeBlob = null;
      // The photo's own bytes, untouched — but only when the photo is the WHOLE of what is
      // being saved. A face with a cap and a moustache on it has to be drawn, or this shortcut
      // hands back the bare face and the props are quietly dropped.
      const _aloneInFrame = imagesToRender.length === 1 && !pathsToRender.length && !shapesToRender.length;
      if (single && _aloneInFrame && !single.rotation && single.cropShape !== 'circle') {
        try { nativeBlob = await fetch(single.url).then(r => r.blob()); } catch { nativeBlob = null; }
        // Only keep the original bytes when that format can actually STATE its size. PNG and
        // JPEG can; WebP, GIF and BMP either cannot or are not honoured by the things people
        // open pictures with. For those the studio's own render is used instead.
        if (nativeBlob && !/^image\/(png|jpeg)$/.test(nativeBlob.type || '')) nativeBlob = null;
      }
      let minX, minY, maxX, maxY, widthInPx, heightInPx;
      if (single) {
        const el = loadedImages[0];
        const natW = el?.naturalWidth || 0, natH = el?.naturalHeight || 0;
        minX = single.x; minY = single.y;
        maxX = single.x + (single.width / 21) * 100;
        maxY = single.y + (single.height / 29.7) * 100;
        // Every pixel the picture has, and not one resampled — the edited SIZE travels with the
        // file rather than being baked into its pixel count (see withPhysicalSize). Shrinking a
        // photo on the sheet and re-rendering it smaller was the only way to make the pixel
        // count match the edit, and that threw detail away for nothing: the file already states
        // it is 15 x 10 cm, so it comes back at 15 x 10 cm with all its detail intact.
        widthInPx  = natW || Math.round(((single.width  / 21)   * PS.width)  * (300 / 2.54));
        heightInPx = natH || Math.round(((single.height / 29.7) * PS.height) * (300 / 2.54));
      } else if (selShape) {
        ({ minX, minY, maxX, maxY } = shapeExportBounds(selShape));
        widthInPx  = Math.round(((maxX - minX) / 100) * PS.width  * (300 / 2.54));
        heightInPx = Math.round(((maxY - minY) / 100) * PS.height * (300 / 2.54));
      } else {
        minX = 0; minY = 0; maxX = 100; maxY = 100;
        // expand if any image extends past the sheet edge (resize overshoot / rounding)
        imagesToRender.forEach(img => {
          maxX = Math.max(maxX, img.x + (img.width  / 21   * 100));
          maxY = Math.max(maxY, img.y + (img.height / 29.7 * 100));
        });
        let pxPerCm = 300 / 2.54;
        imagesToRender.forEach((img, i) => {
          const el = loadedImages[i]; if (!el?.naturalWidth) return;
          const cmW = (img.width / 21) * PS.width;
          if (cmW > 0) pxPerCm = Math.max(pxPerCm, el.naturalWidth / cmW);
        });
        pxPerCm = Math.min(pxPerCm, 600 / 2.54);
        widthInPx  = Math.round((maxX / 100) * PS.width  * pxPerCm);
        heightInPx = Math.round((maxY / 100) * PS.height * pxPerCm);
      }
      const contentWidth  = maxX - minX;
      const contentHeight = maxY - minY;

      canvas.width  = widthInPx;
      canvas.height = heightInPx;

      // High-quality image smoothing (set after resize -- resize resets context state)
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Fill background for full-canvas saves using the canvas background colour.
      // If no background is set (transparent), leave the canvas transparent so
      // the exported PNG has no background.
      if (!single && !selShape && ((canvasBackground && canvasBackground !== 'transparent') || canvasBgFx)) {
        ctx.fillStyle = (canvasBackground && canvasBackground !== 'transparent') ? canvasBackground : '#ffffff';
        ctx.fillRect(0, 0, widthInPx, heightInPx);
        // The shading or pattern on top of it, drawn the way the screen draws it.
        SHEET_FX[canvasBgFx]?.paint(ctx, widthInPx, heightInPx, isDarkSheet(canvasBackground));
      }

      setSaveProgress(10);

      // Draw images -- use loadImgSafe so expired/cross-origin Firebase URLs
      // are fetched via blob (avoids canvas taint and silent draw failures)
      // One pile, screen order — see layersInOrder. A figure below a picture used to be
      // repainted over the top of it because every figure was drawn after every picture.
      for (const _layer of layersInOrder(imagesToRender, shapesToRender)) {
        if (_layer.kind === 'shape') {
          drawShapesOnCanvas(ctx, [_layer.item], widthInPx, heightInPx, minX, minY, contentWidth, contentHeight);
          continue;
        }
        const img = _layer.item;
        const i = imagesToRender.indexOf(img);
        const image = loadedImages[i];
        if (image) {
            const imgXPx      = ((img.x - minX) / contentWidth)  * widthInPx;
            const imgYPx      = ((img.y - minY) / contentHeight) * heightInPx;
            const imgWidthPx  = (img.width  / 21)   * (widthInPx  / contentWidth)  * 100;
            const imgHeightPx = (img.height / 29.7) * (heightInPx / contentHeight) * 100;

            const rot = ((img.rotation || 0) * Math.PI) / 180;
            if (rot) {
              const cx = imgXPx + imgWidthPx / 2;
              const cy = imgYPx + imgHeightPx / 2;
              ctx.save();
              ctx.translate(cx, cy);
              ctx.rotate(rot);
              if (img.cropShape === 'circle') {
                ctx.beginPath();
                ctx.ellipse(0, 0, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2);
                ctx.clip();
              }
              drawImageHQ(ctx, image, -imgWidthPx / 2, -imgHeightPx / 2, imgWidthPx, imgHeightPx);
              ctx.restore();
            } else if (img.cropShape === 'circle') {
              ctx.save();
              ctx.beginPath();
              ctx.ellipse(imgXPx + imgWidthPx / 2, imgYPx + imgHeightPx / 2, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2);
              ctx.clip();
              drawImageHQ(ctx, image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
              ctx.restore();
            } else {
              drawImageHQ(ctx, image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
            }
            setSaveProgress(10 + ((i + 1) / imagesToRender.length) * 70);
        } else {
            console.error('Failed to load image for saving:', img.url);
            setSaveProgress(10 + ((i + 1) / imagesToRender.length) * 70);
        }
      }

      // Drawings last — they were already above every picture, and are now above the figures too.
      drawDrawingLayer(ctx, pathsToRender, widthInPx, heightInPx, minX, minY, contentWidth, contentHeight);

      setSaveProgress(85);

      // A selected, untouched image goes out as its own original bytes (same pixels, same format).
      const rawBlob = nativeBlob || await new Promise(res => canvas.toBlob(res, 'image/png', 1.0));
      if (!rawBlob) throw new Error('Failed to create image');
      // What it measured on the sheet, in centimetres — see declaredSizeCm for why a lone
      // figure declares its geometric size rather than the box it was rendered from.
      const { w: outCmW, h: outCmH } = declaredSizeCm(selShape, { minX, minY, maxX, maxY }, PS);
      // Record that size in the file. Pixels are untouched -- this only tells whatever opens it
      // how big they are meant to be, which is what makes a 5cm object open and print as 5cm
      // instead of at the viewer's guess of 96 dpi.
      const outBlob = await withPhysicalSize(rawBlob, outCmW, outCmH);
      const outExt = outBlob.type === 'image/jpeg' ? 'jpg' : outBlob.type === 'image/webp' ? 'webp' : 'png';

      if (saveType === 'file') {
        const finalFileName = fileName.trim() || DEFAULT_IMAGE_NAME;
        // First save of all: ask in our own words before the browser's folder chooser appears.
        // The chooser itself belongs to the browser and cannot be restyled or bypassed — but it
        // no longer arrives unannounced in the middle of a save, and it is only ever reached by
        // pressing a button in this dialog, which is also what gives it the gesture it needs.
        let allowFolder = true;
        if (canPickFolder() && !(await getStudioFolder({ prompt: false }))) {
          allowFolder = await askForFolder();
        }
        const saved = await saveImageFile(outBlob, finalFileName, outExt, { allowFolder });

        setSaveProgress(100);
        setTimeout(() => {
          setIsSaving(false);
          setSaveProgress(0);
          setSaveMessage({ text: saved.where === 'folder' ? `Saved to ${saved.folder || FOLDER_NAME} as ${saved.name}` : `Saved as ${saved.name}`, ok: true });
          setPendingNavPath(null);
          isSavedRef.current = true;
          // Reset per-image filter metadata now that the final image is saved
          setCanvasImages(prev => prev.map(i => ({ ...i, _filterOrigUrl: undefined, _brightness: undefined, _contrast: undefined })));
          setMagicBrightness(0);
          setMagicContrast(0);
        }, 500);
      } else {
        try {
          const blob = outBlob;
          
          // For bucket save, use the filename from input
          const baseFileName = fileName.trim() || DEFAULT_IMAGE_NAME;
          const cleanFileName = baseFileName.replace(/\s+/g, '_');
          
          await cloudAuthReady();      // same reason as the other two paths
          const _saveUser = getAuth().currentUser;
          // ── Everyone files under their own account, administrators included ─────────────
          // This used to depend on userRole, a prop nothing ever passes, so it held its default
          // — 'Admin' — for every visitor. Every save was therefore addressed to the bare
          // istudio/ folder rather than istudio/<account>/, which is the one place the bucket's
          // rules refuse: signed in or not, admin or not, nothing could reach the Cloud.
          // Being an administrator widens what can be BROWSED (see loadBucketImages); it has
          // never meant keeping your own work somewhere else.
          const _saveFolder = `istudio/${_saveUser?.uid}`;

          // Check if file exists and append number if needed
          let counter = 1;
          let bucketFileName = `${_saveFolder}/${cleanFileName}.${outExt}`;
          
          // Get existing files to check for duplicates
          const { items } = await listObjectsInFolder(_saveFolder, getEnvStorageBucket());
          const existingFiles = items.map(item => item.name);
          
          while (existingFiles.includes(bucketFileName)) {
            bucketFileName = `${_saveFolder}/${cleanFileName}_${counter}.${outExt}`;
            counter++;
          }
          
          const file = new File([blob], `${cleanFileName}.${outExt}`, { type: blob.type || 'image/png' });
          
          await uploadToStorage(
            bucketFileName,
            file,
            file.type,
            getEnvStorageBucket()
          );
          // The whole sheet, saved with nothing picked out, is a piece of WORK rather than a
          // finished picture — so the sheet itself goes up beside the PNG and opening the item
          // again gives every object back, separately editable. A single object saved on its
          // own is a picture and stays one, and so is a save the person asked for as a picture:
          // "Save to Cloud (Image)" means the flat thing, effects and props already in the
          // pixels, with nothing behind it to take apart again.
          if (!single && !selShape && saveType !== 'bucket-image') {
            try {
              const _proj = await buildProject({
                printSize,
                canvasBackground,
                canvasBgFx,
                images: canvasImagesRef.current,
                shapes: canvasShapesRef.current,
                paths: drawingPathsRef.current,
              });
              const _projPath = projectPathFor(bucketFileName);
              await uploadToStorage(
                _projPath,
                projectFile(_proj, _projPath.split('/').pop()),
                'application/json',
                getEnvStorageBucket(),
              );
            } catch (e) {
              // The picture is saved either way; only the "open it and carry on" part is lost.
              console.warn('Cloud save: the sheet itself could not be stored.', e?.message || e);
            }
          }
          // Bust cache so modal shows newly uploaded file
          _bucketImagesCache = null;
          
          setSaveProgress(100);
          setTimeout(() => {
            setIsSaving(false);
            setSaveProgress(0);
            // Say where it actually went. A library that lives on the device looks exactly like
            // a cloud one until you sign in somewhere else and find it empty.
            setSaveMessage({ text: usingCloud() ? 'Image saved to Cloud' : 'Saved to this device', ok: true });
            setPendingNavPath(null);
            isSavedRef.current = true;
            // Reset per-image filter metadata now that the final image is saved
            setCanvasImages(prev => prev.map(i => ({ ...i, _filterOrigUrl: undefined, _brightness: undefined, _contrast: undefined })));
            setMagicBrightness(0);
            setMagicContrast(0);
          }, 500);
        } catch (error) {
          console.error('Error saving to bucket:', error);
          setIsSaving(false);
          setSaveProgress(0);
          setSaveMessage({ text: `Failed to save to Cloud: ${error.message}`, ok: false });
        }
      }
      
    } catch (error) {
      console.error('Save error:', error);
      setIsSaving(false);
      setSaveProgress(0);
      setSaveMessage({ text: `Failed to save: ${error.message}`, ok: false });
    }
  };

  // Print functionality
  // Switch paper size/orientation, keeping every element the same PHYSICAL size (cm).
  const setPrintSizeTo = (nextKey) => {
    const prevKey = printSize;
    if (!PRINT_SIZES[nextKey] || nextKey === prevKey) return;
    const prevW = PRINT_SIZES[prevKey].width;
    const prevH = PRINT_SIZES[prevKey].height;
    const newW  = PRINT_SIZES[nextKey].width;
    const newH  = PRINT_SIZES[nextKey].height;

    // Scale factor: preserves the physical cm size of each element in the new canvas.
    // Images store width in "21-cm units" and height in "29.7-cm units" (hardcoded references).
    // Multiplying by prevDim/newDim keeps (img.width/21)*canvasCSSWidth the same physical size.
    const sx = prevW / newW;
    const sy = prevH / newH;

    setPrintSize(nextKey);

    if (sx !== 1 || sy !== 1) {
      setCanvasImages(prev => prev.map(img => ({
        ...img,
        x:      img.x      * sx,
        y:      img.y      * sy,
        width:  img.width  * sx,
        height: img.height * sy,
      })));

      setCanvasShapes(prev => prev.map(s => ({
        ...s,
        x1: s.x1 * sx,
        y1: s.y1 * sy,
        x2: s.x2 * sx,
        y2: s.y2 * sy,
      })));
    }
  };
  const cyclePrintSize = () => {
    const idx = PRINT_SIZE_ORDER.indexOf(printSize);
    setPrintSizeTo(PRINT_SIZE_ORDER[(idx + 1) % PRINT_SIZE_ORDER.length]);
  };

  /**
   * The sheet as a PDF: one page, exactly the size of the paper it was made for.
   *
   * A picture has no size. A PNG is a grid of pixels and nothing else, so every print path it
   * passes through — the share sheet on a phone, a gallery app, a printer driver — has to decide
   * for itself how big it is meant to be, and they all decide the same way: make it fill the
   * page. That is why a sheet sent from the phone came out close but not right.
   *
   * A PDF page carries its size inside the file. A4 is 21 x 29.7cm in the document itself, so
   * there is nothing left to guess: the picture is printed at the size it was made.
   *
   * Returns null if anything at all goes wrong, so the caller can fall back to the picture.
   */
  const sheetAsPdf = async (sheet, ps) => {
    try {
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF({
        unit: 'cm',
        format: [ps.width, ps.height],
        orientation: ps.orient === 'landscape' ? 'landscape' : 'portrait',
        compress: true,
      });
      pdf.addImage(sheet.toDataURL('image/png'), 'PNG', 0, 0, ps.width, ps.height, undefined, 'FAST');
      return pdf.output('blob');
    } catch (err) {
      console.error('Could not build the PDF; sending the picture instead.', err);
      return null;
    }
  };

  // ── Is there anything to put on paper? ────────────────────────────────────────────────
  // A sheet is not empty because it has no pictures on it. Choose a colour or a shading in
  // Page layout and the sheet itself is the artwork — a coloured card, a ruled page, a tinted
  // backdrop to print and write on — and being told to "add images or drawings first" is the
  // app refusing to print something that is plainly there on the screen in front of you.
  const sheetHasSomething = () =>
    canvasImages.length > 0 || drawingPaths.length > 0 || canvasShapes.length > 0
    || (canvasBackground && canvasBackground !== 'transparent') || !!canvasBgFx;

  const handlePrint = async () => {
    if (!sheetHasSomething()) {
      showAlert('Please add images or drawings to the canvas first.');
      return;
    }

    // Use native Share on Capacitor apps; hidden iframe for all browsers.
    // iframe.contentWindow.print() runs in the same trusted context as the parent
    // document -- Brave (and all Chromium browsers) apply @page sizing correctly
    // without the extra "fit to page" scale they impose on popup windows.
    const isNative = Capacitor.isNativePlatform();

    setIsSaving(true);
    
    try {
      // What gets printed: the selected image alone, else the selected drawn object alone,
      // else the whole sheet. Whatever is printed keeps its exact place and physical size on
      // the chosen paper — nothing is rescaled to fill the page.
      const exportSel = exportSelection();
      const imagesToRender = exportSel.images;
      const pathsToRender  = exportSel.paths;
      const shapesToRender = exportSel.shapes;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      // dpi=350: render at slightly above standard print resolution. Chromium's print engine
      // maps CSS cm units to physical dimensions at the printer DPI; having extra source pixels
      // ensures no upscaling artefacts even if the browser's internal rasterisation step uses a
      // 1.1–1.2x scaling factor. 350 DPI on A4 = 2894x4093 px.
      //
      // Not higher. This number is the size of the picture that has to travel into the print
      // document as text, and the sheet that would not come out of the printer was carrying
      // about two and a half times as many pixels as this. This is the density the same sheet
      // in PCAKE has always printed at.
      const loadedImages = await Promise.all(imagesToRender.map(i => loadImgForOutput(i.url)));
      const dpi = 350;
      const widthInPx  = Math.round(PRINT_SIZES[printSize].width  * (dpi / 2.54));
      const heightInPx = Math.round(PRINT_SIZES[printSize].height * (dpi / 2.54));
      
      canvas.width = widthInPx;
      canvas.height = heightInPx;
      
      // Re-apply smoothing after canvas resize (resize resets context state)
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      // ── The sheet you chose, not a white one ────────────────────────────────────────
      // The Page layout sheet says outright that what is chosen there is what Save and Print
      // put behind the drawing. Save honoured it; this painted plain white and threw away the
      // colour and the shading every time. Paper is opaque, so white goes down first and the
      // chosen colour over it — that way a half-transparent sheet colour reads the same on
      // paper as it does on screen instead of turning muddy against nothing.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, widthInPx, heightInPx);
      if (canvasBackground && canvasBackground !== 'transparent') {
        ctx.fillStyle = canvasBackground;
        ctx.fillRect(0, 0, widthInPx, heightInPx);
      }
      SHEET_FX[canvasBgFx]?.paint(ctx, widthInPx, heightInPx, isDarkSheet(canvasBackground));

      layersInOrder(imagesToRender, shapesToRender).forEach((_layer) => {
        if (_layer.kind === 'shape') {
          drawShapesOnCanvas(ctx, [_layer.item], widthInPx, heightInPx);
          return;
        }
        const img = _layer.item;
        const image = loadedImages[imagesToRender.indexOf(img)];
        if (!image) {
          console.error('Failed to load image for printing:', img.url);
          return;
        }

        // img.width/height are in A4P-space cm (denominator always 21/29.7),
        // expressed as the same fraction of the print canvas pixels -- just like the CSS does.
        const imgWidthPx  = (img.width  / 21)   * widthInPx;
        const imgHeightPx = (img.height / 29.7) * heightInPx;
        const imgXPx = (img.x / 100) * widthInPx;
        const imgYPx = (img.y / 100) * heightInPx;

        const rot = ((img.rotation || 0) * Math.PI) / 180;
        if (rot) {
          const cx = imgXPx + imgWidthPx / 2;
          const cy = imgYPx + imgHeightPx / 2;
          ctx.save();
          ctx.translate(cx, cy);
          ctx.rotate(rot);
          if (img.cropShape === 'circle') {
            ctx.beginPath();
            ctx.ellipse(0, 0, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2);
            ctx.clip();
          }
          drawImageHQ(ctx, image, -imgWidthPx / 2, -imgHeightPx / 2, imgWidthPx, imgHeightPx);
          ctx.restore();
        } else if (img.cropShape === 'circle') {
          ctx.save();
          ctx.beginPath();
          ctx.ellipse(imgXPx + imgWidthPx / 2, imgYPx + imgHeightPx / 2, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2);
          ctx.clip();
          drawImageHQ(ctx, image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
          ctx.restore();
        } else {
          drawImageHQ(ctx, image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
        }
      });
      
      // Draw drawings — through a separate layer so fills only see the drawing
      drawDrawingLayer(ctx, pathsToRender, widthInPx, heightInPx);

      // Build a custom print-preview page: image at top, print bar at bottom
      const ps = PRINT_SIZES[printSize];
      const sizeLabel = `${ps.label} ${ps.orient === 'portrait' ? 'Portrait' : 'Landscape'} &middot; ${ps.width} &times; ${ps.height} cm`;

      if (isNative) {
        // --- Native Capacitor path: use Share plugin (Android / iOS) ---
        // The OS share sheet includes a native Print option on both platforms.
        const fileName = `print-${ps.label}-${ps.orient}.png`;
        const dataUri = canvas.toDataURL('image/png');
        await Share.share({
          title: 'Print Photo',
          url: dataUri,
          dialogTitle: 'Print or share your photo',
        });
      } else {
        // Detect a phone/tablet web browser. Mobile browsers cannot reliably print
        // an iframe (Chrome Mobile, Safari iOS, Samsung Internet all either ignore
        // iframe.print() or print the parent viewport instead). For these we use
        // the Web Share API with a real File object — the OS share sheet exposes
        // a native Print option on both Android and iOS that prints the actual file
        // (not the visible viewport). If Web Share isn't available, we fall back to
        // downloading the PNG so the user can print it from their gallery / Photos.
        const ua = navigator.userAgent || '';
        const isMobileWeb = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)
          || (typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1
              && /Macintosh/i.test(ua)); // iPad on iOS 13+ reports as Macintosh

        if (isMobileWeb) {
          // A PDF, not a picture, and for one reason: it is the only one of the two that knows
          // how big it is. See sheetAsPdf. If it cannot be built, the picture still goes.
          const _pdf = await sheetAsPdf(canvas, ps);
          const blob = _pdf || await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
          const _mime = _pdf ? 'application/pdf' : 'image/png';
          const fileName = `print-${ps.label}-${ps.orient}-${Date.now()}.${_pdf ? 'pdf' : 'png'}`;
          if (!blob) {
            showAlert('Failed to prepare image for printing.');
            setIsSaving(false);
            setActiveTool(null);
            return;
          }

          // Try Web Share API with files first (native share sheet → Print)
          try {
            if (navigator.canShare && typeof File !== 'undefined') {
              const file = new File([blob], fileName, { type: _mime });
              if (navigator.canShare({ files: [file] })) {
                await navigator.share({
                  files: [file],
                  title: 'Print Photo',
                  text: 'Tap Print from the share sheet to print this image.',
                });
                setIsSaving(false);
                setActiveTool(null);
                return;
              }
            }
          } catch (e) {
            // User cancelled the share sheet, or the OS rejected. Fall through to download.
            if (e && e.name === 'AbortError') {
              setIsSaving(false);
              setActiveTool(null);
              return;
            }
          }

          // Fallback: trigger a download so the user can print from Photos / Files
          const blobUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
          showAlert(_pdf
            ? 'Saved to your downloads. Open it from Files and tap Print.'
            : 'Image saved to your downloads. Open it from your gallery or Files app and tap Print.');
          setIsSaving(false);
          setActiveTool(null);
          return;
        }

        // --- Desktop browser path: hidden iframe with isolated document ---
        // The iframe has its own <html><body> with ONLY the image, so the print engine sees no
        // React app DOM, no global stylesheets, no responsive media queries to fight against.
        // Works on Chrome / Brave / Edge / Firefox.
        //
        // ── This is deliberately the plainest document that can carry a sheet ────────────
        // One picture of the whole sheet, as a data: URL, in a page whose size is stated in
        // centimetres. It is the same document PCAKE prints, unchanged, and it has always come
        // out of the printer.
        //
        // It was recently made cleverer: a separate <img> per photograph at its own resolution,
        // a transparent overlay for the ink, blob: URLs instead of base64, and a density up to
        // 600 dpi. On screen and in the print preview that was better in every way. On paper it
        // produced a blank sheet carrying only the browser's own header line. None of it is
        // worth that, so none of it is here.
        //
        // Two details are load-bearing and should not be "improved" without a sheet of paper to
        // prove it:
        //   • data: URL, not blob:. The picture is IN the document. A blob: URL is a pointer
        //     into the parent page, and the printer needs it after the dialog has closed.
        //   • @page size in centimetres, not the name "a4". The name leaves the browser free to
        //     fit the sheet to what the printer can reach, which is the few per cent that went
        //     missing from an 18.2cm picture.
        const dataUrl = canvas.toDataURL('image/png');

        const iframe = document.createElement('iframe');
        // Off-screen but still rendered (display:none breaks contentDocument paint
        // on some browsers before print() is called).
        iframe.style.cssText = 'position:fixed;right:-99999px;bottom:-99999px;width:1px;height:1px;border:0;visibility:hidden;';
        iframe.setAttribute('aria-hidden', 'true');
        document.body.appendChild(iframe);

        const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Print</title>
<style>
  @page { size: ${ps.width}cm ${ps.height}cm; margin: 0; }
  html, body {
    margin: 0; padding: 0;
    width: ${ps.width}cm; height: ${ps.height}cm;
    background: white;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    color-adjust: exact;
  }
  img {
    display: block;
    width: ${ps.width}cm;
    height: ${ps.height}cm;
    margin: 0; padding: 0;
    image-rendering: high-quality;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    color-adjust: exact;
  }
</style>
</head>
<body>
<img id="p" src="${dataUrl}" alt="">
</body>
</html>`;

        let cleaned = false;
        const cleanup = () => {
          if (cleaned) return;
          cleaned = true;
          clearTimeout(fallbackTimer);
          try { document.body.removeChild(iframe); } catch (_) {}
          if (iframe.contentWindow) iframe.contentWindow.onafterprint = null;
          setIsSaving(false);
          setActiveTool(null);
        };
        // 10-min safety fallback if onafterprint never fires (rare on some mobile browsers)
        const fallbackTimer = setTimeout(cleanup, 10 * 60 * 1000);

        // Write the document into the iframe and trigger print after the image loads.
        //
        // ── Once, and only for the document with the sheet in it ────────────────────────
        // An iframe loads twice. Putting one on the page loads its own empty document first,
        // and only then does the sheet written into srcdoc load — so the load handler runs
        // twice, and printing on the first run prints the empty document: a sheet of paper with
        // nothing on it but the browser's header line. Whichever of the two jobs the printer
        // took, that is what came out — the empty one when the sheet was slow to load, and
        // lately both of them, one after the other, which is the second copy.
        //
        // So: nothing is printed until the document actually contains the sheet, and the print
        // itself happens once.
        let printed = false;
        const triggerPrint = () => {
          if (printed) return;
          printed = true;
          try {
            const win = iframe.contentWindow;
            if (!win) return;
            // onafterprint inside the iframe fires when the user dismisses the dialog
            win.onafterprint = cleanup;
            win.focus();
            win.print();
          } catch (err) {
            console.error('iframe print failed:', err);
            cleanup();
          }
        };

        iframe.onload = () => {
          // Wait for the <img> inside the iframe to finish loading before print().
          // Mobile browsers especially will print whatever is rasterised at the moment
          // print() is called -- if the image isn't loaded yet, the page prints blank.
          const doc = iframe.contentDocument;
          const img = doc && doc.getElementById('p');
          if (!img) return;   // the iframe's own empty document: not the sheet, nothing to print
          if (!img.complete) {
            img.onload  = () => setTimeout(triggerPrint, 100);
            img.onerror = () => triggerPrint();
          } else {
            setTimeout(triggerPrint, 100);
          }
        };

        // Inject the document. Using srcdoc (modern, isolates better than doc.write)
        // with a fallback to document.open/write for older mobile browsers.
        if ('srcdoc' in iframe) {
          iframe.srcdoc = html;
        } else {
          const doc = iframe.contentDocument;
          doc.open(); doc.write(html); doc.close();
          // No load event for doc.write path -- trigger directly after DOM ready
          setTimeout(() => iframe.onload(), 100);
        }

        return; // setIsSaving handled in cleanup
      }
      setIsSaving(false);
      setActiveTool(null);

    } catch (error) {
      console.error('Print preparation error:', error);
      showAlert('Failed to prepare image for printing. Please try again.');
      setIsSaving(false);
      setActiveTool(null);
    }
  };

  // Share functionality
  const handleShare = async (platform = 'download') => {
    if (!sheetHasSomething()) {
      showAlert('Please add images or drawings to the canvas first.');
      return;
    }
    
    setIsSaving(true);
    setSaveProgress(0);

    try {
      const exportSel = exportSelection();
      const imagesToRender = exportSel.images;
      const pathsToRender  = exportSel.paths;
      const shapesToRender = exportSel.shapes;
      const selShape = exportSel.shape;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      // ── What gets exported, and at what size ────────────────────────────────
      // Rule: an image is never resampled below its own pixels.
      //   • One image selected → that image alone, at its NATIVE resolution. If it is
      //     untouched (no rotation, no circle crop) the original file bytes are shared as
      //     they are — same pixels, same format, nothing re-encoded.
      //   • Nothing selected → the WHOLE sheet, at a pixel density high enough that the
      //     sharpest image on it keeps its native pixels (never below 300 dpi).
      // It used to export the content's bounding box at 300 dpi of its cm size — a 3000 px
      // photo went out at 1116 px, and "the canvas" was really just the box around the content.
      const PS = PRINT_SIZES[printSize];
      const loadedImages = await Promise.all(imagesToRender.map(i => loadImgForOutput(i.url)));
      const single = exportSel.image;

      let nativeBlob = null;
      if (single && !single.rotation && single.cropShape !== 'circle') {
        try { nativeBlob = await fetch(single.url).then(r => r.blob()); } catch { nativeBlob = null; }
        // Only keep the original bytes when that format can actually STATE its size. PNG and
        // JPEG can; WebP, GIF and BMP either cannot or are not honoured by the things people
        // open pictures with. For those the studio's own render is used instead.
        if (nativeBlob && !/^image\/(png|jpeg)$/.test(nativeBlob.type || '')) nativeBlob = null;
      }

      let minX, minY, maxX, maxY, widthInPx, heightInPx;
      if (single) {
        const el = loadedImages[0];
        const natW = el?.naturalWidth || 0, natH = el?.naturalHeight || 0;
        minX = single.x; minY = single.y;
        maxX = single.x + (single.width / 21) * 100;
        maxY = single.y + (single.height / 29.7) * 100;
        // Every pixel the picture has, and not one resampled — the edited SIZE travels with the
        // file rather than being baked into its pixel count (see withPhysicalSize). Shrinking a
        // photo on the sheet and re-rendering it smaller was the only way to make the pixel
        // count match the edit, and that threw detail away for nothing: the file already states
        // it is 15 x 10 cm, so it comes back at 15 x 10 cm with all its detail intact.
        widthInPx  = natW || Math.round(((single.width  / 21)   * PS.width)  * (300 / 2.54));
        heightInPx = natH || Math.round(((single.height / 29.7) * PS.height) * (300 / 2.54));
      } else if (selShape) {
        ({ minX, minY, maxX, maxY } = shapeExportBounds(selShape));
        widthInPx  = Math.round(((maxX - minX) / 100) * PS.width  * (300 / 2.54));
        heightInPx = Math.round(((maxY - minY) / 100) * PS.height * (300 / 2.54));
      } else {
        minX = 0; minY = 0; maxX = 100; maxY = 100;
        let pxPerCm = 300 / 2.54;
        imagesToRender.forEach((img, i) => {
          const el = loadedImages[i]; if (!el?.naturalWidth) return;
          const cmW = (img.width / 21) * PS.width;
          if (cmW > 0) pxPerCm = Math.max(pxPerCm, el.naturalWidth / cmW);
        });
        pxPerCm = Math.min(pxPerCm, 600 / 2.54);           // sanity cap: 600 dpi
        widthInPx  = Math.round(PS.width  * pxPerCm);
        heightInPx = Math.round(PS.height * pxPerCm);
      }
      const contentWidth = maxX - minX;
      const contentHeight = maxY - minY;

      canvas.width = widthInPx;
      canvas.height = heightInPx;
      
      // Re-apply smoothing after canvas resize (resize resets context state)
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      // The chosen sheet, exactly as Print and Save now draw it — a picture shared out of the
      // studio should look like the sheet it was made on, not a white version of it.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, widthInPx, heightInPx);
      if (!single && !selShape && canvasBackground && canvasBackground !== 'transparent') {
        ctx.fillStyle = canvasBackground;
        ctx.fillRect(0, 0, widthInPx, heightInPx);
      }
      if (!single && !selShape) {
        SHEET_FX[canvasBgFx]?.paint(ctx, widthInPx, heightInPx, isDarkSheet(canvasBackground));
      }
      setSaveProgress(20);

      // Draw images — use loadImgSafe so expired/cross-origin Firebase URLs are
      // fetched via blob (avoids canvas taint and silent draw failures).
      for (const _layer of layersInOrder(imagesToRender, shapesToRender)) {
        if (_layer.kind === 'shape') {
          drawShapesOnCanvas(ctx, [_layer.item], widthInPx, heightInPx, minX, minY, contentWidth, contentHeight);
          continue;
        }
        const img = _layer.item;
        const index = imagesToRender.indexOf(img);
        const image = loadedImages[index];
        if (image) {
          const imgXPx = ((img.x - minX) / contentWidth) * widthInPx;
          const imgYPx = ((img.y - minY) / contentHeight) * heightInPx;
          const imgWidthPx  = (img.width  / 21)   * (widthInPx  / contentWidth)  * 100;
          const imgHeightPx = (img.height / 29.7) * (heightInPx / contentHeight) * 100;

          const rot = ((img.rotation || 0) * Math.PI) / 180;
          if (rot) {
            const cx = imgXPx + imgWidthPx / 2;
            const cy = imgYPx + imgHeightPx / 2;
            ctx.save();
            ctx.translate(cx, cy);
            ctx.rotate(rot);
            if (img.cropShape === 'circle') {
              ctx.beginPath();
              ctx.ellipse(0, 0, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2);
              ctx.clip();
            }
            drawImageHQ(ctx, image, -imgWidthPx / 2, -imgHeightPx / 2, imgWidthPx, imgHeightPx);
            ctx.restore();
          } else if (img.cropShape === 'circle') {
            ctx.save();
            ctx.beginPath();
            ctx.ellipse(imgXPx + imgWidthPx / 2, imgYPx + imgHeightPx / 2, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2);
            ctx.clip();
            drawImageHQ(ctx, image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
            ctx.restore();
          } else {
            drawImageHQ(ctx, image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
          }
        }
        setSaveProgress(20 + ((index + 1) / imagesToRender.length) * 70);
      }

      // Drawings last — above every picture as before, and now above the figures too.
      drawDrawingLayer(ctx, pathsToRender, widthInPx, heightInPx, minX, minY, contentWidth, contentHeight);

      setSaveProgress(95);
      const rawShareBlob = nativeBlob || await new Promise(res => canvas.toBlob(res, 'image/png'));
      // Shared files carry their real-world size too, so a picture opened on the other end
      // measures what it measured here -- same reason as the save path.
      const shareCm = declaredSizeCm(selShape, { minX, minY, maxX, maxY }, PS);
      const outBlob = await withPhysicalSize(rawShareBlob, shareCm.w, shareCm.h);
      const outExt  = (outBlob.type === 'image/jpeg') ? 'jpg' : (outBlob.type === 'image/webp') ? 'webp' : 'png';
      const shareFile = new File([outBlob], `${fileName}.${outExt}`, { type: outBlob.type || 'image/png' });
      const objectUrl = URL.createObjectURL(outBlob);
      const download = () => {
        const link = document.createElement('a');
        link.href = objectUrl; link.download = shareFile.name;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
      };
      const copyToClipboard = async () => {
        try {
          if (!navigator.clipboard?.write || !window.ClipboardItem) return false;
          // The clipboard only takes PNG; re-encode a JPEG source for the copy.
          const pngBlob = outBlob.type === 'image/png' ? outBlob : await new Promise(res => canvas.toBlob(res, 'image/png'));
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })]);
          return true;
        } catch { return false; }
      };
      const done = () => { setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000); };

      const messages = {
        whatsapp: 'Made with PCake', telegram: 'Made with PCake', instagram: 'Made with PCake',
        facebook: 'Made with PCake', x: 'Made with PCake', email: 'Made with PCake',
        bluetooth: fileName, native: fileName,
      };

      if (platform === 'copy') {
        showAlert((await copyToClipboard()) ? 'Image copied — paste it into any app.' : 'Copying images is not supported in this browser. Use Download instead.');
        done();
      } else if (platform === 'download') {
        download(); done();
      } else if (platform === 'native' || (isMobileWeb && 'share' in navigator)) {
        // The OS share sheet. On a phone this is the whole story — Gmail, WhatsApp and the
        // rest receive the file directly.
        let ok = false;
        try {
          const canFile = navigator.canShare && navigator.canShare({ files: [shareFile] });
          await navigator.share(canFile ? { files: [shareFile], title: fileName, text: messages[platform] } : { title: fileName, text: messages[platform] });
          ok = true;
        } catch (e) { ok = e?.name === 'AbortError'; if (!ok) console.error('Share error:', e); }
        if (!ok) { download(); }
        done();
      } else {
        // DESKTOP, a named app. Not the share sheet: Chromium on Windows deletes the shared
        // file the moment share() resolves, and Windows Mail attaches lazily — "the following
        // files could not be attached, try again later" every time. So: download the file,
        // put it on the clipboard too, and open the app with a note. Attach or paste.
        download();
        const copied = await copyToClipboard();
        const hint = copied ? 'The image is downloaded and on your clipboard — attach it or paste it.'
                            : 'The image has been downloaded — attach it.';
        const note = encodeURIComponent(`Made with PCake. ${hint}`);
        setTimeout(() => {
          if (platform === 'email')         window.location.href = `mailto:?subject=${encodeURIComponent('Image from PCake')}&body=${note}`;
          else if (platform === 'whatsapp') window.open(`https://web.whatsapp.com/send?text=${note}`, '_blank');
          else if (platform === 'telegram') window.open(`https://web.telegram.org/`, '_blank');
          else if (platform === 'x')        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent('Made with PCake')}`, '_blank');
          else if (platform === 'facebook') window.open('https://www.facebook.com/', '_blank');
          else if (platform === 'instagram') window.open('https://www.instagram.com/', '_blank');
        }, 250);
        showAlert(hint);
        done();
      }

      setSaveProgress(100);
      setTimeout(() => {
        setIsSaving(false);
        setSaveProgress(0);
        if (activeTool !== 'magic-wand') {
          setActiveTool(null);
          setCurrentControlSection(null);
          setShowTopControls(false);
        } else {
          setCurrentControlSection('magic-wand');
        }
      }, 500);

    } catch (error) {
      console.error('Share error:', error);
      setIsSaving(false);
      setSaveProgress(0);
      if (activeTool !== 'magic-wand') {
        setActiveTool(null);
        setCurrentControlSection(null);
        setShowTopControls(false);
      } else {
        setCurrentControlSection('magic-wand');
      }
      showAlert('Failed to share image. Please try again.');
    }
  };

  // Toggle all bucket images selection
  const getDisplayedBucketImages = (images, search, sortBy, sortDir) => {
    const q = search.trim().toLowerCase();
    const filtered = q ? images.filter(img => img.displayName.toLowerCase().includes(q)) : images;
    return [...filtered].sort((a, b) => {
      if (sortBy === 'size') {
        return sortDir === 'desc' ? b.size - a.size : a.size - b.size;
      }
      if (sortBy === 'name') {
        const na = a.displayName.toLowerCase(), nb = b.displayName.toLowerCase();
        return sortDir === 'desc' ? nb.localeCompare(na) : na.localeCompare(nb);
      }
      // default: sort by date (updated)
      const ta = a.updated ? new Date(a.updated).getTime() : 0;
      const tb = b.updated ? new Date(b.updated).getTime() : 0;
      return sortDir === 'desc' ? tb - ta : ta - tb;
    });
  };

  const toggleSelectAllBucketImages = () => {
    const displayed = getDisplayedBucketImages(bucketImages, bucketSearch, bucketSortBy, bucketSortDir);
    const displayedNames = displayed.map(img => img.fileName);
    const allDisplayedSelected = displayedNames.every(n => selectedBucketImages.includes(n));
    if (allDisplayedSelected) {
      setSelectedBucketImages(prev => prev.filter(n => !displayedNames.includes(n)));
    } else {
      setSelectedBucketImages(prev => [...new Set([...prev, ...displayedNames])]);
    }
  };

  const handleBucketRename = async (img, newBaseName) => {
    const ext = img.displayName.includes('.') ? '.' + img.displayName.split('.').pop() : '';
    const folder = img.fileName.substring(0, img.fileName.lastIndexOf('/') + 1);
    const newFileName = folder + newBaseName.trim() + ext;
    if (!newBaseName.trim() || newFileName === img.fileName) {
      setRenamingBucketFile(null);
      return;
    }
    try {
      await renameInStorage(img.fileName, newFileName, getEnvStorageBucket());
      // Bust cache so next open re-fetches
      _bucketImagesCache = null;
      setBucketImages(prev => prev.map(i =>
        i.fileName === img.fileName
          ? { ...i, fileName: newFileName, displayName: newBaseName.trim() + ext }
          : i
      ));
      setSelectedBucketImages(prev =>
        prev.map(n => n === img.fileName ? newFileName : n)
      );
    } catch (e) {
      showAlert('Rename failed: ' + e.message);
    }
    setRenamingBucketFile(null);
  };

  // Handle bucket image selection toggle
  const handleBucketImageToggle = (imageFileName) => {
    if (selectedBucketImages.includes(imageFileName)) {
      setSelectedBucketImages(prev => prev.filter(name => name !== imageFileName));
    } else {
      setSelectedBucketImages(prev => [...prev, imageFileName]);
    }
  };

  // Color picker shades
  const colorShades = {
    '#000000': ['#000000', '#333333', '#666666', '#999999', '#CCCCCC', '#FFFFFF'],
    '#fa4848': ['#fa4848', '#ff5c5c', '#ff8080', '#ffa3a3', '#ffc7c7', '#ffebeb'],
    '#00FF00': ['#00FF00', '#33FF33', '#66FF66', '#99FF99', '#CCFFCC', '#E5FFE5'],
    '#0000FF': ['#0000FF', '#3333FF', '#6666FF', '#9999FF', '#CCCCFF', '#E5E5FF'],
    '#FFFF00': ['#FFFF00', '#FFFF33', '#FFFF66', '#FFFF99', '#FFFFCC', '#FFFFE5'],
    '#FF00FF': ['#FF00FF', '#FF33FF', '#FF66FF', '#FF99FF', '#FFCCFF', '#FFE5FF'],
    '#00FFFF': ['#00FFFF', '#33FFFF', '#66FFFF', '#99FFFF', '#CCFFFF', '#E5FFFF'],
    '#FF8C00': ['#8A4B00', '#C46A00', '#FF8C00', '#FFA940', '#FFC680', '#FFE3BF'],   // orange
    '#FFD700': ['#8C7400', '#C4A300', '#FFD700', '#FFE14D', '#FFEB80', '#FFF5BF'],   // gold
    '#800080': ['#3D003D', '#5E005E', '#800080', '#A64DA6', '#CC8FCC', '#E6C7E6'],   // purple
    '#008080': ['#003D3D', '#005E5E', '#008080', '#40A6A6', '#80CCCC', '#BFE6E6'],   // teal
    '#FF69B4': ['#B3407A', '#E0509A', '#FF69B4', '#FF8FC7', '#FFB5DA', '#FFDAED'],   // pink
    '#8B4513': ['#4A250A', '#6B3410', '#8B4513', '#A86A3F', '#C69672', '#E3C4AA'],   // brown / wood
    '#E0AC69': ['#5C3A21', '#8D5524', '#C68642', '#E0AC69', '#F1C27D', '#FFDBAC'],   // skin tones, deep → light
    '#FFE0BD': ['#C9A27E', '#E6B894', '#F5CBA7', '#FFE0BD', '#FFEBD6', '#FFF4EA'],   // skin tones, light → fair
  };

  const baseColors = Object.keys(colorShades);

  // Practical cake colour palette for AI colour-change tool
  const cakePalette = {
    'White':     ['#FFFFFF', '#F8F5F0', '#F0EBE0', '#E8DECB', '#DDD0B8', '#D0C0A0'],
    'Cream':     ['#FFF9E6', '#FFF0CC', '#FFE8B3', '#FFD99A', '#FFCA80', '#FFB84D'],
    'Blush':     ['#FDDDE6', '#FBBCCC', '#F99AB2', '#F57898', '#ED5580', '#E0326A'],
    'Rose':      ['#E8B4B8', '#D88A8F', '#C86069', '#B84050', '#A0303A', '#882028'],
    'Red':       ['#FF6B6B', '#F94444', '#E82020', '#CC0000', '#A30000', '#7A0000'],
    'Lavender':  ['#E8D5F0', '#D4B0E5', '#BD8CD5', '#A366C0', '#854AB0', '#662E98'],
    'Sage':      ['#D0DBBF', '#B4C49E', '#99AD80', '#7E9662', '#657E4D', '#4E663B'],
    'Sky':       ['#C5DEEF', '#A0C8E5', '#7AB2D8', '#559DC8', '#3285B5', '#1A6E9E'],
    'Chocolate': ['#7B5C3E', '#6B4D34', '#5C3F2B', '#4D3122', '#3E241A', '#301814'],
    'Gold':      ['#F5D078', '#EFC055', '#E8AC35', '#DE9618', '#D08000', '#BE6A00'],
    'Burgundy':  ['#9B2335', '#812030', '#691C2C', '#531628', '#3E1022', '#2B0A18'],
    'Black':     ['#888888', '#666666', '#444444', '#2E2E2E', '#1A1A1A', '#000000'],
  };
  const cakePaletteNames = Object.keys(cakePalette);

  // Handle tool selection - show top controls
  // ── Where should a crop overlay start? ────────────────────────────────────
  // The union of everything on the sheet, in canvas pixels: images, shapes and drawn
  // strokes alike. It used to be the first IMAGE only, so a sheet holding just a diagram
  // got no overlay at all — the rectangle and circle crops move or resize an existing
  // overlay and never draw a fresh one, so with nothing pre-placed they could not even
  // be started — and with an image plus a diagram the overlay wrapped the image and
  // ignored the rest. A selected image wins when there is one (that is clearly what the
  // user means to crop); otherwise all content; otherwise the sheet with a margin.
  const cropStartBounds = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const r = canvas.getBoundingClientRect();
    const W = r.width, H = r.height;
    const imgBox = (img) => ({
      x1: (img.x / 100) * W, y1: (img.y / 100) * H,
      x2: (img.x / 100) * W + (img.width / 21) * W, y2: (img.y / 100) * H + (img.height / 29.7) * H,
    });
    const sel = selectedImage && canvasImagesRef.current.find(i => i.id === selectedImage);
    if (sel) return imgBox(sel);

    let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
    const take = (b) => { if (!b) return; x1 = Math.min(x1, b.x1); y1 = Math.min(y1, b.y1); x2 = Math.max(x2, b.x2); y2 = Math.max(y2, b.y2); };
    canvasImagesRef.current.forEach(img => take(imgBox(img)));
    canvasShapesRef.current.forEach(sh => {
      if ([sh.x1, sh.y1, sh.x2, sh.y2].every(v => typeof v === 'number')) {
        take({ x1: Math.min(sh.x1, sh.x2) / 100 * W, y1: Math.min(sh.y1, sh.y2) / 100 * H,
               x2: Math.max(sh.x1, sh.x2) / 100 * W, y2: Math.max(sh.y1, sh.y2) / 100 * H });
      }
    });
    drawingPaths.forEach(pth => {
      const sx = W / (pth.canvasW || W), sy = H / (pth.canvasH || H), pad = (pth.size || 4) / 2;
      (pth.path || []).forEach(pt => take({ x1: pt.x * sx - pad, y1: pt.y * sy - pad, x2: pt.x * sx + pad, y2: pt.y * sy + pad }));
    });
    if (x1 === Infinity) return { x1: W * 0.1, y1: H * 0.1, x2: W * 0.9, y2: H * 0.9 };
    // Clamp to the sheet and guarantee a usable size.
    x1 = Math.max(0, x1); y1 = Math.max(0, y1); x2 = Math.min(W, x2); y2 = Math.min(H, y2);
    if (x2 - x1 < 24) { x1 = Math.max(0, x1 - 12); x2 = Math.min(W, x2 + 12); }
    if (y2 - y1 < 24) { y1 = Math.max(0, y1 - 12); y2 = Math.min(H, y2 + 12); }
    return { x1, y1, x2, y2 };
  };

  // Open the colour / style controls for a shape (figure, handwriting, text, stamp) and sync
  // the toolbar's colour, stroke and font state to that shape. This used to be reachable only
  // by toggling the Select/Move button off; with selection always on, a dedicated Style button
  // in the tools row calls it instead.
  const openShapeStyleControls = (shapeId) => {
    const shape = canvasShapesRef.current.find(s => s.id === shapeId);
    if (!shape) return;
    const section = shape.type === 'emoji' ? 'shapes-emoji' : shape.type === 'text' ? 'shapes-text' : 'shapes-draw';
    setIsResizingShape(false);
    setIsRotatingShape(false);
    setActiveShapeTool(shape.type);
    setActiveTool('shapes');
    setCurrentControlSection(section);
    if (shape.strokeColor) setShapeStrokeColor(shape.strokeColor);
    if (shape.fillColor)   setShapeFillColor(shape.fillColor);
    if (shape.strokeWidth) setShapeStrokeWidth(shape.strokeWidth);
    setShapeDashed(shape.dashed || false);
    if (shape.type === 'text' || shape.type === 'emoji') {
      if (shape.fontSize)    setShapeFontSize(shape.fontSize || shape.emojiSize || 18);
      if (shape.fontFamily)  setShapeFontFamily(shape.fontFamily);
      if (shape.bold   != null) setShapeTextBold(shape.bold);
      if (shape.italic != null) setShapeTextItalic(shape.italic);
      setShapeTextUnderline(!!shape.underline);
      setShapeText3D(!!shape.threeD);
    }
    setShowTopControls(true);
  };

  /**
   * The hand-drawn twin of openShapeStyleControls: the controls a selected stroke opens with,
   * and what every ink gesture hands back to on release.
   *
   * It deliberately does NOT re-arm a tool the way the figure version does -- there is no
   * "draw another one of these" for a freehand line, and arming the pen would start inking
   * the moment the user touched the sheet again. activeShapeTool is cleared outright so the
   * shared palette can't mistake the selection for a 2D/3D figure and route a colour tap into
   * the figure branches.
   */
  const openInkStyleControls = (pathId) => {
    const p = drawingPathsRef.current.find(pp => pp.id === pathId);
    if (!p) return;
    setIsResizingShape(false);
    setIsRotatingShape(false);
    setIsMovingShape(false);
    if (positionModeRef.current && positionModeForRef.current !== pathId) {
      // Move belongs to a different object — see positionModeForRef.
      positionModeForRef.current = null;
      positionModeRef.current = false;
      setPositionMode(false);
    }
    if (positionModeRef.current) {
      // Move is on: coordinates are what the user asked to see, palette stays out of it.
      setCurrentControlSection('selectmove');
      setSelectMoveControlMode('position');
      setShowTopControls(true);
      return;
    }
    setActiveShapeTool(null);
    setActiveTool(null);
    setSelectMoveControlMode('position');
    setCurrentControlSection('shapes-draw');
    setShapeColorTarget('stroke');
    // Only the colour is synced into the shared palette: it is what the swatch shows and what
    // a tap on a new colour writes back. The pen's own brush size is deliberately left alone --
    // selecting an old stroke shouldn't quietly resize the pen for the next one.
    if (p.color && p.color !== 'eraser') setShapeStrokeColor(p.color);
    setShowTopControls(true);
  };

  const handleToolSelect = (tool) => {
    // Any tool that paints or draws ends Move; save/share/print/delete are just panels and
    // leave it alone, so Move survives a detour through them.
    if (['draw', 'shapes', 'selection', 'magic-wand'].includes(tool)) setPositionMode(false);
    // When magic-wand is active, save/share/print/delete show their top-bar section
    // without switching away from magic-wand — keeps the AI panel and bottom button active.
    if (activeTool === 'magic-wand' && ['save', 'share', 'print', 'delete'].includes(tool)) {
      setCurrentControlSection(tool);
      setShowTopControls(true);
      setImageSelectionMode(false);
      return;
    }

    // The AI chat bar has no close button of its own any more: picking any other tool is
    // the way out, which is what people expect anyway. Save/share/print/delete above are
    // panels rather than tools and return before this, so they leave the chat alone.
    if (magicSubTool === 'chat') setMagicSubTool(null);

    if (tool === 'resize') {
      if (!selectedImage) {
        showAlert('Please select an image to resize');
        return;
      }
    }

    if (tool === 'draw' && selectedShapeId) {
      const selectedShape = canvasShapes.find(s => s.id === selectedShapeId);
      if (selectedShape) {
        setActiveShapeTool(selectedShape.type);
        setActiveTool('shapes');
        const section = selectedShape.type === 'emoji' ? 'shapes-emoji' : selectedShape.type === 'text' ? 'shapes-text' : 'shapes-draw';
        setCurrentControlSection(section);
        setShowTopControls(true);
        setShowShapeColorPicker(false);
        setShowShapeColorShades(false);
        setCanvasCursor('default');
        return;
      }
    }
    // Pressing Crop while already cropping LEAVES crop. It used to do nothing at all: the only
    // way out was the Escape key, which a phone does not have, so choosing crop and then
    // thinking better of it left you stuck inside it with no way back to the studio.
    if (tool === 'selection' && activeTool === 'selection') { exitCrop(); return; }
    if (tool === 'selection') { setActiveShapeTool(null); }

    if (tool === 'selection') {
      setCanvasCursor('crosshair');
    }
    
    setActiveTool(tool);
    setImageSelectionMode(false); // Turn off image selection mode when another tool is selected
    setCurrentControlSection(tool);
    setShowTopControls(true);
    setShowColorPicker(false);
    setShowColorShades(false);
    
    if (tool === 'selection') {
      // Place the crop overlay around the sheet's content; default to rectangle mode.
      const b = cropStartBounds();
      if (b) {
        setSelectionStart({ x: b.x1, y: b.y1 });
        setSelectionEnd  ({ x: b.x2, y: b.y2 });
        setIsSelecting(true);
      }
      if (!selectionMode) setSelectionMode('rectangle');
      setCanvasCursor('move');
    } else if (tool === 'draw') {
      setCanvasCursor((drawingMode === 'pen' || drawingMode === 'pencil') ? 'crosshair' : 'cell');
    } else {
      setCanvasCursor('default');
    }
  };

  // ---- Shape tool helpers ------------------------------------------------
  const handleShapeToolSelect = (tool) => {
    // Picking something to DRAW ends Move — the new figure should open with its colours.
    setPositionMode(false);
    if (magicSubTool === 'chat') setMagicSubTool(null);   // reaching for a tool leaves AI chat
    setImageSelectionMode(false);
    setShowShapeColorPicker(false);
    setShowShapeColorShades(false);
    setLastUsedShapeTool(tool);
    // Reset lingering resize/rotate states
    setIsResizingShape(false);
    setIsRotatingShape(false);

    // Reset palette to default for the new tool
    const _defaultStroke = '#000000';
    setShapeStrokeColor(_defaultStroke);
    setShapeFillColor('none');
    setShapeColorTarget('stroke');
    setShapeSelectedBase(_defaultStroke);
    if (isShape3D(tool)) {
      // a 3D object is a coloured body, not an outline
      setShapeFillColor('#4f8ef7');
      setShapeColorTarget('fill');
    }

    if (tool === 'handwriting') {
      setDrawingColor(_defaultStroke);
      setActiveShapeTool('handwriting');
      if (activeTool === 'draw') {
        // Already drawing — cycle to the next draw mode
        const modes = ['pen', 'pencil', 'spray', 'eraser', 'fill'];
        const next = modes[(modes.indexOf(drawingMode) + 1) % modes.length];
        setDrawingMode(next);
        setBrushSize(DEFAULT_BRUSH_SIZE);
        setCanvasCursor((next === 'pen' || next === 'pencil') ? 'crosshair' : 'cell');
      } else {
        setActiveTool('draw');
        setCanvasCursor((drawingMode === 'pen' || drawingMode === 'pencil') ? 'crosshair' : 'cell');
      }
      setCurrentControlSection('draw');
      setShowTopControls(true);
      return;
    }

    setFillModeActive(false);

    setActiveShapeTool(tool);
    setActiveTool('shapes');
    setCanvasCursor('crosshair');
    // ── The palette belongs to an object, not to a tool ─────────────────────────────────────
    // Picking a figure tool used to fill the top bar with colours, borders and thickness with
    // nothing on the sheet to apply them to — a palette hanging over an empty selection, which
    // is what "no object is selected but the colour palette is still there" was. Nothing is
    // lost by waiting: a figure is selected the moment it is drawn and the palette opens then,
    // and the colours it is set to carry over to the next figure drawn with the same tool.
    const section = tool === 'emoji' ? 'shapes-emoji' : tool === 'text' ? 'shapes-text' : 'shapes-draw';
    const hasSelection = !!selectedShapeId || selectedShapeIds.size > 0;
    setCurrentControlSection(hasSelection ? section : null);
    setShowTopControls(hasSelection);
  };

  // ── Selfie Mode's own camera ────────────────────────────────────────────────────────────
  // A live picture in the app rather than a handoff to the phone's camera app: the shutter is
  // here, the frame lands straight on the sheet, and — the point — the camera permission is
  // this app's, so nothing else running in it has to ask for a camera a second time.
  /**
   * ── The camera button, and the two things that were wrong with it ─────────────────────────
   *
   * On an iPhone it did nothing at all. Safari will only let code open a file input while the
   * tap that asked for it is still live, and ANY await ends that on the spot — so checking the
   * camera permission first and clicking the input afterwards meant the click was thrown away.
   * No camera, no camera app, no error: a dead button. So the permission is now looked up ahead
   * of time (cameraGrantedRef) and every route out of here starts from a press of its own,
   * synchronously.
   *
   * On Android the camera simply opened, with nothing asked — which is not something an app
   * should do on its own, whatever the browser does or does not require. It asks first now, in
   * its own words, once: after that the answer is remembered and the camera opens straight away.
   */
  const openSelfieCamera = (facing = 'user') => {
    // Already ours: the browser will not ask again, so neither do we.
    if (cameraGrantedRef.current || hasCameraConsent()) { startInAppCamera(facing); return; }
    // Nowhere to hand off to — a desktop has no camera app — so there is only one route.
    if (!COARSE_POINTER || !cameraInputRef.current) { startInAppCamera(facing); return; }
    setCameraPrompt({ facing });
  };

  const startInAppCamera = async (facing = 'user') => {
    try {
      const stream = await openCamera(facing);
      cameraGrantedRef.current = true;       // don't ask again; the grant is the origin's now
      setSelfieFacing(facing);
      selfieStreamRef.current = stream;
      setSelfieStream(prev => { closeCamera(prev); return stream; });
    } catch (err) {
      // Whatever the browser's reason, Selfie Mode still takes a picture: the phone's own camera
      // app is opened instead. That path works on an insecure address, inside a frame, and on a
      // browser with no getUserMedia. It grants the page nothing — so Eyelid will have to ask
      // for itself later — but a refusal that leaves you with no camera at all is worse. The
      // first attempt was doing exactly that: a "blocked, change your settings" notice and no
      // way to take a photo, including where there is no such setting to change.
      //
      // Offered as a button rather than taken silently, because by the time we are in here the
      // tap that started all this is long over, and Safari will not open a file input outside
      // it — the silent version was another dead button on an iPhone, in the one situation
      // where something had already gone wrong.
      if (COARSE_POINTER && cameraInputRef.current) {
        setCameraPrompt({ facing, reason: err?.reason || 'unavailable', detail: err?.detail || '' });
        return;
      }
      showAlert(cameraMessage(err?.reason));
    }
  };
  const closeSelfieCamera = () => {
    selfieStreamRef.current = null;
    setSelfieStream(prev => { closeCamera(prev); return null; });
  };
  /**
   * ── One camera at a time ────────────────────────────────────────────────────────────────
   * Flipping used to ask for the other camera while this one was still running, and let go of
   * the old stream only once the new one had arrived — which is the right order on a machine
   * that can run two at once, and the wrong one on an iPad, which cannot. There the second
   * request never succeeded, the first was left holding a device that had stopped producing
   * frames, and the preview froze on the last picture it had with no way back: the flip button
   * only ever asked for the camera it had just failed to get.
   *
   * So the old one is let go of first. That leaves a moment with no camera at all, and if the
   * other lens then refuses, the one that was working is gone — so the original facing is
   * reopened rather than leaving a dead preview on screen.
   */
  const flipSelfieCamera = async () => {
    const from = selfieFacing;
    const to = from === 'user' ? 'environment' : 'user';
    closeCamera(selfieStreamRef.current);
    selfieStreamRef.current = null;
    try {
      const stream = await openCamera(to);
      selfieStreamRef.current = stream;
      setSelfieFacing(to);
      setSelfieStream(stream);
    } catch (err) {
      try {
        const back = await openCamera(from);
        selfieStreamRef.current = back;
        setSelfieStream(back);
        flash('That camera would not open');
      } catch {
        closeSelfieCamera();
        showAlert(cameraMessage(err?.reason));
      }
    }
  };
  const takeSelfie = () => {
    const v = selfieVideoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement('canvas');
    c.width = v.videoWidth; c.height = v.videoHeight;
    const cx = c.getContext('2d');
    // A front camera is a mirror while you are framing yourself, so the photo is flipped back
    // the right way round — otherwise every word in the picture comes out backwards.
    if (selfieFacing === 'user') { cx.translate(c.width, 0); cx.scale(-1, 1); }
    cx.drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `selfie_${Date.now()}.png`, { type: 'image/png' });
      closeSelfieCamera();
      setSelfieMode(true);
      handleLocalFileUpload({ target: { files: [file], value: '' } });
    }, 'image/png');
  };
  // Hand the stream to the <video> whenever either changes, and make sure the camera is let go
  // of when the app closes or the sheet navigates away.
  useEffect(() => {
    const v = selfieVideoRef.current;
    if (v && selfieStream && v.srcObject !== selfieStream) {
      v.srcObject = selfieStream;
      v.play?.().catch(() => { /* autoplay is allowed for a muted inline video; ignore */ });
    }
  }, [selfieStream]);
  useEffect(() => () => closeCamera(selfieStream), [selfieStream]);

  // ── What Selfie Mode does to the picture ────────────────────────────────────────────────
  // The photo the effects act on: whatever is selected, and failing that the topmost thing on
  // the sheet that is not a prop — so pressing Beauty straight after taking a selfie does the
  // obvious thing instead of asking to be told which picture is meant.
  // The picture the effects act on: the SELECTED one, and nothing else. It used to fall back to
  // the topmost photo on the sheet, which meant pressing Beauty with nothing selected quietly
  // changed whichever picture happened to be on top. An effect is a thing you do to a particular
  // photograph, so with none chosen there is nothing to do and the buttons say so by going flat.
  //
  // From the state rather than the ref: this is read while rendering — to light up the Mirror
  // button, to put the current photo in the effect thumbnails, and to disable the lot when
  // nothing is picked — and a ref does not cause the re-render that would keep any of that
  // honest.
  const selfiePhoto = () => {
    const list = canvasImages || [];
    return list.find(i => i.id === selectedImage && i.source !== 'prop') || null;
  };
  const selfieFxOf = () => selfiePhoto()?._selfieFx || null;
  const selfieMirrored = () => !!selfiePhoto()?._selfieMirror;

  /**
   * Redraw the photo from its ORIGINAL pixels with the chosen effect and mirroring.
   *
   * From the original every time, never from the last result. Warm on top of Warm is twice as
   * warm and there is no way back from it; keeping the untouched copy beside the picture means
   * every press is a fresh decision, pressing the same one again takes it off, and the sharpness
   * of the photograph is never spent on a chain of re-encodings.
   */
  const renderSelfie = async (next = {}) => {
    const photo = selfiePhoto();
    if (!photo) { flash('Take a photo first'); return; }
    const src = photo._fxOrigUrl || photo.url;
    const el = await loadImgSafe(src);
    if (!el?.naturalWidth) return;
    const fxKey = next.fx !== undefined ? next.fx : (photo._selfieFx || null);
    const mirrored = next.mirrored !== undefined ? next.mirrored : !!photo._selfieMirror;
    const fx = fxKey ? SELFIE_FX[fxKey] : null;

    const c = document.createElement('canvas');
    c.width = el.naturalWidth; c.height = el.naturalHeight;
    const g = c.getContext('2d');
    g.imageSmoothingEnabled = true; g.imageSmoothingQuality = 'high';
    if (mirrored) { g.translate(c.width, 0); g.scale(-1, 1); }
    if (fx?.filter) g.filter = fx.filter;
    g.drawImage(el, 0, 0, c.width, c.height);
    g.filter = 'none';
    g.setTransform(1, 0, 0, 1, 0, 0);
    // Smoothing is the picture laid over a blurred copy of itself — the oldest trick there is,
    // and still the one that comes out looking like skin rather than like plastic.
    if (fx?.smooth) {
      const b = document.createElement('canvas');
      b.width = c.width; b.height = c.height;
      const bg = b.getContext('2d');
      bg.filter = `blur(${Math.max(2, Math.round(c.width / 200))}px)`;
      bg.drawImage(c, 0, 0);
      g.globalAlpha = fx.smooth; g.drawImage(b, 0, 0); g.globalAlpha = 1;
    }
    if (fx?.wash) { g.fillStyle = fx.wash; g.fillRect(0, 0, c.width, c.height); }
    if (fx?.vignette) {
      const r = Math.hypot(c.width, c.height) / 2;
      const grad = g.createRadialGradient(c.width / 2, c.height / 2, r * 0.45,
                                          c.width / 2, c.height / 2, r);
      grad.addColorStop(0, 'rgba(0,0,0,0)');
      grad.addColorStop(1, `rgba(0,0,0,${fx.vignette})`);
      g.fillStyle = grad; g.fillRect(0, 0, c.width, c.height);
    }

    const blob = await new Promise(res => c.toBlob(res, 'image/png'));
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    setCanvasImages(prev => prev.map(i => i.id === photo.id
      ? { ...i, url, _fxOrigUrl: src, _selfieFx: fxKey, _selfieMirror: mirrored }
      : i));
    markDirty();
  };
  const pickSelfieFx = (key) => renderSelfie({ fx: selfieFxOf() === key ? null : key });
  const toggleSelfieMirror = () => renderSelfie({ mirrored: !selfieMirrored() });

  /**
   * A face, a heart, a star — dropped in the middle of the photo as a text object.
   *
   * The old Stickers button opened the stamp library, which is a set of pictures somebody has to
   * put there first; on a sheet where nobody had, it opened an empty box. These are characters,
   * so there is nothing to load and nothing to go missing, and once placed they are ordinary
   * text: drag, resize, turn, recolour, save, print.
   */
  const addSelfieEmoji = (ch) => {
    const photo = selfiePhoto();
    const _cW = canvasRef.current?.offsetWidth || 595;
    const _cH = canvasRef.current?.offsetHeight || 841;
    const box = photo
      ? { cx: photo.x + (photo.width / 21) * 50, cy: photo.y + (photo.height / 29.7) * 50, w: photo.width }
      : { cx: 50, cy: 50, w: 8 };
    const fs = Math.max(18, Math.round(((box.w * 0.24) / 21) * _cW));
    const wPct = (fs * EMOJI_BOX_W / _cW) * 100;
    const hPct = (fs * EMOJI_BOX_H / _cH) * 100;
    const before = canvasShapesRef.current;
    const shape = {
      id: Date.now(),
      type: 'text',
      x1: box.cx - wPct / 2,
      y1: box.cy - hPct / 2,
      x2: box.cx + wPct / 2,
      y2: box.cy + hPct / 2,
      strokeColor: shapeStrokeColor,
      fillColor: 'none',
      strokeWidth: shapeStrokeWidth,
      text: ch,
      fontSize: fs,
      fontFamily: shapeFontFamily,
      emojiGlyph: true,          // a picture: it grows with its box, unlike writing
      outlineWidth: 0,
      bold: false, italic: false, underline: false, threeD: false,
      zOrder: before.length,
      layerIndex: ++layerCounterRef.current,
    };
    const after = [...before, shape];
    setCanvasShapes(after);
    canvasShapesRef.current = after;
    pushShapeHistory(before, after);
    setSelectedShapeId(shape.id);
    setOpenGroup(null);
    markDirty();
  };

  /** A prop, dropped roughly where it belongs on the photo and left selected, ready to drag. */
  const addSelfieProp = (key) => {
    const p = SELFIE_PROPS.find(q => q.key === key);
    if (!p) return;
    const photo = selfiePhoto();
    if (!photo) { flash('Tap the photo first'); return; }
    const box = { x: photo.x, y: photo.y, w: photo.width, h: photo.height };
    const wCm = Math.max(0.6, box.w * p.w);
    const hCm = wCm * (p.vh / p.vw);
    const newImage = {
      id: Date.now(),
      layerIndex: ++layerCounterRef.current,
      url: p.url,
      name: p.label,
      size: '',
      source: 'prop',
      // Filed under the photograph. carryProps reads this: from here on the prop is part of
      // that picture, and deleting the picture takes its props with it.
      parentId: photo.id,
      x: box.x + p.cx * (box.w / 21) * 100 - (wCm / 21) * 50,
      y: box.y + p.cy * (box.h / 29.7) * 100 - (hCm / 29.7) * 50,
      width: wCm,
      height: hCm,
      cropShape: null,
    };
    pushImageHistory(canvasImagesRef.current, [...canvasImagesRef.current, newImage]);
    setCanvasImages(prev => [...prev, newImage]);
    setSelectedImage(newImage.id);
    setShowImageControls(newImage.id);
    setOpenGroup(null);
    markDirty();
  };

  // With the keyboard put away for the microphone, the box is open but nothing is focused — so
  // there is no blur left to finish it with. A press anywhere on the sheet finishes it instead,
  // which is what the same press does when the box IS focused. While the box still has focus
  // this stands aside and lets the blur do it, so nothing is committed twice.
  useEffect(() => {
    if (!pendingTextPos && !pendingLabelShapeId) return undefined;
    const onDown = (e) => {
      if (listeningRef.current) return;                       // still speaking
      const t = e.target;
      if (t?.closest?.('textarea, .is-top-toolbar, .is-tools-row, .is-bottom-bar, .is-group-panel, .is-cam')) return;
      if (document.activeElement && document.activeElement.tagName === 'TEXTAREA') return;
      if (pendingTextPos) finalizePendingText(); else commitShapeLabel();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  });

  const finalizePendingText = () => {
    if (pendingTextPos && pendingTextValue.trim()) {
      const _tBefore = canvasShapesRef.current;
      const _cW = canvasRef.current?.offsetWidth  || 595;
      const _cH = canvasRef.current?.offsetHeight || 841;
      const _fs = shapeFontSize;
      // A text box that already exists keeps the box it already has. Editing a text is not a
      // licence to move or resize it: it was being re-measured from scratch on every commit and
      // handed a fresh box as wide as the paper, so a box dragged narrow and tall snapped back
      // to wide and short the moment its text was touched. Only the width the user set decides
      // where the lines break now, and only a box too short to show every line grows — downwards,
      // never upwards, and never narrower or wider than it was.
      //
      // A brand-new text has no box yet, so it still gets one as wide as the paper allowed and
      // as tall as the text turned out to be. The old guess — characters x font size x 0.55, on
      // one line — was both wrong about the width of real glyphs and incapable of expressing a
      // second line, which is the other half of why text never wrapped.
      const _edited = pendingTextShapeId ? _tBefore.find(s => s.id === pendingTextShapeId) : null;
      const _paperW = Math.max(30, ((100 - pendingTextPos.x) / 100) * _cW - 10);
      const _maxW = _edited
        ? Math.max(30, (Math.abs(_edited.x2 - _edited.x1) / 100) * _cW)
        : _paperW;
      const _font = `${shapeTextItalic ? 'italic ' : ''}${shapeTextBold ? 'bold ' : ''}${_fs}px ${shapeFontFamily || 'Arial'}`;
      const _wrapped = wrapTextLines(pendingTextValue, _font, _maxW);
      if (!_measureCtx.current) _measureCtx.current = document.createElement('canvas').getContext('2d');
      _measureCtx.current.font = _font;
      const _estW = Math.max(30, Math.min(_maxW, Math.max(
        ..._wrapped.map(l => _measureCtx.current.measureText(l).width),
      ) + _fs * 0.2));
      const _estH = Math.max(1, _wrapped.length) * _fs * 1.2;
      if (_edited) {
        // Editing existing shape -- same corner, same width, height only ever grown to fit
        const _x0 = Math.min(_edited.x1, _edited.x2);
        const _y0 = Math.min(_edited.y1, _edited.y2);
        const _keepW = Math.abs(_edited.x2 - _edited.x1);
        const _keepH = Math.max(Math.abs(_edited.y2 - _edited.y1), (_estH / _cH) * 100);
        const _tAfter = _tBefore.map(s => s.id === pendingTextShapeId ? {
          ...s,
          text: pendingTextValue,
          x1: _x0,
          y1: _y0,
          x2: _x0 + _keepW,
          y2: _y0 + _keepH,
          strokeColor: shapeStrokeColor,
          fontSize: _fs,
          fontFamily: shapeFontFamily,
          bold: shapeTextBold,
          italic: shapeTextItalic,
          underline: shapeTextUnderline,
          threeD: shapeText3D,
        } : s);
        setCanvasShapes(_tAfter);
        setSelectedShapeId(pendingTextShapeId);
        pushShapeHistory(_tBefore, _tAfter);
        markDirty();
      } else {
        // New shape
        const newShape = {
          id: Date.now(),
          type: 'text',
          x1: pendingTextPos.x,
          y1: pendingTextPos.y,
          x2: pendingTextPos.x + (_estW / _cW) * 100,
          y2: pendingTextPos.y + (_estH / _cH) * 100,
          strokeColor: shapeStrokeColor,
          fillColor: 'none',
          strokeWidth: shapeStrokeWidth,
          text: pendingTextValue,
          fontSize: _fs,
          fontFamily: shapeFontFamily,
          outlineWidth: textOutline,
          bold: shapeTextBold,
          italic: shapeTextItalic,
          underline: shapeTextUnderline,
          threeD: shapeText3D,
          zOrder: canvasShapes.length,
          layerIndex: ++layerCounterRef.current,
        };
        const _tAfter = [..._tBefore, newShape];
        setCanvasShapes(_tAfter);
        setSelectedShapeId(newShape.id);
        pushShapeHistory(_tBefore, _tAfter);
        markDirty();
      }
    }
    setPendingTextPos(null);
    setPendingTextValue('');
    setPendingTextShapeId(null);
    // Reset text defaults only when creating new text, not editing
    if (!pendingTextShapeId) {
      const _defStroke = '#000000';
      setShapeStrokeColor(_defStroke);
      setShapeFontSize(Math.round(Math.max(12, Math.min(24, (canvasRef.current?.offsetHeight || window.innerWidth * (29.7 / 21)) * 0.028))));
      setShapeFontFamily('Arial');
      setShapeTextBold(false);
      setShapeTextItalic(false);
      setShapeTextUnderline(false);
      setShapeText3D(false);
      setActiveShapeTool(null);
      setActiveTool(null);
      setCanvasCursor('default');
    }
  };
  // -----------------------------------------------------------------------

  // Toolbar groups: open by tap, and kept open while a tool from the group is in use.
  const showAiGroup = openGroup === 'ai';
  // While something is selected, the top bar's middle carries that object's controls — colour,
  // line depth, copy, delete — and on a phone they were running off the end of the row. The two
  // chrome buttons that frame it (Add on the left, Close on the right) have nothing to do with
  // editing a selection and come straight back the moment you deselect, so they stand aside and
  // give the row their width. Same rule as the existing one for a drag/resize in progress.
  const anySelection = !!selectedShapeId || !!selectedImage || selectedShapeIds.size > 0;
  // The size/rotate readout takes over the middle of the bar, and it is wide — a lock button,
  // W, H, and the angle. With Copy and Delete still sitting beside it the numbers get clipped,
  // so they stand aside for it. Keyed on the readout being ON SCREEN rather than on a drag
  // being underway: touching a corner shows the numbers straight away, and that is exactly the
  // moment the room is needed, not once the finger starts moving.
  const sizingReadoutOpen = currentControlSection === 'selectmove'
    && (selectMoveControlMode === 'resize' || selectMoveControlMode === 'rotate');
  // Typing text needs the room that Copy and Delete take up — the font, its size, the outline
  // and the new-line button all live in that middle section, and neither Copy nor Delete means
  // anything while a caret is in a box.
  const editingText = !!pendingTextPos || !!pendingTextShapeId || !!pendingLabelShapeId;
  // Typing INTO a text object, as opposed to naming a figure. Placing a text box puts the tool
  // down, which leaves nothing selected and no tool armed — so every path that tidies the bar up
  // took the font, size and outline controls away at exactly the moment they were wanted. This
  // says plainly what should be on screen while there is a caret in a text box, instead of
  // hoping none of those paths fires.
  const typingText = !!pendingTextPos || !!pendingTextShapeId;
  useEffect(() => {
    if (!typingText) return;
    setCurrentControlSection('shapes-text');
    setShowTopControls(true);
  }, [typingText]);
  const hideTopChrome = anySelection
    || editingText
    || aiStatusInTopBar                       // an AI run's progress needs the whole bar
    || activeTool === 'selection'             // the crop menu has more buttons than fit beside them
    || ['save', 'share', 'print'].includes(activeTool)
    || isMovingShape || isResizingShape || isRotatingShape
    || draggingImage || resizingImage || rotatingImage;
  // ── The middle of the bar says one thing at a time ──────────────────────────────────────
  // It is a single narrow strip, and two things were being written into it at once: press
  // Print with a picture selected and the progress of the print appeared BESIDE that picture's
  // X and Y boxes, as though the numbers were something to do with printing. Same for Save and
  // for Share. An AI run already had the strip to itself — "the coordinates box steps aside for
  // it and comes back once this clears" — and this is that same rule, written once, for every
  // other thing that has something to say.
  //
  // A message wins because it is the answer to what was just pressed; the coordinates and the
  // palette describe an object that is not going anywhere and will still be there afterwards.
  const topBarMessage = !!(aiStatusInTopBar || isSaving || pendingNavPath || pendingClear
    || ['save', 'share', 'print'].includes(activeTool));
  // An object's colour/style section belongs to that object. Deselecting clears it on every
  // path that runs through consumePressToDeselect or neutralizeTools — but a selection can also
  // end in ways those do not cover (an undo, a delete, a tool change), and the section was then
  // left on screen describing something that is no longer there. This is the backstop: nothing
  // selected and no tool armed means no object controls.
  useEffect(() => {
    // A text box being typed into is not "nothing selected": there is no shape yet, and the tool
    // has been put down by the act of placing the box, so this backstop would sweep away the very
    // controls that text needs — the font, its size, the outline, the new-line button.
    // Anything selected means its controls are on screen. This is the backstop for every path
    // that selects something without going through the image click above — a shape, an undo, a
    // figure just drawn — so "selected but no buttons" cannot happen by some other route.
    if (anySelection && !editingText && activeTool !== 'selection' && !currentControlSection) {
      setCurrentControlSection('selectmove');
      setSelectMoveControlMode('position');
      setShowTopControls(true);
      return;
    }
    if (anySelection || activeShapeTool || activeTool === 'draw' || editingText) return;
    if (['shapes-draw', 'shapes-text', 'shapes-emoji', 'selectmove'].includes(currentControlSection)) {
      setCurrentControlSection(null);
      setShowTopControls(false);
    }
  }, [anySelection, activeShapeTool, activeTool, currentControlSection, editingText]);

  const showDrawGroup = openGroup === 'draw';
  const aiToolActive = magicSubTool === 'chat' || aiBusy || showBgColorPopdown || showColorPopdown;
  const drawToolActive = activeTool === 'draw' || activeTool === 'shapes' || !!activeShapeTool;

  // ── The caption's typeface ───────────────────────────────────────────────────
  // Words written inside a figure (double-click / long-press) are text like any other, so the
  // one Font button sets their face too. It had no effect on them before: the Font button only
  // came out for the text tool, and the caption was drawn in a hard-coded Arial regardless.
  //
  // This is the figure those controls act on — the one being written into, or a selected figure
  // that already carries a caption. The values are read straight off the shape rather than from
  // the text tool's own state, so choosing a face for a caption never disturbs what the text
  // tool is set to, and the panel always shows what THIS caption is.
  const captionFontShape = (() => {
    if (!pendingLabelShapeId && (activeShapeTool === 'text' || currentControlSection === 'shapes-text')) return null;
    const id = pendingLabelShapeId || selectedShapeId;
    if (!id) return null;
    const s = canvasShapes.find(sh => sh.id === id);
    if (!s || s.type === 'text') return null;
    return (pendingLabelShapeId === s.id || s.text) ? s : null;
  })();
  const fontPanelFamily    = captionFontShape ? (captionFontShape.captionFont || 'Arial') : shapeFontFamily;
  const fontPanelBold      = captionFontShape ? !!captionFontShape.captionBold      : shapeTextBold;
  const fontPanelItalic    = captionFontShape ? !!captionFontShape.captionItalic    : shapeTextItalic;
  const fontPanelUnderline = captionFontShape ? !!captionFontShape.captionUnderline : shapeTextUnderline;
  const fontPanel3D        = captionFontShape ? !!captionFontShape.caption3D        : shapeText3D;
  const setCaptionStyle = (patch) => {
    const _id = captionFontShape?.id;
    if (!_id) return;
    setCanvasShapes(prev => prev.map(s => (s.id === _id ? { ...s, ...patch } : s)));
    markDirty();
    // Chosen from the open panel, which means the caption box behind it kept its words and its
    // place in the sentence. Put the cursor back in it so typing carries straight on.
    if (pendingLabelShapeId) requestAnimationFrame(() => {
      const el = labelInputRef.current;
      if (!el) return;
      el.focus({ preventScroll: true });
      // Focus normally selects the whole caption, so that double-clicking a named figure and
      // typing replaces the name. Coming back from the Font panel is the opposite case — the
      // words are half-written — so the caret goes to the end instead.
      try { el.setSelectionRange(el.value.length, el.value.length); } catch { /* not focusable yet */ }
    });
  };
  const setFontPanelFamily = (v) => {
    if (captionFontShape) { setCaptionStyle({ captionFont: v }); return; }
    setShapeFontFamily(v);
    if (selectedShapeId) setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? refitTextBox({ ...s, fontFamily: v }) : s));
  };
  const setFontPanelBold = (v) => {
    if (captionFontShape) { setCaptionStyle({ captionBold: v }); return; }
    setShapeTextBold(v);
    if (selectedShapeId) setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? refitTextBox({ ...s, bold: v }) : s));
  };
  const setFontPanelItalic = (v) => {
    if (captionFontShape) { setCaptionStyle({ captionItalic: v }); return; }
    setShapeTextItalic(v);
    if (selectedShapeId) setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? refitTextBox({ ...s, italic: v }) : s));
  };
  const setFontPanelUnderline = (v) => {
    if (captionFontShape) { setCaptionStyle({ captionUnderline: v }); return; }
    setShapeTextUnderline(v);
    if (selectedShapeId) setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, underline: v } : s));
  };
  const setFontPanel3D = (v) => {
    if (captionFontShape) { setCaptionStyle({ caption3D: v }); return; }
    setShapeText3D(v);
    if (selectedShapeId) setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, threeD: v } : s));
  };

  // Keyboard-shortcut actions (see the keydown/paste effect near the top of the component).
  keyActionsRef.current = {
    // Undo/redo go through the ref like everything else here. The keydown listener only
    // re-subscribes on a few selection deps, so calling handleUndo/handleRedo directly from
    // inside it meant a closure holding a stale historyIndex: Ctrl+Y right after Ctrl+Z did
    // nothing, because the listener still believed there was nothing to redo.
    undo: () => handleUndo(),
    redo: () => handleRedo(),
    save: () => {
      if (isSaving) return;
      if (activeTool === 'save') { handleSave(); return; }         // panel already open → save now
      if (pendingNavPath) { setPendingNavPath(null); setSaveMessage(null); }
      setShowShareRow(false); setShowLayoutMenu(false);
      handleToolSelect('save');
    },
    print: () => {
      if (isSaving) return;
      setShowShareRow(false); setShowLayoutMenu(false);
      const hasContent = sheetHasSomething();
      if (!hasContent) { handleToolSelect('print'); } else { setActiveTool('print'); handlePrint(); }
    },
    share: () => {
      if (isSaving) return;
      setShowLayoutMenu(false); setShowShareRow(v => !v);
    },
    copy: () => {
      if (selectedShapeId) {
        const sh = canvasShapes.find(x => x.id === selectedShapeId);
        if (!sh) return false;
        shapeClipboardRef.current = { ...sh };
        flash('Object copied — Ctrl+V pastes it');
        return true;
      }
      if (selectedImage) {
        const img = canvasImages.find(i => i.id === selectedImage);
        if (!img) return false;
        copyImageToClipboard(img);
        return true;
      }
      return false;
    },
    duplicate: () => {
      if (selectedShapeId) handleCopyShape();
      else if (selectedImage) handleCopyImage(selectedImage, { stopPropagation: () => {} });
    },
    pasteImages: (files) => handleLocalFileUpload({ target: { files, value: '' } }),
    pasteShape: () => {
      const sh = shapeClipboardRef.current;
      if (!sh) return false;
      const before = canvasShapesRef.current;
      const ns = { ...sh, id: Date.now(), x1: sh.x1 + 2, y1: sh.y1 + 2, x2: sh.x2 + 2, y2: sh.y2 + 2, zOrder: before.length, layerIndex: ++layerCounterRef.current };
      const after = [...before, ns];
      setCanvasShapes(after); canvasShapesRef.current = after;
      pushShapeHistory(before, after);
      setSelectedImage(null); setShowImageControls(null);
      setSelectedShapeId(ns.id);
      return true;
    },
    escape: () => {
      if (showShareRow)    { setShowShareRow(false); return; }
      if (showLayoutMenu)  { setShowLayoutMenu(false); return; }
      if (showEmojiPopdown){ setShowEmojiPopdown(false); return; }
      if (pendingLabelShapeId) { cancelShapeLabelEditor(); return; }  // name box first
      if (pendingClear)    { cancelPendingClear(); return; }
      if (pendingNavPath)  { setPendingNavPath(null); return; }
      if (selectedImage || selectedShapeId || activeTool || activeShapeTool) { neutralizeTools(); return; }
      if (magicSubTool)    { setMagicSubTool(null); return; }   // chat bar open on its own
    },
  };

  return (
    <>
      <input 
        type="file" 
        ref={fileInputRef} 
        style={{ display: 'none' }} 
        onChange={handleLocalFileUpload}
        accept="image/*"
        multiple
      />
      {/* Selfie Mode. capture="user" asks for the FRONT camera — this is a picture of the
          person holding the phone, not of what they are pointing it at — and a photo that
          arrives this way switches the toolbar into the selfie set (see selfieMode). */}
      <input
        type="file"
        ref={cameraInputRef}
        style={{ display: 'none' }}
        onChange={(e) => { if (e.target.files?.length) setSelfieMode(true); handleLocalFileUpload(e); }}
        accept="image/*"
        capture="user"
      />
      <input
        type="file"
        ref={cloudUploadInputRef}
        style={{ display: 'none' }}
        onChange={handleDirectCloudUpload}
        accept="image/*"
        multiple
      />
      
      <div className={`image-studio${fromCustom ? ' is--from-custom' : ''}${(!fromCustom && vpSize.w > vpSize.h && vpSize.h < 550) ? ' is-landscape' : ''}${(!fromCustom && magicSubTool === 'chat') ? ' magic-chat-open' : ''}`}>
        {/* PCAKE — opened from a Custom profile (Buyer / Super): one row of photo tools over the
            picture, in CreativeStudio's controls, and the way back to the profile. The normal
            top toolbar and bottom bar stay hidden in this mode (see their display:none). */}
        {fromCustom && (
          <div className="magic-wand-toolbar">
            <div className="magic-toolbar-row">
              <button className="control-btn vertical" onClick={handleUndo} title="Undo" disabled={historyIndex < 0}><FaUndo /></button>
              <button className="control-btn vertical" onClick={handleRedo} title="Redo" disabled={historyIndex >= drawingHistory.length - 1}><FaRedo /></button>
              {canUseAi && (
                <>
                  <div className="magic-divider" />
                  {/* Background: remove / replace / plate — CreativeStudio's pop-down */}
                  <button
                    ref={bgColorBtnRef}
                    className={`control-btn vertical${showBgColorPopdown ? ' active' : ''}`}
                    onClick={(e) => {
                      const btnRect = e.currentTarget.getBoundingClientRect();
                      const PANEL_W = 220, PANEL_H = 100;
                      const vw = window.innerWidth, vh = window.innerHeight;
                      let left = btnRect.left;
                      if (left + PANEL_W > vw - 6) left = vw - PANEL_W - 6;
                      if (left < 6) left = 6;
                      let top = btnRect.bottom + 6;
                      if (top + PANEL_H > vh - 6) top = vh - PANEL_H - 6;
                      setBgColorPopdownPos({ top, left });
                      if (!showBgColorPopdown) { setBgRemoveMix(false); setBgRemovePending(null); }
                      setShowBgColorPopdown(v => !v);
                    }}
                    disabled={aiHold}
                    title="Remove / Replace Background (AI)"
                  ><FaImage /></button>
                  <button
                    className="control-btn vertical"
                    onClick={handleMagicEnhance}
                    disabled={aiHold || !effectiveImageId}
                    title="Enhance quality (AI)"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="13" r="7" fill="currentColor" opacity="0.15" stroke="currentColor" strokeWidth="1.5"/>
                      <ellipse cx="9.5" cy="12" rx="1" ry="1.2" fill="currentColor"/>
                      <ellipse cx="14.5" cy="12" rx="1" ry="1.2" fill="currentColor"/>
                      <path d="M9.5 15.5 Q12 17.5 14.5 15.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
                      <path d="M3 4 L3.7 5.7 L5.4 6.4 L3.7 7.1 L3 8.8 L2.3 7.1 L0.6 6.4 L2.3 5.7Z" fill="currentColor"/>
                      <path d="M19.5 1.5 L20 2.7 L21.2 3.2 L20 3.7 L19.5 4.9 L19 3.7 L17.8 3.2 L19 2.7Z" fill="currentColor"/>
                      <path d="M21 9 L21.4 10 L22.4 10.4 L21.4 10.8 L21 11.8 L20.6 10.8 L19.6 10.4 L20.6 10Z" fill="currentColor"/>
                    </svg>
                  </button>
                  <button
                    className="control-btn vertical"
                    onClick={handleMagicRemovePlate}
                    disabled={aiHold || !effectiveImageId}
                    title="Remove Cake Plate / Board (AI)"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <ellipse cx="12" cy="15" rx="10" ry="3.5" fill="currentColor" opacity="0.25"/>
                      <ellipse cx="12" cy="14" rx="10" ry="3.5" fill="currentColor" opacity="0.5"/>
                      <ellipse cx="12" cy="13" rx="10" ry="3.5" fill="currentColor"/>
                      <ellipse cx="12" cy="13" rx="7" ry="2.2" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.5"/>
                    </svg>
                  </button>
                  <button
                    ref={colorBtnRef}
                    className={`control-btn vertical${showColorPopdown ? ' active' : ''}`}
                    onClick={(e) => {
                      const btnRect = e.currentTarget.getBoundingClientRect();
                      const PANEL_W = 230, PANEL_H = 80;
                      const vw = window.innerWidth, vh = window.innerHeight;
                      let left = btnRect.left;
                      if (left + PANEL_W > vw - 6) left = vw - PANEL_W - 6;
                      if (left < 6) left = 6;
                      let top = btnRect.bottom + 6;
                      if (top + PANEL_H > vh - 6) top = vh - PANEL_H - 6;
                      setColorPopdownPos({ top, left });
                      setMagicCakeColorBase(null);
                      setShowColorPopdown(v => !v);
                    }}
                    disabled={aiHold || !effectiveImageId}
                    title="Change Cake Colour (AI)"
                  ><FaPalette /></button>
                  <button
                    className={`control-btn vertical${magicSubTool === 'chat' ? ' active' : ''}`}
                    onClick={() => {
                      const next = magicSubTool === 'chat' ? null : 'chat';
                      setMagicSubTool(next);
                      setCurrentControlSection(null);
                      setShowTopControls(false);
                    }}
                    disabled={!online}
                    title={online ? 'AI Edit — describe the change' : 'AI Edit needs a connection'}
                  ><FaComment /></button>
                </>
              )}
              {/* Hologram — Admin/Super; cake tiers — Buyer/Super; name on cake — Buyer */}
              {isStaff && studioStamps.some(s => s.isHologram) && (() => {
                const _holo = studioStamps.find(s => s.isHologram);
                return (
                  <button
                    className="control-btn vertical"
                    onClick={handleHologramStamp}
                    disabled={isSaving}
                    title="Embed hologram stamp"
                  >
                    <img src={_holo.url} style={{ width: 18, height: 18, objectFit: 'contain' }} alt="hologram" />
                  </button>
                );
              })()}
              {canEditTiers && (
                <>
                  <button
                    className="control-btn vertical"
                    style={{ position: 'relative' }}
                    onClick={handleMagicAddTier}
                    disabled={aiHold || !effectiveImageId}
                    title="Add Cake Tier"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <ellipse cx="12" cy="19" rx="9" ry="2.3" fill="currentColor" opacity="0.9"/>
                      <ellipse cx="12" cy="13.5" rx="6.5" ry="2" fill="currentColor" opacity="0.75"/>
                      <ellipse cx="12" cy="9" rx="4" ry="1.6" fill="currentColor" opacity="0.6"/>
                      <path d="M12 9 L12 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                      <circle cx="12" cy="3.5" r="1" fill="currentColor"/>
                    </svg>
                    {(magicTierCounts[effectiveImageId] || 0) > 1 && (
                      <span style={{ position: 'absolute', top: 1, right: 1, fontSize: 9, lineHeight: 1, background: '#fa4848', color: '#fff', borderRadius: 7, minWidth: 12, height: 12, padding: '0 2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {magicTierCounts[effectiveImageId]}
                      </span>
                    )}
                  </button>
                  <button
                    className="control-btn vertical"
                    onClick={handleMagicRemoveTier}
                    disabled={aiHold || !effectiveImageId}
                    title="Remove Cake Tier"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <ellipse cx="12" cy="19" rx="9" ry="2.3" fill="currentColor" opacity="0.9"/>
                      <ellipse cx="12" cy="13.5" rx="6.5" ry="2" fill="currentColor" opacity="0.75"/>
                      <ellipse cx="12" cy="7.5" rx="4" ry="1.6" fill="none" stroke="currentColor" strokeWidth="1.1" strokeDasharray="2 1.6" opacity="0.85"/>
                      <path d="M17.5 5.5 L21.5 5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                    </svg>
                  </button>
                </>
              )}
              {userRole === 'Buyer' && (
                <>
                  <button
                    className={`control-btn vertical${showNamePopup ? ' active' : ''}`}
                    onClick={(e) => {
                      const btnRect = e.currentTarget.getBoundingClientRect();
                      const PANEL_W = 220, PANEL_H = 80;
                      const vw = window.innerWidth, vh = window.innerHeight;
                      let left = btnRect.left;
                      if (left + PANEL_W > vw - 6) left = vw - PANEL_W - 6;
                      if (left < 6) left = 6;
                      let top = btnRect.bottom + 6;
                      if (top + PANEL_H > vh - 6) top = vh - PANEL_H - 6;
                      setNamePopdownPos({ top, left });
                      setPendingNameValue(magicCakeNames[effectiveImageId] || '');
                      setShowNamePopup(v => !v);
                    }}
                    disabled={aiHold || !effectiveImageId}
                    title={magicCakeNames[effectiveImageId]
                      ? `Name on cake: "${magicCakeNames[effectiveImageId]}" — click to change`
                      : 'Add Name on Cake'}
                    style={{ position: 'relative' }}
                  >
                    <FaFont />
                    {magicCakeNames[effectiveImageId] && (
                      <span style={{ position: 'absolute', bottom: 0, right: 0, width: 6, height: 6, borderRadius: '50%', background: '#fa4848' }} />
                    )}
                  </button>
                </>
              )}
              <div className="magic-divider" />
              <button
                className="control-btn vertical"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
                onClick={async () => {
                  const img = canvasImagesRef.current[0] || canvasImages[0];
                  const src = img?.url || img?.src || '';
                  if (src) await stashReturnImage(src);
                  try { sessionStorage.setItem('studio_return_profile_id', location.state?.profileId || ''); } catch { /* non-fatal */ }
                  // Navigate whether or not the image could be stashed — never trap the user here.
                  navigate('/custom');
                }}
                title="Return to Custom"
              >
                <FaChevronLeft style={{ fontSize: 9 }} /><FaSlidersH style={{ fontSize: 11 }} />
              </button>
            </div>
            {aiStatusInTopBar && (
              <div style={{ overflow: 'hidden' }}>
                <div className={`magic-toolbar-status magic-toolbar-status--custom${magicChatError ? ' magic-toolbar-status--error' : ''}`}
                  onClick={magicChatError ? () => setMagicChatError('') : undefined}>
                  {magicChatError ? `${magicChatError}  ✕` : aiStatusText()}
                </div>
                {!magicChatError && aiBusy && (
                  <div style={{ height: 2, background: 'linear-gradient(90deg, transparent, #4a90e2, #7b5ea7, #4a90e2, transparent)', backgroundSize: '200% 100%', animation: 'magic-bar-slide 1.2s linear infinite' }} />
                )}
              </div>
            )}
          </div>
        )}
        {/* TOP TOOLBAR - connect | inline controls | actions + close */}
        <div className={`is-top-toolbar${anySelection ? ' is-has-selection' : ''}`} style={{ display: fromCustom ? 'none' : undefined }}>
          {/* LEFT: Connect button */}
          <div className="is-top-toolbar-left" style={{ display: fromCustom || hideTopChrome ? 'none' : '' }}>
            <div className="connect-menu-wrapper" ref={connectMenuRef}>
              <button
                className="control-btn vertical"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsConnectMenuOpen(!isConnectMenuOpen);
                }}
                title="Add Image"
              >
                <FaPlus />
              </button>
              {isConnectMenuOpen && (
                <div
                  className="connect-dropdown-vertical"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="connect-dropdown-item"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleConnectOption('local'); }}
                  >
                    <FaFolder style={{marginRight:8,opacity:0.7}}/> Folder
                  </button>
                  <button
                    className="connect-dropdown-item"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleConnectOption('camera'); }}
                  >
                    <FaCamera style={{marginRight:8,opacity:0.7}}/> Selfie Mode
                  </button>
                  <button
                    className="connect-dropdown-item"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleConnectOption('bucket'); }}
                  >
                    <FaCloud style={{marginRight:8,opacity:0.7}}/> Cloud
                  </button>
                  <button
                    className="connect-dropdown-item"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleConnectOption('upload-cloud'); }}
                  >
                    <FaCloudUploadAlt style={{marginRight:8,opacity:0.7}}/> Upload to Cloud
                  </button>

                </div>
              )}
            </div>
          </div>

          {/* MIDDLE: Inline control section */}
          <div className="is-top-toolbar-controls" ref={topControlsRef}>
            {pendingNavPath && (
              <div className="unsaved-confirm-section">
                <span className="unsaved-warn-text">Unsaved changes !</span>
              </div>
            )}
            {pendingClear && !pendingNavPath && (
              <div className="unsaved-confirm-section">
                <span className="unsaved-warn-text">Clear everything? Tap the broom again</span>
              </div>
            )}
            {flashMsg && !pendingClear && !pendingNavPath && (
              <div className="unsaved-confirm-section">
                <span className="is-flash-text">{flashMsg}</span>
              </div>
            )}
            {isSaving && (
              <div className="save-progress-container">
                <div className="save-progress-bar">
                  <div
                    className="save-progress-fill"
                    style={{ width: `${saveProgress}%` }}
                  />
                </div>
                <div className="save-progress-text">{saveProgress}%</div>
              </div>
            )}

            {/* AI progress / result, shown here (top toolbar) instead of down by the bottom
                bar -- that strip sat under the canvas where it was easy to miss while working.
                Takes over this whole middle section (including in AI Edit's own chat mode, not
                just the toolbar-button actions) for as long as a request is in flight or its
                result/error is still showing; the position/size "coordinates" box below steps
                aside for it and comes back once this clears. */}
            {aiStatusInTopBar && (
              <div style={{ overflow: 'hidden', width: '100%' }}>
                <div className={`magic-toolbar-status${magicChatError ? ' magic-toolbar-status--error' : ''}`}
                  onClick={magicChatError ? () => setMagicChatError('') : undefined}>
                  {magicChatError ? `${magicChatError}  ✕` : aiStatusText()}
                </div>
                {!magicChatError && aiBusy && (
                  <div style={{ height: 2, background: 'linear-gradient(90deg, transparent, #4a90e2, #7b5ea7, #4a90e2, transparent)', backgroundSize: '200% 100%', animation: 'magic-bar-slide 1.2s linear infinite' }} />
                )}
              </div>
            )}

            {/* Magic wand status now shown inside magic-wand-toolbar row */}

            {currentControlSection === 'draw' && !topBarMessage && (
              <div className="draw-controls">
                <div className="draw-controls-row">
                    {/* Tool selector dropdown */}
                    <button
                      ref={drawPaletteBtnRef}
                      className="color-palette-btn"
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => {
                        const r = drawPaletteBtnRef.current.getBoundingClientRect();
                        setDrawPalettePos({ top: r.bottom + 6, left: r.left });
                        setShowDrawPalette(p => !p);
                      }}
                      title="Drawing tool"
                      style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px' }}
                    >
                      <DrawToolIcon mode={drawingMode} />
                    </button>

                    {/* Colour — shows only the current colour dot; opens the colour pop-down.
                        The eraser has no colour, so it doesn't get one. */}
                    {drawingMode !== 'eraser' && (
                    <button
                      className="color-palette-btn"
                      onMouseDown={e => e.preventDefault()}
                      onClick={(e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        setColorMenuPos({ top: r.bottom + 6, left: r.left });
                        setShowColorPicker(true);
                      }}
                      title="Colour"
                      style={{ display: 'flex', alignItems: 'center', padding: '4px 8px' }}
                    >
                      <div style={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: drawingColor, flexShrink: 0, border: '1px solid rgba(128,128,128,0.4)' }} />
                    </button>
                    )}

                    {/* Brush size — opens size menu */}
                    {!(selectedImage && canvasImages.find(i => i.id === selectedImage)?.name === 'fill') && (
                      <button
                        ref={brushSizeBtnRef}
                        className="color-palette-btn"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => {
                          const r = brushSizeBtnRef.current.getBoundingClientRect();
                          setBrushMenuPos({ top: r.bottom + 6, left: r.left + r.width / 2 });
                          setShowBrushSizeMenu(p => !p);
                        }}
                        title="Brush size"
                        style={{ display: 'flex', alignItems: 'center', padding: '4px 8px' }}
                      >
                        <svg width="26" height="12" style={{ display: 'block' }}>
                          <line x1="2" y1="6" x2="24" y2="6"
                            stroke="currentColor"
                            strokeWidth={Math.min(brushSize * 0.75, 9)}
                            strokeLinecap="round" />
                        </svg>
                      </button>
                    )}
                  </div>
              </div>
            )}

            {/* ---- SELECT/MOVE controls: position / rotate / resize (persistent after action stops) ---- */}
            {currentControlSection === 'selectmove' && !topBarMessage && (() => {
              const ps = PRINT_SIZES[printSize];
              const step = 0.1;
              const dir = lastDragDirRef.current;

              if (selectedShapeIds.size > 1) {
                return null;
              }

              if (selectedShapeId) {
                const selShape = canvasShapes.find(s => s.id === selectedShapeId);
                if (!selShape) {
                  // Not a figure, so it is a hand-drawn stroke: the same three readouts, taken
                  // off the stroke's own bounding box. Read-only here -- a stroke is a cloud of
                  // points with no x1/y1/x2/y2 to type into, so it is moved, sized and turned by
                  // dragging it and its handles.
                  const selInk = drawingPaths.find(p => p.id === selectedShapeId);
                  const inkBox = selInk ? inkPathBBoxPct(selInk) : null;
                  if (!inkBox) return null;
                  const roStyle = { padding: '0 6px', fontSize: 12, minWidth: 34, textAlign: 'center' };
                  if (selectMoveControlMode === 'rotate') {
                    return (
                      <div className="dimension-controls-row">
                        <div className="dimension-control compact">
                          <span className="dim-label">A&deg;</span>
                          <span style={roStyle}>{Math.round(((inkGestureAngle % 360) + 360) % 360)}</span>
                        </div>
                      </div>
                    );
                  }
                  if (selectMoveControlMode === 'resize') {
                    return (
                      <div className="dimension-controls-row">
                        <button
                          className={`aspect-ratio-btn${lockAspectRatio ? ' active' : ''}`}
                          onClick={() => setLockAspectRatio(v => !v)}
                          title={lockAspectRatio ? 'Unlock aspect ratio' : 'Lock aspect ratio'}
                        >
                          {lockAspectRatio ? <FaLock /> : <FaUnlock />}
                        </button>
                        <div className="dimension-control compact">
                          <span className="dim-label">W</span>
                          <span style={roStyle}>{parseFloat(((inkBox.x2 - inkBox.x1) / 100 * ps.width).toFixed(1))}</span>
                        </div>
                        <div className="dimension-control compact">
                          <span className="dim-label">H</span>
                          <span style={roStyle}>{parseFloat(((inkBox.y2 - inkBox.y1) / 100 * ps.height).toFixed(1))}</span>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div className="dimension-controls-row">
                      <div className="dimension-control compact">
                        <span className="dim-label">X</span>
                        <span style={roStyle}>{parseFloat((inkBox.x1 / 100 * ps.width).toFixed(1))}</span>
                      </div>
                      <div className="dimension-control compact">
                        <span className="dim-label">Y</span>
                        <span style={roStyle}>{parseFloat(((100 - inkBox.y1) / 100 * ps.height).toFixed(1))}</span>
                      </div>
                    </div>
                  );
                }

                if (selectMoveControlMode === 'rotate') {
                  const wrap = (v) => Math.round(((v || 0) % 360 + 360) % 360);
                  const curA = wrap(selShape.rotation);
                  const setAngle = (field, v) => setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, [field]: v } : s));
                  const changeA = (newA) => setAngle('rotation', newA);
                  // A 3D object turns in space, so it gets all three angles: X tilts it
                  // forward and back, Y spins it left and right, A is the flat in-plane turn
                  // every figure has. Dragging the orbit handle under the object sets X and Y.
                  if (isShape3D(selShape.type)) {
                    const _p3 = pose3D(selShape);
                    const curX = wrap(_p3.rx), curY = wrap(_p3.ry);
                    const box = (label, val, field) => (
                      <div className="dimension-control compact" key={field}>
                        <span className="dim-label">{label}</span>
                        <button className="dim-step-btn" onClick={() => setAngle(field, val - 5)}>-</button>
                        <input type="number" value={val} step={1} onChange={e => setAngle(field, Number(e.target.value))} />
                        <button className="dim-step-btn" onClick={() => setAngle(field, val + 5)}>+</button>
                      </div>
                    );
                    return (
                      <div className="dimension-controls-row">
                        {box('X°', curX, 'rotX')}
                        {box('Y°', curY, 'rotY')}
                        {box('A°', curA, 'rotation')}
                      </div>
                    );
                  }
                  return (
                    <div className="dimension-controls-row">
                      <div className="dimension-control compact">
                        <span className="dim-label">A°</span>
                        <button className="dim-step-btn" onClick={() => changeA(curA - 1)}>-</button>
                        <input type="number" value={curA} step={1} onChange={e => changeA(Number(e.target.value))} />
                        <button className="dim-step-btn" onClick={() => changeA(curA + 1)}>+</button>
                      </div>
                    </div>
                  );
                }

                if (selectMoveControlMode === 'resize') {
                  if (selShape.type === 'line' || selShape.type === 'arrow') {
                    const dx_cm = (selShape.x2 - selShape.x1) / 100 * ps.width;
                    const dy_cm = (selShape.y2 - selShape.y1) / 100 * ps.height;
                    const oldL = Math.sqrt(dx_cm * dx_cm + dy_cm * dy_cm);
                    const curL = parseFloat(oldL.toFixed(1));
                    const changeL = (newL) => {
                      if (oldL === 0) return;
                      const scale = newL / oldL;
                      setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, x2: s.x1 + (s.x2 - s.x1) * scale, y2: s.y1 + (s.y2 - s.y1) * scale } : s));
                    };
                    const curA = Math.round(((-Math.atan2(dy_cm, dx_cm) * 180 / Math.PI) % 360 + 360) % 360);
                    const changeA = (newA) => {
                      const rad = -newA * Math.PI / 180;
                      const len = oldL;
                      setCanvasShapes(prev => prev.map(s => {
                        if (s.id !== selectedShapeId) return s;
                        return { ...s, x2: s.x1 + (len * Math.cos(rad)) / ps.width * 100, y2: s.y1 + (len * Math.sin(rad)) / ps.height * 100 };
                      }));
                    };
                    return (
                      <div className="dimension-controls-row">
                        <div className="dimension-control compact">
                          <span className="dim-label">L</span>
                          <button className="dim-step-btn" onClick={() => changeL(Math.max(0.1, curL - step))}>-</button>
                          <input type="number" value={curL} min="0.1" step={step} onChange={e => changeL(Math.max(0.1, Number(e.target.value)))} />
                          <button className="dim-step-btn" onClick={() => changeL(curL + step)}>+</button>
                        </div>
                        <div className="dimension-control compact">
                          <span className="dim-label">A°</span>
                          <button className="dim-step-btn" onClick={() => changeA(curA - 1)}>-</button>
                          <input type="number" value={curA} step={1} onChange={e => changeA(Number(e.target.value))} />
                          <button className="dim-step-btn" onClick={() => changeA(curA + 1)}>+</button>
                        </div>
                      </div>
                    );
                  } else {
                    const curW = parseFloat((Math.abs(selShape.x2 - selShape.x1) / 100 * ps.width).toFixed(1));
                    const curH = parseFloat((Math.abs(selShape.y2 - selShape.y1) / 100 * ps.height).toFixed(1));
                    const signX = selShape.x2 >= selShape.x1 ? 1 : -1;
                    const signY = selShape.y2 >= selShape.y1 ? 1 : -1;
                    return (
                      <div className="dimension-controls-row">
                        <button
                          className={`aspect-ratio-btn${lockAspectRatio ? ' active' : ''}`}
                          onClick={() => setLockAspectRatio(v => !v)}
                          title={lockAspectRatio ? 'Unlock aspect ratio' : 'Lock aspect ratio'}
                        >
                          {lockAspectRatio ? <FaLock /> : <FaUnlock />}
                        </button>
                        <div className="dimension-control compact">
                          <span className="dim-label">W</span>
                          <button className="dim-step-btn" onClick={() => setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, x2: s.x1 + signX * Math.max(0.1, curW - step) * 100 / ps.width, ...(lockAspectRatio && curH > 0 ? { y2: s.y1 + signY * Math.max(0.1, curW - step) * (curH / curW) * 100 / ps.height } : {}) } : s))}>-</button>
                          <input type="number" value={curW} min="0.1" step={step} onChange={e => { const nw = Math.max(0.1, Number(e.target.value)); setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, x2: s.x1 + signX * nw * 100 / ps.width, ...(lockAspectRatio && curH > 0 ? { y2: s.y1 + signY * nw * (curH / curW) * 100 / ps.height } : {}) } : s)); }} />
                          <button className="dim-step-btn" onClick={() => setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, x2: s.x1 + signX * (curW + step) * 100 / ps.width, ...(lockAspectRatio && curH > 0 ? { y2: s.y1 + signY * (curW + step) * (curH / curW) * 100 / ps.height } : {}) } : s))}>+</button>
                        </div>
                        <div className="dimension-control compact">
                          <span className="dim-label">H</span>
                          <button className="dim-step-btn" onClick={() => setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, y2: s.y1 + signY * Math.max(0.1, curH - step) * 100 / ps.height, ...(lockAspectRatio && curW > 0 ? { x2: s.x1 + signX * Math.max(0.1, curH - step) * (curW / curH) * 100 / ps.width } : {}) } : s))}>-</button>
                          <input type="number" value={curH} min="0.1" step={step} onChange={e => { const nh = Math.max(0.1, Number(e.target.value)); setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, y2: s.y1 + signY * nh * 100 / ps.height, ...(lockAspectRatio && curW > 0 ? { x2: s.x1 + signX * nh * (curW / curH) * 100 / ps.width } : {}) } : s)); }} />
                          <button className="dim-step-btn" onClick={() => setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, y2: s.y1 + signY * (curH + step) * 100 / ps.height, ...(lockAspectRatio && curW > 0 ? { x2: s.x1 + signX * (curH + step) * (curW / curH) * 100 / ps.width } : {}) } : s))}>+</button>
                        </div>
                      </div>
                    );
                  }
                }

                // Default: X/Y position (always left/top edge for stable display)
                const edgeX = Math.min(selShape.x1, selShape.x2);
                const edgeY = Math.min(selShape.y1, selShape.y2);
                const xCm = parseFloat((edgeX / 100 * ps.width).toFixed(1));
                const yCmFromBottom = parseFloat(((100 - edgeY) / 100 * ps.height).toFixed(1));
                const changeX = (newX) => {
                  const newPct = Math.max(0, Math.min(100, newX / ps.width * 100));
                  const delta = newPct - edgeX;
                  setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, x1: s.x1 + delta, x2: s.x2 + delta } : s));
                };
                const changeY = (newYBottom) => {
                  const newYTopPct = Math.max(0, Math.min(100, 100 - newYBottom / ps.height * 100));
                  const delta = newYTopPct - edgeY;
                  setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, y1: s.y1 + delta, y2: s.y2 + delta } : s));
                };
                return (
                  <div className="dimension-controls-row">
                    <div className="dimension-control compact">
                      <span className="dim-label">X</span>
                      <button className="dim-step-btn" onClick={() => changeX(xCm - step)}>-</button>
                      <input type="number" value={xCm} step={step} onChange={e => changeX(Number(e.target.value))} />
                      <button className="dim-step-btn" onClick={() => changeX(xCm + step)}>+</button>
                    </div>
                    <div className="dimension-control compact">
                      <span className="dim-label">Y</span>
                      <button className="dim-step-btn" onClick={() => changeY(yCmFromBottom - step)}>-</button>
                      <input type="number" value={yCmFromBottom} step={step} onChange={e => changeY(Number(e.target.value))} />
                      <button className="dim-step-btn" onClick={() => changeY(yCmFromBottom + step)}>+</button>
                    </div>
                  </div>
                );
              }

              if (selectedImage) {
                const img = canvasImages.find(i => i.id === selectedImage);
                if (!img) return null;

                // Rotate mode
                if (selectMoveControlMode === 'rotate') {
                  const curA = Math.round(((img.rotation || 0) % 360 + 360) % 360);
                  const changeImgA = (newA) => setCanvasImages(prev => prev.map(i => i.id === selectedImage ? { ...i, rotation: newA } : i));
                  // X tilts the picture forward and back, Y spins it left and right — the same
                  // pair a 3D figure has, set here by number or by the blue handle under it.
                  const wrapT = (v) => Math.round(((v || 0) % 360 + 360) % 360);
                  const turn = turnAnglesOf(img);
                  const curTX = wrapT(turn.rx), curTY = wrapT(turn.ry);
                  const changeTurn = (rx, ry) => { bakeImageTurn(selectedImage, rx, ry); };
                  const turnBox = (label, val, onSet) => (
                    <div className="dimension-control compact" key={label}>
                      <span className="dim-label">{label}</span>
                      <button className="dim-step-btn" onClick={() => onSet(val - 5)}>-</button>
                      <input type="number" value={val} step={1} onChange={e => onSet(Number(e.target.value))} />
                      <button className="dim-step-btn" onClick={() => onSet(val + 5)}>+</button>
                    </div>
                  );
                  return (
                    <div className="dimension-controls-row">
                      {turnBox('X°', curTX, (v) => changeTurn(v, turn.ry))}
                      {turnBox('Y°', curTY, (v) => changeTurn(turn.rx, v))}
                      <div className="dimension-control compact">
                        <span className="dim-label">A°</span>
                        <button className="dim-step-btn" onClick={() => changeImgA(curA - 1)}>-</button>
                        <input type="number" value={curA} step={1} onChange={e => changeImgA(Number(e.target.value))} />
                        <button className="dim-step-btn" onClick={() => changeImgA(curA + 1)}>+</button>
                      </div>
                    </div>
                  );
                }

                // Resize mode: show W/H controls
                if (selectMoveControlMode === 'resize') {
                  const dispW = parseFloat(((resizeWidth / 21) * ps.width).toFixed(1));
                  const dispH = parseFloat(((resizeHeight / 29.7) * ps.height).toFixed(1));
                  // Aspect ratio (use current display ratio, not natural pixel ratio)
                  const _ar = resizeHeight > 0 ? resizeWidth / resizeHeight : 1;
                  const changeW = (newWcm) => {
                    const w = Math.max(0.1, Math.min(ps.width, newWcm)) * 21 / ps.width;
                    if (lockAspectRatio) {
                      const h = Math.min(29.7, Math.max(0.1, w / _ar));
                      setResizeWidth(w);
                      setResizeHeight(h);
                    } else {
                      setResizeWidth(w);
                    }
                  };
                  const changeH = (newHcm) => {
                    const h = Math.max(0.1, Math.min(ps.height, newHcm)) * 29.7 / ps.height;
                    if (lockAspectRatio) {
                      const w = Math.min(21, Math.max(0.1, h * _ar));
                      setResizeHeight(h);
                      setResizeWidth(w);
                    } else {
                      setResizeHeight(h);
                    }
                  };
                  return (
                    <div className="dimension-controls-row">
                      <button
                        className={`aspect-ratio-btn${lockAspectRatio ? ' active' : ''}`}
                        onClick={() => setLockAspectRatio(v => !v)}
                        title={lockAspectRatio ? 'Unlock aspect ratio' : 'Lock aspect ratio'}
                      >
                        {lockAspectRatio ? <FaLock /> : <FaUnlock />}
                      </button>
                      <div className="dimension-control compact">
                        <span className="dim-label">W</span>
                        <button className="dim-step-btn" onClick={() => changeW(dispW - step)}>-</button>
                        <input type="number" value={dispW} min="0.1" max={ps.width} step={step} onChange={e => changeW(Number(e.target.value))} />
                        <button className="dim-step-btn" onClick={() => changeW(dispW + step)}>+</button>
                      </div>
                      <div className="dimension-control compact">
                        <span className="dim-label">H</span>
                        <button className="dim-step-btn" onClick={() => changeH(dispH - step)}>-</button>
                        <input type="number" value={dispH} min="0.1" max={ps.height} step={step} onChange={e => changeH(Number(e.target.value))} />
                        <button className="dim-step-btn" onClick={() => changeH(dispH + step)}>+</button>
                      </div>
                    </div>
                  );
                }

                // Position mode: show X/Y
                const imgWPct = (img.width / 21) * 100;
                const imgHPct = (img.height / 29.7) * 100;
                const xEdgePct = img.x;
                const yEdgePct = img.y;
                const xCm = parseFloat((xEdgePct / 100 * ps.width).toFixed(1));
                const yCmFromBottom = parseFloat(((100 - yEdgePct) / 100 * ps.height).toFixed(1));
                const changeImgX = (newX) => {
                  const newPct = Math.max(0, Math.min(100, newX / ps.width * 100));
                  const delta = newPct - xEdgePct;
                  setCanvasImages(prev => prev.map(i => i.id === selectedImage ? { ...i, x: i.x + delta } : i));
                };
                const changeImgY = (newYBottom) => {
                  const newYTopPct = Math.max(0, Math.min(100, 100 - newYBottom / ps.height * 100));
                  const delta = newYTopPct - yEdgePct;
                  setCanvasImages(prev => prev.map(i => i.id === selectedImage ? { ...i, y: i.y + delta } : i));
                };
                return (
                  <div className="dimension-controls-row">
                    <div className="dimension-control compact">
                      <span className="dim-label">X</span>
                      <button className="dim-step-btn" onClick={() => changeImgX(xCm - step)}>-</button>
                      <input type="number" value={xCm} step={step} onChange={e => changeImgX(Number(e.target.value))} />
                      <button className="dim-step-btn" onClick={() => changeImgX(xCm + step)}>+</button>
                    </div>
                    <div className="dimension-control compact">
                      <span className="dim-label">Y</span>
                      <button className="dim-step-btn" onClick={() => changeImgY(yCmFromBottom - step)}>-</button>
                      <input type="number" value={yCmFromBottom} step={step} onChange={e => changeImgY(Number(e.target.value))} />
                      <button className="dim-step-btn" onClick={() => changeImgY(yCmFromBottom + step)}>+</button>
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            {/* ---- SHAPE DRAW controls (line/arrow/circle/triangle/rectangle) ---- */}
            {(currentControlSection === 'shapes-draw' || currentControlSection === 'shapes-text')
              && (!!selectedShapeId || selectedShapeIds.size > 0) && !topBarMessage && (() => {
              const isText = currentControlSection === 'shapes-text';
              // The selection is a hand-drawn stroke, not a figure: selectedShapeId holds ids
              // from both, so a miss in canvasShapes and a hit in drawingPaths is what tells
              // them apart. A stroke has one colour and no fill, border style or font, so this
              // strips the panel down to the colour swatch alone.
              const isInkSel = !!selectedShapeId
                && !canvasShapes.some(s => s.id === selectedShapeId)
                && drawingPaths.some(p => p.id === selectedShapeId);
              const setShapeColor = applyShapeColour;
              return (
                <div className="draw-controls">
                  {isText ? (
                    /* One line, never two. This row used to wrap, and a second line inside a bar
                       46 pixels tall has nowhere to go but out of the bottom of it — which is the
                       font box hanging below the toolbar. It scrolls sideways instead, like every
                       other row up here. */
                    <div className="draw-controls-row is-text-controls" style={{ flexWrap: 'nowrap', gap: 4 }}>
                      {/* The typeface, bold and italic are not up here any more: they are the
                          three things you set once and rarely touch again, and between them the
                          font box alone took a third of a phone's top bar away from the size,
                          outline and colour controls that get used on every line. They live
                          together under one button in the row below — see the "font" group. */}
                      {/* Size. A phone has no room for a stepper, so one button says what the size
                          is and opens the list. */}
                      <button
                        ref={fontSizeBtnRef}
                        className="color-palette-btn is-font-size-btn"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => {
                          const r = fontSizeBtnRef.current.getBoundingClientRect();
                          setFontMenuPos({ top: r.bottom + 4, left: r.left + r.width / 2 });
                          setShowFontSizeMenu(v => !v);
                        }}
                        title="Font size"
                      >
                        <span className="is-font-size-btn__a">A</span>
                        <span className="is-font-size-btn__n">{shapeFontSize}</span>
                      </button>
                      {/* The letters' own outline — the figures' line-thickness control, doing the
                          same job for text. Nothing has one until it is asked for. */}
                      <button
                        className="color-palette-btn"
                        onMouseDown={e => e.preventDefault()}
                        onClick={(e) => {
                          const r = e.currentTarget.getBoundingClientRect();
                          setStrokeMenuPos({ top: r.bottom + 4, left: r.left + r.width / 2 });
                          setStrokeMenuFor('text');
                          setShowStrokeWidthMenu(v => !v);
                        }}
                        title="Outline thickness"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <span className="is-outline-a" style={{ WebkitTextStrokeWidth: `${Math.min(textOutline, 3) * 0.6}px` }}>A</span>
                      </button>
                      <div className="color-picker-container">
                        <button className="color-palette-btn"
                          onClick={(e) => {
                            const r = e.currentTarget.getBoundingClientRect();
                            setColorMenuPos({ top: r.bottom + 6, left: r.left });
                            setShapeColorTarget('stroke'); setShowShapeColorPicker(true);
                          }}
                          title="Text Color">
                          <FaPalette />
                          <div className="current-color-indicator" style={{ backgroundColor: shapeStrokeColor }} />
                        </button>
                      </div>
                      {/* The outline's colour only matters once there IS an outline, so it turns
                          up then rather than sitting there meaning nothing. */}
                      {textOutline > 0 && (
                        <div className="color-picker-container">
                          <button className="color-palette-btn"
                            onClick={(e) => {
                              const r = e.currentTarget.getBoundingClientRect();
                              setColorMenuPos({ top: r.bottom + 6, left: r.left });
                              setShapeColorTarget('fill'); setShowShapeColorPicker(true);
                            }}
                            title="Outline colour">
                            <FaTint />
                            <div className="current-color-indicator" style={{ backgroundColor: shapeFillColor === 'none' ? '#ffffff' : shapeFillColor, border: '1px solid #ccc' }} />
                          </button>
                        </div>
                      )}
                      {/* New line. A phone keyboard shows "go", not a return key, and enter here
                          finishes the text — so without this there is no way to start a second
                          line while typing. */}
                      {pendingTextPos && (
                        <button
                          className="color-palette-btn"
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => {
                            const ta = document.querySelector('textarea[placeholder="Type text..."]');
                            if (!ta) return;
                            const at = ta.selectionStart ?? pendingTextValue.length;
                            const next = `${pendingTextValue.slice(0, at)}\n${pendingTextValue.slice(ta.selectionEnd ?? at)}`;
                            setPendingTextValue(next);
                            requestAnimationFrame(() => { ta.focus(); ta.setSelectionRange(at + 1, at + 1); });
                          }}
                          title="New line"
                        >
                          <FaLevelDownAlt style={{ transform: 'rotate(90deg)' }} />
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                    {isRotatingShape && selectedShapeId && (() => {
                      const selShape = canvasShapes.find(s => s.id === selectedShapeId);
                      if (!selShape) return null;
                      const curA = Math.round(((selShape.rotation || 0) % 360 + 360) % 360);
                      const changeA = (newA) => setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, rotation: newA } : s));
                      return (
                        <div className="dimension-controls-row" style={{ marginTop: 4 }}>
                          <div className="dimension-control compact">
                            <span className="dim-label">A°</span>
                            <button className="dim-step-btn" onClick={() => changeA(curA - 1)}>-</button>
                            <input type="number" value={curA} step={1} onChange={e => changeA(Number(e.target.value))} />
                            <button className="dim-step-btn" onClick={() => changeA(curA + 1)}>+</button>
                          </div>
                        </div>
                      );
                    })()}
                    {!isResizingShape && !isRotatingShape && <div className="draw-controls-row">
                      <div className="color-picker-container">
                        <button className="color-palette-btn"
                          onClick={(e) => {
                            const r = e.currentTarget.getBoundingClientRect();
                            setColorMenuPos({ top: r.bottom + 6, left: r.left });
                            setShapeColorTarget('stroke'); setShowShapeColorPicker(true);
                          }}
                          title={isInkSel ? 'Line colour' : 'Border Color'}>
                          <FaMinus style={{ fontSize: 14 }} />
                          <div className="current-color-indicator" style={{ backgroundColor: shapeStrokeColor }} />
                        </button>
                      </div>
                      {!isInkSel && (['circle', 'triangle', 'rectangle', 'freeshape'].includes(activeShapeTool) || isShape3D(activeShapeTool)
                        || (isShape2D(activeShapeTool) && !SHAPES_2D[activeShapeTool].open)
                        || (activeShapeTool === 'freeform' && isFreeformClosed(canvasShapes.find(sh => sh.id === selectedShapeId)))) && (
                        <div className="color-picker-container">
                          <button className="color-palette-btn"
                            onClick={(e) => {
                              const r = e.currentTarget.getBoundingClientRect();
                              setColorMenuPos({ top: r.bottom + 6, left: r.left });
                              setShapeColorTarget('fill'); setShowShapeColorPicker(true);
                            }}
                            title={isShape3D(activeShapeTool) ? 'Body colour' : 'Fill Color'}>
                            <FaTint style={{ fontSize: 14 }} />
                            <div className="current-color-indicator" style={{ backgroundColor: shapeFillColor === 'none' ? 'transparent' : shapeFillColor, border: '1px solid #ccc' }} />
                          </button>
                        </div>
                      )}
                      {/* Line weight and dash style are figure properties -- a hand-drawn
                          stroke's thickness belongs to the pen that drew it, so this stays
                          out of the way when the selection is ink. */}
                      {!isInkSel && <div className="brush-size-control">
                        <button
                          ref={strokeWidthBtnRef}
                          className="color-palette-btn"
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => {
                            const r = strokeWidthBtnRef.current.getBoundingClientRect();
                            setStrokeMenuPos({ top: r.bottom + 4, left: r.left + r.width / 2 });
                            setStrokeMenuFor('shape');
                            setShowStrokeWidthMenu(p => !p);
                          }}
                          title="Stroke Width"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <svg width="22" height="12" style={{ display: 'block' }}>
                            <line x1="1" y1="6" x2="21" y2="6" stroke="currentColor"
                              strokeWidth={Math.min(Math.max(shapeStrokeWidth * 1.5, 1), 10)}
                              strokeLinecap="round"
                              strokeDasharray={shapeDashed ? '5 3' : undefined} />
                          </svg>
                        </button>
                      </div>}
                    </div>}
                    {isResizingShape && selectedShapeId && (() => {
                      const selShape = canvasShapes.find(s => s.id === selectedShapeId);
                      if (!selShape) return null;
                      const ps = PRINT_SIZES[printSize];
                      const step = 0.1;
                      if (selShape.type === 'line' || selShape.type === 'arrow') {
                        const dx_cm = (selShape.x2 - selShape.x1) / 100 * ps.width;
                        const dy_cm = (selShape.y2 - selShape.y1) / 100 * ps.height;
                        const oldL = Math.sqrt(dx_cm * dx_cm + dy_cm * dy_cm);
                        const curL = parseFloat(oldL.toFixed(1));
                        const changeL = (newL) => {
                          if (oldL === 0) return;
                          const scale = newL / oldL;
                          setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, x2: s.x1 + (s.x2 - s.x1) * scale, y2: s.y1 + (s.y2 - s.y1) * scale } : s));
                        };
                        const curA = Math.round(((-Math.atan2(dy_cm, dx_cm) * 180 / Math.PI) % 360 + 360) % 360);
                        const changeA = (newA) => {
                          const rad = -newA * Math.PI / 180;
                          const len = oldL;
                          setCanvasShapes(prev => prev.map(s => {
                            if (s.id !== selectedShapeId) return s;
                            return { ...s, x2: s.x1 + (len * Math.cos(rad)) / ps.width * 100, y2: s.y1 + (len * Math.sin(rad)) / ps.height * 100 };
                          }));
                        };
                        return (
                          <div className="dimension-controls-row" style={{ marginTop: 4 }}>
                            <div className="dimension-control compact">
                              <span className="dim-label">L</span>
                              <button className="dim-step-btn" onClick={() => changeL(Math.max(0.1, curL - step))}>-</button>
                              <input type="number" value={curL} min="0.1" step={step} onChange={e => changeL(Math.max(0.1, Number(e.target.value)))} />
                              <button className="dim-step-btn" onClick={() => changeL(curL + step)}>+</button>
                            </div>
                            <div className="dimension-control compact">
                              <span className="dim-label">A&deg;</span>
                              <button className="dim-step-btn" onClick={() => changeA(curA - 1)}>-</button>
                              <input type="number" value={curA} step={1} onChange={e => changeA(Number(e.target.value))} />
                              <button className="dim-step-btn" onClick={() => changeA(curA + 1)}>+</button>
                            </div>
                          </div>
                        );
                      } else {
                        const curW = parseFloat((Math.abs(selShape.x2 - selShape.x1) / 100 * ps.width).toFixed(1));
                        const curH = parseFloat((Math.abs(selShape.y2 - selShape.y1) / 100 * ps.height).toFixed(1));
                        const signX = selShape.x2 >= selShape.x1 ? 1 : -1;
                        const signY = selShape.y2 >= selShape.y1 ? 1 : -1;
                        // Typing a size here has to move the picture with the box, exactly as
                        // dragging a corner does — see the drag-resize twin. Writing keeps the
                        // size it was set to; an emoji, stamp or smiley character, fills its box.
                        const sizeToBox = (ns) => {
                          const wPx = Math.abs(ns.x2 - ns.x1) / 100 * (canvasRef.current?.offsetWidth  || 595);
                          const hPx = Math.abs(ns.y2 - ns.y1) / 100 * (canvasRef.current?.offsetHeight || 841);
                          if (ns.type === 'emoji') {
                            const f = Math.max(8, Math.round(hPx));
                            ns.fontSize = f; ns.emojiSize = f;
                          } else if (isEmojiGlyph(ns)) {
                            ns.fontSize = emojiFsForBox(wPx, hPx);
                          }
                          return ns;
                        };
                        const applyW = (newW_cm) => setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId
                          ? sizeToBox({ ...s, x2: s.x1 + signX * Math.max(0.1, newW_cm) * 100 / ps.width })
                          : s));
                        const applyH = (newH_cm) => {
                          let shown = null;
                          setCanvasShapes(prev => prev.map(s => {
                            if (s.id !== selectedShapeId) return s;
                            const ns = sizeToBox({ ...s, y2: s.y1 + signY * Math.max(0.1, newH_cm) * 100 / ps.height });
                            shown = ns.fontSize;
                            return ns;
                          }));
                          if ((selShape.type === 'emoji' || isEmojiGlyph(selShape)) && shown) setShapeFontSize(shown);
                        };
                        return (
                          <div className="dimension-controls-row" style={{ marginTop: 4 }}>
                            <div className="dimension-control compact">
                              <span className="dim-label">W</span>
                              <button className="dim-step-btn" onClick={() => applyW(curW - step)}>-</button>
                              <input type="number" value={curW} min="0.1" step={step} onChange={e => applyW(Number(e.target.value))} />
                              <button className="dim-step-btn" onClick={() => applyW(curW + step)}>+</button>
                            </div>
                            <div className="dimension-control compact">
                              <span className="dim-label">H</span>
                              <button className="dim-step-btn" onClick={() => applyH(curH - step)}>-</button>
                              <input type="number" value={curH} min="0.1" step={step} onChange={e => applyH(Number(e.target.value))} />
                              <button className="dim-step-btn" onClick={() => applyH(curH + step)}>+</button>
                            </div>
                          </div>
                        );
                      }
                    })()}
                    </>
                  )}
                  {(showShapeColorPicker || showShapeColorShades) && createPortal(
                    <div
                      onMouseDown={e => e.preventDefault()}
                      style={{
                        position: 'fixed', top: colorMenuPos.top, left: colorMenuPos.left,
                        zIndex: 9999,
                        background: 'var(--bg-surface, #fff)',
                        border: '1px solid var(--border-color, #ddd)',
                        borderRadius: 8, padding: 6,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                      }}
                    >
                      {showShapeColorShades ? (
                        <div className="color-shades-controls">
                          {shapeSelectedBase === CUSTOM_KEY && <ColourMixer value={customSwatch || DEFAULT_CUSTOM} onChange={setCustomSwatch} onUse={(hex) => { setCustomSwatch(hex); setShapeColor(hex); }} />}
                          <div className="color-shades-row" style={{ gap: 4, padding: 2, maxWidth: 200 }}>
                            {shadesFor(shapeSelectedBase).map((shade, si) => (
                              <button key={shade}
                                className={`color-shade-option ${(shapeColorTarget === 'stroke' ? shapeStrokeColor : shapeFillColor) === shade ? 'active' : ''}${shapeSelectedBase === CUSTOM_KEY && si === 3 ? ' is-exact' : ''}`}
                                style={{ backgroundColor: shade }}
                                onClick={() => setShapeColor(shade)}
                                title={shade}
                              />
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="base-colors-controls">
                          <div className="base-colors-row" style={{ gap: 4, padding: 2, maxWidth: 200 }}>
                            {shapeColorTarget === 'fill' && (
                              <button
                                className={`base-color-option no-color-option ${shapeFillColor === 'none' ? 'active' : ''}`}
                                onClick={() => { setShapeColor('none'); }}
                                title="No fill"
                              />
                            )}
                            {baseColors.map(color => (
                              <button key={color}
                                className={`base-color-option ${shapeSelectedBase === color ? 'active' : ''}`}
                                style={{ backgroundColor: color }}
                                onClick={() => { setShapeSelectedBase(color); setShowShapeColorShades(true); }}
                                title={color}
                              />
                            ))}
                            <button
                              className={`base-color-option is-wheel${shapeSelectedBase === CUSTOM_KEY ? ' active' : ''}`}
                              style={customSwatch ? { backgroundColor: customSwatch } : undefined}
                              onClick={() => { if (!customSwatch) setCustomSwatch(DEFAULT_CUSTOM); setShapeSelectedBase(CUSTOM_KEY); setShowShapeColorShades(true); }}
                              title="Mix your own colour"
                            />
                            <button
                              className="base-color-option is-pick"
                              style={pickSwatch ? { backgroundColor: pickSwatch } : undefined}
                              onClick={() => startEyedropper('shape')}
                              title="Pick a colour from the sheet"
                            >{!pickSwatch && <FaEyeDropper />}</button>
                          </div>
                        </div>
                      )}
                    </div>,
                    document.body
                  )}
                </div>
              );
            })()}



            {currentControlSection === 'selection' && (
              <div className={`selection-controls${smartCropRunning ? ' is-detecting' : ''}`}>
                <div className="selection-controls-row">
                  {/* ── While it is thinking, the row IS the message ──────────────────────────
                      Six ways to crop fill the row on a phone, so anything after them — Copy,
                      Cut, and the one line that says a detection is running — sits past the
                      right-hand edge, reachable only by dragging the row sideways. Nobody
                      drags a toolbar they have no reason to think has more in it, so pressing
                      Auto Crop looked like pressing a dead button for several seconds: the app
                      appeared broken at the moment it was working hardest.
                      Detection takes over the whole row instead. There is nothing else to
                      press while it runs, so there is nothing to lose by clearing them away,
                      and the message lands in the middle of the bar where it cannot be missed.
                      The buttons come back the moment it finishes. */}
                  {smartCropRunning ? (
                    <span className="is-crop-status" role="status">
                      {autoCropActive ? 'Detecting object…' : 'Detecting…'}
                    </span>
                  ) : (<>
                  <div className="selection-buttons">
                    {/* The way out, first in the row so it is never the button that scrolled
                        off the end. Five ways to crop and none to stop was the whole problem. */}
                    <button
                      className="selection-mode-btn is-crop-exit"
                      onClick={exitCrop}
                      title="Leave crop — change nothing"
                    >
                      <FaChevronLeft />
                    </button>
                    <button
                      className={`selection-mode-btn ${autoCropActive ? 'active' : ''}`}
                      onClick={runAutoCrop}
                      disabled={smartCropRunning || (canvasImages.length === 0 && drawingPaths.every(p => p.type === 'mask' || p.color === 'eraser'))}
                      title="Auto crop: cut the main object out as its own layer, ready to move"
                    >
                      <FaMagic />
                    </button>
                    <button
                      className={`selection-mode-btn ${selectionMode === 'rectangle' && !autoCropActive ? 'active' : ''}`}
                      onClick={() => {
                        setAutoCropActive(false);
                        setSelectionPath([]);
                        setCanvasCursor('move');
                        // If no selection exists yet, place on the content; otherwise keep the
                        // bounds. Asked through cropHasBox rather than "both coordinates are 0",
                        // which is a real position (the top-left of the sheet) and not an absence.
                        if (!cropHasBox()) {
                          const b = cropStartBounds();
                          if (b) {
                            setSelectionStart({ x: b.x1, y: b.y1 });
                            setSelectionEnd  ({ x: b.x2, y: b.y2 });
                          }
                          setIsSelecting(true);
                        }
                        setSelectionMode('rectangle');
                      }}
                      title="Rectangle Crop"
                    >
                      <FaRegSquare />
                    </button>
                    <button
                      className={`selection-mode-btn ${selectionMode === 'circle' && !autoCropActive ? 'active' : ''}`}
                      onClick={() => {
                        setAutoCropActive(false);
                        setSelectionPath([]);
                        setCanvasCursor('move');
                        // ── Circle crop opens ROUND ────────────────────────────────────────
                        // It used to take the content's bounding box, which is a photograph's
                        // aspect ratio — an oval on sight. And because the rim drag scaled both
                        // radii by one number, that first oval was the only oval obtainable:
                        // round was not reachable from anywhere. The box is squared off here,
                        // side = the shorter of the two and kept centred, so it starts as a true
                        // circle; pulling a rim then takes it oval in whichever direction it is
                        // pulled. Done whether or not a box is already up, because picking
                        // Circle is a request for a circle, not for the rectangle's proportions.
                        const _cb = cropHasBox()
                          ? { x1: Math.min(selectionStart.x, selectionEnd.x), y1: Math.min(selectionStart.y, selectionEnd.y),
                              x2: Math.max(selectionStart.x, selectionEnd.x), y2: Math.max(selectionStart.y, selectionEnd.y) }
                          : cropStartBounds();
                        if (_cb) {
                          const _ccx = (_cb.x1 + _cb.x2) / 2, _ccy = (_cb.y1 + _cb.y2) / 2;
                          const _cr = Math.max(10, Math.min(_cb.x2 - _cb.x1, _cb.y2 - _cb.y1) / 2);
                          setSelectionStart({ x: _ccx - _cr, y: _ccy - _cr });
                          setSelectionEnd  ({ x: _ccx + _cr, y: _ccy + _cr });
                        }
                        setIsSelecting(true);
                        setSelectionMode('circle');
                      }}
                      title="Circle Crop"
                    >
                      <FaRegCircle />
                    </button>
                    <button
                      className={`selection-mode-btn ${selectionMode === 'smart' && !autoCropActive ? 'active' : ''}`}
                      onClick={() => {
                        setAutoCropActive(false);
                        setSelectionMode('smart');
                        setCanvasCursor('crosshair');
                        setIsSelecting(false);
                        setSelectionPath([]);
                        setSelectionTouchPos(null);
                        cropPathRef.current = [];
                      }}
                      title="Smart Crop: click on an object to auto-detect its boundary"
                    >
                      <FaFingerprint />
                    </button>
                    <button
                      className={`selection-mode-btn ${selectionMode === 'freeform' && !autoCropActive ? 'active' : ''}`}
                      onClick={() => {
                        setAutoCropActive(false);
                        setSelectionMode('freeform');
                        setCanvasCursor('crosshair');
                        setIsSelecting(false);
                        setSelectionPath([]);
                        setSelectionTouchPos(null);
                      }}
                      title="Freeform Crop: draw a custom shape"
                    >
                      <FaDrawPolygon />
                    </button>
                  </div>
                  {(((selectionMode === 'rectangle' || selectionMode === 'circle' || selectionMode === 'smart') && isSelecting) ||
                    (selectionMode === 'freeform' && selectionPath.length > 2)) && (
                    <>
                      <button
                        className="selection-mode-btn active"
                        style={{ marginLeft: 6 }}
                        onClick={() => endSelection()}
                        title="Copy: keep the selected area as a new image"
                      >
                        <FaCopy />
                      </button>
                      <button
                        className="selection-mode-btn active"
                        style={{ marginLeft: 2 }}
                        onClick={() => cutSelection()}
                        title="Cut: lift the selected area as a new image (and remove it from the original)"
                      >
                        <FaCut />
                      </button>
                    </>
                  )}
                  </>)}
                </div>
              </div>
            )}

            {currentControlSection === 'save' && !isSaving && (
              <div className="save-controls">
                {canvasImages.length === 0 && drawingPaths.length === 0 && canvasShapes.length === 0 ? (
                  <div className="is-toolbar-msg">Nothing on canvas to save</div>
                ) : !saveMessage ? (
                  <div className="save-controls-row">
                    <input
                      type="text"
                      value={fileName}
                      onChange={(e) => setFileName(e.target.value)}
                      placeholder="File name"
                      className="save-input wide"
                      style={{ background: 'white', backgroundColor: 'white', color: '#333', WebkitTextFillColor: '#333', colorScheme: 'light', boxShadow: '0 0 0 1000px white inset', WebkitBoxShadow: '0 0 0 1000px white inset' }}
                    />
                    <select
                      value={saveType}
                      onChange={(e) => setSaveType(e.target.value)}
                      className="save-select wide"
                      style={{ background: 'white', backgroundColor: 'white', color: '#333', WebkitTextFillColor: '#333', colorScheme: 'light', appearance: 'none', WebkitAppearance: 'none', boxShadow: '0 0 0 1000px white inset', WebkitBoxShadow: '0 0 0 1000px white inset' }}
                    >
                      {/* Changing the folder moved to Settings, where a setting belongs — it was
                          never a place to save something, which is all this list should offer. */}
                      <option value="file">{canPickFolder() ? (saveFolderName ? `Save in ${saveFolderName}` : 'Save in Folder') : 'Download'}</option>
                      <option value="bucket">Save to Cloud</option>
                      {/* ── The sheet as a picture, rather than as work in progress ──────────
                          A whole-sheet save to the Cloud writes the picture AND the sheet
                          behind it, so opening that item gives every object back, separately
                          editable. Sometimes that is not what is wanted: the finished thing,
                          flat, with the effects and the props already part of the pixels and
                          nothing left to take apart. That is this, and it only makes sense
                          where the two would differ — a whole sheet, or Selfie Mode, where
                          the sheet is the piece of work. Picking out one photo or one figure
                          already saves a picture and nothing else, so the choice is not
                          offered there. */}
                      {saveCloudImageAvailable && <option value="bucket-image">Save to Cloud (Image)</option>}
                    </select>
                    <button
                      className="save-icon-btn"
                      onClick={handleSave}
                      title="Save"
                    >
                      <FaSave />
                    </button>
                  </div>
                ) : (
                  <div className={`save-message ${saveMessage.ok ? 'save-msg-ok' : 'save-msg-err'}`}>
                    {saveMessage.text}
                  </div>
                )}
              </div>
            )}

            {currentControlSection === 'layout' && (
              <div className="is-toolbar-section">
                <span className="is-toolbar-msg">Layout: {PRINT_SIZES[printSize].display}</span>
              </div>
            )}

            {currentControlSection === 'print' && (
              <div className="is-toolbar-section">
                <span className="is-toolbar-msg">Nothing on canvas to print</span>
              </div>
            )}

            {currentControlSection === 'magic-wand' && null}

            {/* === Magic: Remove BG controls === */}
            {currentControlSection === 'magic-removebg' && (
              <div className="dimension-controls-row">
                <button
                  className="delete-confirm-text-btn"
                  onClick={handleMagicRemoveBg}
                  disabled={aiHold || !selectedImage}
                  title="Remove background (remove.bg)"
                >
                  {magicProcessing ? 'Working...' : 'Remove BG'}
                </button>
                <div style={{ width: 1, height: 20, background: 'var(--border-color,#ddd)', margin: '0 4px', flexShrink: 0 }} />
                <button
                  className="delete-confirm-text-btn"
                  onClick={handleMagicRemovePlate}
                  disabled={aiHold || !selectedImage}
                  title="Remove cake plate/board via AI"
                >
                  {magicProcessing ? 'Working...' : 'Remove Plate'}
                </button>
              </div>
            )}

            {/* === Magic: Contrast control === */}
            {currentControlSection === 'magic-contrast' && (() => {
              const val = magicContrast;
              const pct = ((val + 50) / 100) * 100;
              const s = Math.min(50, pct), e2 = Math.max(50, pct);
              const col = val >= 0 ? '#ff6b35' : '#4a90e2';
              const bg = `linear-gradient(to right,#ccc 0%,#ccc ${s}%,${col} ${s}%,${col} ${e2}%,#ccc ${e2}%,#ccc 100%)`;
              return (
                <div className="dimension-controls-row" style={{ gap: 6 }}>
                  <span className="dim-label" style={{ minWidth: 24, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {val > 0 ? `+${val}` : val}
                  </span>
                  <div className="magic-slider-wrap" style={{ width: 140 }}>
                    <div className="magic-slider-zero-mark" />
                    <input
                      className="magic-slider-inline"
                      type="range" min="-50" max="50" step="1" value={val}
                      style={{ '--slider-bg': bg }}
                      onChange={e => {
                        const n = Number(e.target.value);
                        setMagicContrast(n);
                        // Both values, always. Merging in only the slider being moved meant
                        // the other one read 0 — it is reset after each bake — so touching
                        // contrast on a picture baked at +40 brightness previewed the contrast
                        // alone on the unadjusted photo and then leapt 136 grey levels on
                        // release. Measured: preview 80, result 216.
                        setMagicFilterPreview({ on: true, brightness: magicBrightness, contrast: n });
                      }}
                      onPointerUp={e => applyMagicFilter(magicBrightness, Number(e.target.value))}
                      onKeyUp={e => { if (/^Arrow|Home|End|Page/.test(e.key)) applyMagicFilter(magicBrightness, Number(e.target.value)); }}
                      disabled={aiHold || !selectedImage}
                    />
                  </div>
                </div>
              );
            })()}

            {/* === Magic: Brightness control === */}
            {currentControlSection === 'magic-brightness' && (() => {
              const val = magicBrightness;
              const pct = ((val + 50) / 100) * 100;
              const s = Math.min(50, pct), e2 = Math.max(50, pct);
              const col = val >= 0 ? '#f5a623' : '#4a90e2';
              const bg = `linear-gradient(to right,#ccc 0%,#ccc ${s}%,${col} ${s}%,${col} ${e2}%,#ccc ${e2}%,#ccc 100%)`;
              return (
                <div className="dimension-controls-row" style={{ gap: 6 }}>
                  <span className="dim-label" style={{ minWidth: 24, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {val > 0 ? `+${val}` : val}
                  </span>
                  <div className="magic-slider-wrap" style={{ width: 140 }}>
                    <div className="magic-slider-zero-mark" />
                    <input
                      className="magic-slider-inline"
                      type="range" min="-50" max="50" step="1" value={val}
                      style={{ '--slider-bg': bg }}
                      onChange={e => {
                        const n = Number(e.target.value);
                        setMagicBrightness(n);
                        setMagicFilterPreview({ on: true, brightness: n, contrast: magicContrast });
                      }}
                      onPointerUp={e => applyMagicFilter(Number(e.target.value), magicContrast)}
                      onKeyUp={e => { if (/^Arrow|Home|End|Page/.test(e.key)) applyMagicFilter(Number(e.target.value), magicContrast); }}
                      disabled={aiHold || !selectedImage}
                    />
                  </div>
                </div>
              );
            })()}



            {currentControlSection === 'delete' && (
              <div className="delete-controls">
                <div className="delete-confirm-row">
                  {selectedImage ? (
                    (() => {
                      const image = canvasImages.find((img) => img.id === selectedImage);
                      if (!image) return null;
                      const cancelDelete = () => {
                        setActiveTool(null);
                        setCurrentControlSection(null);
                        setShowTopControls(false);
                      };
                      if (image.source === 'bucket') {
                        return (
                          <>
                            <button
                              className="delete-confirm-text-btn"
                              onClick={() => {
                                const _after = canvasImages.filter((img) => img.id !== selectedImage);
                                pushImageHistory(canvasImages, _after);
                                setCanvasImages(_after);
                                setSelectedImage(null);
                                setShowImageControls(null);
                                cancelDelete();
                              }}
                              disabled={deleting}
                              title="Remove from Canvas only"
                            >Canvas</button>
                            <span className="delete-confirm-sep">|</span>
                            <button
                              className="delete-confirm-text-btn"
                              onClick={() => { if (deleting) return; handleDeleteFromBucketCloudOnly(image); }}
                              disabled={deleting}
                              title="Delete from Cloud only"
                            >Cloud</button>
                            <span className="delete-confirm-sep">|</span>
                            <button
                              className="delete-confirm-text-btn"
                              onClick={() => { if (deleting) return; handleDeleteFromBucket(image); }}
                              disabled={deleting}
                              title="Delete from Cloud and Canvas"
                            >Both</button>
                            <button
                              className="delete-confirm-reject"
                              onClick={cancelDelete}
                              title="Cancel"
                            ><FaTimes /></button>
                          </>
                        );
                      } else {
                        return (
                          <>
                            <button
                              className="delete-confirm-text-btn"
                              onClick={() => {
                                const _after = canvasImages.filter((img) => img.id !== selectedImage);
                                pushImageHistory(canvasImages, _after);
                                setCanvasImages(_after);
                                setSelectedImage(null);
                                setShowImageControls(null);
                                cancelDelete();
                              }}
                              disabled={deleting}
                              title="Confirm Delete"
                            >Delete</button>
                            <button
                              className="delete-confirm-reject"
                              onClick={cancelDelete}
                              title="Cancel"
                            ><FaTimes /></button>
                          </>
                        );
                      }
                    })()
                  ) : (
                    <span className="delete-confirm-label">No image selected</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: function buttons + close */}
          <div className="is-top-toolbar-actions" style={{ display: fromCustom ? 'none' : '' }}>

            {/* Speak instead of typing. It appears — blinking, so it is noticed — for as long as
                there is a caret in a text box or a figure's label, and takes the room Copy and
                Delete leave behind while editing. Pressing it puts the keyboard away and writes
                what is said straight into the box; pressing it again stops listening. */}
            {editingText && SPEECH_API && (() => {
              const lang = SPEECH_LANGS.find(l => l.code === speechLang) || SPEECH_LANGS[0];
              const openLangs = (el) => {
                micPressRef.current.fired = true;
                const r = el.getBoundingClientRect();
                setLangMenuPos({ top: r.bottom + 6, left: Math.min(r.left, window.innerWidth - 190) });
                setShowLangMenu(true);
              };
              return (
                <button
                  className={`control-btn vertical is-dictate${listening ? ' listening' : ' idle'}`}
                  onMouseDown={(e) => {
                    e.preventDefault();          // the text box must keep its caret until we say so
                    const el = e.currentTarget;
                    micPressRef.current.fired = false;
                    micPressRef.current.timer = setTimeout(() => openLangs(el), 500);
                  }}
                  onMouseUp={() => clearTimeout(micPressRef.current.timer)}
                  onMouseLeave={() => clearTimeout(micPressRef.current.timer)}
                  onTouchStart={(e) => {
                    const el = e.currentTarget;
                    micPressRef.current.fired = false;
                    micPressRef.current.timer = setTimeout(() => openLangs(el), 500);
                  }}
                  onTouchEnd={() => clearTimeout(micPressRef.current.timer)}
                  onClick={() => {
                    clearTimeout(micPressRef.current.timer);
                    if (micPressRef.current.fired) { micPressRef.current.fired = false; return; }
                    dictateInto(pendingLabelShapeId ? 'label' : 'text');
                  }}
                  title={listening
                    ? `Listening in ${lang.name} — press to stop`
                    : `Speak the text (${lang.name}) — hold for another language`}
                >
                  <FaMicrophone />
                  <span className="is-dictate__lang" aria-hidden="true">{lang.label}</span>
                </button>
              );
            })()}
            {showLangMenu && createPortal(
              <>
                <div className="is-sheet-scrim" onClick={() => setShowLangMenu(false)} />
                <div className="is-lang-menu" style={{ top: langMenuPos.top, left: langMenuPos.left }} role="menu">
                  {SPEECH_LANGS.map(l => (
                    <button
                      key={l.code || 'auto'}
                      className={`is-lang-opt${l.code === speechLang ? ' active' : ''}`}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => pickSpeechLang(l.code)}
                    >{l.name}</button>
                  ))}
                </div>
              </>,
              document.body,
            )}

            {/* ...and they stand aside for an AI run as well. Its progress takes over the middle
                of the bar, and on a phone a message competing with two buttons for the width is
                a message that gets cut off. Nothing is lost: both come straight back when the
                run ends, with the same object still selected. */}
            {/* Not while cropping. Crop has its own set of buttons and its own idea of what is
                selected — the part of the picture inside the outline, not the picture — so Copy
                and Delete beside it read as "copy that piece", which is not what they do. */}
            {(selectedShapeId || selectedImage) && activeTool !== 'selection' && !editingText && !topBarMessage && !sizingReadoutOpen
              && !(isMovingShape || isResizingShape || isRotatingShape || draggingImage || resizingImage || rotatingImage) && (
              <>
                <button
                  className="function-btn vertical"
                  onClick={() => {
                    if (selectedShapeId) {
                      handleCopyShape();
                    } else {
                      handleCopyImage(selectedImage, { stopPropagation: () => {} });
                    }
                  }}
                  title="Copy"
                  disabled={!selectedImage && !selectedShapeId}
                >
                  <FaCopy />
                </button>
                <button
                  className={`function-btn vertical ${activeTool === 'delete' ? 'active' : ''}`}
                  onClick={() => {
                    if (selectedShapeId) {
                      // The id belongs to a figure or to a hand-drawn stroke; without this
                      // split a stroke's Delete cleared the selection and left the stroke on
                      // the sheet, which read as the button doing nothing.
                      const _sBefore = canvasShapesRef.current;
                      if (_sBefore.some(s => s.id === selectedShapeId)) {
                        const _sAfter = _sBefore.filter(s => s.id !== selectedShapeId);
                        setCanvasShapes(_sAfter);
                        pushShapeHistory(_sBefore, _sAfter);
                      } else {
                        const _pBefore = drawingPathsRef.current;
                        const _pAfter = _pBefore.filter(p => p.id !== selectedShapeId);
                        if (_pAfter.length !== _pBefore.length) {
                          setDrawingPaths(_pAfter);
                          pushInkHistory(_pBefore, _pAfter);
                        }
                      }
                      setSelectedShapeId(null);
                      setActiveTool(null);
                      setActiveShapeTool(null);
                      setSelectedEmoji(null);
                      setCanvasCursor('default');
                      setCurrentControlSection(null);
                      setShowTopControls(false);
                    } else if (selectedImage) {
                      const _imgToDelete = canvasImages.find(i => i.id === selectedImage);
                      if (_imgToDelete && _imgToDelete.source === 'bucket') {
                        handleToolSelect('delete');
                      } else if (_imgToDelete) {
                        const _after = canvasImages.filter(i => i.id !== selectedImage && i.parentId !== selectedImage);
                        pushImageHistory(canvasImages, _after);
                        setCanvasImages(_after);
                        setSelectedImage(null);
                        setShowImageControls(null);
                      }
                    }
                  }}
                  title="Delete"
                  disabled={!selectedImage && !selectedShapeId}
                >
                  <FaTrash />
                </button>
              </>
            )}
            {selectedShapeIds.size > 1 && !topBarMessage && !sizingReadoutOpen && (
              <>
                <button
                  className="function-btn vertical"
                  title="Copy all selected shapes"
                  onClick={() => {
                    const before = canvasShapesRef.current;
                    const copies = canvasShapesRef.current
                      .filter(s => selectedShapeIds.has(s.id))
                      .map(s => ({
                        ...s,
                        id: Date.now() + Math.random(),
                        ...copyOffsetFor(s),
                        layerIndex: ++layerCounterRef.current,
                      }));
                    const after = [...before, ...copies];
                    setCanvasShapes(after);
                    setSelectedShapeIds(new Set(copies.map(c => c.id)));
                    pushShapeHistory(before, after);
                    markDirty();
                  }}
                ><FaCopy /></button>
                <button
                  className="function-btn vertical"
                  title="Delete all selected shapes"
                  onClick={() => {
                    const before = canvasShapesRef.current;
                    const after = before.filter(s => !selectedShapeIds.has(s.id));
                    setCanvasShapes(after);
                    pushShapeHistory(before, after);
                    setSelectedShapeIds(new Set());
                    setActiveTool(null);
                    setActiveShapeTool(null);
                    setShowTopControls(false);
                    setCurrentControlSection(null);
                    setCanvasCursor('default');
                    markDirty();
                  }}
                ><FaTrash /></button>
              </>
            )}
            <button className="is-close-btn" style={{ display: hideTopChrome ? 'none' : '' }} onClick={() => {
              if (pendingNavPath) {
                const dest = pendingNavPath;
                setPendingNavPath(null);
                if (dest === '__back__') closeApp(); else navigate(dest);
              } else {
                handleGuardedNavigate(null);
              }
            }} title="Close">
              <FaTimes />
            </button>
          </div>
        </div>
        {/* LEFT CONTROL PANEL ? hover to reveal, centered on left edge */}
        {/* MAGIC TOOLBAR -- replaces tools panel when Magic Wand is active */}
        {/* AI status used to render here, fixed near the bottom bar -- easy to miss while
            working up on the canvas. It now renders inside the top toolbar itself (see
            aiStatusInTopBar, is-top-toolbar-controls above), covering the chat panel's own
            requests too, not just the toolbar buttons. */}
        {!aiEnabled && selectedImage && (
              <div className="magic-ai-offline" role="status">
                AI tools need a server. Deploy the <code>studio-ai</code> function and set
                {' '}<code>VITE_SUPABASE_URL</code>. Everything else works offline.
              </div>
        )}
        {/* Configured, but there is no connection to use it over. Worth saying plainly and in
            the same place, because the AI buttons have just greyed out and the reason is not
            otherwise visible — and because the sentence people need is the second one. */}
        {aiEnabled && !online && selectedImage && (
              <div className="magic-ai-offline" role="status">
                No connection — the AI tools are waiting for one. Everything else, including
                Smart&nbsp;Crop, works as usual.
              </div>
        )}
        {/* COLLAPSIBLE TOOLS PANEL WRAPPER */}
        <div
          className="is-tools-panel-wrapper"
          ref={toolsWrapperRef}
          style={fromCustom ? { display: 'none' } : undefined}
          onTouchStart={e => setToolsTouchStartY(e.touches[0].clientY)}
          onTouchEnd={e => {
            const dy = e.changedTouches[0].clientY - toolsTouchStartY;
            if (dy < -30) setShowToolsPanel(false);
            else if (dy > 30) setShowToolsPanel(true);
          }}
        >
        {/* CANVAS TOOLS ROW ? always visible, arrow at end */}
          {/* Help needs to be reachable without scrolling, but the tool buttons here can
              overflow and scroll horizontally on a narrow screen -- so a marginLeft:'auto'
              button placed AS THE LAST CHILD of that same scrolling row only reaches the true
              screen edge when nothing is cut off; the moment the tools overflow, there's no
              leftover space for the auto margin to push into and Help just sits after the
              last tool, off past the scrollable edge. Splitting them apart fixes it: this
              outer row never scrolls, the tools scroll in their own flexed sub-row, and Help
              is a fixed sibling that always stays pinned at the true right edge. */}
          <div className="is-tools-row-wrap">
          <div className={`is-tools-row${selfieMode ? ' is-selfie' : ''}`}>
            {/* ── Selfie menu ──────────────────────────────────────────────────────────────
                A photo of a face gets its own toolbar: the effects, the props, the mirror, and
                then the few ordinary tools worth having beside them. The rest of the studio is
                still here, hidden by .is-tools-row.is-selfie — and the way back is the same
                button that got you in, so this is a switch rather than a one-way door. */}
            {selfieMode && (
              <>
                <button
                  className="control-btn vertical is-selfie-btn is-selfie-close"
                  onClick={() => { setSelfieMode(false); setOpenGroup(null); neutralizeTools(); }}
                  title="Back to the normal tools"
                ><FaChevronLeft /></button>
                {isAiEnabled() && (
                  <button
                    className={`control-btn vertical is-selfie-btn${magicSubTool === 'chat' ? ' active' : ''}`}
                    onClick={() => {
                      const next = magicSubTool === 'chat' ? null : 'chat';
                      setOpenGroup(null);
                      setMagicSubTool(next);
                      setCurrentControlSection(null);
                      setShowTopControls(false);
                    }}
                    disabled={!online}
                    title={online ? 'AI — ask for a change to your selfie' : 'AI needs a connection'}
                  ><FaMagic /></button>
                )}
                {/* Effects. The panel shows each one on a thumbnail of YOUR photo, so it is
                    chosen by looking rather than by reading a word like "Faded". All of this
                    acts on the picture that is SELECTED, so with nothing selected there is
                    nothing for it to act on and the buttons go flat rather than quietly
                    changing whichever photo happened to be on top. */}
                <button
                  className={`control-btn vertical is-selfie-btn${(openGroup === 'selfiefx' || selfieFxOf()) ? ' active' : ''}`}
                  onClick={(e) => openGroupPanel(e, 'selfiefx', 5)}
                  disabled={!selfiePhoto()}
                  title={!selfiePhoto() ? 'Tap a photo first'
                    : selfieFxOf() ? `Effect — ${SELFIE_FX[selfieFxOf()].label}` : 'Effects'}
                ><FaAdjust /><span className="figures-btn__caret" aria-hidden="true">▾</span></button>
                {openGroup === 'selfiefx' && createPortal(
                  <>
                    <div className="is-sheet-scrim" onClick={() => setOpenGroup(null)} />
                    <div className="is-group-panel is-selfie-panel" style={{ top: groupPanelPos.top, left: groupPanelPos.left }} role="menu" aria-label="Effects">
                      <div className="is-group-panel__title">Effects</div>
                      <div className="is-selfie-grid">
                        <button
                          className={`is-selfie-fx${!selfieFxOf() ? ' active' : ''}`}
                          onClick={() => renderSelfie({ fx: null })}
                          title="No effect — the photo as taken"
                        >
                          <span className="is-selfie-fx__sw"
                            style={{ backgroundImage: `url(${selfiePhoto()?._fxOrigUrl || selfiePhoto()?.url || ''})` }} />
                          <span className="is-selfie-fx__label">Original</span>
                        </button>
                        {SELFIE_FX_ORDER.map(k => (
                          <button
                            key={k}
                            className={`is-selfie-fx${selfieFxOf() === k ? ' active' : ''}`}
                            onClick={() => pickSelfieFx(k)}
                            title={SELFIE_FX[k].label}
                          >
                            <span
                              className="is-selfie-fx__sw"
                              style={{
                                backgroundImage: `url(${selfiePhoto()?._fxOrigUrl || selfiePhoto()?.url || ''})`,
                                filter: SELFIE_FX[k].filter || undefined,
                              }}
                            />
                            <span className="is-selfie-fx__label">{SELFIE_FX[k].label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </>, document.getElementById('portal-root') || document.body)}
                {/* The props. Each one lands roughly where it belongs and is then an ordinary
                    object: drag it onto the face, turn it, make it bigger, throw it away. */}
                <button
                  className={`control-btn vertical is-selfie-btn${openGroup === 'selfieprops' ? ' active' : ''}`}
                  onClick={(e) => openGroupPanel(e, 'selfieprops', 5)}
                  disabled={!selfiePhoto()}
                  title={selfiePhoto() ? 'Shades, caps and the rest' : 'Tap a photo first'}
                ><FaGem /><span className="figures-btn__caret" aria-hidden="true">▾</span></button>
                {openGroup === 'selfieprops' && createPortal(
                  <>
                    <div className="is-sheet-scrim" onClick={() => setOpenGroup(null)} />
                    <div className="is-group-panel is-selfie-panel" style={{ top: groupPanelPos.top, left: groupPanelPos.left }} role="menu" aria-label="Props">
                      <div className="is-group-panel__title">Dress it up</div>
                      <div className="is-selfie-grid">
                        {SELFIE_PROPS.map(p => (
                          <button
                            key={p.key}
                            className="is-selfie-prop"
                            onClick={() => addSelfieProp(p.key)}
                            title={p.label}
                          >
                            {/* Quoted, so a URL can never end the value early. */}
                            <span className="is-selfie-prop__art" style={{ backgroundImage: `url("${p.url}")` }} />
                            <span className="is-selfie-fx__label">{p.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </>, document.getElementById('portal-root') || document.body)}
                <button
                  className={`control-btn vertical is-selfie-btn${selfieMirrored() ? ' active' : ''}`}
                  onClick={toggleSelfieMirror}
                  disabled={!selfiePhoto()}
                  title={selfiePhoto() ? 'Mirror — flip the photo left to right' : 'Tap a photo first'}
                ><span className="is-mirror-icon" aria-hidden="true">⇋</span></button>
                {/* Faces and hearts, in a list you can see. The heart has no button of its own
                    any more — it is in here with the rest of them, where it belongs. */}
                <button
                  className={`control-btn vertical is-selfie-btn${openGroup === 'selfieemoji' ? ' active' : ''}`}
                  onClick={(e) => openGroupPanel(e, 'selfieemoji', 8)}
                  title="Faces, hearts and stars"
                ><FaSmile /><span className="figures-btn__caret" aria-hidden="true">▾</span></button>
                {openGroup === 'selfieemoji' && createPortal(
                  <>
                    <div className="is-sheet-scrim" onClick={() => setOpenGroup(null)} />
                    <div className="is-group-panel is-selfie-panel is-emoji-panel" style={{ top: groupPanelPos.top, left: groupPanelPos.left }} role="menu" aria-label="Faces">
                      <div className="is-group-panel__title">Faces &amp; hearts</div>
                      <div className="is-emoji-grid">
                        {SELFIE_EMOJI.map(ch => (
                          <button key={ch} className="is-emoji-pick" onClick={() => addSelfieEmoji(ch)} title={ch}>{ch}</button>
                        ))}
                      </div>
                    </div>
                  </>, document.getElementById('portal-root') || document.body)}
                <button
                  className={`control-btn vertical is-selfie-btn${activeShapeTool === 'speechBubble' ? ' active' : ''}`}
                  onClick={() => handleShapeToolSelect('speechBubble')}
                  title="Speech bubble — double tap it to type"
                ><ShapeIcon type="speechBubble" /></button>
                <button
                  className={`control-btn vertical is-selfie-btn${activeTool === 'draw' ? ' active' : ''}`}
                  onClick={() => handleShapeToolSelect('handwriting')}
                  title="Scribble"
                ><DrawToolIcon mode={activeTool === 'draw' ? drawingMode : 'pen'} /></button>
                <button
                  className={`control-btn vertical is-selfie-btn${activeShapeTool === 'text' ? ' active' : ''}`}
                  onClick={() => handleShapeToolSelect('text')}
                  title="Big text"
                ><FaFont /></button>
                <button
                  className="control-btn vertical is-selfie-btn"
                  onClick={handleUndo}
                  title="Undo (Ctrl+Z)"
                  disabled={historyIndex <= 0}
                ><FaUndo /></button>
                {/* Start again. Selfie Mode fills a sheet faster than anything else in the
                    studio — a photo, an effect, a hat, three smileys — and the one button for
                    wiping it was in the row this one replaces. Same two-tap safety as the
                    other broom, and it steps aside while something is selected, where Delete
                    is the button you actually want. */}
                {!selectedShapeId && !selectedImage && (
                  <button
                    className={`control-btn vertical is-selfie-btn${pendingClear ? ' save-blink' : ''}`}
                    onClick={clearCanvas}
                    title={pendingClear ? 'Tap again to clear everything' : 'Clear Canvas'}
                    disabled={canvasImages.length === 0 && drawingPaths.length === 0 && canvasShapes.length === 0}
                  ><FaBroom /></button>
                )}
              </>
            )}
            {/* The way back IN. Selfie Mode used to arrive only with a photo taken through it,
                and closing it was final until another photo was taken — which made the effects
                and the props unreachable for a picture already on the sheet, including the one
                you had just been working on. */}
            {!selfieMode && canvasImages.some(i => i.source !== 'prop') && (
              <button
                className="control-btn vertical"
                onClick={() => { setOpenGroup(null); setSelfieMode(true); }}
                title="Selfie Mode — effects and props for a photo"
              ><FaCamera /></button>
            )}
            {/* The Select / Move tool used to live here. Selection is now the canvas's
                default behaviour — click an object and it is selected, with its position,
                rotate and resize controls already available — so a mode button that had to
                be switched on first was pure friction. Copy and Delete moved to the top
                toolbar, where they are visible whenever something is selected. */}
            {/* A drawn object is selected: toggle between its position/size controls and its
                colour & style controls. The Select/Move button used to be this toggle. */}
            {selectedShapeId && (() => {
              // The MOVE button: lit while the position / size / angle boxes are showing, dark
              // while the colour & style controls are. Press it to switch to the coordinates;
              // press it again to go back to colour & style. Same icon in both states so it
              // reads as one thing: "move".
              const moveOpen = currentControlSection === 'selectmove';
              return (
                <button
                  className={`control-btn vertical${moveOpen ? ' active' : ''}`}
                  onClick={() => {
                    if (moveOpen) {
                      // Leaving Move: the palette is wanted again, and stays wanted for the
                      // next object too. The ref is set here as well as through state -- the
                      // effect that mirrors it runs after this handler, and the open* call
                      // below reads the ref immediately.
                      setPositionMode(false);
                      positionModeRef.current = false;
                      positionModeForRef.current = null;
                      if (canvasShapes.some(s => s.id === selectedShapeId)) openShapeStyleControls(selectedShapeId);
                      else openInkStyleControls(selectedShapeId);
                    } else {
                      // Entering Move: sticky for THIS object, so tapping it again comes back
                      // to its numbers. Another object opens on its own colour and style —
                      // see positionModeForRef.
                      positionModeForRef.current = selectedShapeId;
                      setPositionMode(true);
                      setCurrentControlSection('selectmove');
                      setSelectMoveControlMode('position');
                      setShowTopControls(true);
                    }
                  }}
                  title={moveOpen ? 'Move — showing position; tap for colour & style' : 'Move — show position & size'}
                >
                  <FaArrowsAlt />
                </button>
              );
            })()}
            {selectedImage && !selectedShapeId && (
            <>
              <button
                className={`control-btn vertical${magicSubTool === 'brightness' ? ' active' : ''}`}
                onClick={() => {
                  const next = magicSubTool === 'brightness' ? null : 'brightness';
                  setMagicSubTool(next);
                  setMagicFilterPreview({ on: false, brightness: 0, contrast: 0 });
                  if (next) { setCurrentControlSection('magic-brightness'); setShowTopControls(true); }
                  else { setCurrentControlSection(null); setShowTopControls(false); }
                }}
                title="Brightness"
              ><FaSun /></button>
              <button
                className={`control-btn vertical${magicSubTool === 'contrast' ? ' active' : ''}`}
                onClick={() => {
                  const next = magicSubTool === 'contrast' ? null : 'contrast';
                  setMagicSubTool(next);
                  setMagicFilterPreview({ on: false, brightness: 0, contrast: 0 });
                  if (next) { setCurrentControlSection('magic-contrast'); setShowTopControls(true); }
                  else { setCurrentControlSection(null); setShowTopControls(false); }
                }}
                title="Contrast"
              ><FaAdjust /></button>
              {/* ── AI tools live here now, beside the non-AI image tools. There used to be a
                  separate "Magic Wand" mode with its own toolbar; one row for everything is
                  simpler and matches how the tools are actually used — pick an image, act on it. ── */}
              {isStaff && studioStamps.some(s => s.isHologram) && (() => {
                const _holo = studioStamps.find(s => s.isHologram);
                return (
                  <button
                    className="control-btn vertical"
                    onClick={handleHologramStamp}
                    title="Embed hologram stamp into image"
                    disabled={isSaving}
                  >
                    <img src={_holo.url} style={{ width: 18, height: 18, objectFit: 'contain' }} alt="hologram" />
                  </button>
                );
              })()}
              {canUseAi && <div className="magic-divider" />}
              {/* AI group: one button on small screens; expands to the four AI tools */}
              {canUseAi && <button
                className={`control-btn vertical is-group-btn${(showAiGroup || aiToolActive) ? ' active' : ''}`}
                onClick={(e) => openGroupPanel(e, 'ai', canEditTiers ? 6 : 4)}
                title="AI tools"
              >
                <FaMagic />
                <span className="figures-btn__caret" aria-hidden="true">▾</span>
              </button>}
              {canUseAi && showAiGroup && createPortal(
                <>
                  <div className="is-sheet-scrim" onClick={() => setOpenGroup(null)} />
                  <div className="is-group-panel" style={{ top: groupPanelPos.top, left: groupPanelPos.left }} role="menu" aria-label="AI tools">
                    <div className="is-group-panel__title">AI tools</div>
                    <div className="is-group-panel__row">
              {/* Remove Image Background -- opens colour options popdown */}
              <button
                ref={bgColorBtnRef}
                className={`control-btn vertical${showBgColorPopdown ? ' active' : ''}`}
                onClick={(e) => {
                  const btnRect = e.currentTarget.getBoundingClientRect();
                  const PANEL_W = 220, PANEL_H = 100;
                  const vw = window.innerWidth, vh = window.innerHeight;
                  let left = btnRect.right + 6;
                  if (left + PANEL_W > vw - 6) left = btnRect.left - PANEL_W - 6;
                  if (left < 6) left = 6;
                  let top = btnRect.top;
                  if (top + PANEL_H > vh - 6) top = vh - PANEL_H - 6;
                  if (top < 6) top = 6;
                  setBgColorPopdownPos({ top, left });
                  if (!showBgColorPopdown) { setBgRemoveMix(false); setBgRemovePending(null); }
                  setShowBgColorPopdown(v => !v);
                }}
                disabled={aiHold}
                title="Remove / Replace Background (AI)"
              ><FaImage /></button>
              {/* Beauty -- AI maximum quality enhancement */}
              <button
                className="control-btn vertical"
                onClick={() => { setOpenGroup(null); handleMagicEnhance(); }}
                disabled={aiHold || !effectiveImageId}
                title="Enhance quality (AI)"
              >
                {/* makeup / sparkle face icon */}
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* face circle */}
                  <circle cx="12" cy="13" r="7" fill="currentColor" opacity="0.15" stroke="currentColor" strokeWidth="1.5"/>
                  {/* eyes */}
                  <ellipse cx="9.5" cy="12" rx="1" ry="1.2" fill="currentColor"/>
                  <ellipse cx="14.5" cy="12" rx="1" ry="1.2" fill="currentColor"/>
                  {/* smile */}
                  <path d="M9.5 15.5 Q12 17.5 14.5 15.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
                  {/* sparkles */}
                  <path d="M3 4 L3.7 5.7 L5.4 6.4 L3.7 7.1 L3 8.8 L2.3 7.1 L0.6 6.4 L2.3 5.7Z" fill="currentColor"/>
                  <path d="M19.5 1.5 L20 2.7 L21.2 3.2 L20 3.7 L19.5 4.9 L19 3.7 L17.8 3.2 L19 2.7Z" fill="currentColor"/>
                  <path d="M21 9 L21.4 10 L22.4 10.4 L21.4 10.8 L21 11.8 L20.6 10.8 L19.6 10.4 L20.6 10Z" fill="currentColor"/>
                </svg>
              </button>
              {/* Colour change (AI) */}
              <button
                ref={colorBtnRef}
                className={`control-btn vertical${showColorPopdown ? ' active' : ''}`}
                onClick={(e) => {
                  const btnRect = e.currentTarget.getBoundingClientRect();
                  const PANEL_W = 230, PANEL_H = 80;
                  const vw = window.innerWidth, vh = window.innerHeight;
                  let left = btnRect.right + 6;
                  if (left + PANEL_W > vw - 6) left = btnRect.left - PANEL_W - 6;
                  if (left < 6) left = 6;
                  let top = btnRect.top;
                  if (top + PANEL_H > vh - 6) top = vh - PANEL_H - 6;
                  if (top < 6) top = 6;
                  setColorPopdownPos({ top, left });
                  setMagicCakeColorBase(null);
                  setShowColorPopdown(v => !v);
                }}
                disabled={aiHold || !effectiveImageId}
                title="Change Colour (AI)"
              ><FaPalette /></button>
              {/* AI Edit (multi-image chat) */}
              <button
                className={`control-btn vertical${magicSubTool === 'chat' ? ' active' : ''}`}
                onClick={() => {
                  const next = magicSubTool === 'chat' ? null : 'chat';
                  setOpenGroup(null);
                  setMagicSubTool(next);
                  setCurrentControlSection(null);
                  setShowTopControls(false);
                }}
                disabled={!online}
                title={online ? 'AI Edit — describe the change' : 'AI Edit needs a connection'}
              ><FaComment /></button>
              {canEditTiers && (
                <>
                  <button
                    className="control-btn vertical"
                    onClick={() => { setOpenGroup(null); handleMagicAddTier(); }}
                    disabled={aiHold || !effectiveImageId}
                    title="Add Cake Tier (AI)"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <ellipse cx="12" cy="19" rx="9" ry="2.3" fill="currentColor" opacity="0.9"/>
                      <ellipse cx="12" cy="13.5" rx="6.5" ry="2" fill="currentColor" opacity="0.75"/>
                      <ellipse cx="12" cy="9" rx="4" ry="1.6" fill="currentColor" opacity="0.6"/>
                      <path d="M12 9 L12 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                      <circle cx="12" cy="3.5" r="1" fill="currentColor"/>
                    </svg>
                  </button>
                  <button
                    className="control-btn vertical"
                    onClick={() => { setOpenGroup(null); handleMagicRemoveTier(); }}
                    disabled={aiHold || !effectiveImageId}
                    title="Remove Cake Tier (AI)"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <ellipse cx="12" cy="19" rx="9" ry="2.3" fill="currentColor" opacity="0.9"/>
                      <ellipse cx="12" cy="13.5" rx="6.5" ry="2" fill="currentColor" opacity="0.75"/>
                      <ellipse cx="12" cy="7.5" rx="4" ry="1.6" fill="none" stroke="currentColor" strokeWidth="1.1" strokeDasharray="2 1.6" opacity="0.85"/>
                      <path d="M17.5 5.5 L21.5 5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                    </svg>
                  </button>
                </>
              )}
                    </div>
                  </div>
                </>, document.body)}
            </>
            )}
            {/* Same reasoning as the broom: an armed figure tool is not a reason to hide the
                canvas's own tools, and the pen never hid them. Only a selection does. */}
            {(activeTool === 'draw' || !selectedShapeId) && (<button
              className={`control-btn vertical ${activeTool === 'selection' ? 'active' : ''}`}
              onClick={() => handleToolSelect('selection')}
              title="Crop Selection Tool"
              disabled={canvasImages.length === 0 && drawingPaths.length === 0 && canvasShapes.length === 0}
            >
              <FaVectorSquare />
            </button>)}
            {/* Shown whenever nothing is selected — an armed figure tool no longer hides it. The
                pen already kept it on screen (the activeTool === 'draw' arm), so hiding it only
                for figures was an inconsistency, and after clicking the sheet to put an object
                down the broom is exactly what someone reaches for next. It still steps aside
                while an object IS selected, where Delete is the right button instead. */}
            {(activeTool === 'draw' || (!selectedShapeId && !selectedImage)) && (<button
              className={`control-btn vertical${pendingClear ? ' save-blink' : ''}`}
              onClick={clearCanvas}
              title={pendingClear ? 'Tap again to clear everything' : 'Clear Canvas'}
              disabled={canvasImages.length === 0 && drawingPaths.length === 0 && canvasShapes.length === 0}
            >
              <FaBroom />
            </button>)}
            {/* Bring front / send back works on layerIndex, which only figures and images
                have -- every hand-drawn stroke shares one canvas, so a stroke has no layer of
                its own to raise and the button stays out for ink rather than doing nothing. */}
            {((selectedShapeId && canvasShapes.some(s => s.id === selectedShapeId)) || selectedImage) && <button
              className="control-btn vertical"
              onClick={() => {
                const selectedId = selectedShapeId || selectedImage;
                if (!selectedId) return;
                const allItems = [
                  ...canvasImages.map(i => ({ id: i.id, lx: i.layerIndex ?? 1 })),
                  ...canvasShapes.map(s => ({ id: s.id, lx: s.layerIndex ?? 4 })),
                ].sort((a, b) => a.lx - b.lx);
                if (!allItems.length) return;
                const isAtFront = allItems[allItems.length - 1]?.id === selectedId;
                const newIdx = isAtFront
                  ? (allItems[0]?.lx ?? 0) - 1
                  : (allItems[allItems.length - 1]?.lx ?? 0) + 1;
                if (selectedShapeId) {
                  setCanvasShapes(prev => prev.map(s =>
                    s.id === selectedShapeId ? { ...s, layerIndex: newIdx } : s
                  ));
                } else {
                  setCanvasImages(prev => prev.map(i =>
                    i.id === selectedImage ? { ...i, layerIndex: newIdx } : i
                  ));
                }
              }}
              title="Bring Front / Send Back"
            >
              <FaLayerGroup />
            </button>}
            <button
              className="control-btn vertical"
              onClick={handleUndo}
              title="Undo (Ctrl+Z)"
              disabled={historyIndex < 0}
            >
              <FaUndo />
            </button>
            <button
              className="control-btn vertical"
              onClick={handleRedo}
              title="Redo (Ctrl+Y)"
              disabled={historyIndex >= drawingHistory.length - 1}
            >
              <FaRedo />
            </button>
            {/* Join: in the tool list, as an icon, beside Undo — it appears only while two or more
                selected objects overlap, which is the only time there is anything to join. */}
            {selectedShapeIds.size > 1 && selectionOverlaps(drawingPaths) && (
              <button
                className="control-btn vertical"
                onClick={joinSelectedObjects}
                title="Join the overlapping objects into one picture"
                aria-label="Join"
              >
                <FaObjectGroup />
              </button>
            )}
            {/* AI on an EMPTY sheet — make a picture out of a sentence.
                The AI tools used to live only inside the "an image is selected" block, so on a
                blank canvas there was no way to reach them at all: you needed a picture before
                the AI would help you get one. This button is that missing door; with an image
                selected the fuller AI group above takes over, so it steps aside. */}
            {canUseAi && !selectedImage && !selectedShapeId && isAiEnabled() && (
              <button
                className={`control-btn vertical${magicSubTool === 'chat' ? ' active' : ''}`}
                onClick={() => {
                  const next = magicSubTool === 'chat' ? null : 'chat';
                  setOpenGroup(null);
                  setMagicSubTool(next);
                  setCurrentControlSection(null);
                  setShowTopControls(false);
                }}
                disabled={!online}
                title={online ? 'AI — describe a picture to create' : 'Making a picture needs a connection'}
              ><FaMagic /></button>
            )}
            {/* Pointer: put the current tool down (nothing active, nothing selected) */}
            {(activeTool || activeShapeTool || selectedShapeId || selectedImage) && (
              <button
                className="control-btn vertical"
                onClick={neutralizeTools}
                title="Pointer — put the tool down and deselect (Esc)"
              >
                <FaMousePointer />
              </button>
            )}
            {/* Typeface, bold and italic — one button, one panel. All three are set-once
                choices, so they no longer each hold a slot: the font box has come down out of
                the top bar (where it was eating a third of the width on a phone, and hanging
                out of the bottom of it) and B and I have come in beside it. The button itself
                shows the face in use, so the panel only has to be opened to CHANGE something. */}
            {(activeShapeTool === 'text' || currentControlSection === 'shapes-text' || captionFontShape) && (
              <>
                <button
                  className={`control-btn vertical is-font-btn${(openGroup === 'font' || fontPanelBold || fontPanelItalic) ? ' active' : ''}`}
                  /* Reaching for this button is not finishing the caption: without this the
                     press pulled the focus out of the caption box, which committed it and left
                     the whole figure selected instead of the words being written. */
                  onMouseDown={e => e.preventDefault()}
                  onClick={(e) => openGroupPanel(e, 'font', 5)}
                  title={`Font — ${fontPanelFamily}${fontPanelBold ? ', bold' : ''}${fontPanelItalic ? ', italic' : ''}`}
                >
                  <FaFont />
                  <span className="figures-btn__caret" aria-hidden="true">▾</span>
                </button>
                {openGroup === 'font' && createPortal(
                  <>
                    <div className="is-sheet-scrim" onClick={() => setOpenGroup(null)} />
                    <div className="is-group-panel is-font-panel" style={{ top: groupPanelPos.top, left: groupPanelPos.left }} role="menu" aria-label="Font">
                      <div className="is-group-panel__title">Font</div>
                      <select value={fontPanelFamily} onChange={e => setFontPanelFamily(e.target.value)}
                        title="Font"
                        style={{ fontFamily: fontPanelFamily }}
                        className="is-font-select">
                        {SHAPE_FONTS.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
                      </select>
                      <div className="is-group-panel__row">
                        <button
                          className={`control-btn vertical ${fontPanelBold ? 'active' : ''}`}
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => setFontPanelBold(!fontPanelBold)}
                          title="Bold"
                        ><strong>B</strong></button>
                        <button
                          className={`control-btn vertical ${fontPanelItalic ? 'active' : ''}`}
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => setFontPanelItalic(!fontPanelItalic)}
                          title="Italic"
                        ><em style={{ fontStyle: 'italic' }}>I</em></button>
                        <button
                          className={`control-btn vertical ${fontPanelUnderline ? 'active' : ''}`}
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => setFontPanelUnderline(!fontPanelUnderline)}
                          title="Underline"
                        ><span style={{ textDecoration: 'underline' }}>U</span></button>
                        {/* Raised letters. Any typeface can take it — it is the words drawn a few
                            times behind themselves, not a 3D object — so it belongs here with
                            bold and italic rather than in the figures list. */}
                        <button
                          className={`control-btn vertical ${fontPanel3D ? 'active' : ''}`}
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => setFontPanel3D(!fontPanel3D)}
                          title="3D — raised letters"
                        ><span className="is-3d-a">3D</span></button>
                      </div>
                    </div>
                  </>,
                  document.body
                )}
              </>
            )}
            {/* Format Painter -- shown for draw and text shapes when not in select/move mode.
                A hand-drawn stroke has no border/fill/font set to copy, and the button's own
                handler only ever looks in canvasShapes, so it stays out for ink. */}
            {selectedShapeId && canvasShapes.some(s => s.id === selectedShapeId) && (currentControlSection === 'shapes-draw' || currentControlSection === 'shapes-text') && (
              <button
                className={`control-btn vertical ${formatPainterActive ? 'active' : ''}`}
                onClick={() => {
                  if (formatPainterActive) {
                    setFormatPainterActive(false);
                    formatPainterDataRef.current = null;
                    return;
                  }
                  const shape = canvasShapes.find(s => s.id === selectedShapeId);
                  if (shape) {
                    if (shape.type === 'text') {
                      formatPainterDataRef.current = {
                        _isText: true,
                        strokeColor: shape.strokeColor,
                        fontSize: shape.fontSize,
                        fontFamily: shape.fontFamily,
                        bold: shape.bold,
                        italic: shape.italic,
                        underline: shape.underline,
                        threeD: shape.threeD,
                        outlineWidth: shape.outlineWidth,
                      };
                    } else {
                      formatPainterDataRef.current = {
                        strokeColor: shape.strokeColor,
                        fillColor: shape.fillColor,
                        strokeWidth: shape.strokeWidth,
                        dashed: shape.dashed,
                      };
                    }
                    setFormatPainterActive(true);
                  }
                }}
                title="Format Painter"
              >
                <FaPaintBrush />
              </button>
            )}
            <div className="magic-divider" />
            {/* Drawing tools — in the SAME row as everything else, on every screen size. They
                used to live in a collapsible second row that phones opened via a swipe or a
                shortcut button; once that shortcut went, small screens had no way to reach
                them. One horizontally scrolling row has no such trap. The five FIGURES (line,
                arrow, circle, triangle, rectangle) sit under one control that shows the current
                figure and opens a small picker. */}
              {/* Draw group: one button on small screens; expands to 3D, figures, pen, text, stamps */}
              <button
                className={`control-btn vertical is-group-btn${(showDrawGroup || drawToolActive) ? ' active' : ''}`}
                onClick={(e) => openGroupPanel(e, 'draw', 5)}
                title="Drawing tools"
              >
                <FaPencilRuler />
                <span className="figures-btn__caret" aria-hidden="true">▾</span>
              </button>
              {showDrawGroup && createPortal(
                <>
                  <div className="is-sheet-scrim" onClick={() => setOpenGroup(null)} />
                  <div className="is-group-panel" style={{ top: groupPanelPos.top, left: groupPanelPos.left }} role="menu" aria-label="Drawing tools">
                    <div className="is-group-panel__title">Draw</div>
                    <div className="is-group-panel__row">
              {/* 3D: while on, the Figures picker lists 3D objects instead of flat figures */}
              <button
                className={`control-btn vertical${figures3D ? ' active' : ''}`}
                onClick={() => setFigures3D(v => !v)}
                title={figures3D ? '3D figures on — the Figures button lists 3D objects' : '3D figures — switch the Figures button to 3D objects'}
              >
                <Cube3DIcon active={figures3D} />
              </button>
              {(() => {
                const FIGURES = figures3D ? FIG_3D : FIG_2D;
                const currentFigure = FIG_ALL.find(f => f.tool === activeShapeTool)
                  || FIG_ALL.find(f => f.tool === lastUsedShapeTool)
                  || (figures3D ? FIG_3D[0] : FIG_2D[4]);
                const figureActive = FIG_ALL.some(f => f.tool === activeShapeTool);
                const COLS = 5, CELL = 42;
                const rows = Math.ceil(FIGURES.length / COLS);
                return (
                  <button
                    ref={figuresBtnRef}
                    className={`control-btn vertical figures-btn ${figureActive ? 'active' : ''}`}
                    onClick={(e) => {
                      const r = e.currentTarget.getBoundingClientRect();
                      const MENU_W = COLS * CELL + 12, MENU_H = rows * CELL + 12;
                      let left = r.left, top = r.bottom + 6;
                      if (left + MENU_W > window.innerWidth - 6) left = window.innerWidth - MENU_W - 6;
                      if (top + MENU_H > window.innerHeight - 6) top = Math.max(6, r.top - MENU_H - 6);
                      setFiguresMenuPos({ top, left });
                      setShowFiguresMenu(v => !v);
                    }}
                    title={`Figures — current: ${currentFigure.title}${figures3D ? ' (3D list)' : ''}`}
                  >
                    <ShapeIcon type={currentFigure.tool} />
                    <span className="figures-btn__caret" aria-hidden="true">▾</span>
                  </button>
                );
              })()}
              {showFiguresMenu && createPortal(
                <>
                  <div className="is-sheet-scrim" onClick={() => setShowFiguresMenu(false)} />
                  <div className={`figures-menu${figures3D ? ' is-3d' : ''}`} style={{ top: figuresMenuPos.top, left: figuresMenuPos.left }} role="menu" aria-label={figures3D ? '3D objects' : 'Figures'}>
                    {(figures3D ? FIG_3D : FIG_2D).map(({ tool, title }) => (
                      <button
                        key={tool}
                        className={`control-btn vertical ${activeShapeTool === tool ? 'active' : ''}`}
                        onClick={() => { setShowFiguresMenu(false); setOpenGroup(null); handleShapeToolSelect(tool); }}
                        title={title}
                      ><ShapeIcon type={tool} size={22} /></button>
                    ))}
                  </div>
                </>,
                document.body
              )}
              {[
                { tool: 'handwriting', icon: <DrawToolIcon mode={activeTool === 'draw' ? drawingMode : 'pencil'} />,   title: 'Handwriting' },
                { tool: 'text',        icon: <FaFont />,        title: 'Text' },
                { tool: 'emoji',       icon: <FaImage />, title: 'Stamps' },
              ].map(({ tool, icon, title }) => (
                tool === 'emoji' ? (
                  <button
                    key={tool}
                    ref={emojiBtnRef}
                    className={`control-btn vertical ${activeShapeTool === 'emoji' ? 'active' : ''}`}
                    onClick={(e) => {
                      handleShapeToolSelect('emoji');
                      const btnRect = e.currentTarget.getBoundingClientRect();
                      const PANEL_W = 240;
                      const PANEL_H = 300;
                      const vw = window.innerWidth;
                      const vh = window.innerHeight;
                      // Prefer right of button, then left of button; clamp inside viewport
                      let left = btnRect.right + 6;
                      if (left + PANEL_W > vw - 6) left = btnRect.left - PANEL_W - 6;
                      if (left < 6) left = 6;
                      let top = btnRect.top;
                      if (top + PANEL_H > vh - 6) top = vh - PANEL_H - 6;
                      if (top < 6) top = 6;
                      setEmojiPopdownPos({ top, left });
                      setShowEmojiPopdown(v => !v);
                    }}
                    title="Stamps"
                  >
                    <FaImage />
                  </button>
                ) : (
                  <button
                    key={tool}
                    className={`control-btn vertical ${activeShapeTool === tool ? 'active' : ''}`}
                    onClick={() => { setOpenGroup(null); handleShapeToolSelect(tool); }}
                    title={title}
                  >
                    {icon}
                  </button>
                )
              ))}
                    </div>
                  </div>
                </>, document.body)}
          </div>{/* end is-tools-row (scrollable) */}
            {/* Help — lives here, in the always-visible tools strip, rather than the bottom
                Save/Layout/Share/Print bar or the top red bar. A sibling of the scrollable
                row above (not inside it), flex-shrink: 0, so it always sits at the true right
                edge of the screen instead of wherever the scrollable tools happen to end. */}
            <button
              className="control-btn vertical is-tools-help-btn"
              onClick={() => setShowHelpPanel(true)}
              data-oneadmin-help=""
              title="Help"
            >
              <FaQuestionCircle />
            </button>
          </div>{/* end is-tools-row-wrap */}
        </div>{/* end is-tools-panel-wrapper */}

        {/* AI processing progress bar -- removed, now inside magic-wand-toolbar */}

        {/* MAIN CANVAS AREA */}
        <div
          className="canvas-main-area"
          ref={canvasAreaRef}
          onMouseDown={handleAreaMouseDown}
          onMouseMove={handleAreaMouseMove}
          onMouseUp={handleAreaMouseUp}
          onMouseLeave={handleAreaMouseUp}
          onClick={handleAreaDeselect}
          onTouchEnd={handleAreaDeselect}
          style={fromCustom
            ? {}
            : (vpSize.w > vpSize.h && vpSize.h < 550)
              ? { top: '0', left: '0', right: '0', bottom: '54px' }
              : { top: 'var(--is-top, 46px)' }
          }
        >
          <div className="canvas-container">
            {(() => {
              const ps = PRINT_SIZES[printSize];
              const ar = ps.width / ps.height;
              const isLs = vpSize.w > vpSize.h && vpSize.h < 550;
              const SIDE = 54; // sidebar width in landscape
              // Breathing room around the sheet. It was 24, which on a laptop is a centimetre of
              // paper given up to nothing — the bars above and below already separate the sheet
              // from everything else, and the sheet draws its own dotted edge.
              const PAD = 4;
              const isMagicChat = magicSubTool === 'chat' && !fromCustom;
              // The AI Edit strip is one slim row now (~52px + its 6px margin), not the old
              // three-row card, so it only reserves a fraction of what it used to. Keep this in
              // step with .magic-chat-open .canvas-main-area in the CSS.
              const bottomReserve = (isMagicChat ? 112 : 50) + safeInset.bottom;
              const labelH = 0; // the layout row under the canvas is gone — the sheet gets that space
              const maxH = fromCustom
                ? vpSize.h - 40 - PAD              // fromCustom: only magic toolbar row (~40px)
                : isLs
                  ? vpSize.h - 54 - PAD              // landscape: bottom tools strip (54px)
                  // portrait: whatever the canvas area actually has inside its padding. The sum
                  // on the right is only the first paint's guess, before the area has been
                  // measured once.
                  : (areaH || (vpSize.h - 46 - safeInset.top - toolsStripH - bottomReserve - PAD - labelH)) - PAD;
              // .canvas-container keeps a side padding of its own, and it hides its overflow, so
              // the sheet has to fit INSIDE that. It is worth knowing exactly how much rather
              // than guessing high: every pixel handed back here is a pixel of paper, and on a
              // portrait sheet the height is usually what binds anyway.
              const sidePad = vpSize.w <= 480 ? 10 : vpSize.w <= 768 ? 20 : vpSize.w <= 1024 ? 30 : 0;
              const maxW = isLs
                ? vpSize.w - PAD - sidePad
                // The cap is only here to stop a sheet growing absurd on a very tall screen; at
                // 800 it was catching ordinary large monitors and leaving the paper smaller than
                // the room it had.
                : Math.min(vpSize.w - PAD - sidePad, 1400);
              // Fit the aspect ratio in the available space
              let cW = maxW;
              let cH = Math.round(cW / ar);
              if (cH > maxH) { cH = maxH; cW = Math.round(cH * ar); }
              cW = Math.max(cW, 120);
              cH = Math.max(cH, 80);
              return (
            <>
            <div 
              ref={canvasRef}
              className="a4-canvas"
              style={{
                cursor: canvasCursor,
                width: `${Math.round(cW)}px`,
                height: `${cH}px`,
                // An effect needs something to sit on, so a patterned sheet with no colour
                // chosen is painted white rather than left transparent.
                backgroundColor: canvasBackground !== 'transparent' ? canvasBackground
                  : (canvasBgFx ? '#ffffff' : undefined),
                ...(sheetFxStyle(canvasBgFx, Math.round(cW), isDarkSheet(canvasBackground)) || {}),
              }}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={(e) => {
                // When in any crop/selection mode, mouse leaving must NOT clear the overlay.
                // Just stop any active drag and let the user come back to confirm.
                if (activeTool === 'selection' && selectionMode) {
                  circleCropInteractRef.current = null;
                } else {
                  handleMouseUp(e);
                }
                setEraserPos(null);
              }}
              onClick={handleCanvasClick}
              onMouseDown={startDrawing}
              onTouchStart={(e) => {
                const touch = e.touches[0];
                const canvas = canvasRef.current;
                if (!canvas) return;
                
                const rect = canvas.getBoundingClientRect();
                const x = touch.clientX - rect.left;
                const y = touch.clientY - rect.top;
                
                if (activeTool === 'draw' && drawingMode === 'fill') {
                  e.preventDefault();
                  handleFillClick({ clientX: touch.clientX, clientY: touch.clientY });
                } else if (activeTool === 'draw' && (drawingMode === 'pen' || drawingMode === 'pencil' || drawingMode === 'eraser' || drawingMode === 'spray') && !fillModeActive) {
                  e.preventDefault();
                  setIsDrawing(true);
                  if (drawingMode === 'spray') {
                    const N = 25, radius = brushSize * 1.2;
                    const initDots = [];
                    for (let i = 0; i < N; i++) {
                      const angle = (i / N) * Math.PI * 2 + Math.random() * 0.4;
                      const r = (0.2 + Math.random() * 0.8) * radius;
                      initDots.push({ x: x + r * Math.cos(angle), y: y + r * Math.sin(angle) });
                    }
                    lastPenPointRef.current = { x, y }; setCurrentPath(initDots);
                  } else {
                    lastPenPointRef.current = { x, y }; setCurrentPath([{ x, y }]);
                    if (drawingMode === 'eraser') setEraserPos({ x, y });
                  }
                } else if (activeTool === 'selection' && selectionMode) {
                  e.preventDefault();
                  cropPointerDown(x, y);
                } else if (imageSelectionMode && !activeTool) {
                  e.preventDefault();
                  // See the mouse twin in startDrawing: a touch that lands on a hand-drawn
                  // line selects that stroke; a touch on empty sheet starts the marquee.
                  const _inkHitT = eyedropperActive ? null : inkPathAtPoint(x, y, rect);
                  if (_inkHitT != null) { selectInkPath(_inkHitT, x, y, rect); return; }
                  const xPct = (x / rect.width) * 100;
                  const yPct = (y / rect.height) * 100;
                  isMarqueeSelectingRef.current = true;
                  const mq = { x1: xPct, y1: yPct, x2: xPct, y2: yPct };
                  marqueeRectRef.current = mq;
                  setMarqueeRect(mq);
                  setSelectedShapeIds(new Set());
                  setSelectedShapeId(null);
                  setShowTopControls(false);
                  setCurrentControlSection(null);
                } else if (activeTool === 'shapes' && activeShapeTool) {
                  e.preventDefault();
                  const xPct = (x / rect.width) * 100;
                  const yPct = (y / rect.height) * 100;
                  if (activeShapeTool === 'text') {
                    // Text is created on click (onClick fires after touchEnd on mobile)
                  } else if (activeShapeTool === 'emoji') {
                    if (consumePressToDeselect()) {
                      setActiveShapeTool(null);
                      setActiveTool(null);
                      setCanvasCursor('default');
                      setCurrentControlSection(null);
                      setShowTopControls(false);
                      return;
                    }
                    if (!selectedEmoji) {
                      setActiveShapeTool(null);
                      setActiveTool(null);
                      setCanvasCursor('default');
                      return;
                    }
                    // Dropped on an image: embed into its pixels instead of adding an overlay shape.
                    {
                      const _hit = imageAtPercent(xPct, yPct);
                      if (_hit) {
                        const _fx = (xPct - _hit.x) / ((_hit.width / 21) * 100);
                        const _fy = (yPct - _hit.y) / ((_hit.height / 29.7) * 100);
                        embedStampIntoImage(_hit.id, selectedEmoji, { fx: _fx, fy: _fy });
                        setSelectedEmoji(null);
                        setActiveShapeTool(null);
                        setActiveTool(null);
                        setCanvasCursor('default');
                        return;
                      }
                    }
                    const _eBefore = canvasShapesRef.current;
                    const _cW = canvasRef.current?.offsetWidth  || 595;
                    const _cH = canvasRef.current?.offsetHeight || 841;
                    const _stampW = 20;
                    const _stampH = 20;
                    const newShape = {
                      id: Date.now(), type: 'emoji',
                      x1: xPct - _stampW / 2, y1: yPct - _stampH / 2,
                      x2: xPct + _stampW / 2, y2: yPct + _stampH / 2,
                      strokeColor: shapeStrokeColor, fillColor: 'none',
                      strokeWidth: shapeStrokeWidth, emoji: selectedEmoji, emojiSize: 0,
                      zOrder: canvasShapes.length, layerIndex: ++layerCounterRef.current,
                    };
                    const _eAfter = [..._eBefore, newShape];
                    setCanvasShapes(_eAfter);
                    setSelectedShapeId(newShape.id);
                    pushShapeHistory(_eBefore, _eAfter);
                    markDirty();
                    setSelectedEmoji(null);
                    setActiveShapeTool(null);
                    setActiveTool(null);
                    setCanvasCursor('default');
                    setShowToolsPanel(false);
                  } else {
                    // See the mouse twin in startDrawing: with something selected, this press
                    // puts it down rather than starting another figure.
                    if (consumePressToDeselect()) return;
                    if (isFreeTool(activeShapeTool)) { freeDraftRef.current = [{ x: xPct, y: yPct }]; setFreeDraft(freeDraftRef.current); }
                    setIsDrawingShape(true);
                    setShapeDrawStart({ x: xPct, y: yPct });
                    setShapeDrawEnd({ x: xPct, y: yPct });
                  }
                }
              }}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchEnd}
            >
              {/* Print size dotted frame overlay -- hidden in fromCustom mode */}
              {!fromCustom && <div className="print-size-overlay" style={{ width: '100%', height: '100%' }} />}
              {/* Centimetre ruling. Drawn from the PAPER's measurements rather than from pixels,
                  so a square is a square-centimetre on every screen and on every paper size: one
                  centimetre across is 100/width per cent of the sheet, and one down is
                  100/height. Every fifth line is darker, because counting past four faint ones
                  is how you lose your place. It is guide-work, not artwork — behind everything,
                  deaf to the pointer, and never saved or printed. */}
              {showGrid && !fromCustom && (() => {
                const _gps = PRINT_SIZES[printSize];
                const gw = 100 / _gps.width, gh = 100 / _gps.height;
                const faint = 'rgba(70,110,210,0.22)', firm = 'rgba(70,110,210,0.42)';
                return (
                  <div
                    className="is-grid-overlay"
                    style={{
                      backgroundImage:
                        `linear-gradient(to right, ${firm} 1px, transparent 1px),`
                        + `linear-gradient(to bottom, ${firm} 1px, transparent 1px),`
                        + `linear-gradient(to right, ${faint} 1px, transparent 1px),`
                        + `linear-gradient(to bottom, ${faint} 1px, transparent 1px)`,
                      backgroundSize:
                        `${gw * 5}% ${gh * 5}%, ${gw * 5}% ${gh * 5}%, ${gw}% ${gh}%, ${gw}% ${gh}%`,
                    }}
                  />
                );
              })()}
              {/* Eraser cursor circle overlay */}
              {activeTool === 'draw' && drawingMode === 'eraser' && eraserPos && (
                <div style={{
                  position: 'absolute',
                  left: eraserPos.x - brushSize / 2,
                  top: eraserPos.y - brushSize / 2,
                  width: brushSize,
                  height: brushSize,
                  borderRadius: '50%',
                  border: '2px solid rgba(100,100,100,0.85)',
                  boxShadow: '0 0 0 1px rgba(255,255,255,0.7)',
                  pointerEvents: 'none',
                  zIndex: 10000,
                  boxSizing: 'border-box',
                }} />
              )}
              {/* Drawings -- canvas for reliable destination-out eraser */}
              <canvas
                ref={drawingCanvasRef}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 9999
                }}
              />

              {/* ---- Per-shape SVGs for independent z-index layering ---- */}
              {canvasShapes.map(shape => {
                // Hide the shape currently open in inline text edit to avoid double-render
                if (pendingTextShapeId && shape.id === pendingTextShapeId) return null;
                const el = renderShapeElement(shape, cW, cH);
                if (!el) return null;
                return (
                  <svg key={shape.id} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'visible', zIndex: shape.layerIndex ?? 4, pointerEvents: 'none' }}>
                    {el}
                  </svg>
                );
              })}

              {/* Nodes of the selected freeform figure — drag to reshape */}
              {selectedShapeId && (() => {
                const sh = canvasShapes.find(s => s.id === selectedShapeId);
                if (!sh || sh.type !== 'freeform' || !sh.pts || sh.rotation) return null;
                const bx = Math.min(sh.x1, sh.x2) / 100 * cW, by = Math.min(sh.y1, sh.y2) / 100 * cH;
                const bw = Math.abs(sh.x2 - sh.x1) / 100 * cW, bh = Math.abs(sh.y2 - sh.y1) / 100 * cH;
                return (
                  <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'visible', zIndex: 9000, pointerEvents: 'none' }}>
                    {sh.pts.map((p, i) => (
                      <circle key={i} cx={bx + p.x * bw} cy={by + p.y * bh} r={7} fill="#fff" stroke="#fa4848" strokeWidth={2}
                        style={{ pointerEvents: 'all', cursor: 'grab' }}
                        onMouseDown={(e) => startNodeDrag(e, sh.id, i)}
                        onTouchStart={(e) => startNodeDrag(e, sh.id, i)} />
                    ))}
                  </svg>
                );
              })()}

              {/* Stamp images -- HTML <img> layer avoids SVG cross-origin restrictions */}
              {canvasShapes
                .filter(s => s.type === 'emoji' && s.emoji)
                .map(s => {
                  const minX = Math.min(s.x1, s.x2);
                  const minY = Math.min(s.y1, s.y2);
                  const w    = Math.abs(s.x2 - s.x1) || 5;
                  const h    = Math.abs(s.y2 - s.y1) || 5;
                  const isSel = selectedShapeId === s.id || selectedShapeIds.has(s.id);
                  return (
                    <img
                      key={`stamp-img-${s.id}`}
                      src={s.emoji}
                      alt="stamp"
                      style={{
                        position: 'absolute',
                        left: `${minX}%`,
                        top: `${minY}%`,
                        width: `${w}%`,
                        height: `${h}%`,
                        objectFit: 'contain',
                        pointerEvents: 'none',
                        zIndex: (s.layerIndex ?? 4),
                        transform: s.rotation ? `rotate(${s.rotation}deg)` : undefined,
                        transformOrigin: 'center center',
                        filter: isSel ? 'drop-shadow(0 0 3px #fa4848) drop-shadow(0 0 6px #fa4848) drop-shadow(0 0 10px rgba(250,72,72,0.7))' : undefined,
                      }}
                    />
                  );
                })
              }
              {/* Marquee selection rectangle -- shown while drag-selecting in imageSelectionMode */}
              {marqueeRect && imageSelectionMode && (
                <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'visible', zIndex: 9010, pointerEvents: 'none' }}>
                  <rect
                    x={`${Math.min(marqueeRect.x1, marqueeRect.x2)}%`}
                    y={`${Math.min(marqueeRect.y1, marqueeRect.y2)}%`}
                    width={`${Math.abs(marqueeRect.x2 - marqueeRect.x1)}%`}
                    height={`${Math.abs(marqueeRect.y2 - marqueeRect.y1)}%`}
                    fill="rgba(74,144,226,0.1)"
                    stroke="#4a90e2"
                    strokeWidth={1.5}
                    strokeDasharray="6 3"
                  />
                </svg>
              )}
              {isDrawingShape && shapeDrawStart && shapeDrawEnd && (
                <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'visible', zIndex: 9000, pointerEvents: 'none' }}>
                  {isFreeTool(activeShapeTool) && freeDraft.length >= 2
                    ? renderShapeElement({
                        id: 'preview', type: 'freeform', closed: activeShapeTool === 'freeshape', smooth: false,
                        ...normalizeFreeform(freeDraft),
                        strokeColor: shapeStrokeColor, fillColor: 'none', strokeWidth: shapeStrokeWidth,
                      }, cW, cH)
                    : renderShapeElement({
                    id: 'preview', type: activeShapeTool,
                    x1: shapeDrawStart.x, y1: shapeDrawStart.y,
                    x2: shapeDrawEnd.x,   y2: shapeDrawEnd.y,
                    strokeColor: shapeStrokeColor, fillColor: shapeFillColor,
                    strokeWidth: shapeStrokeWidth,
                  }, cW, cH)}
                </svg>
              )}
              {/* Rotation handle for selected image -- stem + circle above the image top edge */}
              {imageSelectionMode && selectedImage && (() => {
                const img = canvasImages.find(i => i.id === selectedImage);
                if (!img) return null;
                const cx = (img.x / 100 + (img.width / 21) / 2) * cW;
                const cy = (img.y / 100 + (img.height / 29.7) / 2) * cH;
                const h_px = (img.height / 29.7) * cH;
                const topY = cy - h_px / 2;
                const rot = img.rotation || 0;
                const startRotateImg = (e) => {
                  e.stopPropagation();
                  shapeClickedRef.current = true; // absorb the following click so handleCanvasClick won't clear state
                  const canvas = canvasRef.current;
                  if (!canvas) return;
                  const rect = canvas.getBoundingClientRect();
                  rotateImageCenterRef.current = {
                    id: img.id,
                    cx: (img.x / 100 + (img.width / 21) / 2) * rect.width,
                    cy: (img.y / 100 + (img.height / 29.7) / 2) * rect.height,
                    before: canvasImagesRef.current,
                  };
                  setRotatingImage(img.id);
                  setSelectMoveControlMode('rotate');
                  setCurrentControlSection('selectmove');
                  setShowTopControls(true);
                };
                // Under the picture: the blue orbit handle, the same one a 3D figure has. Sideways
                // spins the picture, up and down tilts it (see renderTurnedPicture).
                const bottomY = cy + h_px / 2;
                const startOrbitImg = (e) => {
                  e.stopPropagation();
                  shapeClickedRef.current = true;   // absorb the click that follows the release
                  const r = canvasRef.current && canvasRef.current.getBoundingClientRect();
                  const t = e.touches ? e.touches[0] : e;
                  if (!r || !t) return;
                  const ang = turnAnglesOf(img);
                  imageOrbitRef.current = {
                    id: img.id, sx: t.clientX - r.left, sy: t.clientY - r.top,
                    rx: ang.rx, ry: ang.ry, cur: ang, el: null, before: canvasImagesRef.current,
                  };
                  loadImgSafe(turnSourceOf(img)).then((el) => {
                    if (imageOrbitRef.current && imageOrbitRef.current.id === img.id) imageOrbitRef.current.el = el;
                  });
                  setSelectMoveControlMode('rotate');
                  setCurrentControlSection('selectmove');
                  setShowTopControls(true);
                };
                return (
                  <svg key={`rot-handle-${img.id}`} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'visible', zIndex: 9002, pointerEvents: 'none' }}>
                    <g transform={`rotate(${rot} ${cx} ${cy})`} style={{ pointerEvents: 'all' }}>
                      <line x1={cx} y1={bottomY + 3} x2={cx} y2={bottomY + 22}
                        stroke="#2f7fd6" strokeWidth={1.5} strokeDasharray="3 2"
                        style={{ pointerEvents: 'none' }} />
                      <circle cx={cx} cy={bottomY + 30} r={20} fill="transparent" stroke="none"
                        style={{ cursor: 'move', pointerEvents: 'all' }}
                        onMouseDown={e => startOrbitImg(e)}
                        onTouchStart={e => { e.preventDefault(); startOrbitImg(e); }}
                      />
                      <circle cx={cx} cy={bottomY + 30} r={9} fill="white" stroke="#2f7fd6" strokeWidth={1.5}
                        style={{ pointerEvents: 'none' }} />
                      <ellipse cx={cx} cy={bottomY + 30} rx={6} ry={2.6} fill="none" stroke="#2f7fd6" strokeWidth={1.2}
                        style={{ pointerEvents: 'none' }} />
                      <circle cx={cx} cy={bottomY + 30} r={2} fill="#2f7fd6"
                        style={{ pointerEvents: 'none' }} />
                      <line x1={cx} y1={topY - 3} x2={cx} y2={topY - 22}
                        stroke="#fa4848" strokeWidth={1.5} strokeDasharray="3 2"
                        style={{ pointerEvents: 'none' }} />
                      <circle cx={cx} cy={topY - 30} r={8} fill="white" stroke="#fa4848" strokeWidth={1.5}
                        style={{ cursor: 'grab', pointerEvents: 'all' }}
                        onMouseDown={e => startRotateImg(e)}
                        onTouchStart={e => { e.preventDefault(); startRotateImg(e); }}
                      />
                      <text x={cx} y={topY - 26} textAnchor="middle" dominantBaseline="middle"
                        fontSize={10} fill="#fa4848"
                        style={{ pointerEvents: 'none', userSelect: 'none' }}>&#x21BB;</text>
                    </g>
                  </svg>
                );
              })()}

              {/* Resize/rotate handles for selected shape -- only in select/move mode */}
              {imageSelectionMode && selectedShapeId && (() => {
                const selShape = canvasShapes.find(s => s.id === selectedShapeId);
                if (!selShape) return null;
                const hx1 = (selShape.x1 / 100) * cW;
                const hy1 = (selShape.y1 / 100) * cH;
                const hx2 = (selShape.x2 / 100) * cW;
                const hy2 = (selShape.y2 / 100) * cH;
                const minX = Math.min(hx1, hx2), maxX = Math.max(hx1, hx2);
                const minY = Math.min(hy1, hy2), maxY = Math.max(hy1, hy2);
                const mx = (minX + maxX) / 2, my = (minY + maxY) / 2;
                const isEndpoint   = selShape.type === 'line' || selShape.type === 'arrow';
                const isTextLike   = selShape.type === 'text' || selShape.type === 'emoji';
                const rot          = selShape.rotation || 0;
                // Grabbing a handle shows the numbers, whatever mode the studio happens to be
                // in. These two used to switch the bar over only while imageSelectionMode was
                // on, which is why a figure's corner gave no W/H readout while an image's did —
                // the image handles have always done it unconditionally.
                const startResize  = (e, corner) => { e.stopPropagation(); shapeBeforeRef.current = canvasShapesRef.current; resizingShapeRef.current = { corner, id: selectedShapeId }; setIsResizingShape(true); setSelectMoveControlMode('resize'); shapeClickedRef.current = true; setCurrentControlSection('selectmove'); setShowTopControls(true); };
                const startRotate  = (e) => { e.stopPropagation(); shapeBeforeRef.current = canvasShapesRef.current; rotatingShapeRef.current = { id: selectedShapeId, cx: mx, cy: my }; setIsRotatingShape(true); setSelectMoveControlMode('rotate'); shapeClickedRef.current = true; setCurrentControlSection('selectmove'); setShowTopControls(true); };
                const startOrbit = (e) => {
                  e.stopPropagation();
                  const r = canvasRef.current && canvasRef.current.getBoundingClientRect();
                  const t = e.touches ? e.touches[0] : e;
                  if (!r || !t) return;
                  shapeBeforeRef.current = canvasShapesRef.current;
                  orbitShapeRef.current = {
                    id: selectedShapeId,
                    sx: t.clientX - r.left, sy: t.clientY - r.top,
                    rx: pose3D(selShape).rx, ry: pose3D(selShape).ry,
                  };
                  setIsRotatingShape(true);
                  setSelectMoveControlMode('rotate');
                  shapeClickedRef.current = true;
                  setCurrentControlSection('selectmove');
                  setShowTopControls(true);
                };
                const cornerHandles = isEndpoint
                  ? [{ id: 'p1', cx: hx1, cy: hy1 }, { id: 'p2', cx: hx2, cy: hy2 }]
                  : [{ id: 'nw', cx: minX, cy: minY }, { id: 'ne', cx: maxX, cy: minY },
                     { id: 'sw', cx: minX, cy: maxY }, { id: 'se', cx: maxX, cy: maxY }];
                return (
                  <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'visible', zIndex: 9001, pointerEvents: 'none' }}>
                    <g transform={`rotate(${rot} ${mx} ${my})`} style={{ pointerEvents: 'all' }}>
                      {/* Dashed bounding rect for all non-endpoint shapes */}
                      {!isEndpoint && (
                        <rect x={minX - 3} y={minY - 3} width={maxX - minX + 6} height={maxY - minY + 6}
                          fill="none" stroke="#fa4848" strokeWidth={1.5} strokeDasharray="6 3"
                          style={{ pointerEvents: 'none' }} />
                      )}
                      {/* Corner / endpoint handles -- the visible dot is only 12px across, far
                          below a usable touch target, so an invisible r=20 circle (~40px, in
                          line with the images' 44px resize zones) underneath it does the actual
                          hit-testing; the small dot on top is pointer-events:none, purely
                          decorative, so it never blocks the bigger zone beneath it. */}
                      {cornerHandles.map(h => (
                        <g key={h.id}>
                          <circle cx={h.cx} cy={h.cy} r={20} fill="transparent" stroke="none"
                            style={{ cursor: isEndpoint ? 'crosshair' : 'pointer', pointerEvents: 'all' }}
                            onMouseDown={e => startResize(e, h.id)}
                            onTouchStart={e => startResize(e, h.id)}
                          />
                          <circle cx={h.cx} cy={h.cy} r={6} fill="white" stroke="#fa4848" strokeWidth={1.5}
                            style={{ pointerEvents: 'none' }} />
                        </g>
                      ))}
                      {/* 3D only: the orbit handle, under the figure. Dragging it sideways
                          spins the object and up/down tilts it, so the faces that were hidden
                          round the back can be turned towards you. The handle above still does
                          the flat in-plane turn. */}
                      {isShape3D(selShape.type) && (
                        <>
                          <line x1={mx} y1={maxY} x2={mx} y2={maxY + 22}
                            stroke="#2f7fd6" strokeWidth={1.5} strokeDasharray="3 2"
                            style={{ pointerEvents: 'none' }} />
                          <circle cx={mx} cy={maxY + 30} r={20} fill="transparent" stroke="none"
                            style={{ cursor: 'move', pointerEvents: 'all' }}
                            onMouseDown={e => startOrbit(e)}
                            onTouchStart={e => { e.preventDefault(); startOrbit(e); }}
                          />
                          <circle cx={mx} cy={maxY + 30} r={9} fill="white" stroke="#2f7fd6" strokeWidth={1.5}
                            style={{ pointerEvents: 'none' }} />
                          <ellipse cx={mx} cy={maxY + 30} rx={6} ry={2.6} fill="none" stroke="#2f7fd6" strokeWidth={1.2}
                            style={{ pointerEvents: 'none' }} />
                          <circle cx={mx} cy={maxY + 30} r={2} fill="#2f7fd6"
                            style={{ pointerEvents: 'none' }} />
                        </>
                      )}
                      {/* Rotation handle -- stem + circle -- for all non-endpoint shapes */}
                      {!isEndpoint && (
                        <>
                          <line x1={mx} y1={minY} x2={mx} y2={minY - 22}
                            stroke="#fa4848" strokeWidth={1.5} strokeDasharray="3 2"
                            style={{ pointerEvents: 'none' }} />
                          <circle cx={mx} cy={minY - 30} r={20} fill="transparent" stroke="none"
                            style={{ cursor: 'grab', pointerEvents: 'all' }}
                            onMouseDown={e => startRotate(e)}
                            onTouchStart={e => { e.preventDefault(); startRotate(e); }}
                          />
                          <circle cx={mx} cy={minY - 30} r={8} fill="white" stroke="#fa4848" strokeWidth={1.5}
                            style={{ pointerEvents: 'none' }} />
                          <text x={mx} y={minY - 26} textAnchor="middle" dominantBaseline="middle"
                            fontSize={10} fill="#fa4848"
                            style={{ pointerEvents: 'none', userSelect: 'none' }}>↻</text>
                        </>
                      )}
                    </g>
                  </svg>
                );
              })()}

              {/* Selected hand-drawn stroke -- outline, corner handles, rotation handle, and the
                  body area that drags it. Deliberately above the ink canvas (z 9999) so the
                  outline is never buried under the very strokes it is marking. Only one of this
                  block and the figure block above can render: an id matches a figure or a
                  stroke, never both. */}
              {imageSelectionMode && selectedShapeId && (() => {
                const selInk = drawingPaths.find(p => p.id === selectedShapeId);
                if (!selInk) return null;
                const b = inkPathBBoxPct(selInk);
                if (!b) return null;
                const iMinX = (b.x1 / 100) * cW, iMaxX = (b.x2 / 100) * cW;
                const iMinY = (b.y1 / 100) * cH, iMaxY = (b.y2 / 100) * cH;
                const iMidX = (iMinX + iMaxX) / 2;
                const iW = Math.max(0, iMaxX - iMinX), iH = Math.max(0, iMaxY - iMinY);
                const pointerAt = (e) => {
                  const r = canvasRef.current && canvasRef.current.getBoundingClientRect();
                  if (!r) return null;
                  const t = e.touches ? e.touches[0] : e;
                  if (!t) return null;
                  return { r, px: t.clientX - r.left, py: t.clientY - r.top };
                };
                const startInkDrag = (e) => {
                  e.stopPropagation();
                  const at = pointerAt(e); if (!at) return;
                  const o = inkPathOriginPct(selInk); if (!o) return;
                  shapeClickedRef.current = true;
                  inkBeforeRef.current = drawingPathsRef.current;
                  inkDraggedRef.current = false;
                  draggingInkRef.current = {
                    id: selInk.id,
                    offsetX: at.px - (o.xPct / 100) * at.r.width,
                    offsetY: at.py - (o.yPct / 100) * at.r.height,
                  };
                };
                const startInkResize = (e, corner) => {
                  e.stopPropagation();
                  const lb = inkPathLocalBBox(selInk); if (!lb) return;
                  // Anchor = the corner opposite the one being dragged; it stays put.
                  const anchor = corner === 'se' ? { x: lb.minX, y: lb.minY }
                    : corner === 'nw' ? { x: lb.maxX, y: lb.maxY }
                    : corner === 'ne' ? { x: lb.minX, y: lb.maxY }
                    : { x: lb.maxX, y: lb.minY };
                  shapeClickedRef.current = true;
                  inkBeforeRef.current = drawingPathsRef.current;
                  resizingInkRef.current = {
                    id: selInk.id, corner, anchor,
                    origW: (lb.maxX - lb.minX) || 1, origH: (lb.maxY - lb.minY) || 1,
                    origPath: selInk.path,
                  };
                  setIsResizingShape(true);
                  setSelectMoveControlMode('resize');
                  setCurrentControlSection('selectmove');
                  setShowTopControls(true);
                };
                const startInkRotate = (e) => {
                  e.stopPropagation();
                  const lb = inkPathLocalBBox(selInk); if (!lb) return;
                  shapeClickedRef.current = true;
                  inkBeforeRef.current = drawingPathsRef.current;
                  rotatingInkRef.current = {
                    id: selInk.id,
                    cx: (lb.minX + lb.maxX) / 2, cy: (lb.minY + lb.maxY) / 2,
                    origPath: selInk.path,
                  };
                  setInkGestureAngle(0);
                  setIsRotatingShape(true);
                  setSelectMoveControlMode('rotate');
                  setCurrentControlSection('selectmove');
                  setShowTopControls(true);
                };
                const inkCorners = [
                  { id: 'nw', cx: iMinX, cy: iMinY }, { id: 'ne', cx: iMaxX, cy: iMinY },
                  { id: 'sw', cx: iMinX, cy: iMaxY }, { id: 'se', cx: iMaxX, cy: iMaxY },
                ];
                return (
                  <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'visible', zIndex: 10001, pointerEvents: 'none' }}>
                    <g style={{ pointerEvents: 'all' }}>
                      {/* Body: once the stroke is selected, a press anywhere inside its box
                          moves it -- edge to resize, middle to move, the same rule the figures
                          and images follow. Before selection there is no body at all: only the
                          drawn line itself answers a tap (see inkPathAtPoint). */}
                      <rect x={iMinX} y={iMinY} width={iW} height={iH}
                        fill="transparent" stroke="none"
                        style={{ cursor: 'move', pointerEvents: 'all' }}
                        onMouseDown={startInkDrag}
                        onTouchStart={e => { e.preventDefault(); startInkDrag(e); }} />
                      <rect x={iMinX - 3} y={iMinY - 3} width={iW + 6} height={iH + 6}
                        fill="none" stroke="#fa4848" strokeWidth={1.5} strokeDasharray="6 3"
                        style={{ pointerEvents: 'none' }} />
                      {/* Same sizing as the figure handles: an invisible r=20 (~40px) circle
                          does the hit-testing, the small dot on top is decoration. */}
                      {inkCorners.map(h => (
                        <g key={h.id}>
                          <circle cx={h.cx} cy={h.cy} r={20} fill="transparent" stroke="none"
                            style={{ cursor: 'pointer', pointerEvents: 'all' }}
                            onMouseDown={e => startInkResize(e, h.id)}
                            onTouchStart={e => { e.preventDefault(); startInkResize(e, h.id); }} />
                          <circle cx={h.cx} cy={h.cy} r={6} fill="white" stroke="#fa4848" strokeWidth={1.5}
                            style={{ pointerEvents: 'none' }} />
                        </g>
                      ))}
                      <line x1={iMidX} y1={iMinY} x2={iMidX} y2={iMinY - 22}
                        stroke="#fa4848" strokeWidth={1.5} strokeDasharray="3 2"
                        style={{ pointerEvents: 'none' }} />
                      <circle cx={iMidX} cy={iMinY - 30} r={20} fill="transparent" stroke="none"
                        style={{ cursor: 'grab', pointerEvents: 'all' }}
                        onMouseDown={startInkRotate}
                        onTouchStart={e => { e.preventDefault(); startInkRotate(e); }} />
                      <circle cx={iMidX} cy={iMinY - 30} r={8} fill="white" stroke="#fa4848" strokeWidth={1.5}
                        style={{ pointerEvents: 'none' }} />
                      <text x={iMidX} y={iMinY - 26} textAnchor="middle" dominantBaseline="middle"
                        fontSize={10} fill="#fa4848"
                        style={{ pointerEvents: 'none', userSelect: 'none' }}>&#x21BB;</text>
                    </g>
                  </svg>
                );
              })()}

              {/* ---- Pending text input overlay ---- */}
              {pendingTextPos && (() => {
                // Editing an existing text opens the editor INSIDE that text's own box: same
                // corner, same width, at least the same height. It used to open as a fresh box
                // running from the caret to the right-hand edge of the paper, so a box the user
                // had dragged narrow and tall showed its text wrapping somewhere else entirely
                // while it was being typed, and snapped to the wide shape on commit. Only a
                // brand-new text, which has no box yet, gets the rest of the sheet to fill.
                const _eTxt = pendingTextShapeId ? canvasShapes.find(s => s.id === pendingTextShapeId) : null;
                const _eCW = canvasRef.current?.offsetWidth  || 595;
                const _eCH = canvasRef.current?.offsetHeight || 841;
                const _ePaperW = Math.max(80, ((100 - pendingTextPos.x) / 100) * _eCW - 10);
                // A brand-new text: the editor is as wide as what has been typed (or the hint, while
                // nothing has), growing as letters arrive and wrapping only once it reaches the edge of
                // the paper. It used to open the full width from the caret to the edge of the sheet —
                // a long empty bar for a two-letter word — and then shrank to the text on commit.
                let _eNewW = _ePaperW;
                if (!_eTxt) {
                  if (!_measureCtx.current) _measureCtx.current = document.createElement('canvas').getContext('2d');
                  _measureCtx.current.font = `${shapeTextItalic ? 'italic ' : ''}${shapeTextBold ? 'bold ' : ''}${shapeFontSize}px ${shapeFontFamily || 'Arial'}`;
                  const _lines = (pendingTextValue || 'Type text...').split('\n');
                  const _widest = Math.max(...(_lines.map(l => _measureCtx.current.measureText(l || ' ').width)));
                  _eNewW = Math.min(_ePaperW, Math.max(shapeFontSize * 2, _widest + shapeFontSize * 0.6));
                }
                const _eBoxW = _eTxt ? Math.max(30, (Math.abs(_eTxt.x2 - _eTxt.x1) / 100) * _eCW) : _eNewW;
                const _eBoxH = _eTxt ? Math.max(16, (Math.abs(_eTxt.y2 - _eTxt.y1) / 100) * _eCH) : 0;
                // The border and padding are chrome: they are pulled back out again with a
                // negative margin so the CONTENT box starts on the box's own corner and is
                // exactly as wide as the box. Otherwise the caret sits 6px in and the lines
                // break 12px early, and what you type is not where it lands.
                const _ePadX = 5, _ePadY = 2, _eBorder = 1;
                return (
                <div
                  style={{
                    position: 'absolute',
                    left: `${pendingTextPos.x}%`,
                    top: `${pendingTextPos.y}%`,
                    // Above every shape/image (their zIndex is layerIndex, which starts at 1000
                    // and only grows) and above the ink-drawing canvas (pinned at 9999) -- this
                    // box has to sit on top of everything on the sheet, always, or it renders
                    // behind whatever's already there and typed text is invisible while editing.
                    zIndex: 10002,
                  }}
                  onMouseDown={e => e.stopPropagation()}
                  onMouseUp={e => e.stopPropagation()}
                  onClick={e => e.stopPropagation()}
                >
                  {/* A textarea, and bounded by the paper. It was a single-line input with no
                      maximum width, so a sentence simply grew off the right-hand side of the
                      sheet while it was being typed — past the edge of the page and then past the
                      edge of the screen. The box now stops where the paper does and wraps, which
                      is also exactly what the finished text does (see wrapTextLines), so what you
                      type is laid out the way it will look. Enter still finishes; shift-enter
                      starts a new line, the way it does in the prompt box. */}
                  <textarea
                    autoFocus
                    rows={1}
                    value={pendingTextValue}
                    lang={listenLang}
                    onChange={e => setPendingTextValue(e.target.value)}
                    onBlur={() => { if (!listeningRef.current) finalizePendingText(); }}
                    onKeyDown={e => {
                      // Enter starts a new line, the way it does in every other box of text in
                      // the world. It used to finish the text instead, which meant a keyboard
                      // could not do the one thing the New line button had to be invented for.
                      // Finishing is Ctrl/Cmd+Enter, or simply touching the sheet somewhere else
                      // — the blur below commits — and Escape leaves the text as it was.
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); finalizePendingText(); }
                      if (e.key === 'Escape') { setPendingTextPos(null); setPendingTextValue(''); setPendingTextShapeId(null); }
                    }}
                    placeholder="Type text..."
                    ref={(el) => {
                      if (!el) return;
                      el.style.height = 'auto';
                      // Never shorter than the box it is editing — the dashed editor frame is
                      // the user's only sight of that box while typing, and a frame that
                      // collapsed to the text read as the box itself collapsing.
                      el.style.height = `${Math.max(el.scrollHeight, _eBoxH + _ePadY * 2 + _eBorder * 2)}px`;
                    }}
                    style={{
                      boxSizing: 'border-box',
                      marginLeft: -(_ePadX + _eBorder),
                      marginTop: -(_ePadY + _eBorder),
                      fontSize: shapeFontSize,
                      fontFamily: shapeFontFamily,
                      fontWeight: shapeTextBold ? 'bold' : 'normal',
                      fontStyle: shapeTextItalic ? 'italic' : 'normal',
                      textDecoration: shapeTextUnderline ? 'underline' : 'none',
                      lineHeight: 1.2,
                      color: shapeStrokeColor,
                      background: 'transparent',
                      border: '1px dashed #fa4848',
                      borderRadius: 3,
                      padding: '2px 5px',
                      minWidth: 0,
                      // The box's own width when there is a box, and whatever is left of the
                      // sheet from here to its right edge when there is not.
                      width: `${_eBoxW + _ePadX * 2 + _eBorder * 2}px`,
                      maxWidth: `${_eBoxW + _ePadX * 2 + _eBorder * 2}px`,
                      resize: 'none',
                      overflow: 'hidden',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      outline: 'none',
                    }}
                  />
                </div>
                );
              })()}

              {pendingLabelShapeId && (() => {
                const _s = canvasShapes.find(s => s.id === pendingLabelShapeId);
                if (!_s) return null;
                const _cx = (_s.x1 + _s.x2) / 2, _cy = (_s.y1 + _s.y2) / 2;
                // Fit the box to the shape's own footprint instead of a fixed 70-160px guess --
                // that fixed size routinely overran a small shape's bounds, so the editor ended
                // up floating well outside the very shape it was labelling. Still floored to a
                // usable minimum so a tiny shape doesn't get an unusably cramped box.
                const _lblRect = canvasRef.current?.getBoundingClientRect();
                const _lblBoxWpx = _lblRect ? (Math.abs(_s.x2 - _s.x1) / 100) * _lblRect.width  : 120;
                const _lblBoxHpx = _lblRect ? (Math.abs(_s.y2 - _s.y1) / 100) * _lblRect.height : 40;
                const _lblW = Math.max(56, Math.min(_lblBoxWpx * 0.9, 220));
                const _lblH = Math.max(28, Math.min(_lblBoxHpx * 0.8, 90));
                return (
                  <div
                    style={{
                      position: 'absolute', left: `${_cx}%`, top: `${_cy}%`, transform: 'translate(-50%, -50%)',
                      // Same reasoning as the text-tool overlay above: must outrank any shape's
                      // own zIndex (its layerIndex, 1000+) or it renders behind the very shape
                      // it's labelling and the text being typed is invisible.
                      zIndex: 10002,
                    }}
                    onMouseDown={e => e.stopPropagation()}
                    onMouseUp={e => e.stopPropagation()}
                    onClick={e => e.stopPropagation()}
                  >
                    <textarea
                      ref={labelInputRef}
                      autoFocus
                      value={pendingLabelValue}
                      onChange={e => setPendingLabelValue(e.target.value)}
                      onFocus={e => e.target.select()}
                      // Reaching for a control is not finishing the caption. Focus moving into
                      // the toolbar or an open panel leaves the box open with the words still in
                      // it, so a typeface can be chosen mid-sentence; focus landing anywhere else
                      // finishes it, the way a press on the sheet always has.
                      onBlur={(e) => {
                        if (listeningRef.current) return;
                        const to = e.relatedTarget;
                        if (to?.closest?.('.is-top-toolbar, .is-tools-row, .is-bottom-bar, .is-group-panel, .is-cam')) return;
                        commitShapeLabel();
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitShapeLabel(); }
                        if (e.key === 'Escape') { e.preventDefault(); cancelShapeLabelEditor(); }
                      }}
                      placeholder="Label…"
                      style={{
                        fontSize: 13, fontFamily: 'Arial, sans-serif', textAlign: 'center', color: '#1a1a1a',
                        background: 'rgba(255,255,255,0.95)', border: '1px dashed #fa4848', borderRadius: 4,
                        padding: '3px 6px', width: _lblW, height: _lblH, resize: 'none', outline: 'none',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)', boxSizing: 'border-box',
                      }}
                    />
                  </div>
                );
              })()}

              {/* Images on canvas */}
              {canvasImages.map((img) => (
                <div
                  key={img.id}
                  data-image-id={img.id}
                  /* A cap, a moustache, a pair of ears: cut-out artwork on a transparent
                     ground, exactly like a crop. Every picture on the sheet otherwise gets a
                     soft drop shadow and a hairline box, which is right for a photograph and
                     wrong for these — the shadow has nothing to sit under, so it reads as a
                     pale rectangle floating over the face, and the box makes the prop look
                     like a pasted-on layer instead of part of the picture. is-cutout takes
                     both off and glows the artwork itself when it is selected. */
                  className={`canvas-image${(img.cutout || img.source === 'crop' || img.source === 'prop') ? ' is-cutout' : ''} ${(selectedImage === img.id || selectedShapeIds.has(img.id)) ? 'selected' : ''}`}
                  style={{
                    position: 'absolute',
                    left: `${img.x}%`,
                    top: `${img.y}%`,
                    width: `${(img.width / 21) * 100}%`,
                    height: `${(img.height / 29.7) * 100}%`,
                    border: (magicSubTool === 'chat' && magicChatImages.includes(img.id))
                      ? '2px solid #4a90e2'
                      : (selectedImage === img.id || selectedShapeIds.has(img.id))
                      ? '2px solid #fa4848'
                      : (img.cropShape === 'circle' || img.source === 'crop' || img.source === 'prop')
                      ? 'none'
                      : '1px solid rgba(0,0,0,0.1)',
                    outline: selectedShapeIds.has(img.id) && selectedImage !== img.id ? '2px dashed #fa4848' : undefined,
                    borderRadius: img.cropShape === 'circle' ? '50%' : undefined,
                    // Cropped pieces (smart / freeform / rectangle / circle crops) have
                    // transparent regions in their PNG. The default .canvas-image
                    // box-shadow would outline the whole rectangle, making the piece
                    // look "elevated" against light backgrounds. Suppress it.
                    boxShadow: (img.source === 'crop' && (selectedImage !== img.id && !selectedShapeIds.has(img.id))) ? 'none' : undefined,
                    cursor: activeTool === 'draw' 
                      ? canvasCursor 
                      : activeTool === 'selection'
                      ? 'crosshair' // Show crosshair when selection tool is active
                      : draggingImage === img.id 
                        ? 'grabbing' 
                        : 'move',
                    overflow: 'visible',
                    zIndex: img.layerIndex ?? 1,
                    // While a pen / figure tool is active the sheet draws over images — except the
                    // SELECTED image, which stays grabbable so dragging it moves it (a selected
                    // object always takes precedence over creating a new one).
                    pointerEvents: (activeTool === 'selection' || ((activeTool === 'draw' || activeTool === 'shapes') && selectedImage !== img.id)) ? 'none' : 'auto',
                    touchAction: 'none',
                    backgroundColor: 'transparent',
                    transform: img.rotation ? `rotate(${img.rotation}deg)` : undefined,
                    transformOrigin: 'center center',
                  }}
                  onClick={(e) => {
                    if (!tapIsOnPaint(img, e.clientX, e.clientY)) return;   // the empty part of a cut-out
                    if (activeTool !== 'selection') {
                      handleImageClick(img.id, e);
                    }
                  }}
                  onMouseDown={(e) => {
                    if (!tapIsOnPaint(img, e.clientX, e.clientY)) return;
                    if (activeTool !== 'selection' && (activeTool !== 'draw' || selectedImage === img.id)) {
                      handleMouseDown(e, img.id, 'move');
                    }
                  }}
                  onTouchStart={(e) => {
                    const _t0 = e.touches[0];
                    if (_t0 && !tapIsOnPaint(img, _t0.clientX, _t0.clientY)) return;
                    // Track start position so onTouchEnd can detect a tap vs drag
                    if (magicSubTool === 'chat' && e.touches[0]) {
                      magicTouchStartRef.current = { id: img.id, x: e.touches[0].clientX, y: e.touches[0].clientY };
                    }
                    if (activeTool !== 'selection' && (activeTool !== 'draw' || selectedImage === img.id)) {
                      e.stopPropagation();
                      handleTouchStart(e, img.id, 'move');
                    }
                  }}
                  onTouchEnd={(e) => {
                    // In magic-wand mode, detect a tap (< 12px movement) and fire selection logic
                    if (magicSubTool === 'chat' && magicTouchStartRef.current?.id === img.id) {
                      const touch = e.changedTouches[0];
                      if (touch) {
                        const dx = touch.clientX - magicTouchStartRef.current.x;
                        const dy = touch.clientY - magicTouchStartRef.current.y;
                        if (Math.sqrt(dx * dx + dy * dy) < 12) {
                          e.stopPropagation();
                          // Replicate handleImageClick logic for magic-wand inline (avoids e.preventDefault conflict)
                          const tappedImg = canvasImages.find(i => i.id === img.id);
                          if (tappedImg) {
                            setSelectedImage(img.id);
                            setShowImageControls(img.id);
                            if (magicSubTool === 'chat') {
                              setMagicChatImages(prev => {
                                if (prev.includes(img.id)) return prev.filter(id => id !== img.id);
                                const next = prev.length >= 2 ? [...prev.slice(1), img.id] : [...prev, img.id];
                                return next;
                              });
                            } else if (magicSubTool === 'contrast' || magicSubTool === 'brightness') {
                              setMagicSubTool(null);
                              setCurrentControlSection(null); setShowTopControls(false);
                            } else if (magicSubTool === 'color') {
                              setCurrentControlSection('magic-color'); setShowTopControls(true);
                            }
                          }
                        }
                      }
                      magicTouchStartRef.current = null;
                    }
                  }}
                >
                  {/* AI chat role badge -- only when 2 images are selected AND both exist on canvas */}
                  {magicSubTool === 'chat' && magicChatImages.length === 2 && magicChatImages.includes(img.id) && magicChatImages.every(id => canvasImages.some(i => i.id === id)) && (() => {
                    const sorted = [...magicChatImages].sort((a, b) => {
                      const ia = canvasImages.find(i => i.id === a);
                      const ib = canvasImages.find(i => i.id === b);
                      return (ia?.y ?? 0) - (ib?.y ?? 0);
                    });
                    const isEdit = sorted[0] === img.id;
                    return (
                      <span style={{
                        position: 'absolute', top: 3, left: 3, zIndex: 10001,
                        fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
                        background: isEdit ? 'rgba(74,144,226,0.9)' : 'rgba(120,80,200,0.9)',
                        color: '#fff', padding: '2px 5px', borderRadius: 3,
                        pointerEvents: 'none', textTransform: 'uppercase',
                      }}>{isEdit ? 'Edit' : 'Ref'}</span>
                    );
                  })()}

                  {/* Main Image */}
                  {/* ── Previewing a slider shows the ORIGINAL with the filter on it ──────────
                      The slider's number is absolute: +30 means "thirty brighter than the
                      photograph", which is what the bake produces. But the preview was laying
                      that filter over whatever is on screen — and after one adjustment what is
                      on screen is ALREADY baked. So re-opening brightness on a picture set to
                      +30 and nudging it to +31 previewed 30 on top of 30: the picture lurched
                      to nearly twice as bright, held there while the finger moved, then snapped
                      back to the right value on release. Unstable while dragging, "deformed"
                      until you let go, and correct afterwards — which is exactly the shape of
                      the complaint.
                      Previewing from the stored original makes the preview and the bake the
                      same calculation, so nothing moves when the finger lifts. Contrast is the
                      reason it has to be done this way rather than by dividing out what is
                      already applied: brightness multiplies, contrast does not. */}
                  {(() => {
                    const _prev = img.id === selectedImage && magicFilterPreview.on;
                    const _turning = turnPreview && turnPreview.id === img.id;
                    return (
                  <img
                    src={_turning ? turnPreview.url : (_prev ? (img._filterOrigUrl || img.url) : img.url)}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'fill', // Changed from 'contain' to 'fill' to remove white space
                      pointerEvents: 'none',
                      display: 'block',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      clipPath: img.cropShape === 'circle' ? 'ellipse(50% 50% at center)' : undefined,
                      // Live preview while slider is being dragged -- zero means no change
                      filter: _prev
                        ? `brightness(${1 + magicFilterPreview.brightness / 50}) contrast(${1 + magicFilterPreview.contrast / 50})`
                        : undefined,
                    }}
                    onError={(e) => {
                      console.error('Failed to load image:', img.url);
                      e.target.style.backgroundColor = '#ffebee';
                      e.target.style.display = 'flex';
                      e.target.style.alignItems = 'center';
                      e.target.style.justifyContent = 'center';
                      e.target.innerHTML = '<div style="color: #c62828; font-size: 11px;">Failed to load</div>';
                    }}
                  />
                    );
                  })()}

                  {/* ── Resize handles, sized against the object ────────────────────────────
                      These were a flat 44x44 each, eight of them, placed on the corners and the
                      middle of each side. On a photograph that is fine. On a small object — a
                      pair of sunglasses two centimetres wide — eight 44px squares cover it
                      completely, so every square millimetre of it is a resize handle and there
                      is nowhere left to take hold of it. It could not be moved at all: every
                      drag reshaped it, which is exactly what happens when the thing you grab is
                      always a corner.
                      So a handle is now a fraction of the object, never bigger than the old
                      44 and never smaller than a fingertip; and on a small object the four
                      side handles step aside entirely, leaving its whole middle free to drag.
                      Resizing a small object is what the corners are for. */}
                  {imageSelectionMode && selectedImage === img.id && (() => {
                    const _cw = canvasRef.current?.clientWidth || 400;
                    const _ch = canvasRef.current?.clientHeight || 600;
                    const _objW = (img.width / 21) * _cw;
                    const _objH = (img.height / 29.7) * _ch;
                    const _min = Math.max(1, Math.min(_objW, _objH));
                    // A handle reaches half its own width inward, so capping it at HALF the
                    // object's shorter side leaves the middle half of any object — however
                    // small — free to drag. That is the whole of the cure for a prop covered
                    // corner to corner in handles; see the note below about the four that were
                    // removed along with it.
                    const _hs = Math.max(12, Math.min(44, _min * 0.5));
                    return [
                    { dir: 'nw', style: { top: 0,    left: 0,    cursor: 'nw-resize', transform: 'translate(-50%, -50%)' } },
                    { dir: 'n',  style: { top: 0,    left: '50%',cursor: 'n-resize',  transform: 'translate(-50%, -50%)' } },
                    { dir: 'ne', style: { top: 0,    right: 0,   cursor: 'ne-resize', transform: 'translate(50%, -50%)' } },
                    { dir: 'e',  style: { top: '50%',right: 0,   cursor: 'e-resize',  transform: 'translate(50%, -50%)' } },
                    { dir: 'se', style: { bottom: 0, right: 0,   cursor: 'se-resize', transform: 'translate(50%, 50%)' } },
                    { dir: 's',  style: { bottom: 0, left: '50%',cursor: 's-resize',  transform: 'translate(-50%, 50%)' } },
                    { dir: 'sw', style: { bottom: 0, left: 0,    cursor: 'sw-resize', transform: 'translate(-50%, 50%)' } },
                    { dir: 'w',  style: { top: '50%',left: 0,    cursor: 'w-resize',  transform: 'translate(-50%, -50%)' } },
                  // All eight, always. Dropping the four side handles on a small object was
                  // never what freed its middle — the handle size was — and it quietly cost the
                  // thing that makes sizing readable: take hold of an EDGE and the toolbar turns
                  // from where the picture is into how big it is. Without those handles an edge
                  // was just more of the picture, the readout stayed on position, and a size
                  // could no longer be nudged by eye.
                  ].map(({ dir, style }) => (
                    <div
                      key={dir}
                      style={{
                        position: 'absolute',
                        width: _hs,
                        height: _hs,
                        background: 'transparent',
                        zIndex: 100,
                        touchAction: 'none',
                        ...style,
                        cursor: getRotatedCursor(dir, img.rotation),
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const canvas = canvasRef.current;
                        if (!canvas) return;
                        const rect = canvas.getBoundingClientRect();
                        // Absorb the click that follows the release, the way the figure, ink and
                        // rotate handles already do. Letting go with the pointer past the edge —
                        // which is most of the time, since dragging a corner outward is how you
                        // make something bigger — otherwise ends on the sheet, and a click on the
                        // sheet puts the picture down: the object deselected itself the instant
                        // you finished sizing it, taking the W/H readout with it.
                        shapeClickedRef.current = true;
                        resizeDirectionRef.current = dir;
                        resizeStartImagesRef.current = canvasImagesRef.current;
                        setResizingImage(img.id);
                        dragOffsetRef.current = ({ x: e.clientX - rect.left, y: e.clientY - rect.top });
                        setSelectMoveControlMode('resize');
                        setCurrentControlSection('selectmove');
                        setShowTopControls(true);
                      }}
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const touch = e.touches[0];
                        if (!touch) return;
                        const canvas = canvasRef.current;
                        if (!canvas) return;
                        const rect = canvas.getBoundingClientRect();
                        shapeClickedRef.current = true;   // same as above
                        resizeDirectionRef.current = dir;
                        resizeStartImagesRef.current = canvasImagesRef.current;
                        setResizingImage(img.id);
                        dragOffsetRef.current = ({ x: touch.clientX - rect.left, y: touch.clientY - rect.top });
                        setSelectMoveControlMode('resize');
                        setCurrentControlSection('selectmove');
                        setShowTopControls(true);
                      }}
                    />
                  )); })()}
                </div>
              ))}

              {/* Selection visualization -- rendered last so it always paints above images */}
              {isSelecting && selectionMode === 'circle' && selectionStart && selectionEnd && (() => {
                const _cx = (selectionStart.x + selectionEnd.x) / 2;
                const _cy = (selectionStart.y + selectionEnd.y) / 2;
                const _rx = Math.abs(selectionEnd.x - selectionStart.x) / 2;
                const _ry = Math.abs(selectionEnd.y - selectionStart.y) / 2;
                return (
                  <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 10000 }}>
                    {/* dark scrim outside circle */}
                    <defs>
                      <mask id="circle-crop-mask">
                        <rect width="100%" height="100%" fill="white" />
                        <ellipse cx={_cx} cy={_cy} rx={_rx} ry={_ry} fill="black" />
                      </mask>
                    </defs>
                    <rect width="100%" height="100%" fill="rgba(0,0,0,0.35)" mask="url(#circle-crop-mask)" />
                    {/* circle border */}
                    <ellipse
                      cx={_cx} cy={_cy} rx={_rx} ry={_ry}
                      fill="none"
                      stroke="rgba(255,255,255,0.9)"
                      strokeWidth="1.5"
                      strokeDasharray="6 3"
                    />
                    {/* resize handle at SE */}
                    <circle cx={_cx + _rx * 0.707} cy={_cy + _ry * 0.707} r="7"
                      fill="rgba(255,255,255,0.95)" stroke="rgba(80,120,240,0.9)" strokeWidth="1.5" />
                    <text x={_cx} y={_cy + _ry + 22} textAnchor="middle"
                      fill="rgba(255,255,255,0.85)" fontSize="11" fontFamily="sans-serif">
                      Drag inside to move · Drag edge to resize
                    </text>
                  </svg>
                );
              })()}
              {isSelecting && selectionMode === 'rectangle' && selectionStart && selectionEnd && (() => {
                const _rx = Math.min(selectionStart.x, selectionEnd.x);
                const _ry = Math.min(selectionStart.y, selectionEnd.y);
                const _rw = Math.abs(selectionEnd.x - selectionStart.x);
                const _rh = Math.abs(selectionEnd.y - selectionStart.y);
                return (
                  <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 10000 }}>
                    <defs>
                      <mask id="rect-crop-mask">
                        <rect width="100%" height="100%" fill="white" />
                        <rect x={_rx} y={_ry} width={_rw} height={_rh} fill="black" />
                      </mask>
                    </defs>
                    {/* dark scrim outside rectangle */}
                    <rect width="100%" height="100%" fill="rgba(0,0,0,0.35)" mask="url(#rect-crop-mask)" />
                    {/* rectangle border */}
                    <rect x={_rx} y={_ry} width={_rw} height={_rh}
                      fill="none"
                      stroke="rgba(255,255,255,0.9)"
                      strokeWidth="1.5"
                      strokeDasharray="6 3"
                    />
                    {/* resize handle at SE corner */}
                    <circle cx={_rx + _rw} cy={_ry + _rh} r="7"
                      fill="rgba(255,255,255,0.95)" stroke="rgba(80,120,240,0.9)" strokeWidth="1.5" />
                    <text x={_rx + _rw / 2} y={_ry + _rh + 22} textAnchor="middle"
                      fill="rgba(255,255,255,0.85)" fontSize="11" fontFamily="sans-serif">
                      Drag inside to move · Drag edge to resize
                    </text>
                  </svg>
                );
              })()}
              {isSelecting && selectionMode === 'smart' && (() => {
                const isDetected = smartDetected;
                const isLassoing = !smartCropRunning && !isDetected;
                const polyPoints = isDetected ? selectionPath.map(p => `${p.x},${p.y}`).join(' ') : '';
                const lassoPoints = isLassoing && selectionPath.length > 1 ? selectionPath.map(p => `${p.x},${p.y}`).join(' ') : '';
                // Bounding box for hint text
                const hintX = selectionStart && selectionEnd ? (selectionStart.x + selectionEnd.x) / 2 : 0;
                const hintY = selectionStart ? Math.min(selectionStart.y, selectionEnd?.y ?? selectionStart.y) - 7 : 0;
                return (
                  <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 10000 }}>
                    <defs>
                      <filter id="smart-glow">
                        <feGaussianBlur stdDeviation="2.5" result="blur" />
                        <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                      </filter>
                    </defs>
                    {isLassoing && lassoPoints && (
                      /* Lasso path while user is drawing */
                      <polyline points={lassoPoints} fill="none" stroke="#00ffcc" strokeWidth="2" strokeDasharray="6 4" />
                    )}
                    {smartCropRunning && (
                      /* Pulsing ring while detecting */
                      <rect x={Math.min(selectionStart?.x??0, selectionEnd?.x??0)} y={Math.min(selectionStart?.y??0, selectionEnd?.y??0)}
                        width={Math.abs((selectionEnd?.x??0)-(selectionStart?.x??0))} height={Math.abs((selectionEnd?.y??0)-(selectionStart?.y??0))}
                        fill="none" stroke="rgba(0,255,200,0.4)" strokeWidth="3" strokeDasharray="6 4" />
                    )}
                    {isDetected && (
                      <>
                        <polygon points={polyPoints} fill="none" stroke="rgba(0,255,200,0.35)" strokeWidth="6" filter="url(#smart-glow)" />
                        <polygon points={polyPoints} fill="rgba(0,255,180,0.10)" stroke="#00ffcc" strokeWidth="2" strokeDasharray="8 5" />
                        <text x={hintX} y={hintY} textAnchor="middle" fill="#00ffcc" fontSize="10" fontFamily="sans-serif" opacity="0.9">Tap ✓ to crop</text>
                      </>
                    )}
                  </svg>
                );
              })()}
              {selectionMode === 'freeform' && selectionPath.length > 1 && (
                <svg className="selection-freeform-svg" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 10000 }}>
                  <polyline
                    points={selectionPath.map(p => `${p.x},${p.y}`).join(' ')}
                    fill="rgba(250, 55, 55, 0.15)"
                    stroke="#fa4848"
                    strokeWidth="2.5"
                    strokeDasharray="5,4"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                  {selectionPath.length > 0 && selectionTouchPos && (
                    <line
                      x1={selectionPath[0].x}
                      y1={selectionPath[0].y}
                      x2={selectionTouchPos.x}
                      y2={selectionTouchPos.y}
                      stroke="#fa4848"
                      strokeWidth="1.5"
                      strokeDasharray="4,4"
                      opacity="0.6"
                    />
                  )}
                </svg>
              )}
              {/* The pen for freeform and smart. Its tip is the point the line is drawn at, and
                  on a touch screen that point is lifted clear of the fingertip (see penOffset),
                  so what the pen is drawing is never underneath the finger drawing it. Nothing
                  is marked at the finger itself: the pen IS the cursor. */}
              {(selectionMode === 'freeform' || selectionMode === 'smart') && selectionTouchPos && (() => {
                const tipColor = selectionMode === 'smart' ? '#00ffcc' : '#fa4848';
                // SVG is 14x32 px with the sharp tip at (7, 32), so left = x - 7 and
                // top = y - 32 put that tip exactly on the drawn point.
                return (
                  <svg
                    style={{
                      position: 'absolute',
                      left: selectionTouchPos.x - 7,
                      top: selectionTouchPos.y - 32,
                      width: 14,
                      height: 32,
                      pointerEvents: 'none',
                      zIndex: 10001,
                      filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.8))',
                    }}
                    viewBox="0 0 14 32"
                  >
                    <circle cx="7" cy="3" r="3" fill={tipColor} stroke="white" strokeWidth="0.8" />
                    <rect x="4" y="5" width="6" height="18" fill={tipColor} />
                    <polygon points="4,23 10,23 7,32" fill={tipColor} stroke="white" strokeWidth="0.5" />
                  </svg>
                );
              })()}
            </div>
            </>
          );
          })()}
          </div>
        </div>

        {/* Magic Wand AI Chat panel -- single input strip between canvas and bottom bar */}
        {magicSubTool === 'chat' && (
          <div className="magic-chat-panel">
            <div className="magic-chat-card">
              {/* No status text here. Progress and errors already take over the top toolbar
                  (see aiStatusInTopBar), and the send button animates while a run is in
                  flight, so a second copy down here only ate prompt width. */}

              {/* Prompt history */}
              {showPromptHistory && magicPromptHistory.length > 0 && (
                <div className="magic-prompt-history">
                  {magicPromptHistory.map((p, i) => (
                    <div key={i} className="magic-prompt-history-item">
                      <button
                        className="magic-prompt-history-text"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => { setMagicChatInput(p); setShowPromptHistory(false); }}
                      >
                        {p}
                      </button>
                      <button
                        className="magic-prompt-history-delete"
                        onMouseDown={e => e.preventDefault()}
                        onClick={e => {
                          e.stopPropagation();
                          setMagicPromptHistory(prev => prev.filter((_, idx) => idx !== i));
                        }}
                        title="Remove from history"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <textarea
                className="magic-chat-input"
                value={magicChatInput}
                rows={1}
                // The device's language, not the page's. The document says lang="en", and an
                // on-screen keyboard that takes its cue from the field then came up English with
                // no way to switch — which is no use to somebody writing a prompt in their own
                // language. Spelling help is off for the same reason: it only knows one language.
                lang={listenLang}
                spellCheck={false}
                autoCorrect="off"
                autoCapitalize="sentences"
                onChange={e => setMagicChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleMagicChat(); } }}
                onFocus={(e) => {
                  if (!e.relatedTarget?.classList?.contains('magic-prompt-history-item')) setShowPromptHistory(false);
                }}
                disabled={!online || magicChatLoading}
              />

              {/* Two bare icons, side by side, so nearly the whole row is input. No reset
                  (clearing the box is a select-all away) and no close (picking any other
                  tool leaves AI chat). Both keep their title for hover and long-press. */}
              <div className="magic-chat-actions">
                <button
                  className={`magic-chat-chip${showPromptHistory ? ' active' : ''}`}
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => setShowPromptHistory(s => !s)}
                  disabled={magicPromptHistory.length === 0}
                  title="Recent prompts"
                  aria-label="Recent prompts"
                >
                  <FaHistory />
                </button>
                {/* Which language is being listened for, said out loud and changed with one tap.
                    It was only ever reachable by HOLDING the microphone in a text box — and not
                    reachable at all from here, so dictating a prompt was English whatever the
                    picker said, and there was no way to find that out. The AI itself has always
                    understood German; it was the listening that did not. */}
                {SPEECH_API && (
                  <button
                    className="magic-chat-lang"
                    onClick={(e) => {
                      const r = e.currentTarget.getBoundingClientRect();
                      setLangMenuPos({ top: Math.max(8, r.top - 6 - 44 * SPEECH_LANGS.length), left: Math.max(6, Math.min(r.left, window.innerWidth - 196)) });
                      setShowLangMenu(true);
                    }}
                    title={`Speech language — ${(SPEECH_LANGS.find(l => l.code === speechLang) || SPEECH_LANGS[0]).name}`}
                    aria-label="Speech language"
                  >{(SPEECH_LANGS.find(l => l.code === speechLang) || SPEECH_LANGS[0]).label}</button>
                )}
                {/* One button doing the obvious thing. An empty box has nothing to send, so it
                    offers the microphone instead; the moment there are words — typed or spoken —
                    it becomes Send, because that is what you want next. Holding Send goes back to
                    the microphone, so dictation is still one gesture away once there is text. */}
                {(() => {
                  const hasText = !!magicChatInput.trim();
                  const micMode = !!SPEECH_API && !hasText;
                  const press = sendPressRef.current;
                  const cancelHold = () => { clearTimeout(press.timer); press.timer = null; };
                  return (
                    <button
                      className={`magic-chat-send${listening ? ' listening' : ''}`}
                      onPointerDown={() => {
                        if (!SPEECH_API || micMode || listening) return;
                        press.fired = false;
                        press.timer = setTimeout(() => { press.fired = true; void startDictation(); }, 500);
                      }}
                      onPointerUp={cancelHold}
                      onPointerLeave={cancelHold}
                      onPointerCancel={cancelHold}
                      onClick={() => {
                        // The click that ends a long press must not also send.
                        if (press.fired) { press.fired = false; return; }
                        if (listening) { stopDictation(); return; }
                        if (micMode) { void startDictation(); return; }
                        handleMagicChat();
                      }}
                      disabled={!online || magicChatLoading || (!micMode && !hasText && !listening)}
                      title={listening ? 'Stop listening' : micMode ? 'Speak' : 'Send (Enter) — hold to speak'}
                      aria-label={listening ? 'Stop listening' : micMode ? 'Speak' : 'Send'}
                    >
                      {magicChatLoading
                        ? <span className="magic-chat-dots"><span/><span/><span/></span>
                        : (micMode || listening) ? <FaMicrophone />
                        : <FaPaperPlane />}
                    </button>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* RIGHT PANEL REMOVED - buttons moved to top toolbar */}

        {/* Selfie Mode's camera: live picture, shutter, flip, close. */}
        {selfieStream && createPortal(
          <div className="is-cam">
            <video ref={selfieVideoRef} className={`is-cam__view${selfieFacing === 'user' ? ' mirror' : ''}`}
              autoPlay playsInline muted />
            <div className="is-cam__bar">
              <button className="is-cam__btn" onClick={closeSelfieCamera} title="Close">✕</button>
              <button className="is-cam__shutter" onClick={takeSelfie} title="Take the picture" />
              <button className="is-cam__btn" onClick={flipSelfieCamera} title="Front / back camera">
                <FaSyncAlt />
              </button>
            </div>
          </div>,
          document.body,
        )}

        {/* Where should saved pictures go? Asked once, in the app's own voice, before the
            browser's folder chooser — which is a security dialog only the browser may draw. */}
        <Modal
          isOpen={!!folderPrompt}
          onRequestClose={() => { folderPrompt?.resolve(false); setFolderPrompt(null); }}
          className="image-studio-folder-modal"
          overlayClassName="image-studio-modal-overlay"
        >
          <div className="help-modal-header">
            <span className="help-modal-title"><FaFolder /> Where to keep your pictures</span>
          </div>
          <div className="folder-modal-body">
            <p>Save into a <strong>{SAVE_FOLDER_NAME}</strong> folder instead of Downloads?</p>
            <div className="folder-modal-actions">
              <button
                className="folder-modal-btn ghost"
                onClick={() => { folderPrompt?.resolve(false); setFolderPrompt(null); }}
              >Just download</button>
              <button
                className="folder-modal-btn primary"
                onClick={async () => {
                  const picked = await changeStudioFolder();
                  if (picked) setSaveFolderName(picked);
                  folderPrompt?.resolve(!!picked);
                  setFolderPrompt(null);
                }}
              >Choose folder…</button>
            </div>
          </div>
        </Modal>

        {/* The camera, asked for rather than taken. Both buttons do their work straight from
            the press, which is the only way an iPhone will open either of them — see
            openSelfieCamera. Asked once: after the in-app camera is allowed, this stops
            appearing and the camera opens on the first tap. */}
        <Modal
          isOpen={!!cameraPrompt}
          onRequestClose={() => setCameraPrompt(null)}
          className="image-studio-folder-modal"
          overlayClassName="image-studio-modal-overlay"
        >
          <div className="help-modal-header">
            <span className="help-modal-title"><FaCamera /> Take a photo</span>
          </div>
          <div className="folder-modal-body">
            <p>
              {cameraPrompt?.reason
                ? cameraMessage(cameraPrompt.reason)
                : <>Use the camera <strong>inside the app</strong>, so effects can be lined up as you shoot — or hand over to your phone&apos;s own camera app.</>}
            </p>
            {/* The browser's own word for what went wrong. Small, grey, and last — nobody needs
                it to use the app, and the one person who does need it is whoever is being told
                "it says blocked but I allowed it" and has nothing else to go on. */}
            {cameraPrompt?.detail && (
              <p style={{ opacity: 0.55, fontSize: 12, marginTop: -6 }}>{cameraPrompt.detail}</p>
            )}
            <div className="folder-modal-actions">
              <button
                className="folder-modal-btn ghost"
                onClick={() => { setCameraPrompt(null); cameraInputRef.current?.click(); }}
              >Phone camera</button>
              {!cameraPrompt?.reason && (
                <button
                  className="folder-modal-btn primary"
                  onClick={() => { const f = cameraPrompt?.facing || 'user'; setCameraPrompt(null); startInAppCamera(f); }}
                >Use in app</button>
              )}
            </div>
          </div>
        </Modal>

        {/* Help Modal: FAQ, share-the-app QR, version, developer info */}
        <Modal
          isOpen={showHelpPanel}
          onRequestClose={() => { setShowHelpPanel(false); setHelpFaqOpen(null); }}
          className="image-studio-help-modal"
          overlayClassName="image-studio-modal-overlay"
        >
          <div className="help-modal-header">
            <span className="help-modal-title"><FaQuestionCircle /> {HELP_UI.title[helpLang]}</span>
            <div className="help-lang-switch" role="group" aria-label="Language / Sprache">
              {['en', 'de'].map(lng => (
                <button
                  key={lng}
                  className={`help-lang-btn${helpLang === lng ? ' active' : ''}`}
                  onClick={() => pickHelpLang(lng)}
                  aria-pressed={helpLang === lng}
                >{lng.toUpperCase()}</button>
              ))}
            </div>
            <button
              className="close-modal-btn"
              onClick={() => { setShowHelpPanel(false); setHelpFaqOpen(null); }}
              title="Close"
            >✕</button>
          </div>
          <div className="help-modal-content">
            <section className="help-section">
              {/* The whole FAQ folds away. Ten questions open on arrival is a wall of text in
                  front of the two things most people come here for — installing and sharing. */}
              <button
                className={`help-section-toggle${helpFaqSectionOpen ? ' open' : ''}`}
                onClick={() => { setHelpFaqSectionOpen(v => !v); setHelpFaqOpen(null); }}
                aria-expanded={helpFaqSectionOpen}
              >
                <h3>{HELP_UI.faq[helpLang]}</h3>
                <FaChevronRight className="help-faq-chevron" />
              </button>
              {helpFaqSectionOpen && HELP_FAQ.map((item, i) => (
                <div key={item.q.en} className={`help-faq-item${helpFaqOpen === i ? ' open' : ''}`}>
                  <button
                    className="help-faq-question"
                    onClick={() => setHelpFaqOpen(helpFaqOpen === i ? null : i)}
                  >
                    <span>{item.q[helpLang]}</span>
                    <FaChevronRight className="help-faq-chevron" />
                  </button>
                  {helpFaqOpen === i && <div className="help-faq-answer">{item.a[helpLang]}</div>}
                </div>
              ))}
            </section>

            {/* Installing lives here now, next to the FAQ answer that already explained it,
                instead of in the page-layout menu — it is a once-ever action and that menu is
                for the page. It only appears while the browser will actually take it: already
                installed, or a browser with no install of its own, and there is nothing to
                offer. */}
            {canOfferInstall() && (
              <section className="help-section help-install-section">
                <h3>{HELP_UI.install[helpLang]}</h3>
                <button
                  className="help-install-btn"
                  onClick={() => { setShowHelpPanel(false); setHelpFaqOpen(null); promptInstall('manual'); }}
                >
                  <FaDownload style={{ marginRight: 8 }} />
                  {HELP_UI.installBtn[helpLang]}
                </button>
              </section>
            )}

            {false && <section className="help-section help-share-section">
              <h3>{HELP_UI.share[helpLang]}</h3>
              <div className="help-share-row">
                <div className="help-qr-box">
                  <svg viewBox={`0 0 ${APP_QR_SIZE} ${APP_QR_SIZE}`} width="132" height="132" role="img" aria-label="QR code linking to the CreativeStudio app">
                    <rect width={APP_QR_SIZE} height={APP_QR_SIZE} fill="#ffffff" />
                    <path d={APP_QR_PATH} fill="#000000" />
                  </svg>
                </div>
                <div className="help-share-info">
                  <div className="help-share-url">{APP_SHARE_URL}</div>
                  <div className="help-share-buttons">
                    <button className="function-btn vertical" onClick={shareAppLink} title="Share">
                      <FaShareAlt />
                    </button>
                    <button className="function-btn vertical" onClick={copyAppLink} title="Copy link">
                      <FaCopy />
                    </button>
                  </div>
                  {helpLinkCopied && <div className="help-copied-note">{HELP_UI.copied[helpLang]}</div>}
                </div>
              </div>
            </section>}

            <section className="help-section help-about-section">
              <h3>{HELP_UI.about[helpLang]}</h3>
              <div className="help-about-row">PCake Photo Studio v{pkg.version}</div>
              <div className="help-about-row">
                {APP_DEV_NAME}
                {' · '}
                <a href={`mailto:${APP_DEV_EMAIL}`}>{APP_DEV_EMAIL}</a>
              </div>
              <div className="help-about-row help-about-tagline">&ldquo;{APP_DEV_TAGLINE}&rdquo;</div>
              <a className="help-about-link" href={APP_DEV_SITE} target="_blank" rel="noopener noreferrer">
                {APP_DEV_SITE.replace('https://', '')} <FaExternalLinkAlt />
              </a>
            </section>
          </div>
        </Modal>

        {/* Sign in / Sign up Modal -- gates Cloud storage actions and is reachable directly from
            the bottom bar's account button. */}
        <Modal
          isOpen={showAuthModal}
          onRequestClose={() => { setShowAuthModal(false); pendingAuthActionRef.current = null; }}
          className="image-studio-auth-modal"
          overlayClassName="image-studio-modal-overlay"
        >
          <div className="help-modal-header">
            <span className="help-modal-title">
              <FaUserCircle /> {authMode === 'signup' ? 'Create account' : 'Sign in'}
            </span>
            <button
              className="close-modal-btn"
              onClick={() => { setShowAuthModal(false); pendingAuthActionRef.current = null; }}
              title="Close"
            >✕</button>
          </div>
          {/* Every colour below is set inline, not via a CSS class — deliberately, after the
              class-based version of this form rendered with plain unstyled (dark, native)
              inputs and buttons for some users. Inline styles can't be shadowed by a stale
              stylesheet or a specificity clash, so this always renders the same everywhere.
              They use var(--token, fallback) so they still follow the app's real light/dark
              theme instead of being frozen to one look. */}
          <form onSubmit={handleAuthSubmit} style={{ padding: '22px 20px', background: 'var(--bg-surface, #f8f9fa)' }}>
            <div style={{ textAlign: 'center', marginBottom: 18 }}>
              <div style={{
                display: 'inline-flex', width: 50, height: 50, borderRadius: 16,
                background: 'linear-gradient(135deg, #fa4848 0%, #ff5c5c 100%)',
                alignItems: 'center', justifyContent: 'center', fontSize: 22, color: '#fff',
                boxShadow: '0 4px 14px rgba(250,72,72,0.35)',
              }}>
                <FaUserCircle />
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-muted, #888)', marginTop: 8 }}>
                {authMode === 'signup' ? 'Create your CreativeStudio account' : 'Sign in to use Cloud storage'}
              </div>
            </div>
            {pendingAuthActionRef.current && (
              <div style={{ background: '#fff4e5', color: '#9a6400', padding: '10px 12px', borderRadius: 10, fontSize: 13, marginBottom: 14, textAlign: 'center' }}>
                Sign in to use Cloud storage.
              </div>
            )}
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary, #555)', marginTop: 12 }}>
              Email
              <input
                type="email"
                autoComplete="email"
                value={authEmail}
                onChange={e => setAuthEmail(e.target.value)}
                autoFocus
                style={{
                  display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 6,
                  background: 'var(--input-bg, #fff)', border: '1px solid var(--border-color, #ddd)', borderRadius: 10,
                  color: 'var(--text-primary, #222)', padding: '11px 12px', fontSize: 14, outline: 'none',
                }}
              />
            </label>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary, #555)', marginTop: 14 }}>
              Password
              <input
                type="password"
                autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
                value={authPassword}
                onChange={e => setAuthPassword(e.target.value)}
                style={{
                  display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 6,
                  background: 'var(--input-bg, #fff)', border: '1px solid var(--border-color, #ddd)', borderRadius: 10,
                  color: 'var(--text-primary, #222)', padding: '11px 12px', fontSize: 14, outline: 'none',
                }}
              />
            </label>
            {authError && (
              <div style={{ background: '#fdeaea', color: '#c0392b', padding: '10px 12px', borderRadius: 10, fontSize: 13, marginTop: 14 }}>
                ⚠️ {authError}
              </div>
            )}
            {authNotice && (
              <div style={{ background: '#e8f7ee', color: '#1e7e42', padding: '10px 12px', borderRadius: 10, fontSize: 13, marginTop: 14 }}>
                {authNotice}
              </div>
            )}
            {/* Signing in is one of the few things that genuinely cannot happen offline: it is
                a round trip to the account server by definition. Saying so on the button beats
                letting it spin and then blame the password. */}
            <button
              type="submit"
              disabled={authBusy || !online}
              title={!online ? 'Signing in needs a connection' : undefined}
              style={{
                display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 18, padding: 13,
                borderRadius: 999, border: 'none',
                background: (authBusy || !online) ? '#f3a9a9' : 'linear-gradient(135deg, #fa4848 0%, #ff5c5c 100%)',
                color: '#fff', fontWeight: 700, fontSize: 14, cursor: (authBusy || !online) ? 'default' : 'pointer',
                boxShadow: '0 4px 14px rgba(250,72,72,0.35)',
              }}
            >
              {!online ? 'No connection' : authBusy ? 'Please wait…' : authMode === 'signup' ? 'Create account' : 'Sign in'}
            </button>
            <button
              type="button"
              onClick={() => { setAuthMode(m => m === 'signup' ? 'signin' : 'signup'); setAuthError(null); setAuthNotice(null); }}
              style={{
                display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 10, padding: 8,
                border: 'none', background: 'none', color: '#fa4848', fontWeight: 600, fontSize: 12.5,
                cursor: 'pointer', textAlign: 'center',
              }}
            >
              {authMode === 'signup' ? 'Already have an account? Sign in' : "New here? Create an account"}
            </button>
          </form>
        </Modal>

        {/* Bucket Modal */}
        <Modal
          isOpen={showBucketModal}
          onRequestClose={() => {
            setShowBucketModal(false);
            setBucketModalMode('select');
            setSelectedBucketImages([]);
            setConfirmDeleteBucket(false);
            setBucketDeleteMsg(null);
            setBucketSearch('');
          }}
          className="image-studio-bucket-modal"
          overlayClassName="image-studio-modal-overlay"
        >
          <div className="bucket-modal-header">
            <div className="bucket-search-wrap">
              <input
                className="bucket-search-input"
                type="text"
                placeholder="Search images..."
                value={bucketSearch}
                onChange={e => setBucketSearch(e.target.value)}
              />
              {bucketSearch.length > 0 && (
                <button
                  className="bucket-search-clear"
                  onClick={() => setBucketSearch('')}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            <button
              className="close-modal-btn"
              onClick={() => {
                setShowBucketModal(false);
                setBucketModalMode('select');
                setSelectedBucketImages([]);
                setConfirmDeleteBucket(false);
                setBucketDeleteMsg(null);
                setBucketSearch('');
              }}
            >
              ✕
            </button>
          </div>

          <div className="bucket-modal-content">
            {/* ── Say which library this is ────────────────────────────────────────────────
                Signed in with no connection, the account's library is not empty and not
                broken — it is somewhere else, and what is shown instead is this device's own.
                Unlabelled, that reads as "my pictures have gone", which is the single most
                alarming thing a photo app can imply. Saves made now stay here; the account's
                library comes back untouched when the connection does. */}
            {cloudOffline() && (
              <div className="bucket-offline-note" role="status">
                No connection — showing this device&rsquo;s library. Your account&rsquo;s
                pictures are safe and come back when you are online; anything saved now is
                saved here.
              </div>
            )}
            {isLoading ? (
              <div className="loading-bucket">Loading images...</div>
            ) : bucketImages.length === 0 ? (
              <div className="no-bucket-images">
                {cloudOffline() ? 'Nothing saved on this device yet' : 'No images found in Cloud'}
              </div>
            ) : (
              <>
                {(() => {
                  const _displayed = getDisplayedBucketImages(bucketImages, bucketSearch, bucketSortBy, bucketSortDir);
                  const _displayedNames = _displayed.map(img => img.fileName);
                  const _allSelected = _displayedNames.length > 0 && _displayedNames.every(n => selectedBucketImages.includes(n));
                  const _sortLabel = bucketSortBy === 'date' ? 'by Date' : bucketSortBy === 'size' ? 'by Size' : 'by Name';
                  const _nextSort = bucketSortBy === 'date' ? 'size' : bucketSortBy === 'size' ? 'name' : 'date';
                  return (
                    <>
                      <div className="bucket-selection-info">
                        {/* Row layout depends on state:
                            No selection  → [All ☐] | [count]          | [sort]
                            Selected      → [Delete] | [All ☐ · count]  | [Insert]
                            Confirming    → [empty]  | [confirm dialog]  | [Insert]
                            Feedback      → [empty]  | [msg]             | [Insert]  */}
                        <div className="selection-row">
                          {/* Left */}
                          <div className="bucket-hdr-left">
                            {selectedBucketImages.length === 0 ? (
                              /* No selection: All checkbox lives on the left */
                              <button className="select-all-btn" onClick={toggleSelectAllBucketImages}>
                                {_allSelected
                                  ? <FaCheckSquare style={{ color: '#fa4848', fontSize: 18 }} />
                                  : <FaRegSquare style={{ color: '#bbb', fontSize: 18 }} />
                                }
                                <span className="select-all-label">All</span>
                              </button>
                            ) : (!confirmDeleteBucket && !bucketDeleteMsg) ? (
                              /* Items selected, not confirming: Delete button on the left */
                              <button
                                className="bucket-hdr-btn bucket-hdr-btn--delete"
                                title={`Delete ${selectedBucketImages.length} image(s)`}
                                onClick={() => setConfirmDeleteBucket(true)}
                                disabled={deleting}
                              >
                                <FaTrash />
                              </button>
                            ) : null}
                          </div>

                          {/* Center */}
                          <div className="selection-count">
                            {bucketDeleteMsg ? (
                              /* Feedback message after delete */
                              <span className="bucket-confirm-inline">
                                <span className="bucket-confirm-text" style={{ color: bucketDeleteMsg.ok ? '#2e7d32' : '#c0392b' }}>
                                  {bucketDeleteMsg.text}
                                </span>
                              </span>
                            ) : confirmDeleteBucket ? (
                              /* Confirm dialog — All hidden */
                              <span className="bucket-confirm-inline">
                                <span className="bucket-confirm-text">
                                  Delete {selectedBucketImages.length} image{selectedBucketImages.length !== 1 ? 's' : ''}?
                                </span>
                                <button className="bucket-delete-btn" onClick={handleDeleteMultipleFromBucket} disabled={deleting}>
                                  {deleting ? 'Deleting…' : 'Yes'}
                                </button>
                                <button className="bucket-cancel-btn" onClick={() => setConfirmDeleteBucket(false)} disabled={deleting}>
                                  No
                                </button>
                              </span>
                            ) : selectedBucketImages.length > 0 ? (
                              /* Items selected: All checkbox beside count */
                              <>
                                <button className="select-all-btn" onClick={toggleSelectAllBucketImages} style={{ marginRight: 5 }}>
                                  {_allSelected
                                    ? <FaCheckSquare style={{ color: '#fa4848', fontSize: 16 }} />
                                    : <FaRegSquare style={{ color: '#bbb', fontSize: 16 }} />
                                  }
                                  <span className="select-all-label">All</span>
                                </button>
                                <span>{selectedBucketImages.length}/{_displayed.length}</span>
                              </>
                            ) : (
                              /* No selection: plain count */
                              <span>{selectedBucketImages.length}/{_displayed.length}</span>
                            )}
                          </div>

                          {/* Right */}
                          <div className="bucket-hdr-right">
                            {selectedBucketImages.length > 0 ? (
                              <button
                                className="bucket-hdr-btn bucket-hdr-btn--insert"
                                title={`Insert ${selectedBucketImages.length} image(s) to canvas`}
                                onClick={handleInsertMultipleFromBucket}
                                disabled={isLoading}
                              >
                                <FaPlus style={{ fontSize: 18 }} />
                              </button>
                            ) : (
                              <>
                                <button
                                  className="bucket-hdr-btn bucket-hdr-btn--sort"
                                  title={`Currently: ${_sortLabel}. Click to cycle`}
                                  onClick={() => setBucketSortBy(_nextSort)}
                                >
                                  {_sortLabel}
                                </button>
                                <button
                                  className="bucket-hdr-btn bucket-hdr-btn--sort-dir"
                                  title={bucketSortDir === 'desc' ? 'Descending' : 'Ascending'}
                                  onClick={() => setBucketSortDir(d => d === 'desc' ? 'asc' : 'desc')}
                                >
                                  {bucketSortDir === 'desc' ? '▼' : '▲'}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="bucket-images-grid">
                        {_displayed.map((img, index) => {
                          const _isRenaming = renamingBucketFile?.fileName === img.fileName;
                          const _baseName = img.displayName.includes('.')
                            ? img.displayName.substring(0, img.displayName.lastIndexOf('.'))
                            : img.displayName;
                          return (
                            <div
                              key={index}
                              className={`bucket-image-item ${selectedBucketImages.includes(img.fileName) ? 'selected' : ''}`}
                              onClick={(e) => {
                                if (_isRenaming) return;
                                e.stopPropagation();
                                handleBucketImageToggle(img.fileName);
                              }}
                            >
                              <div className="bucket-image-checkbox">
                                <input
                                  type="checkbox"
                                  checked={selectedBucketImages.includes(img.fileName)}
                                  onChange={(e) => { e.stopPropagation(); handleBucketImageToggle(img.fileName); }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              <div className="bucket-image-preview">
                                {/* Saved as a whole sheet: opening this one alone brings the
                                    objects back rather than placing a picture. */}
                                {img.hasProject && <span className="bucket-project-tag">sheet</span>}
                                {img.previewUrl ? (
                                  <img
                                    src={img.previewUrl}
                                    alt={img.displayName}
                                    onError={(e) => {
                                      const el = e.target;
                                      if (isFirebaseStorageUrl(el.src) && !el.dataset.proxied) { el.dataset.proxied = '1'; el.src = proxiedImageUrl(el.src); return; }
                                      el.style.display = 'none';
                                      el.parentElement.innerHTML = '<div class="preview-error">No Preview</div>';
                                    }}
                                  />
                                ) : (
                                  <div className="preview-error">No Preview</div>
                                )}
                              </div>
                              <div className="bucket-image-info">
                                {_isRenaming ? (
                                  <input
                                    className="bucket-rename-input"
                                    autoFocus
                                    value={renamingBucketFile.newName}
                                    onClick={e => e.stopPropagation()}
                                    onChange={e => setRenamingBucketFile(r => ({ ...r, newName: e.target.value }))}
                                    onBlur={() => handleBucketRename(img, renamingBucketFile.newName)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') handleBucketRename(img, renamingBucketFile.newName);
                                      if (e.key === 'Escape') setRenamingBucketFile(null);
                                    }}
                                  />
                                ) : (
                                  <div
                                    className="bucket-image-name"
                                    title="Click to rename"
                                    onClick={e => {
                                      e.stopPropagation();
                                      setRenamingBucketFile({ fileName: img.fileName, displayName: img.displayName, newName: _baseName });
                                    }}
                                  >
                                    {img.displayName}
                                  </div>
                                )}
                                <div className="bucket-image-meta">
                                  <span className="bucket-image-size">{img.size ? formatFileSize(img.size) : '—'}</span>
                                  {(img.timeCreated || img.updated) && (
                                    <span className="bucket-image-date">
                                      {/* When the picture was SAVED, not when its record was last
                                          touched: renaming rewrites `updated`, which made an old
                                          picture claim it was saved today. Time as well as date,
                                          so several saves on one day can be told apart. */}
                                      {new Date(img.timeCreated || img.updated).toLocaleString(undefined,
                                        { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {_displayed.length === 0 && (
                          <div className="no-bucket-images">No images match your search</div>
                        )}
                      </div>
                    </>
                  );
                })()}

              </>
            )}
          </div>
        </Modal>
        <div
          onClick={(e) => {
            if (!isDirty) return;
            const link = e.target.closest('a[href]');
            if (!link) return;
            e.preventDefault();
            e.stopPropagation();
            handleGuardedNavigate(new URL(link.href).pathname);
          }}
          style={{ display: 'contents' }}
        >
          {/* Page layout picker — opens from the bottom-bar Layout button */}
          {showLayoutMenu && (
            <>
              <div className="is-sheet-scrim" onClick={() => setShowLayoutMenu(false)} />
              <div className="is-layout-menu" role="menu">
                {/* A way out that is where you are looking. The panel could only be dismissed by
                    finding the button that opened it again, or by pressing the sheet behind — on
                    a phone the panel covers the sheet, so there was barely any sheet to press. */}
                <div className="is-layout-menu__head">
                  <div className="is-layout-menu__title">Page layout</div>
                  <button
                    className="is-layout-menu__close"
                    onClick={() => setShowLayoutMenu(false)}
                    title="Close"
                    aria-label="Close"
                  >✕</button>
                </div>
                <div className="is-layout-menu__grid">
                  {PRINT_SIZE_ORDER.map(key => {
                    const ps = PRINT_SIZES[key];
                    const active = key === printSize;
                    return (
                      <button
                        key={key}
                        className={`is-layout-option${active ? ' active' : ''}`}
                        onClick={() => { setPrintSizeTo(key); setShowLayoutMenu(false); }}
                        title={`${ps.display} ${ps.width}×${ps.height} cm`}
                      >
                        <span className={`psi-rect-sm${ps.height > ps.width ? ' portrait' : ' landscape'}`} />
                        <span className="is-layout-option__label">{ps.label}</span>
                        <span className="is-layout-option__sub">{ps.orient}</span>
                      </button>
                    );
                  })}
                </div>
                {/* The sheet's own background — theme, colour, shade. This is where the install
                    button used to sit; installing is a once-ever thing and has moved to Help,
                    beside the FAQ answer that already explained it, which leaves this menu for
                    the one setting that belongs to the page itself. What is chosen here is what
                    Save and Print put behind the drawing, not just a preview. */}
                {/* ── Two rows, not five ──────────────────────────────────────────────────
                    This was four paper buttons, fifteen colour dots, a row of shades that
                    appeared underneath whichever dot was pressed, and then two more headed
                    rows of effects: something like thirty controls, and the panel was taller
                    than the sheet it was describing. The fifteen dots and their shades are one
                    swatch now — the colour picker the browser already has, which reaches every
                    colour rather than the fifteen someone once chose. The two headed rows are
                    one grid with no headings. What is left is the paper, and the look of it. */}
                <div className="is-layout-menu__title is-bg-title">Sheet background</div>
                <div className="is-bg-papers">
                  {BG_THEMES.map(t => (
                    <button
                      key={t.value}
                      className={`is-bg-paper${canvasBackground === t.value ? ' active' : ''}`}
                      onClick={() => { setCanvasBackground(t.value); markDirty(); }}
                      title={t.title}
                    >
                      <span className={`is-bg-paper__sw${t.value === 'transparent' ? ' none' : ''}`}
                        style={t.value === 'transparent' ? undefined : { background: t.value }} />
                      <span className="is-bg-paper__label">{t.label}</span>
                    </button>
                  ))}
                  {/* The browser's own colour dialog used to open here. It is somebody else's
                      window with somebody else's words in it — a "Select colour" heading, a
                      "Chosen colour", a "Customized colours" tray — none of which this panel
                      needs and none of which it can change. This is ours: one word, one rail
                      that holds black, white, every grey and every hue, and a line of shades of
                      whatever was last touched. The colour in use is the swatch on the button. */}
                  <button
                    className={`is-bg-paper is-bg-custom${bgIsCustom || openGroup === 'bgmix' ? ' active' : ''}`}
                    onClick={(e) => openGroupPanel(e, 'bgmix', 6)}
                    title="Mix a colour"
                  >
                    <span
                      className={`is-bg-paper__sw${bgIsCustom ? '' : ' wheel'}`}
                      style={bgIsCustom ? { background: canvasBackground } : undefined}
                    />
                    <span className="is-bg-paper__label">Colour</span>
                  </button>
                  {/* The second palette circle: a colour taken from the sheet, a picture or a figure. */}
                  <button
                    className="is-bg-paper"
                    onClick={() => startEyedropper('bg')}
                    title="Pick a colour from the sheet"
                  >
                    <span className="is-bg-paper__sw is-pick-sw" style={pickSwatch ? { background: pickSwatch } : undefined}>
                      {!pickSwatch && <FaEyeDropper />}
                    </span>
                    <span className="is-bg-paper__label">Pick</span>
                  </button>
                </div>
                {openGroup === 'bgmix' && createPortal(
                  <>
                    <div className="is-sheet-scrim" onClick={() => setOpenGroup(null)} />
                    <div className="is-group-panel is-mix-panel" style={{ top: groupPanelPos.top, left: groupPanelPos.left }} role="menu" aria-label="Customize">
                      <div className="is-group-panel__title">Customize</div>
                      <ColourMixer
                        value={canvasBackground !== 'transparent' ? canvasBackground : (customSwatch || DEFAULT_CUSTOM)}
                        onChange={(hex) => { setCanvasBackground(hex.toLowerCase()); markDirty(); }}
                      />
                      <div
                        className="is-mix-rail"
                        role="slider"
                        aria-label="Colour"
                        aria-valuenow={Math.round(mixPos * 100)}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        tabIndex={0}
                        style={{ background: MIX_RAIL_CSS }}
                        onPointerDown={pickFromRail}
                        onPointerMove={e => { if (e.buttons === 1) pickFromRail(e); }}
                        onKeyDown={e => {
                          const step = e.shiftKey ? 0.05 : 0.01;
                          if (e.key === 'ArrowLeft') { e.preventDefault(); setMixFromPos(Math.max(0, mixPos - step)); }
                          if (e.key === 'ArrowRight') { e.preventDefault(); setMixFromPos(Math.min(1, mixPos + step)); }
                        }}
                      >
                        <span className="is-mix-thumb" style={{ left: `${mixPos * 100}%`, background: canvasBackground === 'transparent' ? '#fff' : canvasBackground }} />
                      </div>
                      <div className="is-mix-shades">
                        {/* Keyed by position, not by colour: a pale colour's lighter shades are
                            all the same white, and duplicate keys leave React unable to tell one
                            swatch from another — it kept the old ones alongside the new. */}
                        {mixShades.map((c, i) => (
                          <button
                            key={i}
                            className={`is-mix-shade${canvasBackground === c ? ' active' : ''}`}
                            style={{ background: c }}
                            onClick={() => { setCanvasBackground(c); markDirty(); }}
                            title={c}
                          />
                        ))}
                      </div>
                    </div>
                  </>, document.getElementById('portal-root') || document.body)}
                {/* Every tile is the sheet in miniature: the colour chosen above with the effect
                    laid over it, drawn by the very CSS the sheet uses. Save and Print draw the
                    same thing on the canvas, so what is in the tile is what comes off the
                    printer. */}
                <div className="is-bg-fx-grid">
                  <button
                    className={`is-bg-fx${!canvasBgFx ? ' active' : ''}`}
                    onClick={() => { setCanvasBgFx(null); markDirty(); }}
                    title="Nothing over the paper"
                  >
                    <span className="is-bg-fx__sw"
                      style={{ backgroundColor: canvasBackground !== 'transparent' ? canvasBackground : '#ffffff' }} />
                    <span className="is-bg-fx__label">Plain</span>
                  </button>
                  {SHEET_FX_PICK.map(k => (
                    <button
                      key={k}
                      className={`is-bg-fx${canvasBgFx === k ? ' active' : ''}`}
                      onClick={() => { setCanvasBgFx(k); markDirty(); }}
                      title={SHEET_FX[k].label}
                    >
                      <span className="is-bg-fx__sw"
                        style={{
                          backgroundColor: canvasBackground !== 'transparent' ? canvasBackground : '#ffffff',
                          ...(sheetFxStyle(k, FX_PREVIEW_CW, isDarkSheet(canvasBackground)) || {}),
                        }} />
                      <span className="is-bg-fx__label">{SHEET_FX[k].label}</span>
                    </button>
                  ))}
                </div>
                <button
                  className={`is-layout-menu__toggle${showGrid ? ' active' : ''}`}
                  onClick={toggleGrid}
                >
                  <span className="grid-toggle-icon" />
                  {showGrid ? 'Hide the centimetre grid' : 'Show a centimetre grid'}
                </button>
              </div>
            </>
          )}
          {/* ImageStudio bottom bar replaces the app bottom menu */}
          <div className="is-bottom-bar" style={{ display: fromCustom ? 'none' : undefined }}>
            <button
              className={`is-bottom-btn${pendingNavPath ? ' save-blink' : ''}${activeTool === 'save' ? ' active' : ''}`}
              onClick={() => {
                if (pendingNavPath) { setPendingNavPath(null); setSaveMessage(null); }
                handleToolSelect('save');
              }}
              title="Save"
            >
              <FaSave />
              <span className="is-bottom-label">Save</span>
            </button>
            <button
              className={`is-bottom-btn${showLayoutMenu ? ' active' : ''}`}
              onClick={() => { setShowShareRow(false); setShowLayoutMenu(v => !v); }}
              title={`Page layout: ${PRINT_SIZES[printSize].display}`}
            >
              <span className={`psi-rect-sm${PRINT_SIZES[printSize].height > PRINT_SIZES[printSize].width ? ' portrait' : ' landscape'}`} style={{ display: 'inline-block' }} />
              <span className="is-bottom-label">Layout</span>
            </button>
            <button
              className={`is-bottom-btn${showShareRow ? ' active' : ''}`}
              onClick={() => { setShowLayoutMenu(false); setShowShareRow(v => !v); }}
              title="Share"
            >
              <FaShareAlt />
              <span className="is-bottom-label">Share</span>
            </button>
            <button
              className={`is-bottom-btn${activeTool === 'print' ? ' active' : ''}`}
              onClick={() => {
                const hasContent = sheetHasSomething();
                if (!hasContent) { handleToolSelect('print'); } else { setActiveTool('print'); handlePrint(); }
              }}
              title="Print"
            >
              <FaPrint />
              <span className="is-bottom-label">Print</span>
            </button>
            {isCloudAuthEnabled() && (
              <button
                ref={accountBtnRef}
                className={`is-bottom-btn${showAccountMenu ? ' active' : ''}`}
                onClick={() => {
                  if (authUser) {
                    const r = accountBtnRef.current.getBoundingClientRect();
                    setAccountMenuPos({
                      bottom: window.innerHeight - r.top + 6,
                      right: Math.max(8, Math.round(window.innerWidth - r.right)),
                    });
                    setShowAccountMenu(v => !v);
                  } else {
                    pendingAuthActionRef.current = null;
                    setAuthMode('signin');
                    setAuthError(null);
                    setAuthNotice(null);
                    setShowAuthModal(true);
                  }
                }}
                title={authUser ? authUser.email : 'Sign in'}
              >
                {authUser
                  ? <div className="is-avatar-dot">{(authUser.email || '?')[0].toUpperCase()}</div>
                  : <FaUserCircle />}
                <span className="is-bottom-label">{authUser ? 'Account' : 'Sign in'}</span>
              </button>
            )}
          </div>
          {/* Share row: takes the bottom bar's place while open. External apps only — the
              in-app chat share belonged to PCAKE. A tap outside (the scrim) restores the bar. */}
          {showShareRow && (
            <>
              <div className="is-sheet-scrim" onClick={() => setShowShareRow(false)} />
              <div className="is-share-row" role="menu" aria-label="Share to">
                {[
                  { key: 'chat',      icon: <FaComments />,  label: 'Chat',     cls: 'chat',      show: true },
                  { key: 'native',    icon: <FaShareAlt />,  label: 'Share…',  cls: 'native',    show: typeof navigator.share === 'function' },
                  { key: 'whatsapp',  icon: <FaWhatsapp />,  label: 'WhatsApp', cls: 'whatsapp',  show: true },
                  { key: 'telegram',  icon: <FaTelegramPlane />, label: 'Telegram', cls: 'telegram', show: true },
                  { key: 'instagram', icon: <FaInstagram />, label: 'Instagram', cls: 'instagram', show: true },
                  { key: 'facebook',  icon: <FaFacebookF />, label: 'Facebook', cls: 'facebook',  show: true },
                  { key: 'x',         icon: <FaTwitter />,   label: 'X',        cls: 'x',         show: true },
                  { key: 'email',     icon: <FaEnvelope />,  label: 'Email',    cls: 'email',     show: true },
                  { key: 'copy',      icon: <FaCopy />,      label: 'Copy',     cls: 'copy',      show: !!(navigator.clipboard && window.ClipboardItem) },
                  { key: 'download',  icon: <FaSave />,      label: 'Download', cls: 'download',  show: true },
                ].filter(t => t.show).map(t => (
                  <button
                    key={t.key}
                    className={`is-share-target ${t.cls}`}
                    onClick={() => { setShowShareRow(false); if (t.key === 'chat') handleChatShare(); else handleShare(t.key); }}
                    title={t.label}
                  >
                    {t.icon}
                    <span className="is-share-target__label">{t.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      {/* Colour pop-down portal — free-draw tool's colour swatch button */}
      {(showColorPicker || showColorShades) && createPortal(
        <div
          className={showColorShades ? 'color-shades-controls' : 'base-colors-controls'}
          onMouseDown={e => e.preventDefault()}
          style={{
            position: 'fixed', top: colorMenuPos.top, left: colorMenuPos.left,
            zIndex: 9999,
            background: 'var(--bg-surface, #fff)',
            border: '1px solid var(--border-color, #ddd)',
            borderRadius: 8, padding: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          }}
        >
          {showColorShades ? (
            <>
              {selectedBaseColor === CUSTOM_KEY && <ColourMixer value={customSwatch || DEFAULT_CUSTOM} onChange={setCustomSwatch} onUse={(hex) => {
                setCustomSwatch(hex);
                setDrawingColor(hex);
                setShowColorShades(false);
                setShowColorPicker(false);
                const selFill = canvasImages.find(i => i.id === selectedImage && i.name === 'fill');
                if (selFill) {
                  const rgb = hexToRgb(hex);
                  if (rgb) recolourFill(selFill.url, rgb).then(newUrl =>
                    setCanvasImages(prev => prev.map(i => i.id === selectedImage ? { ...i, url: newUrl } : i))
                  );
                }
              }} />}
            <div className="color-shades-row" style={{ gap: 4, padding: 2, maxWidth: 200 }}>
              {shadesFor(selectedBaseColor).map((shade, si) => (
                <button
                  key={shade}
                  className={`color-shade-option ${drawingColor === shade ? 'active' : ''}${selectedBaseColor === CUSTOM_KEY && si === 3 ? ' is-exact' : ''}`}
                  style={{ backgroundColor: shade }}
                  onClick={() => {
                    setDrawingColor(shade);
                    setShowColorShades(false);
                    setShowColorPicker(false);
                    // Recolour the selected fill image if one is active
                    const selFill = canvasImages.find(i => i.id === selectedImage && i.name === 'fill');
                    if (selFill) {
                      const rgb = hexToRgb(shade);
                      if (rgb) recolourFill(selFill.url, rgb).then(newUrl =>
                        setCanvasImages(prev => prev.map(i => i.id === selectedImage ? { ...i, url: newUrl } : i))
                      );
                    }
                  }}
                  title={shade}
                />
              ))}
            </div>
            </>
          ) : (
            <div className="base-colors-row" style={{ gap: 4, padding: 2, maxWidth: 200 }}>
              {baseColors.map((color) => (
                <button
                  key={color}
                  className={`base-color-option ${selectedBaseColor === color ? 'active' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => {
                    setSelectedBaseColor(color);
                    setShowColorShades(true);
                  }}
                  title={color}
                />
              ))}
              <button
                className={`base-color-option is-wheel${selectedBaseColor === CUSTOM_KEY ? ' active' : ''}`}
                style={customSwatch ? { backgroundColor: customSwatch } : undefined}
                onClick={() => { if (!customSwatch) setCustomSwatch(DEFAULT_CUSTOM); setSelectedBaseColor(CUSTOM_KEY); setShowColorShades(true); }}
                title="Mix your own colour"
              />
              <button
                className="base-color-option is-pick"
                style={pickSwatch ? { backgroundColor: pickSwatch } : undefined}
                onClick={() => startEyedropper('draw')}
                title="Pick a colour from the sheet"
              >{!pickSwatch && <FaEyeDropper />}</button>
            </div>
          )}
        </div>,
        document.body
      )}
      {/* Account pop-down portal — signed-in state, opens upward from the bottom bar */}
      {showAccountMenu && authUser && createPortal(
        <div
          ref={accountMenuRef}
          onMouseDown={e => e.preventDefault()}
          style={{
            position: 'fixed', bottom: accountMenuPos.bottom, right: accountMenuPos.right,
            zIndex: 9999,
            background: 'var(--bg-surface, #fff)',
            border: '1px solid var(--border-color, #ddd)',
            color: 'var(--text-primary, #333)',
            borderRadius: 8, padding: 6,
            display: 'flex', flexDirection: 'column', gap: 2,
            minWidth: 180, maxWidth: 'calc(100vw - 16px)', boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          }}
        >
          <div style={{ padding: '4px 8px', fontSize: 12, opacity: 0.7, overflowWrap: 'anywhere', lineHeight: 1.35 }}>{authUser.email}</div>
          <button
            onClick={async () => {
              setShowAccountMenu(false);
              // signOut clears the Supabase session, which is the one thing the home page
              // looks at — so landing on / after this shows the home page, not a bounce
              // straight back into the studio.
              try { await signOut(); } catch { /* the session is going either way */ }
              window.location.href = '/';
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', border: 'none',
              background: 'transparent', borderRadius: 4, cursor: 'pointer', color: 'inherit', fontSize: 13,
            }}
          >
            <FaSignOutAlt /> Sign out
          </button>
        </div>,
        document.body
      )}
      {/* Draw palette dropdown portal */}
      {showDrawPalette && createPortal(
        <div
          ref={drawPaletteRef}
          onMouseDown={e => e.preventDefault()}
          style={{
            position: 'fixed', top: drawPalettePos.top, left: drawPalettePos.left,
            zIndex: 9999,
            background: 'var(--bg-surface, #fff)',
            border: '1px solid var(--border-color, #ddd)',
            color: 'var(--text-primary, #333)',
            borderRadius: 8, padding: '4px',
            display: 'flex', flexDirection: 'column', gap: 1,
            minWidth: 48, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          }}
        >
          {[
            { mode: 'pen',    icon: <DrawToolIcon mode="pen" />,    label: 'Pen'      },
            { mode: 'pencil', icon: <DrawToolIcon mode="pencil" />, label: 'Pencil'   },
            { mode: 'spray',  icon: <DrawToolIcon mode="spray" />,  label: 'Spray'    },
            { mode: 'eraser', icon: <DrawToolIcon mode="eraser" />, label: 'Eraser'   },
            { mode: 'fill',   icon: <DrawToolIcon mode="fill" />,   label: 'Fill — tap inside a closed drawing' },
            { mode: 'eyedropper', icon: <DrawToolIcon mode="eyedropper" />, label: 'Eyedropper' },
          ].map(({ mode, icon, label }) => (
            <button key={mode}
              onMouseDown={e => e.preventDefault()}
              onClick={() => {
                if (mode === 'eyedropper') {
                  setEyedropperActive(true);
                  setCanvasCursor('crosshair');
                } else {
                  setDrawingMode(mode);
                  setBrushSize(DEFAULT_BRUSH_SIZE);
                }
                setShowDrawPalette(false);
              }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '6px 12px', border: 'none', borderRadius: 4, cursor: 'pointer',
                background: (mode === 'eyedropper' ? eyedropperActive : drawingMode === mode)
                  ? 'rgba(250,72,72,0.18)' : 'transparent',
                color: (mode === 'eyedropper' ? eyedropperActive : drawingMode === mode)
                  ? '#fa4848' : 'inherit',
                fontSize: 15,
              }}
              title={label}
            >{icon}</button>
          ))}
        </div>,
        document.body
      )}
      {/* Brush size portal */}
      {showBrushSizeMenu && createPortal(
        <div
          ref={brushMenuRef}
          onMouseDown={e => e.preventDefault()}
          style={{
            position: 'fixed', top: brushMenuPos.top, left: brushMenuPos.left,
            transform: 'translateX(-50%)', zIndex: 9999,
            background: 'var(--bg-surface, #fff)', border: '1px solid var(--border-color, #ddd)',
            color: 'var(--text-primary, #333)',
            borderRadius: 8, padding: '4px', display: 'flex', flexDirection: 'column', gap: 1,
            minWidth: 110, boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
          }}
        >
          {[{label: '1px', val: 1}, {label: '2px', val: 2}, {label: '3px', val: 3}, {label: '5px', val: 5}, {label: '8px', val: 8}, {label: '12px', val: 12}, {label: '15px', val: 15}, {label: '20px', val: 20}, {label: '25px', val: 25}, {label: '30px', val: 30}].map(opt => (
            <button key={opt.label}
              onMouseDown={e => e.preventDefault()}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 8px', border: 'none',
                background: brushSize === opt.val ? 'rgba(250,72,72,0.18)' : 'transparent',
                borderRadius: 4, cursor: 'pointer', color: 'inherit' }}
              onClick={() => { setBrushSize(opt.val); setShowBrushSizeMenu(false); }}
            >
              <svg width="44" height="10" style={{ display: 'block', flexShrink: 0 }}>
                <line x1="2" y1="5" x2="42" y2="5" stroke="currentColor"
                  strokeWidth={Math.min(opt.val * 0.8, 9)} strokeLinecap="round" />
              </svg>
              <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{opt.label}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
      {/* Emoji popdown portal -- renders at fixed position, unclipped by any overflow */}
      {showStrokeWidthMenu && createPortal(
        <div
          ref={strokeMenuRef}
          onMouseDown={e => e.preventDefault()}
          style={{
            position: 'fixed', top: strokeMenuPos.top, left: strokeMenuPos.left,
            transform: 'translateX(-50%)', zIndex: 9999,
            background: 'var(--bg-surface, #fff)', border: '1px solid var(--border-color, #ddd)',
            color: 'var(--text-primary, #333)',
            borderRadius: 8, padding: '4px', display: 'flex', flexDirection: 'column', gap: 1,
            minWidth: 110, boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
          }}
        >
          {[...(strokeMenuFor === 'text' ? [{label: 'none', val: 0}] : []),
            {label: '0.5mm', val: 0.5}, {label: '1mm', val: 1}, {label: '1.5mm', val: 1.5}, {label: '2mm', val: 2}, {label: '3mm', val: 3}, {label: '5mm', val: 5}, {label: '8mm', val: 8}, {label: '10mm', val: 10}, {label: '15mm', val: 15}].map(opt => (
            <button key={opt.label}
              onMouseDown={e => e.preventDefault()}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 8px', border: 'none',
                background: (strokeMenuFor === 'text' ? textOutline : shapeStrokeWidth) === opt.val ? 'rgba(250,72,72,0.18)' : 'transparent',
                borderRadius: 4, cursor: 'pointer', color: 'inherit' }}
              onClick={() => {
                // Opened from a text object, this sets the outline round its letters; opened from
                // a figure, its border. Same list, same gesture — the field it writes differs.
                const field = strokeMenuFor === 'text' ? 'outlineWidth' : 'strokeWidth';
                if (strokeMenuFor === 'text') setTextOutline(opt.val); else setShapeStrokeWidth(opt.val);
                if (selectedShapeId) setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, [field]: opt.val } : s));
                else if (selectedShapeIds.size > 0) setCanvasShapes(prev => prev.map(s => selectedShapeIds.has(s.id) ? { ...s, [field]: opt.val } : s));
                setShowStrokeWidthMenu(false);
              }}
            >
              <svg width="44" height="10" style={{ display: 'block', flexShrink: 0 }}>
                <line x1="2" y1="5" x2="42" y2="5" stroke="currentColor"
                  strokeWidth={Math.min(opt.val * 1.8, 9)} strokeLinecap="round" />
              </svg>
              <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{opt.label}</span>
            </button>
          ))}
          <div style={{ borderTop: '1px solid var(--border-color, #eee)', margin: '2px 4px' }} />
          <button
            onMouseDown={e => e.preventDefault()}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 8px', border: 'none',
              background: shapeDashed ? 'rgba(250,72,72,0.18)' : 'transparent',
              borderRadius: 4, cursor: 'pointer', color: 'inherit' }}
            onClick={() => {
              const nd = !shapeDashed;
              setShapeDashed(nd);
              if (selectedShapeId) setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, dashed: nd } : s));
              setShowStrokeWidthMenu(false);
            }}
          >
            <svg width="44" height="10" style={{ display: 'block', flexShrink: 0 }}>
              <line x1="2" y1="5" x2="42" y2="5" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeDasharray="5 3" />
            </svg>
            <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>Dotted</span>
          </button>
        </div>,
        document.body
      )}
      {/* Font size. Sizes are given in points of the sheet, which is what the number on the
          button shows, and each row is drawn at its own size so the choice is made by eye. */}
      {showFontSizeMenu && createPortal(
        <>
          <div className="is-sheet-scrim" onClick={() => setShowFontSizeMenu(false)} />
          <div
            onMouseDown={e => e.preventDefault()}
            style={{
              position: 'fixed', top: fontMenuPos.top, left: fontMenuPos.left,
              transform: 'translateX(-50%)', zIndex: 9999,
              background: 'var(--bg-surface, #fff)', border: '1px solid var(--border-color, #ddd)',
              color: 'var(--text-primary, #333)',
              borderRadius: 8, padding: 4, display: 'flex', flexDirection: 'column', gap: 1,
              minWidth: 92, maxHeight: '60vh', overflowY: 'auto',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            }}
          >
            {[8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 40, 48, 60, 72, 96].map(sz => (
              <button key={sz}
                onMouseDown={e => e.preventDefault()}
                style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '3px 8px', border: 'none',
                  background: shapeFontSize === sz ? 'rgba(250,72,72,0.18)' : 'transparent',
                  borderRadius: 4, cursor: 'pointer', color: 'inherit' }}
                onClick={() => {
                  setShapeFontSize(sz);
                  if (selectedShapeId) setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? refitTextBox({ ...s, fontSize: sz }) : s));
                  setShowFontSizeMenu(false);
                }}
              >
                <span style={{ fontSize: Math.min(sz, 22), lineHeight: 1, width: 26, textAlign: 'center' }}>A</span>
                <span style={{ fontSize: 11 }}>{sz}</span>
              </button>
            ))}
          </div>
        </>,
        document.body
      )}
      {showColorPopdown && createPortal(
        <div
          ref={colorPopdownRef}
          className="emoji-popdown color-popdown"
          style={{ position: 'fixed', top: colorPopdownPos.top, left: colorPopdownPos.left, width: 230 }}
          onMouseDown={e => e.stopPropagation()}
        >
          {magicCakeColorBase ? (
            <>
              {magicCakeColorBase === CUSTOM_KEY && (
                <div style={{ padding: '4px' }}>
                  <ColourMixer
                    value={customSwatch || DEFAULT_CUSTOM}
                    onChange={setCustomSwatch}
                    useLabel="Change to this colour"
                    useDisabled={aiHold || magicProcessing || !effectiveImageId}
                    onUse={(hex) => {
                      if (magicProcessing || !effectiveImageId) return;
                      setCustomSwatch(hex);
                      handleMagicColorChange(hex, 'custom colour');
                      setMagicCakeColorBase(null);
                      setShowColorPopdown(false);
                    }}
                  />
                </div>
              )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, padding: '2px 4px' }}>
              {(magicCakeColorBase === PICK_KEY || magicCakeColorBase === CUSTOM_KEY) && (
                <div className="is-exact-row">
                  <span className="is-exact-row__sw" style={{ backgroundColor: aiShadesFor(magicCakeColorBase)[3] }} />
                  <span>{magicCakeColorBase === PICK_KEY ? 'Picked' : 'Mixed'} {String(aiShadesFor(magicCakeColorBase)[3]).toUpperCase()}</span>
                  <span className="is-exact-row__hint">ringed below</span>
                </div>
              )}
              {aiShadesFor(magicCakeColorBase).map((shade, si) => (
                <button
                  key={shade}
                  className={`base-color-option${(magicCakeColorBase === PICK_KEY || magicCakeColorBase === CUSTOM_KEY) && si === 3 ? ' is-exact' : ''}`}
                  style={{ backgroundColor: shade, width: 28, height: 28, minWidth: 28, minHeight: 28 }}
                  onClick={() => {
                    if (!magicProcessing && effectiveImageId) {
                      handleMagicColorChange(shade, aiColourName(magicCakeColorBase));
                      setMagicCakeColorBase(null);
                      setShowColorPopdown(false);
                    }
                  }}
                  disabled={aiHold || !effectiveImageId}
                  title={(magicCakeColorBase === PICK_KEY || magicCakeColorBase === CUSTOM_KEY) && si === 3 ? `${shade} (exact)` : shade}
                />
              ))}
            </div>
            </>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, padding: '4px' }}>
              {cakePaletteNames.map(name => (
                <button
                  key={name}
                  className="base-color-option"
                  style={{ backgroundColor: cakePalette[name][0], width: 28, height: 28, minWidth: 28, minHeight: 28 }}
                  onClick={() => setMagicCakeColorBase(name)}
                  title={name}
                />
              ))}
              <button
                className="base-color-option is-wheel"
                style={{ width: 28, height: 28, minWidth: 28, minHeight: 28, ...(customSwatch ? { backgroundColor: customSwatch } : {}) }}
                onClick={() => { if (!customSwatch) setCustomSwatch(DEFAULT_CUSTOM); setMagicCakeColorBase(CUSTOM_KEY); }}
                title="Mix your own colour"
              />
              <button
                className="base-color-option is-pick"
                style={{ width: 28, height: 28, minWidth: 28, minHeight: 28, ...(pickSwatch ? { backgroundColor: pickSwatch } : {}) }}
                onClick={() => startEyedropper('ai')}
                title="Pick a colour from the sheet"
              >{!pickSwatch && <FaEyeDropper />}</button>
            </div>
          )}
        </div>,
        document.body
      )}
      {/* Background colour options popdown for Remove Image Background button */}
      {showBgColorPopdown && createPortal(
        <div
          ref={bgColorPopdownRef}
          className="emoji-popdown color-popdown"
          style={{ position: 'fixed', top: bgColorPopdownPos.top, left: bgColorPopdownPos.left, width: 220, maxHeight: 'calc(100vh - 12px)', overflowY: 'auto' }}
          onMouseDown={e => e.stopPropagation()}
        >
          <div style={{ padding: '4px 6px 2px', fontSize: 10, opacity: 0.6, borderBottom: '1px solid var(--border-color,#ddd)', marginBottom: 4 }}>Background</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, padding: '2px 6px 6px' }}>
            {/* No background -- transparent */}
            <button
              className="base-color-option"
              style={{ width: 28, height: 28, minWidth: 28, minHeight: 28, background: 'repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 0 0/10px 10px', border: '1px solid #aaa' }}
              onClick={() => { handleMagicRemoveImageBg(); setShowBgColorPopdown(false); }}
              disabled={aiHold || !effectiveImageId}
              title="No background (transparent)"
            />
            {[
              { hex: '#FFFFFF', label: 'White' },
              { hex: '#000000', label: 'Black' },
              { hex: '#F5F0E8', label: 'Cream' },
              { hex: '#FDDDE6', label: 'Blush' },
              { hex: '#87CEEB', label: 'Sky Blue' },
              { hex: '#90EE90', label: 'Light Green' },
              { hex: '#CCCCCC', label: 'Gray' },
              { hex: '#FFD700', label: 'Gold' },
              { hex: '#FFB3B3', label: 'Light Red' },
              { hex: '#D8B4FE', label: 'Lavender' },
              { hex: '#FED7AA', label: 'Peach' },
              { hex: '#BAE6FD', label: 'Light Blue' },
            ].map(({ hex, label }) => (
              <button
                key={hex}
                className="base-color-option"
                style={{ backgroundColor: hex, width: 28, height: 28, minWidth: 28, minHeight: 28, border: hex === '#FFFFFF' ? '1px solid #ccc' : undefined }}
                onClick={() => { handleMagicRemoveImageBgWithColor(hex, label); setShowBgColorPopdown(false); }}
                disabled={aiHold || !effectiveImageId}
                title={label}
              />
            ))}
            {/* The two palette circles, as in every other palette. Mix takes a colour (hex accepted)
                and pick takes one from the sheet. Neither starts the replacement; "Use this colour" does. */}
            <button
              className={`base-color-option is-wheel${bgRemoveMix ? ' active' : ''}`}
              style={{ width: 28, height: 28, minWidth: 28, minHeight: 28 }}
              onClick={() => { if (!customSwatch) setCustomSwatch(DEFAULT_CUSTOM); setBgRemoveMix(v => !v); }}
              disabled={aiHold || !effectiveImageId}
              title="Mix your own colour"
            />
            <button
              className="base-color-option is-pick"
              style={{ width: 28, height: 28, minWidth: 28, minHeight: 28, ...(pickSwatch ? { backgroundColor: pickSwatch } : {}) }}
              onClick={() => startEyedropper('remove')}
              disabled={aiHold || !effectiveImageId}
              title="Pick a colour from the sheet"
            >{!pickSwatch && <FaEyeDropper />}</button>
          </div>
          {bgRemoveMix && (
            <div style={{ padding: '0 6px 6px' }}>
              <ColourMixer
                value={customSwatch || DEFAULT_CUSTOM}
                onChange={(hex) => { setCustomSwatch(hex); setBgRemovePending({ hex, label: 'custom' }); }}
              />
            </div>
          )}
          {bgRemovePending && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 6px 6px' }}>
              <span style={{ width: 22, height: 22, flex: '0 0 auto', borderRadius: 5, border: '1px solid rgba(0,0,0,0.28)', background: bgRemovePending.hex }} />
              <button
                style={{ flex: 1, padding: '4px 8px', fontSize: 11, borderRadius: 6, border: '1px solid var(--border-color,#ddd)', background: 'transparent', color: 'inherit', cursor: 'pointer' }}
                onClick={() => {
                  handleMagicRemoveImageBgWithColor(bgRemovePending.hex, bgRemovePending.label);
                  setShowBgColorPopdown(false);
                  setBgRemovePending(null);
                  setBgRemoveMix(false);
                }}
                disabled={aiHold || !effectiveImageId}
              >Use this colour</button>
            </div>
          )}
        </div>,
        document.body
      )}
      {/* Cake name popup -- Buyer, opened from Custom (PCake) */}
      {showNamePopup && createPortal(
        <div
          ref={namePopdownRef}
          className="emoji-popdown color-popdown"
          style={{ position: 'fixed', top: namePopdownPos.top, left: namePopdownPos.left, width: 220, padding: 8 }}
          onMouseDown={e => e.stopPropagation()}
        >
          <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 6 }}>
            {magicCakeNames[effectiveImageId]
              ? <>Currently on the cake: <b>{magicCakeNames[effectiveImageId]}</b><br />Edit below to replace it.</>
              : 'Enter the name for the cake'}
          </div>
          <input
            type="text"
            value={pendingNameValue}
            onChange={e => setPendingNameValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleMagicNameApply(); if (e.key === 'Escape') setShowNamePopup(false); }}
            placeholder="e.g. Happy Birthday Alex"
            autoFocus
            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 16, borderRadius: 6, border: '1px solid var(--border-color,#ccc)', marginBottom: 8 }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
            <button
              className="control-btn vertical"
              style={{ width: 'auto', height: 30, padding: '0 10px', fontSize: 12 }}
              onClick={() => setShowNamePopup(false)}
            >Cancel</button>
            <button
              className="control-btn vertical"
              style={{ width: 'auto', height: 30, padding: '0 10px', fontSize: 12, color: '#fa4848' }}
              onClick={handleMagicNameApply}
              disabled={!pendingNameValue.trim()}
            >Apply</button>
          </div>
        </div>,
        document.body
      )}
      {showEmojiPopdown && createPortal(
        <div
          ref={emojiPopdownRef}
          className="emoji-popdown stamp-popdown"
          style={{ position: 'fixed', top: emojiPopdownPos.top, left: emojiPopdownPos.left }}
          onMouseDown={e => e.stopPropagation()}
        >
          {/* -- Top menu bar -- */}
          {/* Save-as-stamp, select and delete are for everyone. The bar used to sit behind an
              Admin/Super check carried over from PCAKE, where the stamps were one shared set;
              here the role came from a prop nothing passes, so it was always 'Admin' and the
              check never hid anything — it only read as if it did. Stamps live in this
              device's own library (lib/localDb), so the only stamps anyone can save or delete
              are their own. */}
          {isStaff && (<div className="stamp-menu-bar">
            <div className="stamp-menu-actions">
              {!stampSelectMode ? (
                <button
                  className="stamp-action-btn"
                  onClick={handleUploadStampRemoveBg}
                  disabled={stampUploading || stampRemoving}
                  title="Save canvas design as stamp (background removed)">
                  {stampRemoving ? '⏳' : <FaSave />}
                </button>
              ) : (
                <>
                  {selectedStampIds.size === 1 && (
                    <button
                      className="stamp-action-btn stamp-hologram-btn"
                      onClick={handleMarkAsHologram}
                      disabled={stampUploading}
                      title="Mark as hologram stamp">
                      {stampUploading ? '⏳' : <FaGem />}
                    </button>
                  )}
                  <button
                    className="stamp-action-btn stamp-del-btn"
                    onClick={handleDeleteStamps}
                    disabled={stampUploading || selectedStampIds.size === 0}
                    title={`Delete ${selectedStampIds.size} stamp(s)`}>
                    {stampUploading ? '⏳' : <FaTrash />}
                  </button>
                </>
              )}
            </div>
            <label className="stamp-select-label" title="Select stamps">
              <input
                type="checkbox"
                checked={stampSelectMode}
                onChange={() => {
                  setStampSelectMode(v => !v);
                  setSelectedStampIds(new Set());
                }}
              />
            </label>
          </div>)}
          {/* -- Stamp grid -- */}
          {studioStamps.length === 0 && !stampUploading && (
            <div className="stamp-empty-msg">No stamps yet</div>
          )}
          <div
            className="stamp-grid"
            ref={stampGridRef}
            onMouseDown={e => {
              const el = stampGridRef.current;
              if (!el) return;
              const startY = e.clientY;
              const startScroll = el.scrollTop;
              const onMove = me => { el.scrollTop = startScroll - (me.clientY - startY); };
              const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
              };
              document.addEventListener('mousemove', onMove);
              document.addEventListener('mouseup', onUp);
            }}
          >
            {studioStamps.map(s => {
              const isChecked = selectedStampIds.has(s.id);
              return (
                <div key={s.id} className={`stamp-thumb-wrap${isChecked ? ' stamp-checked' : ''}`}>
                  <button
                    className={`stamp-thumb-btn${selectedEmoji === s.url && !isChecked ? ' active' : ''}`}
                    onClick={() => {
                      if (stampSelectMode) {
                        setSelectedStampIds(prev => {
                          const next = new Set(prev);
                          if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                          return next;
                        });
                        return;
                      }
                      setSelectedEmoji(s.url);
                      setShowEmojiPopdown(false);
                      setSelectedStampIds(new Set());
                    }}
                    title={stampSelectMode ? 'Select stamp' : 'Use stamp'}>
                    <img src={s.url} alt="stamp" />
                    
                  </button>
                  {stampSelectMode && (
                    <input
                      type="checkbox"
                      className="stamp-delete-checkbox"
                      checked={isChecked}
                      onChange={e => {
                        e.stopPropagation();
                        setSelectedStampIds(prev => {
                          const next = new Set(prev);
                          if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                          return next;
                        });
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ImageStudio;
